import React, { useReducer, useState, useMemo } from "react";
import {
  Bell, Link2, Camera,
  LayoutDashboard, ClipboardList, Factory, Boxes, GitBranch, Truck, FileText,
  Plus, X, Menu, ChevronRight, ChevronDown, Search, AlertTriangle, CheckCircle2,
  Clock, ArrowRight, Building2, Package, TrendingUp, Layers, CircleDot,
  ListOrdered, BarChart3, Users, ArrowUp, ArrowDown, Trash2, Inbox, ShieldCheck, Printer, Download, Activity, ListChecks, Repeat, Calendar, Target, Settings, Network, Sun, Moon, BookOpen,
} from "lucide-react";
import { catalog, parties as partiesApi, orders as ordersApi, stock as stockApi, batches as batchesApi, production as productionApi, dispatch as dispatchApi, purchases as purchasesApi, invoices as invoicesApi, paymentsApi, workCenterDefaultsApi, warehousesApi, itemWarehouseDefaultsApi, storesApi, indentsApi, retailDeliveriesApi, packsApi, users as usersApi, roles as rolesApi, branches as branchesApi, departments as departmentsApi, company as companyApi, shifts as shiftsApi, holidays as holidaysApi, numberSeries as numberSeriesApi, units as unitsApi, hsn as hsnApi, gst as gstApi, performance as performanceApi, dashboard as dashboardApi, reports as reportsApi, tasks as tasksApi, notifications as notificationsApi, ApiError } from "./web/apiClient.js";
import { useResource } from "./web/useResource.js";
import { PieChart, Pie, Cell, BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, LabelList } from "recharts";
import { Spinner, LoadingBlock, InlineError } from "./web/ui.jsx";
import { toBase as uomToBase, formatBasePack, fmtNum as uomFmt } from "./web/uom.js";
let THEME_DARK = false;

/* ============================ helpers ============================ */
const cx = (...a) => a.filter(Boolean).join(" ");
const DAY = 86400000;
const fmtQty = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
// RETAIL PHASE 6: render a BASE quantity with its base uom and, when the item has
// packs, the first pack equivalent — "10 kg (2 tin)". Falls back to the plain
// number when no base uom is known (unchanged look for non-retail / no-pack data).
const packLabel = (baseQty, baseUom, packs) => {
  const ps = packs || [];
  if (!ps.length) return baseUom ? `${uomFmt(baseQty)} ${baseUom}` : fmtQty(baseQty);
  return formatBasePack(baseQty, baseUom, ps[0].packName, ps);
};
const fmtMoney = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const fmtDate = (ts) => (ts ? new Date(ts).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "—");
const fmtDateTime = (ts) => (ts ? new Date(ts).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");
// Relative time for the notification bell ("just now" / "5m ago" / "3h ago" / "2d ago", else date).
const relTime = (ts) => {
  if (!ts) return "";
  const s = Math.max(0, (Date.now() - new Date(ts).getTime()) / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60); if (m < 60) return m + "m ago";
  const h = Math.floor(m / 60); if (h < 24) return h + "h ago";
  const d = Math.floor(h / 24); if (d < 7) return d + "d ago";
  return fmtDateTime(ts);
};
const hrs = (a, b) => (a && b ? Math.max(0, (b - a) / 3.6e6) : 0);
const FREQ = [["once", "One-time"], ["daily", "Daily"], ["weekly", "Weekly"], ["fortnightly", "Fortnightly"], ["monthly", "Monthly"], ["quarterly", "Quarterly"], ["halfyearly", "Half-yearly"], ["yearly", "Yearly"], ["custom", "Custom (days)"]];
const FREQ_LABEL = Object.fromEntries(FREQ);
function nextDue(from, freq, customDays) {
  const d = new Date(from || Date.now());
  if (freq === "daily") d.setDate(d.getDate() + 1);
  else if (freq === "weekly") d.setDate(d.getDate() + 7);
  else if (freq === "fortnightly") d.setDate(d.getDate() + 14);
  else if (freq === "monthly") d.setMonth(d.getMonth() + 1);
  else if (freq === "quarterly") d.setMonth(d.getMonth() + 3);
  else if (freq === "halfyearly") d.setMonth(d.getMonth() + 6);
  else if (freq === "yearly") d.setFullYear(d.getFullYear() + 1);
  else if (freq === "custom") d.setDate(d.getDate() + (Number(customDays) || 1));
  else return null;
  return d.getTime();
}

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const dateKey = (ts) => { const d = new Date(ts); return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0"); };
function isNonWorking(ts, weekOffDow, branchId, holidays) {
  const d = new Date(ts);
  if (d.getDay() === weekOffDow) return true;
  const key = dateKey(ts);
  return (holidays || []).some((h) => h.date === key && (branchId === "*" || h.branchId === "all" || h.branchId === branchId));
}
// working days the item is overdue (week-offs + holidays NOT counted)
function wdOverdue(due, now, weekOffDow, branchId, holidays) {
  if (!due) return 0;
  const sod = (t) => { const x = new Date(t); x.setHours(0, 0, 0, 0); return x.getTime(); };
  let day = sod(due) + DAY; const today = sod(now); let c = 0, g = 0;
  while (day <= today && g < 500) { if (!isNonWorking(day, weekOffDow, branchId, holidays)) c++; day += DAY; g++; }
  return c;
}
// Order -> Delivery pipeline, target TAT in WORKING days per stage
const PIPELINE = [
  ["Order received → Stock decision", 1],
  ["Material issue (Stores)", 1],
  ["Production (routing)", 2],
  ["Ready → Dispatch", 1],
  ["Delivery", 1],
  ["Invoice (Accounts)", 1],
];
const KRA = {
  sales: "Order capture, customer coverage & order accuracy",
  stores: "Stock allocation & material-issue accuracy / TAT",
  qc: "RM + FG quality clearance: QC TAT, pass/reject accuracy",
  planner: "Production scheduling & TAT adherence",
  production: "On-time production, step TAT & yield",
  dispatch: "Timely dispatch, challan accuracy & partial handling",
  accounts: "Invoicing TAT & GST compliance",
  owner: "Overall P&L, fulfilment & on-time delivery",
  supervisor: "Cross-module monitoring & SLA escalation",
};
// work-centre operators + who is responsible for a production order's CURRENT step
const wcOpsOf = (st, wcName) => { const w = (st.workCenters || []).find((x) => x.name === wcName); if (!w) return []; return (w.operatorIds || []).map((id) => st.users.find((u) => u.id === id)).filter(Boolean); };
const poResp = (st, po) => { if (po.assigneeId) return st.users.find((u) => u.id === po.assigneeId) || null; const cur = po.steps[po.stepIdx]; const ops = wcOpsOf(st, cur ? cur.wc : ""); return ops[0] || null; };
const userById = (st, id) => st.users.find((u) => u.id === id);
const uName = (st, id) => { const u = userById(st, id); return u ? u.name : (ROLES[id] ? ROLES[id].name : id); };
// Guarded lookups — a missing id (e.g. a real tenant's item code that isn't in
// the stale mock st.items seed) returns a safe fallback instead of undefined, so
// downstream `.name`/`.available` reads never throw (an unguarded throw during
// render unmounts React → blank screen). Mirrors the party() `|| {name:id}` guard.
const itemById = (items, id) => (items || []).find((i) => i.id === id) || { id, name: id };
const stockOf = (stock, id) => (stock && stock[id]) || {};
const ceilLot = (q, lot) => (lot ? Math.ceil(q / lot) * lot : q);

const PRICE = { "FG-PKM": 1250, "FG-BDM": 950, "FG-BCD": 720, "FG-KKF": 980 };
const WORKCENTERS = ["Oven Room", "Chocolate Room", "Mixing Room", "Roasting", "Laminate Packing", "Tin Packing", "Packing Room"];

function explodeBOM(boms, itemId, qty, acc) {
  acc = acc || {};
  const b = boms[itemId];
  if (!b) { acc[itemId] = (acc[itemId] || 0) + qty; return acc; }
  const f = qty / b.outputQty;
  [...b.comp, ...b.pack].forEach(([cid, q]) => {
    const need = q * f;
    if (boms[cid]) explodeBOM(boms, cid, need, acc);
    else acc[cid] = (acc[cid] || 0) + need;
  });
  return acc;
}
function consumeBOM(s, po) {
  const b = s.boms[po.itemId]; if (!b) return;
  const f = po.plannedQty / b.outputQty;
  [...b.comp, ...b.pack].forEach(([cid, q]) => { const st = s.stock[cid]; if (st) st.available = Math.max(0, st.available - q * f); });
}
function roomOf(po) { return po && po.steps && po.steps[0] ? po.steps[0].wc : null; }
function consumeBOMFrac(s, po, qty) {
  const b = s.boms[po.itemId]; if (!b) return;
  const f = qty / b.outputQty; const room = roomOf(po); const rs = room && s.roomStock ? (s.roomStock[room] = s.roomStock[room] || {}) : null;
  [...b.comp, ...b.pack].forEach(([cid, q]) => {
    let need = q * f;
    if (rs) { const fromRoom = Math.min(rs[cid] || 0, need); rs[cid] = (rs[cid] || 0) - fromRoom; need -= fromRoom; }
    const stk = s.stock[cid]; if (stk) stk.available = Math.max(0, stk.available - need);
  });
}
function returnBOMFrac(s, po, qty) {
  const b = s.boms[po.itemId]; if (!b) return;
  const f = qty / b.outputQty;
  [...b.comp, ...b.pack].forEach(([cid, q]) => { const stk = s.stock[cid]; if (stk) stk.available += q * f; });
}
function shortCode(itemId) { const parts = String(itemId).split("-"); return parts[parts.length - 1]; }
function batchNo(s, itemId, date) {
  const d = new Date(date); const dd = String(d.getDate()).padStart(2, "0"); const mm = String(d.getMonth() + 1).padStart(2, "0"); const yy = String(d.getFullYear()).slice(-2);
  const tag = shortCode(itemId) + "-" + dd + mm + yy;
  const serial = (s.batches || []).filter((b) => b.id.indexOf(tag + "-") === 0).length + 1;
  return tag + "-" + String(serial).padStart(2, "0");
}
function consumeRMBatchesFIFO(s, itemId, qty) {
  const used = []; let need = qty;
  const avail = s.batches.filter((b) => b.itemId === itemId && (b.type === "RM" || b.type === "PM") && b.remaining > 0.0001).sort((a, b) => a.date - b.date);
  for (const bt of avail) { if (need <= 0.0001) break; const take = Math.min(bt.remaining, need); bt.remaining -= take; need -= take; used.push({ batchId: bt.id, itemId, qty: take }); }
  return used;
}
function fgGenealogy(s, itemId, qty) {
  const bill = explodeBOM(s.boms, itemId, qty); let consumed = [];
  Object.entries(bill).forEach(([cid, q]) => { consumed = consumed.concat(consumeRMBatchesFIFO(s, cid, q)); });
  return consumed;
}

/* ============================ printable documents (challan / invoice) ============================ */
const DOC_CSS = "@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;700&display=swap');body{font-family:'Poppins',Arial,'DejaVu Sans',sans-serif;color:#1c1917;margin:32px;font-size:13px}h1{font-size:18px;margin:0 0 2px}.muted{color:#78716c}table{width:100%;border-collapse:collapse;margin-top:6px}th,td{border:1px solid #d6d3d1;padding:6px 8px;text-align:left}th{background:#f5f5f4;font-size:11px;text-transform:uppercase;letter-spacing:.03em}.right{text-align:right}.co{margin-top:16px;font-weight:bold;font-size:14px}.hdr{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #1c1917;padding-bottom:10px;margin-bottom:10px}.sign{margin-top:54px;text-align:right}.bar{background:#fef3c7;border:1px solid #f59e0b;padding:8px 10px;border-radius:6px;margin-bottom:14px;font-size:12px}@media print{.bar{display:none}}";

function openDoc(title, bodyHTML, autoPrint) {
  const w = window.open("", "_blank");
  if (!w) { alert("Popup blocked — please allow popups."); return; }
  const hint = autoPrint ? "" : '<div class="bar">To save as PDF, use the browser Print (Ctrl/Cmd + P) and choose <b>"Save as PDF"</b> as the destination.</div>';
  const script = autoPrint ? "<scr" + "ipt>window.onload=function(){setTimeout(function(){window.print()},250)}</scr" + "ipt>" : "";
  w.document.write("<!doctype html><html><head><meta charset='utf-8'><title>" + title + "</title><style>" + DOC_CSS + "</style></head><body>" + hint + bodyHTML + script + "</body></html>");
  w.document.close();
}

// Live doc generators — build the challan / invoice HTML straight from the real
// /deliveries/:code and /invoices/:code detail shapes. The SELLER company name is
// the CURRENT TENANT, passed in from the authenticated session (/auth/me
// company.name) — never hardcoded — so each tenant's documents carry their own name.
const sellerName = (companyName) => companyName || "Sweet Manufacturing ERP";

function challanDocLive(d, companyName) {
  const seller = sellerName(companyName);
  const groups = {};
  (d.lines || []).forEach((l) => { (groups[l.party.code] = groups[l.party.code] || { party: l.party, rows: [] }).rows.push(l); });
  let rows = "";
  Object.values(groups).forEach((g) => {
    rows += '<div class="co">' + g.party.name + ' <span class="muted">· ' + g.party.code + "</span></div>";
    rows += "<table><thead><tr><th>SO No</th><th>Product</th><th class='right'>Qty (Kg)</th></tr></thead><tbody>";
    g.rows.forEach((l) => { rows += "<tr><td>" + l.orderCode + "</td><td>" + l.item.name + "</td><td class='right'>" + fmtQty(l.qty) + "</td></tr>"; });
    rows += "</tbody></table>";
  });
  const total = (d.lines || []).reduce((a, l) => a + l.qty, 0);
  return "<div class='hdr'><div><h1>DELIVERY CHALLAN</h1><div class='muted'>" + seller + "</div></div><div class='right'><div><b>" + d.code + "</b></div><div class='muted'>" + fmtDate(d.deliveryDate) + "</div><div class='muted'>Status: " + d.status + "</div></div></div>"
    + "<div class='muted'>Delivered via / through: <b>" + (d.via || "—") + "</b></div>"
    + rows
    + "<div class='right' style='margin-top:10px'><b>Total: " + fmtQty(total) + " Kg</b></div>"
    + "<div class='sign'>For " + seller + "<br><br>_____________________<br>Authorised Signatory</div>";
}

// Statutory GST TAX INVOICE — all legally-required fields. Conditionally renders
// CGST+SGST columns (intra / B2C) vs a single IGST column (inter), from the
// snapshotted invoice detail (/invoices/:code). Numbers are frozen at issue.
function invoiceDocLive(inv, companyName) {
  const s = inv.supplier || {};
  const seller = s.name || sellerName(companyName);
  const inter = inv.supplyType === "inter";
  const t = inv.totals || {};
  const esc = (x) => (x == null ? "" : String(x));
  const half = (r) => (Number(r || 0) / 2);

  // --- line table ---
  let head = "<tr><th>#</th><th>Description</th><th>HSN</th><th class='right'>Qty</th><th>UOM</th><th class='right'>Rate</th><th class='right'>Taxable ₹</th>";
  head += inter ? "<th class='right'>IGST</th>" : "<th class='right'>CGST</th><th class='right'>SGST</th>";
  head += "<th class='right'>Total ₹</th></tr>";
  let body = "";
  (inv.lines || []).forEach((l, i) => {
    body += "<tr><td>" + (i + 1) + "</td><td>" + esc(l.item.name) + "</td><td>" + esc(l.hsnCode || "—") + "</td><td class='right'>" + fmtQty(l.qty) + "</td><td>" + esc(l.uom || "—") + "</td><td class='right'>" + fmtMoney(l.unitPrice) + "</td><td class='right'>" + fmtMoney(l.taxableValue) + "</td>";
    body += inter
      ? "<td class='right'>" + fmtMoney(l.igst) + " <span class='muted'>(" + esc(l.gstRate) + "%)</span></td>"
      : "<td class='right'>" + fmtMoney(l.cgst) + " <span class='muted'>(" + half(l.gstRate) + "%)</span></td><td class='right'>" + fmtMoney(l.sgst) + " <span class='muted'>(" + half(l.gstRate) + "%)</span></td>";
    body += "<td class='right'>" + fmtMoney(l.lineTotal) + "</td></tr>";
  });
  const taxCols = inter ? 1 : 2;
  let totRow = "<tr><td colspan='6' class='right'><b>Total</b></td><td class='right'><b>" + fmtMoney(t.totalTaxableValue) + "</b></td>";
  totRow += inter ? "<td class='right'><b>" + fmtMoney(t.totalIgst) + "</b></td>" : "<td class='right'><b>" + fmtMoney(t.totalCgst) + "</b></td><td class='right'><b>" + fmtMoney(t.totalSgst) + "</b></td>";
  totRow += "<td class='right'><b>" + fmtMoney(t.grandTotal) + "</b></td></tr>";
  const lineTable = "<table><thead>" + head + "</thead><tbody>" + body + totRow + "</tbody></table>";

  // --- HSN-wise tax summary (legally required) ---
  let hsnHead = "<tr><th>HSN</th><th class='right'>Rate</th><th class='right'>Taxable ₹</th>";
  hsnHead += inter ? "<th class='right'>IGST ₹</th>" : "<th class='right'>CGST ₹</th><th class='right'>SGST ₹</th>";
  hsnHead += "<th class='right'>Total Tax ₹</th></tr>";
  let hsnBody = "";
  (inv.hsnSummary || []).forEach((g) => {
    hsnBody += "<tr><td>" + esc(g.hsnCode || "—") + "</td><td class='right'>" + esc(g.gstRate) + "%</td><td class='right'>" + fmtMoney(g.taxableValue) + "</td>";
    hsnBody += inter ? "<td class='right'>" + fmtMoney(g.igst) + "</td>" : "<td class='right'>" + fmtMoney(g.cgst) + "</td><td class='right'>" + fmtMoney(g.sgst) + "</td>";
    hsnBody += "<td class='right'>" + fmtMoney(g.totalTax) + "</td></tr>";
  });
  const hsnTable = "<div class='co'>HSN-wise Tax Summary</div><table><thead>" + hsnHead + "</thead><tbody>" + hsnBody + "</tbody></table>";

  // --- totals block (right) ---
  let totals = "<table style='width:auto;margin-left:auto;margin-top:10px'><tbody>";
  totals += "<tr><td>Taxable Value</td><td class='right'>" + fmtMoney(t.totalTaxableValue) + "</td></tr>";
  if (inter) totals += "<tr><td>IGST</td><td class='right'>" + fmtMoney(t.totalIgst) + "</td></tr>";
  else { totals += "<tr><td>CGST</td><td class='right'>" + fmtMoney(t.totalCgst) + "</td></tr><tr><td>SGST</td><td class='right'>" + fmtMoney(t.totalSgst) + "</td></tr>"; }
  totals += "<tr><td>Round Off</td><td class='right'>" + fmtMoney(t.roundOff) + "</td></tr>";
  totals += "<tr><td><b>Grand Total</b></td><td class='right'><b>₹ " + fmtMoney(t.grandTotal) + "</b></td></tr></tbody></table>";

  const pos = inv.placeOfSupply ? (inv.placeOfSupply.name || "") + " (" + inv.placeOfSupply.code + ")" : "—";
  const custGstin = inv.billTo.registered ? (inv.billTo.gstin || "—") : "Unregistered (B2C)";
  const custState = inv.billTo.stateName ? inv.billTo.stateName + " (" + inv.billTo.stateCode + ")" : "—";

  return (
    "<div class='hdr'><div><h1>TAX INVOICE</h1><div class='co' style='margin-top:4px'>" + esc(seller) + "</div>"
    + (s.address ? "<div class='muted'>" + esc(s.address) + "</div>" : "")
    + "<div class='muted'>GSTIN: " + esc(s.gstin || "—") + " · State: " + esc(s.stateName || "—") + " (" + esc(s.stateCode || "—") + ")</div></div>"
    + "<div class='right'><div><b>" + esc(inv.code) + "</b></div><div class='muted'>Date: " + fmtDate(inv.invoiceDate) + "</div><div class='muted'>Challan: " + esc(inv.sourceDelivery) + "</div><div class='muted'>Supply: " + esc(inv.supplyType || "—") + "</div></div></div>"
    + "<div style='display:flex;justify-content:space-between;gap:20px;margin-bottom:8px'>"
    + "<div><b>Bill To</b><br>" + esc(inv.billTo.name) + "<br><span class='muted'>GSTIN: " + esc(custGstin) + "<br>State: " + esc(custState) + "</span></div>"
    + "<div class='right'><b>Place of Supply</b><br>" + esc(pos) + "</div></div>"
    + lineTable
    + hsnTable
    + totals
    + "<div style='margin-top:8px'><b>Amount in words:</b> " + esc(inv.amountInWords || "") + "</div>"
    + "<div class='muted' style='margin-top:10px;font-size:11px'>Declaration: We declare that this invoice shows the actual price of the goods described and that all particulars are true and correct.</div>"
    + "<div class='sign'>For " + esc(seller) + "<br><br>_____________________<br>Authorised Signatory</div>"
  );
}

/* ============================ seed data ============================ */
function buildInitial() {
  const now = Date.now();
  const items = [
    { id: "FG-PKM", name: "Pista Kali Mirch 1Kg", type: "FG", group: "Masala Pista", lot: 10.7289 },
    { id: "FG-BDM", name: "Badam Masala 1Kg", type: "FG", group: "Masala Badam", lot: 5.32515 },
    { id: "FG-BCD", name: "Badam Chocodip 1Kg", type: "FG", group: "Chocolate Badam", lot: 98.918 },
    { id: "FG-KKF", name: "Kaju Kulfi 1Kg", type: "FG", group: "Chocolate Kaju", lot: 82.058 },
    { id: "SFG-BCR", name: "Badam Coating Roasted", type: "SFG", group: "Intermediate" },
    { id: "SFG-CMX", name: "Coating Mix", type: "SFG", group: "Intermediate" },
    { id: "SFG-K32", name: "Kaju 320 Roasted", type: "SFG", group: "Intermediate" },
    { id: "RM-PDY", name: "Pista Dody", type: "RM" }, { id: "RM-SSN", name: "Seasoning", type: "RM" },
    { id: "RM-BCT", name: "Badam Coating", type: "RM" }, { id: "RM-DCH", name: "Dark Chocolate", type: "RM" },
    { id: "RM-FOS", name: "Flavour Oil Soluble", type: "RM" }, { id: "RM-GMS", name: "Garam Masala", type: "RM" },
    { id: "RM-KMP", name: "Kali Mirch Powder", type: "RM" }, { id: "RM-SLT", name: "Salt", type: "RM" },
    { id: "RM-MCH", name: "Milk Chocolate", type: "RM" }, { id: "RM-SHL", name: "Shellac", type: "RM" },
    { id: "RM-K32R", name: "Kaju 320", type: "RM" }, { id: "RM-WCH", name: "White Chocolate", type: "RM" },
    { id: "RM-KPW", name: "Kulfi Powder", type: "RM" }, { id: "RM-PPG", name: "Pista Pishori Green", type: "RM" },
    { id: "PM-PLY", name: "Polly Sg Green 1Kg", type: "PM" }, { id: "PM-STK", name: "Sticker", type: "PM" },
    { id: "PM-O11", name: "Outer No. 11 Pista Outer", type: "PM" }, { id: "PM-O01", name: "Outer 01 Dry Fruit 1Kg", type: "PM" },
    { id: "PM-O03", name: "Outer 03 Dragees Inner", type: "PM" }, { id: "PM-O04", name: "Outer 04 Dragees Outer", type: "PM" },
  ];

  const boms = {
    "FG-PKM": { outputQty: 10.7289, yield: 97.5, comp: [["RM-PDY", 10], ["RM-SSN", 1], ["SFG-CMX", 0.2]], pack: [["PM-PLY", 1], ["PM-STK", 1], ["PM-O11", 0.1]] },
    "FG-BDM": { outputQty: 5.32515, yield: 96.3, comp: [["SFG-BCR", 5], ["RM-GMS", 0.14], ["RM-KMP", 0.03], ["RM-SLT", 0.16], ["SFG-CMX", 0.3]], pack: [["PM-PLY", 1], ["PM-STK", 1], ["PM-O01", 0.1]] },
    "FG-BCD": { outputQty: 98.918, yield: 97.7, comp: [["SFG-BCR", 40], ["RM-MCH", 42], ["RM-DCH", 21], ["RM-SHL", 0.72]], pack: [["PM-PLY", 1], ["PM-STK", 2], ["PM-O03", 1], ["PM-O04", 0.1]] },
    "FG-KKF": { outputQty: 82.058, yield: 96.0, comp: [["SFG-K32", 30], ["RM-WCH", 48], ["RM-KPW", 8.4], ["RM-PPG", 0.53], ["RM-FOS", 0.07], ["RM-SHL", 0.61]], pack: [["PM-PLY", 1], ["PM-STK", 2], ["PM-O03", 1], ["PM-O04", 0.1]] },
    "SFG-BCR": { outputQty: 100, yield: 98, comp: [["RM-BCT", 100]], pack: [] },
    "SFG-CMX": { outputQty: 100, yield: 97, comp: [["RM-DCH", 80], ["RM-FOS", 5]], pack: [] },
    "SFG-K32": { outputQty: 100, yield: 98, comp: [["RM-K32R", 100]], pack: [] },
  };

  const masala = [{ wc: "Oven Room", setup: 2, per: 0.02 }, { wc: "Packing Room", setup: 1, per: 0.01 }];
  const choco = [{ wc: "Chocolate Room", setup: 3, per: 0.03 }, { wc: "Laminate Packing", setup: 1, per: 0.015 }, { wc: "Tin Packing", setup: 1, per: 0.02 }];
  const routings = { "FG-PKM": masala, "FG-BDM": masala, "FG-BCD": choco, "FG-KKF": choco };

  const parties = [
    { id: "P1", parent: null, kind: "group", name: "Mithai Mart Group", gstin: "19AAAAA0000A1Z5" },
    { id: "P2", parent: "P1", kind: "sister", name: "Mithai Mart North", gstin: "07AAAAA0000A1Z2" },
    { id: "P3", parent: "P1", kind: "sister", name: "Mithai Mart South", gstin: "29AAAAA0000A1Z9" },
    { id: "P4", parent: null, kind: "group", name: "Sweet Cravings Pvt Ltd", gstin: "19BBBBB1111B1Z3" },
    { id: "P5", parent: null, kind: "group", name: "Festive Foods Distributors", gstin: "19CCCCC2222C1Z1" },
    { id: "P6", parent: null, kind: "b2c", name: "Online Store (B2C)", gstin: "—" },
  ];

  const L = (id, itemId, ord, alloc, disp, del) => ({
    id, itemId, qtyOrdered: ord, qtyAllocated: alloc, qtyDispatched: disp, qtyDelivered: del,
    rate: PRICE[itemId], requiredBy: now + 4 * DAY,
  });
  const orders = [
    { id: "SO-1001", partyId: "P2", channel: "WhatsApp", date: now - 3 * DAY, lines: [L("l1", "FG-PKM", 100, 100, 40, 40), L("l2", "FG-BCD", 50, 50, 0, 0)] },
    { id: "SO-1002", partyId: "P3", channel: "Call", date: now - 2 * DAY, lines: [L("l3", "FG-BCD", 120, 120, 120, 0), L("l4", "FG-BDM", 80, 0, 0, 0)] },
    { id: "SO-1003", partyId: "P4", channel: "WhatsApp", date: now - DAY, lines: [L("l5", "FG-PKM", 60, 0, 0, 0), L("l6", "FG-KKF", 150, 0, 0, 0)] },
  ];

  const stepsFrom = (itemId, startedAt) =>
    routings[itemId].map((r, i) => ({ wc: r.wc, setup: r.setup, per: r.per, start: i === 0 ? startedAt : null, end: null }));
  const productionOrders = [
    { id: "PO-501", itemId: "FG-BDM", plannedQty: ceilLot(80, 5.32515), status: "In Production", sourceLineId: "l4", sourceLineIds: ["l4"], woId: null, stepIdx: 0, steps: stepsFrom("FG-BDM", now - 2 * 3.6e6), woGenerated: true, woNo: "WO-101", woAt: now - 3 * 3.6e6, issuedQty: ceilLot(80, 5.32515) },
    { id: "PO-502", itemId: "FG-KKF", plannedQty: ceilLot(150, 82.058), status: "Planned", sourceLineId: "l6", sourceLineIds: ["l6"], woId: null, stepIdx: 0, steps: stepsFrom("FG-KKF", null), woGenerated: false, issuedQty: 0 },
    { id: "PO-503", itemId: "FG-PKM", plannedQty: ceilLot(50, 10.7289), status: "Planned", sourceLineId: null, sourceLineIds: [], woId: "WO-102", woNo: "WO-102", forStock: true, stepIdx: 0, steps: stepsFrom("FG-PKM", null), woGenerated: true, issuedQty: 0 },
    { id: "PO-504", itemId: "FG-BCD", plannedQty: ceilLot(100, 98.918), status: "Planned", sourceLineId: null, sourceLineIds: [], woId: "WO-102", woNo: "WO-102", forStock: true, stepIdx: 0, steps: stepsFrom("FG-BCD", null), woGenerated: true, issuedQty: 0 },
  ];

  const deliveries = [
    { id: "DC-301", via: "Self · Tempo WB-02-1234", date: now - 2 * DAY, status: "Delivered", invoiced: true, lines: [{ soLineId: "l1", qty: 40 }] },
    { id: "DC-302", via: "BlueDart Surface", date: now, status: "In Transit", invoiced: false, lines: [{ soLineId: "l3", qty: 120 }] },
  ];
  const invoices = [
    { id: "INV-9001", deliveryId: "DC-301", billTo: "P2", date: now - 2 * DAY, lines: [{ itemId: "FG-PKM", soId: "SO-1001", qty: 40, rate: 1250, amount: 50000 }], total: 50000 },
  ];

  const roomStock = { "Oven Room": { "RM-PDY": 300, "RM-SSN": 120 }, "Chocolate Room": { "RM-DCH": 200, "RM-MCH": 150 } };
  const batches = [
    { id: "PDY-100626-01", itemId: "RM-PDY", type: "RM", qty: 500, remaining: 470, date: now - 7 * DAY, status: "Passed", qcBy: "U-2", poId: "PUR-0" },
    { id: "SSN-100626-01", itemId: "RM-SSN", type: "RM", qty: 200, remaining: 195, date: now - 7 * DAY, status: "Passed", qcBy: "U-2", poId: "PUR-0" },
    { id: "SLT-120626-01", itemId: "RM-SLT", type: "RM", qty: 100, remaining: 100, date: now - 5 * DAY, status: "Passed", qcBy: "U-2", poId: "PUR-2" },
    { id: "DCH-080626-01", itemId: "RM-DCH", type: "RM", qty: 200, remaining: 200, date: now - 9 * DAY, status: "Passed", qcBy: "U-2", poId: "PUR-0" },
    { id: "PKM-150626-01", itemId: "FG-PKM", type: "FG", qty: 50, remaining: 50, date: now - 2 * DAY, status: "Passed", room: "Oven Room", operatorId: "U-3", fgQcBy: "U-2", fgQcAt: now - 2 * DAY, poId: "PO-old", consumed: [{ batchId: "PDY-100626-01", itemId: "RM-PDY", qty: 30 }, { batchId: "SSN-100626-01", itemId: "RM-SSN", qty: 5 }] },
    { id: "KKF-160626-01", itemId: "FG-KKF", type: "FG", qty: 40, remaining: 40, date: now - DAY, status: "QC Pending", room: "Chocolate Room", operatorId: "U-3", poId: "PO-old2", consumed: [] },
  ];
  const purchases = [
    { id: "PUR-1", supplier: "Khaitan Traders", date: now - 2 * DAY, status: "QC Pending", by: "U-2", lines: [{ itemId: "RM-PDY", qty: 500 }, { itemId: "RM-SSN", qty: 200 }] },
    { id: "PUR-2", supplier: "Bengal Agro", date: now - 5 * DAY, status: "Passed", qcAt: now - 4 * DAY, by: "U-2", lines: [{ itemId: "RM-SLT", qty: 100 }] },
  ];
  const S = (a, r, w, t, b) => ({ available: a, reserved: r, wip: w, inTransit: t, buffer: b });
  const stock = {
    "FG-PKM": S(120, 60, 0, 0, 50), "FG-BDM": S(8, 0, 85.2, 0, 20),
    "FG-BCD": S(150, 50, 0, 120, 80), "FG-KKF": S(5, 0, 0, 0, 30),
    "SFG-BCR": S(300, 0, 0, 0, 100), "SFG-CMX": S(40, 0, 0, 0, 20), "SFG-K32": S(250, 0, 0, 0, 80),
    "RM-PDY": S(1200, 0, 0, 0, 300), "RM-SSN": S(200, 0, 0, 0, 50), "RM-BCT": S(800, 0, 0, 0, 200),
    "RM-DCH": S(1500, 0, 0, 0, 300), "RM-FOS": S(80, 0, 0, 0, 20), "RM-GMS": S(150, 0, 0, 0, 40),
    "RM-KMP": S(90, 0, 0, 0, 30), "RM-SLT": S(500, 0, 0, 0, 100), "RM-MCH": S(900, 0, 0, 0, 200),
    "RM-SHL": S(6, 0, 0, 0, 15), "RM-K32R": S(600, 0, 0, 0, 150), "RM-WCH": S(1100, 0, 0, 0, 250),
    "RM-KPW": S(220, 0, 0, 0, 60), "RM-PPG": S(40, 0, 0, 0, 15),
    "PM-PLY": S(5000, 0, 0, 0, 1000), "PM-STK": S(8000, 0, 0, 0, 2000), "PM-O11": S(600, 0, 0, 0, 150),
    "PM-O01": S(700, 0, 0, 0, 150), "PM-O03": S(900, 0, 0, 0, 200), "PM-O04": S(850, 0, 0, 0, 200),
  };

  const tasks = [
    { id: "T-1", title: "Daily cold-room temperature log", description: "Record AM & PM temperature of FG cold room and note any deviation > 2°C.", attachments: ["temp-log-template.xlsx"], assignee: "U-2", freq: "daily", customDays: null, due: now - DAY, status: "open", createdBy: "owner", createdAt: now - 5 * DAY, timesDone: 4, lastDone: now - DAY, history: [{ at: now - DAY, by: "Store Manager", action: "completed", remark: "All normal", attachment: "" }] },
    { id: "T-2", title: "Weekly machine maintenance check", description: "Oven + chocolate room equipment greasing and safety check.", attachments: [], assignee: "U-3", freq: "weekly", customDays: null, due: now + 2 * DAY, status: "inprogress", createdBy: "owner", createdAt: now - 7 * DAY, timesDone: 1, lastDone: now - 5 * DAY, history: [{ at: now - 3600e3, by: "Production Op", action: "in-process", remark: "Started oven section", attachment: "" }] },
    { id: "T-3", title: "Monthly GST return filing", description: "File GSTR-1 & GSTR-3B for the month.", attachments: [], assignee: "U-5", freq: "monthly", customDays: null, due: now + 8 * DAY, status: "open", createdBy: "owner", createdAt: now - 20 * DAY, timesDone: 0, lastDone: null, history: [] },
    { id: "T-4", title: "Send dispatch summary to management", description: "One-time consolidated dispatch report for the quarter.", attachments: [], assignee: "U-4", freq: "once", customDays: null, due: now - 2 * DAY, status: "open", createdBy: "owner", createdAt: now - 4 * DAY, timesDone: 0, lastDone: null, history: [] },
  ];
  const branches = [
    { id: "BR1", name: "Kolkata (HO)", code: "KOL" },
    { id: "BR2", name: "Howrah Plant", code: "HWH" },
  ];
  const users = [
    { id: "U-1", name: "Rajesh Owner", branchId: "BR1", dept: "Management", role: "owner", weekOff: 0, inTime: "10:00", outTime: "19:00", active: true },
    { id: "U-2", name: "Sundar (Stores)", branchId: "BR2", dept: "Stores", role: "stores", weekOff: 2, inTime: "09:00", outTime: "18:00", active: true },
    { id: "U-3", name: "Pintu (Production)", branchId: "BR2", dept: "Production", role: "production", weekOff: 0, inTime: "08:00", outTime: "17:00", active: true },
    { id: "U-4", name: "Lata (Dispatch)", branchId: "BR1", dept: "Dispatch", role: "dispatch", weekOff: 0, inTime: "10:00", outTime: "19:00", active: true },
    { id: "U-5", name: "Mehul (Accounts)", branchId: "BR1", dept: "Accounts", role: "accounts", weekOff: 0, inTime: "10:30", outTime: "19:30", active: true },
    { id: "U-6", name: "Rana (Production)", branchId: "BR2", dept: "Production", role: "production", weekOff: 0, inTime: "08:00", outTime: "17:00", active: true },
    { id: "U-7", name: "Bappa (Production)", branchId: "BR2", dept: "Production", role: "production", weekOff: 4, inTime: "08:00", outTime: "17:00", active: true },
    { id: "U-8", name: "Sourav (Prod Manager)", branchId: "BR2", dept: "Production", role: "prodmgr", weekOff: 0, inTime: "09:00", outTime: "18:00", active: true },
  ];
  const holidays = [
    { id: "H-1", branchId: "all", date: dateKey(now + 1 * DAY), name: "Festival holiday" },
    { id: "H-2", branchId: "BR2", date: dateKey(now + 3 * DAY), name: "Plant maintenance shutdown" },
  ];
  const workCenters = [
    { id: "WC1", name: "Oven Room", dept: "Production", operatorIds: ["U-3", "U-6"] },
    { id: "WC2", name: "Packing Room", dept: "Production", operatorIds: ["U-6", "U-7"] },
    { id: "WC3", name: "Chocolate Room", dept: "Production", operatorIds: ["U-3"] },
    { id: "WC4", name: "Laminate Packing", dept: "Production", operatorIds: ["U-7"] },
    { id: "WC5", name: "Tin Packing", dept: "Production", operatorIds: ["U-6", "U-7"] },
  ];
  const workOrders = [{ id: "WO-102", no: "WO-102", createdBy: "U-3", createdAt: now - 36e5, poIds: ["PO-503", "PO-504"] }];
  const reportsMap = { "U-1": null, "U-2": "U-1", "U-3": "U-8", "U-4": "U-1", "U-5": "U-1", "U-6": "U-8", "U-7": "U-8", "U-8": "U-1" };
  users.forEach((u) => { u.reportsTo = reportsMap[u.id] || null; u.email = u.name.split(" ")[0].toLowerCase().replace(/[^a-z]/g, "") + "@jainyscereals.in"; });
  const departments = [
    { id: "DPT-1", name: "Management", head: "U-1" }, { id: "DPT-2", name: "Sales", head: null },
    { id: "DPT-3", name: "Stores", head: "U-2" }, { id: "DPT-4", name: "Production", head: "U-8" },
    { id: "DPT-5", name: "Dispatch", head: "U-4" }, { id: "DPT-6", name: "Accounts", head: "U-5" },
    { id: "DPT-7", name: "Planning (PPC)", head: null },
  ];
  const settings = {
    company: { name: "Jainys Cereals Pvt Ltd", legalName: "JAINYS CEREALS PRIVATE LIMITED", gstin: "19AABCJ1234M1Z5", pan: "AABCJ1234M", cin: "U15400WB2015PTC012345", address: "12 Industrial Estate, Bantra", city: "Howrah", state: "West Bengal", pincode: "711101", phone: "+91 33 2654 1100", email: "ops@jainyscereals.in", website: "www.jainyscereals.in", currency: "INR" },
    numbering: { SO: "SO-", PO: "PO-", DC: "DC-", INV: "INV-", WO: "WO-", PUR: "PUR-" },
    uoms: ["Kg", "g", "Litre", "ml", "Piece", "Box", "Packet", "Carton"],
    taxes: [{ id: "TX1", name: "GST 5%", rate: 5 }, { id: "TX2", name: "GST 12%", rate: 12 }, { id: "TX3", name: "GST 18%", rate: 18 }],
    shifts: [{ id: "SH-1", name: "General", start: "10:00", end: "19:00", weekOff: 0 }, { id: "SH-2", name: "Morning", start: "06:00", end: "14:00", weekOff: 0 }, { id: "SH-3", name: "Night", start: "22:00", end: "06:00", weekOff: 0 }],
    preferences: { dateFormat: "DD MMM YYYY", currencySymbol: "₹", defaultBranch: "BR1", fyStart: "April", weekStart: "Monday", theme: (function(){try{return localStorage.getItem("growthh-theme")||"Light";}catch(e){return "Light";}})(), density: (function(){try{return localStorage.getItem("growthh-density")||"Comfortable";}catch(e){return "Comfortable";}})() },
  };
  return { items, boms, routings, parties, orders, productionOrders, workOrders, deliveries, invoices, tasks, branches, users, holidays, workCenters, stock, roomStock, purchases, batches, departments, settings, roles: JSON.parse(JSON.stringify(ROLES)), seq: { SO: 1003, PO: 504, DC: 302, INV: 9001, T: 4, U: 8, H: 2, WC: 5, WO: 102, PU: 2, BR: 2, DEPT: 7, TX: 3, SH: 3 } };
}

/* ============================ status logic ============================ */
const lineStatus = (l, pos) => {
  if (l.qtyDelivered >= l.qtyOrdered) return "Delivered";
  if (l.qtyDelivered > 0) return "Partially Delivered";
  if (l.qtyDispatched > 0) return "In Transit";
  if (l.qtyAllocated >= l.qtyOrdered) return "Ready";
  if (pos.some((p) => ((p.sourceLineIds && p.sourceLineIds.length ? p.sourceLineIds : [p.sourceLineId]).includes(l.id)) && p.status !== "Done")) return "In Production";
  return "Pending";
};
const orderStatus = (o, pos) => {
  const s = o.lines.map((l) => lineStatus(l, pos));
  if (s.every((x) => x === "Delivered")) return "Completed";
  if (s.some((x) => ["Delivered", "Partially Delivered", "In Transit"].includes(x))) return "In Progress";
  if (s.some((x) => x === "In Production")) return "In Production";
  if (s.some((x) => x === "Ready")) return "Ready";
  return "Open";
};
const stepStd = (s, q) => s.setup + s.per * q;

/* ============================ reducer ============================ */
function reducer(state, action) {
  const s = JSON.parse(JSON.stringify(state));
  const now = Date.now();
  const item = (id) => s.items.find((i) => i.id === id); // reducer already guards each call with `if (!it) return`
  const findLine = (id) => { for (const o of s.orders) { const l = o.lines.find((x) => x.id === id); if (l) return l; } return null; };

  switch (action.type) {
    case "CREATE_ORDER": {
      s.seq.SO += 1;
      const id = "SO-" + s.seq.SO;
      s.orders.unshift({
        id, partyId: action.partyId, channel: action.channel, date: now,
        lines: action.lines.map((ln, i) => ({
          id: id + "-" + i, itemId: ln.itemId, qtyOrdered: Number(ln.qty),
          qtyAllocated: 0, qtyDispatched: 0, qtyDelivered: 0,
          rate: PRICE[ln.itemId] || 0, requiredBy: now + Number(ln.days || 4) * DAY,
        })),
      });
      return s;
    }
    case "FULFILL": {
      const l = findLine(action.lineId); if (!l) return s;
      const need = l.qtyOrdered - l.qtyAllocated; if (need <= 0) return s;
      const st = s.stock[l.itemId]; if (!st) return s;
      const want = action.qty != null ? Math.max(0, Number(action.qty)) : need;
      const take = Math.min(st.available, need, want); if (take <= 0.0001) return s;
      st.available -= take; st.reserved += take; l.qtyAllocated += take;
      return s;
    }
    case "SEND_TO_PRODUCTION": {
      const l = findLine(action.lineId); if (!l) return s;
      const need = l.qtyOrdered - l.qtyAllocated;
      const qty = action.qty != null ? Math.max(0, Number(action.qty)) : need; if (qty <= 0.0001) return s;
      const it = item(l.itemId); if (!it) return s;
      s.seq.WO = (s.seq.WO || 101) + 1; const woId = "WO-" + s.seq.WO;
      s.seq.PO += 1; const poId = "PO-" + s.seq.PO;
      s.productionOrders.unshift({
        id: poId, itemId: l.itemId, plannedQty: ceilLot(qty, it.lot || qty),
        status: "Planned", sourceLineId: l.id, sourceLineIds: [l.id], forStock: false,
        stepIdx: 0, steps: (s.routings[l.itemId] || []).map((r) => ({ wc: r.wc, setup: r.setup, per: r.per, start: null, end: null })),
        woGenerated: true, woId, woNo: woId, issuedQty: 0,
      });
      s.workOrders.unshift({ id: woId, no: woId, createdBy: action.by || "U-2", createdAt: now, poIds: [poId] });
      return s;
    }
    case "START_PO": {
      const p = s.productionOrders.find((x) => x.id === action.id); if (!p || p.status !== "Planned" || !p.woGenerated) return s;
      p.status = "In Production"; p.steps[0].start = now;
      if (s.stock[p.itemId]) s.stock[p.itemId].wip += p.plannedQty;
      return s;
    }
    case "GENERATE_WO": {
      const p = s.productionOrders.find((x) => x.id === action.id); if (!p || p.status !== "Planned" || p.woGenerated) return s;
      s.seq.WO = (s.seq.WO || 0) + 1; p.woGenerated = true; p.woNo = "WO-" + s.seq.WO; p.woAt = now;
      return s;
    }
    case "CREATE_WORK_ORDER": {
      const picks = (action.picks || []).filter((pk) => (Number(pk.qty) || 0) > 0); if (!picks.length) return s;
      s.seq.WO = (s.seq.WO || 101) + 1; const woId = "WO-" + s.seq.WO; const poIds = [];
      picks.forEach((pk) => {
        const it = item(pk.itemId); if (!it) return;
        s.seq.PO += 1; const poId = "PO-" + s.seq.PO; const lineIds = (pk.lineIds || []).slice();
        s.productionOrders.unshift({
          id: poId, itemId: pk.itemId, plannedQty: ceilLot(Number(pk.qty), it.lot || Number(pk.qty)),
          status: "Planned", sourceLineId: lineIds[0] || null, sourceLineIds: lineIds, forStock: lineIds.length === 0,
          stepIdx: 0, steps: (s.routings[pk.itemId] || []).map((r) => ({ wc: r.wc, setup: r.setup, per: r.per, start: null, end: null })),
          woGenerated: true, woId, woNo: woId, issuedQty: 0,
        });
        poIds.push(poId);
      });
      if (!poIds.length) return s;
      s.workOrders.unshift({ id: woId, no: woId, createdBy: action.by || "U-3", createdAt: now, poIds });
      return s;
    }
    case "ADD_WO_ITEM": {
      const wo = s.workOrders.find((w) => w.id === action.woId); if (!wo) return s;
      const it = item(action.itemId); const qty = Number(action.qty) || 0; if (!it || qty <= 0) return s;
      s.seq.PO += 1; const poId = "PO-" + s.seq.PO; const lineIds = (action.lineIds || []).slice();
      s.productionOrders.unshift({
        id: poId, itemId: action.itemId, plannedQty: ceilLot(qty, it.lot || qty),
        status: "Planned", sourceLineId: lineIds[0] || null, sourceLineIds: lineIds, forStock: lineIds.length === 0,
        stepIdx: 0, steps: (s.routings[action.itemId] || []).map((r) => ({ wc: r.wc, setup: r.setup, per: r.per, start: null, end: null })),
        woGenerated: true, woId: wo.id, woNo: wo.id, issuedQty: 0,
      });
      wo.poIds.push(poId);
      return s;
    }
    case "ADVANCE_PO": {
      const p = s.productionOrders.find((x) => x.id === action.id); if (!p || p.status !== "In Production") return s;
      p.steps[p.stepIdx].end = now;
      if (p.stepIdx + 1 < p.steps.length) { p.stepIdx += 1; p.steps[p.stepIdx].start = now; }
      else {
        const rem = p.plannedQty - (p.issuedQty || 0); if (rem > 0.0001) { consumeBOMFrac(s, p, rem); p.issuedQty = p.plannedQty; }
        p.status = "Done";
        const st = s.stock[p.itemId]; if (st) st.wip = Math.max(0, st.wip - p.plannedQty);
        const resp = poResp(s, p); const it = s.items.find((i) => i.id === p.itemId);
        s.batches.unshift({ id: batchNo(s, p.itemId, now), itemId: p.itemId, type: it ? it.type : "FG", qty: p.plannedQty, remaining: p.plannedQty, date: now, status: "QC Pending", room: roomOf(p), operatorId: resp ? resp.id : (p.assigneeId || null), poId: p.id, consumed: [] });
      }
      return s;
    }
    case "CREATE_DISPATCH": {
      s.seq.DC += 1;
      const id = "DC-" + s.seq.DC;
      const lines = action.picks.filter((p) => Number(p.qty) > 0).map((p) => ({ soLineId: p.soLineId, qty: Number(p.qty) }));
      if (!lines.length) return s;
      lines.forEach((pk) => { const l = findLine(pk.soLineId); if (!l) return; const st = s.stock[l.itemId]; if (st) { st.reserved = Math.max(0, st.reserved - pk.qty); st.inTransit += pk.qty; } l.qtyDispatched += pk.qty; });
      s.deliveries.unshift({ id, via: action.via || "", date: now, status: "In Transit", invoiced: false, lines });
      return s;
    }
    case "DELIVER": {
      const d = s.deliveries.find((x) => x.id === action.id); if (!d || d.status === "Delivered") return s;
      d.status = "Delivered";
      d.lines.forEach((dl) => { const l = findLine(dl.soLineId); if (!l) return; const st = s.stock[l.itemId]; if (st) st.inTransit = Math.max(0, st.inTransit - dl.qty); l.qtyDelivered += dl.qty; });
      return s;
    }
    case "INVOICE": {
      const d = s.deliveries.find((x) => x.id === action.id); if (!d || d.invoiced) return s;
      const groups = {};
      d.lines.forEach((dl) => {
        const o = s.orders.find((x) => x.lines.some((l) => l.id === dl.soLineId));
        const l = o.lines.find((x) => x.id === dl.soLineId);
        (groups[o.partyId] = groups[o.partyId] || []).push({ itemId: l.itemId, soId: o.id, qty: dl.qty, rate: l.rate, amount: dl.qty * l.rate });
      });
      Object.keys(groups).forEach((pid) => {
        s.seq.INV += 1;
        const lines = groups[pid];
        s.invoices.unshift({ id: "INV-" + s.seq.INV, deliveryId: d.id, billTo: pid, date: now, lines, total: lines.reduce((a, x) => a + x.amount, 0) });
      });
      d.invoiced = true;
      return s;
    }
    case "ADD_TASK": {
      s.seq.T = (s.seq.T || 0) + 1;
      s.tasks.unshift({ id: "T-" + s.seq.T, title: action.title, description: action.description || "", attachments: action.attachments || [], assignee: action.assignee, freq: action.freq, customDays: action.customDays || null, due: action.due || null, status: "open", createdBy: action.by || "owner", createdAt: now, timesDone: 0, lastDone: null, history: [] });
      return s;
    }
    case "START_TASK": {
      const t = s.tasks.find((x) => x.id === action.id); if (!t) return s;
      t.status = "inprogress"; t.history = t.history || [];
      t.history.unshift({ at: now, by: action.by || "user", action: "in-process", remark: action.remark || "", attachment: action.attachment || "" });
      return s;
    }
    case "COMPLETE_TASK": {
      const t = s.tasks.find((x) => x.id === action.id); if (!t) return s;
      t.timesDone = (t.timesDone || 0) + 1; t.lastDone = now; t.history = t.history || [];
      t.history.unshift({ at: now, by: action.by || "user", action: "completed", remark: action.remark || "", attachment: action.attachment || "" });
      if (t.freq === "once") { t.status = "done"; t.doneAt = now; }
      else { let nd = nextDue(t.due || now, t.freq, t.customDays); while (nd && nd <= now) nd = nextDue(nd, t.freq, t.customDays); t.due = nd; t.status = "open"; }
      return s;
    }
    case "REOPEN_TASK": {
      const t = s.tasks.find((x) => x.id === action.id); if (!t) return s;
      t.status = "open"; if (t.freq === "once") t.doneAt = null; t.history = t.history || [];
      t.history.unshift({ at: now, by: action.by || "manager", action: "reopened", remark: action.remark || "" });
      return s;
    }
    case "EDIT_TASK": {
      const t = s.tasks.find((x) => x.id === action.id); if (!t) return s;
      if (action.assignee) t.assignee = action.assignee;
      if (action.due !== undefined) t.due = action.due;
      t.history = t.history || [];
      t.history.unshift({ at: now, by: action.by || "manager", action: "rescheduled / reassigned", remark: action.remark || "" });
      return s;
    }
    case "DELETE_TASK": { s.tasks = s.tasks.filter((t) => t.id !== action.id); return s; }
    case "CLOSE_TASKS": { const ids = action.ids || []; s.tasks.forEach((t) => { if (ids.includes(t.id) && t.status !== "closed") { t.status = "closed"; t.history = t.history || []; t.history.unshift({ at: now, by: action.by || "manager", action: "closed (cancelled)", remark: action.remark || "" }); } }); return s; }
    case "DELETE_TASKS": { const ids = action.ids || []; s.tasks = s.tasks.filter((t) => !ids.includes(t.id)); return s; }
    case "ADD_USER": {
      s.seq.U = (s.seq.U || 0) + 1;
      s.users.push({ id: "U-" + s.seq.U, name: action.name, branchId: action.branchId, dept: action.dept || (ROLES[action.role] ? ROLES[action.role].dept : "General"), role: action.role, reportsTo: action.reportsTo || null, email: action.email || (action.name.split(" ")[0].toLowerCase().replace(/[^a-z]/g, "") + "@jainyscereals.in"), weekOff: Number(action.weekOff), inTime: action.inTime, outTime: action.outTime, active: true });
      return s;
    }
    case "DELETE_USER": { s.users = s.users.filter((u) => u.id !== action.id); return s; }
    case "ADD_HOLIDAY": {
      s.seq.H = (s.seq.H || 0) + 1;
      s.holidays.push({ id: "H-" + s.seq.H, branchId: action.branchId, date: action.date, name: action.name });
      s.holidays.sort((a, b) => a.date.localeCompare(b.date));
      return s;
    }
    case "DELETE_HOLIDAY": { s.holidays = s.holidays.filter((h) => h.id !== action.id); return s; }
    case "ADD_BRANCH": { s.seq.BR = (s.seq.BR || 2) + 1; s.branches.push({ id: "BR" + s.seq.BR, name: action.name, code: action.code, type: action.btype || "Branch" }); return s; }
    case "UPDATE_BRANCH": { const br = s.branches.find((x) => x.id === action.id); if (br) Object.assign(br, action.patch); return s; }
    case "REMOVE_BRANCH": { if (s.users.some((u) => u.branchId === action.id)) return s; s.branches = s.branches.filter((b) => b.id !== action.id); return s; }
    case "UPDATE_COMPANY": { Object.assign(s.settings.company, action.patch); return s; }
    case "UPDATE_PREFERENCES": { Object.assign(s.settings.preferences, action.patch); return s; }
    case "UPDATE_NUMBERING": { s.settings.numbering[action.key] = action.prefix; return s; }
    case "ADD_UOM": { const v = (action.name || "").trim(); if (v && !s.settings.uoms.includes(v)) s.settings.uoms.push(v); return s; }
    case "REMOVE_UOM": { s.settings.uoms = s.settings.uoms.filter((u) => u !== action.name); return s; }
    case "ADD_TAX": { const nm = (action.name || "").trim(); if (!nm) return s; s.seq.TX = (s.seq.TX || 3) + 1; s.settings.taxes.push({ id: "TX" + s.seq.TX, name: nm, rate: Number(action.rate) || 0 }); return s; }
    case "REMOVE_TAX": { s.settings.taxes = s.settings.taxes.filter((t) => t.id !== action.id); return s; }
    case "ADD_SHIFT": { const nm = (action.name || "").trim(); if (!nm) return s; s.seq.SH = (s.seq.SH || 3) + 1; s.settings.shifts.push({ id: "SH-" + s.seq.SH, name: nm, start: action.start || "09:00", end: action.end || "18:00", weekOff: Number(action.weekOff) || 0 }); return s; }
    case "UPDATE_SHIFT": { const sh = s.settings.shifts.find((x) => x.id === action.id); if (sh) Object.assign(sh, action.patch); return s; }
    case "REMOVE_SHIFT": { s.settings.shifts = s.settings.shifts.filter((x) => x.id !== action.id); return s; }
    case "ADD_DEPARTMENT": { const nm = (action.name || "").trim(); if (!nm) return s; s.seq.DEPT = (s.seq.DEPT || 0) + 1; s.departments.push({ id: "DPT-" + s.seq.DEPT, name: nm, head: action.head || null }); return s; }
    case "UPDATE_DEPARTMENT": { const d = s.departments.find((x) => x.id === action.id); if (d) Object.assign(d, action.patch); return s; }
    case "REMOVE_DEPARTMENT": { s.departments = s.departments.filter((d) => d.id !== action.id); return s; }
    case "UPDATE_USER": { const u = s.users.find((x) => x.id === action.id); if (u) Object.assign(u, action.patch); return s; }
    case "TOGGLE_USER_ACTIVE": { const u = s.users.find((x) => x.id === action.id); if (u) u.active = !u.active; return s; }
    case "TOGGLE_ROLE_NAV": { const r = s.roles[action.role]; if (r && !r.can.all) { const i = r.nav.indexOf(action.module); if (i >= 0) r.nav.splice(i, 1); else r.nav.push(action.module); } return s; }
    case "TOGGLE_ROLE_PERM": { const r = s.roles[action.role]; if (r && !r.can.all) { r.can[action.perm] = !r.can[action.perm]; } return s; }
    case "ADD_ROLE": { const key = (action.key || "").trim().toLowerCase().replace(/[^a-z0-9]/g, ""); if (key && !s.roles[key]) s.roles[key] = { name: action.name || key, dept: action.dept || "General", nav: ["queue", "tasks"], can: {} }; return s; }
    case "ASSIGN_PRODUCTION": { const p = s.productionOrders.find((x) => x.id === action.poId); if (p) p.assigneeId = action.userId || null; return s; }
    case "CLAIM_PRODUCTION": { const p = s.productionOrders.find((x) => x.id === action.poId); if (p) p.assigneeId = action.userId; return s; }
    case "ROUTING_EDIT": {
      const r = s.routings[action.itemId][action.idx];
      r[action.key] = action.key === "wc" ? action.value : (Number(action.value) || 0);
      return s;
    }
    case "ROUTING_ADD": { s.routings[action.itemId].push({ wc: WORKCENTERS[0], setup: 1, per: 0.01 }); return s; }
    case "ROUTING_DEL": { s.routings[action.itemId].splice(action.idx, 1); return s; }
    case "ROUTING_MOVE": {
      const arr = s.routings[action.itemId]; const j = action.idx + action.dir;
      if (j < 0 || j >= arr.length) return s;
      [arr[action.idx], arr[j]] = [arr[j], arr[action.idx]]; return s;
    }
    case "FG_QC": {
      const bt = s.batches.find((b) => b.id === action.batchId); if (!bt || bt.status !== "QC Pending") return s;
      if (action.pass) {
        const stk = s.stock[bt.itemId]; if (stk) stk.available += bt.qty;
        bt.consumed = fgGenealogy(s, bt.itemId, bt.qty);
        bt.status = "Passed"; bt.fgQcBy = action.by || "U-2"; bt.fgQcAt = now;
        const po = s.productionOrders.find((x) => x.id === bt.poId);
        if (po && stk) { const srcIds = (po.sourceLineIds && po.sourceLineIds.length) ? po.sourceLineIds : (po.sourceLineId ? [po.sourceLineId] : []); srcIds.forEach((sid) => { const l = findLine(sid); if (l) { const need = l.qtyOrdered - l.qtyAllocated; const take = Math.min(stk.available, Math.max(0, need)); if (take > 0.0001) { stk.available -= take; stk.reserved += take; l.qtyAllocated += take; } } }); }
      } else { bt.status = "Rejected"; bt.fgQcBy = action.by || "U-2"; bt.fgQcAt = now; }
      return s;
    }
    case "ISSUE_MATERIALS": {
      const p = s.productionOrders.find((x) => x.id === action.id); if (!p || !p.woGenerated) return s;
      const remaining = p.plannedQty - (p.issuedQty || 0);
      const q = Number(action.qty) > 0 ? Number(action.qty) : Math.max(0, remaining); if (q <= 0.0001) return s;
      consumeBOMFrac(s, p, q); p.issuedQty = (p.issuedQty || 0) + q; return s;
    }
    case "RETURN_MATERIALS": {
      const p = s.productionOrders.find((x) => x.id === action.id); if (!p) return s;
      const q = Math.min(Number(action.qty) || 0, p.issuedQty || 0); if (q <= 0.0001) return s;
      returnBOMFrac(s, p, q); p.issuedQty = Math.max(0, (p.issuedQty || 0) - q); return s;
    }
    case "TRANSFER_TO_ROOM": {
      const stk = s.stock[action.itemId]; if (!stk) return s;
      const take = Math.min(Math.max(0, Number(action.qty) || 0), stk.available); if (take <= 0.0001) return s;
      stk.available -= take; const rs = (s.roomStock[action.room] = s.roomStock[action.room] || {}); rs[action.itemId] = (rs[action.itemId] || 0) + take; return s;
    }
    case "RETURN_FROM_ROOM": {
      const rs = (s.roomStock[action.room] = s.roomStock[action.room] || {}); const have = rs[action.itemId] || 0;
      const take = Math.min(Math.max(0, Number(action.qty) || 0), have); if (take <= 0.0001) return s;
      rs[action.itemId] = have - take; const stk = s.stock[action.itemId]; if (stk) stk.available += take; return s;
    }
    case "CREATE_PURCHASE": {
      const lines = (action.lines || []).filter((l) => l.itemId && Number(l.qty) > 0).map((l) => ({ itemId: l.itemId, qty: Number(l.qty) }));
      if (!lines.length) return s; s.seq.PU = (s.seq.PU || 0) + 1;
      s.purchases.unshift({ id: "PUR-" + s.seq.PU, supplier: action.supplier || "Supplier", date: now, status: "QC Pending", by: action.by || "U-2", lines });
      return s;
    }
    case "QC_PURCHASE": {
      const pu = s.purchases.find((x) => x.id === action.id); if (!pu || pu.status !== "QC Pending") return s;
      if (action.pass) {
        pu.lines.forEach((l) => { const stk = s.stock[l.itemId]; if (stk) stk.available += l.qty; const it = s.items.find((i) => i.id === l.itemId); s.batches.unshift({ id: batchNo(s, l.itemId, now), itemId: l.itemId, type: it ? it.type : "RM", qty: l.qty, remaining: l.qty, date: now, status: "Passed", qcBy: action.by || "U-2", poId: pu.id }); });
        pu.status = "Passed"; pu.qcAt = now; pu.qcBy = action.by || "U-2";
      } else { pu.status = "Rejected"; pu.qcAt = now; pu.qcBy = action.by || "U-2"; }
      return s;
    }
    case "CREATE_STOCK_PO": {
      const it = s.items.find((i) => i.id === action.itemId); if (!it) return s;
      s.seq.PO += 1;
      s.productionOrders.unshift({
        id: "PO-" + s.seq.PO, itemId: action.itemId, plannedQty: ceilLot(Number(action.qty) || 0, it.lot || 1),
        status: "Planned", sourceLineId: null, forStock: true, stepIdx: 0, woGenerated: false, issuedQty: 0,
        steps: s.routings[action.itemId].map((r) => ({ wc: r.wc, setup: r.setup, per: r.per, start: null, end: null })),
      });
      return s;
    }
    default: return state;
  }
}

/* ============================ UI atoms ============================ */
const TONE = {
  emerald: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  amber: "bg-amber-50 text-amber-700 ring-amber-600/20",
  sky: "bg-sky-50 text-sky-700 ring-sky-600/20",
  stone: "bg-slate-100 text-slate-600 ring-slate-500/20",
  rose: "bg-rose-50 text-rose-700 ring-rose-600/20",
  violet: "bg-violet-50 text-violet-700 ring-violet-600/20",
  indigo: "bg-indigo-50 text-indigo-600 ring-indigo-500/20",
};
const statusTone = (s) => ({
  Delivered: "emerald", Completed: "emerald", Ready: "emerald", Done: "emerald",
  "In Production": "amber", Planned: "amber", "In Progress": "amber",
  "In Transit": "sky", "Partially Delivered": "violet", Pending: "stone", Open: "stone",
}[s] || "stone");

const Badge = ({ tone = "stone", children }) => (
  <span className={cx("inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset", TONE[tone])}>{children}</span>
);
const Btn = ({ variant = "primary", className, children, ...p }) => {
  const v = {
    primary: "text-white", ghost: "text-slate-600 hover:bg-slate-100",
    soft: "bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50",
  }[variant];
  return <button {...p} data-primary={variant === "primary" ? "1" : undefined} style={variant === "primary" ? { backgroundColor: "#4F6FE8", boxShadow: "0 1px 2px rgba(79,111,232,0.25)" } : undefined}
    className={cx("inline-flex items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition disabled:opacity-40 disabled:cursor-not-allowed", v, className)}>{children}</button>;
};
const Card = ({ className, children }) => <div className={cx("rounded-xl bg-white ring-1 ring-slate-200/80 shadow-sm", className)}>{children}</div>;
const Th = ({ className, children }) => <th className={cx("px-4 max-md:px-2 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-400", className)}>{children}</th>;
const Td = ({ className, children, ...p }) => <td {...p} className={cx("px-4 max-md:px-2 py-3 text-sm text-slate-700", className)}>{children}</td>;
const Mono = ({ children, className }) => <span className={cx("mono", className)}>{children}</span>;

function Modal({ open, onClose, title, children, footer, wide }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/40 p-4 pt-16 max-md:pt-6" onClick={onClose}>
      <div className={cx("w-full rounded-xl bg-white shadow-2xl ring-1 ring-slate-200", wide ? "max-w-3xl" : "max-w-xl")} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3.5 max-md:px-4">
          <h3 className="text-base font-semibold text-slate-800">{title}</h3>
          <button onClick={onClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-100"><X size={18} /></button>
        </div>
        <div className="px-5 py-4 max-md:px-4">{children}</div>
        {footer && <div className="flex justify-end gap-2 border-t border-slate-100 px-5 py-3.5 max-md:px-4">{footer}</div>}
      </div>
    </div>
  );
}
const KPI = ({ icon: Icon, label, value, tone = "stone" }) => (
  <Card className="p-4">
    <div className="flex items-center justify-between">
      <span className="text-xs font-medium text-slate-400">{label}</span>
      <span className={cx("rounded-md p-1.5 ring-1 ring-inset", TONE[tone])}><Icon size={15} /></span>
    </div>
    <div className="mt-2 text-2xl font-semibold text-slate-800"><Mono>{value}</Mono></div>
  </Card>
);

/* ============================ Dashboard ============================ */
function ChartDefs() {
  return (
    <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true"><defs>
      <linearGradient id="g-em" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#6ee7b7" /><stop offset="100%" stopColor="#059669" /></linearGradient>
      <linearGradient id="g-vi" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#c4b5fd" /><stop offset="100%" stopColor="#7c3aed" /></linearGradient>
      <linearGradient id="g-ro" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#fda4af" /><stop offset="100%" stopColor="#e11d48" /></linearGradient>
      <linearGradient id="g-am" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#fcd34d" /><stop offset="100%" stopColor="#d97706" /></linearGradient>
      <linearGradient id="g-bl" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#7dd3fc" /><stop offset="100%" stopColor="#0284c7" /></linearGradient>
      <linearGradient id="g-gauge" x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stopColor="#e11d48" /><stop offset="50%" stopColor="#f59e0b" /><stop offset="100%" stopColor="#16a34a" /></linearGradient>
    </defs></svg>
  );
}
const SOLID = { emerald: "#10b981", violet: "#7c3aed", rose: "#e11d48", amber: "#d97706", blue: "#0284c7", stone: "#a8a29e" };
const GRAD = { emerald: "url(#g-em)", violet: "url(#g-vi)", rose: "url(#g-ro)", amber: "url(#g-am)", blue: "url(#g-bl)", stone: "#a8a29e" };

function Donut({ data, size = 132, center, sub }) {
  const total = data.reduce((a, d) => a + d.value, 0) || 1;
  const r = size * 0.38, cx = size / 2, cy = size / 2, C = 2 * Math.PI * r, sw = size * 0.14;
  let off = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0">
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#f1f0ec" strokeWidth={sw} />
      {data.map((d, i) => { const frac = d.value / total; const dash = frac * C; const el = <circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke={d.grad || d.color} strokeWidth={sw} strokeDasharray={`${dash} ${C - dash}`} strokeDashoffset={-off} transform={`rotate(-90 ${cx} ${cy})`} />; off += dash; return el; })}
      <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central" fontSize={size * 0.22} fontWeight="700" fill={THEME_DARK ? "rgba(255,255,255,0.92)" : "#1c1917"}>{center}</text>
      {sub && <text x={cx} y={cy + size * 0.17} textAnchor="middle" fontSize={size * 0.085} fill="#78716c">{sub}</text>}
    </svg>
  );
}
function Legend({ data }) {
  return <div className="flex flex-wrap gap-x-3 gap-y-1">{data.map((d, i) => <span key={i} className="inline-flex items-center gap-1.5 text-xs text-slate-500"><span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: d.color }} />{d.label} <Mono className="text-slate-400">{d.value}</Mono></span>)}</div>;
}
function Bars({ data, fmt }) {
  const max = Math.max(1, ...data.map((d) => d.value));
  return (
    <div className="flex h-40 items-end gap-2">
      {data.map((d, i) => (
        <div key={i} className="flex flex-1 flex-col items-center justify-end gap-1" style={{ minWidth: 0 }}>
          <span className="text-slate-500" style={{ fontSize: 10 }}>{(fmt || ((v) => v))(d.value)}</span>
          <div className="w-full rounded-t" style={{ height: (d.value / max) * 100 + "%", minHeight: d.value > 0 ? 4 : 0, background: d.color || "linear-gradient(180deg,#7B92F0,#4F6FE8)" }} />
          <span className="w-full truncate text-center text-slate-400" style={{ fontSize: 10 }} title={d.label}>{d.label}</span>
        </div>
      ))}
    </div>
  );
}
function Gauge({ value, label }) {
  const v = Math.max(0, Math.min(100, value));
  const W = 220, H = 132, cx = W / 2, cy = 120, r = 88, sw = 18;
  const polar = (f) => { const ang = Math.PI * (1 - f); return [cx + r * Math.cos(ang), cy - r * Math.sin(ang)]; };
  const [x0, y0] = polar(0), [x1, y1] = polar(1);
  const need = Math.PI * (1 - v / 100), L = r - 6;
  const nx = cx + L * Math.cos(need), ny = cy - L * Math.sin(need);
  const tone = v >= 70 ? "#16a34a" : v >= 40 ? "#d97706" : "#e11d48";
  const arc = `M ${x0} ${y0} A ${r} ${r} 0 0 1 ${x1} ${y1}`;
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H + 26}`} style={{ maxWidth: 240 }}>
      <path d={arc} fill="none" stroke="#f1f0ec" strokeWidth={sw} strokeLinecap="round" />
      <path d={arc} fill="none" stroke="url(#g-gauge)" strokeWidth={sw} strokeLinecap="round" />
      <line x1={cx} y1={cy} x2={nx} y2={ny} stroke="#44403c" strokeWidth={3.5} strokeLinecap="round" />
      <circle cx={cx} cy={cy} r={6} fill="#44403c" />
      <text x={cx} y={cy - 20} textAnchor="middle" fontSize={32} fontWeight="700" fill={tone}>{Math.round(v)}%</text>
      <text x={x0} y={cy + 16} textAnchor="middle" fontSize={10} fill="#a8a29e">0</text>
      <text x={x1} y={cy + 16} textAnchor="middle" fontSize={10} fill="#a8a29e">100</text>
      <text x={cx} y={H + 18} textAnchor="middle" fontSize={12} fill="#78716c">{label}</text>
    </svg>
  );
}

/* ===================== Dashboard — LIVE, visually rich (Phase 1b + Step B) ====
   Fetches the tenant's config (chosen widgets) → GET /metrics for the KPI keys +
   GET /charts for the chart keys → renders a KPI "pulse strip" of headline numbers
   plus a responsive grid of brand-themed recharts (donut/bar/line/gauge/hbar).
   Admins get a Customize modal that picks BOTH KPI cards AND chart widgets. All
   charts are tenant-scoped real data; perf charts skip for non-reports roles. */

const BRAND = "#4F6FE8";
const STATUS_COLORS = { "Open": "#94a3b8", "Ready": "#0284c7", "In Production": "#d97706", "In Progress": "#7c3aed", "Completed": "#16a34a" };
const STOCK_COLORS = { "Below buffer": "#dc2626", "Adequate": "#16a34a", "Overstock": "#f59e0b" };
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const monthLabel = (m) => { const p = String(m).split("-"); return MONTHS[Number(p[1]) - 1] || m; };
const weekLabel = (d) => { const p = String(d).split("-"); return `${p[2]} ${MONTHS[Number(p[1]) - 1] || ""}`; };
const fmtINRShort = (v) => { v = Number(v) || 0; const a = Math.abs(v); if (a >= 1e7) return "₹" + (v / 1e7).toFixed(2) + "Cr"; if (a >= 1e5) return "₹" + (v / 1e5).toFixed(2) + "L"; if (a >= 1e3) return "₹" + Math.round(v / 1e3) + "k"; return "₹" + Math.round(v); };
const chartTheme = (dark) => dark
  ? { axis: "#94a3b8", grid: "#2a3350", tipBg: "#161B2E", tipBd: "#2a3350", tipText: "#e2e8f0", cursor: "rgba(255,255,255,0.05)" }
  : { axis: "#64748b", grid: "#eef2f7", tipBg: "#ffffff", tipBd: "#e2e8f0", tipText: "#1e293b", cursor: "rgba(79,111,232,0.07)" };
const tipStyle = (t) => ({ background: t.tipBg, border: `1px solid ${t.tipBd}`, borderRadius: 8, fontSize: 12, color: t.tipText, boxShadow: "0 4px 14px rgba(0,0,0,0.12)" });

function fmtMetric(m) {
  if (m.value === null || m.value === undefined) return "—";
  if (m.unit === "INR") return fmtMoney(m.value);
  if (m.unit === "%") return (typeof m.value === "number" ? m.value : Number(m.value)) + "%";
  if (typeof m.value === "number") return m.value.toLocaleString("en-IN");
  return String(m.value);
}

function KpiCard({ m }) {
  if (m.key === "top_performer") {
    return (
      <Card className="p-4">
        <div className="text-xs font-medium text-slate-400">{m.label}</div>
        <div className="mt-2 truncate text-xl font-semibold text-slate-800">{m.meta?.name || "—"}</div>
        <div className="mt-0.5 text-xs text-slate-500">{fmtMetric(m)} jobs{m.meta?.onTimeRate != null ? ` · ${m.meta.onTimeRate}% on-time` : ""}</div>
      </Card>
    );
  }
  if (m.key === "perf_leaderboard") {
    const list = m.meta?.leaderboard || [];
    return (
      <Card className="p-4 sm:col-span-2">
        <div className="mb-2 text-xs font-medium text-slate-400">{m.label}</div>
        {list.length === 0 ? <p className="text-sm text-slate-400">No completed jobs.</p> : (
          <div className="space-y-1">
            {list.map((e, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span className="truncate text-slate-700"><span className="mr-1.5 text-slate-400">{i + 1}.</span>{e.name || "—"}</span>
                <span className="shrink-0 text-xs text-slate-500"><Mono>{e.completed}</Mono> done{e.onTimeRate != null ? ` · ${e.onTimeRate}%` : ""}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    );
  }
  const accent = m.unit === "INR" ? "text-indigo-600" : m.unit === "%" ? "text-emerald-600" : "text-slate-800";
  return (
    <Card className="p-4">
      <div className="text-xs font-medium text-slate-400">{m.label}</div>
      <div className={cx("mt-2 text-2xl font-semibold", accent)}><Mono>{fmtMetric(m)}</Mono></div>
      {m.unit && m.unit !== "INR" && m.unit !== "%" ? <div className="mt-0.5 text-xs text-slate-400">{m.unit}</div> : null}
    </Card>
  );
}

const ChartEmpty = ({ label }) => <div className="flex h-[180px] items-center justify-center text-sm text-slate-400">{label}</div>;

function OrdersDonut({ data, dark }) {
  const t = chartTheme(dark);
  const total = (data || []).reduce((a, d) => a + d.count, 0);
  const shown = (data || []).filter((d) => d.count > 0);
  if (total === 0) return <ChartEmpty label="No orders" />;
  return (
    <div>
      <div className="relative">
        <ResponsiveContainer width="100%" height={190}>
          <PieChart>
            <Pie data={shown} dataKey="count" nameKey="status" innerRadius={56} outerRadius={82} paddingAngle={2} stroke="none">
              {shown.map((d, i) => <Cell key={i} fill={STATUS_COLORS[d.status] || BRAND} />)}
            </Pie>
            <Tooltip contentStyle={tipStyle(t)} itemStyle={{ color: t.tipText }} />
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <div className="text-2xl font-semibold text-slate-800"><Mono>{total}</Mono></div>
          <div className="text-xs text-slate-400">orders</div>
        </div>
      </div>
      <div className="mt-2 flex flex-wrap justify-center gap-x-3 gap-y-1">
        {shown.map((d) => (
          <span key={d.status} className="flex items-center gap-1 text-xs text-slate-500">
            <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: STATUS_COLORS[d.status] || BRAND }} />{d.status} <Mono className="text-slate-400">{d.count}</Mono>
          </span>
        ))}
      </div>
    </div>
  );
}

function MoneyBarChart({ data, dark }) {
  const t = chartTheme(dark);
  return (
    <ResponsiveContainer width="100%" height={210}>
      <BarChart data={data} margin={{ top: 8, right: 8, left: 4, bottom: 0 }}>
        <CartesianGrid vertical={false} stroke={t.grid} />
        <XAxis dataKey="month" tickFormatter={monthLabel} tick={{ fill: t.axis, fontSize: 11 }} axisLine={false} tickLine={false} />
        <YAxis tickFormatter={fmtINRShort} tick={{ fill: t.axis, fontSize: 11 }} axisLine={false} tickLine={false} width={52} />
        <Tooltip formatter={(v) => [fmtMoney(v), "Invoiced"]} labelFormatter={monthLabel} contentStyle={tipStyle(t)} labelStyle={{ color: t.tipText }} cursor={{ fill: t.cursor }} />
        <Bar dataKey="value" fill={BRAND} radius={[4, 4, 0, 0]} maxBarSize={44} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function OutputLineChart({ data, dark }) {
  const t = chartTheme(dark);
  return (
    <ResponsiveContainer width="100%" height={210}>
      <LineChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
        <CartesianGrid vertical={false} stroke={t.grid} />
        <XAxis dataKey="weekStart" tickFormatter={weekLabel} tick={{ fill: t.axis, fontSize: 10 }} axisLine={false} tickLine={false} minTickGap={8} />
        <YAxis allowDecimals={false} tick={{ fill: t.axis, fontSize: 11 }} axisLine={false} tickLine={false} width={28} />
        <Tooltip formatter={(v) => [v, "Completed"]} labelFormatter={weekLabel} contentStyle={tipStyle(t)} labelStyle={{ color: t.tipText }} cursor={{ stroke: t.axis, strokeDasharray: "3 3" }} />
        <Line type="monotone" dataKey="completed" stroke={BRAND} strokeWidth={2.5} dot={{ r: 3, fill: BRAND, strokeWidth: 0 }} activeDot={{ r: 5 }} />
      </LineChart>
    </ResponsiveContainer>
  );
}

function BandBarChart({ data, dark, xKey, colors }) {
  const t = chartTheme(dark);
  return (
    <ResponsiveContainer width="100%" height={200}>
      <BarChart data={data} margin={{ top: 16, right: 8, left: 4, bottom: 0 }}>
        <CartesianGrid vertical={false} stroke={t.grid} />
        <XAxis dataKey={xKey} tick={{ fill: t.axis, fontSize: 11 }} axisLine={false} tickLine={false} interval={0} />
        <YAxis allowDecimals={false} tick={{ fill: t.axis, fontSize: 11 }} axisLine={false} tickLine={false} width={28} />
        <Tooltip contentStyle={tipStyle(t)} labelStyle={{ color: t.tipText }} cursor={{ fill: t.cursor }} />
        <Bar dataKey="count" radius={[4, 4, 0, 0]} maxBarSize={56}>
          {data.map((d, i) => <Cell key={i} fill={(colors && colors[d[xKey]]) || BRAND} />)}
          <LabelList dataKey="count" position="top" fill={t.axis} fontSize={11} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function LeaderboardHBar({ data, dark }) {
  const t = chartTheme(dark);
  const h = Math.max(140, data.length * 40);
  return (
    <ResponsiveContainer width="100%" height={h}>
      <BarChart data={data} layout="vertical" margin={{ top: 4, right: 40, left: 4, bottom: 4 }}>
        <XAxis type="number" hide allowDecimals={false} />
        <YAxis type="category" dataKey="name" width={96} tick={{ fill: t.axis, fontSize: 11 }} axisLine={false} tickLine={false} />
        <Tooltip formatter={(v, _n, p) => [`${v} done · ${p?.payload?.onTimeRate ?? "—"}% on-time`, p?.payload?.name]} contentStyle={tipStyle(t)} labelStyle={{ color: t.tipText }} cursor={{ fill: t.cursor }} />
        <Bar dataKey="completed" fill={BRAND} radius={[0, 4, 4, 0]} maxBarSize={24}>
          <LabelList dataKey="completed" position="right" fill={t.axis} fontSize={11} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function GaugeChart({ value, target, dark }) {
  const t = chartTheme(dark);
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  const color = v >= 90 ? "#16a34a" : v >= 70 ? "#f59e0b" : "#dc2626";
  const label = Number.isInteger(v) ? String(v) : v.toFixed(1);
  return (
    <div className="flex flex-col items-center py-2">
      <svg viewBox="0 0 200 120" width="100%" height={150} style={{ maxWidth: 260 }}>
        <path d="M20 100 A80 80 0 0 1 180 100" fill="none" stroke={t.grid} strokeWidth={16} strokeLinecap="round" />
        <path d="M20 100 A80 80 0 0 1 180 100" fill="none" stroke={color} strokeWidth={16} strokeLinecap="round" pathLength={100} strokeDasharray={`${v} 100`} />
        <text x="100" y="88" textAnchor="middle" fontSize="34" fontWeight="700" fill={color}>{label}%</text>
        <text x="100" y="110" textAnchor="middle" fontSize="11" fill={t.axis}>target {target}%</text>
      </svg>
    </div>
  );
}

const WIDE_CHARTS = new Set(["invoiced_value_by_month", "output_over_time", "perf_leaderboard_bar"]);

function ChartWidget({ chart, dark }) {
  let body;
  switch (chart.key) {
    case "orders_by_status": body = <OrdersDonut data={chart.data} dark={dark} />; break;
    case "invoiced_value_by_month": body = <MoneyBarChart data={chart.data} dark={dark} />; break;
    case "output_over_time": body = <OutputLineChart data={chart.data} dark={dark} />; break;
    case "stock_health": body = <BandBarChart data={chart.data} dark={dark} xKey="band" colors={STOCK_COLORS} />; break;
    case "production_by_stage": body = (chart.data || []).length ? <BandBarChart data={chart.data} dark={dark} xKey="room" /> : <ChartEmpty label="No production in progress" />; break;
    case "on_time_gauge": body = <GaugeChart value={chart.data?.value} target={chart.data?.target} dark={dark} />; break;
    case "perf_leaderboard_bar": body = (chart.data || []).length ? <LeaderboardHBar data={chart.data} dark={dark} /> : <ChartEmpty label="No completed jobs" />; break;
    default: body = <ChartEmpty label="—" />;
  }
  return (
    <Card className={cx("p-4", WIDE_CHARTS.has(chart.key) && "lg:col-span-2")}>
      <div className="mb-2 text-sm font-semibold text-slate-700">{chart.label}</div>
      {body}
    </Card>
  );
}

function CustomizeDashboardModal({ selected, onClose, onSaved }) {
  const cat = useResource(() => dashboardApi.getCatalog(), []);
  const all = cat.data?.metrics || [];
  const [sel, setSel] = useState(() => new Set(selected));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const toggle = (key) => setSel((s) => { const nx = new Set(s); nx.has(key) ? nx.delete(key) : nx.add(key); return nx; });
  const save = async () => {
    setBusy(true); setErr("");
    try {
      const keys = all.filter((m) => sel.has(m.key)).map((m) => m.key); // catalog order
      await dashboardApi.putConfig(keys);
      onSaved(keys);
    } catch (e) { setErr(e instanceof ApiError ? e.message : "Could not save."); setBusy(false); }
  };
  const sections = [["kpi", "KPI cards"], ["chart", "Charts"]];
  return (
    <Modal open onClose={onClose} wide title="Customize dashboard"
      footer={<><span className="mr-auto text-xs text-slate-400">{sel.size} selected</span><Btn variant="soft" onClick={onClose} disabled={busy}>Cancel</Btn><Btn onClick={save} disabled={busy}>{busy ? "Saving…" : "Save"}</Btn></>}>
      {cat.loading && all.length === 0 ? <LoadingBlock label="Loading widgets…" /> : cat.error ? <InlineError message={cat.error} onRetry={cat.reload} /> : (
        <div className="space-y-4">
          <p className="text-xs text-slate-400">Pick the number cards and charts for the company dashboard. Performance widgets appear only for users with Reports access.</p>
          {sections.map(([wt, label]) => {
            const rows = all.filter((m) => (m.widgetType || "kpi") === wt);
            if (!rows.length) return null;
            return (
              <div key={wt}>
                <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</div>
                <div className="grid gap-1.5 sm:grid-cols-2">
                  {rows.map((m) => (
                    <label key={m.key} className={cx("flex cursor-pointer items-start gap-2 rounded-lg px-3 py-2 ring-1", sel.has(m.key) ? "bg-indigo-50/60 ring-indigo-200" : "ring-slate-200 hover:bg-slate-50")}>
                      <input type="checkbox" checked={sel.has(m.key)} onChange={() => toggle(m.key)} className="mt-0.5 accent-indigo-600" />
                      <span className="min-w-0">
                        <span className="flex items-center gap-1.5 text-sm font-medium text-slate-700">{m.label}<span className={cx("rounded px-1 py-px text-[10px] font-normal", m.category === "performance" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500")}>{m.category === "performance" ? "Perf" : "Ops"}</span></span>
                        {m.description ? <span className="block text-xs text-slate-400">{m.description}</span> : null}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
          {err ? <InlineError message={err} /> : null}
        </div>
      )}
    </Modal>
  );
}

function Dashboard({ auth, dark }) {
  const isAdmin = !!(auth?.role?.permissions?.includes("*") || (Array.isArray(auth?.role?.navModules) && auth.role.navModules.includes("settings")));
  const [config, setConfig] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [charts, setCharts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [customize, setCustomize] = useState(false);

  const load = React.useCallback(async () => {
    setLoading(true); setError("");
    try {
      // ONE request: config + metrics + charts, computed from a single shared
      // data load server-side. This was three calls — getConfig blocking, then
      // metrics and charts each re-loading the same nine tables.
      const { config: cfg, metrics: m, charts: c } = await dashboardApi.getSummary();
      setConfig(cfg); setKpis(m); setCharts(c);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not load the dashboard.");
    } finally { setLoading(false); }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const kpiCards = kpis?.metrics || [];
  const chartCards = charts?.charts || [];
  const gatedCount = new Set([...(kpis?.meta?.gated || []), ...(charts?.meta?.gated || [])]).size;
  const nothing = kpiCards.length === 0 && chartCards.length === 0;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <div className="flex items-center gap-2 text-lg font-semibold text-slate-800"><LayoutDashboard size={18} className="text-indigo-500" /> Dashboard</div>
          <div className="text-xs text-slate-400">{auth?.company?.name ? auth.company.name + " · " : ""}Live KPIs &amp; charts{config?.isDefault ? " · default set" : ""}</div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {loading ? <Spinner size={14} /> : null}
          {isAdmin
            ? <Btn onClick={() => setCustomize(true)}><Settings size={14} /> Customize</Btn>
            : <span className="text-xs text-slate-400">Dashboard set by your admin</span>}
        </div>
      </div>

      {error ? <InlineError message={error} onRetry={load} /> : null}

      {loading && !kpis ? (
        <LoadingBlock label="Loading dashboard…" />
      ) : nothing && !error ? (
        <Card className="p-8 text-center text-sm text-slate-400">
          No widgets selected. {isAdmin ? "Click Customize to choose KPI cards and charts." : "Your admin hasn't set up the dashboard yet."}
        </Card>
      ) : (
        <>
          {kpiCards.length > 0 ? (
            <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
              {kpiCards.map((m) => <KpiCard key={m.key} m={m} />)}
            </div>
          ) : null}
          {chartCards.length > 0 ? (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {chartCards.map((c) => <ChartWidget key={c.key} chart={c} dark={dark} />)}
            </div>
          ) : null}
        </>
      )}

      {gatedCount > 0 ? (
        <p className="text-xs text-slate-400">{gatedCount} performance widget{gatedCount === 1 ? "" : "s"} hidden — needs Reports access.</p>
      ) : null}

      {customize && (
        <CustomizeDashboardModal
          selected={config?.selectedKeys || []}
          onClose={() => setCustomize(false)}
          onSaved={() => { setCustomize(false); load(); }}
        />
      )}
    </div>
  );
}


/* ============================ Orders ============================ */
/* ===================== Sales Orders — LIVE (W3) =====================
   OrdersView (list) + OrderDrawer (detail) + NewOrderModal (create) source from
   the real /orders + /parties + /items APIs. Status shown is the backend's
   DERIVED status — the client-side orderStatus()/lineStatus() helpers are NOT
   called here (they remain for the still-mock screens). The per-line FULFILL /
   "Stock decision" path is left on the mock reducer untouched (lights up W4);
   it is fed a shim line so opening a real order's drawer never errors. */

const SO_CHANNELS = ["WhatsApp", "Call", "Email", "Portal"];
const ORDER_STATUS_FILTERS = ["Open", "Ready", "In Production", "In Progress", "Completed"];

function OrdersView({ st, dispatch, party, role, auth }) {
  // Only a createOrder-holder may create an order — gate the TRIGGER, so roles that
  // merely have the "orders" nav (prodmgr/dispatch/planner) don't open a modal they
  // can't submit (and previously met empty pickers in).
  const canCreateOrder = authCan(auth, role, "createOrder");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [channel, setChannel] = useState("");
  const [open, setOpen] = useState(false);
  const [drawer, setDrawer] = useState(null); // order code

  const { data, loading, error, reload } = useResource(
    () => ordersApi.listOrders({ q: q.trim() || undefined, status: status || undefined, channel: channel || undefined }),
    [q, status, channel],
  );
  const rows = data?.orders || [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search size={15} className="absolute left-2.5 top-2.5 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search orders…"
            className="w-60 rounded-md bg-white py-1.5 pl-8 pr-3 text-sm text-slate-700 ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-md bg-white py-1.5 px-2.5 text-sm text-slate-700 ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400">
          <option value="">All statuses</option>
          {ORDER_STATUS_FILTERS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={channel} onChange={(e) => setChannel(e.target.value)} className="rounded-md bg-white py-1.5 px-2.5 text-sm text-slate-700 ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400">
          <option value="">All channels</option>
          {SO_CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <div className="ml-auto flex items-center gap-2 text-xs text-slate-400">
          {loading && rows.length > 0 ? <Spinner size={14} /> : null}
          <span>{data ? `${data.total} order${data.total === 1 ? "" : "s"}` : ""}</span>
          {canCreateOrder && <Btn onClick={() => setOpen(true)}><Plus size={15} /> New order</Btn>}
        </div>
      </div>

      {error ? <InlineError message={error} onRetry={reload} /> : null}
      <Card className="overflow-x-auto">
        {loading && rows.length === 0 ? (
          <LoadingBlock label="Loading orders…" />
        ) : (
          <table className="w-full">
            <thead className="border-b border-slate-100"><tr><Th>Order</Th><Th>Customer</Th><Th>Channel</Th><Th>Lines</Th><Th>Status</Th><Th>Date</Th><Th /></tr></thead>
            <tbody className="divide-y divide-slate-50">
              {rows.map((o) => (
                <tr key={o.code} className="cursor-pointer hover:bg-slate-50/70" onClick={() => setDrawer(o.code)}>
                  <Td><Mono className="font-medium text-slate-800">{o.code}</Mono></Td>
                  <Td><div className="font-medium text-slate-700">{o.party?.name}</div><div className="text-xs text-slate-400"><Mono>{o.party?.code}</Mono></div></Td>
                  <Td className="text-slate-500">{o.channel}</Td>
                  <Td><Mono>{o.lineCount}</Mono></Td>
                  <Td><Badge tone={statusTone(o.status)}>{o.status}</Badge></Td>
                  <Td className="text-slate-500">{fmtDate(o.orderDate)}</Td>
                  <Td className="text-right"><ChevronRight size={16} className="text-slate-300" /></Td>
                </tr>
              ))}
              {rows.length === 0 && !loading && <tr><Td className="text-slate-400">No orders match.</Td></tr>}
            </tbody>
          </table>
        )}
      </Card>

      <NewOrderModal open={open} onClose={() => setOpen(false)} onCreated={() => { setOpen(false); reload(); }} />
      {drawer && <OrderDrawer st={st} code={drawer} party={party} dispatch={dispatch} role={role} onClose={() => setDrawer(null)} onChanged={reload} />}
    </div>
  );
}

function NewOrderModal({ open, onClose, onCreated }) {
  const [partyCode, setPartyCode] = useState("");
  const [channel, setChannel] = useState("WhatsApp");
  const [lines, setLines] = useState([{ itemCode: "", qty: 50, days: 4 }]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Real pickers — fetched on open, cached for the modal's lifecycle.
  const partyRes = useResource(() => (open ? partiesApi.listParties() : Promise.resolve(null)), [open]);
  const itemRes = useResource(() => (open ? catalog.listItems({ type: "FG" }) : Promise.resolve(null)), [open]);
  const partyList = partyRes.data?.parties || [];
  const fgList = itemRes.data?.items || [];
  const dataLoading = open && (partyRes.loading || itemRes.loading);
  const loadErr = partyRes.error || itemRes.error;

  React.useEffect(() => {
    if (!open) return;
    setError(""); setBusy(false); setChannel("WhatsApp");
  }, [open]);
  // Default the pickers to the first option once each list lands.
  React.useEffect(() => { if (partyList.length && !partyCode) setPartyCode(partyList[0].code); }, [partyList, partyCode]);
  React.useEffect(() => { if (fgList.length) setLines((ls) => ls.map((l) => (l.itemCode ? l : { ...l, itemCode: fgList[0].code }))); }, [fgList]);

  const setLine = (i, k, v) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l)));

  const submit = async () => {
    if (busy) return;
    setError("");
    if (!partyCode) { setError("Select a customer."); return; }
    const valid = lines.filter((l) => l.itemCode && Number(l.qty) > 0);
    if (!valid.length) { setError("Add at least one line with a quantity."); return; }
    setBusy(true);
    try {
      await ordersApi.createOrder({
        partyCode,
        channel,
        lines: valid.map((l) => ({ itemCode: l.itemCode, qty: Number(l.qty), days: l.days === "" ? undefined : Number(l.days) })),
      });
      setLines([{ itemCode: fgList[0]?.code || "", qty: 50, days: 4 }]);
      onCreated();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not create the order.");
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="New sales order" wide
      footer={<><Btn variant="soft" onClick={onClose}>Cancel</Btn><Btn onClick={submit} disabled={busy || dataLoading}>{busy ? "Creating…" : "Create order"}</Btn></>}>
      {dataLoading ? (
        <LoadingBlock label="Loading customers & products…" />
      ) : loadErr ? (
        <InlineError message={loadErr} onRetry={() => { partyRes.reload(); itemRes.reload(); }} />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-sm"><span className="mb-1 block font-medium text-slate-600">Customer</span>
              <select value={partyCode} onChange={(e) => setPartyCode(e.target.value)} className="w-full rounded-md bg-white py-1.5 px-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400">
                {partyList.length === 0 && <option value="">No customers yet</option>}
                {partyList.map((p) => <option key={p.code} value={p.code}>{p.name}{p.parentCode ? ` (↳ ${p.parentCode})` : ""}</option>)}
              </select></label>
            <label className="text-sm"><span className="mb-1 block font-medium text-slate-600">Channel</span>
              <select value={channel} onChange={(e) => setChannel(e.target.value)} className="w-full rounded-md bg-white py-1.5 px-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400">
                {SO_CHANNELS.map((c) => <option key={c}>{c}</option>)}
              </select></label>
          </div>
          <div className="mt-4 space-y-2">
            <div className="grid grid-cols-12 gap-2 text-xs font-semibold uppercase text-slate-400"><span className="col-span-6">Product (FG)</span><span className="col-span-3">Qty (Kg)</span><span className="col-span-2">Need in (d)</span><span /></div>
            {lines.map((l, i) => (
              <div key={i} className="grid grid-cols-12 items-center gap-2">
                <select className="col-span-6 rounded-md bg-white py-1.5 px-2.5 text-sm ring-1 ring-slate-200" value={l.itemCode} onChange={(e) => setLine(i, "itemCode", e.target.value)}>
                  {fgList.length === 0 && <option value="">No FG items</option>}
                  {fgList.map((it) => <option key={it.code} value={it.code}>{it.name}</option>)}
                </select>
                <input type="number" className="col-span-3 mono rounded-md bg-white py-1.5 px-2.5 text-sm ring-1 ring-slate-200" value={l.qty} onChange={(e) => setLine(i, "qty", e.target.value)} />
                <input type="number" className="col-span-2 mono rounded-md bg-white py-1.5 px-2.5 text-sm ring-1 ring-slate-200" value={l.days} onChange={(e) => setLine(i, "days", e.target.value)} />
                <button className="col-span-1 text-slate-300 hover:text-rose-500" onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}><X size={16} /></button>
              </div>
            ))}
            <button className="mt-1 text-sm font-medium text-amber-700 hover:underline" onClick={() => setLines((ls) => [...ls, { itemCode: fgList[0]?.code || "", qty: 50, days: 4 }])}>+ Add line</button>
          </div>
          <p className="mt-3 text-xs text-slate-400">Rate auto-fills from the catalog price list; the new order opens as <b>Open</b>.</p>
          {error ? <InlineError message={error} /> : null}
        </>
      )}
    </Modal>
  );
}

function OrderDrawer({ st, code, party, dispatch, role, onClose, onChanged }) {
  const { data, loading, error, reload } = useResource(() => ordersApi.getOrder(code), [code]);
  const order = data?.order;
  const [decide, setDecide] = useState(null);
  const dcInvoice = (dcId) => st.invoices.find((iv) => iv.deliveryId === dcId);
  const TR = ({ dot, children }) => (
    <div className="flex items-start gap-2 text-xs"><span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: dot }} /><span className="text-slate-600">{children}</span></div>
  );
  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-slate-900/30" onClick={onClose}>
      <div className="h-full w-full max-w-lg overflow-y-auto bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <div>
            <div className="flex items-center gap-2">
              <Mono className="text-lg font-semibold text-slate-800">{order ? order.code : code}</Mono>
              {order && <Badge tone={statusTone(order.status)}>{order.status}</Badge>}
            </div>
            {order && <div className="mt-0.5 text-sm text-slate-500">{order.party?.name} · {order.channel} · {fmtDate(order.orderDate)}</div>}
          </div>
          <button onClick={onClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-100"><X size={18} /></button>
        </div>

        {loading && !order ? (
          <LoadingBlock label="Loading order…" />
        ) : error ? (
          <div className="p-5"><InlineError message={error} onRetry={reload} /></div>
        ) : order ? (
          <div className="space-y-3 p-5">
            {order.lines.map((l) => {
              const itemCode = l.item?.code;
              // Free-stock + trace still read the MOCK reducer (real stock/PO/DC
              // land in W4); guard so a real order never crashes the drawer.
              const stk = st.stock[itemCode];
              const avail = stk ? stk.available : 0;
              const linePOs = st.productionOrders.filter((po) => po.sourceLineId === l.lineCode);
              const lineDCs = st.deliveries.filter((d) => d.lines.some((dl) => dl.soLineId === l.lineCode));
              const traced = linePOs.length || lineDCs.length;
              // Shim line for the still-mock Stock-decision/FULFILL modal (W4).
              const shimLine = { id: l.lineCode, itemId: itemCode, itemName: l.item?.name, qtyOrdered: l.qtyOrdered, qtyAllocated: l.qtyAllocated, qtyDispatched: l.qtyDispatched, qtyDelivered: l.qtyDelivered };
              return (
                <div key={l.lineCode} className="rounded-lg ring-1 ring-slate-200">
                  <div className="flex items-center justify-between px-4 py-3">
                    <div><div className="font-medium text-slate-800">{l.item?.name}</div><div className="text-xs text-slate-400"><Mono>{itemCode}</Mono> · {fmtMoney(l.rate)}/Kg{stk ? <> · free stock <Mono>{fmtQty(avail)}</Mono> Kg</> : ""}</div></div>
                    <Badge tone={statusTone(l.status)}>{l.status}</Badge>
                  </div>
                  <div className="grid grid-cols-4 gap-px overflow-hidden bg-slate-100 text-center text-xs">
                    {[["Ordered", l.qtyOrdered], ["Allocated", l.qtyAllocated], ["Dispatched", l.qtyDispatched], ["Delivered", l.qtyDelivered]].map(([k, v]) => (
                      <div key={k} className="bg-white py-2"><div className="text-slate-400">{k}</div><Mono className="font-medium text-slate-700">{fmtQty(v)}</Mono></div>
                    ))}
                  </div>
                  {traced ? (
                    <div className="space-y-1.5 border-t border-slate-100 px-4 py-3">
                      <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Trace</div>
                      {linePOs.map((po) => <TR key={po.id} dot="#d97706"><Mono>{po.id}</Mono> · batch {fmtQty(po.plannedQty)} Kg · {po.status}{po.status === "In Production" ? ` · ${po.steps[po.stepIdx].wc} (${po.stepIdx + 1}/${po.steps.length})` : ""}</TR>)}
                      {lineDCs.map((d) => { const dl = d.lines.find((x) => x.soLineId === l.lineCode); const inv = dcInvoice(d.id); return <TR key={d.id} dot="#0284c7"><Mono>{d.id}</Mono> · {fmtQty(dl.qty)} Kg · {d.status}{inv ? ` · billed ` : ""}{inv ? <Mono>{inv.id}</Mono> : ""}</TR>; })}
                    </div>
                  ) : null}
                  {l.qtyAllocated < l.qtyOrdered && (
                    <div className="border-t border-slate-100 px-4 py-2.5">
                      <p className="text-center text-xs text-slate-400"><Factory size={12} className="mr-1 inline text-amber-500" />{fmtQty(l.qtyOrdered - l.qtyAllocated)} Kg pending — decide <b>Stock / Production / Both</b> in <b>Production → WO Pending</b>.</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : null}

      </div>
    </div>
  );
}

/* ============================ Production ============================ */
/* ============================ Work Order (pre-start detail: BOM materials + routing) ============================ */
function WorkOrderModal({ st, po, open, onClose, dispatch, role }) {
  if (!po) return null;
  const item = (id) => itemById(st.items, id);
  const bom = st.boms[po.itemId];
  const route = st.routings[po.itemId] || [];
  const f = bom ? po.plannedQty / bom.outputQty : 0;
  const comp = bom ? bom.comp.map(([cid, q]) => ({ cid, q: q * f })) : [];
  const pack = bom ? bom.pack.map(([cid, q]) => ({ cid, q: q * f })) : [];
  const totalTat = route.reduce((a, s) => a + stepStd(s, po.plannedQty), 0);
  const o = po.sourceLineId ? st.orders.find((x) => x.lines.some((l) => l.id === po.sourceLineId)) : null;
  const srcLabel = o ? o.id + " · " + (st.parties.find((pp) => pp.id === o.partyId) || { name: "—" }).name : "Make-to-stock";

  const MatTable = ({ title, rows, icon: Icon, tone }) => (
    <div className="rounded-lg ring-1 ring-slate-200 overflow-x-auto">
      <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2"><Icon size={14} className={tone} /><span className="text-sm font-semibold text-slate-700">{title}</span></div>
      <table className="w-full">
        <thead><tr><Th>Material</Th><Th>Type</Th><Th className="text-right">Required (per BOM)</Th><Th className="text-right">In stock</Th><Th>Status</Th></tr></thead>
        <tbody className="divide-y divide-slate-50">
          {rows.length === 0 ? <tr><Td className="text-slate-400">— none —</Td></tr> : rows.map(({ cid, q }) => {
            const it = item(cid); const av = st.stock[cid] ? st.stock[cid].available : 0; const short = av < q - 0.001;
            return (
              <tr key={cid}>
                <Td className="font-medium text-slate-700">{it ? it.name : cid}</Td>
                <Td><Badge tone={it && it.type === "SFG" ? "violet" : it && it.type === "PM" ? "sky" : "stone"}>{it ? it.type : "RM"}</Badge></Td>
                <Td className="text-right"><Mono className="font-medium text-slate-800">{fmtQty(q)}</Mono> Kg</Td>
                <Td className="text-right"><Mono className={short ? "text-rose-600" : "text-slate-500"}>{fmtQty(av)}</Mono></Td>
                <Td>{short ? <Badge tone="rose"><AlertTriangle size={11} /> Short {fmtQty(q - av)}{it && it.type === "SFG" ? " · make SFG first" : ""}</Badge> : <Badge tone="emerald">OK</Badge>}</Td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <Modal open={open} onClose={onClose} title={"Work order — " + po.id + (po.woNo ? " · " + po.woNo : "")} wide
      footer={<>
        <Btn variant="soft" onClick={onClose}>Close</Btn>
        {po.status === "Planned" && !po.woGenerated && can(role, "startProd") && <Btn onClick={() => dispatch({ type: "GENERATE_WO", id: po.id })}><ListOrdered size={14} /> Generate work order</Btn>}
        {po.woGenerated && po.status !== "Done" && (po.issuedQty || 0) < po.plannedQty - 0.001 && can(role, "issue") && <Btn variant="soft" onClick={() => dispatch({ type: "ISSUE_MATERIALS", id: po.id })}><Package size={14} /> Issue all remaining</Btn>}
        {po.status === "Planned" && po.woGenerated && can(role, "startProd") && <Btn onClick={() => { dispatch({ type: "START_PO", id: po.id }); onClose(); }}><Factory size={14} /> Start production</Btn>}
      </>}>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className="rounded-lg bg-slate-50 p-3"><div className="text-xs text-slate-400">Product</div><div className="text-sm font-medium text-slate-700">{item(po.itemId).name}</div></div>
          <div className="rounded-lg bg-slate-50 p-3"><div className="text-xs text-slate-400">Planned output</div><Mono className="text-sm font-semibold text-slate-800">{fmtQty(po.plannedQty)} Kg</Mono></div>
          <div className="rounded-lg bg-emerald-50 p-3"><div className="text-xs text-emerald-700">BOM yield</div><Mono className="text-sm font-semibold text-emerald-700">{bom ? bom.yield : "—"}%</Mono></div>
          <div className="rounded-lg bg-slate-50 p-3"><div className="text-xs text-slate-400">For</div><div className="truncate text-sm font-medium text-slate-700">{srcLabel}</div></div>
        </div>

        <MatTable title="Raw / sub-assembly needed (issued during production)" rows={comp} icon={Boxes} tone="text-amber-600" />
        <MatTable title="Packaging material needed" rows={pack} icon={Package} tone="text-sky-600" />

        <div className="rounded-lg ring-1 ring-slate-200">
          <div className="flex flex-wrap items-center justify-between gap-1 border-b border-slate-100 px-3 py-2">
            <div className="flex items-center gap-2"><GitBranch size={14} className="text-violet-600" /><span className="text-sm font-semibold text-slate-700">Process steps (routing)</span></div>
            <span className="text-xs text-slate-400">Total ~<Mono className="text-slate-600">{totalTat.toFixed(1)}</Mono> h · runs in Production dept</span>
          </div>
          <ol className="divide-y divide-slate-50">
            {route.length === 0 ? <li className="px-3 py-2 text-sm text-slate-400">No routing defined for this product.</li> : route.map((s, i) => (
              <li key={i} className="flex items-center justify-between px-3 py-2.5 text-sm">
                <span className="flex items-center gap-2"><span className="flex h-5 w-5 items-center justify-center rounded-full bg-violet-100 text-xs font-semibold text-violet-700">{i + 1}</span><span className="font-medium text-slate-700">{s.wc}</span>{i + 1 < route.length && <ArrowRight size={12} className="text-slate-300" />}</span>
                <span className="text-xs text-slate-400">setup {s.setup}h + {s.per}h/Kg → <Mono className="text-slate-600">{stepStd(s, po.plannedQty).toFixed(1)} h</Mono></span>
              </li>
            ))}
          </ol>
        </div>

        <p className="text-xs text-slate-400">Flow: <b>Generate work order</b> → <b>Store Manager</b> <b>issues</b> raw material (full or partial — <b>before or after</b> Start; whatever is issued is deducted from RM stock at that time) → <b>Start production</b> → step-by-step "Next" → "Finish" on the last step → <Mono>{fmtQty(po.plannedQty)}</Mono> Kg is added to finished stock{po.sourceLineId ? " (against this SO, so it is allocated directly for dispatch)" : ""}. Any remaining RM at Finish is auto-consumed.</p>
      </div>
    </Modal>
  );
}

function AssignCtrl({ st, po, dispatch, me }) {
  const resp = poResp(st, po);
  const cur = po.steps[po.stepIdx];
  const ops = wcOpsOf(st, cur ? cur.wc : "");
  const mine = resp && me && resp.id === me.id;
  const canClaim = me && me.role === "production" && po.assigneeId !== me.id;
  return (
    <div className="mt-2 rounded-md bg-slate-50 p-2 text-xs ring-1 ring-slate-200/70">
      <div className="flex items-center justify-between"><span className="text-slate-400">Responsible{po.assigneeId ? "" : " · auto"}</span>{mine && <Badge tone="emerald">You</Badge>}</div>
      <div className="mt-0.5 font-medium text-slate-700">{resp ? resp.name : "— no operator in " + (cur ? cur.wc : "WC")}</div>
      <div className="mt-1.5 flex items-center gap-1.5">
        <select value={po.assigneeId || ""} onChange={(e) => dispatch({ type: "ASSIGN_PRODUCTION", poId: po.id, userId: e.target.value })} className="min-w-0 flex-1 rounded bg-white py-1 px-1.5 text-xs ring-1 ring-slate-200">
          <option value="">Auto · {cur ? cur.wc : "WC"}</option>
          {ops.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        {canClaim && <button onClick={() => dispatch({ type: "CLAIM_PRODUCTION", poId: po.id, userId: me.id })} className="shrink-0 rounded bg-amber-100 px-2 py-1 font-medium text-amber-800 hover:bg-amber-200">Claim</button>}
      </div>
    </div>
  );
}

function StockDecisionModal({ st, line, order, open, onClose, dispatch, party, live, onAllocated }) {
  if (!line) return null;
  const item = (id) => st.items.find((i) => i.id === id);
  const fresh = st.orders.flatMap((o) => o.lines).find((x) => x.id === line.id) || line;
  const need = fresh.qtyOrdered - fresh.qtyAllocated;
  // When opened live (from a real order line) BOTH buttons hit the backend:
  // "Dispatch from store" → POST /orders/:order/lines/:line/fulfill (W4a); we
  // fetch REAL free stock so the max reflects the backend. "Send to production"
  // → POST /production/orders {sourceLineCodes:[lineCode], qty} (W4b), which makes
  // an MTO production order for this line — its derived status then flips to
  // "In Production". When NOT live (the still-mock MyQueue) both stay mock.
  const liveStock = useResource(() => (live ? stockApi.getStock(fresh.itemId) : Promise.resolve(null)), [live, fresh.itemId]);
  const mockAvail = st.stock[fresh.itemId] ? st.stock[fresh.itemId].available : 0;
  const avail = live && liveStock.data ? liveStock.data.available : mockAvail;
  const maxStore = Math.max(0, Math.min(need, avail));
  // Guard: the item may not exist in the stale mock st.items seed (always the case
  // for a real tenant's own catalog). Mirror party()'s `|| {name:id}` fallback so
  // it.name can never throw (an unguarded throw here unmounts the app → blank
  // screen). Prefer the real name carried on the line, else the item code.
  const it = item(fresh.itemId) || { name: line.itemName || fresh.itemId };
  const [dq, setDq] = useState(Number(maxStore.toFixed(2)));
  const [pq, setPq] = useState(Number(Math.max(0, need).toFixed(2)));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [pBusy, setPBusy] = useState(false);
  const [pErr, setPErr] = useState("");
  React.useEffect(() => { if (live && liveStock.data) setDq(Number(Math.max(0, Math.min(need, liveStock.data.available)).toFixed(2))); }, [live, liveStock.data]);
  const allocate = async () => {
    const want = Number(dq) || 0;
    if (want <= 0.0001) return;
    if (!live) { dispatch({ type: "FULFILL", lineId: fresh.id, qty: Math.min(want, maxStore) }); onClose(); return; }
    setBusy(true); setErr("");
    try { await ordersApi.fulfillLine(order.id, fresh.id, want); onClose(); onAllocated && onAllocated(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : "Allocation failed."); setBusy(false); }
  };
  const sendToProd = async () => {
    const q = Number(pq) || 0;
    if (q <= 0.0001) return;
    if (!live) { dispatch({ type: "SEND_TO_PRODUCTION", lineId: fresh.id, qty: q }); onClose(); return; }
    setPBusy(true); setPErr("");
    try { await productionApi.createPO({ sourceLineCodes: [fresh.id], qty: q }); onClose(); onAllocated && onAllocated(); }
    catch (e) { setPErr(e instanceof ApiError ? e.message : "Could not send to production."); setPBusy(false); }
  };
  const Cell = ({ label, value, tone }) => (<div className={cx("rounded-lg py-2 px-2 text-center", tone === "emerald" ? "bg-emerald-50" : tone === "amber" ? "bg-amber-50" : "bg-slate-50")}><div className={cx("text-xs", tone === "emerald" ? "text-emerald-700" : tone === "amber" ? "text-amber-700" : "text-slate-400")}>{label}</div><div className={cx("mono text-sm font-semibold", tone === "emerald" ? "text-emerald-700" : tone === "amber" ? "text-amber-700" : "text-slate-800")}>{value}</div></div>);
  return (
    <Modal open={open} onClose={onClose} title={"Stock decision — " + it.name} footer={<Btn variant="soft" onClick={onClose}>Close</Btn>}>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Cell label="Company" value={party(order.partyId).name} />
          <Cell label="Order" value={order.id} />
          <Cell label="Order qty" value={fmtQty(fresh.qtyOrdered)} />
          <Cell label="In store" value={fmtQty(avail)} tone={avail >= need ? "emerald" : "amber"} />
        </div>
        <div className="text-xs text-slate-400">Already allocated: <Mono>{fmtQty(fresh.qtyAllocated)}</Mono> Kg · Pending: <Mono>{fmtQty(Math.max(0, need))}</Mono> Kg</div>

        <div className="rounded-lg p-3 ring-1 ring-slate-200">
          <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-slate-700"><Package size={15} className="text-emerald-600" /> Dispatch from store</div>
          <p className="mb-2 text-xs text-slate-400">How much to allocate from store stock (allocate → ready-to-dispatch).</p>
          <div className="flex flex-wrap items-center gap-2">
            <input type="number" value={dq} onChange={(e) => setDq(e.target.value)} className="w-28 rounded-md bg-white py-1.5 px-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400" />
            <span className="text-xs text-slate-400">/ max {fmtQty(maxStore)} Kg{live && liveStock.loading ? <> <Spinner size={11} /></> : ""}</span>
            <Btn disabled={busy || (Number(dq) || 0) <= 0 || (!live && maxStore <= 0)} onClick={allocate}><Package size={13} /> {busy ? "Allocating…" : "Dispatch from store"}</Btn>
          </div>
          {err ? <div className="mt-2"><InlineError message={err} /></div> : null}
          {maxStore <= 0 && <p className="mt-1.5 text-xs text-rose-500">No store stock — send to production.</p>}
        </div>

        <div className="rounded-lg p-3 ring-1 ring-slate-200">
          <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-slate-700"><Factory size={15} className="text-amber-600" /> Send to production</div>
          <p className="mb-2 text-xs text-slate-400">Send this item to production — a production order is created against this order line, and the line status becomes <span className="text-amber-600">In Production</span>. If the item has no active BOM + routing the backend returns 400 (shown here).</p>
          <div className="flex flex-wrap items-center gap-2">
            <input type="number" value={pq} onChange={(e) => setPq(e.target.value)} className="w-28 rounded-md bg-white py-1.5 px-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400" />
            <span className="text-xs text-slate-400">Kg</span>
            <Btn variant="soft" disabled={pBusy || (Number(pq) || 0) <= 0} onClick={sendToProd}><Factory size={13} /> {pBusy ? "Sending…" : "Send to production"}</Btn>
          </div>
          {pErr ? <div className="mt-2"><InlineError message={pErr} /></div> : null}
        </div>
        <p className="text-xs text-slate-400">You can dispatch part from stock and send the rest to production (separate buttons) — both are now live.</p>
      </div>
    </Modal>
  );
}

function IssueModal({ st, po, open, onClose, dispatch }) {
  if (!po) return null;
  const item = (id) => itemById(st.items, id);
  const room = po.steps && po.steps[0] ? po.steps[0].wc : null;
  const roomStk = (room && st.roomStock && st.roomStock[room]) ? st.roomStock[room] : {};
  const issued = po.issuedQty || 0;
  const remaining = Math.max(0, po.plannedQty - issued);
  const [qty, setQty] = useState(remaining);
  const [ret, setRet] = useState("");
  const q = Math.max(0, Number(qty) || 0);
  const bom = st.boms[po.itemId];
  const f = bom ? q / bom.outputQty : 0;
  const rows = bom ? [...bom.comp, ...bom.pack].map(([cid, qc]) => ({ cid, need: qc * f })) : [];
  const issue = () => { if (q > 0.0001) { dispatch({ type: "ISSUE_MATERIALS", id: po.id, qty: q }); onClose(); } };
  const doReturn = () => { const rq = Math.min(Number(ret) || 0, issued); if (rq > 0.0001) { dispatch({ type: "RETURN_MATERIALS", id: po.id, qty: rq }); onClose(); } };
  const over = issued - po.plannedQty;
  return (
    <Modal open={open} onClose={onClose} title={"Issue / Return — " + po.id + (room ? " · " + room : "")} footer={<Btn variant="soft" onClick={onClose}>Close</Btn>}>
      <div className="space-y-3">
        <div className="grid grid-cols-3 gap-2 text-center text-xs">
          <div className="rounded-lg bg-slate-50 py-2"><div className="text-slate-400">Planned</div><Mono className="text-sm font-semibold text-slate-700">{fmtQty(po.plannedQty)}</Mono></div>
          <div className="rounded-lg bg-emerald-50 py-2"><div className="text-emerald-700">Issued so far</div><Mono className="text-sm font-semibold text-emerald-700">{fmtQty(issued)}</Mono></div>
          <div className={cx("rounded-lg py-2", Math.abs(over) < 0.01 ? "bg-slate-50" : over > 0 ? "bg-rose-50" : "bg-amber-50")}><div className={over > 0.01 ? "text-rose-700" : over < -0.01 ? "text-amber-700" : "text-slate-400"}>{over > 0.01 ? "Over-issued" : over < -0.01 ? "Remaining" : "Balanced"}</div><Mono className={cx("text-sm font-semibold", over > 0.01 ? "text-rose-700" : "text-amber-700")}>{fmtQty(Math.abs(over) < 0.01 ? 0 : Math.abs(over))}</Mono></div>
        </div>

        <div className="rounded-lg p-3 ring-1 ring-slate-200">
          <div className="mb-1.5 flex items-center gap-2 text-sm font-semibold text-slate-700"><Package size={15} className="text-emerald-600" /> Issue <span className="text-xs font-normal text-slate-400">(you can issue extra if needed)</span></div>
          <div className="flex flex-wrap items-center gap-2">
            <input type="number" value={qty} onChange={(e) => setQty(e.target.value)} className="w-28 rounded-md bg-white py-1.5 px-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400" />
            <Btn variant="soft" onClick={() => setQty((remaining / 2).toFixed(2))}>Half</Btn>
            <Btn variant="soft" onClick={() => setQty(remaining)}>Remaining</Btn>
            <Btn onClick={issue}><Package size={13} /> Issue {fmtQty(q)} Kg</Btn>
          </div>
          <div className="mt-2 overflow-x-auto rounded-lg ring-1 ring-slate-100">
            <table className="w-full"><thead className="bg-slate-50/60"><tr><Th>Material</Th><Th className="text-right">Consume</Th><Th className="text-right">In room</Th><Th className="text-right">Main</Th><Th>Status</Th></tr></thead>
              <tbody className="divide-y divide-slate-50">
                {rows.length === 0 ? <tr><Td className="text-slate-400" colSpan={5}>— qty din —</Td></tr> : rows.map(({ cid, need }) => { const it = item(cid); const rq = roomStk[cid] || 0; const av = st.stock[cid] ? st.stock[cid].available : 0; const short = (rq + av) < need - 0.001; return (
                  <tr key={cid}><Td className="font-medium text-slate-700">{it ? it.name : cid}</Td><Td className="text-right"><Mono>{fmtQty(need)}</Mono></Td><Td className="text-right"><Mono className={rq > 0 ? "text-amber-700" : "text-slate-300"}>{fmtQty(rq)}</Mono></Td><Td className="text-right"><Mono className="text-slate-500">{fmtQty(av)}</Mono></Td><Td>{short ? <Badge tone="rose"><AlertTriangle size={11} /> Short</Badge> : <Badge tone="emerald">OK</Badge>}</Td></tr>
                ); })}
              </tbody></table>
          </div>
          <p className="mt-1.5 text-xs text-slate-400">Drawn from <b>room</b> stock first, then from the main store. If the room is short, transfer from Inventory → Room-wise.</p>
        </div>

        {issued > 0.0001 && <div className="rounded-lg p-3 ring-1 ring-slate-200">
          <div className="mb-1.5 flex items-center gap-2 text-sm font-semibold text-slate-700"><ArrowUp size={15} className="text-sky-600" /> Return <span className="text-xs font-normal text-slate-400">(return if over-issued)</span></div>
          <div className="flex flex-wrap items-center gap-2">
            <input type="number" value={ret} onChange={(e) => setRet(e.target.value)} placeholder="Return qty" className="w-28 rounded-md bg-white py-1.5 px-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400" />
            <span className="text-xs text-slate-400">/ issued {fmtQty(issued)}</span>
            <Btn variant="soft" onClick={doReturn}><ArrowUp size={13} /> Return to store</Btn>
          </div>
          <p className="mt-1.5 text-xs text-slate-400">Over-issued RM is returned to the main store and the issued amount drops — everything stays tracked.</p>
        </div>}
      </div>
    </Modal>
  );
}

/* ============================ Production + Work Orders — LIVE (W4b) ============================
   The operational lifecycle now sources from the real /production API:
     listPOs / getPO  → board / list / detail
     createPO (MTS + MTO) · generate-wo · start · issue · return · advance · cancel
     listWorkOrders / createWorkOrder
   Button gating runs under the REAL session (authCan → /auth/me permissions, owner
   "*"); the mock "View as" switcher no longer gates these screens. The backend
   enforces every transition, so an ApiError envelope (start-before-WO 409,
   advance-when-Planned 409, issue>planned 400, missing-BOM/routing 400) is surfaced
   inline. assign/claim + add-WO-item have NO backend and stay mock/hidden. */

// Real-session permission check for the live Production/WO module. Prefers the
// permissions carried by /auth/me; falls back to the mock role table only when
// no auth is injected (legacy standalone render).
function authCan(auth, role, key) {
  const perms = auth && auth.role && Array.isArray(auth.role.permissions) ? auth.role.permissions : null;
  if (perms) return perms.includes("*") || perms.includes(key);
  return can(role, key);
}

// ASSIGNMENT IS AUTHORIZATION (per-PO). A PO's assignee may run THAT PO even with
// no production permission — mirrors assertMayActOnPo() on the server, so the
// button shows exactly when the API would allow the call. `po` needs assignedUser.
function authCanPo(auth, role, key, po) {
  if (authCan(auth, role, key)) return true;
  const myId = auth && auth.user ? auth.user.id : null;
  return !!(myId && po && po.assignedUser && po.assignedUser.id === myId);
}

const stepTat = (s, qty) => (Number(s.setupTime) || 0) + (Number(s.perUnitTime) || 0) * (Number(qty) || 0);
const fmtClock = (ts) => (ts ? new Date(ts).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : null);
const indexStockByCode = (list) => { const m = {}; (list || []).forEach((s) => { m[s.item ? s.item.code : s.code] = s; }); return m; };

/* ---- live PO detail (was the mock WorkOrderModal; that one stays for MyQueue) ---- */
// PRODUCTION #9: confirm/enter the ACTUAL output at finish. Default = the planned qty, so
// confirming as-is books the planned quantity EXACTLY as before. The value feeds ONLY the
// minted batch's qty (→ finished stock at QC-pass); RM consumption + WIP are untouched.
// `target` = { code, plannedQty, run: (actualOutput) => Promise } | null.
function FinishPoModal({ target, onClose, onDone }) {
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  React.useEffect(() => { if (target) { setVal(String(target.plannedQty)); setErr(""); setBusy(false); } }, [target]);
  const submit = async () => {
    const out = Number(val);
    if (!(out > 0)) { setErr("Enter the actual output produced (greater than 0)."); return; }
    if (out > target.plannedQty * 3) { setErr(`That looks too high — more than 3× the planned ${fmtQty(target.plannedQty)}. Re-check the quantity.`); return; }
    setBusy(true); setErr("");
    try { await target.run(out); onDone && onDone(); onClose(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : "Could not finish the order."); setBusy(false); }
  };
  return (
    <Modal open={!!target} onClose={busy ? undefined : onClose} title="Finish — actual output produced"
      footer={target ? <><Btn variant="soft" disabled={busy} onClick={onClose}>Cancel</Btn><Btn disabled={busy} onClick={submit}>{busy ? "Finishing…" : "Confirm & finish"}</Btn></> : null}>
      {target ? (
        <div className="space-y-3">
          <div className="text-sm text-slate-600">How much of <Mono className="text-slate-800">{target.code}</Mono> was actually produced? This quantity is what goes to finished stock.</div>
          <label className="block">
            <span className="text-xs font-medium text-slate-500">Actual output produced</span>
            <input type="number" step="any" value={val} onChange={(e) => setVal(e.target.value)} autoFocus onKeyDown={(e) => { if (e.key === "Enter") submit(); }} className="mono mt-1 w-full rounded-md bg-white py-2 px-3 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400" />
          </label>
          <div className="text-xs text-slate-400">Planned was <b>{fmtQty(target.plannedQty)}</b>. Leave it as-is to book the planned quantity (the usual case), or correct it (e.g. 101.3 instead of 100).</div>
          {err ? <InlineError message={err} /> : null}
        </div>
      ) : null}
    </Modal>
  );
}

// LOT-WISE production entry (Phase B) — shown inside PoDetailModal ONLY when the tenant's
// settings.lotWiseProduction flag is on AND the PO's FG item has a lot size. Records date-wise
// partial lot entries (each mints a QC-Pending batch); the PO auto-completes when produced ≥
// planned (backend does the WIP-reversal-once). View + form; no classic path touched.
function LotWisePanel({ po, reload, canWrite }) {
  const lot = Number(po.item.lot) || 0;
  // Production-date options: exactly Today / Yesterday / Day before (T-2), computed as IST days.
  const istYmd = (offsetDays) => new Date(Date.now() + 5.5 * 3600e3 - offsetDays * 86400e3).toISOString().slice(0, 10);
  const dayOpts = [{ v: istYmd(0), l: "Today" }, { v: istYmd(1), l: "Yesterday" }, { v: istYmd(2), l: "Day before" }];
  const [pd, setPd] = useState(dayOpts[0].v);
  const [lots, setLots] = useState("1");
  const [actual, setActual] = useState(String(lot));
  const [actualTouched, setActualTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const lotsN = Math.max(0, Math.floor(Number(lots) || 0));
  const expected = Math.round(lotsN * lot * 1000) / 1000;
  // Keep Actual in step with Expected as Lot Count changes — until the user overrides it.
  React.useEffect(() => { if (!actualTouched) setActual(expected > 0 ? String(expected) : ""); }, [expected, actualTouched]);
  const actualN = Number(actual) || 0;
  const diffPct = expected > 0 ? ((actualN - expected) / expected) * 100 : 0;
  const done = po.status === "Done";
  const remaining = po.remainingQty != null ? po.remainingQty : Math.max(0, po.plannedQty - (po.producedQty || 0));
  // LOT-WISE ±4% OVER guard (mirrors the backend): this entry may not push cumulative Σ actualOutput
  // above planned×1.04. producedQty is the server-derived Σ of prior entries.
  const plannedN = Number(po.plannedQty) || 0;
  const producedNow = Number(po.producedQty) || 0;
  const overCap = Math.round(plannedN * 1.04 * 1000) / 1000;
  const overHeadroom = Math.max(0, Math.round((overCap - producedNow) * 1000) / 1000);
  const wouldExceed = plannedN > 0 && actualN > 0 && producedNow + actualN > overCap + 1e-9;
  const IN = "mono w-full rounded-md bg-white py-1.5 px-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400";
  const entries = po.lotEntries || [];
  const record = async () => {
    if (!(lotsN > 0)) { setErr("Enter a lot count (at least 1)."); return; }
    if (!(actualN > 0)) { setErr("Enter the actual output produced (greater than 0)."); return; }
    setBusy(true); setErr("");
    try {
      await productionApi.recordLotEntry(po.code, { productionDate: pd, lotCount: lotsN, actualOutput: actualN });
      setLots("1"); setActualTouched(false); await reload();
    } catch (e) { setErr(e instanceof ApiError ? e.message : "Could not record the lot entry."); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg bg-indigo-50/40 ring-1 ring-indigo-200">
      <div className="flex flex-wrap items-center gap-2 border-b border-indigo-100 px-3 py-2">
        <Layers size={14} className="text-indigo-600" /><span className="text-sm font-semibold text-slate-700">Lot-wise production</span>
        <span className="text-xs text-slate-500">standard lot = {fmtQty(lot)} Kg</span>
        <span className="ml-auto text-xs">{done ? <Badge tone="emerald">Completed</Badge> : <span className="text-slate-600">Produced <b className="text-slate-800">{fmtQty(po.producedQty || 0)}</b> / {fmtQty(po.plannedQty)} · remaining <b className="text-slate-800">{fmtQty(remaining)}</b></span>}</span>
      </div>
      {!done && canWrite ? (
        <>
          <div className="grid gap-2 px-3 py-3 sm:grid-cols-4">
            <label className="block"><span className="text-xs font-medium text-slate-500">Production date</span>
              <select value={pd} onChange={(e) => setPd(e.target.value)} className={IN}>{dayOpts.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}</select></label>
            <label className="block"><span className="text-xs font-medium text-slate-500">Lot count</span>
              <input type="number" min="1" step="1" value={lots} onChange={(e) => setLots(e.target.value)} className={IN} /></label>
            <label className="block"><span className="text-xs font-medium text-slate-500">Expected output</span>
              <div className={cx(IN, "bg-slate-50 text-slate-600")}>{fmtQty(expected)} Kg</div></label>
            <label className="block"><span className="text-xs font-medium text-slate-500">Actual output</span>
              <input type="number" step="any" value={actual} onChange={(e) => { setActualTouched(true); setActual(e.target.value); }} className={IN} />
              {expected > 0 && actualN > 0 ? <span className={cx("text-[11px]", Math.abs(diffPct) > 4 ? "text-amber-600" : "text-slate-400")}>{diffPct >= 0 ? "+" : ""}{diffPct.toFixed(1)}% vs expected</span> : null}</label>
          </div>
          {err ? <div className="px-3 pb-2"><InlineError message={err} /></div> : null}
          {wouldExceed ? <div className="px-3 pb-2"><InlineError message={`Total output would exceed +4% of planned (${fmtQty(overHeadroom)} of ${fmtQty(actualN)} allowed). Reduce this entry.`} /></div> : null}
          <div className="px-3 pb-3"><Btn disabled={busy || wouldExceed} onClick={record}><Plus size={14} /> {busy ? "Recording…" : "Record lot entry"}</Btn></div>
        </>
      ) : err ? <div className="px-3 py-2"><InlineError message={err} /></div> : null}
      <div className="overflow-x-auto border-t border-indigo-100">
        <table className="w-full"><thead><tr><Th>Date</Th><Th className="text-right">Lots</Th><Th className="text-right">Expected</Th><Th className="text-right">Actual</Th><Th>Batch</Th></tr></thead>
          <tbody className="divide-y divide-slate-50">
            {entries.length === 0 ? <tr><Td className="text-slate-400" colSpan={5}>No lot entries yet{done ? "" : " — record the first above"}.</Td></tr> : entries.map((e, i) => (
              <tr key={i}>
                <Td className="text-slate-700">{fmtDate(e.productionDate)}</Td>
                <Td className="text-right"><Mono>{e.lotCount}</Mono></Td>
                <Td className="text-right"><Mono className="text-slate-500">{fmtQty(e.expectedQty)}</Mono></Td>
                <Td className="text-right"><Mono className="font-medium text-slate-800">{fmtQty(e.actualOutput)}</Mono></Td>
                <Td>{e.batch ? <span className="inline-flex items-center gap-1"><Mono className="text-xs text-slate-600">{e.batch.code}</Mono><Badge tone={e.batch.status === "Passed" ? "emerald" : e.batch.status === "Rejected" ? "rose" : "amber"}>{e.batch.status}</Badge></span> : "—"}</Td>
              </tr>
            ))}
          </tbody></table>
      </div>
    </div>
  );
}

function PoDetailModal({ code, auth, role, open, onClose, onChanged, onIssue }) {
  const { data, loading, error, reload } = useResource(
    () => (code ? productionApi.getPO(code) : Promise.resolve(null)),
    [code],
  );
  const po = data && data.order;
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [finishTarget, setFinishTarget] = useState(null); // PRODUCTION #9: actual-output prompt
  const assigneesRes = useResource(() => tasksApi.listAssignees(), []);
  const assignees = (assigneesRes.data && assigneesRes.data.users) || [];
  const [assignSel, setAssignSel] = useState("");
  const curAssignee = po && po.assignedUser ? po.assignedUser.id : "";
  React.useEffect(() => { setAssignSel(curAssignee); }, [curAssignee]);
  const pcan = (k) => authCan(auth, role, k);
  const act = async (fn, close) => {
    setBusy(true); setErr("");
    try { await fn(); await reload(); onChanged && onChanged(); if (close) onClose(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : "Action failed."); }
    finally { setBusy(false); }
  };
  const planned = po ? po.plannedQty : 0;
  // LOT-WISE (Phase B): active ONLY when the tenant's flag is on AND this PO's FG item has a lot
  // size. When false (every tenant today, incl JAINYS) the classic FinishPoModal path is used —
  // byte-identical to before. Gate value comes from /auth/me (auth.company.settings).
  const lotWise = auth?.company?.settings?.lotWiseProduction === true && !!po && !!po.item && po.item.lot != null && Number(po.item.lot) > 0;
  const total = po ? po.steps.length : 0;
  const lastStep = po ? po.stepIdx + 1 >= total : false;
  const curRoom = po && po.status === "In Production" && po.steps[po.stepIdx] ? po.steps[po.stepIdx].workCenter : null;
  const nextRoom = po && !lastStep && po.steps[po.stepIdx + 1] ? po.steps[po.stepIdx + 1].workCenter : null;
  // Plain-language status for non-technical staff (display only — backend status unchanged).
  const plainStatus = po ? (po.status === "Planned" ? "Waiting to start" : po.status === "Done" ? "Finished" : curRoom ? "In " + curRoom : "In production") : "";
  const totalTat = po ? po.steps.reduce((a, s) => a + stepTat(s, planned), 0) : 0;
  const srcLabel = po ? (po.forStock ? "Make-to-stock" : (po.sourceLines.map((s) => s.orderCode).join(", ") || "—")) : "";

  const ReqTable = ({ title, rows, icon: Icon, tone }) => (
    <div className="rounded-lg ring-1 ring-slate-200 overflow-x-auto">
      <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2"><Icon size={14} className={tone} /><span className="text-sm font-semibold text-slate-700">{title}</span></div>
      <table className="w-full"><thead><tr><Th>Material</Th><Th>Type</Th><Th className="text-right">Required (for batch)</Th></tr></thead>
        <tbody className="divide-y divide-slate-50">
          {rows.length === 0 ? <tr><Td className="text-slate-400" colSpan={3}>— none —</Td></tr> : rows.map((r) => (
            <tr key={r.code}><Td className="font-medium text-slate-700">{r.name}<div className="text-xs font-normal text-slate-400"><Mono>{r.code}</Mono></div></Td>
              <Td><Badge tone={r.type === "SFG" ? "violet" : r.type === "PM" ? "sky" : "stone"}>{r.type}</Badge></Td>
              <Td className="text-right"><Mono className="font-medium text-slate-800">{fmtQty(r.totalQty)}</Mono></Td></tr>
          ))}
        </tbody></table>
    </div>
  );

  return (
    <>
    <Modal open={open} onClose={onClose} title={"Production order — " + (po ? po.code : code || "")} wide
      footer={<>
        <Btn variant="soft" onClick={onClose}>Close</Btn>
        {po && po.status === "Planned" && !po.woCode && pcan("stockPO") && (po.assignedUser
          ? <Btn disabled={busy} onClick={() => act(() => productionApi.generateWO(po.code, po.assignedUser.id))}><ListOrdered size={14} /> Generate work order</Btn>
          : <span className="self-center text-xs text-amber-600">Assign an employee below to generate a work order</span>)}
        {po && po.status === "Planned" && po.woCode && authCanPo(auth, role, "startProd", po) && <Btn disabled={busy} onClick={() => act(() => productionApi.startPO(po.code))}><Factory size={14} /> Start production</Btn>}
        {po && po.status === "In Production" && pcan("issue") && <Btn variant="soft" disabled={busy} onClick={() => onIssue(po.code)}><Package size={14} /> Issue / Return RM</Btn>}
        {po && po.status === "In Production" && authCanPo(auth, role, "advanceProd", po) && !(lotWise && lastStep) && <Btn disabled={busy} onClick={() => lastStep ? setFinishTarget({ code: po.code, plannedQty: po.plannedQty, run: (out) => productionApi.advancePO(po.code, out) }) : act(() => productionApi.advancePO(po.code))}>{lastStep ? <><CheckCircle2 size={14} /> Finish production</> : <>Done here → move to {nextRoom || "next room"} <ArrowRight size={14} /></>}</Btn>}
        {po && po.status !== "Done" && po.isActive && pcan("stockPO") && !lotWise && <Btn disabled={busy} onClick={() => setFinishTarget({ code: po.code, plannedQty: po.plannedQty, run: (out) => productionApi.completePO(po.code, out) })}><CheckCircle2 size={14} /> Mark complete</Btn>}
        {po && po.status === "Planned" && (po.issuedQty || 0) < 0.0001 && po.isActive && pcan("stockPO") && <Btn variant="ghost" disabled={busy} onClick={() => act(() => productionApi.cancelPO(po.code), true)}><X size={14} /> Cancel PO</Btn>}
      </>}>
      {loading && !po ? <LoadingBlock label="Loading production order…" /> : error ? <InlineError message={error} onRetry={reload} /> : po ? (
        <div className="space-y-4">
          {err ? <InlineError message={err} /> : null}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-lg bg-slate-50 p-3"><div className="text-xs text-slate-400">Product</div><div className="text-sm font-medium text-slate-700">{po.item.name}</div><Mono className="text-xs text-slate-400">{po.item.code}</Mono></div>
            <div className="rounded-lg bg-slate-50 p-3"><div className="text-xs text-slate-400">Planned output</div><Mono className="text-sm font-semibold text-slate-800">{fmtQty(po.plannedQty)} Kg</Mono><div className="text-xs text-slate-400">req {fmtQty(po.requestedQty)}</div></div>
            <div className="rounded-lg bg-slate-50 p-3"><div className="text-xs text-slate-400">Status</div><Badge tone={statusTone(po.status)}>{plainStatus}</Badge>{!po.isActive && <div className="mt-1"><Badge tone="rose">Cancelled</Badge></div>}</div>
            <div className="rounded-lg bg-slate-50 p-3"><div className="text-xs text-slate-400">{po.forStock ? "For" : "For order(s)"}</div><div className="truncate text-sm font-medium text-slate-700">{srcLabel}</div>{po.woCode && <Mono className="text-xs text-emerald-600">{po.woCode}</Mono>}</div>
          </div>

          {lotWise && po.status !== "Planned" ? <LotWisePanel po={po} reload={reload} canWrite={pcan("stockPO")} /> : null}

          <div className="grid grid-cols-3 gap-2 text-center text-xs">
            <div className="rounded-lg bg-emerald-50 py-2"><div className="text-emerald-700">RM issued</div><Mono className="text-sm font-semibold text-emerald-700">{fmtQty(po.issuedQty)}/{fmtQty(po.plannedQty)}</Mono></div>
            <div className="rounded-lg bg-slate-50 py-2"><div className="text-slate-400">Work order</div><Mono className="text-sm font-semibold text-slate-700">{po.woGenerated ? (po.woCode || "released") : "—"}</Mono></div>
            <div className="rounded-lg bg-slate-50 py-2"><div className="text-slate-400">Now at</div>{po.status === "In Production" ? <div className="text-xs font-semibold text-slate-700">{curRoom || "—"}<div className="font-normal text-slate-400">step {po.stepIdx + 1} of {total}</div></div> : <Mono className="text-sm font-semibold text-slate-400">—</Mono>}</div>
          </div>

          {/* Performance attribution (Phase 1): assignee + completion timing. */}
          <div className="rounded-lg bg-indigo-50/50 px-3 py-2 text-sm ring-1 ring-indigo-100">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
              <span className="text-slate-500">Assigned to: <span className="font-medium text-slate-800">{po.assignedUser ? po.assignedUser.name : <span className="text-slate-400">Unassigned</span>}</span></span>
              {pcan("stockPO") && po.status !== "Done" && po.isActive && (
                <span className="flex items-center gap-1.5">
                  <select value={assignSel} onChange={(e) => setAssignSel(e.target.value)} className="rounded-md bg-white py-1 px-2 text-xs ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400"><option value="">Assign to…</option>{assignees.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</select>
                  <button disabled={busy || !assignSel || assignSel === curAssignee} onClick={() => act(() => productionApi.assignPO(po.code, assignSel))} className="rounded-md px-2 py-1 text-xs font-medium text-indigo-600 ring-1 ring-indigo-200 hover:bg-indigo-50 disabled:opacity-40">{po.assignedUser ? "Reassign" : "Assign"}</button>
                </span>
              )}
              {po.completedAt
                ? <><span className="flex items-center gap-1 text-emerald-700"><CheckCircle2 size={13} /> Completed {fmtDateTime(po.completedAt)}{po.completedByUser ? " by " + po.completedByUser.name : ""}</span>
                    {po.rawDurationHours != null && <span className="text-slate-500">Duration (raw): <Mono className="font-medium text-slate-800">{po.rawDurationHours} h</Mono></span>}</>
                : <span className="text-slate-400">Not yet completed</span>}
            </div>
          </div>

          <div className="rounded-lg ring-1 ring-slate-200">
            <div className="flex flex-wrap items-center justify-between gap-1 border-b border-slate-100 px-3 py-2">
              <div className="flex items-center gap-2"><GitBranch size={14} className="text-violet-600" /><span className="text-sm font-semibold text-slate-700">Process steps (routing)</span></div>
              <span className="text-xs text-slate-400">Total ~<Mono className="text-slate-600">{totalTat.toFixed(1)}</Mono> h</span>
            </div>
            <ol className="divide-y divide-slate-50">
              {po.steps.length === 0 ? <li className="px-3 py-2 text-sm text-slate-400">No routing steps.</li> : po.steps.map((s, i) => {
                const current = po.status === "In Production" && i === po.stepIdx;
                const done = !!s.endedAt;
                return (
                  <li key={s.stepNo} className={cx("flex items-center justify-between px-3 py-2.5 text-sm", current ? "bg-amber-50/60" : "")}>
                    <span className="flex items-center gap-2">
                      <span className={cx("flex h-5 w-5 items-center justify-center rounded-full text-xs font-semibold", done ? "bg-emerald-100 text-emerald-700" : current ? "bg-amber-200 text-amber-800" : "bg-slate-100 text-slate-500")}>{done ? <CheckCircle2 size={12} /> : i + 1}</span>
                      <span className={cx("font-medium", current ? "text-amber-800" : "text-slate-700")}>{s.workCenter}</span>
                      {current && <Badge tone="amber">Current</Badge>}
                    </span>
                    <span className="text-xs text-slate-400">{s.startedAt ? <>start <Mono className="text-slate-500">{fmtClock(s.startedAt)}</Mono></> : <>setup {s.setupTime || 0}h + {s.perUnitTime || 0}h/Kg</>}{s.endedAt ? <> · end <Mono className="text-slate-500">{fmtClock(s.endedAt)}</Mono></> : ""}</span>
                  </li>
                );
              })}
            </ol>
          </div>

          {po.sourceLines.length > 0 && (
            <div className="rounded-lg ring-1 ring-slate-200">
              <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2"><ClipboardList size={14} className="text-amber-600" /><span className="text-sm font-semibold text-slate-700">Sales order line(s) served</span></div>
              <ul className="divide-y divide-slate-50">
                {po.sourceLines.map((sl) => <li key={sl.lineCode} className="px-3 py-2 text-sm text-slate-600"><Mono className="text-slate-800">{sl.orderCode}</Mono> · line <Mono className="text-slate-500">{sl.lineCode}</Mono></li>)}
              </ul>
            </div>
          )}

          {po.linkedBatch && (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-emerald-50 px-3 py-2.5 ring-1 ring-emerald-200">
              <span className="flex items-center gap-2 text-sm font-medium text-emerald-800"><Boxes size={15} /> Batch born: <Mono>{po.linkedBatch.code}</Mono></span>
              <span className="text-xs text-emerald-700"><Badge tone={po.linkedBatch.status === "Passed" ? "emerald" : "amber"}>{po.linkedBatch.status}</Badge> · {fmtQty(po.linkedBatch.remaining)} Kg left — QC it in Batches</span>
            </div>
          )}

          {po.bomRequirement ? <>
            <ReqTable title="Raw / packaging required (exploded to RM/PM)" rows={po.bomRequirement.leaves} icon={Boxes} tone="text-amber-600" />
            {po.bomRequirement.intermediates && po.bomRequirement.intermediates.length > 0 && <ReqTable title="Sub-assemblies (SFG)" rows={po.bomRequirement.intermediates} icon={Layers} tone="text-violet-600" />}
          </> : <p className="text-xs text-slate-400">No BOM explosion available.</p>}
        </div>
      ) : null}
    </Modal>
    <FinishPoModal target={finishTarget} onClose={() => setFinishTarget(null)} onDone={() => { reload(); onChanged && onChanged(); }} />
    </>
  );
}

/* ---- live Issue / Return (was the mock IssueModal; that one stays for MyQueue) ---- */
function LiveIssueModal({ code, open, onClose, onChanged, auth }) {
  // VENDOR-WISE Phase 2: when ON, the batch picks drive REAL consumption via one atomic
  // issue-batchwise call. OFF ⇒ the classic issuePO + informational recordIssuedBatches.
  const vendorWise = auth?.company?.settings?.vendorWiseStock === true;
  const { data, loading, error, reload } = useResource(
    () => (code ? productionApi.getPO(code) : Promise.resolve(null)),
    [code],
  );
  const stockRes = useResource(() => stockApi.listStock({ limit: 200 }), []);
  const roomsRes = useResource(() => stockApi.getRooms(), []);
  const batchRes = useResource(() => batchesApi.listBatches({ limit: 200 }), []);
  const po = data && data.order;
  const planned = po ? po.plannedQty : 0;
  const issued = po ? po.issuedQty : 0;
  const remaining = Math.max(0, planned - issued);
  const inProd = po ? po.status === "In Production" : false;
  // Issue/return is allowed from WORK-ORDER creation through In Production (matches
  // the backend). "Start first" is no longer required — the Store can issue as soon
  // as the WO exists. Blocked only when there is no WO yet, or the PO is Done.
  const hasWO = po ? !!po.woGenerated : false;
  const canIssueStatus = po ? (hasWO && po.status !== "Done") : false;
  const [qty, setQty] = useState("");
  const [ret, setRet] = useState("");
  const [sel, setSel] = useState({}); // itemCode → [{batchCode, qty}] (edited overrides FEFO)
  const [showDetails, setShowDetails] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  React.useEffect(() => { if (po) setQty(String(Number(remaining.toFixed(2)))); /* eslint-disable-next-line */ }, [po && po.code, planned, issued]);
  React.useEffect(() => { if (!open) setSel({}); }, [open, code]);
  const q = Math.max(0, Number(qty) || 0);
  const stockMap = indexStockByCode(stockRes.data && stockRes.data.stock);
  const room = po && po.steps && po.steps[0] ? po.steps[0].workCenter : null;
  const roomMap = {};
  if (room) { const rm = (roomsRes.data && roomsRes.data.rooms || []).find((r) => r.room === room); (rm ? rm.items : []).forEach((e) => { roomMap[e.itemCode] = e.qty; }); }
  const leaves = po && po.consumes ? po.consumes : [];

  // --- BATCH STATE per material (the three rules) ---------------------------
  // all batches per item; FEFO oldest-first. Three states drive the rules:
  //   passed w/ remaining  → MANDATORY batch selection (rule 1)
  //   no passed but PENDING → BLOCK "QC it first" (rule 3)
  //   neither (no batch)    → issue allowed without a batch (rule 2)
  const batchesByItem = {};
  ((batchRes.data && batchRes.data.batches) || []).forEach((b) => { (batchesByItem[b.item.code] = batchesByItem[b.item.code] || []).push(b); });
  Object.values(batchesByItem).forEach((a) => a.sort((x, y) => new Date(x.producedAt) - new Date(y.producedAt)));
  const passedOf = (c) => (batchesByItem[c] || []).filter((b) => b.status === "Passed" && (b.remaining || 0) > 0);
  const pendingOf = (c) => (batchesByItem[c] || []).filter((b) => b.status === "QC Pending" && (b.remaining || 0) > 0);
  const stateOf = (c) => (passedOf(c).length ? "mandatory" : pendingOf(c).length ? "blocked" : "nobatch");

  const fefoFill = (c, need) => { let n = need, out = []; for (const b of passedOf(c)) { if (n <= 0.001) break; const take = Math.min(b.remaining, n); out.push({ batchCode: b.code, qty: Number(take.toFixed(3)) }); n -= take; } if (out.length === 0 && passedOf(c)[0]) out = [{ batchCode: passedOf(c)[0].code, qty: Number(need.toFixed(3)) }]; return out; };
  const linesFor = (c, need) => sel[c] || fefoFill(c, need);
  const setLines = (c, lines) => setSel((s) => ({ ...s, [c]: lines }));

  const rows = planned > 0 ? leaves.map((l) => {
    const consume = l.totalQty * (q / planned);
    const inRoom = roomMap[l.code] || 0;
    const inStore = stockMap[l.code] ? stockMap[l.code].available : 0;
    const fromRoom = Math.min(inRoom, consume);
    const fromStore = Math.max(0, consume - fromRoom);
    const short = (inRoom + inStore) < consume - 0.001;
    return { ...l, consume, inRoom, inStore, fromRoom, fromStore, short, state: stateOf(l.code) };
  }) : [];

  // Validity: a mandatory material needs batches selected whose qtys cover consume.
  const matOk = (m) => {
    if (m.state === "blocked") return false;
    if (m.state === "nobatch") return true;
    const lines = linesFor(m.code, m.consume);
    const sum = lines.reduce((a, l) => a + (Number(l.qty) || 0), 0);
    return lines.length > 0 && lines.every((l) => l.batchCode && Number(l.qty) > 0) && sum >= m.consume - 0.01;
  };
  const active = rows.filter((m) => m.consume > 0.0001);
  const blocked = active.filter((m) => m.state === "blocked");
  const mandUnsatisfied = active.some((m) => m.state === "mandatory" && !matOk(m));
  const canIssue = canIssueStatus && q > 0.0001 && blocked.length === 0 && !mandUnsatisfied && !busy;

  const doIssueRecord = async () => {
    if (!canIssue) return;
    setBusy(true); setErr("");
    try {
      if (vendorWise) {
        // ONE atomic call: picks drive real consumption (batch.remaining + aggregate in lockstep).
        const materials = active
          .filter((m) => m.state === "mandatory")
          .map((m) => ({ itemCode: m.code, lines: linesFor(m.code, m.consume).filter((l) => l.batchCode && Number(l.qty) > 0).map((l) => ({ batchCode: l.batchCode, qty: Number(l.qty) })) }))
          .filter((m) => m.lines.length);
        await productionApi.issueBatchwise(code, { qty: q, materials });
      } else {
        await productionApi.issuePO(code, q); // aggregate stock — unchanged (classic path)
        // record the physical batch note for every material that HAS passed batches
        for (const m of active) {
          if (m.state !== "mandatory") continue;
          const lines = linesFor(m.code, m.consume).filter((l) => l.batchCode && Number(l.qty) > 0).map((l) => ({ batchCode: l.batchCode, qty: Number(l.qty) }));
          if (lines.length) await productionApi.recordIssuedBatches(code, { itemCode: m.code, room: room || undefined, lines });
        }
      }
      await reload(); onChanged && onChanged();
    } catch (e) { setErr(e instanceof ApiError ? e.message : "Issue failed."); }
    finally { setBusy(false); }
  };
  const doReturn = async () => {
    const rq = Math.min(Number(ret) || 0, issued);
    if (rq <= 0.0001) return;
    setBusy(true); setErr("");
    try { await productionApi.returnMaterials(code, rq); setRet(""); await reload(); onChanged && onChanged(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : "Return failed."); }
    finally { setBusy(false); }
  };

  const fgName = po && po.item ? po.item.name : "";
  const SEL = "rounded-md bg-white py-1 px-1.5 text-xs ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400";
  const QIN = "mono w-20 rounded-md bg-white py-1 px-1.5 text-right text-xs ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400";
  const blockReason = !hasWO ? "Create the work order first" : (po && po.status === "Done") ? "This production order is finished" : q <= 0.0001 ? "Enter a quantity" : blocked.length ? "Some materials aren't QC-passed" : mandUnsatisfied ? "Choose the batch(es) you're sending" : "";
  return (
    <Modal open={open} onClose={onClose} wide title={"Issue raw materials" + (fgName ? " — " + fgName : "") + (po ? " · " + po.code : "")} footer={<Btn variant="soft" onClick={onClose}>Close</Btn>}>
      {loading && !po ? <LoadingBlock label="Loading…" /> : error ? <InlineError message={error} onRetry={reload} /> : po ? (
        <div className="space-y-3">
          {err ? <InlineError message={err} /> : null}
          <p className="text-xs text-slate-500">These are the raw materials this batch needs. Confirm the quantity and which physical batch you're sending to the room.</p>
          {!hasWO && <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-700 ring-1 ring-amber-200">Create the <b>work order</b> first — then you can issue this order's materials.</p>}
          {hasWO && !inProd && po.status !== "Done" && <p className="rounded-md bg-sky-50 px-3 py-2 text-xs text-sky-700 ring-1 ring-sky-200">You can issue materials now — production doesn't have to be started first.</p>}
          <div className="grid grid-cols-3 gap-2 text-center text-xs">
            <div className="rounded-lg bg-slate-50 py-2"><div className="text-slate-400">Planned</div><Mono className="text-sm font-semibold text-slate-700">{fmtQty(planned)}</Mono></div>
            <div className="rounded-lg bg-emerald-50 py-2"><div className="text-emerald-700">Issued so far</div><Mono className="text-sm font-semibold text-emerald-700">{fmtQty(issued)}</Mono></div>
            <div className="rounded-lg bg-amber-50 py-2"><div className="text-amber-700">Remaining</div><Mono className="text-sm font-semibold text-amber-700">{fmtQty(remaining)}</Mono></div>
          </div>
          <div className="rounded-lg p-3 ring-1 ring-slate-200 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold text-slate-700"><Package size={15} className="mr-1 inline text-emerald-600" />Quantity to issue</span>
              <input type="number" value={qty} onChange={(e) => setQty(e.target.value)} className="w-24 rounded-md bg-white py-1.5 px-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400" />
              <Btn variant="soft" onClick={() => setQty(String(Number(remaining.toFixed(2))))}>All remaining</Btn>
              <button onClick={() => setShowDetails((v) => !v)} className="ml-auto text-xs text-slate-400 hover:text-slate-600">{showDetails ? "Hide" : "Show"} room/store details</button>
            </div>
            {rows.length === 0 ? <p className="text-xs text-slate-400">Enter a quantity to see the materials.</p> : (
              <div className="space-y-2">
                {rows.map((m) => (
                  <div key={m.code} className={cx("rounded-lg ring-1", m.state === "blocked" ? "ring-rose-200 bg-rose-50/30" : "ring-slate-200")}>
                    <div className="flex flex-wrap items-center gap-2 px-3 py-2">
                      <span className="font-medium text-slate-700">{m.name}</span><Mono className="text-xs text-slate-400">{m.code}</Mono>
                      <span className="text-xs text-slate-500">Needed: <b className="text-slate-700">{fmtQty(m.consume)}</b> Kg</span>
                      {m.short && <Badge tone="rose"><AlertTriangle size={11} /> Short in stock</Badge>}
                    </div>
                    {showDetails && <div className="px-3 pb-1.5 text-xs text-slate-400">In this room: <Mono className="text-amber-700">{fmtQty(m.inRoom)}</Mono> · In main store: <Mono>{fmtQty(m.inStore)}</Mono> · will draw <Mono className="text-amber-700">{fmtQty(m.fromRoom)}</Mono> from room + <Mono>{fmtQty(m.fromStore)}</Mono> from store</div>}
                    <div className="border-t border-slate-100 px-3 py-2">
                      {m.state === "blocked" ? (
                        <p className="text-xs font-medium text-rose-600"><AlertTriangle size={12} className="mr-1 inline" />This raw material isn't QC-passed yet — pass its QC before issuing.</p>
                      ) : m.state === "nobatch" ? (
                        <p className="text-xs text-slate-400">No batch on record — will issue without a batch record.</p>
                      ) : (() => {
                        const lines = linesFor(m.code, m.consume);
                        const sum = lines.reduce((a, l) => a + (Number(l.qty) || 0), 0);
                        const short = sum < m.consume - 0.01;
                        return (
                          <div className="space-y-1.5">
                            <div className="flex items-center gap-2 text-xs text-slate-500"><ClipboardList size={12} className="text-amber-600" />Which batch are you sending?<button onClick={() => setLines(m.code, fefoFill(m.code, m.consume))} className="text-indigo-600 hover:text-indigo-800">use oldest (FEFO)</button></div>
                            {lines.map((l, i) => (
                              <div key={i} className="flex flex-wrap items-center gap-1.5">
                                <select value={l.batchCode} onChange={(e) => setLines(m.code, lines.map((x, j) => j === i ? { ...x, batchCode: e.target.value } : x))} className={SEL}>
                                  <option value="">Choose batch…</option>{passedOf(m.code).map((b) => <option key={b.code} value={b.code}>{b.code} · QC'd {fmtDate(b.producedAt)} · {fmtQty(b.remaining)} on hand</option>)}
                                </select>
                                <input type="number" value={l.qty} onChange={(e) => setLines(m.code, lines.map((x, j) => j === i ? { ...x, qty: e.target.value } : x))} className={QIN} placeholder="Kg" />
                                {lines.length > 1 && <button onClick={() => setLines(m.code, lines.filter((_, j) => j !== i))} className="text-slate-400 hover:text-rose-500"><X size={13} /></button>}
                              </div>
                            ))}
                            <div className="flex items-center gap-2 text-xs">
                              <button onClick={() => setLines(m.code, [...lines, { batchCode: "", qty: "" }])} className="text-indigo-600 hover:text-indigo-800"><Plus size={11} className="mr-0.5 inline" />split across another batch</button>
                              <span className={short ? "text-rose-500" : "text-slate-400"}>{fmtQty(sum)} / {fmtQty(m.consume)} Kg{short ? " — cover the full amount" : " ✓"}</span>
                            </div>
                            <p className="text-[11px] text-slate-400">We suggest the oldest stock (FEFO). If you're sending a different bag, change it here.</p>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <Btn disabled={!canIssue} onClick={doIssueRecord}><Package size={14} /> {busy ? "Issuing…" : "Issue & record batch"}</Btn>
              {!canIssue && blockReason && <span className="text-xs text-amber-600">{blockReason}</span>}
            </div>
          </div>
          {issued > 0.0001 && <div className="rounded-lg p-3 ring-1 ring-slate-200">
            <div className="mb-1.5 flex items-center gap-2 text-sm font-semibold text-slate-700"><ArrowUp size={15} className="text-sky-600" /> Return</div>
            <div className="flex flex-wrap items-center gap-2">
              <input type="number" value={ret} onChange={(e) => setRet(e.target.value)} placeholder="Return qty" className="w-28 rounded-md bg-white py-1.5 px-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400" />
              <span className="text-xs text-slate-400">/ issued {fmtQty(issued)}</span>
              <Btn variant="soft" disabled={busy || !canIssueStatus} onClick={doReturn}><ArrowUp size={13} /> Return to store</Btn>
            </div>
          </div>}
        </div>
      ) : null}
    </Modal>
  );
}


/* ============================ Work Orders (live, demand-aggregated) ============================ */
function CreateWOModal({ open, onClose, onCreated }) {
  const res = useResource(async () => {
    if (!open) return null;
    const list = await ordersApi.listOrders();
    const details = await Promise.all((list.orders || []).map((o) => ordersApi.getOrder(o.code)));
    // PRODUCTION #3: make-to-stock can produce SFG too (backend createPo accepts any item with an
    // active BOM + routing). List FG + SFG (two calls merged; FG stays identical to before).
    const [fgRes, sfgRes] = await Promise.all([catalog.listItems({ type: "FG" }), catalog.listItems({ type: "SFG" })]);
    // Starting-room map: each routing's firstWorkCenter = the item's starting room.
    const rtRes = await catalog.listRoutings();
    // Active employees for the REQUIRED per-PO "Assign to" picker. Use the
    // auth-only /tasks/assignees (NOT the settings-gated users.list) so a
    // production manager gets a populated picker — else mandatory assignment
    // would block them from creating any work order.
    const uRes = await tasksApi.listAssignees().catch(() => ({ users: [] }));
    return { orders: details.map((d) => d.order).filter(Boolean), fg: [...(fgRes.items || []), ...(sfgRes.items || [])], routings: rtRes.routings || [], users: uRes.users || [] };
  }, [open]);
  const orders = (res.data && res.data.orders) || [];
  const fg = (res.data && res.data.fg) || [];
  const users = (res.data && res.data.users) || [];
  // itemCode → starting room (workCenter of routing step 1). Undefined = no routing.
  const roomByItem = {};
  ((res.data && res.data.routings) || []).forEach((r) => { if (r.itemCode) roomByItem[r.itemCode] = r.firstWorkCenter || null; });
  const roomOf = (code) => (code in roomByItem ? roomByItem[code] : null); // null = no routing / no starting step
  // Pending demand: lines still "Pending" (backend-derived) with qty left, grouped by item.
  const byItem = {};
  orders.forEach((o) => (o.lines || []).forEach((l) => {
    const left = (l.qtyOrdered || 0) - (l.qtyAllocated || 0);
    if (l.status === "Pending" && left > 0.0001 && l.item) {
      const k = l.item.code;
      (byItem[k] = byItem[k] || { itemCode: k, name: l.item.name, total: 0, lineCodes: [], orders: new Set() });
      byItem[k].total += left;
      byItem[k].lineCodes.push(l.lineCode);
      byItem[k].orders.add(o.code);
    }
  }));
  const rows = Object.values(byItem);
  const [sel, setSel] = useState({});
  const [assign, setAssign] = useState({}); // itemCode → assignedUserId (Phase 1)
  const [stockItem, setStockItem] = useState(""); const [stockQty, setStockQty] = useState(""); const [stockAssign, setStockAssign] = useState("");
  const [extra, setExtra] = useState([]);
  const [busy, setBusy] = useState(false); const [err, setErr] = useState("");
  const toggle = (it) => setSel((s) => { const n = { ...s }; if (n[it.itemCode] != null) delete n[it.itemCode]; else n[it.itemCode] = Number(it.total.toFixed(2)); return n; });
  const addStock = () => { if (stockItem && Number(stockQty) > 0 && stockAssign) { setExtra((e) => [...e, { itemCode: stockItem, qty: Number(stockQty), assignedUserId: stockAssign }]); setStockItem(""); setStockQty(""); setStockAssign(""); } };
  const userName = (id) => { const u = users.find((x) => x.id === id); return u ? u.name : null; };
  // Reusable "Assign to" employee dropdown (Performance Phase 1).
  // Assignee is REQUIRED — the empty value is a placeholder, not a valid choice.
  const AssignSelect = ({ value, onChange, className }) => (
    <select value={value || ""} onChange={(e) => onChange(e.target.value || null)} className={"rounded-md bg-white py-1 px-2 text-sm ring-1 focus:outline-none focus:ring-indigo-400 " + (value ? "ring-slate-200" : "ring-amber-300") + " " + (className || "")}>
      <option value="">Assign to…</option>
      {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
    </select>
  );
  const fgName = (code) => { const i = fg.find((x) => x.code === code); return i ? i.name : code; };
  const count = Object.keys(sel).length + extra.length;
  const [result, setResult] = useState(null); // {created:[{woCode,room,itemCount}], failed:[{room,error}]}
  // Reset transient state each time the modal opens (component stays mounted).
  React.useEffect(() => { if (open) { setResult(null); setErr(""); } }, [open]);

  // Build creation units from the selection (MTO picks + MTS extras), each with
  // its starting room, then GROUP BY starting room. Step 2: a multi-room
  // selection becomes one single-room work order PER room (auto-grouped).
  const units = [
    ...rows.filter((it) => sel[it.itemCode] != null).map((it) => ({ itemCode: it.itemCode, name: it.name, qty: Number(sel[it.itemCode]) || it.total, lineCodes: it.lineCodes, mts: false, room: roomOf(it.itemCode), assignedUserId: assign[it.itemCode] || null })),
    ...extra.map((ex) => ({ itemCode: ex.itemCode, name: fgName(ex.itemCode), qty: ex.qty, lineCodes: null, mts: true, room: roomOf(ex.itemCode), assignedUserId: ex.assignedUserId || null })),
  ];
  const noRoomUnits = units.filter((u) => !u.room); // no routing → excluded
  const groupsMap = {};
  units.filter((u) => u.room).forEach((u) => { (groupsMap[u.room] = groupsMap[u.room] || []).push(u); });
  const groups = Object.entries(groupsMap).map(([room, us]) => ({ room, units: us })); // one WO per room
  const hasMissing = noRoomUnits.length > 0;
  // Every selected/extra unit must have an assignee (mandatory on WO creation).
  const unassignedUnits = units.filter((u) => !u.assignedUserId);
  const needsAssignee = unassignedUnits.length > 0;
  // No-routing items or any unassigned item block submit; multi-room is fine.
  const blocked = count > 0 && (hasMissing || groups.length === 0 || needsAssignee);

  const RoomTag = ({ code }) => {
    const room = roomOf(code);
    return room
      ? <Badge tone="sky">{room}</Badge>
      : <Badge tone="rose"><AlertTriangle size={11} /> no routing</Badge>;
  };

  const create = async () => {
    if (groups.length === 0) return;
    if (hasMissing) { setErr("Remove items with no routing/starting room before creating: " + [...new Set(noRoomUnits.map((u) => u.itemCode))].join(", ") + "."); return; }
    if (needsAssignee) { setErr("Every item must be assigned to an employee before creating a work order — pick an 'Assign to' for each."); return; }
    setBusy(true); setErr(""); setResult(null);
    const created = []; const failed = [];
    // One work order per room-group. Each group is single-room → passes the
    // backend guard. A group that fails is reported; earlier groups stand.
    for (const g of groups) {
      try {
        const poCodes = [];
        for (const u of g.units) {
          const r = u.mts
            ? await productionApi.createPO({ itemCode: u.itemCode, qty: u.qty, forStock: true, assignedUserId: u.assignedUserId || undefined })
            : await productionApi.createPO({ sourceLineCodes: u.lineCodes, qty: u.qty, assignedUserId: u.assignedUserId || undefined });
          poCodes.push(r.order.code);
        }
        const r = await productionApi.createWorkOrder(poCodes);
        created.push({ woCode: r.workOrder.code, room: g.room, itemCount: g.units.length });
      } catch (e) {
        failed.push({ room: g.room, error: e instanceof ApiError ? e.message : "creation failed" });
      }
    }
    onCreated && onCreated(); // refresh the WO list to show whatever was created
    if (failed.length === 0) { setSel({}); setExtra([]); setAssign({}); }
    setResult({ created, failed });
    setBusy(false);
  };

  const btnLabel = busy
    ? "Creating…"
    : groups.length > 1
      ? "Create " + groups.length + " work orders"
      : "Create work order" + (count ? " (" + count + " item)" : "");

  return (
    <Modal open={open} onClose={onClose} title="Create work order" wide
      footer={result
        ? <Btn onClick={() => { onClose(); }}>Done</Btn>
        : <><Btn variant="soft" onClick={onClose}>Cancel</Btn><Btn onClick={create} disabled={busy || count === 0 || blocked}><Layers size={14} /> {btnLabel}</Btn></>}>
      {res.loading && !res.data ? <LoadingBlock label="Loading order demand…" /> : res.error ? <InlineError message={res.error} onRetry={res.reload} /> : result ? (
        <div className="space-y-3">
          {result.created.length > 0 && (
            <div className="rounded-lg bg-emerald-50 p-3 ring-1 ring-emerald-200">
              <div className="mb-1 flex items-center gap-1.5 text-sm font-semibold text-emerald-700"><CheckCircle2 size={15} /> Created {result.created.length} work order{result.created.length === 1 ? "" : "s"}</div>
              {result.created.map((c) => <div key={c.woCode} className="text-xs text-emerald-800"><Mono className="font-semibold">{c.woCode}</Mono> — {c.room} · {c.itemCount} item{c.itemCount === 1 ? "" : "s"}</div>)}
            </div>
          )}
          {result.failed.length > 0 && (
            <div className="rounded-lg bg-rose-50 p-3 ring-1 ring-rose-200">
              <div className="mb-1 flex items-center gap-1.5 text-sm font-semibold text-rose-700"><AlertTriangle size={15} /> {result.failed.length} group{result.failed.length === 1 ? "" : "s"} failed</div>
              {result.failed.map((f, i) => <div key={i} className="text-xs text-rose-700"><b>{f.room}</b>: {f.error}</div>)}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {err ? <InlineError message={err} /> : null}
          {/* Grouping preview: how the selection splits into work orders by room. */}
          {groups.length > 0 && (
            <div className="rounded-lg bg-indigo-50/60 p-3 ring-1 ring-indigo-200/70">
              <div className="mb-1.5 text-sm font-semibold text-slate-700">This will create {groups.length} work order{groups.length === 1 ? "" : "s"}:</div>
              <div className="space-y-1.5">
                {groups.map((g, i) => (
                  <div key={g.room} className="text-xs">
                    <span className="font-medium text-slate-700">Work Order {i + 1}</span> — <Badge tone="sky">{g.room}</Badge>
                    <span className="text-slate-500">: {g.units.map((u) => u.name + " (" + fmtQty(u.qty) + ")" + (u.assignedUserId ? " → " + (userName(u.assignedUserId) || "?") : "")).join(", ")}</span>
                  </div>
                ))}
              </div>
              {hasMissing && <div className="mt-1.5 flex items-start gap-1.5 text-xs text-rose-600"><AlertTriangle size={12} className="mt-0.5 shrink-0" /> Excluded (no routing): <b>{[...new Set(noRoomUnits.map((u) => u.itemCode))].join(", ")}</b> — remove before creating.</div>}
              {needsAssignee && <div className="mt-1.5 flex items-start gap-1.5 text-xs text-amber-600"><AlertTriangle size={12} className="mt-0.5 shrink-0" /> Every item needs an <b>Assign to</b> before you can create the work order{unassignedUnits.length ? " (" + unassignedUnits.length + " missing)" : ""}.</div>}
            </div>
          )}
          {groups.length === 0 && hasMissing && (
            <div className="flex items-start gap-2 rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-700 ring-1 ring-rose-200"><AlertTriangle size={14} className="mt-0.5 shrink-0" /> The selected item(s) have no routing/starting room and can't be made into a work order.</div>
          )}
          <div>
            <div className="mb-1.5 text-sm font-semibold text-slate-700">Pending demand (live orders) — same item auto-merge</div>
            {rows.length === 0 ? <p className="text-sm text-slate-400">No pending order demand right now. You can add a make-to-stock item below.</p> : (
              <div className="overflow-x-auto rounded-lg ring-1 ring-slate-200">
                <table className="w-full"><thead className="bg-slate-50/60"><tr><Th>Pick</Th><Th>Item</Th><Th>Starts in</Th><Th>Orders (merged)</Th><Th className="text-right">Pending qty</Th><Th className="text-right">WO qty</Th><Th>Assign to</Th></tr></thead>
                  <tbody className="divide-y divide-slate-50">
                    {rows.map((it) => { const on = sel[it.itemCode] != null; return (
                      <tr key={it.itemCode} className={on ? "bg-amber-50/40" : ""}>
                        <Td><input type="checkbox" checked={on} onChange={() => toggle(it)} className="h-4 w-4 accent-amber-600" /></Td>
                        <Td className="font-medium text-slate-700">{it.name}<div className="text-xs font-normal text-slate-400"><Mono>{it.itemCode}</Mono></div></Td>
                        <Td><RoomTag code={it.itemCode} /></Td>
                        <Td className="text-xs text-slate-500"><Mono>{[...it.orders].join(", ")}</Mono> <span className="text-slate-400">({it.lineCodes.length} line)</span></Td>
                        <Td className="text-right"><Mono>{fmtQty(it.total)}</Mono></Td>
                        <Td className="text-right">{on ? <input type="number" value={sel[it.itemCode]} onChange={(e) => setSel((s) => ({ ...s, [it.itemCode]: e.target.value }))} className="w-24 rounded-md bg-white py-1 px-2 text-right text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400" /> : <span className="text-slate-300">—</span>}</Td>
                        <Td>{on ? <AssignSelect value={assign[it.itemCode]} onChange={(v) => setAssign((a) => ({ ...a, [it.itemCode]: v }))} /> : <span className="text-slate-300">—</span>}</Td>
                      </tr>
                    ); })}
                  </tbody></table>
              </div>
            )}
          </div>
          <div className="rounded-lg bg-slate-50/70 p-3">
            <div className="mb-1.5 text-sm font-semibold text-slate-700">+ Make-to-stock item (optional)</div>
            <div className="flex flex-wrap items-center gap-2">
              <select value={stockItem} onChange={(e) => setStockItem(e.target.value)} className="rounded-md bg-white py-1.5 px-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400"><option value="">Item select…</option>{fg.map((i) => <option key={i.code} value={i.code}>{i.name} ({i.type}){roomOf(i.code) ? " — " + roomOf(i.code) : ""}</option>)}</select>
              <input type="number" value={stockQty} onChange={(e) => setStockQty(e.target.value)} placeholder="Qty (Kg)" className="w-28 rounded-md bg-white py-1.5 px-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400" />
              <AssignSelect value={stockAssign} onChange={(v) => setStockAssign(v)} />
              <Btn variant="soft" onClick={addStock} disabled={!stockItem || !(Number(stockQty) > 0) || !stockAssign}><Plus size={13} /> Add</Btn>
              {extra.map((e, i) => <Badge key={i} tone="violet">{fgName(e.itemCode)}{roomOf(e.itemCode) ? " · " + roomOf(e.itemCode) : " · no routing"} · {fmtQty(e.qty)}{e.assignedUserId ? " · " + (userName(e.assignedUserId) || "?") : ""}<button onClick={() => setExtra((x) => x.filter((_, k) => k !== i))} className="ml-1"><X size={11} /></button></Badge>)}
            </div>
          </div>
          <p className="text-xs text-slate-400">Each pick creates a production order (MTO: against the order line; MTS: for stock). Items are <b>auto-grouped by starting room</b> — one work order per room (each work order is single-room). Select across rooms freely; the preview above shows the split. If an item has no active BOM + routing the backend returns 400 (shown here).</p>
        </div>
      )}
    </Modal>
  );
}

function WorkOrdersView({ auth, role }) {
  const { data, loading, error, reload } = useResource(async () => {
    const [wos, pos] = await Promise.all([productionApi.listWorkOrders(), productionApi.listPOs({ limit: 200 })]);
    const byCode = {}; ((pos && pos.orders) || []).forEach((p) => { byCode[p.code] = p; });
    return { workOrders: (wos && wos.workOrders) || [], poByCode: byCode };
  }, []);
  const workOrders = (data && data.workOrders) || [];
  const poByCode = (data && data.poByCode) || {};
  const [createOpen, setCreateOpen] = useState(false);
  const [issueCode, setIssueCode] = useState(null);
  const [detailCode, setDetailCode] = useState(null);
  const [matWo, setMatWo] = useState(null); // WO whose consolidated-materials panel is open
  const [issueAllWo, setIssueAllWo] = useState(null); // { code, poCodes } for the Issue-all preview
  const [view, setView] = useState("cards");
  const [openWo, setOpenWo] = useState(null);
  const [busyCode, setBusyCode] = useState(null);
  const [actErr, setActErr] = useState("");
  const [finishTarget, setFinishTarget] = useState(null); // PRODUCTION #9
  const pcan = (k) => authCan(auth, role, k);
  const canMake = pcan("stockPO");
  const linesOf = (wo) => wo.poCodes.map((c) => poByCode[c]).filter(Boolean);
  const woStatus = (ls) => { if (!ls.length) return "Empty"; if (ls.every((p) => p.status === "Done")) return "Done"; if (ls.some((p) => p.status === "In Production")) return "In Production"; return "Planned"; };
  const act = async (code, fn) => {
    setBusyCode(code); setActErr("");
    try { await fn(); await reload(); }
    catch (e) { setActErr((e instanceof ApiError ? e.message : "Action failed.") + " (" + code + ")"); }
    finally { setBusyCode(null); }
  };
  const runLoop = async (codes, fn, label) => {
    if (!codes.length) return;
    setBusyCode(label); setActErr("");
    const errs = [];
    for (const c of codes) { try { await fn(c); } catch (e) { errs.push(c + ": " + (e instanceof ApiError ? e.message : "failed")); } }
    setBusyCode(null); setActErr(errs.join(" · "));
    await reload();
  };
  const stepLabel = (p) => p.status === "In Production" && p.currentStep ? "In " + p.currentStep.workCenter + (p.steps ? " · step " + (p.stepIdx + 1) + " of " + p.steps.length : "") : null;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-1 rounded-lg bg-slate-100 p-1">
          {[["list", "List"], ["cards", "Cards"]].map(([k, l]) => <button key={k} onClick={() => { setView(k); setDrawerOpen(false); }} className={cx("rounded-md px-3 py-1 text-sm font-medium", view === k ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700")}>{l}</button>)}
        </div>
        <div className="flex items-center gap-2">{loading && workOrders.length > 0 ? <Spinner size={14} /> : null}{canMake && <Btn onClick={() => setCreateOpen(true)}><Plus size={15} /> Create work order</Btn>}</div>
      </div>
      <p className="-mt-2 text-xs text-slate-400">Multi-item work order — bundle order demand (same item merged) + make-to-stock; for each member PO: issue RM → start → advance. <span className="text-slate-300">"Add item" has no backend, so it is hidden.</span></p>
      {actErr ? <InlineError message={actErr} /> : null}
      {error ? <InlineError message={error} onRetry={reload} /> : null}
      {loading && workOrders.length === 0 ? <LoadingBlock label="Loading work orders…" /> : workOrders.length === 0 ? (
        <Card className="p-8 text-center text-sm text-slate-400">No work orders yet. Use "Create work order" to bundle order demand.</Card>
      ) : view === "list" ? (
        <Card className="overflow-x-auto"><table className="w-full">
          <thead className="border-b border-slate-100"><tr><Th>WO</Th><Th>PO</Th><Th>Item</Th><Th>Assigned</Th><Th className="text-right">Qty</Th><Th className="text-right">RM issued</Th><Th>Status / step</Th></tr></thead>
          <tbody className="divide-y divide-slate-50">
            {workOrders.flatMap((wo) => linesOf(wo).map((p) => (
              <tr key={wo.code + p.code} className="cursor-pointer hover:bg-slate-50/50" onClick={() => setDetailCode(p.code)}>
                <Td><Mono className="text-slate-500">{wo.code}</Mono></Td>
                <Td><Mono className="font-semibold text-slate-800">{p.code}</Mono></Td>
                <Td className="text-slate-700">{p.item.name}</Td>
                <Td className="text-xs">{p.assignedUser ? <span className="text-slate-600">{p.assignedUser.name}</span> : <span className="text-slate-300">Unassigned</span>}</Td>
                <Td className="text-right"><Mono>{fmtQty(p.plannedQty)}</Mono></Td>
                <Td className="text-right"><Mono className={(p.issuedQty || 0) >= p.plannedQty - 0.001 ? "text-emerald-600" : (p.issuedQty || 0) > 0 ? "text-amber-600" : "text-slate-400"}>{fmtQty(p.issuedQty || 0)}/{fmtQty(p.plannedQty)}</Mono></Td>
                <Td>{stepLabel(p) ? <span className="text-xs text-slate-600">{stepLabel(p)}</span> : <Badge tone={statusTone(p.status)}>{p.status}</Badge>}{p.completedAt && <div className="mt-0.5 text-[10px] text-emerald-600">✓ {fmtDateTime(p.completedAt)}</div>}</Td>
              </tr>
            )))}
          </tbody>
        </table></Card>
      ) : workOrders.map((wo) => {
        const ls = linesOf(wo); const status = woStatus(ls); const open = openWo === wo.code;
        const issued = ls.reduce((a, p) => a + (p.issuedQty || 0), 0); const planned = ls.reduce((a, p) => a + p.plannedQty, 0);
        const underIssued = ls.filter((p) => p.status === "In Production" && (p.issuedQty || 0) < p.plannedQty - 0.001);
        const startable = ls.filter((p) => p.status === "Planned");
        return (
          <Card key={wo.code} className="overflow-hidden">
            <button onClick={() => setOpenWo(open ? null : wo.code)} className="flex w-full flex-wrap items-center justify-between gap-2 px-4 py-3 text-left hover:bg-slate-50/60">
              <span className="flex items-center gap-2.5"><ChevronRight size={15} className={cx("text-slate-400 transition", open ? "rotate-90" : "")} /><Layers size={17} className="text-amber-600" /><Mono className="font-semibold text-slate-800">{wo.code}</Mono><Badge tone={statusTone(status)}>{status}</Badge><span className="text-xs text-slate-400">{wo.poCount} item · {fmtDate(wo.createdAt)}</span></span>
              <span className="text-xs text-slate-500">RM issued <Mono className={issued >= planned - 0.001 ? "text-emerald-600" : "text-amber-600"}>{fmtQty(issued)}/{fmtQty(planned)}</Mono></span>
            </button>
            {open && <div className="space-y-4 border-t border-slate-100 p-4">
              <div className="overflow-x-auto rounded-lg ring-1 ring-slate-200">
                <table className="w-full"><thead className="bg-slate-50/60"><tr><Th>Item</Th><Th>For</Th><Th>Assigned</Th><Th className="text-right">Qty</Th><Th className="text-right">RM issued</Th><Th>Status / step</Th><Th>Action</Th></tr></thead>
                  <tbody className="divide-y divide-slate-50">
                    {ls.map((p) => (
                      <tr key={p.code}>
                        <Td className="font-medium text-slate-700">{p.item.name}<div className="text-xs font-normal text-slate-400"><Mono>{p.code}</Mono></div></Td>
                        <Td className="text-xs text-slate-500">{p.forStock ? "Make-to-stock" : (p.sourceLineCodes || []).join(", ") || "—"}</Td>
                        <Td className="text-xs">{p.assignedUser ? <span className="text-slate-600">{p.assignedUser.name}</span> : <span className="text-slate-300">Unassigned</span>}</Td>
                        <Td className="text-right"><Mono>{fmtQty(p.plannedQty)}</Mono></Td>
                        <Td className="text-right"><Mono className={(p.issuedQty || 0) >= p.plannedQty - 0.001 ? "text-emerald-600" : "text-amber-600"}>{fmtQty(p.issuedQty || 0)}/{fmtQty(p.plannedQty)}</Mono></Td>
                        <Td>{stepLabel(p) ? <span className="text-xs text-slate-600">{stepLabel(p)}</span> : <Badge tone={statusTone(p.status)}>{p.status}</Badge>}{p.completedAt && <div className="mt-0.5 text-[10px] text-emerald-600">✓ {fmtDateTime(p.completedAt)}{p.completedByUser ? " · " + p.completedByUser.name.split(" ")[0] : ""}{p.rawDurationHours != null ? " · " + p.rawDurationHours + "h" : ""}</div>}</Td>
                        <Td><div className="flex flex-wrap gap-1.5">
                          {p.status === "In Production" && pcan("issue") && <Btn variant="soft" disabled={busyCode === p.code} onClick={() => setIssueCode(p.code)}><Package size={12} /> Issue/Return</Btn>}
                          {p.status === "Planned" && authCanPo(auth, role, "startProd", p) && <Btn variant="soft" disabled={busyCode === p.code} onClick={() => act(p.code, () => productionApi.startPO(p.code))}><Factory size={12} /> Start</Btn>}
                          {p.status === "In Production" && authCanPo(auth, role, "advanceProd", p) && <Btn variant="soft" disabled={busyCode === p.code} onClick={() => act(p.code, () => productionApi.advancePO(p.code))}>Advance <ArrowRight size={12} /></Btn>}
                          {p.status !== "Done" && p.isActive && authCanPo(auth, role, "stockPO", p) && <Btn variant="soft" disabled={busyCode === p.code} onClick={() => setFinishTarget({ code: p.code, plannedQty: p.plannedQty, run: (out) => productionApi.completePO(p.code, out) })}><CheckCircle2 size={12} /> Complete</Btn>}
                          <button onClick={() => setDetailCode(p.code)} className="rounded-md px-2 py-1 text-xs font-medium text-slate-500 ring-1 ring-slate-200 hover:bg-slate-50">Detail</button>
                        </div></Td>
                      </tr>
                    ))}
                  </tbody></table>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Btn variant="soft" onClick={() => setMatWo(wo.code)}><Boxes size={13} /> Consolidated materials</Btn>
                {underIssued.length > 0 && pcan("issue") && <Btn variant="soft" disabled={!!busyCode} onClick={() => setIssueAllWo({ code: wo.code, poCodes: underIssued.map((p) => p.code) })}><Package size={13} /> Issue all remaining ({underIssued.length})</Btn>}
                {startable.length > 0 && pcan("startProd") && <Btn variant="soft" disabled={!!busyCode} onClick={() => runLoop(startable.map((p) => p.code), (c) => productionApi.startPO(c), "start-all-" + wo.code)}><Factory size={13} /> Start all ({startable.length})</Btn>}
              </div>
            </div>}
          </Card>
        );
      })}
      <CreateWOModal open={createOpen} onClose={() => setCreateOpen(false)} onCreated={reload} />
      <LiveIssueModal key={issueCode || "noissue"} code={issueCode} open={!!issueCode} onClose={() => setIssueCode(null)} onChanged={reload} auth={auth} />
      <PoDetailModal key={detailCode || "nodetail"} code={detailCode} auth={auth} role={role} open={!!detailCode} onClose={() => setDetailCode(null)} onChanged={reload} onIssue={(c) => { setDetailCode(null); setIssueCode(c); }} />
      <WoMaterialsModal key={matWo || "nomat"} code={matWo} open={!!matWo} onClose={() => setMatWo(null)} canSend={pcan("issue") || canMake}
        onIssueAll={pcan("issue") ? (poCodes) => { const c = matWo; setMatWo(null); setIssueAllWo({ code: c, poCodes }); } : null} />
      <IssueAllPreviewModal key={(issueAllWo && issueAllWo.code) || "noissueall"} woCode={issueAllWo && issueAllWo.code} poCodes={issueAllWo && issueAllWo.poCodes} open={!!issueAllWo} onClose={() => setIssueAllWo(null)} onDone={reload} />
      <FinishPoModal target={finishTarget} onClose={() => setFinishTarget(null)} onDone={reload} />
    </div>
  );
}

// Consolidated materials for a whole work order — total direct RM/PM required to
// produce ALL its items, shared components summed, each paired with on-hand stock
// + a low-stock/shortfall flag. One combined list so the employee gathers all
// materials at once instead of opening each item's detail. (Per-component ISSUED
// is intentionally not shown — issuance isn't tracked per component; this is
// required-vs-on-hand, which is truthful.)
function WoMaterialsModal({ code, open, onClose, canSend, onIssueAll }) {
  const { data, loading, error, reload } = useResource(
    () => (code ? productionApi.getWorkOrderMaterials(code) : Promise.resolve(null)),
    [code],
  );
  // ROOM READ-BACK (Phase B, display-only): the batches the store recorded as
  // physically sent, per RM material, aggregated across the WO's member POs. Reads
  // the Phase-A GET /issued-batches; changes no write path. getWoMaterials doesn't
  // expose member PO codes, so derive them frontend-only via listPOs.woCode.
  const sentRes = useResource(async () => {
    if (!code || !open) return null;
    const pos = ((await productionApi.listPOs({ limit: 200 })).orders || []).filter((p) => p.woCode === code);
    const perPo = await Promise.all(pos.map(async (p) => ({ poCode: p.code, issued: ((await productionApi.getIssuedBatches(p.code)).issued) || [] })));
    const byMat = {}; // RM material code → [{ poCode, batchCode, qty, qcDate }]
    perPo.forEach(({ poCode, issued }) => issued.forEach((r) => { (byMat[r.item.code] = byMat[r.item.code] || []).push({ poCode, batchCode: r.batchCode, qty: r.qty, qcDate: r.qcDate }); }));
    // `pos` is kept as-is: listPOs summaries already carry assignedUser, so the
    // RESPONSIBLE strip below costs no extra request and no backend change.
    return { byMat, poCount: pos.length, pos };
  }, [code, open]);
  const sentByMat = (sentRes.data && sentRes.data.byMat) || {};
  const multiPo = (sentRes.data && sentRes.data.poCount) > 1;
  const woPos = (sentRes.data && sentRes.data.pos) || [];
  // Mirrors WorkOrdersView's own `underIssued` EXACTLY so the in-modal issue
  // button offers precisely the POs the WO-row button does — no new rule.
  const underIssuedPos = woPos.filter((p) => p.status === "In Production" && (p.issuedQty || 0) < p.plannedQty - 0.001);
  const d = data;
  const room = d && d.room;
  const [mode, setMode] = useState("view"); // "view" | "send"
  const [lines, setLines] = useState({}); // itemCode → qty string (editable send amounts)
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null); // { room, count, results:[{itemCode, sent, shortfall}] }
  React.useEffect(() => { if (open) { setMode("view"); setResult(null); setErr(""); } }, [open, code]);
  const statusCell = (m) => {
    if (m.status === "short") return <Badge tone="rose"><AlertTriangle size={11} /> Short by {fmtQty(m.shortBy)}</Badge>;
    if (m.status === "low") return <Badge tone="amber"><AlertTriangle size={11} /> Low stock</Badge>;
    return <Badge tone="emerald">OK</Badge>;
  };
  const toSend = (d && d.materials ? d.materials.filter((m) => m.toSend > 0) : []);
  const startSend = () => {
    const init = {}; toSend.forEach((m) => { init[m.code] = String(m.toSend); });
    setLines(init); setResult(null); setErr(""); setMode("send");
  };
  const sendLinesArr = () => Object.entries(lines).map(([itemCode, q]) => ({ itemCode, qty: Number(q) || 0 })).filter((l) => l.qty > 0);
  const confirmSend = async () => {
    const ls = sendLinesArr();
    if (!room || ls.length === 0 || busy) return;
    setBusy(true); setErr("");
    try {
      const r = await stockApi.transferToRoomBatch({ room, lines: ls });
      setResult(r); await reload(); setMode("view");
    } catch (e) { setErr(e instanceof ApiError ? e.message : "Send failed."); }
    finally { setBusy(false); }
  };
  const sentByCode = result ? Object.fromEntries(result.results.map((r) => [r.itemCode, r])) : {};
  return (
    <Modal open={open} onClose={onClose} title={"Consolidated materials — " + (code || "")} wide
      footer={mode === "send"
        ? <><Btn variant="soft" onClick={() => setMode("view")}>Back</Btn><Btn onClick={confirmSend} disabled={busy || sendLinesArr().length === 0}><ArrowRight size={14} /> {busy ? "Sending…" : "Send " + sendLinesArr().length + " to " + (room || "room")}</Btn></>
        : <Btn variant="soft" onClick={onClose}>Close</Btn>}>
      {loading && !d ? <LoadingBlock label="Computing materials…" /> : error ? <InlineError message={error} onRetry={reload} /> : d ? (
        mode === "send" ? (
          <div className="space-y-3">
            {err ? <InlineError message={err} /> : null}
            <div className="text-sm text-slate-600">Move materials from the store into <b className="text-slate-800">{room}</b> — the WO's process room. Adjust any quantity before sending; each is capped at store on-hand.</div>
            <div className="rounded-lg bg-sky-50/60 px-3 py-2 text-xs text-slate-600 ring-1 ring-sky-100">This is a <b className="text-slate-700">stock move</b>, not an issue — it records no batch. Use <b className="text-slate-700">Issue all remaining</b> (or a PO's <b className="text-slate-700">Issue</b>) to consume the materials and record which batch went.</div>
            <div className="overflow-x-auto rounded-lg ring-1 ring-slate-200"><table className="w-full">
              <thead className="bg-slate-50/60"><tr><Th>Component</Th><Th className="text-right whitespace-nowrap">In store</Th><Th className="text-right whitespace-nowrap">Send qty</Th></tr></thead>
              <tbody className="divide-y divide-slate-50">
                {toSend.length === 0 ? <tr><Td className="text-slate-400" colSpan={3}>Nothing to send — the room already has everything.</Td></tr> : toSend.map((m) => (
                  <tr key={m.code}>
                    <Td className="font-medium text-slate-700">{m.name}<div className="text-xs font-normal text-slate-400"><Mono>{m.code}</Mono></div></Td>
                    <Td className="text-right"><Mono className="text-slate-500">{fmtQty(m.available)}</Mono></Td>
                    <Td className="text-right"><input type="number" value={lines[m.code] ?? ""} onChange={(e) => setLines((s) => ({ ...s, [m.code]: e.target.value }))} className={cx("mono w-28 rounded-md bg-white py-1 px-2 text-right text-sm ring-1 focus:outline-none focus:ring-indigo-400", (Number(lines[m.code]) || 0) > m.available + 1e-6 ? "ring-amber-300" : "ring-slate-200")} /></Td>
                  </tr>
                ))}
              </tbody></table></div>
            <p className="text-xs text-slate-400">One atomic transfer moves all lines from store → the room in a single call. A line above store on-hand sends what's available and reports the shortfall.</p>
          </div>
        ) : (
        <div className="space-y-3">
          <div className="text-sm text-slate-600">
            Materials to produce <Mono className="font-semibold text-slate-800">{d.woCode}</Mono>
            <span className="text-slate-400"> ({d.items.map((i) => i.name + " ×" + fmtQty(i.plannedQty)).join(", ")})</span>
          </div>
          {/* RESPONSIBLE — per PO, from the summaries listPOs already returned for
              the read-back (they carry assignedUser). A WO can span several POs and
              therefore several assignees, so each PO is listed rather than one name.
              Errors are surfaced, never silently blank (same rule as the read-back). */}
          <div className="flex flex-wrap items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-xs ring-1 ring-slate-100">
            <span className="font-semibold text-slate-600"><Users size={12} className="mr-1 inline text-amber-600" />Responsible</span>
            {sentRes.loading ? <span className="text-slate-400">Loading…</span>
              : sentRes.error ? <span className="text-rose-600">Couldn't load — <button onClick={sentRes.reload} className="underline">retry</button></span>
              : woPos.length === 0 ? <span className="text-slate-400">No production orders found for this work order.</span>
              : woPos.map((p) => (
                  <span key={p.code} className="inline-flex items-center gap-1.5 rounded-md bg-white px-2 py-0.5 ring-1 ring-slate-200">
                    <Mono className="text-slate-400">{p.code}</Mono>
                    <span className={p.assignedUser ? "font-medium text-slate-700" : "text-slate-400"}>{p.assignedUser ? p.assignedUser.name : "Unassigned"}</span>
                  </span>
                ))}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-xs text-slate-500">
              <b>{d.componentCount}</b> component{d.componentCount === 1 ? "" : "s"} · process room <b className="text-slate-700">{room || "—"}</b>
              {d.shortfallCount > 0
                ? <> · <span className="font-semibold text-rose-600">{d.shortfallCount} short (room + store)</span></>
                : <> · <span className="text-emerald-600">all covered</span></>}
              {d.toSendCount > 0 && <> · <span className="text-amber-700">{d.toSendCount} still to send</span></>}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {canSend && room && d.toSendCount > 0 && <Btn variant="soft" onClick={startSend}><ArrowRight size={13} /> Send stock to {room}</Btn>}
              {onIssueAll && underIssuedPos.length > 0 && <Btn variant="soft" onClick={() => onIssueAll(underIssuedPos.map((p) => p.code))}><Package size={13} /> Issue all remaining ({underIssuedPos.length})</Btn>}
            </div>
          </div>
          {/* ISSUE vs TRANSFER — the two are different operations and used to sit
              side by side unlabelled, which is why the store gathered via the
              transfer and no batch was ever recorded. Say it plainly. */}
          <div className="rounded-lg bg-sky-50/60 px-3 py-2 text-xs text-slate-600 ring-1 ring-sky-100">
            <b className="text-slate-700">Send stock to {room || "the room"}</b> moves materials from the store into the room so they're ready to use — it is <b>not</b> issuing, and it records no batch.
            {" "}<b className="text-slate-700">Issue</b> consumes the materials against the work order and records <b>which batch</b> went — that's what fills “Sent batches” below.
          </div>
          {result && <div className="rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-800 ring-1 ring-emerald-200"><CheckCircle2 size={13} className="mr-1 inline" /> Sent {result.count} component{result.count === 1 ? "" : "s"} to <b>{result.room}</b> in one transfer{result.results.some((r) => r.shortfall > 0.001) ? <> · <span className="text-amber-700">{result.results.filter((r) => r.shortfall > 0.001).length} capped at store on-hand</span></> : ""}.</div>}
          <div className="overflow-x-auto rounded-lg ring-1 ring-slate-200">
            <table className="w-full">
              <thead className="bg-slate-50/60"><tr><Th>Component</Th><Th>Type</Th><Th className="text-right whitespace-nowrap">Required</Th><Th className="text-right whitespace-nowrap">In room</Th><Th className="text-right whitespace-nowrap">In store</Th><Th className="text-right whitespace-nowrap">Still to send</Th><Th className="whitespace-nowrap">Status</Th></tr></thead>
              <tbody className="divide-y divide-slate-50">
                {d.materials.length === 0 ? <tr><Td className="text-slate-400" colSpan={7}>No materials (no BOM found for this work order's items).</Td></tr> : d.materials.map((m) => (
                  <tr key={m.code} className={m.status === "short" ? "bg-rose-50/40" : m.status === "low" ? "bg-amber-50/30" : ""}>
                    <Td className="font-medium text-slate-700">{m.name}<div className="text-xs font-normal text-slate-400"><Mono>{m.code}</Mono></div></Td>
                    <Td><Badge tone={m.type === "PM" ? "sky" : m.type === "SFG" ? "violet" : "stone"}>{m.type}</Badge></Td>
                    <Td className="text-right"><Mono className="font-semibold text-slate-800">{fmtQty(m.totalRequired)}</Mono></Td>
                    <Td className="text-right"><Mono className={m.inRoom > 0 ? "text-amber-700" : "text-slate-300"}>{fmtQty(m.inRoom)}</Mono></Td>
                    <Td className="text-right"><Mono className={m.status === "short" ? "text-rose-600" : m.status === "low" ? "text-amber-600" : "text-slate-500"}>{fmtQty(m.available)}</Mono></Td>
                    <Td className="text-right"><Mono className={m.toSend > 0 ? "text-slate-700" : "text-emerald-600"}>{m.toSend > 0 ? fmtQty(m.toSend) : "✓"}</Mono></Td>
                    <Td>{statusCell(m)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-400"><b>In room</b> = already in {room || "the process room"}; <b>Still to send</b> = required − in-room (capped by store). Status is short only if room + store together can't cover it. Shared components are summed across the WO's items.</p>
          {/* SENT BATCHES — the store's record of which physical bags they actually
              sent, for the room to verify against. Shows the RECORDED batch (which
              may differ from FEFO if a bag was misplaced), not a computed guess. */}
          <div className="rounded-lg ring-1 ring-slate-200">
            <div className="border-b border-slate-100 px-3 py-2 text-sm font-semibold text-slate-700"><ClipboardList size={14} className="mr-1.5 inline text-amber-600" />Sent batches <span className="text-xs font-normal text-slate-400">— physically verify each bag against this record</span></div>
            {sentRes.loading ? <p className="px-3 py-2 text-xs text-slate-400">Loading recorded batches…</p>
              : sentRes.error ? (
                // A FAILED read must never render as "no batches recorded" — that is
                // indistinguishable from a truthful empty and hides a real 403/network fault.
                <div className="px-3 py-2"><InlineError message={"Couldn't load the sent-batch record — " + sentRes.error} onRetry={sentRes.reload} /></div>
              ) : (
              <div className="divide-y divide-slate-50">
                {d.materials.filter((m) => m.type === "RM" || m.type === "PM").map((m) => {
                  const sent = sentByMat[m.code] || [];
                  return (
                    <div key={m.code} className="flex flex-wrap items-start gap-x-3 gap-y-1 px-3 py-2 text-xs">
                      <span className="min-w-[9rem] font-medium text-slate-700">{m.name} <Mono className="text-slate-400">{m.code}</Mono></span>
                      {sent.length === 0
                        ? <span className="text-slate-400">No batches recorded yet — the store hasn't logged what was sent.</span>
                        : <span className="flex flex-wrap gap-x-3 gap-y-1">{sent.map((b, i) => (
                            <span key={i} className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-0.5 text-emerald-800 ring-1 ring-emerald-100">
                              <Mono className="font-semibold">{b.batchCode}</Mono> · {fmtQty(b.qty)} Kg{b.qcDate ? <> · QC'd {fmtDate(b.qcDate)}</> : null}{multiPo && b.poCode ? <span className="text-emerald-600"> · {b.poCode}</span> : null}
                            </span>
                          ))}</span>}
                    </div>
                  );
                })}
                {d.materials.filter((m) => m.type === "RM" || m.type === "PM").length === 0 && <p className="px-3 py-2 text-xs text-slate-400">No RM/PM components for this work order.</p>}
              </div>
            )}
          </div>
        </div>
        )
      ) : null}
    </Modal>
  );
}

// WO-level "Issue all remaining" PREVIEW: before firing, show per under-issued PO
// what will be issued (full remaining) + the consolidated component draw with the
// room-first split (reuses the LiveIssueModal sourcing logic) + a room+store
// shortfall check. On confirm it loops the per-PO issue (the existing mechanism —
// NOT a new atomic endpoint) and reports each PO's success/failure.
function IssueAllPreviewModal({ woCode, poCodes, open, onClose, onDone }) {
  const key = poCodes ? poCodes.join(",") : "";
  const res = useResource(async () => {
    if (!open || !poCodes || poCodes.length === 0) return null;
    const details = await Promise.all(poCodes.map((c) => productionApi.getPO(c)));
    const pos = details.map((d) => d.order).filter(Boolean);
    const rooms = (await stockApi.getRooms()).rooms || [];
    const stock = (await stockApi.listStock({ limit: 200 })).stock || [];
    const batches = (await batchesApi.listBatches({ limit: 200 })).batches || [];
    return { pos, rooms, stock, batches };
  }, [open, key]);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null); // [{code, ok, qty, error}]
  React.useEffect(() => { if (open) setResults(null); }, [open, key]);
  const pos = (res.data && res.data.pos) || [];
  const room = pos[0] && pos[0].steps && pos[0].steps[0] ? pos[0].steps[0].workCenter : null;
  const roomMap = {};
  if (room) { const rm = ((res.data && res.data.rooms) || []).find((r) => r.room === room); (rm ? rm.items : []).forEach((e) => { roomMap[e.itemCode] = e.qty; }); }
  const stockMap = indexStockByCode(res.data && res.data.stock);
  // Batch state per component — the SAME three rules as the item-wise issue:
  // passed w/ remaining → FEFO-record (mandatory); pending-only → BLOCK; none → allow.
  const batchesByItem = {};
  ((res.data && res.data.batches) || []).forEach((b) => { (batchesByItem[b.item.code] = batchesByItem[b.item.code] || []).push(b); });
  Object.values(batchesByItem).forEach((a) => a.sort((x, y) => new Date(x.producedAt) - new Date(y.producedAt)));
  const passedOf = (c) => (batchesByItem[c] || []).filter((b) => b.status === "Passed" && (b.remaining || 0) > 0);
  const stateOf = (c) => (passedOf(c).length ? "mandatory" : (batchesByItem[c] || []).some((b) => b.status === "QC Pending" && (b.remaining || 0) > 0) ? "blocked" : "nobatch");
  const fefoFill = (c, need) => { let n = need, out = []; for (const b of passedOf(c)) { if (n <= 0.001) break; const take = Math.min(b.remaining, n); out.push({ batchCode: b.code, qty: Number(take.toFixed(3)) }); n -= take; } if (out.length === 0 && passedOf(c)[0]) out = [{ batchCode: passedOf(c)[0].code, qty: Number(need.toFixed(3)) }]; return out; };
  // Consolidated component draw = Σ over under-issued POs of (leaf × remaining/planned).
  const consumeByComp = {};
  const poRows = pos.map((p) => {
    const rem = Math.max(0, p.plannedQty - (p.issuedQty || 0));
    const factor = p.plannedQty > 0 ? rem / p.plannedQty : 0;
    ((p.consumes) || []).forEach((l) => {
      const c = (consumeByComp[l.code] = consumeByComp[l.code] || { code: l.code, name: l.name, type: l.type, consume: 0 });
      c.consume += l.totalQty * factor;
    });
    return { code: p.code, name: p.item.name, remaining: rem };
  });
  const compRows = Object.values(consumeByComp).map((c) => {
    const inRoom = roomMap[c.code] || 0;
    const inStore = stockMap[c.code] ? stockMap[c.code].available : 0;
    const fromRoom = Math.min(inRoom, c.consume);
    const fromStore = Math.max(0, c.consume - fromRoom);
    const short = (inRoom + inStore) < c.consume - 0.001;
    return { ...c, inRoom, inStore, fromRoom, fromStore, short, state: stateOf(c.code) };
  }).sort((a, b) => (a.short === b.short ? a.code.localeCompare(b.code) : a.short ? -1 : 1));
  const shortCount = compRows.filter((c) => c.short).length;
  const blockedComps = compRows.filter((c) => c.state === "blocked"); // not QC-passed → block the bulk
  const confirm = async () => {
    if (busy) return;
    setBusy(true);
    const out = [];
    for (const p of pos) {
      const rem = Math.max(0, p.plannedQty - (p.issuedQty || 0));
      if (rem <= 0.0001) { out.push({ code: p.code, ok: true, qty: 0, skipped: true }); continue; }
      try {
        await productionApi.issuePO(p.code, rem);
        // FEFO-record the physical batch note for each component that has passed
        // batches (mandatory). Auto-FEFO in bulk; change a bag via the item-wise issue.
        const factor = p.plannedQty > 0 ? rem / p.plannedQty : 0;
        for (const l of (p.consumes || [])) {
          if (stateOf(l.code) !== "mandatory") continue;
          const need = l.totalQty * factor;
          const lines = fefoFill(l.code, need).filter((x) => x.batchCode && x.qty > 0);
          if (lines.length) await productionApi.recordIssuedBatches(p.code, { itemCode: l.code, room: room || undefined, lines });
        }
        out.push({ code: p.code, ok: true, qty: rem });
      }
      catch (e) { out.push({ code: p.code, ok: false, error: e instanceof ApiError ? e.message : "failed" }); }
    }
    setResults(out);
    onDone && onDone();
    setBusy(false);
  };
  const okCount = results ? results.filter((r) => r.ok).length : 0;
  const failCount = results ? results.filter((r) => !r.ok).length : 0;
  return (
    <Modal open={open} onClose={onClose} wide title={"Issue all remaining — " + (woCode || "")}
      footer={results
        ? <Btn variant="soft" onClick={onClose}>Close</Btn>
        : <><Btn variant="soft" onClick={onClose}>Cancel</Btn><Btn onClick={confirm} disabled={busy || res.loading || pos.length === 0 || blockedComps.length > 0}><Package size={14} /> {busy ? "Issuing…" : "Issue all (" + poRows.length + " PO" + (poRows.length === 1 ? "" : "s") + ")"}</Btn></>}>
      {res.loading && !res.data ? <LoadingBlock label="Building preview…" /> : res.error ? <InlineError message={res.error} onRetry={res.reload} /> : results ? (
        <div className="space-y-2">
          <div className={cx("rounded-lg px-3 py-2 text-sm ring-1", failCount === 0 ? "bg-emerald-50 text-emerald-800 ring-emerald-200" : "bg-amber-50 text-amber-800 ring-amber-200")}>
            {failCount === 0 ? <><CheckCircle2 size={14} className="mr-1 inline" /> Issued {okCount} PO{okCount === 1 ? "" : "s"}.</> : <><AlertTriangle size={14} className="mr-1 inline" /> {okCount} issued, <b>{failCount} failed</b> — see below.</>}
          </div>
          <div className="divide-y divide-slate-50 rounded-lg ring-1 ring-slate-200">
            {results.map((r) => (
              <div key={r.code} className="flex items-center justify-between px-3 py-2 text-sm">
                <Mono className="font-medium text-slate-700">{r.code}</Mono>
                {r.ok ? <span className="text-emerald-600">{r.skipped ? "nothing to issue" : "✓ issued " + fmtQty(r.qty) + " Kg"}</span> : <span className="text-rose-600">✗ {r.error}</span>}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="text-sm text-slate-600">This issues the <b>full remaining</b> for each under-issued PO. Process room: <b className="text-slate-800">{room || "—"}</b>. {shortCount > 0 ? <span className="font-semibold text-rose-600">{shortCount} component{shortCount === 1 ? "" : "s"} short (room + store).</span> : <span className="text-emerald-600">All components covered by room + store.</span>}</div>
          {blockedComps.length > 0 && <div className="rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-700 ring-1 ring-rose-200"><AlertTriangle size={12} className="mr-1 inline" />These aren't QC-passed yet — pass their QC before issuing: <b>{blockedComps.map((c) => c.code).join(", ")}</b>. The batch is auto-recorded (oldest first) for QC-passed materials; change a specific bag from the item-wise <b>Issue</b>.</div>}
          <div className="rounded-lg ring-1 ring-slate-200">
            <div className="border-b border-slate-100 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">Per PO — made-item qty to issue</div>
            <div className="divide-y divide-slate-50">
              {poRows.map((p) => (
                <div key={p.code} className="flex items-center justify-between px-3 py-1.5 text-sm"><span className="text-slate-700"><Mono className="font-medium">{p.code}</Mono> · {p.name}</span><Mono className="text-slate-600">{fmtQty(p.remaining)} Kg</Mono></div>
              ))}
            </div>
          </div>
          <div className="overflow-x-auto rounded-lg ring-1 ring-slate-200"><table className="w-full">
            <thead className="bg-slate-50/60"><tr><Th>Component (consolidated)</Th><Th className="text-right whitespace-nowrap">Consume</Th><Th className="text-right whitespace-nowrap">From room</Th><Th className="text-right whitespace-nowrap">From store</Th><Th className="whitespace-nowrap">Status</Th></tr></thead>
            <tbody className="divide-y divide-slate-50">
              {compRows.map((c) => (
                <tr key={c.code} className={c.short ? "bg-rose-50/40" : ""}>
                  <Td className="font-medium text-slate-700">{c.name}<div className="text-xs font-normal text-slate-400"><Mono>{c.code}</Mono></div></Td>
                  <Td className="text-right"><Mono>{fmtQty(c.consume)}</Mono></Td>
                  <Td className="text-right"><Mono className={c.fromRoom > 0 ? "text-amber-700" : "text-slate-300"}>{fmtQty(c.fromRoom)}</Mono></Td>
                  <Td className="text-right"><Mono className="text-slate-600">{fmtQty(c.fromStore)}</Mono></Td>
                  <Td>{c.short ? <Badge tone="rose"><AlertTriangle size={11} /> Short</Badge> : <Badge tone="emerald">OK</Badge>}</Td>
                </tr>
              ))}
            </tbody></table></div>
          <p className="text-xs text-slate-400">Room-first: each component draws <b>from room</b> first, then the balance <b>from store</b>. Issuing runs one call per PO (each its own transaction); results are reported after. Short components will fail their PO's issue — send materials to the room first if needed.</p>
        </div>
      )}
    </Modal>
  );
}

// Bulk WO creation from selected WO-Pending POs. Groups the selection BY STARTING
// ROOM (one WO per room — createWo enforces single-room), requires an assignee per
// item, then: (1) assigns each PO (createWo rejects unassigned POs), (2) calls the
// existing createWo once per room group. Same-room POs merge into ONE multi-item WO.
function BulkWoModal({ pos, assignees, open, onClose, onDone }) {
  const [assign, setAssign] = useState({}); // poCode → assigneeId
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null); // { created:[{woCode,room,count}], failed:[{room,error}] }
  React.useEffect(() => {
    if (open) {
      const init = {}; pos.forEach((p) => { if (p.assignedUser) init[p.code] = p.assignedUser.id; });
      setAssign(init); setResult(null); setErr(""); setBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  // group selected POs by starting room (the WO key)
  const groups = {};
  pos.forEach((p) => { (groups[p.startingRoom] = groups[p.startingRoom] || []).push(p); });
  const groupList = Object.entries(groups).map(([room, items]) => ({ room, items }));
  const needsAssignee = pos.filter((p) => !assign[p.code]);
  const setA = (code, v) => setAssign((s) => ({ ...s, [code]: v || null }));
  const confirm = async () => {
    if (needsAssignee.length > 0 || busy || pos.length === 0) return;
    setBusy(true); setErr("");
    const created = []; const failed = [];
    // 1) set each PO's assignee — createWo requires assigned POs.
    for (const p of pos) {
      try { await productionApi.assignPO(p.code, assign[p.code]); }
      catch (e) { failed.push({ room: p.startingRoom, error: p.code + ": " + (e instanceof ApiError ? e.message : "assign failed") }); }
    }
    // 2) one createWo per starting-room group (backend enforces single-room + assignees).
    for (const g of groupList) {
      try { const r = await productionApi.createWorkOrder(g.items.map((p) => p.code)); created.push({ woCode: r.workOrder.code, room: g.room, count: g.items.length }); }
      catch (e) { failed.push({ room: g.room, error: e instanceof ApiError ? e.message : "creation failed" }); }
    }
    setResult({ created, failed });
    onDone && onDone();
    setBusy(false);
  };
  return (
    <Modal open={open} onClose={onClose} wide title={"Create work orders — " + pos.length + " PO" + (pos.length === 1 ? "" : "s") + " selected"}
      footer={result
        ? <Btn onClick={onClose}>Done</Btn>
        : <><Btn variant="soft" onClick={onClose}>Cancel</Btn><Btn onClick={confirm} disabled={busy || needsAssignee.length > 0 || pos.length === 0}><Layers size={14} /> {busy ? "Creating…" : "Create " + groupList.length + " work order" + (groupList.length === 1 ? "" : "s")}</Btn></>}>
      {result ? (
        <div className="space-y-2">
          {result.created.length > 0 && <div className="rounded-lg bg-emerald-50 p-3 ring-1 ring-emerald-200"><div className="mb-1 flex items-center gap-1.5 text-sm font-semibold text-emerald-700"><CheckCircle2 size={15} /> Created {result.created.length} work order{result.created.length === 1 ? "" : "s"}</div>{result.created.map((c) => <div key={c.woCode} className="text-xs text-emerald-800"><Mono className="font-semibold">{c.woCode}</Mono> — {c.room} · {c.count} item{c.count === 1 ? "" : "s"}</div>)}</div>}
          {result.failed.length > 0 && <div className="rounded-lg bg-rose-50 p-3 ring-1 ring-rose-200"><div className="mb-1 flex items-center gap-1.5 text-sm font-semibold text-rose-700"><AlertTriangle size={15} /> {result.failed.length} failed</div>{result.failed.map((f, i) => <div key={i} className="text-xs text-rose-700"><b>{f.room}</b>: {f.error}</div>)}</div>}
        </div>
      ) : (
        <div className="space-y-3">
          {err ? <InlineError message={err} /> : null}
          <div className="rounded-lg bg-indigo-50/60 p-3 text-sm text-slate-700 ring-1 ring-indigo-200/70">This will create <b>{groupList.length}</b> work order{groupList.length === 1 ? "" : "s"}{groupList.length > 1 ? " — one per starting room" : ""}: {groupList.map((g, i) => <span key={g.room}>{i > 0 ? ", " : ""}<b>{g.room}</b> ({g.items.length} item{g.items.length === 1 ? "" : "s"})</span>)}. Assign every item first.</div>
          {needsAssignee.length > 0 && <div className="flex items-start gap-1.5 text-xs text-amber-600"><AlertTriangle size={12} className="mt-0.5 shrink-0" /> {needsAssignee.length} item{needsAssignee.length === 1 ? "" : "s"} still need an <b>Assign to</b> before you can create.</div>}
          {groupList.map((g) => (
            <div key={g.room} className="rounded-lg ring-1 ring-slate-200">
              <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2 text-sm font-semibold text-slate-700"><Badge tone="sky">{g.room}</Badge> One work order · {g.items.length} item{g.items.length === 1 ? "" : "s"}</div>
              <div className="divide-y divide-slate-50">
                {g.items.map((p) => (
                  <div key={p.code} className="flex items-center justify-between gap-2 px-3 py-1.5 text-sm">
                    <span className="min-w-0 truncate"><Mono className="font-medium text-slate-800">{p.code}</Mono> <span className="text-slate-600">{p.item.name}</span> <span className="text-xs text-slate-400">{fmtQty(p.plannedQty)} Kg</span></span>
                    <select value={assign[p.code] || ""} onChange={(e) => setA(p.code, e.target.value)} className={cx("shrink-0 rounded-md bg-white py-1 px-2 text-xs ring-1 focus:outline-none focus:ring-indigo-400", assign[p.code] ? "ring-slate-200" : "ring-amber-300")}>
                      <option value="">Assign to…</option>
                      {assignees.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

// Shared modal: pick an employee, then either GENERATE a work order (assignee
// required server-side) or (re)ASSIGN an existing PO. Reused by the Production tabs.
function PoAssignModal({ po, action, assignees, open, onClose, onDone }) {
  const [assigneeId, setAssigneeId] = useState("");
  const [busy, setBusy] = useState(false); const [err, setErr] = useState("");
  React.useEffect(() => { if (open) { setAssigneeId((po && po.assignedUser && po.assignedUser.id) || ""); setErr(""); setBusy(false); } }, [open, po]);
  const gen = action === "generate";
  const submit = async () => {
    if (!assigneeId || busy || !po) return;
    setBusy(true); setErr("");
    try {
      if (gen) await productionApi.generateWO(po.code, assigneeId);
      else await productionApi.assignPO(po.code, assigneeId);
      onClose(); onDone && onDone();
    } catch (e) { setErr(e instanceof ApiError ? e.message : "Action failed."); setBusy(false); }
  };
  const title = gen ? "Generate work order" : (po && po.assignedUser ? "Reassign production order" : "Assign production order");
  return (
    <Modal open={open} onClose={onClose} title={title}
      footer={<><Btn variant="soft" onClick={onClose}>Cancel</Btn><Btn onClick={submit} disabled={busy || !assigneeId}>{busy ? "Saving…" : gen ? "Generate work order" : (po && po.assignedUser ? "Reassign" : "Assign")}</Btn></>}>
      {po && (
        <div className="space-y-3">
          <div className="text-sm text-slate-600"><Mono className="font-semibold text-slate-800">{po.code}</Mono> · {po.item && po.item.name} · {fmtQty(po.plannedQty)} Kg</div>
          {gen && <p className="text-xs text-slate-500">A work order sends this production to a person — pick who is responsible. It then appears in their <b>My Queue → Production</b>.</p>}
          {!gen && po.assignedUser && <p className="text-xs text-slate-500">Currently assigned to <b>{po.assignedUser.name}</b>. Pick a new employee to reassign.</p>}
          <label className="block text-sm"><span className="mb-1 block font-medium text-slate-600">Assign to <span className="text-rose-500">*</span></span>
            <select value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)} className={"w-full rounded-md bg-white py-1.5 px-2.5 text-sm ring-1 focus:outline-none focus:ring-indigo-400 " + (assigneeId ? "ring-slate-200" : "ring-amber-300")}>
              <option value="">Select an employee…</option>
              {assignees.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </label>
          {!assigneeId && <p className="text-xs text-amber-600">An assignee is required{gen ? " to generate a work order" : ""}.</p>}
          {assignees.length === 0 && <p className="text-xs text-rose-500">No employees available to assign.</p>}
          {err ? <InlineError message={err} /> : null}
        </div>
      )}
    </Modal>
  );
}

// Production screen: three status tabs so a manager sees at a glance what needs a
// work order (WO Pending) vs what's running (In Progress) vs finished (Done),
// plus the Materials (BOM) view. Search filters within the active tab.
// SALES-ORDER DEMAND → DECISION (production flow simplification). Lives at the top
// of the Production "WO Pending" tab: pending SO lines appear here directly and the
// user decides Stock / Production / Both per line — the decision that used to live
// on the Sales-order screen. The PO is NEVER surfaced: "Production" creates it (and
// its WO) transparently behind the scenes. Backend is unchanged — this only
// orchestrates the existing fulfillLine / createPO / createWorkOrder endpoints.
function SalesDemandDecider({ auth, role, onChanged }) {
  const pcan = (k) => authCan(auth, role, k);
  const canStock = pcan("fulfill");     // dispatch-from-stock allocation
  const canProduce = pcan("stockPO");   // create the production work order
  const res = useResource(async () => {
    const list = await ordersApi.listOrders();
    const details = await Promise.all(((list && list.orders) || []).map((o) => ordersApi.getOrder(o.code)));
    const orders = details.map((d) => d.order).filter(Boolean);
    const stockRes = await stockApi.listStock({ limit: 200 }).catch(() => ({ stock: [] }));
    const stockByCode = {}; ((stockRes && stockRes.stock) || []).forEach((r) => { stockByCode[r.item.code] = r.available; });
    const rtRes = await catalog.listRoutings().catch(() => ({ routings: [] }));
    const roomByItem = {}; ((rtRes && rtRes.routings) || []).forEach((r) => { if (r.itemCode) roomByItem[r.itemCode] = r.firstWorkCenter || null; });
    const uRes = await tasksApi.listAssignees().catch(() => ({ users: [] }));
    // Per-room default responsible → prefills the assignee below. Lenient: a room
    // with no default (or a renamed room whose default no longer matches) simply
    // yields no prefill and the user picks manually — never an error.
    const wcRes = await workCenterDefaultsApi.list().catch(() => ({ workCenters: [] }));
    const defaultByRoom = {}; ((wcRes && wcRes.workCenters) || []).forEach((w) => { if (w.defaultUser) defaultByRoom[w.workCenter] = w.defaultUser.id; });
    return { orders, stockByCode, roomByItem, defaultByRoom, users: (uRes && uRes.users) || [] };
  }, []);
  const d = res.data;
  const [edit, setEdit] = useState({});   // rowKey → { decision, assignee, stockQty, prodQty }
  const [sel, setSel] = useState({});      // rowKey → true (multi-select for bulk produce)
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  // Build LINE-level pending demand (not item-aggregated): each undelivered SO line
  // that still has qty to allocate is one decidable row.
  const rows = [];
  ((d && d.orders) || []).forEach((o) => (o.lines || []).forEach((l) => {
    const pending = (l.qtyOrdered || 0) - (l.qtyAllocated || 0);
    if (l.status === "Pending" && pending > 0.0001 && l.item) {
      rows.push({
        key: o.code + "|" + l.lineCode, orderCode: o.code, lineCode: l.lineCode,
        itemCode: l.item.code, itemName: l.item.name, pending,
        requiredBy: l.requiredBy, room: d.roomByItem[l.item.code] ?? null,
        stock: d.stockByCode[l.item.code] ?? 0,
      });
    }
  }));
  rows.sort((a, b) => (a.requiredBy || "").localeCompare(b.requiredBy || "") || a.orderCode.localeCompare(b.orderCode));

  // Lazy default (same pattern as the FEFO batch prefill): the room's default
  // responsible is used until the user picks someone else for that row. MANDATORY is
  // unchanged — a room with no default leaves this empty and the send still blocks.
  const defaultByRoom = (d && d.defaultByRoom) || {};
  const rowState = (r) => {
    const e = edit[r.key] || {};
    if (e.assignee !== undefined) return e; // an explicit pick (incl. clearing it) wins
    const def = r.room ? defaultByRoom[r.room] : null; // no room / no default / renamed room → no prefill
    return def ? { ...e, assignee: def } : e;
  };
  const setRow = (k, patch) => setEdit((e) => ({ ...e, [k]: { ...(e[k] || {}), ...patch } }));
  const reloadAll = async () => { await res.reload(); onChanged && onChanged(); };

  // --- the three actions (all reuse existing endpoints) ---
  const doStock = async (r, qty) => {
    // fulfillLine allocates from FG stock → the line goes to the dispatch queue. No WO.
    await ordersApi.fulfillLine(r.orderCode, r.lineCode, qty);
  };
  const doProduction = async (r, qty, assignee) => {
    // createPO (make-to-order from this SO line) → createWorkOrder. The PO is the
    // internal engine; the user only ever sees the WO.
    const po = await productionApi.createPO({ sourceLineCodes: [r.lineCode], qty, assignedUserId: assignee });
    await productionApi.createWorkOrder([po.order.code]);
  };

  const applyRow = async (r) => {
    const st = rowState(r);
    const dec = st.decision;
    setErr(""); setMsg(""); setBusy(r.key);
    try {
      if (dec === "stock") {
        if (!canStock) throw new Error("You lack the stock-decision permission.");
        await doStock(r, r.pending);
        setMsg(`${r.itemCode}: ${fmtQty(r.pending)} dispatched from stock → ready to dispatch.`);
      } else if (dec === "production") {
        if (!canProduce) throw new Error("You lack the production permission.");
        if (!st.assignee) throw new Error("Pick a responsible person for production.");
        await doProduction(r, r.pending, st.assignee);
        setMsg(`${r.itemCode}: work order created for ${fmtQty(r.pending)}.`);
      } else if (dec === "both") {
        const a = Number(st.stockQty) || 0, b = Number(st.prodQty) || 0;
        if (a <= 0 && b <= 0) throw new Error("Enter a stock and/or production quantity.");
        if (a + b > r.pending + 1e-6) throw new Error(`Stock + production (${a + b}) exceeds pending (${fmtQty(r.pending)}).`);
        if (b > 0 && !st.assignee) throw new Error("Pick a responsible person for the production portion.");
        // Stock first (pure allocation), then production for the remainder. Both
        // increment the SAME line's qtyAllocated — no double-allocation because
        // a + b <= pending is enforced above.
        if (a > 0) { if (!canStock) throw new Error("You lack the stock-decision permission."); await doStock(r, a); }
        if (b > 0) { if (!canProduce) throw new Error("You lack the production permission."); await doProduction(r, b, st.assignee); }
        setMsg(`${r.itemCode}: ${fmtQty(a)} from stock + ${fmtQty(b)} to production.`);
      } else throw new Error("Choose Stock, Production, or Both.");
      setEdit((e) => { const n = { ...e }; delete n[r.key]; return n; });
      await reloadAll();
    } catch (e) { setErr(e instanceof ApiError ? e.message : (e.message || "Action failed.")); }
    finally { setBusy(null); }
  };

  // Bulk: send the SELECTED lines to production (each an assignee), grouped by
  // starting room into one WO per room — mirrors the existing PO-bundling flow.
  const selRows = rows.filter((r) => sel[r.key]);
  const bulkProduce = async () => {
    setErr(""); setMsg(""); setBusy("bulk");
    try {
      if (!canProduce) throw new Error("You lack the production permission.");
      const missing = selRows.filter((r) => !rowState(r).assignee);
      if (missing.length) throw new Error(`Assign a responsible person to: ${missing.map((r) => r.itemCode).join(", ")}.`);
      const noRoom = selRows.filter((r) => !r.room);
      if (noRoom.length) throw new Error(`No routing/room for: ${noRoom.map((r) => r.itemCode).join(", ")}.`);
      // createPO per line, then one WO per starting room.
      const byRoom = new Map();
      for (const r of selRows) {
        const po = await productionApi.createPO({ sourceLineCodes: [r.lineCode], qty: r.pending, assignedUserId: rowState(r).assignee });
        const arr = byRoom.get(r.room) || []; arr.push(po.order.code); byRoom.set(r.room, arr);
      }
      let wos = 0;
      for (const [, poCodes] of byRoom) { await productionApi.createWorkOrder(poCodes); wos++; }
      setMsg(`Created ${wos} work order(s) from ${selRows.length} line(s).`);
      setSel({}); setEdit({});
      await reloadAll();
    } catch (e) { setErr(e instanceof ApiError ? e.message : (e.message || "Bulk production failed.")); }
    finally { setBusy(null); }
  };

  if (res.loading && !d) return <Card className="p-4"><LoadingBlock label="Loading sales-order demand…" /></Card>;
  if (res.error) return <Card className="p-4"><InlineError message={res.error} onRetry={res.reload} /></Card>;
  const DEC = (r, k, label, tone) => { const on = rowState(r).decision === k; return (
    <button onClick={() => setRow(r.key, { decision: k })} className={cx("rounded-md px-2 py-0.5 text-xs font-medium ring-1", on ? tone : "text-slate-500 ring-slate-200 hover:bg-slate-50")}>{label}</button>
  ); };
  const IN = "mono w-20 rounded-md bg-white py-1 px-1.5 text-right text-xs ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400";
  return (
    <Card className="overflow-hidden">
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-2.5">
        <ClipboardList size={15} className="text-amber-600" /><span className="text-sm font-semibold text-slate-700">Sales-order demand — decide</span>
        <span className="text-xs text-slate-400">Stock (dispatch) · Production (make) · Both (split). Production needs a responsible person.</span>
        {selRows.length > 0 && canProduce && <Btn className="ml-auto" disabled={busy === "bulk"} onClick={bulkProduce}><Layers size={13} /> Send {selRows.length} to production</Btn>}
      </div>
      {err ? <div className="px-4 pt-2"><InlineError message={err} /></div> : null}
      {msg ? <div className="mx-4 mt-2 rounded-md bg-emerald-50 px-3 py-1.5 text-xs text-emerald-700 ring-1 ring-emerald-200">{msg}</div> : null}
      {rows.length === 0 ? <p className="px-4 py-5 text-sm text-slate-400">No undecided sales-order demand — every ordered line is allocated.</p> : (
        <div className="overflow-x-auto"><table className="w-full">
          <thead className="border-b border-slate-100"><tr><Th className="w-8"></Th><Th>Item</Th><Th>SO</Th><Th className="text-right">Pending</Th><Th className="text-right">In stock</Th><Th>Room</Th><Th>Required by</Th><Th>Responsible</Th><Th>Decision</Th><Th className="text-right">Act</Th></tr></thead>
          <tbody className="divide-y divide-slate-50">
            {rows.map((r) => { const st = rowState(r); const dec = st.decision; return (
              <tr key={r.key} className={cx(sel[r.key] ? "bg-indigo-50/30" : "hover:bg-slate-50/50")}>
                <Td>{r.room ? <input type="checkbox" checked={!!sel[r.key]} onChange={() => setSel((x) => { const n = { ...x }; if (n[r.key]) delete n[r.key]; else n[r.key] = true; return n; })} className="h-4 w-4 accent-indigo-600 align-middle" /> : <span className="text-slate-300" title="No routing">—</span>}</Td>
                <Td className="font-medium text-slate-700">{r.itemName}<div className="text-xs font-normal text-slate-400"><Mono>{r.itemCode}</Mono></div></Td>
                <Td><Mono className="text-xs text-slate-500">{r.orderCode}</Mono></Td>
                <Td className="text-right"><Mono className="font-semibold text-slate-800">{fmtQty(r.pending)}</Mono></Td>
                <Td className="text-right"><Mono className={r.stock > 0 ? "text-emerald-600" : "text-slate-300"}>{fmtQty(r.stock)}</Mono></Td>
                <Td className="text-xs">{r.room ? <Badge tone="sky">{r.room}</Badge> : <Badge tone="rose">No routing</Badge>}</Td>
                <Td className="text-xs text-slate-500">{r.requiredBy ? fmtDate(r.requiredBy) : "—"}</Td>
                <Td>{(dec === "production" || dec === "both") ? <select value={st.assignee || ""} onChange={(e) => setRow(r.key, { assignee: e.target.value })} className="rounded-md bg-white py-1 px-1.5 text-xs ring-1 ring-slate-200"><option value="">Assign…</option>{(d.users || []).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</select> : <span className="text-slate-300 text-xs">—</span>}</Td>
                <Td><div className="flex items-center gap-1">
                  {DEC(r, "stock", "Stock", "bg-emerald-50 text-emerald-700 ring-emerald-200")}
                  {DEC(r, "production", "Prod", "bg-amber-50 text-amber-700 ring-amber-200")}
                  {DEC(r, "both", "Both", "bg-indigo-50 text-indigo-700 ring-indigo-200")}
                  {dec === "both" && <span className="ml-1 inline-flex items-center gap-1"><input type="number" placeholder="stk" value={st.stockQty ?? ""} onChange={(e) => setRow(r.key, { stockQty: e.target.value })} className={IN} /><input type="number" placeholder="prod" value={st.prodQty ?? ""} onChange={(e) => setRow(r.key, { prodQty: e.target.value })} className={IN} /></span>}
                </div></Td>
                <Td className="text-right">{dec ? <Btn variant="soft" disabled={busy === r.key} onClick={() => applyRow(r)}>{busy === r.key ? "…" : "Apply"}</Btn> : <span className="text-slate-300 text-xs">pick</span>}</Td>
              </tr>
            ); })}
          </tbody>
        </table></div>
      )}
      <p className="px-4 py-2 text-xs text-slate-400"><b>Stock</b> allocates from finished-goods stock and goes straight to dispatch (no work order). <b>Production</b> creates a work order (with the responsible person) and starts the make flow. <b>Both</b> splits the line. Backed by the same operations as before — the decision just lives here now.</p>
    </Card>
  );
}

function ProductionView({ auth, role }) {
  const [tab, setTab] = useState("pending");
  const [q, setQ] = useState("");
  const [stockOpen, setStockOpen] = useState(false);
  const [detailCode, setDetailCode] = useState(null);
  const [issueCode, setIssueCode] = useState(null);
  const [assignModal, setAssignModal] = useState(null); // { po, action:"generate"|"assign" }
  const [sel, setSel] = useState({}); // WO-Pending: selected PO codes for bulk WO creation
  const [bulkOpen, setBulkOpen] = useState(false);
  const [busyCode, setBusyCode] = useState(null);
  const [actErr, setActErr] = useState("");
  const [finishTarget, setFinishTarget] = useState(null); // PRODUCTION #9
  const { data, loading, error, reload } = useResource(
    () => productionApi.listPOs({ q: q.trim() || undefined, limit: 200 }),
    [q],
  );
  const rows = (data && data.orders) || [];
  const assigneesRes = useResource(() => tasksApi.listAssignees(), []);
  const assignees = (assigneesRes.data && assigneesRes.data.users) || [];
  const pcan = (k) => authCan(auth, role, k);
  const act = async (code, fn) => {
    setBusyCode(code); setActErr("");
    try { await fn(); await reload(); }
    catch (e) { setActErr((e instanceof ApiError ? e.message : "Action failed.") + " (" + code + ")"); }
    finally { setBusyCode(null); }
  };

  // Split by WORK state: pending = no WO yet; inprogress = has WO, not done; done.
  const pending = rows.filter((p) => !p.woCode && p.status !== "Done");
  const inprogress = rows.filter((p) => p.woCode && p.status !== "Done");
  const done = rows.filter((p) => p.status === "Done");
  const tabs = [["pending", "WO Pending", pending.length], ["inprogress", "In Progress", inprogress.length], ["done", "Done", done.length], ["materials", "Materials (BOM)", null]];

  // WO-Pending multi-select → bulk WO creation. Only POs WITH a starting room
  // (routing) are selectable — a WO groups by starting room, so a routing-less PO
  // can't be bundled. Select-all respects the current (server-filtered) list.
  const selectablePending = pending.filter((p) => p.startingRoom);
  const selectedPending = selectablePending.filter((p) => sel[p.code]);
  const allSelected = selectablePending.length > 0 && selectedPending.length === selectablePending.length;
  const toggleSel = (code) => setSel((s) => { const n = { ...s }; if (n[code]) delete n[code]; else n[code] = true; return n; });
  const toggleAll = () => setSel(allSelected ? {} : Object.fromEntries(selectablePending.map((p) => [p.code, true])));

  const forCell = (p) => p.forStock
    ? <Badge tone="violet">For stock</Badge>
    : <span className="inline-flex items-center gap-1"><Badge tone="amber">For SO</Badge>{(p.sourceOrderCodes || []).length > 0 && <Mono className="text-xs text-slate-500">{p.sourceOrderCodes.join(", ")}</Mono>}</span>;
  const assignedCell = (p) => p.assignedUser ? <span className="text-slate-600">{p.assignedUser.name}</span> : <Badge tone="rose">Unassigned</Badge>;
  const rmCell = (p) => {
    const iss = p.issuedQty || 0;
    const tone = iss >= p.plannedQty - 0.001 ? "emerald" : iss > 0.0001 ? "amber" : "stone";
    const label = iss >= p.plannedQty - 0.001 ? "Fully issued" : iss > 0.0001 ? "Partial" : "Not issued";
    return <span className="inline-flex items-center gap-1.5"><Badge tone={tone}>{label}</Badge><Mono className="text-xs text-slate-500">{fmtQty(iss)}/{fmtQty(p.plannedQty)}</Mono></span>;
  };
  const roomCell = (p) => p.currentStep ? p.currentStep.workCenter + (p.status === "In Production" ? " · step " + (p.stepIdx + 1) : "") : "—";
  const stop = (e) => e.stopPropagation();

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex w-min gap-1 rounded-lg bg-slate-100 p-1">
            {tabs.map(([k, l, n]) => (
              <button key={k} onClick={() => setTab(k)} className={cx("whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium", tab === k ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700")}>{l}{k === "pending" && n > 0
                ? <span className="ml-1.5 inline-flex items-center rounded-full bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-700">{n}</span>
                : n != null ? " (" + n + ")" : ""}</button>
            ))}
          </div>
          <div className="relative">
            <Search size={15} className="absolute left-2.5 top-2.5 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find PO / item code…" className="w-60 rounded-md bg-white py-1.5 pl-8 pr-3 text-sm text-slate-700 ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400" />
          </div>
          {loading && rows.length > 0 ? <Spinner size={14} /> : null}
        </div>
        {pcan("stockPO") && <Btn variant="soft" onClick={() => setStockOpen(true)}><Plus size={14} /> New stock production</Btn>}
      </div>
      {actErr ? <InlineError message={actErr} /> : null}
      {error ? <InlineError message={error} onRetry={reload} /> : null}
      {pending.length > 0 && pcan("stockPO") && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800 ring-1 ring-amber-200">
          <AlertTriangle size={15} className="shrink-0 text-amber-600" />
          <span>{pending.length} production order{pending.length === 1 ? "" : "s"} awaiting a Work Order — generate to release raw material to Stores.</span>
          <button onClick={() => setTab("pending")} className="ml-auto rounded-md bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800 ring-1 ring-amber-300 hover:bg-amber-200">Review WO Pending</button>
        </div>
      )}

      {tab === "materials" ? <MaterialsPanel q={q} /> : loading && rows.length === 0 ? <LoadingBlock label="Loading production orders…" /> : tab === "pending" ? (
        <div className="space-y-4">
        {(pcan("fulfill") || pcan("stockPO")) && <SalesDemandDecider auth={auth} role={role} onChanged={reload} />}
        <Card>
          {pcan("stockPO") && pending.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-3 py-2">
              <span className="text-xs text-slate-500">{selectedPending.length > 0 ? <><b className="text-slate-700">{selectedPending.length}</b> selected — will auto-split into one work order per starting room</> : "Tick POs to bundle into work orders (grouped by starting room)"}</span>
              {selectedPending.length > 0 && <Btn className="ml-auto" onClick={() => setBulkOpen(true)}><Layers size={13} /> Create work order(s) from selected ({selectedPending.length})</Btn>}
            </div>
          )}
          <div className="overflow-x-auto"><table className="w-full">
          <thead className="border-b border-slate-100"><tr><Th className="w-8"><input type="checkbox" checked={allSelected} onChange={toggleAll} disabled={selectablePending.length === 0} className="h-4 w-4 accent-indigo-600 align-middle" /></Th><Th>PO</Th><Th>Item</Th><Th className="text-right">Qty</Th><Th>For</Th><Th>Starting room</Th><Th>Required by</Th><Th>Assigned</Th><Th className="text-right">Action</Th></tr></thead>
          <tbody className="divide-y divide-slate-50">
            {pending.length === 0 ? <tr><Td className="text-slate-400" colSpan={9}>{q ? "Nothing matches this search." : "No POs waiting for a work order."}</Td></tr> : pending.map((p) => (
              <tr key={p.code} className={cx("cursor-pointer hover:bg-slate-50/50", sel[p.code] ? "bg-indigo-50/40" : "")} onClick={() => setDetailCode(p.code)}>
                <Td onClick={stop}>{p.startingRoom ? <input type="checkbox" checked={!!sel[p.code]} onChange={() => toggleSel(p.code)} className="h-4 w-4 accent-indigo-600 align-middle" /> : <span className="text-slate-300" title="No routing — set one in Production Master">—</span>}</Td>
                <Td><Mono className="font-semibold text-slate-800">{p.code}</Mono></Td>
                <Td className="text-slate-700">{p.item.name}<div className="text-xs text-slate-400"><Mono>{p.item.code}</Mono></div></Td>
                <Td className="text-right"><Mono>{fmtQty(p.plannedQty)}</Mono></Td>
                <Td className="text-xs">{forCell(p)}</Td>
                <Td className="text-xs">{p.startingRoom ? <Badge tone="sky">{p.startingRoom}</Badge> : <Badge tone="rose">No routing</Badge>}</Td>
                <Td className="text-xs text-slate-500">{p.requiredBy ? fmtDate(p.requiredBy) : <span className="text-slate-300">—</span>}</Td>
                <Td className="text-xs">{assignedCell(p)}</Td>
                <Td onClick={stop}>
                  <div className="flex items-center justify-end gap-1.5">
                    {pcan("stockPO") && p.startingRoom && <Btn onClick={() => setAssignModal({ po: p, action: "generate" })}><ListOrdered size={13} /> Generate WO</Btn>}
                    {pcan("stockPO") && !p.startingRoom && <span className="text-xs text-rose-500">Set a routing first</span>}
                    {pcan("stockPO") && <button onClick={() => setAssignModal({ po: p, action: "assign" })} className="rounded-md px-2 py-1 text-xs font-medium text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50">{p.assignedUser ? "Reassign" : "Assign"}</button>}
                    {pcan("stockPO") && (p.issuedQty || 0) < 0.0001 && <button onClick={() => act(p.code, () => productionApi.cancelPO(p.code))} disabled={busyCode === p.code} className="rounded-md px-2 py-1 text-xs font-medium text-rose-500 ring-1 ring-rose-200 hover:bg-rose-50 disabled:opacity-40">Cancel</button>}
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table></div></Card>
        </div>
      ) : tab === "inprogress" ? (
        <Card className="overflow-x-auto"><table className="w-full">
          <thead className="border-b border-slate-100"><tr><Th>PO / WO</Th><Th>Item</Th><Th className="text-right">Qty</Th><Th>Assigned</Th><Th>RM issued</Th><Th>Current room</Th><Th>Status</Th><Th className="text-right">Action</Th></tr></thead>
          <tbody className="divide-y divide-slate-50">
            {inprogress.length === 0 ? <tr><Td className="text-slate-400" colSpan={8}>{q ? "Nothing matches this search." : "Nothing in progress — generate a work order from WO Pending."}</Td></tr> : inprogress.map((p) => (
              <tr key={p.code} className="cursor-pointer hover:bg-slate-50/50" onClick={() => setDetailCode(p.code)}>
                <Td><Mono className="font-semibold text-slate-800">{p.code}</Mono><div className="text-xs text-sky-600"><Mono>{p.woCode}</Mono></div></Td>
                <Td className="text-slate-700">{p.item.name}<div className="text-xs text-slate-400"><Mono>{p.item.code}</Mono></div></Td>
                <Td className="text-right"><Mono>{fmtQty(p.plannedQty)}</Mono></Td>
                <Td className="text-xs">{assignedCell(p)}</Td>
                <Td className="text-xs">{rmCell(p)}</Td>
                <Td className="text-xs text-slate-500">{roomCell(p)}</Td>
                <Td><Badge tone={statusTone(p.status)}>{p.status}</Badge></Td>
                <Td onClick={stop}>
                  <div className="flex items-center justify-end gap-1.5">
                    {p.status === "Planned" && authCanPo(auth, role, "startProd", p) && <Btn disabled={busyCode === p.code} onClick={() => act(p.code, () => productionApi.startPO(p.code))}><Factory size={13} /> Start</Btn>}
                    {p.status === "In Production" && pcan("issue") && <button onClick={() => setIssueCode(p.code)} className="rounded-md px-2 py-1 text-xs font-medium text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50">Issue RM</button>}
                    {p.status === "In Production" && authCanPo(auth, role, "advanceProd", p) && <button onClick={() => act(p.code, () => productionApi.advancePO(p.code))} disabled={busyCode === p.code} className="rounded-md px-2 py-1 text-xs font-medium text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-40">Advance</button>}
                    {p.status === "In Production" && authCanPo(auth, role, "stockPO", p) && <button onClick={() => setFinishTarget({ code: p.code, plannedQty: p.plannedQty, run: (out) => productionApi.completePO(p.code, out) })} disabled={busyCode === p.code} className="rounded-md px-2 py-1 text-xs font-medium text-emerald-600 ring-1 ring-emerald-200 hover:bg-emerald-50 disabled:opacity-40">Complete</button>}
                    {pcan("stockPO") && <button onClick={() => setAssignModal({ po: p, action: "assign" })} className="rounded-md px-2 py-1 text-xs font-medium text-slate-500 ring-1 ring-slate-200 hover:bg-slate-50">Reassign</button>}
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table></Card>
      ) : (
        <Card className="overflow-x-auto"><table className="w-full">
          <thead className="border-b border-slate-100"><tr><Th>PO</Th><Th>Item</Th><Th>Assigned</Th><Th className="text-right">Qty</Th><Th>Completed</Th><Th>By</Th><Th>Batch</Th><Th className="text-right">Duration (raw)</Th></tr></thead>
          <tbody className="divide-y divide-slate-50">
            {done.map((p) => (
              <tr key={p.code} className="cursor-pointer hover:bg-slate-50/50" onClick={() => setDetailCode(p.code)}>
                <Td><Mono className="font-medium text-slate-700">{p.code}</Mono>{p.woCode && <Mono className="ml-1 text-xs text-slate-400">{p.woCode}</Mono>}</Td>
                <Td>{p.item.name}</Td>
                <Td className="text-xs">{p.assignedUser ? <span className="text-slate-600">{p.assignedUser.name}</span> : <span className="text-slate-300">—</span>}</Td>
                <Td className="text-right"><Mono>{fmtQty(p.plannedQty)}</Mono> Kg</Td>
                <Td className="text-xs text-slate-600">{p.completedAt ? fmtDateTime(p.completedAt) : <Badge tone="emerald"><CheckCircle2 size={12} /> Done</Badge>}</Td>
                <Td className="text-xs text-slate-600">{p.completedByUser ? p.completedByUser.name : "—"}</Td>
                <Td className="text-xs text-slate-400"><Mono>{p.forStock ? "stock" : (p.sourceOrderCodes || []).join(", ") || "—"}</Mono></Td>
                <Td className="text-right"><Mono className="text-slate-700">{p.rawDurationHours != null ? p.rawDurationHours + " h" : "—"}</Mono></Td>
              </tr>
            ))}
            {done.length === 0 && <tr><Td className="text-slate-400" colSpan={8}>{q ? "Nothing matches this search." : "None completed yet."}</Td></tr>}
          </tbody>
        </table></Card>
      )}

      <NewStockPOModal open={stockOpen} onClose={() => setStockOpen(false)} onCreated={reload} />
      <PoAssignModal po={assignModal && assignModal.po} action={assignModal && assignModal.action} assignees={assignees} open={!!assignModal} onClose={() => setAssignModal(null)} onDone={reload} />
      <BulkWoModal pos={selectedPending} assignees={assignees} open={bulkOpen} onClose={() => setBulkOpen(false)} onDone={() => { setSel({}); reload(); }} />
      <PoDetailModal key={detailCode || "nodetail"} code={detailCode} auth={auth} role={role} open={!!detailCode} onClose={() => setDetailCode(null)} onChanged={reload} onIssue={(c) => { setDetailCode(null); setIssueCode(c); }} />
      <LiveIssueModal key={issueCode || "noissue"} code={issueCode} open={!!issueCode} onClose={() => setIssueCode(null)} onChanged={reload} auth={auth} />
      <FinishPoModal target={finishTarget} onClose={() => setFinishTarget(null)} onDone={reload} />
    </div>
  );
}

function MaterialsPanel({ q }) {
  const { data, loading, error, reload } = useResource(async () => {
    const list = await productionApi.listPOs({ q: q && q.trim() ? q.trim() : undefined, limit: 200 });
    const activeRows = ((list && list.orders) || []).filter((p) => p.status !== "Done");
    const details = await Promise.all(activeRows.map((p) => productionApi.getPO(p.code)));
    const stockList = await stockApi.listStock({ limit: 200 });
    return { pos: details.map((d) => d.order).filter(Boolean), stockMap: indexStockByCode(stockList && stockList.stock) };
  }, [q]);
  const pos = (data && data.pos) || [];
  const stockMap = (data && data.stockMap) || {};
  if (loading && !data) return <LoadingBlock label="Loading material requirements…" />;
  if (error) return <InlineError message={error} onRetry={reload} />;
  if (!pos.length) return <Card className="p-6 text-sm text-slate-400">No active production orders. Once a PO is created (or sent to production), its material requirement appears here.</Card>;
  const RmTable = ({ rows }) => (
    <div className="overflow-x-auto"><table className="w-full">
      <thead className="border-b border-slate-50"><tr><Th>Material</Th><Th>Type</Th><Th className="text-right">Required</Th><Th className="text-right">Available</Th><Th>Status</Th></tr></thead>
      <tbody className="divide-y divide-slate-50">
        {rows.map((r) => { const av = stockMap[r.code] ? stockMap[r.code].available : 0; const short = av < r.totalQty - 0.001; return (
          <tr key={r.code}>
            <Td className="font-medium text-slate-700">{r.name}<div className="text-xs font-normal text-slate-400"><Mono>{r.code}</Mono></div></Td>
            <Td><Badge tone={r.type === "SFG" ? "violet" : r.type === "PM" ? "sky" : "stone"}>{r.type}</Badge></Td>
            <Td className="text-right"><Mono>{fmtQty(r.totalQty)}</Mono></Td>
            <Td className="text-right"><Mono className={short ? "text-rose-600" : "text-slate-500"}>{fmtQty(av)}</Mono></Td>
            <Td>{short ? <Badge tone="rose"><AlertTriangle size={11} /> Short {fmtQty(r.totalQty - av)}{r.type === "SFG" ? " · produce" : ""}</Badge> : <Badge tone="emerald">OK</Badge>}</Td>
          </tr>
        ); })}
      </tbody>
    </table></div>
  );
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500">Live BOM explosion (RM/PM + sub-assemblies) per active production order, vs the real on-hand <b>Available</b> stock.</p>
      {pos.map((p) => {
        const req = p.bomRequirement;
        const leaves = req ? req.leaves : [];
        const inter = req ? (req.intermediates || []) : [];
        return (
          <Card key={p.code}>
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-4 py-3">
              <div><Mono className="font-semibold text-slate-800">{p.code}</Mono> <span className="text-sm text-slate-600">{p.item.name}</span><span className="ml-2 text-xs text-slate-400">batch <Mono>{fmtQty(p.plannedQty)}</Mono> Kg · {p.forStock ? "make-to-stock" : (p.sourceLines.map((s) => s.orderCode).join(", ") || "—")}</span></div>
              {(p.issuedQty || 0) >= p.plannedQty - 0.001
                ? <Badge tone="emerald"><CheckCircle2 size={12} /> Fully issued</Badge>
                : <span className="text-xs text-slate-400">issued <Mono>{fmtQty(p.issuedQty || 0)}/{fmtQty(p.plannedQty)}</Mono> · <Badge tone={statusTone(p.status)}>{p.status}</Badge></span>}
            </div>
            {leaves.length === 0 && inter.length === 0 ? <div className="px-4 py-3 text-sm text-slate-400">No BOM requirement.</div> : <>
              {inter.length > 0 && <div className="px-4 pt-2 text-xs font-semibold uppercase tracking-wide text-violet-500">Sub-assemblies</div>}
              {inter.length > 0 && <RmTable rows={inter} />}
              {leaves.length > 0 && <div className="px-4 pt-2 text-xs font-semibold uppercase tracking-wide text-amber-500">Raw / packaging</div>}
              {leaves.length > 0 && <RmTable rows={leaves} />}
            </>}
          </Card>
        );
      })}
    </div>
  );
}

// Make-to-stock production — MULTIPLE items in one submit. Each line = {FG, qty};
// one make-to-stock PO is created per line. Duplicate items MERGE (qtys summed).
// Submit is PARTIAL-SUCCESS: valid lines create; any line whose item lacks an
// active BOM + routing (createPO 400) is reported by item + reason, the rest still create.
function NewStockPOModal({ open, onClose, onCreated }) {
  // PRODUCTION #3: SFG are manufactured too — list FG + SFG. Two calls merged (not a single
  // untyped fetch) so the FG list is identical to before and RM/PM can't crowd a limited fetch.
  // The backend createPo already accepts any item with an active BOM + routing (no type filter).
  const fgRes = useResource(async () => {
    if (!open) return null;
    const [f, s] = await Promise.all([catalog.listItems({ type: "FG" }), catalog.listItems({ type: "SFG" })]);
    return { items: [...(f.items || []), ...(s.items || [])] };
  }, [open]);
  const fg = (fgRes.data && fgRes.data.items) || [];
  const [lines, setLines] = useState([{ itemCode: "", qty: 100 }]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null); // { created:[{code,name,qty}], failed:[{name,error}] }
  React.useEffect(() => { if (open) { setLines([{ itemCode: "", qty: 100 }]); setResult(null); setErr(""); setBusy(false); } }, [open]);
  const setLine = (i, k, v) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const addLine = () => setLines((ls) => [...ls, { itemCode: "", qty: 100 }]);
  const rmLine = (i) => setLines((ls) => ls.filter((_, j) => j !== i));
  const fgFor = (code) => fg.find((x) => x.code === code);
  const fgName = (code) => { const i = fgFor(code); return i ? i.name : code; };
  // Valid lines, with duplicate items merged (quantities summed) → one PO per item.
  const valid = lines.filter((l) => l.itemCode && Number(l.qty) > 0);
  const mergedMap = {}; valid.forEach((l) => { mergedMap[l.itemCode] = (mergedMap[l.itemCode] || 0) + Number(l.qty); });
  const merged = Object.entries(mergedMap).map(([itemCode, qty]) => ({ itemCode, qty }));
  const hasDup = merged.length < valid.length;
  const submit = async () => {
    if (merged.length === 0 || busy) return;
    setBusy(true); setErr(""); setResult(null);
    const created = []; const failed = [];
    for (const m of merged) {
      try { const r = await productionApi.createPO({ itemCode: m.itemCode, qty: m.qty, forStock: true }); created.push({ code: r.order.code, name: fgName(m.itemCode), qty: r.order.plannedQty }); }
      catch (e) { failed.push({ name: fgName(m.itemCode), error: e instanceof ApiError ? e.message : "could not be created" }); }
    }
    setResult({ created, failed });
    if (created.length > 0) onCreated && onCreated();
    setBusy(false);
  };
  return (
    <Modal open={open} onClose={onClose} wide title="Make-to-stock production"
      footer={result
        ? <Btn onClick={onClose}>Done</Btn>
        : <><Btn variant="soft" onClick={onClose}>Cancel</Btn><Btn onClick={submit} disabled={busy || merged.length === 0}><Plus size={14} /> {busy ? "Creating…" : "Create " + (merged.length || "") + " stock PO" + (merged.length === 1 ? "" : "s")}</Btn></>}>
      {fgRes.loading && !fgRes.data ? <LoadingBlock label="Loading products…" /> : fgRes.error ? <InlineError message={fgRes.error} onRetry={fgRes.reload} /> : result ? (
        <div className="space-y-2">
          {result.created.length > 0 && <div className="rounded-lg bg-emerald-50 p-3 ring-1 ring-emerald-200"><div className="mb-1 flex items-center gap-1.5 text-sm font-semibold text-emerald-700"><CheckCircle2 size={15} /> Created {result.created.length} stock PO{result.created.length === 1 ? "" : "s"}</div>{result.created.map((c) => <div key={c.code} className="text-xs text-emerald-800"><Mono className="font-semibold">{c.code}</Mono> — {c.name} · {fmtQty(c.qty)} Kg</div>)}</div>}
          {result.failed.length > 0 && <div className="rounded-lg bg-rose-50 p-3 ring-1 ring-rose-200"><div className="mb-1 flex items-center gap-1.5 text-sm font-semibold text-rose-700"><AlertTriangle size={15} /> {result.failed.length} item{result.failed.length === 1 ? "" : "s"} failed</div>{result.failed.map((f, i) => <div key={i} className="text-xs text-rose-700"><b>{f.name}</b>: {f.error}</div>)}</div>}
        </div>
      ) : (
        <div className="space-y-3">
          {err ? <InlineError message={err} /> : null}
          <div className="space-y-2">
            {lines.map((l, i) => {
              const lot = fgFor(l.itemCode)?.lot;
              return (
                <div key={i} className="flex items-center gap-2">
                  <select value={l.itemCode} onChange={(e) => setLine(i, "itemCode", e.target.value)} className="flex-1 rounded-md bg-white py-1.5 px-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400"><option value="">Product (FG / SFG)…</option>{fg.map((x) => <option key={x.code} value={x.code}>{x.name} ({x.code}) — {x.type}</option>)}</select>
                  <div className="relative"><input type="number" value={l.qty} onChange={(e) => setLine(i, "qty", e.target.value)} placeholder="Qty" className="mono w-28 rounded-md bg-white py-1.5 pl-2.5 pr-7 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400" /><span className="pointer-events-none absolute right-2 top-2 text-xs text-slate-400">Kg</span></div>
                  <span className="w-20 text-xs text-slate-400">{lot ? "lot " + fmtQty(lot) : ""}</span>
                  <button onClick={() => rmLine(i)} disabled={lines.length === 1} className="text-slate-400 hover:text-rose-500 disabled:opacity-30"><X size={15} /></button>
                </div>
              );
            })}
          </div>
          <Btn variant="soft" onClick={addLine}><Plus size={13} /> Add item</Btn>
          {hasDup && <p className="text-xs text-amber-600">An item is listed more than once — its quantities will be combined into one PO.</p>}
          <p className="text-xs text-slate-400">One make-to-stock PO per item — not tied to any order, for building FG stock. Each qty is rounded up to the item's lot size. An item without an active BOM + routing is reported as failed (the valid ones still create). When finished, the goods go to Batches as a QC-Pending batch.</p>
        </div>
      )}
    </Modal>
  );
}

/* ===================== Inventory / Stock — LIVE (W4a) =====================
   Main buckets, room-wise stock, and the store+room grand total all source from
   the real /stock + /stock/rooms APIs; room transfer/return POST to the live
   endpoints. The `wip` bucket shown here is the real value from /stock; as of
   W4b the Production/Work-Order screens that MOVE wip (start/advance) are live
   too, so this bucket updates as POs run. */

const STOCK_TYPE_TABS = [["FG", "FG · Finished"], ["SFG", "SFG · Semi-finished"], ["RM", "RM · Raw material"], ["PM", "PM · Packing"]];

function StockMainPanel({ auth }) {
  const [tab, setTab] = useState("FG");
  const [q, setQ] = useState("");
  const [belowOnly, setBelowOnly] = useState(false);
  const { data, loading, error, reload } = useResource(
    () => stockApi.listStock({ type: tab, q: q.trim() || undefined, belowBuffer: belowOnly || undefined }),
    [tab, q, belowOnly],
  );
  const rows = data?.stock || [];
  // VENDOR-WISE Phase 1 (VIEW, gated): expandable vendor/batch breakdown under each RM item. Off ⇒
  // the payload carries no vendorBatches and this is inert → the classic aggregate table is unchanged.
  const vendorWise = auth?.company?.settings?.vendorWiseStock === true;
  const [expanded, setExpanded] = useState(() => new Set());
  const toggleExpand = (code) => setExpanded((s) => { const n = new Set(s); n.has(code) ? n.delete(code) : n.add(code); return n; });
  const dot = (c) => <span className="mr-1 inline-block h-2 w-2 rounded-full align-middle" style={{ backgroundColor: c }} />;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1 rounded-lg bg-slate-100 p-1 w-min">
          {STOCK_TYPE_TABS.map(([k, l]) => <button key={k} onClick={() => setTab(k)} className={cx("whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium", tab === k ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700")}>{l}</button>)}
        </div>
        <label className="flex items-center gap-1.5 rounded-md bg-white px-2.5 py-1.5 ring-1 ring-slate-200">
          <Search size={14} className="text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search code / name…" className="w-44 bg-transparent text-sm text-slate-700 focus:outline-none" />
        </label>
        <label className="flex items-center gap-1.5 text-xs text-slate-500"><input type="checkbox" checked={belowOnly} onChange={(e) => setBelowOnly(e.target.checked)} className="accent-rose-600" /> Below buffer only</label>
        <div className="ml-auto flex items-center gap-2 text-xs text-slate-400">{loading && rows.length > 0 ? <Spinner size={14} /> : null}<span>{data ? `${data.total} item${data.total === 1 ? "" : "s"}` : ""}</span></div>
      </div>
      {error ? <InlineError message={error} onRetry={reload} /> : null}
      <Card className="overflow-x-auto">
        {loading && rows.length === 0 ? <LoadingBlock label="Loading stock…" /> : (
          <table className="w-full">
            <thead className="border-b border-slate-100"><tr>
              <Th>Code</Th><Th>Item</Th>
              <Th className="text-right">{dot("#059669")}Available</Th><Th className="text-right">{dot("#7c3aed")}Reserved</Th>
              <Th className="text-right">{dot("#d97706")}WIP</Th><Th className="text-right">{dot("#0284c7")}In transit</Th>
              <Th className="text-right">{dot("#a8a29e")}Buffer</Th><Th className="text-right">Total</Th>
            </tr></thead>
            <tbody className="divide-y divide-slate-50">
              {rows.map((s) => {
                const tot = s.available + s.reserved + s.wip + s.inTransit;
                const low = s.belowBuffer;
                // Expandable only when the flag is on AND this RM item carries a vendor/batch breakdown.
                const canExpand = vendorWise && Array.isArray(s.vendorBatches);
                const isOpen = canExpand && expanded.has(s.item.code);
                const drift = s.vendorDrift;
                return (
                  <React.Fragment key={s.item.code}>
                  <tr className={cx(low ? "bg-rose-50/40" : "", canExpand ? "cursor-pointer hover:bg-slate-50/50" : "")} onClick={canExpand ? () => toggleExpand(s.item.code) : undefined}>
                    <Td><span className="inline-flex items-center gap-1.5">{canExpand ? <ChevronRight size={13} className={cx("text-slate-400 transition-transform", isOpen && "rotate-90")} /> : null}<Mono className="text-slate-500">{s.item.code}</Mono></span></Td>
                    <Td className="font-medium text-slate-700">{s.item.name}{low && <AlertTriangle size={13} className="ml-2 inline text-rose-500" />}</Td>
                    <Td className="text-right"><Mono className={low ? "font-medium text-rose-600" : "text-slate-700"}>{fmtQty(s.available)}</Mono></Td>
                    <Td className="text-right"><Mono className="text-slate-500">{fmtQty(s.reserved)}</Mono></Td>
                    <Td className="text-right"><Mono className="text-slate-500">{fmtQty(s.wip)}</Mono></Td>
                    <Td className="text-right"><Mono className="text-slate-500">{fmtQty(s.inTransit)}</Mono></Td>
                    <Td className="text-right"><Mono className="text-slate-400">{fmtQty(s.buffer)}</Mono></Td>
                    <Td className="text-right"><Mono className="font-semibold text-slate-800">{fmtQty(tot)}</Mono></Td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-indigo-50/30">
                      <Td colSpan={8} className="px-4 py-2 align-top">
                        <div className="mb-1 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                          Vendor / batch breakdown
                          {drift != null && Math.abs(drift) > 0.001 ? <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">⚠ drift {fmtQty(drift)} vs aggregate</span> : null}
                        </div>
                        {(s.vendorBatches || []).length === 0 ? <span className="text-xs text-slate-400">No batches on hand.</span> : (
                          <div className="flex max-w-md flex-col gap-0.5">
                            {s.vendorBatches.map((vb, i) => (
                              <div key={i} className="flex items-center justify-between gap-4 text-sm">
                                <span className="text-slate-700">{vb.vendor} <Mono className="ml-1 text-xs text-slate-400">{vb.batchNo}</Mono></span>
                                <Mono className="text-slate-600">{fmtQty(vb.remaining)}</Mono>
                              </div>
                            ))}
                            <div className="mt-1 flex items-center justify-between gap-4 border-t border-indigo-100 pt-1 text-sm">
                              <span className="text-xs font-medium text-slate-500">Σ batches (= available {fmtQty(s.available)})</span>
                              <Mono className="font-semibold text-slate-800">{fmtQty((s.vendorBatches || []).reduce((a, x) => a + x.remaining, 0))}</Mono>
                            </div>
                          </div>
                        )}
                      </Td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
              {rows.length === 0 && !loading && <tr><Td className="text-slate-400" colSpan={8}>No stock rows match.</Td></tr>}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

function RoomStockPanel() {
  const roomsRes = useResource(() => stockApi.getRooms(), []);
  const itemsRes = useResource(() => stockApi.listStock({ limit: 200 }), []);
  const rooms = roomsRes.data?.rooms || [];
  const stockRows = itemsRes.data?.stock || [];
  const rmItems = stockRows.filter((s) => s.item.type === "RM" || s.item.type === "PM");
  const roomNames = rooms.map((r) => r.room);
  const availOf = (code) => { const r = stockRows.find((s) => s.item.code === code); return r ? r.available : 0; };

  const [room, setRoom] = useState("");
  const [it, setIt] = useState("");
  const [qty, setQty] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  React.useEffect(() => { if (!room && roomNames.length) setRoom(roomNames[0]); }, [roomNames, room]);

  const reloadAll = () => Promise.all([roomsRes.reload(), itemsRes.reload()]);
  const send = async () => {
    setError("");
    const q = Number(qty);
    if (!it || !room || !(q > 0)) { setError("Pick an item, a room, and a quantity."); return; }
    if (q > availOf(it) + 1e-6) { setError(`Only ${fmtQty(availOf(it))} available in store for ${it}.`); return; }
    setBusy(true);
    try { await stockApi.transferToRoom({ itemCode: it, room, qty: q }); setIt(""); setQty(""); await reloadAll(); }
    catch (e) { setError(e instanceof ApiError ? e.message : "Transfer failed."); }
    finally { setBusy(false); }
  };
  const doReturn = async (itemCode, roomName, q) => {
    setError("");
    setBusy(true);
    try { await stockApi.returnFromRoom({ itemCode, room: roomName, qty: q }); await reloadAll(); }
    catch (e) { setError(e instanceof ApiError ? e.message : "Return failed."); }
    finally { setBusy(false); }
  };

  const loadErr = roomsRes.error || itemsRes.error;
  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="mb-2 text-sm font-semibold text-slate-700">Move stock → room</div>
        <div className="flex flex-wrap items-center gap-2">
          <select value={it} onChange={(e) => setIt(e.target.value)} className="rounded-md bg-white py-1.5 px-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400"><option value="">RM / PM item…</option>{rmItems.map((x) => <option key={x.item.code} value={x.item.code}>{x.item.name} (stock {fmtQty(x.available)})</option>)}</select>
          <input type="number" value={qty} onChange={(e) => setQty(e.target.value)} placeholder="Qty" className="w-24 rounded-md bg-white py-1.5 px-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400" />
          <ArrowRight size={15} className="text-slate-400" />
          <select value={room} onChange={(e) => setRoom(e.target.value)} className="rounded-md bg-white py-1.5 px-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400">{roomNames.length === 0 && <option value="">No rooms</option>}{roomNames.map((r) => <option key={r} value={r}>{r}</option>)}</select>
          <Btn onClick={send} disabled={busy}><ArrowRight size={13} /> Send to room</Btn>
        </div>
        {error ? <div className="mt-2"><InlineError message={error} /></div> : null}
        <p className="mt-1.5 text-xs text-slate-400">Moves from the main store to that room's store (available − qty). On a WO issue it is drawn <b>from the room first</b>, then from main.</p>
      </Card>
      {loadErr ? <InlineError message={loadErr} onRetry={reloadAll} /> : null}
      {roomsRes.loading && rooms.length === 0 ? <LoadingBlock label="Loading room stock…" /> : rooms.length === 0 ? (
        <Card className="p-8 text-center text-sm text-slate-400">No stock in any room. Move an RM/PM item to a room above.</Card>
      ) : rooms.map((rm) => (
        <Card key={rm.room} className="p-4 overflow-x-auto">
          <div className="mb-2 flex items-center gap-2 border-b border-slate-100 pb-2"><Factory size={15} className="text-amber-600" /><span className="font-semibold text-slate-800">{rm.room}</span><span className="text-xs text-slate-400">{rm.items.length} item</span></div>
          <table className="w-full"><thead><tr><Th>Item</Th><Th className="text-right">In room</Th><Th>Action</Th></tr></thead><tbody className="divide-y divide-slate-50">
            {rm.items.map((e) => <tr key={e.itemCode}><Td className="font-medium text-slate-700">{e.name} <Mono className="ml-1 text-xs text-slate-400">{e.itemCode}</Mono></Td><Td className="text-right"><Mono className="font-semibold text-slate-800">{fmtQty(e.qty)}</Mono></Td><Td><Btn variant="soft" disabled={busy} onClick={() => doReturn(e.itemCode, rm.room, e.qty)}><ArrowUp size={12} /> Return to store</Btn></Td></tr>)}
          </tbody></table>
        </Card>
      ))}
    </div>
  );
}

function InventoryTotalPanel() {
  const stockRes = useResource(() => stockApi.listStock({ limit: 200 }), []);
  const roomsRes = useResource(() => stockApi.getRooms(), []);
  const stockRows = stockRes.data?.stock || [];
  const rooms = roomsRes.data?.rooms || [];
  const roomNames = rooms.map((r) => r.room);
  const roomQty = (roomName, code) => { const r = rooms.find((x) => x.room === roomName); if (!r) return 0; const e = r.items.find((i) => i.itemCode === code); return e ? e.qty : 0; };
  const rmItems = stockRows.filter((s) => s.item.type === "RM" || s.item.type === "PM");
  const loadErr = stockRes.error || roomsRes.error;
  if (loadErr) return <InlineError message={loadErr} onRetry={() => { stockRes.reload(); roomsRes.reload(); }} />;
  if ((stockRes.loading || roomsRes.loading) && stockRows.length === 0) return <LoadingBlock label="Loading total inventory…" />;
  return (
    <Card className="overflow-x-auto">
      <p className="px-1 pb-2 text-xs text-slate-400">Total inventory = main store + every room's stock combined. RM/PM sits partly in the store and partly in rooms — grand total + breakdown here.</p>
      <table className="w-full">
        <thead className="border-b border-slate-100"><tr>
          <Th>Item</Th><Th className="text-right">In store</Th>
          {roomNames.map((r) => <Th key={r} className="text-right">{r}</Th>)}
          <Th className="text-right">Grand total</Th>
        </tr></thead>
        <tbody className="divide-y divide-slate-50">
          {rmItems.map((s) => {
            const code = s.item.code;
            const store = s.available;
            const inRooms = roomNames.reduce((a, r) => a + roomQty(r, code), 0);
            const grand = store + inRooms;
            if (grand <= 0.0001) return null;
            return (
              <tr key={code} className="hover:bg-slate-50/40">
                <Td className="font-medium text-slate-700">{s.item.name} <Mono className="ml-1 text-xs text-slate-400">{code}</Mono></Td>
                <Td className="text-right"><Mono className="text-slate-700">{fmtQty(store)}</Mono></Td>
                {roomNames.map((r) => { const q = roomQty(r, code); return <Td key={r} className="text-right"><Mono className={q > 0 ? "text-amber-700" : "text-slate-300"}>{q > 0 ? fmtQty(q) : "\u2014"}</Mono></Td>; })}
                <Td className="text-right"><Mono className="font-semibold text-slate-800">{fmtQty(grand)}</Mono></Td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
}

// Weekly room stock-take: physically count a room's RM/PM and SET the actual
// balance (overwrites the computed qty), recording the variance for a wastage
// audit. Count sheet (system vs counted, live variance) + adjustment history.
// Gated implicitly: only inventory-capable users reach Store Stock, and the
// backend enforces requireModule("inventory") on room-adjust/-batch anyway.
function StockTakePanel() {
  const roomsRes = useResource(() => stockApi.getRooms(), []);
  const rooms = roomsRes.data?.rooms || [];
  const roomNames = rooms.map((r) => r.room);
  const [room, setRoom] = useState("");
  React.useEffect(() => { if (!room && roomNames.length) setRoom(roomNames[0]); }, [roomNames, room]);
  const roomItems = (rooms.find((r) => r.room === room)?.items) || [];

  // Adjustment history for the selected room (recent first).
  const histRes = useResource(() => (room ? stockApi.listAdjustments({ room }) : Promise.resolve({ adjustments: [], total: 0 })), [room]);
  const history = histRes.data?.adjustments || [];

  // Counted qty per itemCode (prefilled with system qty); reset when the room's
  // items change (room switch or reload).
  const [counted, setCounted] = useState({});
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const itemsKey = roomItems.map((e) => e.itemCode + ":" + e.qty).join("|");
  React.useEffect(() => { const m = {}; roomItems.forEach((e) => { m[e.itemCode] = String(Number(e.qty.toFixed(4))); }); setCounted(m); setMsg(""); setErr(""); /* eslint-disable-next-line */ }, [itemsKey, room]);

  const varianceOf = (e) => { const c = Number(counted[e.itemCode]); return Number.isFinite(c) ? c - e.qty : 0; };
  const changed = roomItems.filter((e) => { const c = Number(counted[e.itemCode]); return Number.isFinite(c) && c >= 0 && Math.abs(c - e.qty) > 0.0001; });

  const save = async () => {
    setErr(""); setMsg("");
    if (changed.length === 0) { setErr("No changes — edit a Counted qty that differs from the system qty."); return; }
    const counts = changed.map((e) => ({ itemCode: e.itemCode, countedQty: Number(counted[e.itemCode]), reason: reason.trim() || undefined }));
    setBusy(true);
    try {
      const res = await stockApi.roomAdjustBatch({ room, counts });
      setMsg(`Stock adjusted — ${res.count} item${res.count === 1 ? "" : "s"} updated.`);
      setReason("");
      await Promise.all([roomsRes.reload(), histRes.reload()]);
    } catch (e) { setErr(e instanceof ApiError ? e.message : "Save failed."); }
    finally { setBusy(false); }
  };

  const vTone = (v) => (v < -0.0001 ? "text-rose-600" : v > 0.0001 ? "text-emerald-600" : "text-slate-400");
  const loadErr = roomsRes.error;
  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="mb-1 flex flex-wrap items-center gap-2">
          <ClipboardList size={16} className="text-amber-600" /><span className="text-sm font-semibold text-slate-700">Weekly stock-take</span>
          <span className="text-xs text-slate-400">physically count a room and set the actual balance</span>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs font-medium text-slate-400">Room</span>
            <select value={room} onChange={(e) => setRoom(e.target.value)} className="rounded-md bg-white py-1.5 px-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400">{roomNames.length === 0 && <option value="">No rooms</option>}{roomNames.map((r) => <option key={r} value={r}>{r}</option>)}</select>
          </div>
        </div>
        {loadErr ? <InlineError message={loadErr} onRetry={roomsRes.reload} /> : roomsRes.loading && rooms.length === 0 ? <LoadingBlock label="Loading room stock…" /> : roomNames.length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-400">No stock in any room. Move an RM/PM item to a room first (Room-wise → Send to room), then count it here.</p>
        ) : roomItems.length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-400">Nothing currently in <b>{room}</b> to count.</p>
        ) : (
          <>
            <div className="mt-1 overflow-x-auto rounded-lg ring-1 ring-slate-100">
              <table className="w-full"><thead className="bg-slate-50/60"><tr><Th>Item</Th><Th className="text-right">System qty</Th><Th className="text-right">Counted qty</Th><Th className="text-right">Variance</Th></tr></thead>
                <tbody className="divide-y divide-slate-50">
                  {roomItems.map((e) => { const v = varianceOf(e); return (
                    <tr key={e.itemCode}>
                      <Td className="font-medium text-slate-700">{e.name} <Mono className="ml-1 text-xs text-slate-400">{e.itemCode}</Mono></Td>
                      <Td className="text-right"><Mono className="text-slate-600">{fmtQty(e.qty)}</Mono></Td>
                      <Td className="text-right"><input type="number" value={counted[e.itemCode] ?? ""} onChange={(ev) => setCounted((m) => ({ ...m, [e.itemCode]: ev.target.value }))} className="w-24 rounded-md bg-white py-1 px-2 text-right text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400" /></Td>
                      <Td className="text-right"><Mono className={vTone(v)}>{v > 0.0001 ? "+" : ""}{fmtQty(v)}</Mono></Td>
                    </tr>
                  ); })}
                </tbody></table>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason / note (optional)" className="min-w-0 flex-1 rounded-md bg-white py-1.5 px-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400" />
              <span className="text-xs text-slate-400">{changed.length} change{changed.length === 1 ? "" : "s"}</span>
              <Btn disabled={busy || changed.length === 0} onClick={save}><CheckCircle2 size={14} /> {busy ? "Saving…" : "Save stock-take"}</Btn>
            </div>
            <p className="mt-1.5 text-xs text-slate-400">Saving <b>sets</b> each room item to your counted value (overwriting the computed qty) and records the variance (counted − system) for the wastage audit below. Negative variance = shrinkage.</p>
            {msg ? <div className="mt-2 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-700 ring-1 ring-emerald-200">{msg}</div> : null}
            {err ? <div className="mt-2"><InlineError message={err} /></div> : null}
          </>
        )}
      </Card>

      <Card>
        <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-3"><Activity size={15} className="text-slate-500" /><span className="text-sm font-semibold text-slate-700">Adjustment history{room ? " · " + room : ""}</span><span className="text-xs text-slate-400">(variance / wastage record)</span></div>
        {histRes.loading && history.length === 0 ? <LoadingBlock label="Loading history…" /> : histRes.error ? <InlineError message={histRes.error} onRetry={histRes.reload} /> : history.length === 0 ? (
          <p className="px-4 py-6 text-center text-sm text-slate-400">No adjustments recorded for this room yet.</p>
        ) : (
          <div className="overflow-x-auto"><table className="w-full">
            <thead className="border-b border-slate-50"><tr><Th>When</Th><Th>Item</Th><Th className="text-right">System</Th><Th className="text-right">Counted</Th><Th className="text-right">Variance</Th><Th>Reason</Th><Th>By</Th></tr></thead>
            <tbody className="divide-y divide-slate-50">
              {history.map((a) => (
                <tr key={a.id}>
                  <Td className="whitespace-nowrap text-xs text-slate-500">{fmtDateTime(a.adjustedAt)}</Td>
                  <Td className="text-slate-700">{a.item.name} <Mono className="ml-1 text-xs text-slate-400">{a.item.code}</Mono></Td>
                  <Td className="text-right"><Mono className="text-slate-500">{fmtQty(a.systemQty)}</Mono></Td>
                  <Td className="text-right"><Mono className="text-slate-700">{fmtQty(a.countedQty)}</Mono></Td>
                  <Td className="text-right"><Mono className={vTone(a.variance)}>{a.variance > 0.0001 ? "+" : ""}{fmtQty(a.variance)}</Mono></Td>
                  <Td className="text-xs text-slate-500">{a.reason || "—"}</Td>
                  <Td className="text-xs text-slate-500">{a.adjustedBy || "—"}</Td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </Card>
    </div>
  );
}

// OPENING BALANCE (Phase A): the go-live load. Type/paste multi-row entry that
// SETS store stock.available per item and mints an OPENING batch, so a tenant that
// already holds stock can enter it WITHOUT faking production/purchases.
//
// Bootstrap fix: this lists ALL ACTIVE items — unlike the room Stock-take, which
// can only re-count items already present in a room (and shows nothing at all on a
// fresh tenant, since listRooms skips qty<=0).
function OpeningBalancePanel() {
  const itemsRes = useResource(() => catalog.listItems({ limit: 200 }), []);
  const stockRes = useResource(() => stockApi.listStock({ limit: 200 }), []);
  // Opening-balance rows carry room = NULL (store-level). listAdjustments has no
  // "store-only" filter — passing no room returns every adjustment — so filter
  // here and label the source "Store", the counterpart to a room stock-take.
  const histRes = useResource(() => stockApi.listAdjustments({}), []);
  const history = ((histRes.data && histRes.data.adjustments) || []).filter((a) => a.room == null);
  const items = (itemsRes.data && itemsRes.data.items) || [];
  const stock = (stockRes.data && stockRes.data.stock) || [];
  const byCode = {}; stock.forEach((s) => { byCode[s.item.code] = s; });
  const [type, setType] = useState("FG");
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState({}); // itemCode → { qty, buffer }
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);

  const rows = items
    .filter((i) => i.type === type)
    .filter((i) => !q.trim() || (i.code + " " + i.name).toLowerCase().includes(q.trim().toLowerCase()))
    .sort((a, b) => a.code.localeCompare(b.code));
  const cur = (code) => byCode[code] || { available: 0, buffer: 0 };
  const val = (code, k) => { const e = edit[code]; if (e && e[k] !== undefined) return e[k]; return String(cur(code)[k === "qty" ? "available" : "buffer"] ?? 0); };
  const setVal = (code, k, v) => setEdit((s) => ({ ...s, [code]: { ...(s[code] || {}), [k]: v } }));
  // A row counts as "changed" only if its qty or buffer differs from what's stored.
  const changed = Object.entries(edit).filter(([code, e]) => {
    const c = cur(code);
    const qChanged = e.qty !== undefined && Number(e.qty) !== Number(c.available ?? 0);
    const bChanged = e.buffer !== undefined && Number(e.buffer) !== Number(c.buffer ?? 0);
    return (qChanged || bChanged) && !Number.isNaN(Number(e.qty ?? c.available));
  });
  const save = async () => {
    if (!changed.length || busy) return;
    setBusy(true); setErr(""); setResult(null);
    try {
      const lines = changed.map(([code, e]) => {
        const c = cur(code);
        const line = { itemCode: code, countedQty: Number(e.qty !== undefined ? e.qty : c.available ?? 0) };
        if (e.buffer !== undefined && Number(e.buffer) !== Number(c.buffer ?? 0)) line.buffer = Number(e.buffer);
        return line;
      });
      const r = await stockApi.storeAdjustBatch({ lines });
      setResult(r); setEdit({});
      await Promise.all([stockRes.reload(), itemsRes.reload(), histRes.reload()]);
    } catch (e) { setErr(e instanceof ApiError ? e.message : "Could not save opening balances."); }
    finally { setBusy(false); }
  };
  const IN = "mono w-28 rounded-md bg-white py-1 px-2 text-right text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400";
  const vTone = (v) => (v < -0.0001 ? "text-rose-600" : v > 0.0001 ? "text-emerald-600" : "text-slate-400");
  if (itemsRes.loading && items.length === 0) return <Card className="p-6"><LoadingBlock label="Loading items…" /></Card>;
  if (itemsRes.error) return <Card className="p-4"><InlineError message={itemsRes.error} onRetry={itemsRes.reload} /></Card>;
  return (
    <Card>
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-3">
        <ClipboardList size={16} className="text-amber-600" />
        <span className="text-sm font-semibold text-slate-700">Opening balance</span>
        <div className="ml-2 flex gap-1 rounded-lg bg-slate-100 p-1">
          {STOCK_TYPE_TABS.map(([k, l]) => <button key={k} onClick={() => setType(k)} className={cx("whitespace-nowrap rounded-md px-2.5 py-1 text-xs font-medium", type === k ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700")}>{l}</button>)}
        </div>
        <div className="relative"><Search size={14} className="absolute left-2.5 top-2 text-slate-400" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search code / name…" className="w-44 rounded-md bg-white py-1.5 pl-8 pr-2 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400" /></div>
        <Btn className="ml-auto" onClick={save} disabled={busy || changed.length === 0}><CheckCircle2 size={14} /> {busy ? "Saving…" : "Save opening balances" + (changed.length ? " (" + changed.length + ")" : "")}</Btn>
      </div>
      {err ? <div className="px-4 pt-3"><InlineError message={err} /></div> : null}
      {result && (
        <div className="mx-4 mt-3 rounded-lg bg-emerald-50 p-3 ring-1 ring-emerald-200">
          <div className="mb-1 flex items-center gap-1.5 text-sm font-semibold text-emerald-700"><CheckCircle2 size={15} /> Set opening stock for {result.count} item{result.count === 1 ? "" : "s"} — an Opening batch was created per item</div>
          {(result.adjusted || []).map((a) => (
            <div key={a.itemCode} className="text-xs text-emerald-800">
              <Mono className="font-semibold">{a.itemCode}</Mono> {fmtQty(a.systemQty)} → <b>{fmtQty(a.countedQty)}</b> <span className="text-emerald-600">({a.variance > 0 ? "+" : ""}{fmtQty(a.variance)})</span>
              {a.batchCode && <> · batch <Mono>{a.batchCode}</Mono></>}
            </div>
          ))}
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="border-b border-slate-100"><tr><Th>Item</Th><Th className="text-right">Current available</Th><Th className="text-right">Opening qty</Th><Th className="text-right">Buffer (reorder)</Th><Th className="text-right">Variance</Th></tr></thead>
          <tbody className="divide-y divide-slate-50">
            {rows.length === 0 ? <tr><Td className="text-slate-400" colSpan={5}>{q ? "Nothing matches this search." : "No active " + type + " items."}</Td></tr> : rows.map((i) => {
              const c = cur(i.code);
              const v = Number(val(i.code, "qty")) - Number(c.available ?? 0);
              return (
                <tr key={i.code} className={cx("hover:bg-slate-50/50", edit[i.code] ? "bg-indigo-50/30" : "")}>
                  <Td className="font-medium text-slate-700">{i.name}<div className="text-xs font-normal text-slate-400"><Mono>{i.code}</Mono> · {i.type}</div></Td>
                  <Td className="text-right"><Mono className="text-slate-500">{fmtQty(c.available ?? 0)}</Mono></Td>
                  <Td className="text-right"><input type="number" value={val(i.code, "qty")} onChange={(e) => setVal(i.code, "qty", e.target.value)} className={IN} /></Td>
                  <Td className="text-right"><input type="number" value={val(i.code, "buffer")} onChange={(e) => setVal(i.code, "buffer", e.target.value)} className={IN} /></Td>
                  <Td className="text-right">{Math.abs(v) < 0.0001 ? <span className="text-slate-300">—</span> : <Mono className={v > 0 ? "text-emerald-600" : "text-rose-600"}>{v > 0 ? "+" : ""}{fmtQty(v)}</Mono>}</Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="px-4 py-2.5 text-xs text-slate-400">Sets <b>opening STORE stock</b> (Store-wise "Available") for stock you already hold at go-live — no production or purchase needed. Each item also gets a traceable <b>Opening batch</b> (status Passed), so opening stock is consumable/dispatchable like any other batch. Quantities are <b>absolute</b> (re-entering sets, never adds). <b>Buffer</b> is the reorder threshold that drives the below-buffer KPI. Bulk <b>CSV upload is coming in a later phase</b>.</p>
      {history.length > 0 && (
        <div className="border-t border-slate-100">
          <div className="flex items-center gap-2 px-4 py-3"><Activity size={15} className="text-slate-500" /><span className="text-sm font-semibold text-slate-700">Opening balance history</span><span className="text-xs text-slate-400">(store-level — what was set, and by whom)</span></div>
          <div className="overflow-x-auto"><table className="w-full">
            <thead className="border-b border-slate-50"><tr><Th>When</Th><Th>Where</Th><Th>Item</Th><Th className="text-right">Was</Th><Th className="text-right">Set to</Th><Th className="text-right">Variance</Th><Th>Reason</Th><Th>By</Th></tr></thead>
            <tbody className="divide-y divide-slate-50">
              {history.map((a) => (
                <tr key={a.id}>
                  <Td className="whitespace-nowrap text-xs text-slate-500">{fmtDateTime(a.adjustedAt)}</Td>
                  <Td><Badge tone="stone">Store</Badge></Td>
                  <Td className="text-slate-700">{a.item.name} <Mono className="ml-1 text-xs text-slate-400">{a.item.code}</Mono></Td>
                  <Td className="text-right"><Mono className="text-slate-500">{fmtQty(a.systemQty)}</Mono></Td>
                  <Td className="text-right"><Mono className="text-slate-700">{fmtQty(a.countedQty)}</Mono></Td>
                  <Td className="text-right"><Mono className={vTone(a.variance)}>{a.variance > 0.0001 ? "+" : ""}{fmtQty(a.variance)}</Mono></Td>
                  <Td className="text-xs text-slate-500">{a.reason || "—"}</Td>
                  <Td className="text-xs text-slate-500">{a.adjustedBy || "—"}</Td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      )}
    </Card>
  );
}

function InventoryView({ auth }) {
  const [mode, setMode] = useState("main");
  // Opening balance is the go-live load — gated to the roles that own stock:
  // the owner ("*") and the Store Manager (the "inventory" module), matching the
  // /stock/store-adjust gate exactly.
  const canOpen = !!(auth && auth.role && (
    (Array.isArray(auth.role.permissions) && auth.role.permissions.includes("*")) ||
    (Array.isArray(auth.role.navModules) && auth.role.navModules.includes("inventory"))
  ));
  const TAB = (k, label) => (
    <button key={k} onClick={() => setMode(k)} className={cx("whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium", mode === k ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700")}>{label}</button>
  );
  return (
    <div className="space-y-4">
      <div className="flex gap-1 rounded-lg bg-slate-100 p-1 w-min">
        {TAB("main", "Store-wise")}
        {TAB("room", "Room-wise")}
        {TAB("total", "Total (store + room)")}
        {TAB("adjust", "Stock-take")}
        {canOpen && TAB("opening", "Opening balance")}
      </div>
      {mode === "room" ? <RoomStockPanel /> : mode === "total" ? <InventoryTotalPanel /> : mode === "adjust" ? <StockTakePanel /> : mode === "opening" && canOpen ? <OpeningBalancePanel /> : <StockMainPanel auth={auth} />}
    </div>
  );
}

/* ===================== Catalog (Items + BOM) — LIVE (W2) =====================
   view==="bom" ("Items & BOM"). Real API via apiClient + the useResource hook.
   The mock item id (e.g. "FG-PKM") === the backend `code`; screens keep showing
   the human code and use the uuid only for write calls under the hood. Runs
   under the REAL authenticated session (owner sees all) — independent of the
   mock "View as" switcher. Other modules stay on the mock reducer until W3+. */

const ITEM_TYPE_TONE = { FG: "amber", SFG: "violet", PM: "sky", RM: "stone" };
const ITEM_TYPES = ["FG", "SFG", "RM", "PM"];

// PRODUCTION #4: searchable Product Group combobox. Seeds from GET /items/groups (the
// distinct groups this tenant already uses) so users PICK a consistent existing group
// or TYPE a new one — no free-text drift. The value stays a plain string bound to the
// item form's `group`; the submitted payload is unchanged (still items.group text).
function GroupCombobox({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const res = useResource(() => catalog.listGroups(), []);
  const all = (res.data && res.data.groups) || [];
  const v = value || "";
  const filtered = v ? all.filter((g) => g.toLowerCase().includes(v.toLowerCase())) : all;
  const exact = all.some((g) => g.toLowerCase() === v.toLowerCase());
  return (
    <div className="relative">
      <input
        value={v}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Pick an existing group or type a new one"
        className={SETTINGS_IN}
      />
      {open && filtered.length > 0 && (
        <div className="absolute z-20 mt-1 max-h-48 w-full overflow-y-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-slate-200">
          {filtered.map((g) => (
            <button key={g} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => { onChange(g); setOpen(false); }}
              className="block w-full truncate px-3 py-1.5 text-left text-sm text-slate-700 hover:bg-indigo-50">{g}</button>
          ))}
          {v && !exact && <div className="border-t border-slate-100 px-3 py-1.5 text-xs text-slate-400">Enter to add “{v}” as a new group</div>}
        </div>
      )}
    </div>
  );
}

function ItemModal({ open, onClose, onSaved, item }) {
  const edit = !!item;
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [type, setType] = useState("RM");
  const [group, setGroup] = useState("");
  const [lot, setLot] = useState("");
  // GST tax config (Phase 2 UI)
  const [uomCode, setUomCode] = useState("");
  const [hsnCode, setHsnCode] = useState("");
  const [gstOverride, setGstOverride] = useState("");
  const [unitPrice, setUnitPrice] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const unitsRes = useResource(() => unitsApi.list(), []);
  const hsnRes = useResource(() => hsnApi.list(), []);
  const unitList = unitsRes.data?.units || [];
  const hsnList = hsnRes.data?.hsnCodes || [];
  React.useEffect(() => {
    if (!open) return;
    setError(""); setBusy(false);
    setCode(item ? item.code : "");
    setName(item ? item.name : "");
    setType(item ? item.type : "RM");
    setGroup(item && item.group ? item.group : "");
    setLot(item && item.lot != null ? String(item.lot) : "");
    setUomCode(item?.uom?.code || "");
    setHsnCode(item?.hsn?.code || "");
    setGstOverride(item && item.gstRateOverride != null ? String(item.gstRateOverride) : "");
    setUnitPrice(item && item.unitPrice != null ? String(item.unitPrice) : "");
  }, [open, item]);

  // Live effective-rate preview (mirrors backend: override ?? hsn.default ?? null).
  const selHsn = hsnList.find((h) => h.code === hsnCode);
  const effRate = gstOverride !== "" ? Number(gstOverride) : (selHsn ? selHsn.defaultGstRate : null);

  const save = async () => {
    if (busy) return;
    if (!code.trim() || !name.trim()) { setError("Code and name are required."); return; }
    setBusy(true); setError("");
    // For create, omit empties; for edit, send null to clear.
    const taxVal = (v, isNum) => (v === "" ? (edit ? null : undefined) : (isNum ? Number(v) : v));
    const body = {
      code: code.trim(),
      name: name.trim(),
      type,
      group: group.trim() || (edit ? null : undefined),
      lot: lot.trim() === "" ? (edit ? null : undefined) : Number(lot),
      uomCode: taxVal(uomCode, false),
      hsnCode: taxVal(hsnCode, false),
      gstRateOverride: taxVal(gstOverride, true),
      unitPrice: taxVal(unitPrice, true),
    };
    try {
      if (edit) {
        const { code: _c, ...patch } = body;
        await catalog.updateItem(item.id, patch);
      } else {
        await catalog.createItem(body);
      }
      onSaved();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not save the item.");
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={edit ? `Edit item · ${item.code}` : "New item"} wide
      footer={<><Btn variant="soft" onClick={onClose}>Cancel</Btn><Btn onClick={save} disabled={busy}>{busy ? "Saving…" : edit ? "Save changes" : "Create item"}</Btn></>}>
      <div className="grid gap-3 sm:grid-cols-2">
        <SField label="Code"><input value={code} disabled={edit} onChange={(e) => setCode(e.target.value.toUpperCase())} className={SETTINGS_IN} placeholder="e.g. RM-NEW" /></SField>
        <SField label="Type"><select value={type} onChange={(e) => setType(e.target.value)} className={SETTINGS_IN}>{ITEM_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}</select></SField>
        <SField label="Name" full><input value={name} onChange={(e) => setName(e.target.value)} className={SETTINGS_IN} placeholder="Item name" /></SField>
        <SField label="Group (optional)"><GroupCombobox value={group} onChange={setGroup} /></SField>
        <SField label="Lot size (optional)"><input type="number" value={lot} onChange={(e) => setLot(e.target.value)} className={SETTINGS_IN} placeholder="e.g. 10.7289" /></SField>
      </div>
      <div className="mt-4 mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Tax &amp; pricing (for GST invoicing)</div>
      <div className="grid gap-3 sm:grid-cols-2">
        <SField label="Unit of measure"><select value={uomCode} onChange={(e) => setUomCode(e.target.value)} className={SETTINGS_IN}><option value="">— None —</option>{unitList.map((u) => <option key={u.id} value={u.code}>{u.code} · {u.name}</option>)}</select></SField>
        <SField label="Selling price (₹ / unit)"><input type="number" min="0" step="0.01" value={unitPrice} onChange={(e) => setUnitPrice(e.target.value)} className={SETTINGS_IN} placeholder="e.g. 250" /></SField>
        <SField label="HSN code"><select value={hsnCode} onChange={(e) => setHsnCode(e.target.value)} className={SETTINGS_IN}><option value="">— None —</option>{hsnList.map((h) => <option key={h.id} value={h.code}>{h.code} · {h.defaultGstRate}%{h.description ? " · " + h.description : ""}</option>)}</select></SField>
        <SField label="GST rate override (%) — blank = use HSN default"><input type="number" min="0" max="28" step="0.01" value={gstOverride} onChange={(e) => setGstOverride(e.target.value)} className={SETTINGS_IN} placeholder={selHsn ? `default ${selHsn.defaultGstRate}%` : "e.g. 12"} /></SField>
      </div>
      <div className="mt-2 rounded-lg bg-slate-50 p-2.5 text-sm">
        <span className="text-slate-500">Effective GST rate: </span>
        {effRate != null ? <Mono className="font-semibold text-indigo-700">{effRate}%</Mono> : <span className="text-amber-600">not configured (set an HSN or override to enable tax invoicing)</span>}
      </div>
      {error ? <InlineError message={error} /> : null}
    </Modal>
  );
}

function CatalogItemsPanel({ seed, onConsumeSeed } = {}) {
  const [type, setType] = useState("");
  const [q, setQ] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [modal, setModal] = useState(null); // null | "new" | itemRow
  const [acting, setActing] = useState(null); // id mid-activate/deactivate

  // PRODUCTION #2: when the header item-search picks an item, it seeds this screen's
  // search so we land filtered on that item. Reset the type/inactive filters too so
  // the picked item can't be hidden by a stale tab. Consume the seed once applied.
  React.useEffect(() => {
    if (seed && seed.q != null) {
      setType(""); setIncludeInactive(true); setQ(seed.q);
      if (onConsumeSeed) onConsumeSeed();
    }
  }, [seed]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data, loading, error, reload } = useResource(
    () => catalog.listItems({ type, q: q.trim() || undefined, includeInactive }),
    [type, q, includeInactive],
  );
  const rows = data?.items || [];

  const toggleActive = async (row) => {
    setActing(row.id);
    try {
      if (row.isActive) await catalog.deactivateItem(row.id);
      else await catalog.activateItem(row.id);
      await reload();
    } catch (e) {
      // Surface failures inline by reusing the list error slot on next render.
      alert(e instanceof ApiError ? e.message : "Action failed.");
    } finally {
      setActing(null);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1 rounded-lg bg-slate-100 p-1">
          {[["", "All"], ...ITEM_TYPES.map((t) => [t, t])].map(([k, l]) => (
            <button key={k || "all"} onClick={() => setType(k)} className={cx("whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium", type === k ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700")}>{l}</button>
          ))}
        </div>
        <label className="flex items-center gap-1.5 rounded-md bg-white px-2.5 py-1.5 ring-1 ring-slate-200">
          <Search size={14} className="text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search code / name…" className="w-48 bg-transparent text-sm text-slate-700 focus:outline-none" />
        </label>
        <label className="flex items-center gap-1.5 text-xs text-slate-500"><input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} className="accent-indigo-600" /> Show inactive</label>
        <div className="ml-auto flex items-center gap-2 text-xs text-slate-400">
          {loading && rows.length > 0 ? <Spinner size={14} /> : null}
          <span>{data ? `${data.total} item${data.total === 1 ? "" : "s"}` : ""}</span>
          <Btn onClick={() => setModal("new")}><Plus size={15} /> New item</Btn>
        </div>
      </div>

      {error ? <InlineError message={error} onRetry={reload} /> : null}
      <Card className="overflow-x-auto">
        {loading && rows.length === 0 ? (
          <LoadingBlock label="Loading items…" />
        ) : (
          <table className="w-full">
            <thead className="border-b border-slate-100"><tr><Th>Code</Th><Th>Item</Th><Th>Type</Th><Th>Group</Th><Th className="text-right">Lot</Th><Th className="text-right">Price</Th><Th>GST</Th><Th>Status</Th><Th className="text-right">Actions</Th></tr></thead>
            <tbody className="divide-y divide-slate-50">
              {rows.map((i) => (
                <tr key={i.id} className={i.isActive ? "" : "opacity-50"}>
                  <Td><Mono className="text-slate-500">{i.code}</Mono></Td>
                  <Td className="font-medium text-slate-700">{i.name}</Td>
                  <Td><Badge tone={ITEM_TYPE_TONE[i.type] || "stone"}>{i.type}</Badge></Td>
                  <Td className="text-slate-500">{i.group || <span className="text-slate-300">—</span>}</Td>
                  <Td className="text-right"><Mono className="text-slate-500">{i.lot != null ? fmtQty(Number(i.lot)) : "—"}</Mono></Td>
                  <Td className="text-right"><Mono className="text-slate-500">{i.unitPrice != null ? fmtMoney(i.unitPrice) : <span className="text-slate-300">—</span>}</Mono></Td>
                  <Td>{i.effectiveGstRate != null ? <Badge tone="indigo">{i.hsn ? i.hsn.code + " · " : ""}{i.effectiveGstRate}%</Badge> : <span className="text-xs text-slate-300">unconfigured</span>}</Td>
                  <Td>{i.isActive ? <Badge tone="emerald">Active</Badge> : <Badge tone="stone">Inactive</Badge>}</Td>
                  <Td className="text-right">
                    <div className="flex justify-end gap-1.5">
                      <Btn variant="ghost" onClick={() => setModal(i)}>Edit</Btn>
                      <Btn variant="soft" disabled={acting === i.id} onClick={() => toggleActive(i)}>{acting === i.id ? "…" : i.isActive ? "Deactivate" : "Activate"}</Btn>
                    </div>
                  </Td>
                </tr>
              ))}
              {rows.length === 0 && !loading && <tr><Td className="text-slate-400">No items match.</Td></tr>}
            </tbody>
          </table>
        )}
      </Card>

      <ItemModal open={modal != null} item={modal && modal !== "new" ? modal : null} onClose={() => setModal(null)}
        onSaved={() => { setModal(null); reload(); }} />
    </div>
  );
}

// BomModal — create/edit a BOM. Sibling of ItemModal; same Modal/Btn/SField
// chrome + inline error/loading. On CREATE the user picks the made item (FG/SFG)
// this BOM is for; on EDIT that parent is fixed (shown read-only). Components +
// packing are dynamic add/remove rows of {code, qty}. Wires to the already-built
// backend: POST /boms (create) and PUT /boms/:itemCode (wholesale replace).
function BomModal({ open, onClose, onSaved, bom }) {
  const edit = !!bom;
  const [itemCode, setItemCode] = useState("");
  const [outputQty, setOutputQty] = useState("");
  const [yieldPct, setYieldPct] = useState("");
  const [comp, setComp] = useState([{ item: null, qty: "" }]); // PRODUCTION #1: row holds the picked item object
  const [pack, setPack] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Item options for the pickers — loaded when the modal opens.
  // NOTE limit is explicit: omitting it took the /items schema DEFAULT of 50, so
  // these pickers silently showed only 50 items on every tenant. 200 is the schema
  // max and covers every tenant but the largest; a tenant with >200 items still
  // truncates silently here (see `truncated` below). The durable fix is the
  // server-side-search ItemPicker used by CreatePurchaseModal — porting these
  // three pickers to it is a follow-up, deliberately not bundled with the
  // purchase-picker fix.
  const itemsRes = useResource(() => (open ? catalog.listItems({ limit: 200 }) : Promise.resolve(null)), [open]);
  const items = itemsRes.data?.items || [];
  const truncated = !!(itemsRes.data && itemsRes.data.total > items.length);
  const madeItems = items.filter((i) => i.type === "FG" || i.type === "SFG");
  // PRODUCTION #1: the product's uom for the output-line unit — from getBom on edit (a code
  // string), from the picked item in the loaded list on create. "" if the item has no uom set.
  const productUom = edit ? (bom?.item?.uom || "") : (items.find((i) => i.code === itemCode)?.uom?.code || "");

  React.useEffect(() => {
    if (!open) return;
    setError("");
    setBusy(false);
    if (bom) {
      setItemCode(bom.item?.code || "");
      setOutputQty(bom.outputQty != null ? String(bom.outputQty) : "");
      setYieldPct(bom.yield != null ? String(bom.yield) : "");
      // PRODUCTION #1: reconstruct the ItemPicker value from getBom (code+name+type+uom) so an
      // existing component — even an inactive one — resolves without the 200-cap picker list.
      const toRow = (c) => ({ item: { code: c.code, name: c.name, type: c.type, uom: c.uom ? { code: c.uom } : null }, qty: String(c.qty) });
      setComp((bom.comp || []).map(toRow));
      setPack((bom.pack || []).map(toRow));
    } else {
      setItemCode("");
      setOutputQty("");
      setYieldPct("");
      setComp([{ item: null, qty: "" }]);
      setPack([]);
    }
  }, [open, bom]);

  const editRow = (rows, setRows, idx, key, val) => setRows(rows.map((r, i) => (i === idx ? { ...r, [key]: val } : r)));
  const addRow = (rows, setRows) => setRows([...rows, { item: null, qty: "" }]);
  const delRow = (rows, setRows, idx) => setRows(rows.filter((_, i) => i !== idx));

  const save = async () => {
    if (busy) return;
    setError("");
    const parent = edit ? bom.item?.code : itemCode.trim();
    if (!parent) { setError("Pick the item this BOM is for."); return; }
    const oq = Number(outputQty);
    if (!(oq > 0)) { setError("Output qty must be greater than 0."); return; }
    // Drop blank rows; coerce qty to real numbers (backend requires numbers > 0). The picker
    // supplies the item object; we submit its code (stored BOM shape unchanged: code + qty).
    const clean = (rows) => rows.map((r) => ({ code: r.item ? r.item.code : "", qty: Number(r.qty) })).filter((r) => r.code && r.qty > 0);
    const compClean = clean(comp);
    const packClean = clean(pack);
    if (compClean.length === 0) { setError("Add at least one component (item + qty > 0)."); return; }
    const payload = {
      outputQty: oq,
      ...(yieldPct.trim() !== "" ? { yield: Number(yieldPct) } : {}),
      comp: compClean,
      pack: packClean,
    };
    setBusy(true);
    try {
      if (edit) await catalog.updateBom(parent, payload);
      else await catalog.createBom({ itemCode: parent, ...payload });
      onSaved(parent);
    } catch (e) {
      // Envelope message (e.g. 409 "A BOM already exists for this item", or an
      // unknown component code) surfaced inline.
      setError(e instanceof ApiError ? e.message : "Could not save the BOM.");
      setBusy(false);
    }
  };

  const rowSection = (title, rows, setRows, emptyHint) => (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">{title}</span>
        <Btn variant="soft" onClick={() => addRow(rows, setRows)}><Plus size={13} /> Add row</Btn>
      </div>
      {rows.length === 0 ? <p className="px-1 pb-1 text-xs text-slate-400">{emptyHint}</p> : null}
      <div className="space-y-1.5">
        {rows.map((r, idx) => (
          <div key={idx} className="flex flex-wrap items-center gap-2">
            {/* PRODUCTION #1: searchable ItemPicker (name search, no 200-cap, resolves inactive
                components on edit). No typeFilter — a component may be RM / SFG / PM, so all are
                searchable; the backend validates the code on save.
                LAYOUT FIX: ItemPicker is flex-1 with no min-width, so in this row (qty/uom/delete
                are fixed-width) it was the only shrinkable element and collapsed to ~1 char on
                narrow/zoomed widths, truncating its w-full dropdown to "F". flex-wrap on the row +
                a min-w-[12rem] floor here keep the picker readable and wrap the siblings below. */}
            <div className="flex min-w-[12rem] flex-1">
              <ItemPicker value={r.item} onPick={(it) => editRow(rows, setRows, idx, "item", it)} onClear={() => editRow(rows, setRows, idx, "item", null)} />
            </div>
            <input type="number" value={r.qty} onChange={(e) => editRow(rows, setRows, idx, "qty", e.target.value)} placeholder="Qty" className={cx(SETTINGS_IN, "w-24")} />
            <span className="w-10 text-xs text-slate-400">{r.item && r.item.uom ? r.item.uom.code : ""}</span>
            <button onClick={() => delRow(rows, setRows, idx)} className="rounded-md p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600"><X size={15} /></button>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <Modal open={open} onClose={onClose} wide title={edit ? `Edit BOM · ${bom.item?.code}` : "New BOM"}
      footer={<><Btn variant="soft" onClick={onClose}>Cancel</Btn><Btn onClick={save} disabled={busy}>{busy ? "Saving…" : edit ? "Save changes" : "Create BOM"}</Btn></>}>
      {itemsRes.loading && items.length === 0 ? <LoadingBlock label="Loading items…" /> : (
        <div className="space-y-4">
          {truncated && <div className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800 ring-1 ring-amber-200"><AlertTriangle size={13} className="mr-1 inline" /> Showing the first {items.length} of {itemsRes.data.total} items — these pickers do not yet search, so an item beyond the first {items.length} won't be listed. Create the BOM for such an item from <b>Items &amp; BOM</b>, or ask for the searchable picker.</div>}
          <div className="grid gap-3 sm:grid-cols-3">
            <SField label="For item (FG / SFG)">
              {edit ? (
                <input value={`${bom.item?.code} · ${bom.item?.name}`} disabled className={SETTINGS_IN} />
              ) : (
                <select value={itemCode} onChange={(e) => setItemCode(e.target.value)} className={SETTINGS_IN}>
                  <option value="">Select item…</option>
                  {madeItems.map((i) => <option key={i.code} value={i.code}>{i.code} · {i.name} ({i.type})</option>)}
                </select>
              )}
            </SField>
            <SField label={`Output qty / batch${productUom ? ` (${productUom})` : ""}`}><input type="number" value={outputQty} onChange={(e) => setOutputQty(e.target.value)} className={SETTINGS_IN} placeholder="e.g. 100" /></SField>
            <SField label="Yield % (optional)"><input type="number" value={yieldPct} onChange={(e) => setYieldPct(e.target.value)} className={SETTINGS_IN} placeholder="e.g. 95" /></SField>
          </div>
          {rowSection("Components", comp, setComp, "No components yet — add at least one.")}
          {rowSection("Packing (optional)", pack, setPack, "No packing rows.")}
          {error ? <InlineError message={error} /> : null}
        </div>
      )}
    </Modal>
  );
}

function CatalogBomPanel() {
  const list = useResource(() => catalog.listBoms({}), []);
  const boms = list.data?.boms || [];
  const [openCode, setOpenCode] = useState(null);
  const [modal, setModal] = useState(null); // null | "new" | "edit"
  // Default selection to the first BOM once the list loads. The list endpoint
  // projects itemCode/itemName (not code/name).
  React.useEffect(() => {
    if (!openCode && boms.length) setOpenCode(boms[0].itemCode);
  }, [boms, openCode]);

  const detail = useResource(
    () => (openCode ? catalog.getBom(openCode) : Promise.resolve(null)),
    [openCode],
  );
  const d = detail.data;

  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <Card className="p-2 lg:col-span-1">
        <div className="mb-1 flex items-center justify-between px-1 pt-1">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Bill of materials</span>
          <Btn onClick={() => setModal("new")}><Plus size={14} /> New BOM</Btn>
        </div>
        {list.loading && boms.length === 0 ? <LoadingBlock label="Loading BOMs…" /> : null}
        {list.error ? <InlineError message={list.error} onRetry={list.reload} /> : null}
        {boms.map((b) => (
          <button key={b.itemCode} onClick={() => setOpenCode(b.itemCode)} className={cx("flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm", openCode === b.itemCode ? "bg-amber-50 text-amber-900 ring-1 ring-amber-600/15" : "text-slate-600 hover:bg-slate-50")}>
            <span className="min-w-0"><span className="block truncate font-medium">{b.itemName}</span><Mono className="text-xs text-slate-400">{b.itemCode} · {b.componentCount ?? "—"} comp</Mono></span>
            <GitBranch size={14} className="shrink-0 text-slate-300" />
          </button>
        ))}
        {!list.loading && boms.length === 0 && !list.error && <p className="px-3 py-2 text-sm text-slate-400">No BOMs yet.</p>}
      </Card>
      <Card className="p-4 lg:col-span-2">
        {detail.loading ? <LoadingBlock label="Loading BOM…" /> : detail.error ? (
          <InlineError message={detail.error} onRetry={detail.reload} />
        ) : d ? (
          <>
            <div className="mb-3 flex items-center justify-between border-b border-slate-100 pb-3">
              <div><div className="font-semibold text-slate-800">{d.item?.name}</div><div className="text-xs text-slate-400"><Mono>{d.item?.code}</Mono> · output <Mono>{fmtQty(Number(d.outputQty))}</Mono>{d.item?.uom ? " " + d.item.uom : ""}/batch</div></div>
              <div className="flex items-center gap-3">
                <div className="text-right text-xs"><div className="text-slate-400">Yield</div><Mono className="text-base font-semibold text-emerald-600">{d.yield}%</Mono></div>
                <Btn variant="soft" onClick={() => setModal("edit")}>Edit</Btn>
              </div>
            </div>
            <div className="mb-3">
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Components</div>
              {(d.comp || []).map((c, k) => (
                <div key={"c" + k} className="flex items-center justify-between rounded-md px-2 py-1.5 hover:bg-slate-50">
                  <span className="text-sm text-slate-700">{c.name} <Mono className="text-xs text-slate-400">· {c.code}</Mono></span>
                  <Mono className="text-xs text-slate-500">{fmtQty(Number(c.qty))}{c.uom ? " " + c.uom : ""}</Mono>
                </div>
              ))}
              {(d.comp || []).length === 0 && <p className="px-2 text-sm text-slate-400">No components.</p>}
            </div>
            <div>
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Packing</div>
              {(d.pack || []).map((c, k) => (
                <div key={"p" + k} className="flex items-center justify-between rounded-md px-2 py-1.5 hover:bg-slate-50">
                  <span className="text-sm text-slate-700">{c.name} <Mono className="text-xs text-slate-400">· {c.code}</Mono></span>
                  <Mono className="text-xs text-slate-500">{fmtQty(Number(c.qty))}{c.uom ? " " + c.uom : ""}</Mono>
                </div>
              ))}
              {(d.pack || []).length === 0 && <p className="px-2 text-sm text-slate-400">No packing.</p>}
            </div>
          </>
        ) : (
          <p className="text-sm text-slate-400">Select a BOM to view its components.</p>
        )}
      </Card>
      <BomModal
        open={modal != null}
        bom={modal === "edit" ? d : null}
        onClose={() => setModal(null)}
        onSaved={(code) => { setModal(null); setOpenCode(code); list.reload(); detail.reload(); }}
      />
    </div>
  );
}

function CatalogView({ seed, onConsumeSeed } = {}) {
  const [tab, setTab] = useState("items");
  // A header-search seed always lands on the Items tab (that's where an item opens).
  React.useEffect(() => { if (seed) setTab("items"); }, [seed]);
  return (
    <div className="space-y-4">
      <div className="flex gap-1 rounded-lg bg-slate-100 p-1 w-min">
        <button onClick={() => setTab("items")} className={cx("whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium", tab === "items" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700")}>Items</button>
        <button onClick={() => setTab("bom")} className={cx("whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium", tab === "bom" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700")}>Bill of materials</button>
      </div>
      {tab === "items" ? <CatalogItemsPanel seed={seed} onConsumeSeed={onConsumeSeed} /> : <CatalogBomPanel />}
    </div>
  );
}

/* ============================ Deliveries ============================ */
/* ============================ Dispatch / Deliveries — LIVE (W5) ============================
   Dispatchable lines (allocated-but-unshipped) come from /dispatch/dispatchable;
   creating a challan POSTs /dispatch {via,picks} (may span parties; server caps
   qty ≤ dispatchable). Deliver/cancel/generate-invoice POST their endpoints and
   reload. Gated by the REAL session (authCan → /auth/me perms). */
function DeliveriesView({ auth, role }) {
  const { data, loading, error, reload } = useResource(() => dispatchApi.listDeliveries(), []);
  const rows = data?.deliveries || [];
  const [open, setOpen] = useState(false);
  const [exp, setExp] = useState(null);
  const [busyCode, setBusyCode] = useState(null);
  const [actErr, setActErr] = useState("");
  const [notice, setNotice] = useState("");
  const pcan = (k) => authCan(auth, role, k);
  const act = async (code, fn, okMsg) => {
    setBusyCode(code); setActErr(""); setNotice("");
    try { const r = await fn(); await reload(); if (okMsg) setNotice(typeof okMsg === "function" ? okMsg(r) : okMsg); }
    catch (e) { setActErr((e instanceof ApiError ? e.message : "Action failed.") + " (" + code + ")"); }
    finally { setBusyCode(null); }
  };
  const openChallan = async (code, print) => {
    try { const d = (await dispatchApi.getDelivery(code)).delivery; openDoc("Challan " + code, challanDocLive(d, auth?.company?.name), print); }
    catch (e) { setActErr(e instanceof ApiError ? e.message : "Could not load the challan."); }
  };
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-slate-400">Dispatch = allocated stock load kore challan banano → Delivered mark → invoice generate. Ek challan-e <b>multiple company</b>-r line jete pare.</p>
        {pcan("dispatch") && <Btn onClick={() => setOpen(true)}><Plus size={15} /> New dispatch</Btn>}
      </div>
      {notice ? <div className="rounded-md bg-emerald-50 px-3 py-2 text-xs text-emerald-700 ring-1 ring-emerald-200">{notice}</div> : null}
      {actErr ? <InlineError message={actErr} /> : null}
      {error ? <InlineError message={error} onRetry={reload} /> : null}
      {loading && rows.length === 0 ? <LoadingBlock label="Loading deliveries…" /> : (
        <Card className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-slate-100"><tr><Th>Challan</Th><Th>Companies</Th><Th>Assigned</Th><Th>Via</Th><Th className="text-right">Qty</Th><Th>Status</Th><Th>Date</Th><Th className="text-right">Documents / actions</Th></tr></thead>
            <tbody className="divide-y divide-slate-50">
              {rows.length === 0 ? <tr><Td className="text-slate-400" colSpan={8}>No delivery challans yet — create one with "New dispatch".</Td></tr> : rows.map((d) => {
                const cos = d.parties.map((p) => p.name);
                return (
                  <React.Fragment key={d.code}>
                    <tr className="cursor-pointer hover:bg-slate-50/70" onClick={() => setExp(exp === d.code ? null : d.code)}>
                      <Td><Mono className="font-medium text-slate-800">{d.code}</Mono></Td>
                      <Td><div className="font-medium text-slate-700">{cos[0] || "—"}</div>{cos.length > 1 && <span className="text-xs font-medium text-amber-700">+{cos.length - 1} more company</span>}</Td>
                      <Td className="text-xs">{d.assignedUser ? <span className="text-slate-600">{d.assignedUser.name}</span> : <span className="text-slate-300">Unassigned</span>}</Td>
                      <Td className="text-slate-600">{d.via || <span className="text-slate-300">—</span>}</Td>
                      <Td className="text-right"><Mono>{fmtQty(d.totalQty)}</Mono> Kg</Td>
                      <Td><Badge tone={statusTone(d.status)}>{d.status}</Badge>{d.deliveredAt && <div className="mt-0.5 text-[10px] text-emerald-600">✓ {fmtDateTime(d.deliveredAt)}{d.deliveredByUser ? " · " + d.deliveredByUser.name.split(" ")[0] : ""}{d.rawDurationHours != null ? " · " + d.rawDurationHours + "h" : ""}</div>}</Td>
                      <Td className="text-slate-500">{fmtDate(d.deliveryDate)}</Td>
                      <Td className="text-right">
                        <div className="flex flex-wrap justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
                          <Btn variant="ghost" onClick={() => openChallan(d.code, false)}><Download size={13} /> PDF</Btn>
                          <Btn variant="ghost" onClick={() => openChallan(d.code, true)}><Printer size={13} /> Print</Btn>
                          {d.status === "In Transit" && pcan("deliver") && <Btn variant="soft" disabled={busyCode === d.code} onClick={() => act(d.code, () => dispatchApi.deliver(d.code))}><CheckCircle2 size={13} /> Delivered</Btn>}
                          {d.status === "In Transit" && !d.invoiced && pcan("deliver") && <Btn variant="soft" disabled={busyCode === d.code} onClick={() => act(d.code, () => dispatchApi.cancelDelivery(d.code))}><X size={13} /> Cancel</Btn>}
                          {/* Delivered but still not invoiced = auto-invoicing was skipped or failed
                              (e.g. company GST state unset). The delivery stands; invoice is manual. */}
                          {d.status === "Delivered" && !d.invoiced && <Badge tone="amber"><AlertTriangle size={11} /> Needs invoice</Badge>}
                          {!d.invoiced && pcan("invoice") && <Btn disabled={busyCode === d.code} onClick={() => act(d.code, () => invoicesApi.generateInvoice(d.code), (r) => (r && r.invoices ? r.invoices.length : 0) + " invoice(s) generated")}><FileText size={13} /> Invoice</Btn>}
                          {d.invoiced && <Badge tone="emerald">Invoiced</Badge>}
                        </div>
                      </Td>
                    </tr>
                    {exp === d.code && <tr><td colSpan={8} className="bg-slate-50/60 px-6 py-2"><DeliveryLines code={d.code} /></td></tr>}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}
      <NewDispatchModal open={open} onClose={() => setOpen(false)} onCreated={() => { reload(); setNotice("Dispatch challan created (In Transit)."); }} />
    </div>
  );
}

function DeliveryLines({ code }) {
  const { data, loading, error, reload } = useResource(() => dispatchApi.getDelivery(code), [code]);
  const d = data?.delivery;
  if (loading && !d) return <LoadingBlock label="Loading lines…" />;
  if (error) return <InlineError message={error} onRetry={reload} />;
  if (!d) return null;
  return (
    <div className="py-1">
      {/* Performance attribution (Phase 3): assignee + delivery-completion timing. */}
      <div className="mb-2 flex flex-wrap items-center gap-x-5 gap-y-1 rounded-md bg-indigo-50/50 px-2.5 py-1.5 text-sm ring-1 ring-indigo-100">
        <span className="text-slate-500">Assigned to: <span className="font-medium text-slate-800">{d.assignedUser ? d.assignedUser.name : <span className="text-slate-400">Unassigned</span>}</span></span>
        {d.deliveredAt
          ? <><span className="flex items-center gap-1 text-emerald-700"><CheckCircle2 size={13} /> Delivered {fmtDateTime(d.deliveredAt)}{d.deliveredByUser ? " by " + d.deliveredByUser.name : ""}</span>
              {d.rawDurationHours != null && <span className="text-slate-500">Duration (raw): <Mono className="font-medium text-slate-800">{d.rawDurationHours} h</Mono></span>}</>
          : <span className="text-slate-400">Not yet delivered</span>}
      </div>
      {d.lines.map((l, k) => (
        <div key={k} className="flex justify-between py-1 text-sm"><span className="text-slate-600">{l.item.name} <span className="text-xs text-slate-400">· <Mono>{l.orderCode}</Mono> · {l.party.name}</span></span><Mono className="text-slate-700">{fmtQty(l.qty)} Kg</Mono></div>
      ))}
    </div>
  );
}

function NewDispatchModal({ open, onClose, onCreated }) {
  const { data, loading, error, reload } = useResource(async () => {
    if (!open) return null;
    // The employee picker is optional; users.list is admin-gated ("settings"), so
    // a non-admin dispatcher gets 403 — degrade gracefully to no picker rather than
    // blocking DC creation (assignment stays optional).
    const [disp, us] = await Promise.all([dispatchApi.listDispatchable(), usersApi.list({ status: "ACTIVE" }).catch(() => ({ users: [] }))]);
    return { dispatchable: disp.dispatchable || [], users: us.users || [] };
  }, [open]);
  const dlines = data?.dispatchable || [];
  const users = data?.users || [];
  const [picks, setPicks] = useState({});
  const [via, setVia] = useState("");
  const [assignedUserId, setAssignedUserId] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  React.useEffect(() => { if (open) { setPicks({}); setVia(""); setAssignedUserId(""); setErr(""); } }, [open]);
  const groups = {};
  dlines.forEach((x) => { (groups[x.party.code] = groups[x.party.code] || { party: x.party, rows: [] }).rows.push(x); });
  const selCount = Object.keys(picks).filter((k) => picks[k]).length;
  // ONE COMPANY PER CHALLAN. The first ticked line LOCKS the challan to its company;
  // the other groups grey out until the selection is cleared. Not a nicety — a mixed
  // challan can only be delivered all-at-once, which would invoice a company whose
  // goods are still on the truck. createDispatch enforces the same rule server-side.
  const lockedParty = (() => {
    const first = dlines.find((x) => picks[x.lineCode]);
    return first ? first.party.code : null;
  })();
  const selectAllFor = (g) => setPicks((q) => {
    const n = { ...q };
    g.rows.forEach((x) => { n[x.lineCode] = x.dispatchable; });
    return n;
  });
  const clearAll = () => setPicks({});
  const submit = async () => {
    const arr = dlines.filter((x) => picks[x.lineCode]).map((x) => ({ orderCode: x.orderCode, lineCode: x.lineCode, qty: Math.min(Number(picks[x.lineCode]), x.dispatchable) }));
    if (!arr.length) return;
    setBusy(true); setErr("");
    try { await dispatchApi.createDispatch({ via: via.trim() || undefined, picks: arr, assignedUserId: assignedUserId || undefined }); setPicks({}); setVia(""); setAssignedUserId(""); onClose(); onCreated && onCreated(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : "Could not create the dispatch."); setBusy(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title="Create dispatch (delivery challan)" wide
      footer={<><Btn variant="soft" onClick={onClose}>Cancel</Btn><Btn onClick={submit} disabled={busy || selCount === 0}>{busy ? "Creating…" : lockedParty ? "Create challan for " + (groups[lockedParty]?.party.name || lockedParty) + " (" + selCount + ")" : "Create challan"}</Btn></>}>
      {loading && !data ? <LoadingBlock label="Loading dispatchable lines…" /> : error ? <InlineError message={error} onRetry={reload} /> : dlines.length === 0 ? <p className="text-sm text-slate-500">No dispatchable (allocated, un-shipped) lines. Fulfill/allocate an order first.</p> : (
        <>
          {err ? <InlineError message={err} /> : null}
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-sm"><span className="mb-1 block font-medium text-slate-600">Delivered via / through</span>
              <input value={via} onChange={(e) => setVia(e.target.value)} placeholder="e.g. Self · Tempo WB-02-1234 / BlueDart / Ramesh (driver)" className="w-full rounded-md bg-white py-1.5 px-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400" /></label>
            <label className="block text-sm"><span className="mb-1 block font-medium text-slate-600">Assign to (store employee)</span>
              <select value={assignedUserId} onChange={(e) => setAssignedUserId(e.target.value)} className="w-full rounded-md bg-white py-1.5 px-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400"><option value="">Unassigned</option>{users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</select></label>
          </div>
          <div className="mt-3 mb-1.5 flex flex-wrap items-center gap-2">
            <p className="text-xs text-slate-400">One challan is for one company, so it can be delivered and invoiced on its own schedule. Create a separate challan for each company. (Partial by lowering qty.)</p>
            {lockedParty && <Btn variant="soft" className="ml-auto" onClick={clearAll}>Clear selection</Btn>}
          </div>
          <div className="space-y-3">
            {Object.values(groups).map((g) => {
              const off = !!lockedParty && lockedParty !== g.party.code;
              return (
              <div key={g.party.code} className={cx("rounded-lg ring-1", off ? "opacity-50 ring-slate-100" : lockedParty === g.party.code ? "ring-amber-300" : "ring-slate-200")}>
                <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-3 py-2">
                  <Building2 size={14} className="text-amber-600" /><span className="text-sm font-semibold text-slate-700">{g.party.name}</span><Mono className="text-xs text-slate-400">{g.party.code}</Mono>
                  {off ? <span className="ml-auto text-xs text-slate-400">Another company is on this challan — dispatch this one separately.</span>
                    : <button onClick={() => selectAllFor(g)} className="ml-auto rounded-md px-2 py-0.5 text-xs font-medium text-indigo-600 ring-1 ring-indigo-200 hover:bg-indigo-50">Select all for this company</button>}
                </div>
                <div className="space-y-1.5 p-2">
                  {g.rows.map((x) => (
                    <div key={x.lineCode} className={cx("flex items-center gap-3 rounded-md px-2 py-1.5", off ? "" : "hover:bg-slate-50")}>
                      <input type="checkbox" disabled={off} checked={!!picks[x.lineCode]} onChange={(e) => setPicks((q) => ({ ...q, [x.lineCode]: e.target.checked ? x.dispatchable : undefined }))} className="h-4 w-4 accent-amber-700 disabled:opacity-40" />
                      <div className="min-w-0 flex-1"><div className="truncate text-sm font-medium text-slate-700">{x.item.name}</div><div className="text-xs text-slate-400"><Mono>{x.orderCode}</Mono> · ready <Mono>{fmtQty(x.dispatchable)}</Mono> Kg</div></div>
                      <input type="number" disabled={off || !picks[x.lineCode]} value={picks[x.lineCode] ?? ""} max={x.dispatchable} onChange={(e) => setPicks((q) => ({ ...q, [x.lineCode]: e.target.value }))} className="mono w-24 rounded-md bg-white py-1.5 px-2.5 text-sm ring-1 ring-slate-200 disabled:bg-slate-50" placeholder="Qty" />
                    </div>
                  ))}
                </div>
              </div>
              );
            })}
          </div>
        </>
      )}
    </Modal>
  );
}

/* ============================ Invoices — LIVE (W5) ============================
   List from /invoices; generate-from-delivery lives on the Deliveries screen (a
   cross-party DC mints one INV per party). PDF/print fetch the detail and render
   the live invoice doc. */
/* ============================ Payments (Phase 2) ============================
   Record money RECEIVED against invoices. Two modes:
     • invoice-wise — pay ONE named invoice; any excess goes on account as an
       advance and deliberately does NOT spill onto other invoices.
     • lump sum     — auto-allocate across the party's OPEN invoices oldest-first
       (FIFO), leftover on account.
   The allocation PREVIEW is computed SERVER-SIDE by the same allocator the write
   uses (POST /payments/preview), so what you confirm is exactly what is recorded —
   a client-side re-implementation would be a second source of truth. */
const PAY_METHODS = [["bank", "Bank transfer"], ["cash", "Cash"], ["upi", "UPI"], ["cheque", "Cheque"], ["other", "Other"]];

function RecordPaymentModal({ open, onClose, onSaved, partyCode, invoiceCode }) {
  const [mode, setMode] = useState(invoiceCode ? "invoice" : "lumpsum");
  // WHO IS PAYING — state, not a read-only prop.
  //   invoice mode  : LOCKED to the invoice's party (the invoice determines it).
  //   lump-sum mode : the user PICKS. Defaults to empty on purpose — this used to
  //                   silently inherit rows[0] (the tenant's lowest invoice code),
  //                   so a lump sum paid as an arbitrary company with no prompt.
  const [party, setParty] = useState(partyCode || "");
  const [pq, setPq] = useState("");        // picker search text
  const [picking, setPicking] = useState(false);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("bank");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);

  React.useEffect(() => {
    if (!open) return;
    setMode(invoiceCode ? "invoice" : "lumpsum");
    setParty(partyCode || ""); setPq(""); setPicking(false);
    setAmount(""); setMethod("bank"); setReference(""); setNotes("");
    setDate(new Date().toISOString().slice(0, 10));
    setPreview(null); setErr(""); setDone(null); setBusy(false);
  }, [open, invoiceCode, partyCode]);

  // The party's open invoices + any existing advance ("₹X on account").
  const stateRes = useResource(
    () => (open && party ? paymentsApi.partyState(party) : Promise.resolve(null)),
    [open, party],
  );
  // The picker list — payments-gated (same permission as the create it feeds), so
  // ACCOUNTS, who lack the "customers" module, can load it. Only fetched when the
  // user actually has a choice to make (lump-sum mode).
  const partiesRes = useResource(
    () => (open && !invoiceCode ? paymentsApi.pickerParties() : Promise.resolve(null)),
    [open, invoiceCode],
  );
  const allParties = (partiesRes.data && partiesRes.data.parties) || [];
  const sistersOf = (code) => allParties.filter((x) => x.parentCode === code);
  const partyByCode = (code) => allParties.find((x) => x.code === code) || null;
  const pickList = allParties.filter((x) => {
    const q = pq.trim().toLowerCase();
    if (!q) return true;
    return x.name.toLowerCase().includes(q) || x.code.toLowerCase().includes(q);
  });
  const st = stateRes.data;
  const amt = Number(amount) || 0;
  const isGroup = !!(st && st.scope && st.scope.isGroup);
  const chosen = st && invoiceCode ? (st.openInvoices || []).find((o) => o.code === invoiceCode) : null;

  // Debounced live preview — the server runs the real allocator.
  React.useEffect(() => {
    if (!open || !party || amt <= 0) { setPreview(null); return; }
    let dead = false;
    const t = setTimeout(async () => {
      try {
        const p = await paymentsApi.preview({ partyCode: party, amount: amt, mode, invoiceCode: mode === "invoice" ? invoiceCode : undefined });
        if (!dead) { setPreview(p); setErr(""); }
      } catch (e) { if (!dead) { setPreview(null); setErr(e instanceof ApiError ? e.message : "Could not preview the allocation."); } }
    }, 300);
    return () => { dead = true; clearTimeout(t); };
  }, [open, party, amt, mode, invoiceCode]);

  const save = async () => {
    if (amt <= 0 || busy) return;
    if (!party) { setErr("Pick who's paying."); return; }
    setBusy(true); setErr("");
    try {
      const r = await paymentsApi.create({
        partyCode: party, amount: amt, mode, method,
        invoiceCode: mode === "invoice" ? invoiceCode : undefined,
        paymentDate: date ? new Date(date + "T00:00:00.000Z").toISOString() : undefined,
        reference: reference.trim() || undefined,
        notes: notes.trim() || undefined,
      });
      setDone(r); onSaved && onSaved();
    } catch (e) { setErr(e instanceof ApiError ? e.message : "Could not record the payment."); }
    finally { setBusy(false); }
  };

  const IN = "w-full rounded-md bg-white py-1.5 px-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400";
  return (
    <Modal open={open} onClose={onClose} wide title={"Record payment" + (party ? " — " + party : "")}
      footer={done
        ? <Btn onClick={onClose}>Done</Btn>
        : <><Btn variant="soft" onClick={onClose}>Cancel</Btn><Btn onClick={save} disabled={busy || amt <= 0 || !party}>{busy ? "Recording…" : "Record payment"}</Btn></>}>
      {done ? (
        <div className="space-y-3">
          <div className="rounded-lg bg-emerald-50 p-3 ring-1 ring-emerald-200">
            <div className="mb-1 flex items-center gap-1.5 text-sm font-semibold text-emerald-700"><CheckCircle2 size={15} /> Recorded {done.payment.code} — {fmtMoney(done.payment.amount)}</div>
            {done.payment.allocations.map((a) => (
              <div key={a.invoiceCode} className="text-xs text-emerald-800">
                <Mono className="font-semibold">{a.invoiceCode}</Mono>{a.billTo && a.billTo.code !== partyCode ? <span className="text-emerald-700"> ({a.billTo.code})</span> : null} received {fmtMoney(a.amount)} · balance now <b>{fmtMoney(a.balanceAfter)}</b>{a.balanceAfter <= 0.001 && " — fully paid"}
              </div>
            ))}
            {done.payment.unapplied > 0.001 && <div className="mt-1 text-xs text-emerald-800">{fmtMoney(done.payment.unapplied)} left <b>on account</b> as an advance (future invoices will consume it).</div>}
          </div>
          {done.advance > 0.001 && <p className="text-xs text-slate-500">{partyCode} now holds <b>{fmtMoney(done.advance)}</b> on account.</p>}
        </div>
      ) : (
        <div className="space-y-3">
          {err ? <InlineError message={err} /> : null}
          {/* WHO'S PAYING. Locked in invoice mode (the invoice decides); a searchable
              pick in lump-sum mode. Mothers are listed alongside sisters — that is what
              makes the group-wide FIFO reachable at all, since a mother is never billed
              and so can never arrive here from an invoice's billTo. */}
          {invoiceCode ? null : (
            <div className="rounded-lg ring-1 ring-slate-200">
              <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-3 py-2">
                <Users size={14} className="text-amber-600" />
                <span className="text-sm font-semibold text-slate-700">Who's paying?</span>
                {party ? (
                  <>
                    <span className="inline-flex items-center gap-1.5 rounded-md bg-slate-50 px-2 py-0.5 text-sm ring-1 ring-slate-200">
                      <Mono className="text-slate-400">{party}</Mono>
                      <span className="font-medium text-slate-700">{partyByCode(party)?.name || ""}</span>
                      {sistersOf(party).length > 0 && <Badge tone="violet">group · {sistersOf(party).length} sisters</Badge>}
                    </span>
                    <Btn variant="soft" className="ml-auto" onClick={() => { setPicking((v) => !v); setPq(""); }}>Change</Btn>
                  </>
                ) : (
                  <span className="text-xs text-rose-600">Pick a company before recording — nothing is assumed.</span>
                )}
              </div>
              {(!party || picking) && (
                <div className="p-2">
                  <input value={pq} onChange={(e) => setPq(e.target.value)} placeholder="Search company by name or code…" className={IN} autoFocus />
                  {partiesRes.loading ? <p className="px-1 py-2 text-xs text-slate-400">Loading companies…</p>
                    : partiesRes.error ? <div className="pt-2"><InlineError message={partiesRes.error} onRetry={partiesRes.reload} /></div>
                    : pickList.length === 0 ? <p className="px-1 py-2 text-xs text-slate-400">No company matches “{pq}”.</p>
                    : (
                      <div className="mt-2 max-h-56 overflow-y-auto rounded-md ring-1 ring-slate-100">
                        {pickList.map((x) => {
                          const kids = sistersOf(x.code);
                          const mum = x.parentCode ? partyByCode(x.parentCode) : null;
                          return (
                            <button key={x.code} onClick={() => { setParty(x.code); setPicking(false); setPq(""); }}
                              className={cx("flex w-full items-center gap-2 px-2.5 py-1.5 text-left hover:bg-indigo-50/60", x.parentCode ? "pl-6" : "")}>
                              <Mono className="w-14 shrink-0 text-xs text-slate-400">{x.code}</Mono>
                              <span className="flex-1 text-sm font-medium text-slate-700">{x.name}</span>
                              {kids.length > 0 && <Badge tone="violet">group · pays for {kids.length} sisters</Badge>}
                              {mum && <span className="text-xs text-slate-400">under {mum.name}</span>}
                            </button>
                          );
                        })}
                      </div>
                    )}
                </div>
              )}
            </div>
          )}
          {st && st.scope && st.scope.isGroup && (
            <div className="rounded-md bg-violet-50 px-3 py-2 text-xs text-violet-800 ring-1 ring-violet-200">
              <b>Group payment.</b> {st.party?.name || party} is a mother party — a lump sum settles the open
              invoices of the whole group ({st.scope.partyCodes.join(", ")}), oldest first, whichever sister they belong to.
            </div>
          )}
          {st && st.advance > 0.001 && (
            <div className="rounded-md bg-sky-50 px-3 py-2 text-xs text-sky-800 ring-1 ring-sky-200">
              <b>{fmtMoney(st.advance)}</b> already on account for {st.party?.name || party} (unallocated advance).
            </div>
          )}
          <div className="flex gap-1 rounded-lg bg-slate-100 p-1 w-min">
            {[["invoice", "Against a specific invoice"], ["lumpsum", "Lump sum (auto-allocate oldest first)"]].map(([k, l]) => (
              <button key={k} disabled={k === "invoice" && !invoiceCode} onClick={() => setMode(k)}
                className={cx("whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium disabled:opacity-40",
                  mode === k ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700")}>{l}</button>
            ))}
          </div>
          {mode === "invoice" && chosen && (
            <div className="rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-600 ring-1 ring-slate-200">
              Paying <Mono className="font-semibold text-slate-800">{chosen.code}</Mono> — total {fmtMoney(chosen.total)}, already received {fmtMoney(chosen.received)}, <b>balance {fmtMoney(chosen.balance)}</b>.
              Any excess stays on account rather than moving to another invoice.
            </div>
          )}
          <div className="grid gap-3 sm:grid-cols-3">
            <label className="block text-sm"><span className="mb-1 block font-medium text-slate-600">Amount</span>
              <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" className={IN} autoFocus /></label>
            <label className="block text-sm"><span className="mb-1 block font-medium text-slate-600">Payment date</span>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={IN} /></label>
            <label className="block text-sm"><span className="mb-1 block font-medium text-slate-600">Method</span>
              <select value={method} onChange={(e) => setMethod(e.target.value)} className={IN}>{PAY_METHODS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-sm"><span className="mb-1 block font-medium text-slate-600">Reference</span>
              <input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Cheque no / UTR / txn ref" className={IN} /></label>
            <label className="block text-sm"><span className="mb-1 block font-medium text-slate-600">Notes</span>
              <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional" className={IN} /></label>
          </div>
          {amt > 0 && preview && (
            <div className="rounded-lg ring-1 ring-slate-200 overflow-x-auto">
              <div className="border-b border-slate-100 px-3 py-2 text-xs font-semibold text-slate-600">
                {mode === "lumpsum" ? "This will allocate oldest-first:" : "This will allocate:"}
              </div>
              {preview.allocations.length === 0
                ? <p className="px-3 py-2 text-xs text-slate-500">No open invoices — the whole amount goes on account as an advance.</p>
                : <table className="w-full"><thead className="bg-slate-50/60"><tr><Th>Invoice</Th>{isGroup && <Th>Billed to</Th>}<Th className="text-right">Invoice total</Th><Th className="text-right">Gets</Th><Th className="text-right">Balance after</Th></tr></thead>
                    <tbody className="divide-y divide-slate-50">
                      {preview.allocations.map((a) => (
                        <tr key={a.invoiceCode}>
                          <Td><Mono className="font-medium text-slate-700">{a.invoiceCode}</Mono><div className="text-xs text-slate-400">{fmtDate(a.invoiceDate)}</div></Td>
                          {isGroup && <Td className="text-xs"><span className="text-slate-600">{a.billTo?.name}</span><div className="text-slate-400"><Mono>{a.billTo?.code}</Mono></div></Td>}
                          <Td className="text-right"><Mono className="text-slate-500">{fmtMoney(a.invoiceTotal)}</Mono></Td>
                          <Td className="text-right"><Mono className="font-semibold text-emerald-700">{fmtMoney(a.amount)}</Mono></Td>
                          <Td className="text-right"><Mono className={a.balanceAfter <= 0.001 ? "text-emerald-600" : "text-amber-600"}>{fmtMoney(a.balanceAfter)}</Mono>{a.balanceAfter <= 0.001 && <span className="ml-1 text-[10px] text-emerald-600">paid</span>}</Td>
                        </tr>
                      ))}
                    </tbody></table>}
              <div className="flex flex-wrap justify-between gap-2 border-t border-slate-100 px-3 py-2 text-xs">
                <span className="text-slate-500">Allocated <b className="text-slate-700">{fmtMoney(preview.allocated)}</b> of {fmtMoney(amt)}</span>
                {preview.advance > 0.001 && <span className="text-sky-700">{fmtMoney(preview.advance)} will go <b>on account</b> as an advance</span>}
              </div>
            </div>
          )}
          <p className="text-xs text-slate-400">Allocation is computed on the server by the same logic that records it — this preview is exactly what will be saved.</p>
        </div>
      )}
    </Modal>
  );
}

// BULK IMPORT (Phase 6) — upload a bank/collection CSV, auto-map rows to parties,
// FIFO-record each. CSV is parsed CLIENT-SIDE to JSON rows and posted (no file
// storage). Two-step: preview (writes nothing) → confirm.
const IMPORT_HEADERS = ["party_code", "party_name", "amount", "payment_date", "method", "reference", "notes"];

// Minimal CSV parser — handles quoted fields, commas and quotes inside quotes,
// and CRLF. No external lib (none is warranted for a flat receipts sheet).
function parseCsv(text) {
  const rows = [];
  let row = [], field = "", q = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (q) {
      if (c === '"') { if (text[i + 1] === '"') { field += '"'; i++; } else q = false; }
      else field += c;
    } else if (c === '"') q = true;
    else if (c === ",") { row.push(field); field = ""; }
    else if (c === "\n") { row.push(field); rows.push(row); row = []; field = ""; }
    else if (c === "\r") { /* skip */ }
    else field += c;
  }
  if (field !== "" || row.length) { row.push(field); rows.push(row); }
  return rows.filter((r) => r.some((c) => c.trim() !== ""));
}

function csvToPaymentRows(text) {
  const grid = parseCsv(text);
  if (grid.length === 0) return { rows: [], error: "The file is empty." };
  const header = grid[0].map((h) => h.trim().toLowerCase());
  const idx = (name) => header.indexOf(name);
  if (idx("amount") < 0) return { rows: [], error: 'Missing an "amount" column. Download the template for the expected headers.' };
  const rows = grid.slice(1).map((cols) => {
    const get = (name) => { const i = idx(name); return i >= 0 ? (cols[i] ?? "").trim() : ""; };
    const amountRaw = get("amount").replace(/[,₹\s]/g, "");
    const r = { amount: Number(amountRaw) };
    const pc = get("party_code"), pn = get("party_name"), d = get("payment_date"), m = get("method"), ref = get("reference"), notes = get("notes");
    if (pc) r.partyCode = pc;
    if (pn) r.partyName = pn;
    if (d) r.paymentDate = d;
    if (m) r.method = m.toLowerCase();
    if (ref) r.reference = ref;
    if (notes) r.notes = notes;
    return r;
  });
  return { rows };
}

function ImportPaymentsModal({ open, onClose, onSaved }) {
  const [rows, setRows] = useState(null); // parsed rows
  const [fileName, setFileName] = useState("");
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const fileRef = React.useRef(null);

  React.useEffect(() => { if (open) { setRows(null); setFileName(""); setPreview(null); setResult(null); setErr(""); setBusy(false); } }, [open]);

  const downloadTemplate = () => {
    const sample = IMPORT_HEADERS.join(",") + "\n" +
      "P1,,50000,2026-07-16,bank,NEFT-12345,group payment\n" +
      "P4,,10000,2026-07-16,upi,UPI-998,\n" +
      ",Sweet Cravings Pvt Ltd,5000,,cheque,CHQ-77,by name if no code";
    const blob = new Blob([sample], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "payments-import-template.csv";
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const onFile = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    setErr(""); setPreview(null); setResult(null);
    const text = await f.text();
    const { rows: parsed, error } = csvToPaymentRows(text);
    if (error) { setErr(error); setRows(null); setFileName(""); return; }
    if (parsed.length === 0) { setErr("No data rows found under the header."); return; }
    setRows(parsed); setFileName(f.name);
    setBusy(true);
    try { setPreview(await paymentsApi.importPreview(parsed)); }
    catch (ex) { setErr(ex instanceof ApiError ? ex.message : "Could not preview the file."); }
    finally { setBusy(false); }
  };

  const commit = async () => {
    if (!rows || busy) return;
    setBusy(true); setErr("");
    try { const r = await paymentsApi.importCommit(rows); setResult(r); onSaved && onSaved(); }
    catch (ex) { setErr(ex instanceof ApiError ? ex.message : "Could not import the payments."); }
    finally { setBusy(false); }
  };

  const view = result || preview;
  const rr = view ? view.results : [];
  return (
    <Modal open={open} onClose={onClose} wide title="Import payments (CSV)"
      footer={result
        ? <Btn onClick={onClose}>Done</Btn>
        : <><Btn variant="soft" onClick={onClose}>Cancel</Btn>{preview && preview.summary.valid > 0 && <Btn onClick={commit} disabled={busy}>{busy ? "Importing…" : "Import " + preview.summary.valid + " payment(s)"}</Btn>}</>}>
      <div className="space-y-3">
        {err ? <InlineError message={err} /> : null}
        {!view && (
          <div className="rounded-lg border border-dashed border-slate-300 p-5 text-center">
            <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" />
            <p className="mb-2 text-sm text-slate-600">Upload a CSV of receipts. Each row is one payment, auto-mapped to a customer and recorded oldest-invoice-first.</p>
            <div className="flex items-center justify-center gap-2">
              <Btn onClick={() => fileRef.current && fileRef.current.click()} disabled={busy}><Plus size={14} /> {busy ? "Reading…" : "Choose CSV file"}</Btn>
              <Btn variant="ghost" onClick={downloadTemplate}><Download size={13} /> Download template</Btn>
            </div>
            <p className="mt-3 text-xs text-slate-400">Columns: <Mono>party_code</Mono> (or <Mono>party_name</Mono>), <Mono>amount</Mono>, optional <Mono>payment_date</Mono> / <Mono>method</Mono> / <Mono>reference</Mono> / <Mono>notes</Mono>. Match by code where you can — names must be an exact, unambiguous match.</p>
          </div>
        )}
        {view && (
          <>
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <span className="font-medium text-slate-700">{fileName}</span>
              {result
                ? <><Badge tone="emerald">{result.summary.recorded} recorded</Badge>{result.summary.failed > 0 && <Badge tone="rose">{result.summary.failed} failed</Badge>}<span className="text-slate-500">{fmtMoney(result.summary.totalAmount)} total</span></>
                : <><Badge tone="emerald">{preview.summary.valid} valid</Badge>{preview.summary.invalid > 0 && <Badge tone="amber">{preview.summary.invalid} to skip</Badge>}<span className="text-slate-500">{fmtMoney(preview.summary.totalAmount)} will post</span></>}
            </div>
            <div className="overflow-x-auto rounded-lg ring-1 ring-slate-200"><table className="w-full">
              <thead className="bg-slate-50/60"><tr><Th>#</Th><Th>Party</Th><Th className="text-right">Amount</Th><Th>Allocates to</Th><Th>{result ? "Payment" : "Status"}</Th></tr></thead>
              <tbody className="divide-y divide-slate-50">
                {rr.map((r) => (
                  <tr key={r.line} className={r.ok ? "" : "bg-rose-50/40"}>
                    <Td className="text-slate-400">{r.line}</Td>
                    <Td>{r.matchedParty ? <><span className="font-medium text-slate-700">{r.matchedParty.code}</span>{r.matchedParty.isGroup && <Badge tone="violet" className="ml-1">group</Badge>}</> : <span className="text-slate-400">{r.input.partyCode || r.input.partyName || "—"}</span>}</Td>
                    <Td className="text-right"><Mono>{fmtMoney(r.input.amount)}</Mono></Td>
                    <Td className="text-xs text-slate-500">{r.ok ? ((r.allocations || []).map((a) => a.invoiceCode + " " + fmtMoney(a.amount)).join(", ") || "—") + (r.advance > 0.001 ? " · " + fmtMoney(r.advance) + " on account" : "") : <span className="text-slate-300">—</span>}</Td>
                    <Td className="text-xs">{r.ok ? (result ? <Mono className="text-emerald-600">{r.paymentCode}</Mono> : <Badge tone="emerald">OK</Badge>) : <span className="text-rose-600">{r.error}</span>}</Td>
                  </tr>
                ))}
              </tbody>
            </table></div>
            {!result && preview.summary.invalid > 0 && <p className="text-xs text-amber-600">{preview.summary.invalid} row(s) will be skipped (see the errors). Only the valid rows are imported.</p>}
          </>
        )}
      </div>
    </Modal>
  );
}

function InvoicesView({ auth, role }) {
  const { data, loading, error, reload } = useResource(() => invoicesApi.listInvoices({ limit: 200 }), []);
  const rows = data?.invoices || [];
  const [exp, setExp] = useState(null);
  const [tab, setTab] = useState("all"); // all | pending | complete
  const [pay, setPay] = useState(null); // { partyCode, invoiceCode? }
  const [imp, setImp] = useState(false);
  // Recording receipts is the accounts job — same capability that raises the invoice.
  const canPay = authCan(auth, role, "invoice");
  const openInv = async (code, print) => {
    try { const iv = (await invoicesApi.getInvoice(code)).invoice; openDoc("Invoice " + code, invoiceDocLive(iv, auth?.company?.name), print); }
    catch (e) { alert(e instanceof ApiError ? e.message : "Could not load the invoice."); }
  };
  // Three states, all DERIVED from allocations (no stored status): Complete when
  // nothing is outstanding, Partial when some money has landed, else Pending.
  const stateOf = (inv) => (inv.balance <= 0.001 ? "Complete" : inv.received > 0.001 ? "Partial" : "Pending");
  // The Pending TAB means "not fully paid" — so it includes Partial, per the spec.
  const shown = rows.filter((i) => (tab === "all" ? true : tab === "complete" ? stateOf(i) === "Complete" : stateOf(i) !== "Complete"));
  const count = (k) => rows.filter((i) => (k === "all" ? true : k === "complete" ? stateOf(i) === "Complete" : stateOf(i) !== "Complete")).length;
  const TABS = [["all", "All"], ["pending", "Pending"], ["complete", "Complete"]];
  const tone = (st) => (st === "Complete" ? "emerald" : st === "Partial" ? "amber" : "stone");
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-1 rounded-lg bg-slate-100 p-1">
          {TABS.map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} className={cx("flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium", tab === k ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700")}>
              {l}<span className={cx("rounded-full px-1.5 text-xs", tab === k ? "bg-slate-100 text-slate-600" : "bg-slate-200/70 text-slate-500")}>{count(k)}</span>
            </button>
          ))}
        </div>
        {canPay && <Btn variant="soft" onClick={() => setImp(true)}><Download size={14} /> Import CSV</Btn>}
        {canPay && <Btn onClick={() => setPay({ partyCode: "" })}><Plus size={15} /> Record payment</Btn>}
      </div>
      {error ? <InlineError message={error} onRetry={reload} /> : null}
      {loading && rows.length === 0 ? <LoadingBlock label="Loading invoices…" /> : (
        <Card className="overflow-x-auto"><table className="w-full">
          <thead className="border-b border-slate-100"><tr><Th>Invoice</Th><Th>Bill to</Th><Th>Date</Th><Th className="text-right">Total</Th><Th className="text-right">Received</Th><Th className="text-right">Balance</Th><Th>Status</Th><Th className="text-right">Actions</Th></tr></thead>
          <tbody className="divide-y divide-slate-50">
            {shown.map((inv) => { const st = stateOf(inv); return (
              <React.Fragment key={inv.code}>
                <tr className="cursor-pointer hover:bg-slate-50/70" onClick={() => setExp(exp === inv.code ? null : inv.code)}>
                  <Td><Mono className="font-medium text-slate-800">{inv.code}</Mono><div className="text-xs text-slate-400"><Mono>{inv.deliveryCode}</Mono></div></Td>
                  <Td><div className="font-medium text-slate-700">{inv.billTo.name}</div><Mono className="text-xs text-slate-400">{inv.billTo.code}</Mono></Td>
                  <Td className="text-slate-500">{fmtDate(inv.invoiceDate)}</Td>
                  <Td className="text-right"><Mono className="font-semibold text-slate-800">{fmtMoney(inv.total)}</Mono></Td>
                  <Td className="text-right"><Mono className={inv.received > 0.001 ? "text-emerald-600" : "text-slate-300"}>{fmtMoney(inv.received)}</Mono></Td>
                  <Td className="text-right"><Mono className={inv.balance <= 0.001 ? "text-slate-300" : "text-amber-700 font-semibold"}>{fmtMoney(inv.balance)}</Mono></Td>
                  <Td><Badge tone={tone(st)}>{st}</Badge></Td>
                  <Td className="text-right"><div className="flex justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
                    {canPay && inv.balance > 0.001 && <Btn variant="soft" onClick={() => setPay({ partyCode: inv.billTo.code, invoiceCode: inv.code })}><FileText size={13} /> Pay</Btn>}
                    <Btn variant="ghost" onClick={() => openInv(inv.code, false)}><Download size={13} /> PDF</Btn>
                    <Btn variant="ghost" onClick={() => openInv(inv.code, true)}><Printer size={13} /> Print</Btn>
                  </div></Td>
                </tr>
                {exp === inv.code && <tr><td colSpan={8} className="bg-slate-50/60 px-6 py-2"><InvoiceLines code={inv.code} /></td></tr>}
              </React.Fragment>
            ); })}
            {shown.length === 0 && <tr><Td className="text-slate-400" colSpan={8}>{rows.length === 0 ? "No invoices yet — generate one from a delivery challan (Deliveries → Invoice)." : tab === "complete" ? "No fully-paid invoices yet." : "Nothing outstanding — every invoice is fully paid."}</Td></tr>}
          </tbody>
        </table></Card>
      )}
      <ImportPaymentsModal open={imp} onClose={() => setImp(false)} onSaved={reload} />
      <RecordPaymentModal key={(pay && pay.partyCode + (pay.invoiceCode || "")) || "nopay"} open={!!pay}
        partyCode={pay && pay.partyCode} invoiceCode={pay && pay.invoiceCode}
        onClose={() => setPay(null)} onSaved={reload} />
    </div>
  );
}

function InvoiceLines({ code }) {
  const { data, loading, error, reload } = useResource(() => invoicesApi.getInvoice(code), [code]);
  const iv = data?.invoice;
  if (loading && !iv) return <LoadingBlock label="Loading lines…" />;
  if (error) return <InlineError message={error} onRetry={reload} />;
  if (!iv) return null;
  const t = iv.totals || {};
  const inter = iv.supplyType === "inter";
  return (
    <div className="py-1">
      <div className="mb-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
        <Badge tone={inter ? "amber" : "indigo"}>{inter ? "Inter-state · IGST" : (iv.supplyType === "unregistered" ? "B2C · CGST+SGST" : "Intra-state · CGST+SGST")}</Badge>
        {iv.placeOfSupply && <span>Place of supply: <span className="font-medium text-slate-600">{iv.placeOfSupply.name} ({iv.placeOfSupply.code})</span></span>}
        <span>· Bill-to GSTIN: <Mono>{iv.billTo.registered ? (iv.billTo.gstin || "—") : "Unregistered"}</Mono></span>
      </div>
      <div className="overflow-x-auto"><table className="w-full text-sm">
        <thead className="text-xs text-slate-400"><tr><th className="py-1 text-left">Item</th><th className="text-left">HSN</th><th className="text-right">Qty</th><th className="text-right">Rate</th><th className="text-right">Taxable</th><th className="text-right">{inter ? "IGST" : "CGST"}</th>{!inter && <th className="text-right">SGST</th>}<th className="text-right">Total</th></tr></thead>
        <tbody>
          {iv.lines.map((l, k) => (
            <tr key={k} className="text-slate-600">
              <td className="py-0.5">{l.item.name} <span className="text-xs text-slate-400">· {l.gstRate}%</span></td>
              <td><Mono className="text-xs text-slate-500">{l.hsnCode || "—"}</Mono></td>
              <td className="text-right"><Mono>{fmtQty(l.qty)}</Mono></td>
              <td className="text-right"><Mono>{fmtMoney(l.unitPrice)}</Mono></td>
              <td className="text-right"><Mono>{fmtMoney(l.taxableValue)}</Mono></td>
              <td className="text-right"><Mono>{fmtMoney(inter ? l.igst : l.cgst)}</Mono></td>
              {!inter && <td className="text-right"><Mono>{fmtMoney(l.sgst)}</Mono></td>}
              <td className="text-right"><Mono className="text-slate-700">{fmtMoney(l.lineTotal)}</Mono></td>
            </tr>
          ))}
        </tbody>
      </table></div>
      <div className="mt-1 flex justify-end gap-4 text-xs text-slate-500">
        <span>Taxable <Mono>{fmtMoney(t.totalTaxableValue)}</Mono></span>
        {inter ? <span>IGST <Mono>{fmtMoney(t.totalIgst)}</Mono></span> : <><span>CGST <Mono>{fmtMoney(t.totalCgst)}</Mono></span><span>SGST <Mono>{fmtMoney(t.totalSgst)}</Mono></span></>}
        <span>Round-off <Mono>{fmtMoney(t.roundOff)}</Mono></span>
        <span className="font-semibold text-slate-700">Grand Total <Mono>₹{fmtMoney(t.grandTotal)}</Mono></span>
      </div>
    </div>
  );
}

/* ============================ Routing / Process & TAT ============================ */
// REAL per-room default-responsible panel (replaces the old mock operator pool,
// which lived in the in-memory reducer with fake user ids and vanished on refresh).
// Rooms are derived server-side DISTINCT from routing_steps.work_center, so this
// always lists the rooms actually in use. Setting a default persists per tenant and
// PREFILLS the responsible person when work in that room is sent to production.
function WorkCentersPanel({ auth, role }) {
  const res = useResource(() => workCenterDefaultsApi.list(), []);
  const usersRes = useResource(() => tasksApi.listAssignees(), []);
  const rooms = (res.data && res.data.workCenters) || [];
  const people = (usersRes.data && usersRes.data.users) || [];
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState("");
  // Same roles the write gate allows: owner ("*"), planner (routing nav), prodmgr
  // (stockPO), supervisor (monitor).
  const canEdit = !!(authCan(auth, role, "*") || authCan(auth, role, "stockPO") || authCan(auth, role, "monitor")
    || (auth && auth.role && (auth.role.navModules || []).some((m) => m === "routing" || m === "settings")));
  const setDefault = async (workCenter, defaultUserId) => {
    setBusy(workCenter); setErr("");
    try { await workCenterDefaultsApi.set({ workCenter, defaultUserId: defaultUserId || null }); await res.reload(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : "Could not save the default."); }
    finally { setBusy(null); }
  };
  return (
    <Card>
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-3">
        <Users size={16} className="text-amber-600" /><span className="text-sm font-semibold text-slate-700">Rooms &amp; default responsible person</span>
        <span className="text-xs text-slate-400">set once — auto-filled when work in that room is sent to production</span>
      </div>
      {err ? <div className="px-4 pt-2"><InlineError message={err} /></div> : null}
      {res.loading && rooms.length === 0 ? <LoadingBlock label="Loading rooms…" /> : res.error ? <div className="p-4"><InlineError message={res.error} onRetry={res.reload} /></div> : rooms.length === 0 ? (
        <p className="px-4 py-6 text-sm text-slate-400">No rooms yet — a room appears here once it is used as a step in an item's routing (above).</p>
      ) : (
        <div className="divide-y divide-slate-50">
          {rooms.map((w) => (
            <div key={w.workCenter} className="flex flex-wrap items-center gap-3 px-4 py-3">
              <div className="w-48 shrink-0 text-sm font-medium text-slate-700">{w.workCenter}</div>
              <div className="flex flex-1 items-center gap-2">
                {canEdit ? (
                  <select value={w.defaultUser ? w.defaultUser.id : ""} disabled={busy === w.workCenter}
                    onChange={(e) => setDefault(w.workCenter, e.target.value)}
                    className="rounded-md bg-white py-1.5 px-2 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400">
                    <option value="">No default — pick manually each time</option>
                    {people.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                ) : w.defaultUser ? <span className="text-sm text-slate-600">{w.defaultUser.name}</span> : <span className="text-xs text-slate-400">No default set</span>}
                {busy === w.workCenter && <Spinner size={13} />}
              </div>
              {w.defaultUser ? <Badge tone="emerald">Auto-fills</Badge> : <Badge tone="stone">Manual</Badge>}
            </div>
          ))}
        </div>
      )}
      <p className="px-4 py-2.5 text-xs text-slate-400">The <b>default responsible person</b> for a room is pre-selected when a sales-order line whose starting room is that room is sent to production — you can still change it per line. A room with no default simply needs a manual pick (the responsible person is always required).</p>
    </Card>
  );
}

function RoutingView({ st, dispatch, role, auth }) {
  // Production Master lists EVERY active routable item (FG+SFG) with its routing
  // state — routing-less items included — so a FIRST routing can be created here.
  const list = useResource(() => catalog.listRoutableItems(), []);
  const items = list.data?.items || [];
  const [openCode, setOpenCode] = useState(null);
  React.useEffect(() => {
    // Default to the first item that already HAS a routing (a populated view), else
    // the first item overall (a routing-less one — its empty editor invites a first routing).
    if (!openCode && items.length) setOpenCode((items.find((i) => i.hasRouting) || items[0]).itemCode);
  }, [items, openCode]);

  const selected = items.find((i) => i.itemCode === openCode) || null;
  const hasRouting = !!selected?.hasRouting;

  // Only fetch steps for items that HAVE a routing; routing-less items open an empty editor.
  const detail = useResource(
    () => (openCode && hasRouting ? catalog.getRouting(openCode) : Promise.resolve(null)),
    [openCode, hasRouting],
  );
  // Editable working copy of the steps.
  const [steps, setSteps] = useState([]);
  const [q, setQ] = useState(100);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState("");
  React.useEffect(() => {
    const d = detail.data;
    if (d && d.item && d.item.code === openCode) {
      // Fresh detail for the selected item — hydrate.
      setSteps((d.steps || []).map((s) => ({
        workCenter: s.workCenter,
        setupTime: Number(s.setupTime) || 0,
        perUnitTime: Number(s.perUnitTime) || 0,
      })));
      setSaveErr("");
    } else if (selected && !hasRouting) {
      // Routing-less selection — start with a blank routing.
      setSteps([]);
      setSaveErr("");
    }
    // else: awaiting fresh detail for a has-routing item — keep prior steps (avoids
    // rendering the previous item's steps under the new selection).
  }, [openCode, detail.data, selected, hasRouting]);

  const it = selected ? { name: selected.itemName, code: selected.itemCode } : null;
  const totalTat = steps.reduce((a, s) => a + (Number(s.setupTime) || 0) + (Number(s.perUnitTime) || 0) * q, 0);
  const setStep = (idx, key, value) => setSteps((arr) => arr.map((s, j) => (j === idx ? { ...s, [key]: value } : s)));
  const moveStep = (idx, dir) => setSteps((arr) => { const j = idx + dir; if (j < 0 || j >= arr.length) return arr; const c = [...arr]; [c[idx], c[j]] = [c[j], c[idx]]; return c; });
  const delStep = (idx) => setSteps((arr) => arr.filter((_, j) => j !== idx));
  const addStep = () => setSteps((arr) => [...arr, { workCenter: WORKCENTERS[0], setupTime: 1, perUnitTime: 0.01 }]);

  const save = async () => {
    if (!openCode || saving || steps.length === 0) return;
    setSaving(true);
    setSaveErr("");
    const payloadSteps = steps.map((s) => ({
      workCenter: s.workCenter,
      setupTime: Number(s.setupTime) || 0,
      perUnitTime: Number(s.perUnitTime) || 0,
    }));
    try {
      // Create-if-absent, update-if-present: a routing-less item's first save POSTs
      // a new routing; thereafter it's a wholesale PUT replace.
      if (hasRouting) {
        await catalog.updateRouting(openCode, { steps: payloadSteps });
      } else {
        await catalog.createRouting({ itemCode: openCode, steps: payloadSteps });
      }
      await list.reload(); // flips the item to "N steps"; hasRouting change refetches detail
      await detail.reload();
    } catch (e) {
      setSaveErr(e instanceof ApiError ? e.message : "Could not save the routing.");
    } finally {
      setSaving(false);
    }
  };

  const needsRouting = items.filter((i) => !i.hasRouting).length;

  return (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-3">
      <Card className="p-2 lg:col-span-1">
        {needsRouting > 0 && (
          <p className="px-3 py-1.5 text-xs text-slate-400"><b className="text-amber-700">{needsRouting}</b> item(s) need a routing before they can be produced.</p>
        )}
        {list.loading && items.length === 0 ? <LoadingBlock label="Loading items…" /> : null}
        {list.error ? <InlineError message={list.error} onRetry={list.reload} /> : null}
        {items.map((r) => (
          <button key={r.itemCode} onClick={() => setOpenCode(r.itemCode)} className={cx("flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2.5 text-left text-sm", openCode === r.itemCode ? "bg-amber-50 text-amber-900 ring-1 ring-amber-600/15" : "text-slate-600 hover:bg-slate-50")}>
            <span className="min-w-0"><span className="block truncate font-medium">{r.itemName}</span><Mono className="text-xs text-slate-400">{r.itemCode} · {r.itemType}</Mono></span>
            {r.hasRouting
              ? <span className="shrink-0 text-xs text-slate-400">{r.stepCount} steps</span>
              : <span className="shrink-0"><Badge tone="amber">Needs routing</Badge></span>}
          </button>
        ))}
        {!list.loading && items.length === 0 && !list.error && <p className="px-3 py-2 text-sm text-slate-400">No routable (FG/SFG) items yet.</p>}
      </Card>
      <Card className="p-4 lg:col-span-2">
        {detail.loading && hasRouting ? <LoadingBlock label="Loading routing…" /> : detail.error ? (
          <InlineError message={detail.error} onRetry={detail.reload} />
        ) : it ? (
          <>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-3">
              <div>
                <div className="flex items-center gap-2"><span className="font-semibold text-slate-800">{it.name}</span>{!hasRouting && <Badge tone="amber">New routing</Badge>}</div>
                <div className="text-xs text-slate-400"><Mono>{it.code}</Mono> · {selected?.itemType} · production route &amp; TAT</div>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-500">TAT for <input type="number" value={q} onChange={(e) => setQ(Number(e.target.value) || 0)} className="mono w-16 rounded-md bg-white py-1 px-2 ring-1 ring-slate-200" /> Kg → <Mono className="text-base font-semibold text-amber-700">{totalTat.toFixed(1)}h</Mono></div>
            </div>
            <div className="space-y-2">
              {steps.map((s, idx) => (
                <div key={idx} className="grid grid-cols-12 items-center gap-2 rounded-lg px-2 py-1.5 ring-1 ring-slate-200">
                  <span className="col-span-1 mono text-center text-xs text-slate-400">{idx + 1}</span>
                  <select className="col-span-4 rounded-md bg-white py-1.5 px-2 text-sm ring-1 ring-slate-200" value={s.workCenter} onChange={(e) => setStep(idx, "workCenter", e.target.value)}>
                    {[...new Set([...WORKCENTERS, s.workCenter])].filter(Boolean).map((w) => <option key={w}>{w}</option>)}
                  </select>
                  <label className="col-span-3 flex items-center gap-1 text-xs text-slate-500">setup<input type="number" step="0.5" value={s.setupTime} onChange={(e) => setStep(idx, "setupTime", e.target.value)} className="mono w-full rounded-md bg-white py-1 px-2 ring-1 ring-slate-200" />h</label>
                  <label className="col-span-2 flex items-center gap-1 text-xs text-slate-500">/Kg<input type="number" step="0.005" value={s.perUnitTime} onChange={(e) => setStep(idx, "perUnitTime", e.target.value)} className="mono w-full rounded-md bg-white py-1 px-2 ring-1 ring-slate-200" />h</label>
                  <div className="col-span-2 flex items-center justify-end gap-1 text-slate-400">
                    <button onClick={() => moveStep(idx, -1)} className="hover:text-slate-700"><ArrowUp size={14} /></button>
                    <button onClick={() => moveStep(idx, 1)} className="hover:text-slate-700"><ArrowDown size={14} /></button>
                    <button onClick={() => delStep(idx)} className="hover:text-rose-500"><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
              {steps.length === 0 && <p className="px-2 py-2 text-sm text-slate-400">{hasRouting ? "No steps — add one below." : "This item has no routing yet — add its production steps, then Save routing to make it producible."}</p>}
            </div>
            <div className="mt-2 flex items-center justify-between">
              <button className="text-sm font-medium text-amber-700 hover:underline" onClick={addStep}>+ Add step</button>
              <Btn onClick={save} disabled={saving || steps.length === 0}>{saving ? "Saving…" : hasRouting ? "Save routing" : "Create routing"}</Btn>
            </div>
            {saveErr ? <InlineError message={saveErr} /> : null}
            <p className="mt-3 text-xs text-slate-400">Qty-wise TAT: step TAT = setup + (per-Kg × qty). Edits are local until you press <b>{hasRouting ? "Save routing" : "Create routing"}</b> — the first save of a routing-less item creates its routing (POST /routings); later edits replace the whole step list (PUT /routings). An item needs a routing before it can go to production.</p>
          </>
        ) : (
          <p className="text-sm text-slate-400">Select an item to view or create its routing.</p>
        )}
      </Card>
      </div>
      <WorkCentersPanel auth={auth} role={role} />
    </div>
  );
}

/* ============================ Purchases (procurement + QC) — LIVE (W5) ============================
   List/create/QC/cancel from /purchases. QC pass adds qty to stock.available and
   mints a real RM/PM batch (visible in the now-real Inventory + Batches screens).
   Gated by the REAL session: create/cancel→stockPO, QC→fgqc. */

// Searchable item picker. Searching SERVER-SIDE (the /items `q` filter, ilike on
// name OR code) with a small limit is the only way every item stays reachable:
// /items caps limit at 200, so any load-everything dropdown silently truncates a
// tenant with more items than that — the picked item simply isn't in the list,
// with no error. Typing narrows on the server instead, so tenant size is irrelevant.
function ItemPicker({ value, onPick, onClear, typeFilter }) {
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [open, setOpen] = useState(false);
  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 250); // debounce keystrokes
    return () => clearTimeout(t);
  }, [q]);
  const res = useResource(
    () => catalog.listItems({ q: debounced || undefined, type: typeFilter || undefined, limit: 50 }),
    [debounced, typeFilter],
  );
  const found = (res.data && res.data.items) || [];
  const total = (res.data && res.data.total) || 0;
  // RM/PM first (a purchase is normally raw/packing material) — the old dropdown's
  // sort, kept. Any type stays purchasable; this biases, it does not restrict.
  const list = [...found].sort((a, b) => { const r = (t) => (t === "RM" || t === "PM" ? 0 : 1); return r(a.type) - r(b.type) || a.code.localeCompare(b.code); });
  if (value) {
    return (
      <div className="flex flex-1 items-center gap-2 rounded-md bg-slate-50 py-1.5 px-2.5 text-sm ring-1 ring-slate-200">
        <span className="min-w-0 flex-1 truncate font-medium text-slate-700">{value.name} <Mono className="text-xs text-slate-400">{value.code}</Mono></span>
        <Badge tone={value.type === "PM" ? "sky" : value.type === "RM" ? "stone" : "violet"}>{value.type}</Badge>
        <button onClick={onClear} className="text-slate-400 hover:text-rose-500" title="Change item"><X size={14} /></button>
      </div>
    );
  }
  return (
    <div className="relative flex-1">
      <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
      <input
        value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Search item by name or code…"
        className="w-full rounded-md bg-white py-1.5 pl-8 pr-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400"
      />
      {open && (
        <div className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-slate-200">
          {res.error ? <div className="px-3 py-2"><InlineError message={res.error} onRetry={res.reload} /></div>
            : res.loading && found.length === 0 ? <div className="px-3 py-2 text-xs text-slate-400">Searching…</div>
            : list.length === 0 ? <div className="px-3 py-2 text-xs text-slate-500">No matching items — add RM/PM items in <b>Items &amp; BOM</b> first.</div>
            : <>
                {list.map((it) => (
                  <button key={it.code} onMouseDown={(e) => e.preventDefault()} onClick={() => { onPick(it); setQ(""); setOpen(false); }}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-indigo-50">
                    <span className="min-w-0 flex-1 truncate text-slate-700">{it.name} <Mono className="text-xs text-slate-400">{it.code}</Mono></span>
                    <Badge tone={it.type === "PM" ? "sky" : it.type === "RM" ? "stone" : "violet"}>{it.type}</Badge>
                  </button>
                ))}
                {total > list.length && <div className="px-3 py-1.5 text-xs text-slate-400">Showing {list.length} of {total} — type to narrow.</div>}
              </>}
        </div>
      )}
    </div>
  );
}

function CreatePurchaseModal({ open, onClose, onCreated, priced }) {
  const [supplier, setSupplier] = useState("");
  const [rows, setRows] = useState([{ item: null, qty: "", rate: "" }]);
  const [busy, setBusy] = useState(false); const [err, setErr] = useState("");
  React.useEffect(() => { if (open) { setRows([{ item: null, qty: "", rate: "" }]); setSupplier(""); setErr(""); setBusy(false); } }, [open]);
  const setRow = (i, patch) => setRows((r) => r.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  // PRICED PURCHASE (P0): show paise. Only active when the tenant flag is on.
  const money2 = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const rowAmount = (r) => (r.item && Number(r.qty) > 0 && r.rate !== "" && Number(r.rate) >= 0 ? Number(r.qty) * Number(r.rate) : null);
  // A line counts only with an item AND a positive qty — the same rule the server
  // enforces (lines.min(1), qty positive), so the button can never submit a body
  // the API would reject.
  const validLines = rows.filter((r) => r.item && Number(r.qty) > 0);
  const poTotal = priced ? validLines.reduce((s, r) => s + (rowAmount(r) ?? 0), 0) : 0;
  const canCreate = validLines.length > 0 && !busy;
  const create = async () => {
    if (!canCreate) return;
    setBusy(true); setErr("");
    try {
      // Include rate ONLY when the priced flag is on and a non-negative rate was entered; otherwise
      // the body is exactly today's (itemCode + qty) so a flag-off create is byte-identical.
      await purchasesApi.createPurchase({ supplier: supplier.trim() || undefined, lines: validLines.map((r) => ({ itemCode: r.item.code, qty: Number(r.qty), ...(priced && r.rate !== "" && Number(r.rate) >= 0 ? { rate: Number(r.rate) } : {}) })) });
      onClose(); onCreated && onCreated();
    } catch (e) { setErr(e instanceof ApiError ? e.message : "Could not create the purchase."); setBusy(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title="New purchase (raw material)" wide
      footer={<><Btn variant="soft" onClick={onClose}>Cancel</Btn><Btn onClick={create} disabled={!canCreate}>{busy ? "Creating…" : "Create purchase"}</Btn></>}>
      <div className="space-y-3">
        {err ? <InlineError message={err} /> : null}
        <label className="block text-sm"><span className="mb-1 block font-medium text-slate-600">Supplier</span><input value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder="Supplier name (optional — defaults to 'Supplier')" className="w-full rounded-md bg-white py-1.5 px-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400" /></label>
        <div className="space-y-2">{rows.map((r, i) => (
          <div key={i} className="flex items-center gap-2">
            <ItemPicker value={r.item} onPick={(it) => setRow(i, priced ? { item: it, rate: it.lastPurchasePrice != null ? String(it.lastPurchasePrice) : "" } : { item: it })} onClear={() => setRow(i, { item: null })} />
            <input type="number" value={r.qty} onChange={(e) => setRow(i, { qty: e.target.value })} placeholder="Qty" className="w-28 rounded-md bg-white py-1.5 px-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400" />
            {priced && <input type="number" min="0" step="0.01" value={r.rate} onChange={(e) => setRow(i, { rate: e.target.value })} placeholder="Rate" title="Unit rate — pre-fills from the item's last purchase price; editable" className="w-24 rounded-md bg-white py-1.5 px-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400" />}
            {priced && <span className="w-24 shrink-0 text-right text-sm tabular-nums text-slate-500">{rowAmount(r) != null ? money2(rowAmount(r)) : "—"}</span>}
            {rows.length > 1 && <button onClick={() => setRows((x) => x.filter((_, j) => j !== i))} className="text-slate-400 hover:text-rose-500"><X size={15} /></button>}
          </div>
        ))}</div>
        <div className="flex items-center gap-3">
          <Btn variant="soft" onClick={() => setRows((r) => [...r, { item: null, qty: "", rate: "" }])}><Plus size={13} /> Add item</Btn>
          {priced && validLines.length > 0 && <span className="ml-auto text-sm text-slate-600">PO total: <b className="tabular-nums text-slate-800">{money2(poTotal)}</b></span>}
        </div>
        {validLines.length === 0 && <p className="text-xs text-amber-600">Pick an item and enter a quantity above to create this purchase.</p>}
        <p className="text-xs text-slate-400">On create the status is <b>QC Pending</b>. On QC <b>pass</b> the quantity is added to stock (Available) and a real RM/PM batch is created (visible in Batches); on reject nothing is added.</p>
      </div>
    </Modal>
  );
}

function PurchasesView({ auth, role }) {
  const { data, loading, error, reload } = useResource(() => purchasesApi.listPurchases(), []);
  const rows = data?.purchases || [];
  const [open, setOpen] = useState(false);
  const [exp, setExp] = useState(null);
  const [busyCode, setBusyCode] = useState(null);
  const [actErr, setActErr] = useState("");
  // GRN + PARTIAL QC (P1): when on, QC opens a per-line GRN/pass/fail modal instead of the boolean.
  const grn = auth?.company?.settings?.grnQc === true;
  const [qcCode, setQcCode] = useState(null);
  const pcan = (k) => authCan(auth, role, k);
  // "buy" (not stockPO): stockPO also creates/assigns/force-completes PRODUCTION
  // orders, so gating purchases on it handed the floor to whoever could procure.
  const canBuy = pcan("buy");
  const canQc = pcan("fgqc");
  const tone = (x) => (x === "Passed" ? "emerald" : x === "Rejected" ? "rose" : "amber");
  const act = async (code, fn) => {
    setBusyCode(code); setActErr("");
    try { await fn(); await reload(); }
    catch (e) { setActErr((e instanceof ApiError ? e.message : "Action failed.") + " (" + code + ")"); }
    finally { setBusyCode(null); }
  };
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-slate-400">RM/PM purchase → <b>QC</b> → on pass, added to stock (Available) + a real batch is created.</p>
        {canBuy && <Btn onClick={() => setOpen(true)}><Plus size={15} /> New purchase</Btn>}
      </div>
      {actErr ? <InlineError message={actErr} /> : null}
      {error ? <InlineError message={error} onRetry={reload} /> : null}
      {loading && rows.length === 0 ? <LoadingBlock label="Loading purchases…" /> : rows.length === 0 ? <Card className="p-8 text-center text-sm text-slate-400">No purchases yet.</Card> : (
        <Card className="overflow-x-auto"><table className="w-full">
          <thead className="border-b border-slate-100"><tr><Th>Purchase</Th><Th>Supplier</Th><Th>Date</Th><Th>Status</Th><Th className="text-right">Items</Th><Th className="text-right">Total qty</Th>{grn && <><Th className="text-right">PO amt</Th><Th className="text-right">Invoice</Th><Th className="text-right">Diff</Th><Th>Stage</Th></>}<Th>Action</Th></tr></thead>
          <tbody className="divide-y divide-slate-50">
            {rows.map((pu) => { const totQty = (pu.lines || []).reduce((a, l) => a + l.qty, 0); return (
              <React.Fragment key={pu.code}>
                <tr className="cursor-pointer hover:bg-slate-50/50" onClick={() => setExp(exp === pu.code ? null : pu.code)}>
                  <Td><Mono className="font-semibold text-slate-800">{pu.code}</Mono></Td>
                  <Td className="text-slate-700">{pu.supplier}</Td>
                  <Td className="text-slate-500">{fmtDate(pu.purchaseDate)}</Td>
                  <Td><Badge tone={tone(pu.status)}>{pu.status}</Badge></Td>
                  <Td className="text-right text-slate-500">{pu.lineCount}</Td>
                  <Td className="text-right"><Mono>{fmtQty(totQty)}</Mono></Td>
                  {grn && <>
                    <Td className="text-right text-slate-600">{pu.totalAmount != null ? fmtMoney(pu.totalAmount) : <span className="text-slate-300">—</span>}</Td>
                    <Td className="text-right text-slate-600">{pu.invoiceAmount != null ? fmtMoney(pu.invoiceAmount) : <span className="text-slate-300">—</span>}</Td>
                    <Td className="text-right">{pu.priceDiff != null ? <span className={cx("tabular-nums", Math.abs(pu.priceDiff) > 0.009 ? "text-amber-700" : "text-slate-500")}>{pu.priceDiff >= 0 ? "+" : ""}{fmtMoney(pu.priceDiff)}{pu.priceDiffPct != null ? ` (${pu.priceDiffPct >= 0 ? "+" : ""}${pu.priceDiffPct}%)` : ""}</span> : <span className="text-slate-300">—</span>}</Td>
                    <Td>{pu.purchaseCompletedAt ? <Badge tone="emerald"><CheckCircle2 size={11} /> Complete</Badge> : pu.invoiceVerification === "verified" ? <Badge tone="sky">To accounts</Badge> : pu.invoiceVerification === "returned" ? <Badge tone="rose">Returned</Badge> : <span className="text-xs text-slate-300">—</span>}</Td>
                  </>}
                  <Td><div className="flex flex-wrap gap-1.5" onClick={(e) => e.stopPropagation()}>
                    <Btn variant="soft" onClick={() => window.open(purchasesApi.purchasePdfUrl(pu.code), "_blank", "noopener")}><FileText size={12} /> PDF</Btn>
                    {pu.status === "QC Pending" ? <>
                      {canQc && (grn
                        ? <Btn variant="soft" disabled={busyCode === pu.code} onClick={() => setQcCode(pu.code)}><ClipboardList size={12} /> GRN / QC</Btn>
                        : <><Btn variant="soft" disabled={busyCode === pu.code} onClick={() => act(pu.code, () => purchasesApi.qcPurchase(pu.code, false))}><X size={12} /> Reject</Btn><Btn variant="soft" disabled={busyCode === pu.code} onClick={() => act(pu.code, () => purchasesApi.qcPurchase(pu.code, true))}><CheckCircle2 size={12} /> QC pass</Btn></>)}
                      {canBuy && <Btn variant="ghost" disabled={busyCode === pu.code} onClick={() => act(pu.code, () => purchasesApi.cancelPurchase(pu.code))}>Cancel</Btn>}
                    </> : grn && pu.invoiceVerification === "verified" && !pu.purchaseCompletedAt
                      ? <Btn variant="soft" disabled={busyCode === pu.code} onClick={() => act(pu.code, () => purchasesApi.completePurchase(pu.code))}><CheckCircle2 size={12} /> Mark complete</Btn>
                      : <span className="text-xs text-slate-300">—</span>}
                  </div></Td>
                </tr>
                {exp === pu.code && <tr><td colSpan={grn ? 11 : 7} className="bg-slate-50/60 px-6 py-2"><PurchaseLines code={pu.code} grn={grn} /></td></tr>}
              </React.Fragment>
            ); })}
          </tbody>
        </table></Card>
      )}
      <CreatePurchaseModal open={open} onClose={() => setOpen(false)} onCreated={reload} priced={auth?.company?.settings?.pricedPurchase === true} />
      <PurchaseGrnQcModal code={qcCode} open={!!qcCode} onClose={() => setQcCode(null)} onDone={() => { setQcCode(null); reload(); }} />
    </div>
  );
}

// GRN + PARTIAL QC (gated by settings.grnQc). Per line: received (GRN, may differ from ordered) →
// split into passed (→ stock + batch/sticker) and failed (→ reject + reason). Enforces
// passed+failed ≤ received before submit.
function PurchaseGrnQcModal({ code, open, onClose, onDone }) {
  const { data, loading, error } = useResource(() => (code ? purchasesApi.getPurchase(code) : Promise.resolve(null)), [code]);
  const p = data?.purchase;
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false); const [err, setErr] = useState("");
  // GRN INVOICE (Step 8): optional invoice metadata + a fail-soft file.
  const [invNo, setInvNo] = useState(""); const [invDate, setInvDate] = useState(""); const [invAmount, setInvAmount] = useState("");
  const [file, setFile] = useState(null); // { base64, name, mime }
  const [fileBusy, setFileBusy] = useState(false); // compressing/reading indicator
  const fileRef = React.useRef(null);
  const cameraRef = React.useRef(null); // second input with capture="environment" — opens the rear camera on mobile
  const [note, setNote] = useState(""); const [done, setDone] = useState(false);
  React.useEffect(() => {
    if (p) setRows(p.lines.map((l) => ({ itemCode: l.item.code, name: l.item.name, poQty: l.qty, received: String(l.qty), passed: String(l.qty), failed: "0", reason: "" })));
    setErr(""); setNote(""); setDone(false); setInvNo(""); setInvDate(""); setInvAmount(""); setFile(null); if (fileRef.current) fileRef.current.value = ""; if (cameraRef.current) cameraRef.current.value = "";
  }, [p]);
  const setRow = (i, patch) => setRows((r) => r.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const IN = "w-24 rounded-md bg-white py-1.5 px-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400";
  const INF = "w-full rounded-md bg-white py-1.5 px-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400";
  const overFilled = rows.some((r) => Number(r.passed || 0) + Number(r.failed || 0) > Number(r.received || 0) + 1e-9);
  const anyPassed = rows.some((r) => Number(r.passed || 0) > 0);
  // Read a File/Blob → { base64, mime } (async; never blocks the main thread).
  const readAsBase64 = (blob, name, mime) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("read failed"));
    reader.onload = () => { const s = String(reader.result || ""); resolve({ base64: s.includes(",") ? s.split(",")[1] : s, name, mime }); };
    reader.readAsDataURL(blob);
  });
  // Client-side image compress: downscale to max ~1600px + JPEG quality until under ~480KB base64,
  // so a multi-MB phone photo attaches instead of being rejected. Non-images (PDF) pass through.
  const compressImage = (f) => new Promise((resolve, reject) => {
    // Load the image through a data: URL (via FileReader) — NOT URL.createObjectURL. The app's CSP is
    // `img-src 'self' data:`, which BLOCKS blob: URLs: a blob: <img> src is refused and fires onerror,
    // surfacing as "Could not read that file" for every JPG/PNG (PDFs skip this path, so they worked).
    // A data: URL is allowed by the CSP and does not taint the canvas, so toDataURL() below still works.
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("read failed"));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("image load failed"));
      img.onload = () => {
        // GUARANTEE the result stays under the backend fileBase64 cap (~700K chars). A detailed invoice
        // PHOTO (dense text) can exceed the cap even at the lowest JPEG quality — so if the quality sweep
        // bottoms out and it's still too big, shrink the pixel dimensions and sweep again, converging
        // under CAP. (PDFs skip compression and always fit.)
        const CAP = 480 * 1024 * (4 / 3); // ~480KB file ⇒ base64 chars stay under the backend ~512KB cap
        const canvas = document.createElement("canvas");
        const g = canvas.getContext("2d");
        const renderAt = (maxDim) => {
          let { width: w, height: h } = img;
          if (w > maxDim || h > maxDim) { const s = maxDim / Math.max(w, h); w = Math.round(w * s); h = Math.round(h * s); }
          canvas.width = w; canvas.height = h; g.drawImage(img, 0, 0, w, h);
          let out = "";
          for (let q = 0.85; q >= 0.37; q -= 0.12) { out = canvas.toDataURL("image/jpeg", q).split(",")[1] || ""; if (out.length <= CAP) return out; }
          return out; // quality floor reached at this size — caller shrinks dimensions if still over CAP
        };
        let maxDim = 1600, b64 = renderAt(maxDim);
        while (b64.length > CAP && maxDim > 480) { maxDim = Math.round(maxDim * 0.8); b64 = renderAt(maxDim); }
        resolve({ base64: b64, name: (f.name || "photo").replace(/\.[^.]+$/, "") + ".jpg", mime: "image/jpeg" });
      };
      img.src = String(reader.result || ""); // data: URL — allowed by CSP (blob: is not)
    };
    reader.readAsDataURL(f);
  });
  const onFile = async (e) => {
    const f = e.target.files && e.target.files[0]; if (!f) { setFile(null); return; }
    // Hard sanity cap BEFORE any work — reject an absurdly large upload (pre-compress).
    if (f.size > 10 * 1024 * 1024) { setErr("File too large (max 10MB)."); if (fileRef.current) fileRef.current.value = ""; setFile(null); return; }
    setErr(""); setFileBusy(true);
    try {
      const isImage = (f.type || "").indexOf("image/") === 0;
      const picked = isImage ? await compressImage(f) : await readAsBase64(f, f.name, f.type || undefined);
      setFile(picked);
    } catch (e2) { setErr("Could not read that file — try another."); setFile(null); if (fileRef.current) fileRef.current.value = ""; }
    finally { setFileBusy(false); }
  };
  const submit = async () => {
    if (overFilled) { setErr("Passed + failed cannot exceed the received qty on any line."); return; }
    setBusy(true); setErr(""); setNote("");
    try {
      await purchasesApi.qcPurchase(code, { pass: anyPassed, lines: rows.map((r) => ({ itemCode: r.itemCode, receivedQty: Number(r.received || 0), passedQty: Number(r.passed || 0), failedQty: Number(r.failed || 0), rejectReason: Number(r.failed || 0) > 0 && r.reason.trim() ? r.reason.trim() : undefined })) });
      const hasInvoice = invNo.trim() || invDate || invAmount !== "" || file;
      let fileNote = "";
      if (hasInvoice) {
        const res = await purchasesApi.savePurchaseInvoice(code, { invoiceNo: invNo.trim() || undefined, invoiceDate: invDate || undefined, invoiceAmount: invAmount !== "" ? Number(invAmount) : undefined, fileBase64: file?.base64, fileName: file?.name, mime: file?.mime });
        fileNote = (res && res.note) || "";
      }
      if (fileNote) { setNote(fileNote); setDone(true); setBusy(false); } // keep open to surface the soft note
      else { onDone && onDone(); }
    } catch (e) { setErr(e instanceof ApiError ? e.message : "Save failed."); setBusy(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title={`GRN + QC${p ? " · " + p.code : ""}`} wide
      footer={done
        ? <Btn onClick={() => onDone && onDone()}>Done</Btn>
        : <><Btn variant="soft" onClick={onClose}>Cancel</Btn><Btn onClick={submit} disabled={busy || fileBusy || overFilled || !rows.length}>{busy ? "Recording…" : fileBusy ? "Processing file…" : anyPassed ? "Record GRN + QC pass" : "Reject all"}</Btn></>}>
      {loading && !p ? <LoadingBlock label="Loading…" /> : error ? <InlineError message={error} /> : (
        <div className="space-y-3">
          {err ? <InlineError message={err} /> : null}
          {note ? <div className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-700 ring-1 ring-amber-200">{note}</div> : null}
          <p className="text-xs text-slate-400">Enter the <b>received</b> qty (GRN — may differ from the ordered qty). Split it into <b>passed</b> (added to stock, a batch/sticker is minted) and <b>failed</b> (rejected with a reason — no stock).</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-slate-500"><Th>Item</Th><Th className="text-right">Ordered</Th><Th className="text-right">Received</Th><Th className="text-right">Passed</Th><Th className="text-right">Failed</Th><Th>Reject reason</Th></tr></thead>
              <tbody className="divide-y divide-slate-50">
                {rows.map((r, i) => { const over = Number(r.passed || 0) + Number(r.failed || 0) > Number(r.received || 0) + 1e-9; return (
                  <tr key={i}>
                    <Td className="font-medium text-slate-700">{r.name} <Mono className="text-xs text-slate-400">{r.itemCode}</Mono></Td>
                    <Td className="text-right text-slate-500"><Mono>{fmtQty(r.poQty)}</Mono></Td>
                    <Td className="text-right"><input type="number" min="0" step="any" value={r.received} onChange={(e) => setRow(i, { received: e.target.value })} className={IN} /></Td>
                    <Td className="text-right"><input type="number" min="0" step="any" value={r.passed} onChange={(e) => setRow(i, { passed: e.target.value })} className={cx(IN, over && "ring-rose-300")} /></Td>
                    <Td className="text-right"><input type="number" min="0" step="any" value={r.failed} onChange={(e) => setRow(i, { failed: e.target.value })} className={cx(IN, over && "ring-rose-300")} /></Td>
                    <Td><input value={r.reason} onChange={(e) => setRow(i, { reason: e.target.value })} placeholder={Number(r.failed || 0) > 0 ? "reason (failed qty)" : "—"} disabled={!(Number(r.failed || 0) > 0)} className="w-40 rounded-md bg-white py-1.5 px-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400 disabled:bg-slate-50 disabled:text-slate-300" /></Td>
                  </tr>
                ); })}
              </tbody>
            </table>
          </div>
          {overFilled && <p className="text-xs text-rose-600">Passed + failed exceeds received on a highlighted line.</p>}
          <div className="space-y-2 rounded-lg bg-slate-50/70 p-3 ring-1 ring-slate-200">
            <div className="text-xs font-semibold text-slate-600">GRN / Vendor invoice <span className="font-normal text-slate-400">(optional)</span></div>
            <div className="grid gap-2 sm:grid-cols-3">
              <label className="block text-xs text-slate-500">Invoice no<input value={invNo} onChange={(e) => setInvNo(e.target.value)} placeholder="INV-…" className={cx(INF, "mt-1")} /></label>
              <label className="block text-xs text-slate-500">Invoice date<input type="date" value={invDate} onChange={(e) => setInvDate(e.target.value)} className={cx(INF, "mt-1")} /></label>
              <label className="block text-xs text-slate-500">Invoice amount<input type="number" min="0" step="0.01" value={invAmount} onChange={(e) => setInvAmount(e.target.value)} placeholder="₹" className={cx(INF, "mt-1")} /></label>
            </div>
            <div className="text-xs text-slate-500">
              <div className="mb-1">Invoice file (image/PDF, optional)</div>
              {/* SCOPED trigger: a hidden input opened ONLY by this button (a ref .click()). No wrapping
                  full-width label, so clicks elsewhere in the modal never open the OS file picker. */}
              <input ref={fileRef} type="file" accept="image/*,application/pdf" onChange={onFile} className="hidden" />
              {/* Second input with capture="environment": opens the rear camera directly on mobile; on
                  desktop capture is ignored and it degrades to a normal image picker. Same onFile handler. */}
              <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={onFile} className="hidden" />
              <div className="flex flex-wrap items-center gap-2">
                <Btn variant="soft" type="button" disabled={fileBusy} onClick={() => fileRef.current && fileRef.current.click()}>{fileBusy ? "Processing…" : (file ? "Change file" : "Choose file")}</Btn>
                <Btn variant="soft" type="button" disabled={fileBusy} onClick={() => cameraRef.current && cameraRef.current.click()}><Camera size={14} className="mr-1" />Take photo</Btn>
                {file ? <span className="inline-flex items-center gap-1 text-xs text-slate-600"><Mono className="max-w-[160px] truncate">{file.name}</Mono><button type="button" onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ""; if (cameraRef.current) cameraRef.current.value = ""; }} className="text-slate-400 hover:text-rose-500" title="Remove"><X size={12} /></button></span>
                  : <span className="text-xs text-slate-400">No file chosen</span>}
              </div>
              <p className="mt-1 text-[11px] text-slate-400">Photos are compressed automatically before upload.</p>
            </div>
            {p && p.totalAmount != null ? <p className="text-[11px] text-slate-400">PO total {fmtMoney(p.totalAmount)} — the invoice amount is stored for the later price-difference check.</p> : null}
          </div>
        </div>
      )}
    </Modal>
  );
}

function PurchaseLines({ code, grn }) {
  const { data, loading, error, reload } = useResource(() => purchasesApi.getPurchase(code), [code]);
  const p = data?.purchase;
  if (loading && !p) return <LoadingBlock label="Loading…" />;
  if (error) return <InlineError message={error} onRetry={reload} />;
  if (!p) return null;
  return (
    <div className="space-y-2 py-1 overflow-x-auto">
      <table className="w-full"><thead><tr><Th>Item</Th><Th>Type</Th><Th className="text-right">Qty</Th></tr></thead>
        <tbody className="divide-y divide-slate-50">
          {p.lines.map((l, i) => <tr key={i}><Td className="font-medium text-slate-700">{l.item.name} <Mono className="text-xs text-slate-400">{l.item.code}</Mono></Td><Td><Badge tone={l.item.type === "PM" ? "sky" : "stone"}>{l.item.type || "RM"}</Badge></Td><Td className="text-right"><Mono>{fmtQty(l.qty)}</Mono></Td></tr>)}
        </tbody></table>
      {p.batches && p.batches.length > 0 && <div className="rounded-md bg-emerald-50 px-3 py-2 text-xs text-emerald-700 ring-1 ring-emerald-200">QC passed — batch(es) created: {p.batches.map((b) => <Mono key={b.batchCode} className="mr-2">{b.batchCode} ({fmtQty(b.qty)} Kg)</Mono>)}</div>}
      {(p.invoiceNo || p.invoiceAmount != null || p.hasInvoiceFile) && <PurchaseInvoiceRow code={code} p={p} grn={grn} onChanged={reload} />}
    </div>
  );
}

// GRN INVOICE read-back — invoice no/date/amount + a fail-soft "View invoice" (signed URL) link.
function PurchaseInvoiceRow({ code, p, grn, onChanged }) {
  const [busy, setBusy] = useState(false); const [msg, setMsg] = useState("");
  const [rejecting, setRejecting] = useState(false); const [reason, setReason] = useState("");
  const view = async () => {
    setBusy(true); setMsg("");
    try { const r = await purchasesApi.purchaseInvoiceFile(code); if (r && r.url) window.open(r.url, "_blank", "noopener"); else setMsg((r && r.note) || "File unavailable."); }
    catch (e) { setMsg(e instanceof ApiError ? e.message : "Could not open the file."); }
    finally { setBusy(false); }
  };
  const verify = async (decision, r) => {
    setBusy(true); setMsg("");
    try { await purchasesApi.verifyPurchaseInvoice(code, { decision, reason: r }); setRejecting(false); setReason(""); onChanged && onChanged(); }
    catch (e) { setMsg(e instanceof ApiError ? e.message : "Verify failed."); setBusy(false); }
  };
  // Steps 9/9a/11 — verify is offered only on a QC-passed, invoiced purchase when grnQc is on.
  const canVerify = grn && p.status === "Passed" && p.invoiceAmount != null;
  const v = p.invoiceVerification;
  return (
    <div className="space-y-1.5 rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-600 ring-1 ring-slate-200">
      <div>
        <span className="font-semibold text-slate-700">Vendor invoice:</span>{" "}
        {p.invoiceNo ? <>No <Mono>{p.invoiceNo}</Mono> · </> : null}
        {p.invoiceDate ? <>{fmtDate(p.invoiceDate)} · </> : null}
        {p.invoiceAmount != null ? <>amount {fmtMoney(p.invoiceAmount)}{p.totalAmount != null ? <span className="text-slate-400"> (PO {fmtMoney(p.totalAmount)})</span> : null} · </> : null}
        {p.hasInvoiceFile ? <button onClick={view} disabled={busy} className="font-medium text-indigo-600 hover:underline disabled:opacity-50">{busy ? "opening…" : "View invoice"}</button> : <span className="text-slate-400">no file</span>}
      </div>
      {p.priceDiff != null && <div>Price difference: <b className={cx("tabular-nums", Math.abs(p.priceDiff) > 0.009 ? "text-amber-700" : "text-slate-600")}>{p.priceDiff >= 0 ? "+" : ""}{fmtMoney(p.priceDiff)}{p.priceDiffPct != null ? ` (${p.priceDiffPct >= 0 ? "+" : ""}${p.priceDiffPct}%)` : ""}</b> <span className="text-slate-400">(invoice − PO)</span></div>}
      {v === "verified" && <div><Badge tone="emerald"><CheckCircle2 size={11} /> Verified · to accounts</Badge>{p.invoiceVerifiedAt ? <span className="ml-1 text-slate-400">{fmtDate(p.invoiceVerifiedAt)}</span> : null}</div>}
      {v === "returned" && <div><Badge tone="rose">Returned for invoice correction</Badge>{p.invoiceRejectReason ? <span className="ml-1 text-slate-500">“{p.invoiceRejectReason}”</span> : null}</div>}
      {canVerify && v == null && !rejecting && <div className="flex items-center gap-1.5"><Btn variant="soft" disabled={busy} onClick={() => verify("approve")}><CheckCircle2 size={12} /> Approve (to accounts)</Btn><Btn variant="ghost" disabled={busy} onClick={() => setRejecting(true)}><X size={12} /> Return to store</Btn></div>}
      {canVerify && rejecting && <div className="flex flex-wrap items-center gap-1.5"><input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="reason for invoice correction" className="w-60 rounded-md bg-white py-1 px-2 text-xs ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400" /><Btn variant="soft" disabled={busy || !reason.trim()} onClick={() => verify("reject", reason.trim())}>Return</Btn><Btn variant="ghost" disabled={busy} onClick={() => { setRejecting(false); setReason(""); }}>Cancel</Btn></div>}
      {msg ? <span className="text-amber-600">{msg}</span> : null}
    </div>
  );
}

/* ============================ Batches (traceability + sticker + FG QC) ============================ */
function stickerDoc(batch, itemName, companyName) {
  return '<div style="border:3px solid #1c1917;border-radius:14px;padding:34px 40px;max-width:460px;margin:40px auto;text-align:center">'
    + '<div style="font-size:12px;letter-spacing:.16em;color:#78716c;text-transform:uppercase">' + sellerName(companyName) + '</div>'
    + '<div style="font-size:30px;font-weight:800;margin:18px 0 8px">' + itemName + '</div>'
    + '<div style="font-size:11px;color:#78716c;letter-spacing:.08em">BATCH NO</div>'
    + '<div style="font-size:34px;font-weight:800;font-family:monospace;letter-spacing:.04em;margin-top:2px">' + batch.id + '</div>'
    + '<div style="margin-top:18px;font-size:12px;color:#78716c">' + batch.type + ' \u00b7 ' + fmtQty(batch.qty) + ' Kg \u00b7 ' + fmtDate(batch.date) + '</div></div>';
}

/* ===================== Batches + QC — LIVE (W4a) =====================
   List + genealogy + FG/SFG QC all source from the real /batches API. QC pass/
   reject POST to /batches/:code/qc; pass adds to available + FIFO genealogy +
   auto-allocates to the source SO line (server-side). The fgqc capability is
   enforced by the backend (the real owner session passes). */

const BATCH_STATUS_TONE = { Passed: "emerald", Rejected: "rose", "QC Pending": "amber" };

function BatchGenealogy({ code }) {
  const { data, loading, error, reload } = useResource(() => batchesApi.getBatch(code), [code]);
  const b = data?.batch;
  if (loading && !b) return <LoadingBlock label="Loading genealogy…" />;
  if (error) return <div className="p-2"><InlineError message={error} onRetry={reload} /></div>;
  if (!b) return null;
  const isRM = b.itemType === "RM" || b.itemType === "PM";
  return (
    <div className="grid gap-3 p-2 sm:grid-cols-2">
      <div className="text-xs">
        <div className="mb-1 font-semibold text-slate-600">Details</div>
        <div className="space-y-0.5 text-slate-500">
          <div>Item: <span className="text-slate-700">{b.item.name}</span> <Mono className="text-slate-400">{b.item.code}</Mono></div>
          <div>Status: <span className="text-slate-700">{b.status}</span></div>
          <div>Room: <span className="text-slate-700">{b.room || "—"}</span></div>
          <div>Operator / responsible: <span className="text-slate-700">{b.operator?.name || "—"}</span></div>
          {!isRM ? <div>Source PO: <Mono className="text-slate-700">{b.poCode || "—"}</Mono></div> : null}
          <div>Qty / remaining: <Mono className="text-slate-700">{fmtQty(b.qty)}</Mono> / <Mono className="text-slate-700">{fmtQty(b.remaining)}</Mono> Kg</div>
          <div>Produced: <span className="text-slate-700">{fmtDate(b.producedAt)}</span></div>
          {b.fgQcAt ? <div>QC: <span className="text-slate-700">{b.status}</span> by <span className="text-slate-700">{b.qcBy?.name || "—"}</span> on <span className="text-slate-700">{fmtDate(b.fgQcAt)}</span></div> : null}
        </div>
      </div>
      <div className="text-xs">
        {isRM ? (
          <>
            <div className="mb-1 font-semibold text-slate-600">Batches produced from this RM/PM batch (consumed by)</div>
            {(!b.consumedBy || b.consumedBy.length === 0) ? <div className="text-slate-400">No FG/SFG batch has drawn from this batch yet.</div> : <table className="w-full"><tbody className="divide-y divide-slate-100">{b.consumedBy.map((c, i) => <tr key={i}><Td className="py-1"><Mono className="text-slate-500">{c.producedBatchCode}</Mono></Td><Td className="py-1 text-right"><Mono>{fmtQty(c.qty)}</Mono></Td></tr>)}</tbody></table>}
          </>
        ) : (
          <>
            <div className="mb-1 font-semibold text-slate-600">Which RM/PM batches it was made from (FIFO · consumed from)</div>
            {(!b.consumedFrom || b.consumedFrom.length === 0) ? <div className="text-slate-400">{b.status === "QC Pending" ? "RM batches are linked on QC pass (FIFO)." : "—"}</div> : <table className="w-full"><tbody className="divide-y divide-slate-100">{b.consumedFrom.map((c, i) => <tr key={i}><Td className="py-1 text-slate-600"><Mono>{c.itemCode}</Mono></Td><Td className="py-1"><Mono className="text-slate-500">{c.batchCode}</Mono></Td><Td className="py-1 text-right"><Mono>{fmtQty(c.qty)}</Mono></Td></tr>)}</tbody></table>}
          </>
        )}
      </div>
    </div>
  );
}

function BatchesView({ auth }) {
  const [tab, setTab] = useState("all");
  const [open, setOpen] = useState(null);
  const [acting, setActing] = useState(null);
  const [actErr, setActErr] = useState("");
  const [q, setQ] = useState("");
  const [dq, setDq] = useState(""); // debounced query
  const [justPassed, setJustPassed] = useState(null); // batch to offer a sticker for, after QC pass
  React.useEffect(() => { const t = setTimeout(() => setDq(q.trim()), 250); return () => clearTimeout(t); }, [q]);
  const tabs = [["all", "All"], ["RM", "RM / PM"], ["FG", "FG / SFG"], ["qc", "FG QC pending"]];
  const { data, loading, error, reload } = useResource(
    // Backend `q` filters by batch code, item code AND item name; it runs alongside
    // the tab's status/type filter.
    () => batchesApi.listBatches({ status: tab === "qc" ? "QC Pending" : undefined, q: dq || undefined, limit: 200 }),
    [tab, dq],
  );
  const all = data?.batches || [];
  const list = all.filter((b) => tab === "RM" ? (b.itemType === "RM" || b.itemType === "PM") : tab === "FG" ? (b.itemType === "FG" || b.itemType === "SFG") : true);
  const printSticker = (b) => openDoc("Sticker " + b.code, stickerDoc({ id: b.code, type: b.itemType, qty: b.qty, date: b.producedAt }, b.item.name, auth?.company?.name), true);
  const doQc = async (b, pass) => {
    setActErr(""); setActing(b.code); setJustPassed(null);
    try {
      await batchesApi.qcBatch(b.code, pass);
      await reload();
      // On PASS, surface a one-click "Print sticker" for the batch just passed,
      // so the user doesn't have to hunt for it in the list. (Not auto-firing the
      // print dialog — a clear prompt is safer.) Reuses printSticker verbatim.
      if (pass) setJustPassed(b);
    }
    catch (e) { setActErr(e instanceof ApiError ? e.message : "QC action failed."); }
    finally { setActing(null); }
  };
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="flex gap-1 rounded-lg bg-slate-100 p-1 w-min">{tabs.map(([k, l]) => <button key={k} onClick={() => setTab(k)} className={cx("whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium", tab === k ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700")}>{l}</button>)}</div>
        <div className="relative"><Search size={14} className="absolute left-2.5 top-2 text-slate-400" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search batch no or item…" className="w-56 rounded-md bg-white py-1.5 pl-8 pr-2 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400" />{q && <button onClick={() => setQ("")} className="absolute right-2 top-1.5 text-slate-400 hover:text-slate-600"><X size={14} /></button>}</div>
        <div className="ml-auto flex items-center gap-2 text-xs text-slate-400">{loading && all.length > 0 ? <Spinner size={14} /> : null}<span>{data ? `${data.total} batch${data.total === 1 ? "" : "es"}` : ""}</span></div>
      </div>
      {justPassed && (
        <div className="flex flex-wrap items-center gap-2 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-800 ring-1 ring-emerald-200">
          <CheckCircle2 size={15} className="text-emerald-600" /> QC passed <Mono className="font-semibold">{justPassed.code}</Mono> — {justPassed.item.name}.
          <Btn variant="soft" className="ml-1" onClick={() => { printSticker(justPassed); setJustPassed(null); }}><Printer size={13} /> Print sticker</Btn>
          <button onClick={() => setJustPassed(null)} className="ml-auto text-xs text-emerald-600 hover:text-emerald-800">Dismiss</button>
        </div>
      )}
      <p className="text-xs text-slate-400">Every FG/SFG/RM batch is auto-numbered. RM batch → PO + QC. FG/SFG batch → which room and which RM batches it was made from (FIFO genealogy). On QC pass, FG is added to <b>available</b> stock + auto-allocated to the source order line. Sticker = batch no + item name.</p>
      {actErr ? <InlineError message={actErr} /> : null}
      {error ? <InlineError message={error} onRetry={reload} /> : null}
      <Card className="overflow-x-auto">
        {loading && all.length === 0 ? <LoadingBlock label="Loading batches…" /> : (
          <table className="w-full">
            <thead className="border-b border-slate-100"><tr><Th>Batch no</Th><Th>Item</Th><Th>Type</Th><Th className="text-right">Qty</Th><Th className="text-right">Remaining</Th><Th>Date</Th><Th>Status</Th><Th>Source / Room</Th><Th>Operator</Th><Th>Action</Th></tr></thead>
            <tbody className="divide-y divide-slate-50">
              {list.length === 0 ? <tr><Td className="text-slate-400" colSpan={10}>No batches yet.</Td></tr> : list.map((b) => { const isRM = b.itemType === "RM" || b.itemType === "PM"; return (
                <React.Fragment key={b.code}>
                  <tr className="hover:bg-slate-50/50">
                    <Td><button onClick={() => setOpen(open === b.code ? null : b.code)} className="inline-flex items-center gap-1 font-semibold text-slate-800"><ChevronRight size={13} className={cx("text-slate-400 transition", open === b.code ? "rotate-90" : "")} /><Mono>{b.code}</Mono></button></Td>
                    <Td className="text-slate-700">{b.item.name} <Mono className="ml-1 text-xs text-slate-400">{b.item.code}</Mono></Td>
                    <Td><Badge tone={isRM ? "stone" : "violet"}>{b.itemType}</Badge></Td>
                    <Td className="text-right"><Mono>{fmtQty(b.qty)}</Mono></Td>
                    <Td className="text-right"><Mono className="text-slate-500">{fmtQty(b.remaining)}</Mono></Td>
                    <Td className="text-slate-500">{fmtDate(b.producedAt)}</Td>
                    <Td><Badge tone={BATCH_STATUS_TONE[b.status] || "stone"}>{b.status}</Badge></Td>
                    <Td className="text-xs text-slate-500">{isRM ? (b.poCode || "—") : (<span>{b.room || "—"}{b.poCode ? <Mono className="ml-1 text-slate-400">{b.poCode}</Mono> : null}</span>)}</Td>
                    <Td className="text-xs text-slate-600">{b.operator?.name || "—"}</Td>
                    <Td><div className="flex flex-wrap gap-1.5">
                      <Btn variant="soft" onClick={() => printSticker(b)}><Printer size={12} /> Sticker</Btn>
                      {!isRM && b.status === "QC Pending" && <><Btn variant="soft" disabled={acting === b.code} onClick={() => doQc(b, false)}><X size={12} /> Reject</Btn><Btn disabled={acting === b.code} onClick={() => doQc(b, true)}><CheckCircle2 size={12} /> {acting === b.code ? "…" : "QC pass"}</Btn></>}
                    </div></Td>
                  </tr>
                  {open === b.code && <tr><Td colSpan={10} className="bg-slate-50/40"><BatchGenealogy code={b.code} /></Td></tr>}
                </React.Fragment>
              ); })}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

/* ============================ Customers ============================ */
function csvCell(v) { const s = String(v == null ? "" : v); return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s; }
function downloadCSV(filename, rows) {
  const csv = "\ufeff" + rows.map((r) => r.map(csvCell).join(",")).join("\r\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; document.body.appendChild(a); a.click(); document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}
const slug = (s) => String(s).replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase();

/* ===================== Customers (Parties) — LIVE (W3) =====================
   The customer master (group → sister hierarchy) now sources from the real
   /parties/tree API, with a NEW create/edit modal (POST/PATCH /parties) and
   activate/deactivate controls. The LEDGER + Excel export below still aggregate
   the MOCK reducer (orders + invoices) — they go live in W5 once Invoices are
   wired; this is flagged in-UI so we don't claim it's real. */

const PARTY_KINDS = [["group", "Group / Master"], ["sister", "Sister concern"], ["b2c", "B2C channel"]];
const PARTY_KIND_TONE = { group: "amber", sister: "violet", b2c: "sky" };

function PartyModal({ open, onClose, onSaved, party, parentOptions }) {
  const edit = !!party;
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [kind, setKind] = useState("group");
  const [gstin, setGstin] = useState("");
  const [parentCode, setParentCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  React.useEffect(() => {
    if (!open) return;
    setError(""); setBusy(false);
    setCode(party ? party.code : "");
    setName(party ? party.name : "");
    setKind(party ? party.kind : "group");
    setGstin(party && party.gstin ? party.gstin : "");
    setParentCode(party && party.parentCode ? party.parentCode : "");
  }, [open, party]);

  const save = async () => {
    if (busy) return;
    if (!code.trim() || !name.trim()) { setError("Code and name are required."); return; }
    setBusy(true); setError("");
    const body = { name: name.trim(), kind, gstin: gstin.trim() || undefined, parentCode: parentCode || undefined };
    try {
      if (edit) await partiesApi.updateParty(party.code, body);
      else await partiesApi.createParty({ code: code.trim(), ...body });
      onSaved();
    } catch (e) {
      // Envelope message (e.g. 409 duplicate code, unknown parentCode) inline.
      setError(e instanceof ApiError ? e.message : "Could not save the customer.");
      setBusy(false);
    }
  };

  // No self-parenting on edit.
  const opts = (parentOptions || []).filter((p) => !edit || p.code !== party.code);
  return (
    <Modal open={open} onClose={onClose} title={edit ? `Edit customer · ${party.code}` : "New customer"}
      footer={<><Btn variant="soft" onClick={onClose}>Cancel</Btn><Btn onClick={save} disabled={busy}>{busy ? "Saving…" : edit ? "Save changes" : "Create customer"}</Btn></>}>
      <div className="grid gap-3 sm:grid-cols-2">
        <SField label="Code"><input value={code} disabled={edit} onChange={(e) => setCode(e.target.value.toUpperCase())} className={SETTINGS_IN} placeholder="e.g. P-NEW" /></SField>
        <SField label="Kind"><select value={kind} onChange={(e) => setKind(e.target.value)} className={SETTINGS_IN}>{PARTY_KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></SField>
        <SField label="Name" full><input value={name} onChange={(e) => setName(e.target.value)} className={SETTINGS_IN} placeholder="Customer name" /></SField>
        <SField label="GSTIN (optional)"><input value={gstin} onChange={(e) => setGstin(e.target.value.toUpperCase())} className={SETTINGS_IN} placeholder="e.g. 19AAAAA0000A1Z5" /></SField>
        <SField label="Parent (optional · for sisters)"><select value={parentCode} onChange={(e) => setParentCode(e.target.value)} className={SETTINGS_IN}><option value="">— none —</option>{opts.map((p) => <option key={p.code} value={p.code}>{p.name} ({p.code})</option>)}</select></SField>
      </div>
      {error ? <InlineError message={error} /> : null}
    </Modal>
  );
}

function CustomersView() {
  const [exp, setExp] = useState(null);
  const [modal, setModal] = useState(null); // null | "new" | partyNode
  const [acting, setActing] = useState(null); // code mid activate/deactivate
  const { data, loading, error, reload } = useResource(() => partiesApi.getPartyTree(), []);
  const tree = data?.tree || [];
  // Flatten active tree → parent-picker options for the modal.
  const flatParents = tree.flatMap((g) => [g, ...(g.children || [])]).map((p) => ({ code: p.code, name: p.name }));

  // --- Ledger / Excel export: now LIVE (W5) via /receivables (debits-only Σ of
  // invoice totals; payments/credits intentionally not modelled). The per-party
  // rollup powers the header totals; the expandable table + CSV fetch the party's
  // invoice list (running balance) on demand. ---
  const recRes = useResource(() => invoicesApi.getReceivables(), []);
  const recByCode = {};
  ((recRes.data && recRes.data.receivables) || []).forEach((r) => { recByCode[r.party.code] = r; });
  const invTot = (code) => (recByCode[code] ? recByCode[code].totalInvoiced : 0);
  const invCount = (code) => (recByCode[code] ? recByCode[code].invoiceCount : 0);

  const exportCompany = async (code, name) => {
    try {
      const led = (await invoicesApi.getReceivables(code)).invoices || [];
      const rows = [["Date", "Invoice No", "Amount (INR)", "Running balance (INR)"]];
      led.forEach((e) => rows.push([fmtDate(e.date), e.code, e.total.toFixed(2), e.runningBalance.toFixed(2)]));
      if (!led.length) rows.push(["—", "no invoices yet", "", ""]);
      else rows.push(["", "", "TOTAL " + invTot(code).toFixed(2), ""]);
      downloadCSV("ledger-" + slug(name) + ".csv", rows);
    } catch (e) { alert(e instanceof ApiError ? e.message : "Could not export the ledger."); }
  };
  const exportGroup = async (nodes, gname) => {
    try {
      const rows = [["Company", "Date", "Invoice No", "Amount (INR)", "Running balance (INR)"]];
      let any = false, tot = 0;
      for (const n of nodes) {
        const led = (await invoicesApi.getReceivables(n.code)).invoices || [];
        led.forEach((e) => { any = true; tot += e.total; rows.push([n.name, fmtDate(e.date), e.code, e.total.toFixed(2), e.runningBalance.toFixed(2)]); });
      }
      if (!any) rows.push(["—", "no invoices yet", "", "", ""]);
      else rows.push(["", "", "", "GROUP TOTAL " + tot.toFixed(2), ""]);
      downloadCSV("group-ledger-" + slug(gname) + ".csv", rows);
    } catch (e) { alert(e instanceof ApiError ? e.message : "Could not export the group ledger."); }
  };
  const LedgerTable = ({ code }) => {
    const { data: ld, loading: lloading, error: lerror, reload: lreload } = useResource(() => invoicesApi.getReceivables(code), [code]);
    const led = (ld && ld.invoices) || [];
    if (lloading && !ld) return <div className="mt-2"><LoadingBlock label="Loading ledger…" /></div>;
    if (lerror) return <div className="mt-2"><InlineError message={lerror} onRetry={lreload} /></div>;
    return (
      <div className="mt-2 overflow-x-auto rounded-lg ring-1 ring-slate-200">
        <table className="w-full"><thead className="border-b border-slate-100 bg-slate-50/60"><tr><Th>Date</Th><Th>Invoice</Th><Th className="text-right">Amount</Th><Th className="text-right">Running balance</Th></tr></thead>
          <tbody className="divide-y divide-slate-50">
            {led.length === 0 ? <tr><Td className="text-slate-400" colSpan={4}>No invoices yet.</Td></tr> : led.map((e) => (
              <tr key={e.code}><Td className="text-slate-500">{fmtDate(e.date)}</Td><Td><Mono className="font-medium text-slate-700">{e.code}</Mono></Td><Td className="text-right"><Mono className="text-slate-700">{fmtMoney(e.total)}</Mono></Td><Td className="text-right"><Mono className="font-semibold text-slate-800">{fmtMoney(e.runningBalance)}</Mono></Td></tr>
            ))}
          </tbody></table>
      </div>
    );
  };

  const toggleActive = async (node) => {
    setActing(node.code);
    try {
      if (node.isActive) await partiesApi.deactivateParty(node.code);
      else await partiesApi.activateParty(node.code);
      await reload();
    } catch (e) {
      alert(e instanceof ApiError ? e.message : "Action failed.");
    } finally {
      setActing(null);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs text-slate-400">Real customer master (parties API) · group → sister hierarchy · live invoice ledger</div>
        <div className="flex items-center gap-2">{(loading || recRes.loading) && tree.length > 0 ? <Spinner size={14} /> : null}<Btn onClick={() => setModal("new")}><Plus size={15} /> New customer</Btn></div>
      </div>
      {error ? <InlineError message={error} onRetry={reload} /> : null}
      {recRes.error ? <InlineError message={recRes.error} onRetry={recRes.reload} /> : null}

      {loading && tree.length === 0 ? <LoadingBlock label="Loading customers…" /> : tree.map((g) => {
        const sisters = g.children || [];
        const nodes = sisters.length ? sisters : [g];
        const groupInv = nodes.reduce((a, n) => a + invTot(n.code), 0);
        const groupCount = nodes.reduce((a, n) => a + invCount(n.code), 0);
        return (
          <Card key={g.code} className={cx("p-4", g.isActive ? "" : "opacity-60")}>
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2"><Building2 size={18} className="text-amber-600" /><div><div className="flex items-center gap-2 font-semibold text-slate-800">{g.name}<Badge tone={PARTY_KIND_TONE[g.kind] || "stone"}>{g.kind}</Badge>{!g.isActive && <Badge tone="stone">Inactive</Badge>}</div><div className="text-xs text-slate-400">{sisters.length ? `Master client · ${sisters.length} sister concern${sisters.length === 1 ? "" : "s"}` : g.kind === "b2c" ? "B2C channel" : "Standalone customer"} · GSTIN {g.gstin || "—"} · <Mono>{g.code}</Mono></div></div></div>
              <div className="flex flex-wrap items-center gap-2">
                <div className="text-right text-xs"><div className="text-slate-400">Group invoiced</div><Mono className="text-base font-semibold text-slate-800">{fmtMoney(groupInv)}</Mono> <span className="text-slate-400">· {groupCount} inv</span></div>
                <Btn variant="ghost" onClick={() => setModal(g)}>Edit</Btn>
                <Btn variant="soft" disabled={acting === g.code} onClick={() => toggleActive(g)}>{acting === g.code ? "…" : g.isActive ? "Deactivate" : "Activate"}</Btn>
                <Btn variant="soft" onClick={() => exportGroup(nodes, g.name)}><Download size={14} /> Group ledger</Btn>
              </div>
            </div>
            <div className="mt-3 space-y-2">
              {nodes.map((n) => (
                <div key={n.code} className={cx("rounded-lg ring-1 ring-slate-200", n.isActive ? "" : "opacity-60")}>
                  <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
                    <button onClick={() => setExp(exp === n.code ? null : n.code)} className="flex min-w-0 items-center gap-2 text-left text-sm">
                      <ChevronRight size={14} className={cx("shrink-0 text-slate-400 transition", exp === n.code ? "rotate-90" : "")} />
                      {sisters.length > 0 && <span className="text-slate-300">↳</span>}
                      <span className="truncate font-medium text-slate-700">{n.name}</span>
                      {n !== g && <Badge tone={PARTY_KIND_TONE[n.kind] || "stone"}>{n.kind}</Badge>}
                      <Mono className="hidden text-xs text-slate-400 sm:inline">{n.gstin || n.code}</Mono>
                    </button>
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="text-xs text-slate-500"><Mono>{invCount(n.code)}</Mono> inv · invoiced <Mono className="font-semibold text-slate-700">{fmtMoney(invTot(n.code))}</Mono></div>
                      {sisters.length > 0 && <><Btn variant="ghost" onClick={() => setModal(n)}>Edit</Btn><Btn variant="soft" disabled={acting === n.code} onClick={() => toggleActive(n)}>{acting === n.code ? "…" : n.isActive ? "Deactivate" : "Activate"}</Btn></>}
                      <Btn variant="soft" onClick={() => exportCompany(n.code, n.name)}><Download size={13} /> Excel</Btn>
                    </div>
                  </div>
                  {exp === n.code && <div className="border-t border-slate-100 p-3"><LedgerTable code={n.code} /></div>}
                </div>
              ))}
            </div>
          </Card>
        );
      })}
      {!loading && tree.length === 0 && !error && <Card className="p-8 text-center text-sm text-slate-400">No customers yet — add one with “New customer”.</Card>}
      <p className="px-1 text-xs text-slate-400">Ledger = invoices raised against that company (date · invoice · amount · running balance) — now <b>live</b> (/receivables). "Excel" = CSV download. Group ledger = the parent + all sister concerns together. Debits-only: payments/credits are a separate module (future).</p>

      <PartyModal open={modal != null} party={modal && modal !== "new" ? modal : null} parentOptions={flatParents} onClose={() => setModal(null)} onSaved={() => { setModal(null); reload(); }} />
    </div>
  );
}

/* ===================== Performance (Phase 2) ===================== */
// Per-employee performance from production-order completion. Durations are
// WORKING time (week-offs + company holidays excluded, via the holiday-aware
// calc); on-time = a PO completed on/before its source order's required-by date
// (make-to-stock POs have no due date and are excluded from the on-time rate).
const perfDayStr = (d) => new Date(d).toISOString().slice(0, 10);

function EmployeePerfModal({ userId, from, to, type, open, onClose }) {
  const { data, loading, error, reload } = useResource(
    () => (userId ? performanceApi.employeeDetail(userId, { from: from || undefined, to: to || undefined, type }) : Promise.resolve(null)),
    [userId, from, to, type],
  );
  const d = data;
  const s = d && d.summary;
  return (
    <Modal open={open} onClose={onClose} wide title={"Performance — " + (d ? d.user.name : "")}>
      {loading && !d ? <LoadingBlock label="Loading employee report…" /> : error ? <InlineError message={error} onRetry={reload} /> : d ? (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
            <Badge tone="violet">{d.user.role ? d.user.role.name : "—"}</Badge>
            <span>Window <Mono>{d.from}</Mono> → <Mono>{d.to}</Mono></span>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <div className="rounded-lg bg-slate-50 p-3"><div className="text-xs text-slate-400">Completed jobs</div><Mono className="text-lg font-semibold text-slate-800">{s.completedCount}</Mono><div className="text-xs text-slate-400">{s.productionCount} prod · {s.dispatchCount} dispatch</div></div>
            <div className="rounded-lg bg-slate-50 p-3"><div className="text-xs text-slate-400">Avg working duration</div><Mono className="text-lg font-semibold text-slate-800">{s.avgWorkingDuration}</Mono><span className="text-xs text-slate-400"> working days</span></div>
            <div className="rounded-lg bg-slate-50 p-3"><div className="text-xs text-slate-400">Avg raw (calendar)</div><Mono className="text-lg font-semibold text-slate-500">{s.avgRawDurationHours}</Mono><span className="text-xs text-slate-400"> h</span></div>
            <div className="rounded-lg bg-emerald-50 p-3"><div className="text-xs text-emerald-700">On-time rate</div><Mono className="text-lg font-semibold text-emerald-700">{s.onTimeRate == null ? "—" : s.onTimeRate + "%"}</Mono><div className="text-xs text-emerald-600/80">{s.onTimeCount}/{s.onTimeEligible} due-dated</div></div>
          </div>
          <div>
            <div className="mb-1.5 text-sm font-semibold text-slate-700">Completed jobs (production orders + deliveries)</div>
            <div className="overflow-x-auto rounded-lg ring-1 ring-slate-200">
              <table className="w-full"><thead className="bg-slate-50/60"><tr><Th>Type</Th><Th>Code</Th><Th>Item / party</Th><Th className="text-right">Qty</Th><Th>Created</Th><Th>Completed</Th><Th className="text-right">Working days</Th><Th className="text-right">Raw (h)</Th><Th>Due</Th><Th>On-time</Th></tr></thead>
                <tbody className="divide-y divide-slate-50">
                  {d.orders.length === 0 ? <tr><Td className="text-slate-400" colSpan={10}>No completed jobs in this window.</Td></tr> : d.orders.map((o) => (
                    <tr key={o.type + o.code}>
                      <Td><Badge tone={o.type === "dispatch" ? "sky" : "violet"}>{o.type === "dispatch" ? "Dispatch" : "Production"}</Badge></Td>
                      <Td><Mono className="font-semibold text-slate-800">{o.code}</Mono></Td>
                      <Td className="text-slate-700">{o.item.name}{o.item.code ? <div className="text-xs text-slate-400"><Mono>{o.item.code}</Mono></div> : null}</Td>
                      <Td className="text-right"><Mono>{fmtQty(o.qty)}</Mono></Td>
                      <Td className="text-xs text-slate-500">{fmtDateTime(o.createdAt)}</Td>
                      <Td className="text-xs text-slate-500">{fmtDateTime(o.completedAt)}</Td>
                      <Td className="text-right"><Mono className="font-medium text-slate-800">{o.workingDuration}</Mono></Td>
                      <Td className="text-right"><Mono className="text-slate-500">{o.rawDurationHours}</Mono></Td>
                      <Td className="text-xs text-slate-500">{o.dueDate ? fmtDate(o.dueDate) : <span className="text-slate-300">— (no due)</span>}</Td>
                      <Td>{o.onTime == null ? <span className="text-slate-300 text-xs">n/a</span> : o.onTime ? <Badge tone="emerald"><CheckCircle2 size={11} /> On-time</Badge> : <Badge tone="rose"><AlertTriangle size={11} /> Late</Badge>}</Td>
                    </tr>
                  ))}
                </tbody></table>
            </div>
          </div>
          <p className="text-xs text-slate-400">Working days exclude week-offs + company/branch holidays (holiday-aware calc). Raw is calendar hours (completed − created). On-time = completed on/before the order's required-by date. Production = PO created→completed; Dispatch = challan created→delivered.</p>
        </div>
      ) : null}
    </Modal>
  );
}

function PerformanceView({ auth, role }) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [type, setType] = useState("all"); // production | dispatch | all (Phase 3)
  const [sort, setSort] = useState({ key: "completedCount", dir: "desc" });
  const [drillUser, setDrillUser] = useState(null);
  const { data, loading, error, reload } = useResource(async () => {
    const [emp, sum] = await Promise.all([
      performanceApi.employees({ from: from || undefined, to: to || undefined, type }),
      performanceApi.summary({ from: from || undefined, to: to || undefined, type }),
    ]);
    return { emp, sum };
  }, [from, to, type]);
  const emp = data && data.emp;
  const sum = data && data.sum;
  const quick = (days) => { const t = new Date(); const f = new Date(t.getTime() - days * 86400000); setFrom(perfDayStr(f)); setTo(perfDayStr(t)); };
  const thisMonth = () => { const t = new Date(); setFrom(perfDayStr(new Date(t.getFullYear(), t.getMonth(), 1))); setTo(perfDayStr(t)); };

  const rows = emp ? [...emp.employees] : [];
  const val = (r, k) => (k === "name" ? r.user.name.toLowerCase() : k === "onTimeRate" ? (r.onTimeRate == null ? -1 : r.onTimeRate) : r[k]);
  rows.sort((a, b) => { const av = val(a, sort.key), bv = val(b, sort.key); const c = av < bv ? -1 : av > bv ? 1 : 0; return sort.dir === "asc" ? c : -c; });
  const setSortKey = (k) => setSort((s) => ({ key: k, dir: s.key === k && s.dir === "desc" ? "asc" : "desc" }));
  const SortTh = ({ k, children, right }) => (
    <Th className={cx("cursor-pointer select-none hover:text-slate-600", right && "text-right")} ><span onClick={() => setSortKey(k)}>{children}{sort.key === k ? (sort.dir === "desc" ? " ↓" : " ↑") : ""}</span></Th>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2"><TrendingUp size={18} className="text-indigo-600" /><h2 className="text-lg font-semibold text-slate-800">Employee performance</h2>{loading && data ? <Spinner size={14} /> : null}</div>
          <div className="flex gap-1 rounded-lg bg-slate-100 p-1">
            {[["all", "All"], ["production", "Production"], ["dispatch", "Dispatch"]].map(([k, l]) => (
              <button key={k} onClick={() => setType(k)} className={cx("rounded-md px-3 py-1 text-sm font-medium", type === k ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700")}>{l}</button>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <Btn variant="soft" onClick={() => quick(30)}>Last 30 days</Btn>
          <Btn variant="soft" onClick={() => quick(90)}>Last 90 days</Btn>
          <Btn variant="soft" onClick={thisMonth}>This month</Btn>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-md bg-white py-1 px-2 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400" title="From (completed)" />
          <span className="text-slate-400">→</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-md bg-white py-1 px-2 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400" title="To (completed)" />
          {(from || to) && <button onClick={() => { setFrom(""); setTo(""); }} className="text-xs text-slate-400 hover:text-slate-600">clear</button>}
        </div>
      </div>

      <div className="flex items-start gap-2 rounded-lg bg-indigo-50/60 px-3 py-2 text-xs text-slate-600 ring-1 ring-indigo-100">
        <Clock size={14} className="mt-0.5 shrink-0 text-indigo-500" />
        <span>Showing <b>{type === "all" ? "production + dispatch" : type}</b> jobs. Durations are <b>working time</b> — week-offs + company/branch holidays are excluded (holiday-aware), so the unit is <b>working days</b> (raw calendar hours shown alongside). <b>On-time</b> = a job (production PO or delivery) completed on/before its order's required-by date; jobs with no due date (make-to-stock POs / DCs without a required-by) are excluded from the on-time rate. Window is by <b>completion date</b>{emp ? <> — showing <Mono>{emp.from}</Mono> → <Mono>{emp.to}</Mono></> : null}.</span>
      </div>

      {error ? <InlineError message={error} onRetry={reload} /> : loading && !data ? <LoadingBlock label="Computing performance…" /> : (
        <>
          {sum && (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <div className="rounded-xl bg-white p-3 ring-1 ring-slate-200/80"><div className="text-xs text-slate-400">Completed jobs</div><Mono className="text-xl font-semibold text-slate-800">{sum.totalCompleted}</Mono><div className="text-xs text-slate-400">{sum.productionCompleted != null ? sum.productionCompleted + " prod · " + sum.dispatchCompleted + " dispatch" : sum.assignedCompleted + " assigned"}</div></div>
              <div className="rounded-xl bg-white p-3 ring-1 ring-slate-200/80"><div className="text-xs text-slate-400">Employees</div><Mono className="text-xl font-semibold text-slate-800">{sum.employeeCount}</Mono></div>
              <div className="rounded-xl bg-white p-3 ring-1 ring-slate-200/80"><div className="text-xs text-slate-400">Avg working duration</div><Mono className="text-xl font-semibold text-slate-800">{sum.avgWorkingDuration}</Mono><span className="text-xs text-slate-400"> working days</span></div>
              <div className="rounded-xl bg-white p-3 ring-1 ring-slate-200/80"><div className="text-xs text-slate-400">Overall on-time</div><Mono className="text-xl font-semibold text-emerald-700">{sum.onTimeRate == null ? "—" : sum.onTimeRate + "%"}</Mono><div className="text-xs text-slate-400">{sum.onTimeCount}/{sum.onTimeEligible} due-dated</div></div>
            </div>
          )}

          <Card className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-slate-100"><tr>
                <SortTh k="name">Employee</SortTh>
                <Th>Role</Th>
                <SortTh k="completedCount" right>Completed</SortTh>
                <SortTh k="avgWorkingDuration" right>Avg working days</SortTh>
                <SortTh k="avgRawDurationHours" right>Avg raw (h)</SortTh>
                <SortTh k="onTimeRate" right>On-time</SortTh>
                <Th></Th>
              </tr></thead>
              <tbody className="divide-y divide-slate-50">
                {rows.length === 0 ? <tr><Td className="text-slate-400" colSpan={7}>No employees with completed jobs in this window.</Td></tr> : rows.map((r) => (
                  <tr key={r.user.id} className="cursor-pointer hover:bg-slate-50/50" onClick={() => setDrillUser(r.user.id)}>
                    <Td className="font-medium text-slate-800">{r.user.name}</Td>
                    <Td className="text-xs text-slate-500">{r.user.role ? r.user.role.name : "—"}</Td>
                    <Td className="text-right"><Mono className="font-semibold text-slate-800">{r.completedCount}</Mono></Td>
                    <Td className="text-right"><Mono>{r.avgWorkingDuration}</Mono> <span className="text-xs text-slate-400">d</span></Td>
                    <Td className="text-right"><Mono className="text-slate-500">{r.avgRawDurationHours}</Mono></Td>
                    <Td className="text-right">{r.onTimeRate == null ? <span className="text-slate-300">—</span> : <Badge tone={r.onTimeRate >= 80 ? "emerald" : r.onTimeRate >= 50 ? "amber" : "rose"}>{r.onTimeRate}%</Badge>}<div className="text-[10px] text-slate-400">{r.onTimeCount}/{r.onTimeEligible}</div></Td>
                    <Td><span className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600">Report <ArrowRight size={12} /></span></Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}

      <EmployeePerfModal key={drillUser || "none"} userId={drillUser} from={from} to={to} type={type} open={!!drillUser} onClose={() => setDrillUser(null)} />
    </div>
  );
}

/* ============================ Reports ============================
   Phase A: the three SALES tabs (Mother company / Sister company / Order (PO)
   wise) are LIVE from /api/v1/reports/sales (aggregated from real orders + lines
   + invoices). The User KPI / KRA tab is still the mock preview — Phase B wires
   it to real task + performance data (and adds the Transaction ledger tab). */
function SalesFill({ v }) {
  return (<div className="flex items-center gap-2"><div className="h-1.5 w-16 overflow-hidden rounded-full bg-slate-100"><div className="h-full bg-emerald-500" style={{ width: v + "%" }} /></div><Mono className="text-xs text-slate-500">{v}%</Mono></div>);
}
function SalesReport({ level, from, to, search }) {
  const { data, loading, error, reload } = useResource(
    () => reportsApi.sales({ level, from: from || undefined, to: to || undefined }),
    [level, from, to],
  );
  // Guard the transient where useResource still holds the PREVIOUS level's rows
  // during a refetch after `level` changes (their shape differs per level).
  const all = (data && data.level === level && data.rows) || [];
  const s = (search || "").toLowerCase();
  const match = (r) => !s || (level === "order" ? r.orderCode + " " + r.customer.name : level === "sister" ? r.partyCode + " " + r.partyName : r.motherCode + " " + r.motherName).toLowerCase().includes(s);
  const rows = all.filter(match);
  const tot = rows.reduce((a, r) => { a.orders += r.orders || 1; a.ordered += r.orderedQty; a.delivered += r.deliveredQty; a.pending += r.pendingQty; a.val += r.orderValue; a.inv += r.invoicedValue; return a; }, { orders: 0, ordered: 0, delivered: 0, pending: 0, val: 0, inv: 0 });
  if (loading && all.length === 0) return <Card className="p-6"><LoadingBlock label="Loading report…" /></Card>;
  if (error) return <Card className="p-4"><InlineError message={error} onRetry={reload} /></Card>;
  const qtyR = (v) => <Td className="text-right"><Mono>{fmtQty(v)}</Mono></Td>;
  return (
    <Card className="overflow-x-auto">
      <table className="w-full">
        {level === "mother" && <>
          <thead className="border-b border-slate-100"><tr><Th>Mother company</Th><Th className="text-center">Sisters</Th><Th className="text-center">Orders</Th><Th className="text-right">Ordered</Th><Th className="text-right">Delivered</Th><Th className="text-right">Pending</Th><Th>Fulfilment</Th><Th className="text-right">Order value</Th><Th className="text-right">Invoiced</Th></tr></thead>
          <tbody className="divide-y divide-slate-50">
            {rows.length === 0 ? <tr><Td className="text-slate-400" colSpan={9}>No data for this range.</Td></tr> : rows.map((r) => (<tr key={r.motherCode} className="hover:bg-slate-50/50">
              <Td className="font-medium text-slate-800">{r.motherName} <Mono className="ml-1 text-xs text-slate-400">{r.motherCode}</Mono></Td>
              <Td className="text-center"><Mono className="text-slate-500">{r.sisters || "—"}</Mono></Td>
              <Td className="text-center"><Mono>{r.orders}</Mono></Td>
              {qtyR(r.orderedQty)}<Td className="text-right"><Mono className="text-emerald-600">{fmtQty(r.deliveredQty)}</Mono></Td><Td className="text-right"><Mono className="text-amber-600">{fmtQty(r.pendingQty)}</Mono></Td>
              <Td><SalesFill v={r.fulfilmentPct} /></Td>
              <Td className="text-right"><Mono>{fmtMoney(r.orderValue)}</Mono></Td>
              <Td className="text-right"><Mono className="font-semibold text-slate-800">{fmtMoney(r.invoicedValue)}</Mono></Td>
            </tr>))}
          </tbody>
        </>}
        {level === "sister" && <>
          <thead className="border-b border-slate-100"><tr><Th>Customer</Th><Th>Mother</Th><Th className="text-center">Orders</Th><Th className="text-right">Ordered</Th><Th className="text-right">Delivered</Th><Th className="text-right">Pending</Th><Th>Fulfilment</Th><Th className="text-right">Order value</Th><Th className="text-right">Invoiced</Th></tr></thead>
          <tbody className="divide-y divide-slate-50">
            {rows.length === 0 ? <tr><Td className="text-slate-400" colSpan={9}>No data for this range.</Td></tr> : rows.map((r) => (<tr key={r.partyCode} className="hover:bg-slate-50/50">
              <Td className="font-medium text-slate-800">{r.partyName} <Mono className="ml-1 text-xs text-slate-400">{r.partyCode}</Mono></Td>
              <Td className="text-slate-500">{r.motherName || "—"}</Td>
              <Td className="text-center"><Mono>{r.orders}</Mono></Td>
              {qtyR(r.orderedQty)}<Td className="text-right"><Mono className="text-emerald-600">{fmtQty(r.deliveredQty)}</Mono></Td><Td className="text-right"><Mono className="text-amber-600">{fmtQty(r.pendingQty)}</Mono></Td>
              <Td><SalesFill v={r.fulfilmentPct} /></Td>
              <Td className="text-right"><Mono>{fmtMoney(r.orderValue)}</Mono></Td>
              <Td className="text-right"><Mono className="font-semibold text-slate-800">{fmtMoney(r.invoicedValue)}</Mono></Td>
            </tr>))}
          </tbody>
        </>}
        {level === "order" && <>
          <thead className="border-b border-slate-100"><tr><Th>Order (PO)</Th><Th>Date</Th><Th>Customer</Th><Th>Mother</Th><Th className="text-right">Ordered</Th><Th className="text-right">Delivered</Th><Th className="text-right">Pending</Th><Th>Status</Th><Th className="text-right">Order value</Th><Th className="text-right">Invoiced</Th></tr></thead>
          <tbody className="divide-y divide-slate-50">
            {rows.length === 0 ? <tr><Td className="text-slate-400" colSpan={10}>No data for this range.</Td></tr> : rows.map((r) => (<tr key={r.orderCode} className="hover:bg-slate-50/50">
              <Td><Mono className="font-medium text-slate-800">{r.orderCode}</Mono></Td>
              <Td className="text-slate-500">{fmtDate(r.orderDate)}</Td>
              <Td className="text-slate-700">{r.customer.name}</Td>
              <Td className="text-slate-500">{r.motherName || "—"}</Td>
              {qtyR(r.orderedQty)}<Td className="text-right"><Mono className="text-emerald-600">{fmtQty(r.deliveredQty)}</Mono></Td><Td className="text-right"><Mono className="text-amber-600">{fmtQty(r.pendingQty)}</Mono></Td>
              <Td><Badge tone={statusTone(r.status)}>{r.status}</Badge></Td>
              <Td className="text-right"><Mono>{fmtMoney(r.orderValue)}</Mono></Td>
              <Td className="text-right"><Mono className="font-semibold text-slate-800">{fmtMoney(r.invoicedValue)}</Mono></Td>
            </tr>))}
          </tbody>
        </>}
        {rows.length > 0 && <tfoot><tr className="border-t border-slate-200 bg-slate-50/60 font-semibold">
          <Td className="text-slate-700">Total</Td>
          {level === "order"
            ? <Td colSpan={3} /> /* Date · Customer · Mother */
            : <><Td /><Td className="text-center"><Mono>{tot.orders}</Mono></Td></> /* Sisters/Mother · Orders */}
          <Td className="text-right"><Mono>{fmtQty(tot.ordered)}</Mono></Td>
          <Td className="text-right"><Mono className="text-emerald-600">{fmtQty(tot.delivered)}</Mono></Td>
          <Td className="text-right"><Mono className="text-amber-600">{fmtQty(tot.pending)}</Mono></Td>
          <Td>{level === "order" ? "" : <SalesFill v={tot.ordered ? Math.round((tot.delivered / tot.ordered) * 1000) / 10 : 0} />}</Td>
          <Td className="text-right"><Mono>{fmtMoney(tot.val)}</Mono></Td>
          <Td className="text-right"><Mono className="text-slate-800">{fmtMoney(tot.inv)}</Mono></Td>
        </tr></tfoot>}
      </table>
    </Card>
  );
}

// KPI / KRA scorecard (Phase B, LIVE): per-employee TASK aggregate + the REUSED
// performance domain (process jobs + on-time) — the process columns are identical
// to the Performance screen for the same window (reused, not recomputed).
function KpiScorecard({ from, to }) {
  const { data, loading, error, reload } = useResource(() => reportsApi.kpi({ from: from || undefined, to: to || undefined }), [from, to]);
  const rows = (data && data.rows) || [];
  const rateBadge = (v) => (v == null ? <span className="text-xs text-slate-300">—</span> : <Badge tone={v >= 90 ? "emerald" : v >= 70 ? "amber" : "rose"}>{v}%</Badge>);
  if (loading && rows.length === 0) return <Card className="p-6"><LoadingBlock label="Loading scorecard…" /></Card>;
  if (error) return <Card className="p-4"><InlineError message={error} onRetry={reload} /></Card>;
  return (
    <Card className="overflow-x-auto">
      <table className="w-full">
        <thead className="border-b border-slate-100"><tr>
          <Th>Employee</Th>
          <Th className="text-center">Assigned</Th><Th className="text-center">Done</Th><Th className="text-center">Pending</Th><Th className="text-center">Overdue</Th><Th>Task on-time</Th>
          <Th className="text-center">Jobs (prod·disp)</Th><Th className="text-center">Avg w.days</Th><Th>Process on-time</Th>
          <Th>Overall SLA</Th>
        </tr></thead>
        <tbody className="divide-y divide-slate-50">
          {rows.map((r) => (<tr key={r.userId} className="hover:bg-slate-50/50">
            <Td className="font-medium text-slate-800">{r.name}</Td>
            <Td className="text-center"><Mono className="text-slate-700">{r.assigned}</Mono></Td>
            <Td className="text-center"><Mono className="text-emerald-600">{r.completed}</Mono></Td>
            <Td className="text-center"><Mono className="text-slate-600">{r.pending}</Mono></Td>
            <Td className="text-center">{r.overdue > 0 ? <Badge tone="rose"><AlertTriangle size={10} /> {r.overdue}</Badge> : <Mono className="text-slate-300">0</Mono>}</Td>
            <Td>{rateBadge(r.taskOnTimeRate)}</Td>
            <Td className="text-center"><Mono className="text-slate-700">{r.jobsCompleted}</Mono>{r.jobsCompleted > 0 && <span className="text-xs text-slate-400"> · {r.productionCount}p·{r.dispatchCount}d</span>}</Td>
            <Td className="text-center"><Mono className="text-slate-600">{r.avgWorkingDays || "—"}</Mono></Td>
            <Td>{rateBadge(r.processOnTimeRate)}</Td>
            <Td>{rateBadge(r.overallOnTimeRate)}</Td>
          </tr>))}
          {rows.length === 0 && <tr><Td className="text-slate-400" colSpan={10}>No employees.</Td></tr>}
        </tbody>
      </table>
      <p className="px-4 py-2.5 text-xs text-slate-400">Combined scorecard: <b>Tasks</b> (from the real task backend) + <b>Process</b> (jobs completed &amp; on-time — the SAME numbers as the Performance screen, reused not recomputed). Overall SLA = count-weighted blend of task and process on-time. The Performance screen remains the deep process-only report.</p>
    </Card>
  );
}

// Company-wise TRANSACTION ledger (Phase B, LIVE): a customer's SO→DC→INV document
// timeline — distinct from Customers' financial Group ledger (invoice amounts +
// balance). A running `balance` (= cumulative invoiced) is the seam for a future
// payments/financial ledger.
function LedgerReport({ from, to }) {
  // Customer list comes from the REPORTS domain (reports-gated), not /parties
  // (customers-module-gated) — so a reports user without the customers module
  // (e.g. a production manager) still gets a populated customer picker.
  const partiesRes = useResource(() => reportsApi.ledgerCustomers(), []);
  const flat = (partiesRes.data && partiesRes.data.parties) || [];
  const [partyCode, setPartyCode] = useState("");
  const { data, loading, error, reload } = useResource(
    () => (partyCode ? reportsApi.ledger({ partyCode, from: from || undefined, to: to || undefined }) : Promise.resolve(null)),
    [partyCode, from, to],
  );
  const rows = (data && data.rows) || [];
  const typeBadge = (t) => (t === "order" ? <Badge tone="amber">Order</Badge> : t === "delivery" ? <Badge tone="sky">Delivery</Badge> : <Badge tone="violet">Invoice</Badge>);
  const IN = "rounded-md bg-white py-1.5 px-2 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400";
  return (
    <Card>
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-3">
        <FileText size={16} className="text-amber-600" /><span className="text-sm font-semibold text-slate-700">Transaction ledger</span>
        <select value={partyCode} onChange={(e) => setPartyCode(e.target.value)} className={cx(IN, "ml-2")}><option value="">Select a customer…</option>{flat.map((p) => <option key={p.code} value={p.code}>{p.name} ({p.code})</option>)}</select>
        {data && <span className="ml-auto text-xs text-slate-400">{data.total} event(s) · invoiced {fmtMoney(data.totalInvoiced)}</span>}
      </div>
      {error ? <div className="px-4 pt-2"><InlineError message={error} onRetry={reload} /></div> : null}
      {!partyCode ? <p className="px-4 py-6 text-sm text-slate-400">Pick a customer to see their document flow — sales order → delivery challan → invoice, in chronological order.</p>
        : loading && rows.length === 0 ? <LoadingBlock label="Loading ledger…" />
        : rows.length === 0 ? <p className="px-4 py-6 text-sm text-slate-400">No transactions in this range.</p>
        : <div className="overflow-x-auto"><table className="w-full">
            <thead className="border-b border-slate-50"><tr><Th>Date</Th><Th>Type</Th><Th>Document</Th><Th>Description</Th><Th className="text-right">Qty</Th><Th className="text-right">Amount</Th><Th className="text-right">Balance</Th></tr></thead>
            <tbody className="divide-y divide-slate-50">
              {rows.map((e, i) => (<tr key={i} className="hover:bg-slate-50/50">
                <Td className="text-slate-500">{fmtDate(e.date)}</Td>
                <Td>{typeBadge(e.type)}</Td>
                <Td><Mono className="font-medium text-slate-800">{e.docCode}</Mono> <span className="text-xs text-slate-400">{e.status}</span></Td>
                <Td className="text-slate-500">{e.description}</Td>
                <Td className="text-right">{e.qty != null ? <Mono>{fmtQty(e.qty)}</Mono> : <span className="text-slate-300">—</span>}</Td>
                <Td className="text-right">{e.amount != null ? <Mono className={e.type === "invoice" ? "font-semibold text-slate-800" : "text-slate-500"}>{fmtMoney(e.amount)}</Mono> : <span className="text-slate-300">—</span>}</Td>
                <Td className="text-right"><Mono className="text-slate-600">{fmtMoney(e.balance)}</Mono></Td>
              </tr>))}
            </tbody>
          </table></div>}
      <p className="px-4 py-2.5 text-xs text-slate-400">Operational document flow (what happened). Distinct from the Customers → Group ledger (invoice amounts + balance). <b>Balance</b> = cumulative invoiced (debits); a future payments module adds credit events → a true running amount-due. Payments aren't modelled yet.</p>
    </Card>
  );
}

// FINANCIAL STATEMENT (Phase 5) — the client-facing statement: the SO→DC→INV
// timeline PLUS payment credits, a running amount-due, a summary (previous
// pending / invoiced / received / balance due / advance / pipeline), and a CSV
// download. A mother party aggregates the whole group.
function StatementReport({ from, to }) {
  const partiesRes = useResource(() => reportsApi.ledgerCustomers(), []);
  const flat = (partiesRes.data && partiesRes.data.parties) || [];
  const [partyCode, setPartyCode] = useState("");
  const { data, loading, error, reload } = useResource(
    () => (partyCode ? reportsApi.statement({ partyCode, from: from || undefined, to: to || undefined }) : Promise.resolve(null)),
    [partyCode, from, to],
  );
  const rows = (data && data.rows) || [];
  const sm = data && data.summary;
  const isGroup = !!(data && data.scope && data.scope.isGroup);
  const typeBadge = (t) => t === "order" ? <Badge tone="amber">Order</Badge> : t === "delivery" ? <Badge tone="sky">Delivery</Badge> : t === "invoice" ? <Badge tone="violet">Invoice</Badge> : <Badge tone="emerald">Payment</Badge>;
  const IN = "rounded-md bg-white py-1.5 px-2 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400";

  const downloadCsv = () => {
    if (!data) return;
    const esc = (v) => { const x = String(v ?? ""); return /[",\n]/.test(x) ? '"' + x.replace(/"/g, '""') + '"' : x; };
    const L = [];
    L.push(["Statement", data.party.name + " (" + data.party.code + ")"]);
    if (isGroup) L.push(["Group", data.scope.partyCodes.join(" + ")]);
    L.push(["Period", (from || "beginning") + " to " + (to || "today")]);
    L.push([]);
    L.push(["Previous pending", sm.previousPending]);
    L.push(["Invoiced (period)", sm.invoiced]);
    L.push(["Received (period)", sm.received]);
    L.push(["Balance due (closing)", sm.balanceDue]);
    L.push(["Advance on account", sm.advance]);
    L.push(["Running / not delivered", sm.runningNotDelivered]);
    L.push([]);
    L.push(["Date", "Type", "Document", isGroup ? "Billed to" : null, "Description", "Debit", "Credit", "Balance"].filter((x) => x !== null));
    for (const e of rows) L.push([fmtDate(e.date), e.type, e.docCode, isGroup ? e.party.code : null, e.description, e.debit ?? "", e.credit ?? "", e.balance].filter((x) => x !== null));
    const csv = L.map((r) => r.map(esc).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "statement-" + data.party.code + (from ? "-" + from : "") + (to ? "-" + to : "") + ".csv";
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const Stat = ({ label, value, tone }) => (
    <div className="rounded-lg bg-slate-50 px-3 py-2 ring-1 ring-slate-100">
      <div className="text-[11px] font-medium uppercase tracking-wide text-slate-400">{label}</div>
      <div className={cx("mono text-sm font-semibold", tone || "text-slate-800")}>{fmtMoney(value)}</div>
    </div>
  );
  return (
    <Card>
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-3">
        <FileText size={16} className="text-amber-600" /><span className="text-sm font-semibold text-slate-700">Financial statement</span>
        <select value={partyCode} onChange={(e) => setPartyCode(e.target.value)} className={cx(IN, "ml-2")}><option value="">Select a customer…</option>{flat.map((p) => <option key={p.code} value={p.code}>{p.name} ({p.code}){p.parentCode ? "" : " ▸"}</option>)}</select>
        {data && <Btn variant="soft" className="ml-auto" onClick={downloadCsv}><Download size={14} /> Download statement (CSV)</Btn>}
      </div>
      {error ? <div className="px-4 pt-2"><InlineError message={error} onRetry={reload} /></div> : null}
      {!partyCode ? <p className="px-4 py-6 text-sm text-slate-400">Pick a customer (or a mother company ▸ for the whole group) to see their statement — invoices, payments, running balance, and what's still in the pipeline.</p>
        : loading && rows.length === 0 ? <LoadingBlock label="Loading statement…" />
        : !sm ? <p className="px-4 py-6 text-sm text-slate-400">No activity for this customer.</p>
        : <>
          {isGroup && <div className="mx-4 mt-3 rounded-md bg-violet-50 px-3 py-1.5 text-xs text-violet-800 ring-1 ring-violet-200">Group statement — aggregates {data.scope.partyCodes.join(", ")}.</div>}
          <div className="grid gap-2 px-4 py-3 sm:grid-cols-3 lg:grid-cols-6">
            <Stat label="Previous pending" value={sm.previousPending} tone="text-slate-500" />
            <Stat label="Invoiced" value={sm.invoiced} />
            <Stat label="Received" value={sm.received} tone="text-emerald-600" />
            <Stat label="Balance due" value={sm.balanceDue} tone={sm.balanceDue > 0.001 ? "text-rose-600" : "text-slate-800"} />
            <Stat label="On account" value={sm.advance} tone="text-sky-600" />
            <Stat label="Running (pipeline)" value={sm.runningNotDelivered} tone="text-amber-600" />
          </div>
          {rows.length === 0 ? <p className="px-4 py-6 text-sm text-slate-400">No transactions in this date range (opening balance {fmtMoney(sm.previousPending)}).</p>
          : <div className="overflow-x-auto"><table className="w-full">
            <thead className="border-b border-slate-50"><tr><Th>Date</Th><Th>Type</Th><Th>Document</Th>{isGroup && <Th>Billed to</Th>}<Th>Description</Th><Th className="text-right">Debit</Th><Th className="text-right">Credit</Th><Th className="text-right">Balance</Th></tr></thead>
            <tbody className="divide-y divide-slate-50">
              {from && <tr className="bg-slate-50/40"><Td className="text-slate-400" colSpan={isGroup ? 5 : 4}>Previous balance brought forward</Td><Td className="text-right text-slate-300">—</Td><Td className="text-right text-slate-300">—</Td><Td className="text-right"><Mono className="font-semibold text-slate-600">{fmtMoney(sm.previousPending)}</Mono></Td></tr>}
              {rows.map((e, i) => (<tr key={i} className="hover:bg-slate-50/50">
                <Td className="text-slate-500">{fmtDate(e.date)}</Td>
                <Td>{typeBadge(e.type)}</Td>
                <Td><Mono className="font-medium text-slate-800">{e.docCode}</Mono></Td>
                {isGroup && <Td className="text-xs text-slate-500"><Mono>{e.party.code}</Mono></Td>}
                <Td className="text-xs text-slate-500">{e.description}</Td>
                <Td className="text-right">{e.debit != null ? <Mono className="font-semibold text-slate-800">{fmtMoney(e.debit)}</Mono> : <span className="text-slate-300">—</span>}</Td>
                <Td className="text-right">{e.credit != null ? <Mono className="font-semibold text-emerald-600">{fmtMoney(e.credit)}</Mono> : <span className="text-slate-300">—</span>}</Td>
                <Td className="text-right"><Mono className={e.balance > 0.001 ? "text-slate-700" : "text-slate-400"}>{fmtMoney(e.balance)}</Mono></Td>
              </tr>))}
            </tbody>
          </table></div>}
        </>}
      <p className="px-4 py-2.5 text-xs text-slate-400"><b>Balance</b> = amount due (invoices − payments). <b>On account</b> = advance the customer has paid beyond their invoices. <b>Running (pipeline)</b> = ordered but not yet delivered/invoiced. Payments in green are credits. Download gives a CSV for the selected date range.</p>
    </Card>
  );
}

function ReportsView() {
  const [tab, setTab] = useState("mother");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [search, setSearch] = useState("");
  const tabs = [["mother", "Mother company"], ["sister", "Sister company"], ["po", "Order (PO) wise"], ["kpi", "User KPI / KRA"], ["ledger", "Transaction ledger"], ["statement", "Statement"]];
  const levelOf = { mother: "mother", sister: "sister", po: "order" };
  const isSales = tab === "mother" || tab === "sister" || tab === "po";
  const IN = "rounded-md bg-white py-1.5 px-2 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400";
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex w-min gap-1 rounded-lg bg-slate-100 p-1">{tabs.map(([k, l]) => <button key={k} onClick={() => setTab(k)} className={cx("whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium", tab === k ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700")}>{l}</button>)}</div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {isSales && <div className="relative"><Search size={14} className="absolute left-2.5 top-2 text-slate-400" /><input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Customer / order…" className={cx(IN, "w-40 pl-8")} /></div>}
          <span className="text-xs text-slate-400">{tab === "kpi" ? "Window" : "Date"}</span>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={IN} title="From" /><span className="text-xs text-slate-300">→</span><input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={IN} title="To" />
          {(from || to || search) && <button onClick={() => { setFrom(""); setTo(""); setSearch(""); }} className="text-xs text-slate-400 hover:text-slate-600">clear</button>}
        </div>
      </div>
      {isSales && <SalesReport level={levelOf[tab]} from={from} to={to} search={search} />}
      {tab === "kpi" && <KpiScorecard from={from} to={to} />}
      {tab === "ledger" && <LedgerReport from={from} to={to} />}
      {tab === "statement" && <StatementReport from={from} to={to} />}
    </div>
  );
}

/* ============================ Roles & departments ============================ */
let ROLES = {
  owner:      { name: "Owner / Admin",       dept: "Management", nav: ["dashboard", "monitor", "tasks", "people", "queue", "orders", "production", "workorders", "routing", "purchases", "inventory", "batches", "bom", "deliveries", "invoices", "customers", "reports", "settings"], can: { all: true } },
  supervisor: { name: "Supervisor (Monitor)", dept: "Management", nav: ["dashboard", "monitor", "tasks", "people", "orders", "production", "workorders", "routing", "inventory", "bom", "deliveries", "invoices", "customers", "reports"], can: { monitor: true } },
  sales:      { name: "Sales Exec",          dept: "Sales",      nav: ["queue", "tasks", "orders", "customers"],                                  can: { createOrder: true } },
  stores:     { name: "Store Manager",       dept: "Stores",     nav: ["queue", "tasks", "purchases", "inventory", "workorders", "batches", "bom", "reports"],   can: { issue: true, buy: true } },
  qc:         { name: "QC Department",       dept: "Quality Control", nav: ["queue", "tasks", "batches", "purchases", "inventory", "reports"],                   can: { fgqc: true } },
  prodmgr:    { name: "Production Manager",   dept: "Production", nav: ["dashboard", "queue", "orders", "production", "workorders", "inventory", "batches", "reports"], can: { fulfill: true, stockPO: true, prodmgr: true, startProd: true, advanceProd: true } },
  planner:    { name: "Production Planner",  dept: "Planning (PPC)", nav: ["queue", "tasks", "orders", "production", "workorders", "routing", "purchases", "inventory", "reports"], can: {} },
  production: { name: "Production Op",        dept: "Production", nav: ["queue", "tasks", "production", "workorders", "routing"],                                can: { startProd: true, advanceProd: true } },
  dispatch:   { name: "Dispatch In-charge",  dept: "Dispatch",   nav: ["queue", "tasks", "orders", "deliveries"],                                 can: { dispatch: true, deliver: true } },
  accounts:   { name: "Accounts",            dept: "Accounts",   nav: ["queue", "tasks", "deliveries", "invoices", "purchases", "reports"],       can: { invoice: true } },
};
const can = (role, key) => { const r = ROLES[role]; return !!r && (r.can.all || !!r.can[key]); };

/* ============================ My Queue (role inbox) ============================ */
/* ============================ My Queue — LIVE (real backend) ============================
   Regular Task tab = the current user's real assigned tasks (/tasks?assigneeId=me).
   Workflow tab = process work pending for THIS user, composed from the real
   order/production/dispatch/invoice/purchase endpoints and routed INTO the
   existing live action flows (StockDecisionModal / PoDetailModal / LiveIssueModal
   / NewDispatchModal / generateInvoice / qcPurchase) — My Queue does not rebuild
   those, it points at them.

   OWNERSHIP PROVISION — every workflow stage declares which permission makes it
   "your kind of work" and an ownership model:
     'role'     → shown to ANY user whose role holds the permission (role-wide).
     'assigned' → shown to the user the items are ASSIGNED to (endpoint filtered
                  by assignedUserId = current user).
   Today only PRODUCTION is 'assigned'; the rest are 'role'. To switch another
   stage to user-based later: flip its WF_OWNERSHIP entry to 'assigned' AND make
   its endpoint accept an assignedUserId filter (production.listPOs already does;
   the orders/dispatch/invoice/purchase reads would need that filter added). The
   group receives `assignedUserId` (= me when 'assigned', else null) and applies
   it in its fetch — production honors it now; the others accept-but-ignore it
   until their endpoints support it. */
const WF_OWNERSHIP = { stock: "role", issue: "role", prod: "assigned", dispatch: "role", invoice: "role", purchaseqc: "role" };
// key → { title, Icon, perms:[...] (any grants the stage) }. Order = display order.
const WORKFLOW_STAGES = [
  { key: "stock", title: "Stock decision", Icon: ClipboardList, perms: ["fulfill"] },
  { key: "issue", title: "Issue materials", Icon: Boxes, perms: ["issue"] },
  { key: "prod", title: "Production (assigned to me)", Icon: Factory, perms: ["startProd", "advanceProd"] },
  { key: "dispatch", title: "Ready to dispatch", Icon: Truck, perms: ["dispatch"] },
  { key: "invoice", title: "To invoice", Icon: FileText, perms: ["invoice"] },
  { key: "purchaseqc", title: "Purchase QC", Icon: ShieldCheck, perms: ["fgqc"] },
];

// Collapsible group shell + a compact row (shared by every workflow group).
function QueueGroup({ Icon, title, count, open, onToggle, children }) {
  return (
    <Card className="overflow-hidden">
      <button onClick={onToggle} className="flex w-full items-center justify-between gap-2 px-4 py-3 hover:bg-slate-50">
        <span className="flex items-center gap-2"><Icon size={16} className="text-amber-600" /><span className="text-sm font-semibold text-slate-700">{title}</span><span className={cx("rounded-full px-2 py-0.5 text-xs font-medium", count > 0 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-400")}>{count}</span></span>
        <ChevronRight size={16} className={cx("text-slate-400 transition", open ? "rotate-90" : "")} />
      </button>
      {open && <div className="space-y-2 border-t border-slate-50 p-3">{children}</div>}
    </Card>
  );
}
function QRow({ left, sub, action }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg px-3 py-2 ring-1 ring-slate-200">
      <div className="min-w-0"><div className="truncate text-sm font-medium text-slate-700">{left}</div>{sub && <div className="truncate text-xs text-slate-400">{sub}</div>}</div>
      {action}
    </div>
  );
}
const qEmpty = (loading, len, msg) => (loading && len === 0 ? <p className="text-sm text-slate-400">Loading…</p> : len === 0 ? <p className="text-sm text-slate-400">{msg}</p> : null);

// 1) STOCK DECISION (perm 'fulfill', role) — SO lines still awaiting a decision.
function StockDecisionGroup({ open, onToggle, onCount, st, party, dispatch }) {
  const { data, loading, error, reload } = useResource(() => ordersApi.pendingDecisions(), []);
  const lines = (data && data.lines) || [];
  const [decide, setDecide] = useState(null);
  React.useEffect(() => { if (data) onCount("stock", lines.length); }, [data]); // eslint-disable-line
  return (
    <QueueGroup Icon={ClipboardList} title="Stock decision" count={lines.length} open={open} onToggle={onToggle}>
      {error ? <InlineError message={error} onRetry={reload} /> : qEmpty(loading, lines.length, "Nothing pending.") || lines.map((l) => (
        <QRow key={l.orderCode + l.lineCode} left={`${l.item.name} · ${fmtQty(l.pendingQty)} Kg`} sub={`${l.orderCode} · ${l.party.name}`}
          action={<Btn variant="soft" onClick={() => setDecide(l)}><Package size={13} /> Dispatch / Produce</Btn>} />
      ))}
      {decide && <StockDecisionModal key={decide.lineCode} st={st} party={party} dispatch={dispatch} open live
        order={{ id: decide.orderCode, partyId: decide.party.code }}
        line={{ id: decide.lineCode, itemId: decide.item.code, itemName: decide.item.name, qtyOrdered: decide.qtyOrdered, qtyAllocated: decide.qtyAllocated, qtyDispatched: 0, qtyDelivered: 0 }}
        onClose={() => setDecide(null)} onAllocated={() => { setDecide(null); reload(); }} />}
    </QueueGroup>
  );
}

// 2) ISSUE MATERIALS (perm 'issue', role) — WO-released POs with RM still to issue.
function IssueGroup({ open, onToggle, onCount, auth }) {
  // Issue-permission-gated endpoint (a stores user lacks the production module,
  // so it can't use listPOs); the server already applies the pending filter.
  const { data, loading, error, reload } = useResource(() => productionApi.pendingIssue(), []);
  const toIssue = (data && data.orders) || [];
  const [issueCode, setIssueCode] = useState(null);
  React.useEffect(() => { if (data) onCount("issue", toIssue.length); }, [data]); // eslint-disable-line
  return (
    <QueueGroup Icon={Boxes} title="Issue materials" count={toIssue.length} open={open} onToggle={onToggle}>
      {error ? <InlineError message={error} onRetry={reload} /> : qEmpty(loading, toIssue.length, "No WO-released PO has RM pending.") || toIssue.map((p) => (
        <QRow key={p.code} left={`${p.code} · ${p.item.name}`} sub={`${p.woCode} · issued ${fmtQty(p.issuedQty || 0)}/${fmtQty(p.plannedQty)} Kg · ${p.status}`}
          action={<Btn variant="soft" onClick={() => setIssueCode(p.code)}><Package size={13} /> Issue RM</Btn>} />
      ))}
      <LiveIssueModal key={issueCode || "noissue"} code={issueCode} open={!!issueCode} onClose={() => setIssueCode(null)} onChanged={reload} auth={auth} />
    </QueueGroup>
  );
}

// 3) PRODUCTION (perm 'startProd'/'advanceProd', ASSIGNED) — only POs assigned to me.
function ProductionGroup({ open, onToggle, onCount, assignedUserId, auth, role }) {
  // ownership='assigned' → filter to me; if the flag were flipped to 'role',
  // assignedUserId arrives null and this shows every active PO (role-wide).
  const { data, loading, error, reload } = useResource(() => productionApi.listPOs({ assignedUserId: assignedUserId || undefined, limit: 200 }), [assignedUserId]);
  const active = ((data && data.orders) || []).filter((p) => p.status === "Planned" || p.status === "In Production");
  const [detailCode, setDetailCode] = useState(null);
  const [issueCode, setIssueCode] = useState(null);
  React.useEffect(() => { if (data) onCount("prod", active.length); }, [data]); // eslint-disable-line
  return (
    <QueueGroup Icon={Factory} title="Production (assigned to me)" count={active.length} open={open} onToggle={onToggle}>
      {error ? <InlineError message={error} onRetry={reload} /> : qEmpty(loading, active.length, "No production assigned to you.") || active.map((p) => (
        <QRow key={p.code} left={`${p.code} · ${p.item.name}`} sub={`${p.status}${p.currentStep ? " · " + p.currentStep.workCenter + " (" + (p.stepIdx + 1) + ")" : ""} · ${fmtQty(p.plannedQty)} Kg`}
          action={<Btn variant="soft" onClick={() => setDetailCode(p.code)}>{p.status === "Planned" ? <><Factory size={13} /> Start</> : <>Advance <ChevronRight size={13} /></>}</Btn>} />
      ))}
      <PoDetailModal key={detailCode || "nodetail"} code={detailCode} auth={auth} role={role} open={!!detailCode} onClose={() => setDetailCode(null)} onChanged={reload} onIssue={(c) => { setDetailCode(null); setIssueCode(c); }} />
      <LiveIssueModal key={issueCode || "noissue"} code={issueCode} open={!!issueCode} onClose={() => setIssueCode(null)} onChanged={reload} auth={auth} />
    </QueueGroup>
  );
}

// 4) READY TO DISPATCH (perm 'dispatch', role) — allocated-but-unshipped lines.
function DispatchGroup({ open, onToggle, onCount }) {
  const { data, loading, error, reload } = useResource(() => dispatchApi.listDispatchable(), []);
  const rows = (data && data.dispatchable) || [];
  const [dispOpen, setDispOpen] = useState(false);
  React.useEffect(() => { if (data) onCount("dispatch", rows.length); }, [data]); // eslint-disable-line
  return (
    <QueueGroup Icon={Truck} title="Ready to dispatch" count={rows.length} open={open} onToggle={onToggle}>
      {error ? <InlineError message={error} onRetry={reload} /> : <>
        {rows.length > 0 && <Btn className="w-full" onClick={() => setDispOpen(true)}><Plus size={14} /> New dispatch (challan)</Btn>}
        {qEmpty(loading, rows.length, "Nothing allocated awaiting dispatch.") || rows.map((r) => (
          <QRow key={r.orderCode + r.lineCode} left={`${r.item.name} · ${fmtQty(r.dispatchable)} Kg`} sub={`${r.orderCode} · ${r.party.name}`} />
        ))}
      </>}
      <NewDispatchModal open={dispOpen} onClose={() => setDispOpen(false)} onCreated={() => { setDispOpen(false); reload(); }} />
    </QueueGroup>
  );
}

// 5) TO INVOICE (perm 'invoice', role) — delivered challans not yet invoiced.
function InvoiceGroup({ open, onToggle, onCount }) {
  // qs() drops invoiced:false, so filter the not-yet-invoiced client-side.
  const { data, loading, error, reload } = useResource(() => dispatchApi.listDeliveries({ status: "Delivered" }), []);
  const dcs = ((data && data.deliveries) || []).filter((d) => !d.invoiced);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState("");
  React.useEffect(() => { if (data) onCount("invoice", dcs.length); }, [data]); // eslint-disable-line
  const gen = async (code) => { setBusy(code); setErr(""); try { await invoicesApi.generateInvoice(code); await reload(); } catch (e) { setErr(e instanceof ApiError ? e.message : "Invoice failed."); } finally { setBusy(null); } };
  return (
    <QueueGroup Icon={FileText} title="To invoice" count={dcs.length} open={open} onToggle={onToggle}>
      {error ? <InlineError message={error} onRetry={reload} /> : <>
        {err ? <InlineError message={err} /> : null}
        {qEmpty(loading, dcs.length, "Nothing to invoice.") || dcs.map((d) => (
          <QRow key={d.code} left={d.code} sub={`delivered · ${fmtQty(d.totalQty)} Kg`}
            action={<Btn variant="soft" disabled={busy === d.code} onClick={() => gen(d.code)}><FileText size={13} /> {busy === d.code ? "…" : "Invoice"}</Btn>} />
        ))}
      </>}
    </QueueGroup>
  );
}

// 6) PURCHASE QC (perm 'fgqc', role) — inbound purchases awaiting QC.
function PurchaseQcGroup({ open, onToggle, onCount }) {
  const { data, loading, error, reload } = useResource(() => purchasesApi.listPurchases({ status: "QC Pending" }), []);
  const purs = (data && data.purchases) || [];
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState("");
  React.useEffect(() => { if (data) onCount("purchaseqc", purs.length); }, [data]); // eslint-disable-line
  const qc = async (code, pass) => { setBusy(code); setErr(""); try { await purchasesApi.qcPurchase(code, pass); await reload(); } catch (e) { setErr(e instanceof ApiError ? e.message : "QC action failed."); } finally { setBusy(null); } };
  return (
    <QueueGroup Icon={ShieldCheck} title="Purchase QC" count={purs.length} open={open} onToggle={onToggle}>
      {error ? <InlineError message={error} onRetry={reload} /> : <>
        {err ? <InlineError message={err} /> : null}
        {qEmpty(loading, purs.length, "No purchase awaiting QC.") || purs.map((p) => (
          <QRow key={p.code} left={`${p.code} · ${p.supplier}`} sub={`${p.lineCount} line(s) awaiting QC`}
            action={<div className="flex gap-1.5"><Btn variant="soft" disabled={busy === p.code} onClick={() => qc(p.code, false)}><X size={12} /> Reject</Btn><Btn variant="soft" disabled={busy === p.code} onClick={() => qc(p.code, true)}><CheckCircle2 size={12} /> QC pass</Btn></div>} />
        ))}
      </>}
    </QueueGroup>
  );
}

const QUEUE_GROUP_COMPONENT = { stock: StockDecisionGroup, issue: IssueGroup, prod: ProductionGroup, dispatch: DispatchGroup, invoice: InvoiceGroup, purchaseqc: PurchaseQcGroup };

// Regular Task tab — the current user's REAL assigned tasks (occurrences included,
// templates excluded by the backend). Reuses the Tasks-screen detail/complete flow.
function RegularTasksPanel({ myId, auth, onCount }) {
  const { data, loading, error, reload } = useResource(() => (myId ? tasksApi.listTasks({ assigneeId: myId, limit: 200 }) : Promise.resolve({ tasks: [], total: 0 })), [myId]);
  const list = (data && data.tasks) || [];
  const active = list.filter((t) => t.status !== "closed");
  const [detailId, setDetailId] = useState(null);
  const isManager = !!(auth && ((auth.role && auth.role.permissions && auth.role.permissions.includes("*")) || (auth.user && auth.user.isPlatformAdmin)));
  React.useEffect(() => { if (data) onCount(active.length); }, [data]); // eslint-disable-line
  if (loading && list.length === 0) return <Card className="p-6"><LoadingBlock label="Loading your tasks…" /></Card>;
  if (error) return <Card className="p-4"><InlineError message={error} onRetry={reload} /></Card>;
  if (active.length === 0) return <Card className="p-6 text-sm text-slate-400">You have no assigned tasks.</Card>;
  return (
    <>
      <div className="grid gap-3 sm:grid-cols-2">
        {active.map((t) => (
          <button key={t.id} onClick={() => setDetailId(t.id)} className="rounded-xl bg-white p-4 text-left ring-1 ring-slate-200 transition hover:ring-amber-300">
            <div className="flex items-start justify-between gap-2"><span className="text-sm font-semibold text-slate-800">{t.title}</span>{taskStatusBadge(t)}</div>
            {t.description && <div className="mt-1 truncate text-xs text-slate-500">{t.description}</div>}
            <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs text-slate-400">{taskKindBadge(t)}<span className="inline-flex items-center gap-1"><Calendar size={11} /> {fmtDate(t.dueDate)}</span>{t.attachmentCount > 0 && <span className="inline-flex items-center gap-1"><Download size={11} /> {t.attachmentCount}</span>}</div>
            <div className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-amber-700">Open &amp; update <ChevronRight size={12} /></div>
          </button>
        ))}
      </div>
      {detailId && <TaskDetailLive id={detailId} open onClose={() => setDetailId(null)} onChanged={reload} myId={myId} isManager={isManager} />}
    </>
  );
}

function MyQueue({ st, dispatch, role, me, go, party, auth }) {
  const myId = auth && auth.user ? auth.user.id : null;
  const perms = (auth && auth.role && auth.role.permissions) || [];
  const isAdmin = perms.includes("*") || !!(auth && auth.user && auth.user.isPlatformAdmin);
  const hasPerm = (p) => isAdmin || perms.includes(p);
  const [qtab, setQtab] = useState("regular");
  const [openGroups, setOpenGroups] = useState({});
  const [counts, setCounts] = useState({});
  const [taskCount, setTaskCount] = useState(0);
  const setCount = React.useCallback((key, n) => setCounts((c) => (c[key] === n ? c : { ...c, [key]: n })), []);

  // A stage is visible iff the user holds one of its permissions (role gate).
  // For 'assigned' stages the ITEMS are additionally filtered to the user.
  //
  // EXCEPT Production: a PO's assignee must find it here even with NO production
  // permission, or the assignment is invisible and the work is lost. Probe only
  // for users the permission gate would exclude (listPos self-scopes, so this
  // call is allowed for anyone); permission-holders skip the fetch entirely.
  const prodByPerm = hasPerm("startProd") || hasPerm("advanceProd");
  const prodProbe = useResource(
    () => (!prodByPerm && myId ? productionApi.listPOs({ assignedUserId: myId, limit: 200 }) : Promise.resolve(null)),
    [prodByPerm, myId],
  );
  const assignedProd = ((prodProbe.data && prodProbe.data.orders) || []).some((p) => p.status === "Planned" || p.status === "In Production");
  const stages = WORKFLOW_STAGES.filter((s) => s.perms.some(hasPerm) || (s.key === "prod" && assignedProd));
  const wfTotal = stages.reduce((a, s) => a + (counts[s.key] || 0), 0);
  const isOpen = (k) => (openGroups[k] !== undefined ? openGroups[k] : stages.length <= 2);
  const toggle = (k) => setOpenGroups((g) => ({ ...g, [k]: !isOpen(k) }));

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2.5 rounded-xl bg-white p-3.5 ring-1 ring-slate-200">
        <ShieldCheck size={18} className="shrink-0 text-amber-600" />
        <div className="text-sm text-slate-600">Logged in as <span className="font-semibold text-slate-800">{auth?.user?.name || (me ? me.name : (ROLES[role] ? ROLES[role].name : role))}</span>{auth?.role?.name ? " · " + auth.role.name : ""}. Your assigned tasks and the process work that's yours to do — in separate tabs.</div>
      </div>

      <div className="flex w-min gap-1 rounded-lg bg-slate-100 p-1">
        {[["regular", "Regular Task", taskCount], ["workflow", "Workflow Task", wfTotal]].map(([k, l, nBadge]) => <button key={k} onClick={() => setQtab(k)} className={cx("flex items-center gap-2 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium", qtab === k ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700")}>{l}{nBadge > 0 && <span className={cx("rounded-full px-1.5 text-xs", qtab === k ? "bg-amber-100 text-amber-700" : "bg-slate-200 text-slate-500")}>{nBadge}</span>}</button>)}
      </div>

      {/* Both panels stay mounted (toggled via `hidden`) so the tab badge counts stay live. */}
      <div className={cx(qtab === "regular" ? "" : "hidden")}>
        <RegularTasksPanel myId={myId} auth={auth} onCount={setTaskCount} />
      </div>
      <div className={cx("space-y-3", qtab === "workflow" ? "" : "hidden")}>
        {stages.length === 0
          ? <Card className="p-6 text-sm text-slate-400">Your role has no workflow (process) tasks, and no production is assigned to you.</Card>
          : stages.map((s) => {
              const C = QUEUE_GROUP_COMPONENT[s.key];
              const assignedUserId = WF_OWNERSHIP[s.key] === "assigned" ? myId : null;
              return <C key={s.key} open={isOpen(s.key)} onToggle={() => toggle(s.key)} onCount={setCount} assignedUserId={assignedUserId} st={st} party={party} dispatch={dispatch} auth={auth} role={role} />;
            })}
      </div>
    </div>
  );
}

/* ============================ App shell ============================ */
/* ============================ Monitor (cross-module SLA: who has what pending, on-time vs delayed) ============================ */
function OrderTracking({ st, party }) {
  const [q, setQ] = useState("");
  const [fStatus, setFStatus] = useState("all");
  const [exp, setExp] = useState(null);
  const pos = st.productionOrders;
  const item = (id) => itemById(st.items, id);
  const STAGES = ["Received", "Stock", "Production", "Dispatch", "Delivery", "Invoice"];
  const ownerOf = { Stock: "Store Manager", Production: "Production", Dispatch: "Dispatch In-charge", Delivery: "Dispatch In-charge", Invoice: "Accounts" };
  const stepRows = (o, idx, stage) => {
    const L = o.lines;
    const relPOs = pos.filter((pp) => pp.sourceLineId && L.some((l) => l.id === pp.sourceLineId));
    const dels = st.deliveries.filter((d) => d.lines.some((dl) => L.some((l) => l.id === dl.soLineId)));
    const invs = st.invoices.filter((iv) => { const d = st.deliveries.find((x) => x.id === iv.deliveryId); return d && d.lines.some((dl) => L.some((l) => l.id === dl.soLineId)); });
    const sumFor = (d) => d.lines.filter((dl) => L.some((l) => l.id === dl.soLineId)).reduce((a, x) => a + x.qty, 0);
    const stat = (i) => (stage === "Complete" || i < idx ? "done" : i === idx ? "current" : "pending");
    return [
      { name: "Order received", status: "done", owner: "Sales", lines: ["Placed " + fmtDate(o.date) + " · " + (o.channel || "B2B") + " · " + party(o.partyId).name, L.length + " line(s) · " + fmtQty(L.reduce((a, l) => a + l.qtyOrdered, 0)) + " Kg ordered"] },
      { name: "Stock allocation", status: stat(1), owner: "Store Manager", lines: L.map((l) => item(l.itemId).name + ": ordered " + fmtQty(l.qtyOrdered) + ", allocated " + fmtQty(l.qtyAllocated) + " Kg" + (l.qtyAllocated < l.qtyOrdered ? " (short → produce)" : "")) },
      { name: "Production", status: stat(2), owner: "Production", lines: relPOs.length ? relPOs.map((pp) => { const resp = poResp(st, pp); const cur = pp.steps[pp.stepIdx]; return pp.id + " · " + item(pp.itemId).name + " · " + fmtQty(pp.plannedQty) + " Kg — " + pp.status + (pp.status === "In Production" && cur ? " · " + cur.wc + " (" + (pp.stepIdx + 1) + "/" + pp.steps.length + ")" : "") + (resp ? " · " + resp.name : ""); }) : ["No production needed — served from stock"] },
      { name: "Dispatch", status: stat(3), owner: "Dispatch In-charge", lines: dels.length ? dels.map((d) => d.id + " · via " + (d.via || "—") + " · " + fmtQty(sumFor(d)) + " Kg · " + d.status) : ["Not dispatched yet"] },
      { name: "Delivery", status: stat(4), owner: "Dispatch In-charge", lines: dels.filter((d) => d.status === "Delivered").length ? dels.filter((d) => d.status === "Delivered").map((d) => d.id + " · delivered " + fmtQty(sumFor(d)) + " Kg") : ["Not delivered yet"] },
      { name: "Invoice", status: stat(5), owner: "Accounts", lines: invs.length ? invs.map((iv) => iv.id + " · " + fmtMoney(iv.total) + " · bill-to " + iv.billTo) : ["Not invoiced yet"] },
    ];
  };
  const info = (o) => {
    const L = o.lines;
    const relPOs = pos.filter((p) => p.sourceLineId && L.some((l) => l.id === p.sourceLineId));
    const dels = st.deliveries.filter((d) => d.lines.some((dl) => L.some((l) => l.id === dl.soLineId)));
    const anyPending = L.some((l) => lineStatus(l, pos) === "Pending");
    const anyProd = relPOs.some((p) => p.status !== "Done");
    const anyDispatch = L.some((l) => l.qtyAllocated - l.qtyDispatched > 0.001);
    const anyTransit = dels.some((d) => d.status === "In Transit");
    const anyInvoice = dels.some((d) => d.status === "Delivered" && !d.invoiced);
    let stage = "Complete", ownerUser = null;
    if (anyPending) stage = "Stock";
    else if (anyProd) { stage = "Production"; const rp = relPOs.find((p) => p.status !== "Done"); ownerUser = rp ? poResp(st, rp) : null; }
    else if (anyDispatch) stage = "Dispatch";
    else if (anyTransit) stage = "Delivery";
    else if (anyInvoice) stage = "Invoice";
    const idx = stage === "Complete" ? STAGES.length : STAGES.indexOf(stage);
    const owner = stage === "Complete" ? "—" : (ownerUser ? ownerUser.name : ownerOf[stage]);
    return { stage, idx, owner };
  };
  const rows = st.orders.map((o) => ({ o, ...info(o) })).filter(({ o, stage }) => (q ? (o.id + " " + party(o.partyId).name).toLowerCase().includes(q.toLowerCase()) : true) && (fStatus === "all" ? true : fStatus === "complete" ? stage === "Complete" : stage !== "Complete"));
  return (
    <Card>
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-3">
        <Truck size={16} className="text-amber-600" /><span className="text-sm font-semibold text-slate-700">Order &rarr; Delivery tracking</span>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <div className="relative"><Search size={14} className="absolute left-2.5 top-2 text-slate-400" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Order / customer…" className="w-44 rounded-md bg-white py-1.5 pl-8 pr-2 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400" /></div>
          <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} className="rounded-md bg-white py-1.5 px-2 text-sm ring-1 ring-slate-200"><option value="all">All</option><option value="active">In progress</option><option value="complete">Complete</option></select>
        </div>
      </div>
      <div className="overflow-x-auto"><table className="w-full">
        <thead className="border-b border-slate-50"><tr><Th>Order</Th><Th>Customer</Th><Th style={{ minWidth: 170 }}>Progress</Th><Th>Current step</Th><Th>Owner now</Th><Th>Status</Th></tr></thead>
        <tbody className="divide-y divide-slate-50">
          {rows.map(({ o, stage, idx, owner }) => (
            <React.Fragment key={o.id}>
            <tr onClick={() => setExp(exp === o.id ? null : o.id)} className="cursor-pointer hover:bg-slate-50/60">
              <Td><span className="inline-flex items-center gap-1.5"><ChevronRight size={13} className={cx("text-slate-400 transition", exp === o.id ? "rotate-90" : "")} /><Mono className="font-semibold text-slate-800">{o.id}</Mono></span></Td>
              <Td className="text-slate-600">{party(o.partyId).name}</Td>
              <Td>
                <div className="flex items-center gap-0.5" style={{ minWidth: 150 }}>
                  {STAGES.map((sn, i) => { const done = stage === "Complete" || i < idx; const cur = stage !== "Complete" && i === idx; return <div key={i} title={sn} className="h-2 flex-1 rounded-full" style={{ background: done ? "linear-gradient(90deg,#34d399,#059669)" : cur ? "linear-gradient(90deg,#fcd34d,#d97706)" : "#e7e5e4" }} />; })}
                </div>
                <div className="mt-1 flex justify-between" style={{ fontSize: 9 }}><span className="text-slate-400">{STAGES[0]}</span><span className="text-slate-400">{STAGES[STAGES.length - 1]}</span></div>
              </Td>
              <Td>{stage === "Complete" ? <Badge tone="emerald">Done</Badge> : <Badge tone="amber">{stage}</Badge>}</Td>
              <Td className="text-slate-600">{owner}</Td>
              <Td>{stage === "Complete" ? <Badge tone="emerald"><CheckCircle2 size={11} /> Complete</Badge> : <span className="text-xs text-slate-500">In progress · step {idx}/{STAGES.length - 1}</span>}</Td>
            </tr>
            {exp === o.id && <tr><td colSpan={6} className="bg-slate-50/70 px-4 py-3">
              <div className="space-y-2">
                {stepRows(o, idx, stage).map((s, k) => (
                  <div key={k} className="flex gap-3 rounded-lg bg-white p-2.5 ring-1 ring-slate-100">
                    <div className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: s.status === "done" ? "#059669" : s.status === "current" ? "#d97706" : "#d6d3d1" }} />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2"><span className="text-sm font-medium text-slate-700">{s.name}</span>{s.status === "done" ? <Badge tone="emerald">Done</Badge> : s.status === "current" ? <Badge tone="amber">In progress</Badge> : <Badge tone="stone">Pending</Badge>}<span className="text-xs text-slate-400">{s.owner}</span></div>
                      <div className="mt-1 space-y-0.5">{s.lines.map((ln, m) => <div key={m} className="text-xs text-slate-500">{ln}</div>)}</div>
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-2 text-xs text-slate-400">Each step's work/quantity/doc (PO · challan · invoice) is shown here. Per-step <b>exact date-time</b> needs the backend.</p>
            </td></tr>}
            </React.Fragment>
          ))}
          {rows.length === 0 && <tr><Td className="text-slate-400" colSpan={6}>No orders match this filter.</Td></tr>}
        </tbody>
      </table></div>
      <p className="px-4 py-2.5 text-xs text-slate-400">Shows which pipeline step each order is at and who currently owns the work. "Complete" once all steps are done + invoiced. (Per-step exact completion time will come from the backend.)</p>
    </Card>
  );
}

// System Controller — Task management board (LIVE, real /tasks). Replaces the mock
// MonitorTasks. Manager (monitor/owner) actions call the real endpoints with the
// monitor-override authz, so a System-Controller holder can reopen/reassign/
// change-due/delete/bulk-delete ANY task (not just ones they created). Reuses the
// Phase-2 TaskDetailLive + task badges. Workflow/Order-tracking tabs stay mock (Phase B).
function MonitorTasks({ auth }) {
  const myId = auth && auth.user ? auth.user.id : null;
  const isMonitor = !!(auth && ((auth.role && auth.role.permissions && (auth.role.permissions.includes("*") || auth.role.permissions.includes("monitor"))) || (auth.user && auth.user.isPlatformAdmin)));
  const [tab, setTab] = useState("due");
  const [fAssignee, setFAssignee] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState({});
  const [detailId, setDetailId] = useState(null);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState("");
  const usersRes = useResource(() => tasksApi.listAssignees(), []);
  const users = (usersRes.data && usersRes.data.users) || [];
  // Server-side filters (assignee/date-range/name); the tab (status grouping) is
  // applied client-side so "Due/overdue" can span open+in_progress+reopened.
  const { data, loading, error, reload } = useResource(
    () => tasksApi.listTasks({ assigneeId: fAssignee || undefined, from: from || undefined, to: to || undefined, q: q || undefined, limit: 200 }),
    [fAssignee, from, to, q],
  );
  const all = (data && data.tasks) || [];
  const today = todayISO();
  const isActive = (t) => t.status === "open" || t.status === "in_progress" || t.status === "reopened";
  const isOverdue = (t) => isActive(t) && t.dueDate && t.dueDate < today;
  const inTab = (t) => (tab === "due" ? isActive(t) : tab === "completed" ? t.status === "completed" : true);
  const list = all.filter(inTab);
  const overdueCount = all.filter(isOverdue).length;
  const selIds = list.filter((t) => sel[t.id]).map((t) => t.id);
  const allChecked = list.length > 0 && list.every((t) => sel[t.id]);
  const toggleAll = () => { const v = !allChecked; const n = { ...sel }; list.forEach((t) => { n[t.id] = v; }); setSel(n); };
  const clearSel = () => setSel({});
  const run = async (key, fn) => { setBusy(key); setErr(""); try { await fn(); await reload(); } catch (e) { setErr(e instanceof ApiError ? e.message : "Action failed."); } finally { setBusy(null); } };
  const bulkDelete = async () => { if (!selIds.length) return; setBusy("bulk"); setErr(""); try { await tasksApi.bulkDeleteTasks(selIds); clearSel(); await reload(); } catch (e) { setErr(e instanceof ApiError ? e.message : "Bulk delete failed."); } finally { setBusy(null); } };
  const tabs = [["due", "Due / overdue"], ["completed", "Completed"], ["all", "All"]];
  const IN = "rounded-md bg-white py-1 px-2 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400";
  return (
    <Card>
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-3">
        <ListChecks size={16} className="text-amber-600" /><span className="text-sm font-semibold text-slate-700">Task management</span>
        {overdueCount > 0 && <Badge tone="rose"><AlertTriangle size={11} /> {overdueCount} overdue</Badge>}
        <div className="flex gap-1 rounded-lg bg-slate-100 p-1">{tabs.map(([k, l]) => <button key={k} onClick={() => { setTab(k); clearSel(); }} className={cx("rounded-md px-3 py-1 text-xs font-medium", tab === k ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700")}>{l}</button>)}</div>
        <div className="ml-auto flex items-center gap-2">
          {isMonitor && selIds.length > 0 && <>
            <span className="text-xs text-slate-500">{selIds.length} selected</span>
            <button disabled={busy === "bulk"} onClick={bulkDelete} className="inline-flex items-center gap-1 rounded-md bg-rose-100 px-2.5 py-1 text-xs font-medium text-rose-700 hover:bg-rose-200 disabled:opacity-50"><Trash2 size={12} /> {busy === "bulk" ? "Deleting…" : "Bulk delete"}</button>
          </>}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-50 px-4 py-2.5">
        <span className="text-xs font-medium text-slate-400">Filter</span>
        <div className="relative"><Search size={14} className="absolute left-2.5 top-2 text-slate-400" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Task name…" className={cx(IN, "w-40 pl-8")} /></div>
        <select value={fAssignee} onChange={(e) => setFAssignee(e.target.value)} className={IN}><option value="">All assignees</option>{users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</select>
        <span className="text-xs text-slate-400">Due</span>
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={IN} title="From due date" />
        <span className="text-xs text-slate-300">→</span>
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={IN} title="To due date" />
        {(q || fAssignee || from || to) && <button onClick={() => { setQ(""); setFAssignee(""); setFrom(""); setTo(""); }} className="text-xs text-slate-400 hover:text-slate-600">clear</button>}
        <span className="ml-auto text-xs text-slate-400">{data ? `${list.length} shown` : ""}{loading && all.length > 0 ? " · updating…" : ""}</span>
      </div>
      {err ? <div className="px-4 pt-2"><InlineError message={err} /></div> : null}
      {error ? <div className="px-4 pt-2"><InlineError message={error} onRetry={reload} /></div> : null}
      <div className="overflow-x-auto">
        {loading && all.length === 0 ? <LoadingBlock label="Loading tasks…" /> : (
          <table className="w-full">
            <thead className="border-b border-slate-50"><tr>
              {isMonitor && <Th className="w-8"><input type="checkbox" checked={allChecked} onChange={toggleAll} /></Th>}
              <Th>Task</Th><Th>Assignee</Th><Th>Created by</Th><Th>Due</Th><Th>Status</Th><Th /></tr></thead>
            <tbody className="divide-y divide-slate-50">
              {list.length === 0 ? <tr><Td className="text-slate-400" colSpan={isMonitor ? 7 : 6}>No tasks match this filter/tab.</Td></tr> : list.map((t) => {
                const canEdit = isMonitor && isActive(t); // reassign/change-due only on active tasks
                return (
                  <tr key={t.id} className={cx(sel[t.id] ? "bg-amber-50/40" : isOverdue(t) ? "bg-rose-50/30" : "")}>
                    {isMonitor && <Td><input type="checkbox" checked={!!sel[t.id]} onChange={() => setSel((s) => ({ ...s, [t.id]: !s[t.id] }))} /></Td>}
                    <Td className="font-medium text-slate-700"><button onClick={() => setDetailId(t.id)} className="text-left hover:text-amber-700 hover:underline">{t.title}</button>{t.attachmentCount > 0 && <span className="ml-1 inline-flex items-center gap-0.5 text-xs text-slate-400"><Download size={10} />{t.attachmentCount}</span>}</Td>
                    <Td>{canEdit ? (
                      <select value={(t.assignee && t.assignee.id) || ""} disabled={busy === t.id} onChange={(e) => run(t.id, () => tasksApi.updateTask(t.id, { assigneeId: e.target.value }))} className={cx(IN, "py-1 px-1.5")}>{users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</select>
                    ) : <span className="text-slate-600">{(t.assignee && t.assignee.name) || "—"}</span>}</Td>
                    <Td className="text-slate-500">{(t.createdBy && t.createdBy.name) || "—"}</Td>
                    <Td>{canEdit ? (
                      <input type="date" value={t.dueDate || ""} disabled={busy === t.id} onChange={(e) => run(t.id, () => tasksApi.updateTask(t.id, { dueDate: e.target.value || null }))} className={cx(IN, "py-1 px-1.5")} />
                    ) : <span className="text-slate-500">{fmtDate(t.dueDate)}</span>}</Td>
                    <Td>{taskStatusBadge(t)}</Td>
                    <Td className="text-right"><div className="flex justify-end gap-1.5">
                      {isMonitor && (t.status === "completed" || t.status === "closed") && <button disabled={busy === t.id} onClick={() => run(t.id, () => tasksApi.setTaskStatus(t.id, { status: "reopened" }))} className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-amber-700 ring-1 ring-amber-200 hover:bg-amber-50 disabled:opacity-50"><Repeat size={12} /> Reopen</button>}
                      {isMonitor && <button disabled={busy === t.id} onClick={() => run(t.id, () => tasksApi.deleteTask(t.id))} className="text-slate-300 hover:text-rose-500 disabled:opacity-50" title="Delete task"><Trash2 size={14} /></button>}
                    </div></Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      <p className="px-4 py-2.5 text-xs text-slate-400">{isMonitor ? "Manager actions: reassign / change due date on active tasks, Reopen completed/closed, Delete or bulk-delete. " : "View only — System Controller actions need the monitor permission. "}Overdue = due date passed and not completed.</p>
      {detailId && <TaskDetailLive id={detailId} open onClose={() => setDetailId(null)} onChanged={reload} myId={myId} isManager={isMonitor} />}
    </Card>
  );
}

// System Controller — workflow oversight (LIVE, Phase B). Replaces the mock
// Workflow Task + Order-tracking tabs. Monitors (perm 'monitor'/'*') can
// HOLD/UNHOLD (temporary) and CLOSE (permanent) orders & purchases; OWNERS ('*')
// can DELETE. Overdue: orders via line requiredBy (server flag), purchases via
// expectedDate. Reads use the monitor-relaxed order/production/dispatch/purchase
// endpoints. Phase A's Regular Task board (MonitorTasks) is unchanged.
const scMonitor = (auth) => !!(auth && ((auth.role && auth.role.permissions && (auth.role.permissions.includes("*") || auth.role.permissions.includes("monitor"))) || (auth.user && auth.user.isPlatformAdmin)));
const scOwner = (auth) => !!(auth && ((auth.role && auth.role.permissions && auth.role.permissions.includes("*")) || (auth.user && auth.user.isPlatformAdmin)));
const SC_STAGES = ["Received", "Stock", "Production", "Dispatch", "Delivery", "Invoice"];
// Map an order's derived status → current pipeline stage index + owner role.
function orderStage(o) {
  if (o.closedAt) return { idx: -1, label: "Closed", owner: "—" };
  switch (o.status) {
    case "Completed": return { idx: 6, label: "Complete", owner: "—" };
    case "In Progress": return { idx: 4, label: "Dispatch / Delivery", owner: "Dispatch In-charge" };
    case "In Production": return { idx: 2, label: "Production", owner: "Production" };
    case "Ready": return { idx: 3, label: "Ready to dispatch", owner: "Dispatch In-charge" };
    default: return { idx: 1, label: "Stock decision", owner: "Prod. Manager" }; // Open
  }
}
function wfStateBadges(x, overdue) {
  return (
    <span className="inline-flex flex-wrap gap-1">
      {x.closedAt ? <Badge tone="stone">Closed</Badge> : x.onHold ? <Badge tone="amber"><Clock size={10} /> On hold</Badge> : null}
      {overdue ? <Badge tone="rose"><AlertTriangle size={10} /> Overdue</Badge> : null}
    </span>
  );
}

// ORDER TRACKING (real) — per-order pipeline + hold/close/delete oversight.
function OrderTrackingLive({ auth }) {
  const isMonitor = scMonitor(auth), isOwner = scOwner(auth);
  // PER-LINE DETAIL, fetched LAZILY. listOrders returns only the order-level rollup
  // (deriveOrderStatus collapses the per-line statuses the server already computed),
  // so the lines are NOT in hand here — unlike the stage drill-down. GET /orders/:code
  // already returns every line with its own derived status + quantities, so expanding
  // costs ONE request for THAT order, cached by code. listOrders is left untouched:
  // it also feeds the Workflow-Task stat tiles, and loading every order's lines on
  // every page load to serve one expanded row would be the wrong trade.
  const [expOrder, setExpOrder] = useState(null); // order code whose lines are shown
  const [lineCache, setLineCache] = useState({}); // code → { loading, lines, error }
  const loadLines = async (code) => {
    setLineCache((c) => ({ ...c, [code]: { loading: true } }));
    try {
      const r = await ordersApi.getOrder(code);
      setLineCache((c) => ({ ...c, [code]: { loading: false, lines: (r.order && r.order.lines) || [] } }));
    } catch (e) {
      setLineCache((c) => ({ ...c, [code]: { loading: false, error: e instanceof ApiError ? e.message : "Could not load this order's items." } }));
    }
  };
  const toggleOrder = (code) => {
    if (expOrder === code) { setExpOrder(null); return; } // collapse; keep the cache
    setExpOrder(code);
    const hit = lineCache[code];
    if (hit && !hit.error && !hit.loading) return; // already fetched — no refetch
    loadLines(code);
  };
  const [q, setQ] = useState("");
  const [fStatus, setFStatus] = useState("all");
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState("");
  const { data, loading, error, reload } = useResource(() => ordersApi.listOrders({ includeInactive: true }), []);
  const orders = (data && data.orders) || [];
  const shown = orders.filter((o) => (q ? (o.code + " " + o.party.name).toLowerCase().includes(q.toLowerCase()) : true) && (fStatus === "all" ? true : fStatus === "complete" ? o.status === "Completed" : o.status !== "Completed" && !o.closedAt));
  const act = async (code, fn) => { setBusy(code); setErr(""); try { await fn(); await reload(); } catch (e) { setErr(e instanceof ApiError ? e.message : "Action failed."); } finally { setBusy(null); } };
  const IN = "rounded-md bg-white py-1.5 px-2 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400";
  return (
    <Card>
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-3">
        <Truck size={16} className="text-amber-600" /><span className="text-sm font-semibold text-slate-700">Order → Delivery tracking</span>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <div className="relative"><Search size={14} className="absolute left-2.5 top-2 text-slate-400" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Order / customer…" className={cx(IN, "w-44 pl-8")} /></div>
          <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} className={IN}><option value="all">All</option><option value="active">In progress</option><option value="complete">Complete</option></select>
        </div>
      </div>
      {err ? <div className="px-4 pt-2"><InlineError message={err} /></div> : null}
      {error ? <div className="px-4 pt-2"><InlineError message={error} onRetry={reload} /></div> : null}
      <div className="overflow-x-auto">
        {loading && orders.length === 0 ? <LoadingBlock label="Loading orders…" /> : (
          <table className="w-full">
            <thead className="border-b border-slate-50"><tr><Th className="w-8" /><Th>Order</Th><Th>Customer</Th><Th style={{ minWidth: 160 }}>Progress</Th><Th>Current step</Th><Th>Owner now</Th><Th>State</Th><Th /></tr></thead>
            <tbody className="divide-y divide-slate-50">
              {shown.length === 0 ? <tr><Td className="text-slate-400" colSpan={8}>No orders match this filter.</Td></tr> : shown.map((o) => {
                const s = orderStage(o);
                const open = expOrder === o.code;
                const det = lineCache[o.code] || {};
                return (
                  <React.Fragment key={o.code}>
                  <tr className={cx("cursor-pointer hover:bg-slate-50/60", o.overdue ? "bg-rose-50/30" : "", o.closedAt ? "opacity-60" : "")} onClick={() => toggleOrder(o.code)}>
                    <Td><ChevronDown size={14} className={cx("text-slate-400 transition-transform", open && "rotate-180")} /></Td>
                    <Td><Mono className="font-semibold text-slate-800">{o.code}</Mono></Td>
                    <Td className="text-slate-600">{o.party.name}</Td>
                    <Td><div className="flex items-center gap-0.5" style={{ minWidth: 140 }}>{SC_STAGES.map((sn, i) => { const done = s.idx >= 6 || i < s.idx; const cur = s.idx >= 0 && s.idx < 6 && i === s.idx; return <div key={i} title={sn} className="h-2 flex-1 rounded-full" style={{ background: done ? "linear-gradient(90deg,#34d399,#059669)" : cur ? "linear-gradient(90deg,#fcd34d,#d97706)" : "#e7e5e4" }} />; })}</div></Td>
                    <Td>{s.idx >= 6 ? <Badge tone="emerald"><CheckCircle2 size={11} /> Complete</Badge> : <Badge tone="amber">{s.label}</Badge>}</Td>
                    <Td className="text-slate-600">{s.owner}</Td>
                    <Td>{wfStateBadges(o, o.overdue) || <span className="text-xs text-slate-300">—</span>}</Td>
                    <Td className="text-right"><div className="flex justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
                      {isMonitor && !o.closedAt && (o.onHold
                        ? <button disabled={busy === o.code} onClick={() => act(o.code, () => ordersApi.unholdOrder(o.code))} className="rounded-md px-2 py-1 text-xs font-medium text-amber-700 ring-1 ring-amber-200 hover:bg-amber-50 disabled:opacity-50">Unhold</button>
                        : <button disabled={busy === o.code} onClick={() => act(o.code, () => ordersApi.holdOrder(o.code))} className="rounded-md px-2 py-1 text-xs font-medium text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-50">Hold</button>)}
                      {isMonitor && !o.closedAt && <button disabled={busy === o.code} onClick={() => act(o.code, () => ordersApi.closeOrder(o.code))} className="rounded-md px-2 py-1 text-xs font-medium text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-50">Close</button>}
                      {isOwner && <button disabled={busy === o.code} onClick={() => act(o.code, () => ordersApi.deleteOrder(o.code))} className="text-slate-300 hover:text-rose-500 disabled:opacity-50" title="Delete (owner only)"><Trash2 size={14} /></button>}
                    </div></Td>
                  </tr>
                  {open && (
                    <tr><Td colSpan={8} className="bg-slate-50/40 !p-0">
                      <div className="px-4 py-2">
                        <div className="mb-1 text-xs text-slate-400">The items on <b className="text-slate-500">{o.code}</b> and where each one has actually got to — the order's single <b className="text-slate-500">{s.label}</b> badge is the roll-up of these.</div>
                        {det.loading ? <p className="py-2 text-xs text-slate-400">Loading items…</p>
                          : det.error ? <div className="py-1"><InlineError message={det.error} onRetry={() => loadLines(o.code)} /></div>
                          : (det.lines || []).length === 0 ? <p className="py-2 text-xs text-slate-400">This order has no items.</p>
                          : (
                            <div className="overflow-x-auto rounded-md ring-1 ring-slate-200">
                              <table className="w-full bg-white">
                                <thead className="bg-slate-50/60"><tr><Th>Item</Th><Th>Status</Th><Th className="text-right">Ordered</Th><Th className="text-right">Allocated</Th><Th className="text-right">Dispatched</Th><Th className="text-right">Delivered</Th><Th>Required by</Th></tr></thead>
                                <tbody className="divide-y divide-slate-50">
                                  {(det.lines || []).map((l) => {
                                    // Overdue = past required-by and not fully delivered — the same rule
                                    // listOrders uses for the order-level flag.
                                    const late = !!l.requiredBy && String(l.requiredBy).slice(0, 10) < todayISO() && l.status !== "Delivered";
                                    return (
                                      <tr key={l.lineCode} className={late ? "bg-rose-50/40" : ""}>
                                        <Td className="text-xs font-medium text-slate-700">{l.item.name} <Mono className="ml-1 text-slate-400">{l.item.code}</Mono></Td>
                                        <Td><Badge tone={statusTone(l.status)}>{l.status}</Badge></Td>
                                        <Td className="text-right"><Mono className="text-xs text-slate-700">{fmtQty(l.qtyOrdered)}</Mono></Td>
                                        <Td className="text-right"><Mono className={cx("text-xs", l.qtyAllocated > 0 ? "text-slate-600" : "text-slate-300")}>{fmtQty(l.qtyAllocated)}</Mono></Td>
                                        <Td className="text-right"><Mono className={cx("text-xs", l.qtyDispatched > 0 ? "text-slate-600" : "text-slate-300")}>{fmtQty(l.qtyDispatched)}</Mono></Td>
                                        <Td className="text-right"><Mono className={cx("text-xs", l.qtyDelivered > 0 ? "text-emerald-600" : "text-slate-300")}>{fmtQty(l.qtyDelivered)}</Mono></Td>
                                        <Td className="whitespace-nowrap text-xs text-slate-500">{l.requiredBy ? fmtDate(l.requiredBy) : "—"}{late && <Badge tone="rose">Overdue</Badge>}</Td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                      </div>
                    </Td></tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      <p className="px-4 py-2.5 text-xs text-slate-400">{isMonitor ? "Hold = temporary pause · Close = permanent stop. " : ""}{isOwner ? "Delete is owner-only. " : ""}Overdue = a line past its required-by date, order not completed.</p>
    </Card>
  );
}

// WORKFLOW TASK (real) — manager oversight: pending work by stage (all items) +
// stats + purchases oversight (hold/close/set-expected/delete).
function WorkflowOversight({ auth }) {
  const isMonitor = scMonitor(auth), isOwner = scOwner(auth);
  const [filter, setFilter] = useState("all");
  const [openStage, setOpenStage] = useState(null); // which stage's drill-down is expanded
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState("");
  const today = todayISO();
  const ordersRes = useResource(() => ordersApi.listOrders({ includeInactive: true }), []);
  const stockRes = useResource(() => ordersApi.pendingDecisions(), []);
  const poRes = useResource(() => productionApi.listPOs({ limit: 200 }), []);
  const dispRes = useResource(() => dispatchApi.listDispatchable(), []);
  const delRes = useResource(() => dispatchApi.listDeliveries({ status: "Delivered" }), []);
  const purRes = useResource(() => purchasesApi.listPurchases(), []);
  // FG QC is the one stage whose list isn't already fetched for something else.
  const qcRes = useResource(() => batchesApi.listBatches({ status: "QC Pending", limit: 200 }), []);
  const orders = (ordersRes.data && ordersRes.data.orders) || [];
  const stockLines = (stockRes.data && stockRes.data.lines) || [];
  const pos = (poRes.data && poRes.data.orders) || [];
  const dispatchable = (dispRes.data && dispRes.data.dispatchable) || [];
  const toInvoice = ((delRes.data && delRes.data.deliveries) || []).filter((d) => !d.invoiced);
  const purchases = (purRes.data && purRes.data.purchases) || [];
  // FG/SFG ONLY — filter the type EXPLICITLY. The Batches screen's "FG QC pending" tab
  // filters on status alone and lets the type fall through, so it would list RM too;
  // a stage named FG QC must not. (Latent today: RM is QC'd as a PURCHASE and its
  // batches are minted already-Passed, so no RM batch is ever QC-Pending.)
  const fgQcBatches = (((qcRes.data && qcRes.data.batches) || [])
    .filter((b) => b.status === "QC Pending" && (b.itemType === "FG" || b.itemType === "SFG")));
  const purOverdue = (p) => !!p.expectedDate && String(p.expectedDate).slice(0, 10) < today && p.status !== "Passed" && !p.closedAt;
  const isPast = (d) => !!d && String(d).slice(0, 10) < today;

  // Pending-by-stage (manager-wide — ALL items, not one user's).
  //
  // Each stage carries its ITEMS, not just a count — the six lists above are already
  // fetched, so the drill-down costs no extra request; the count is just items.length.
  //
  // `od` is the per-item overdue predicate. Material issue / Production / Dispatch
  // previously had `overdue: 0` HARDCODED — a literal rendered as if it were a
  // measurement, which also made "Overdue only" structurally unable to surface them.
  // They now use the item's own requiredBy, the same rule Stock decision always used.
  // Invoice has od:null on purpose — nothing carries an invoicing due date, so its
  // overdue is NOT APPLICABLE rather than a fake 0.
  const stages = [
    { key: "stock", label: "Stock decision", owner: "Prod. Manager", items: stockLines, od: (l) => isPast(l.requiredBy),
      head: ["Order", "Company", "Item", "Pending", "Required by"],
      row: (l) => [<Mono className="text-slate-500">{l.orderCode}</Mono>, l.party?.name || "—", l.item?.name || "—", fmtQty(l.pendingQty), l.requiredBy ? fmtDate(l.requiredBy) : "—"] },
    { key: "issue", label: "Material issue", owner: "Store Manager",
      items: pos.filter((p) => p.woCode && p.status !== "Done" && (p.issuedQty || 0) < p.plannedQty - 0.001), od: (p) => isPast(p.requiredBy),
      head: ["PO", "Item", "Issued / planned", "Status", "Responsible", "Required by"],
      row: (p) => [<Mono className="text-slate-500">{p.code}</Mono>, p.item?.name || "—", fmtQty(p.issuedQty || 0) + " / " + fmtQty(p.plannedQty), p.status, p.assignedUser?.name || "Unassigned", p.requiredBy ? fmtDate(p.requiredBy) : "—"] },
    { key: "prod", label: "Production", owner: "Production",
      items: pos.filter((p) => p.status === "Planned" || p.status === "In Production"), od: (p) => isPast(p.requiredBy),
      head: ["PO", "Item", "Status", "Room", "Responsible", "Required by"],
      row: (p) => [<Mono className="text-slate-500">{p.code}</Mono>, p.item?.name || "—", p.status, p.startingRoom || "—", p.assignedUser?.name || "Unassigned", p.requiredBy ? fmtDate(p.requiredBy) : "—"] },
    // FG QC sits after Production because that is WHERE THE WORK ARISES — a finished
    // batch then needs checking. It is NOT a gate on Dispatch and must not be read as
    // one: finalizePoTx mints the batch "QC Pending" while the aggregate FG stock is
    // already allocatable, and neither fulfillLine nor the dispatch service checks for
    // a Passed batch — FG can be (and is) dispatched while un-QC'd. Whether it SHOULD
    // block dispatch is a separate question.
    // od:null → overdue "n/a": a batch carries no due date. Same honest treatment as
    // Invoice — never a 0 that looks measured.
    { key: "fgqc", label: "FG QC", owner: "QC Department", items: fgQcBatches, od: null,
      head: ["Batch", "Item", "Produced", "PO", "Made by", "Room"],
      row: (b) => [<Mono className="text-slate-500">{b.code}</Mono>, b.item?.name || "—", b.producedAt ? fmtDate(b.producedAt) : "—", b.poCode ? <Mono className="text-slate-500">{b.poCode}</Mono> : "—", b.operator?.name || "—", b.room || "—"] },
    { key: "dispatch", label: "Dispatch", owner: "Dispatch In-charge", items: dispatchable, od: (x) => isPast(x.requiredBy),
      head: ["Order", "Company", "Item", "Ready to send", "Required by"],
      row: (x) => [<Mono className="text-slate-500">{x.orderCode}</Mono>, x.party?.name || "—", x.item?.name || "—", fmtQty(x.dispatchable), x.requiredBy ? fmtDate(x.requiredBy) : "—"] },
    { key: "invoice", label: "Invoice", owner: "Accounts", items: toInvoice, od: null,
      head: ["Challan", "Company", "Delivered"],
      row: (d) => [<Mono className="text-slate-500">{d.code}</Mono>, (d.parties || []).map((x) => x.name).join(", ") || d.party?.name || "—", d.deliveredAt ? fmtDate(d.deliveredAt) : "—"] },
    { key: "purchaseqc", label: "Purchase QC", owner: "Store Manager",
      items: purchases.filter((p) => p.status === "QC Pending" && !p.closedAt), od: purOverdue,
      head: ["Purchase", "Supplier", "Items", "Expected"],
      // NOTE: purchases.supplier is a plain STRING (not a party object), and a purchase
      // has no single item — it carries lines:[{itemCode, qty}]. Render those.
      row: (p) => [<Mono className="text-slate-500">{p.code}</Mono>, p.supplier || "—", (p.lines || []).map((l) => l.itemCode).join(", ") || "—", p.expectedDate ? fmtDate(p.expectedDate) : "—"] },
  ].map((s) => ({ ...s, count: s.items.length, overdue: s.od ? s.items.filter(s.od).length : null }));
  const pending = stages.reduce((a, s) => a + s.count, 0);
  const overdueCount = orders.filter((o) => o.overdue).length + purchases.filter(purOverdue).length;
  const onHold = orders.filter((o) => o.onHold).length + purchases.filter((p) => p.onHold).length;
  const closed = orders.filter((o) => o.closedAt).length + purchases.filter((p) => p.closedAt).length;
  // `overdue > 0` is false for the n/a (null) Invoice stage — so Overdue-only simply
  // omits it, rather than claiming it has none.
  const shownStages = filter === "overdue" ? stages.filter((s) => (s.overdue || 0) > 0) : stages;
  const shownPurchases = filter === "overdue" ? purchases.filter(purOverdue) : purchases;
  const act = async (code, fn) => { setBusy(code); setErr(""); try { await fn(); await purRes.reload(); } catch (e) { setErr(e instanceof ApiError ? e.message : "Action failed."); } finally { setBusy(null); } };
  const Stat = ({ label, value, tone }) => (<div className={cx("rounded-lg p-3", tone === "rose" ? "bg-rose-50" : tone === "amber" ? "bg-amber-50" : tone === "stone" ? "bg-slate-50" : "bg-slate-50")}><div className={cx("text-xs", tone === "rose" ? "text-rose-700" : tone === "amber" ? "text-amber-700" : "text-slate-400")}>{label}</div><Mono className={cx("text-lg font-semibold", tone === "rose" ? "text-rose-700" : tone === "amber" ? "text-amber-700" : "text-slate-800")}>{value}</Mono></div>);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 items-center gap-3 sm:grid-cols-5">
        <Stat label="Pending items" value={pending} tone="stone" />
        <Stat label="Overdue" value={overdueCount} tone="rose" />
        <Stat label="On hold" value={onHold} tone="amber" />
        <Stat label="Closed" value={closed} tone="stone" />
        <div className="flex justify-end"><div className="flex gap-1 rounded-lg bg-slate-100 p-1">{[["all", "All"], ["overdue", "Overdue only"]].map(([k, l]) => <button key={k} onClick={() => setFilter(k)} className={cx("rounded-md px-3 py-1 text-xs font-medium", filter === k ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700")}>{l}</button>)}</div></div>
      </div>

      <Card className="overflow-x-auto">
        <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-3"><CircleDot size={14} className="text-amber-600" /><span className="text-sm font-semibold text-slate-700">Pending work by stage (all)</span></div>
        <table className="w-full">
          <thead className="border-b border-slate-50"><tr><Th className="w-8"></Th><Th>Stage</Th><Th>Owner (role)</Th><Th className="text-right">Pending</Th><Th className="text-right">Overdue</Th></tr></thead>
          <tbody className="divide-y divide-slate-50">
            {shownStages.length === 0 ? <tr><Td className="text-slate-400" colSpan={5}>No overdue pending work.</Td></tr> : shownStages.map((s) => {
              const open = openStage === s.key;
              return (
                <React.Fragment key={s.key}>
                  <tr className={cx((s.overdue || 0) > 0 ? "bg-rose-50/30" : "", s.count > 0 && "cursor-pointer hover:bg-slate-50/60")}
                    onClick={() => s.count > 0 && setOpenStage(open ? null : s.key)}>
                    <Td>{s.count > 0 ? <ChevronDown size={14} className={cx("text-slate-400 transition-transform", open && "rotate-180")} /> : null}</Td>
                    <Td className="font-medium text-slate-700">{s.label}</Td>
                    <Td className="text-slate-500">{s.owner}</Td>
                    <Td className="text-right"><Mono className="text-slate-700">{s.count}</Mono></Td>
                    <Td className="text-right">{s.overdue === null ? <span className="text-xs text-slate-300" title="No invoicing due date exists — overdue is not applicable here.">n/a</span>
                      : s.overdue > 0 ? <Badge tone="rose">{s.overdue}</Badge> : <span className="text-xs text-slate-300">0</span>}</Td>
                  </tr>
                  {open && (
                    <tr><Td colSpan={5} className="bg-slate-50/40 !p-0">
                      <div className="px-4 py-2">
                        <div className="mb-1 text-xs text-slate-400">The <b className="text-slate-500">{s.count}</b> item{s.count === 1 ? "" : "s"} waiting at <b className="text-slate-500">{s.label}</b> — each is one task for {s.owner}.
                          {/* Only the issue/prod pair overlaps; saying this on every stage was noise. */}
                          {(s.key === "issue" || s.key === "prod") && " A PO can appear under both Material issue and Production: those are two separate tasks (issue the RM, then make it), not a duplicate."}</div>
                        <div className="overflow-x-auto rounded-md ring-1 ring-slate-200">
                          <table className="w-full bg-white">
                            <thead className="bg-slate-50/60"><tr>{s.head.map((h) => <Th key={h} className="whitespace-nowrap">{h}</Th>)}{s.od && <Th></Th>}</tr></thead>
                            <tbody className="divide-y divide-slate-50">
                              {s.items.map((it, i) => {
                                const late = s.od ? s.od(it) : false;
                                return (
                                  <tr key={i} className={late ? "bg-rose-50/40" : ""}>
                                    {s.row(it).map((c, j) => <Td key={j} className="whitespace-nowrap text-xs text-slate-600">{c}</Td>)}
                                    {s.od && <Td className="text-right">{late ? <Badge tone="rose">Overdue</Badge> : null}</Td>}
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </Td></tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        <p className="px-4 py-2 text-xs text-slate-400">Manager-wide pending work across the pipeline — click a stage to see which orders/purchases are waiting there. Order hold/close/delete is on the Order tracking tab; purchase oversight is below.</p>
      </Card>

      <Card>
        <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-3"><ShieldCheck size={14} className="text-amber-600" /><span className="text-sm font-semibold text-slate-700">Purchases oversight</span></div>
        {err ? <div className="px-4 pt-2"><InlineError message={err} /></div> : null}
        <div className="overflow-x-auto"><table className="w-full">
          <thead className="border-b border-slate-50"><tr><Th>Purchase</Th><Th>Supplier</Th><Th>Status</Th><Th>Expected</Th><Th>State</Th><Th /></tr></thead>
          <tbody className="divide-y divide-slate-50">
            {shownPurchases.length === 0 ? <tr><Td className="text-slate-400" colSpan={6}>{filter === "overdue" ? "No overdue purchases." : "No purchases."}</Td></tr> : shownPurchases.map((p) => (
              <tr key={p.code} className={cx(purOverdue(p) ? "bg-rose-50/30" : "", p.closedAt ? "opacity-60" : "")}>
                <Td><Mono className="font-semibold text-slate-800">{p.code}</Mono></Td>
                <Td className="text-slate-600">{p.supplier}</Td>
                <Td><Badge tone={p.status === "Passed" ? "emerald" : p.status === "Rejected" ? "rose" : "amber"}>{p.status}</Badge></Td>
                <Td>{isMonitor && !p.closedAt ? <input type="date" value={p.expectedDate ? String(p.expectedDate).slice(0, 10) : ""} disabled={busy === p.code} onChange={(e) => act(p.code, () => purchasesApi.setPurchaseExpected(p.code, e.target.value || null))} className="rounded-md bg-white py-1 px-1.5 text-sm ring-1 ring-slate-200" /> : <span className="text-slate-500">{p.expectedDate ? fmtDate(p.expectedDate) : "—"}</span>}</Td>
                <Td>{wfStateBadges(p, purOverdue(p)) || <span className="text-xs text-slate-300">—</span>}</Td>
                <Td className="text-right"><div className="flex justify-end gap-1.5">
                  {isMonitor && !p.closedAt && (p.onHold
                    ? <button disabled={busy === p.code} onClick={() => act(p.code, () => purchasesApi.unholdPurchase(p.code))} className="rounded-md px-2 py-1 text-xs font-medium text-amber-700 ring-1 ring-amber-200 hover:bg-amber-50 disabled:opacity-50">Unhold</button>
                    : <button disabled={busy === p.code} onClick={() => act(p.code, () => purchasesApi.holdPurchase(p.code))} className="rounded-md px-2 py-1 text-xs font-medium text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-50">Hold</button>)}
                  {isMonitor && !p.closedAt && <button disabled={busy === p.code} onClick={() => act(p.code, () => purchasesApi.closePurchase(p.code))} className="rounded-md px-2 py-1 text-xs font-medium text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-50">Close</button>}
                  {isOwner && <button disabled={busy === p.code} onClick={() => act(p.code, () => purchasesApi.deletePurchase(p.code))} className="text-slate-300 hover:text-rose-500 disabled:opacity-50" title="Delete (owner only)"><Trash2 size={14} /></button>}
                </div></Td>
              </tr>
            ))}
          </tbody>
        </table></div>
        <p className="px-4 py-2.5 text-xs text-slate-400">Set the expected date to drive purchase overdue. Hold/Close = monitor; Delete = owner-only.</p>
      </Card>
    </div>
  );
}

function MonitorView({ st, party, dispatch, role, me, auth }) {
  const [mtab, setMtab] = useState("regular");
  return (
    <div className="space-y-4">
      <div className="flex w-min gap-1 rounded-lg bg-slate-100 p-1">
        {[["regular", "Regular Task"], ["workflow", "Workflow Task"], ["orders", "Order tracking"]].map(([k, l]) => <button key={k} onClick={() => setMtab(k)} className={cx("whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium", mtab === k ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700")}>{l}</button>)}
      </div>
      {mtab === "regular" && <MonitorTasks auth={auth} />}
      {mtab === "workflow" && <WorkflowOversight auth={auth} />}
      {mtab === "orders" && <OrderTrackingLive auth={auth} />}
    </div>
  );
}

/* ============================ Tasks (management assigns; recurring by frequency; assignee completes) ============================ */
function NewTaskModal({ st, open, onClose, dispatch, role, me }) {
  const assignable = st.users.filter((u) => u.active);
  const [title, setTitle] = useState("");
  const [assignee, setAssignee] = useState((st.users.find((u) => u.active && u.role !== "owner" && u.role !== "supervisor") || st.users[0]).id);
  const [freq, setFreq] = useState("once");
  const [customDays, setCustomDays] = useState(7);
  const [dueStr, setDueStr] = useState(() => new Date(Date.now() + 2 * DAY).toISOString().slice(0, 10));
  const [description, setDescription] = useState("");
  const [attachments, setAttachments] = useState([]);
  const submit = () => {
    if (!title.trim()) return;
    const due = dueStr ? new Date(dueStr + "T09:00:00").getTime() : null;
    dispatch({ type: "ADD_TASK", title: title.trim(), description, attachments, assignee, freq, customDays: freq === "custom" ? Number(customDays) : null, due, by: me ? me.name : (ROLES[role] ? ROLES[role].name : role) });
    setTitle(""); setDescription(""); setAttachments([]); onClose();
  };
  return (
    <Modal open={open} onClose={onClose} title="Issue a task" footer={<><Btn variant="soft" onClick={onClose}>Cancel</Btn><Btn onClick={submit}>Assign task</Btn></>}>
      <div className="space-y-3">
        <label className="block text-sm"><span className="mb-1 block font-medium text-slate-600">Task</span><input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Daily floor cleaning checklist" className="w-full rounded-md bg-white py-1.5 px-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400" /></label>
        <label className="block text-sm"><span className="mb-1 block font-medium text-slate-600">Description</span><textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder="Details / instructions…" className="w-full rounded-md bg-white py-1.5 px-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400" /></label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-sm"><span className="mb-1 block font-medium text-slate-600">Assign to</span><select value={assignee} onChange={(e) => setAssignee(e.target.value)} className="w-full rounded-md bg-white py-1.5 px-2.5 text-sm ring-1 ring-slate-200">{assignable.map((u) => <option key={u.id} value={u.id}>{u.name} · {ROLES[u.role] ? ROLES[u.role].name : u.role}</option>)}</select></label>
          <label className="block text-sm"><span className="mb-1 block font-medium text-slate-600">Frequency</span><select value={freq} onChange={(e) => setFreq(e.target.value)} className="w-full rounded-md bg-white py-1.5 px-2.5 text-sm ring-1 ring-slate-200">{FREQ.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-sm"><span className="mb-1 block font-medium text-slate-600">{freq === "once" ? "Due / planned date" : "First due / start"}</span><input type="date" value={dueStr} onChange={(e) => setDueStr(e.target.value)} className="w-full rounded-md bg-white py-1.5 px-2.5 text-sm ring-1 ring-slate-200" /></label>
          {freq === "custom" && <label className="block text-sm"><span className="mb-1 block font-medium text-slate-600">Repeat every (days)</span><input type="number" value={customDays} onChange={(e) => setCustomDays(e.target.value)} className="mono w-full rounded-md bg-white py-1.5 px-2.5 text-sm ring-1 ring-slate-200" /></label>}
        </div>
        <label className="block text-sm"><span className="mb-1 block font-medium text-slate-600">Attachment</span>
          <input type="file" multiple onChange={(e) => setAttachments([...e.target.files].map((f) => f.name))} className="block w-full text-sm text-slate-500" />
          {attachments.length > 0 && <div className="mt-1 flex flex-wrap gap-1">{attachments.map((a, i) => <span key={i} className="inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600"><Download size={11} />{a}</span>)}</div>}
        </label>
        {freq !== "once" && <p className="text-xs text-slate-400">Recurring: on completion the system automatically sets the due date for the next <b>{FREQ_LABEL[freq]}</b> occurrence.</p>}
      </div>
    </Modal>
  );
}

function TaskDetailModal({ st, task, open, onClose, dispatch, role, me }) {
  const t = st.tasks.find((x) => x.id === task.id) || task;
  const now = Date.now();
  const byName = me ? me.name : (ROLES[role] ? ROLES[role].name : role);
  const canManage = can(role, "all") || can(role, "monitor");
  const isAssignee = !!me && t.assignee === me.id;
  const [remark, setRemark] = useState("");
  const [attach, setAttach] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [eAssignee, setEAssignee] = useState(t.assignee);
  const [eDue, setEDue] = useState(t.due ? new Date(t.due).toISOString().slice(0, 10) : "");
  const assignable = st.users.filter((u) => u.active);
  const reset = () => { setRemark(""); setAttach(""); };
  const statusBadge = t.status === "done" ? <Badge tone="emerald"><CheckCircle2 size={11} /> Completed</Badge> : t.status === "closed" ? <Badge tone="stone">Closed</Badge> : t.status === "inprogress" ? <Badge tone="sky"><Clock size={11} /> In-process</Badge> : (t.due && t.due < now ? <Badge tone="rose"><AlertTriangle size={11} /> Open · overdue</Badge> : <Badge tone="amber">Open</Badge>);
  return (
    <Modal open={open} onClose={onClose} title={t.title} wide
      footer={<>
        <Btn variant="soft" onClick={onClose}>Close</Btn>
        {t.status === "open" && isAssignee && <Btn variant="soft" onClick={() => { dispatch({ type: "START_TASK", id: t.id, by: byName, remark, attachment: attach }); reset(); }}><Clock size={14} /> Mark in-process</Btn>}
        {(t.status === "open" || t.status === "inprogress") && isAssignee && <Btn onClick={() => { dispatch({ type: "COMPLETE_TASK", id: t.id, by: byName, remark, attachment: attach }); reset(); onClose(); }}><CheckCircle2 size={14} /> Complete</Btn>}
        {(t.status === "done" || t.status === "closed") && canManage && <Btn variant="soft" onClick={() => { dispatch({ type: "REOPEN_TASK", id: t.id, by: byName, remark }); reset(); }}><Repeat size={14} /> Reopen</Btn>}
      </>}>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">{statusBadge}{t.freq !== "once" && <Badge tone="violet"><Repeat size={10} /> {FREQ_LABEL[t.freq]}</Badge>}<span className="text-xs text-slate-400">Assigned to <b className="text-slate-600">{uName(st, t.assignee)}</b> · planned <b className="text-slate-600">{fmtDate(t.due)}</b> · issued by {t.createdBy} · done {t.timesDone || 0}×</span></div>
        {t.description && <div className="rounded-lg bg-slate-50 p-3 text-sm text-slate-600">{t.description}</div>}
        {t.attachments && t.attachments.length > 0 && <div className="flex flex-wrap gap-1.5">{t.attachments.map((a, i) => <span key={i} className="inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-1 text-xs text-slate-600"><Download size={11} />{a}</span>)}</div>}

        {canManage && (editOpen ? (
          <div className="space-y-2 rounded-lg bg-amber-50/40 p-3 ring-1 ring-amber-200">
            <div className="text-xs font-semibold text-amber-800">Reschedule / reassign</div>
            <div className="grid grid-cols-2 gap-2">
              <select value={eAssignee} onChange={(e) => setEAssignee(e.target.value)} className="rounded-md bg-white py-1.5 px-2.5 text-sm ring-1 ring-slate-200">{assignable.map((u) => <option key={u.id} value={u.id}>{u.name} · {ROLES[u.role] ? ROLES[u.role].name : u.role}</option>)}</select>
              <input type="date" value={eDue} onChange={(e) => setEDue(e.target.value)} className="rounded-md bg-white py-1.5 px-2.5 text-sm ring-1 ring-slate-200" />
            </div>
            <div className="flex gap-2"><Btn variant="soft" onClick={() => setEditOpen(false)}>Cancel</Btn><Btn onClick={() => { dispatch({ type: "EDIT_TASK", id: t.id, assignee: eAssignee, due: eDue ? new Date(eDue + "T09:00:00").getTime() : null, by: byName }); setEditOpen(false); }}>Save</Btn></div>
          </div>
        ) : <button onClick={() => setEditOpen(true)} className="text-xs font-medium text-amber-700 hover:underline">Change assignee / planned date</button>)}

        {(t.status === "open" || t.status === "inprogress") && isAssignee && (
          <div className="space-y-2 rounded-lg p-3 ring-1 ring-slate-200">
            <div className="text-xs font-semibold text-slate-600">Add remark / attachment (saved with your update)</div>
            <textarea value={remark} onChange={(e) => setRemark(e.target.value)} rows={2} placeholder="Remark…" className="w-full rounded-md bg-white py-1.5 px-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400" />
            <input type="file" onChange={(e) => setAttach(e.target.files[0] ? e.target.files[0].name : "")} className="block w-full text-sm text-slate-500" />
            {attach && <span className="inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600"><Download size={11} />{attach}</span>}
          </div>
        )}

        <div>
          <div className="mb-1 text-xs font-semibold text-slate-600">Activity / remarks log</div>
          {(!t.history || t.history.length === 0) ? <p className="text-sm text-slate-400">No activity yet.</p> : (
            <div className="space-y-1.5">
              {t.history.map((h, i) => (
                <div key={i} className="flex gap-2 text-sm">
                  <span className="mt-1"><CircleDot size={11} className="text-slate-300" /></span>
                  <div><span className="font-medium text-slate-700">{h.action}</span> <span className="text-xs text-slate-400">· {h.by} · {fmtDate(h.at)}</span>{h.remark && <div className="text-xs text-slate-500">“{h.remark}”</div>}{h.attachment && <span className="mt-0.5 inline-flex items-center gap-1 rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-500"><Download size={10} />{h.attachment}</span>}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

/* ============================ Tasks — LIVE (Phase 2, real /api/v1/tasks) ============================
   Wired to the real Tasks backend (Phase 1a/1b): one-time tasks + recurring
   TEMPLATES (occurrences auto-generated by the daily cron), replacing the mock
   reducer for THIS screen. ANYONE can assign; authz-aware actions mirror the
   backend (assignee starts/completes; creator reopens/closes/edits/deletes;
   manager overrides). Attachment LINKS work end-to-end; file-upload is stubbed
   (paste a URL/reference). NOTE: MyQueue + System Controller still use the mock
   TaskDetailModal above — those screens are wired in later phases; untouched. */
const REC_FREQ = [["daily", "Daily"], ["weekly", "Weekly"], ["fortnightly", "Fortnightly"], ["monthly", "Monthly"], ["quarterly", "Quarterly"], ["half_yearly", "Half-Yearly"], ["end_of_month", "End of Month"], ["custom", "Custom (every N days)"]];
const REC_FREQ_LABEL = Object.fromEntries(REC_FREQ);
const MONTHLY_FREQ = ["monthly", "quarterly", "half_yearly"];
const TASK_STATUS_META = { open: { tone: "amber", label: "Open" }, in_progress: { tone: "sky", label: "In-process" }, completed: { tone: "emerald", label: "Completed" }, reopened: { tone: "amber", label: "Reopened" }, closed: { tone: "stone", label: "Closed" } };
const TASK_IN = "rounded-md bg-white py-1.5 px-2.5 text-sm text-slate-700 ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400";
const todayISO = () => new Date().toISOString().slice(0, 10);
function taskStatusBadge(t) {
  // A recurring TEMPLATE (kind='recurring', not a generated occurrence) is a definition, not a to-do:
  // show a template-appropriate state — Active / Ended (end-date passed) / Closed — never "Completed".
  if (t.kind === "recurring" && !t.parentTaskId) {
    if (t.status === "closed") return <Badge tone="stone">Closed</Badge>;
    if (t.endDate && t.endDate < todayISO()) return <Badge tone="stone"><Clock size={11} /> Ended</Badge>;
    return <Badge tone="violet"><Repeat size={11} /> Active</Badge>;
  }
  const active = t.status === "open" || t.status === "in_progress" || t.status === "reopened";
  if (active && t.dueDate && t.dueDate < todayISO()) return <Badge tone="rose"><AlertTriangle size={11} /> Overdue</Badge>;
  const m = TASK_STATUS_META[t.status] || { tone: "stone", label: t.status };
  return <Badge tone={m.tone}>{t.status === "completed" ? <CheckCircle2 size={11} /> : t.status === "in_progress" ? <Clock size={11} /> : null}{m.label}</Badge>;
}
function taskKindBadge(t) {
  if (t.parentTaskId) return <Badge tone="violet"><Repeat size={10} /> Recurring</Badge>;
  if (t.kind === "recurring") return <Badge tone="indigo"><Repeat size={10} /> Template</Badge>;
  return <Badge tone="stone">One-time</Badge>;
}

// Link-attachment editor (shared by create + complete). Links work end-to-end; a
// file field is offered as a URL/reference since binary upload is still stubbed.
function LinkAttachmentsEditor({ items, setItems }) {
  const [url, setUrl] = useState("");
  const [label, setLabel] = useState("");
  const add = () => { if (!url.trim()) return; setItems([...items, { kind: "link", linkUrl: url.trim(), label: label.trim() || undefined }]); setUrl(""); setLabel(""); };
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://link…" className={cx(TASK_IN, "min-w-[160px] flex-1")} />
        <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="label (optional)" className={cx(TASK_IN, "w-32")} />
        <Btn variant="soft" onClick={add}><Plus size={13} /> Add link</Btn>
      </div>
      {items.length > 0 && <div className="flex flex-wrap gap-1.5">{items.map((a, i) => <span key={i} className="inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600"><Download size={11} />{a.label || a.linkUrl}<button onClick={() => setItems(items.filter((_, j) => j !== i))} className="text-slate-400 hover:text-rose-500"><X size={11} /></button></span>)}</div>}
    </div>
  );
}

// Create modal — one-time OR recurring template. Anyone can assign (not gated).
function TaskCreateLive({ open, onClose, onCreated, users }) {
  const [kind, setKind] = useState("one_time");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [assigneeId, setAssigneeId] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [frequency, setFrequency] = useState("daily");
  const [endDate, setEndDate] = useState("");
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [intervalDays, setIntervalDays] = useState(7);
  const [links, setLinks] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  React.useEffect(() => {
    if (!open) return;
    setKind("one_time"); setTitle(""); setDescription(""); setAssigneeId(users[0] ? users[0].id : "");
    setDueDate(new Date(Date.now() + 2 * 86400000).toISOString().slice(0, 10));
    setFrequency("daily"); setEndDate(new Date(Date.now() + 90 * 86400000).toISOString().slice(0, 10));
    setDayOfMonth(1); setIntervalDays(7); setLinks([]); setError(""); setBusy(false);
  }, [open, users]);
  const recurring = kind === "recurring";
  const monthly = MONTHLY_FREQ.includes(frequency);
  const submit = async () => {
    if (busy) return;
    if (!title.trim()) { setError("Title is required."); return; }
    if (!assigneeId) { setError("Pick an assignee."); return; }
    if (recurring && !endDate) { setError("Recurring tasks need an end date."); return; }
    const body = { title: title.trim(), description: description.trim() || undefined, assigneeId, kind, dueDate: dueDate || undefined, attachments: links.length ? links : undefined };
    if (recurring) { body.frequency = frequency; body.endDate = endDate || undefined; if (monthly) body.dayOfMonth = Number(dayOfMonth); if (frequency === "custom") body.intervalDays = Number(intervalDays); }
    setBusy(true); setError("");
    try { await tasksApi.createTask(body); onCreated(); }
    catch (e) { setError(e instanceof ApiError ? e.message : "Could not create the task."); setBusy(false); }
  };
  const seg = (val, lbl) => <button onClick={() => setKind(val)} className={cx("flex-1 rounded-md px-3 py-1.5 text-sm font-medium", kind === val ? "bg-white text-slate-800 shadow-sm" : "text-slate-500")}>{lbl}</button>;
  return (
    <Modal open={open} onClose={onClose} title={recurring ? "New recurring task" : "Assign a task"}
      footer={<><Btn variant="soft" onClick={onClose}>Cancel</Btn><Btn onClick={submit} disabled={busy}>{busy ? "Saving…" : recurring ? "Create recurring" : "Assign task"}</Btn></>}>
      <div className="space-y-3">
        <div className="flex gap-1 rounded-lg bg-slate-100 p-1">{seg("one_time", "One-time")}{seg("recurring", "Recurring")}</div>
        <label className="block text-sm"><span className="mb-1 block font-medium text-slate-600">Task</span><input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Clean mixing room" className={cx(TASK_IN, "w-full")} /></label>
        <label className="block text-sm"><span className="mb-1 block font-medium text-slate-600">Description</span><textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} placeholder="Details / instructions…" className={cx(TASK_IN, "w-full")} /></label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-sm"><span className="mb-1 block font-medium text-slate-600">Assign to</span><select value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)} className={cx(TASK_IN, "w-full")}><option value="" disabled>Select…</option>{users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</select></label>
          <label className="block text-sm"><span className="mb-1 block font-medium text-slate-600">{recurring ? "Start / first occurrence" : "Due date"}</span><input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className={cx(TASK_IN, "w-full")} /></label>
        </div>
        {recurring && (
          <div className="space-y-3 rounded-lg bg-violet-50/40 p-3 ring-1 ring-violet-200">
            <div className="grid grid-cols-2 gap-3">
              <label className="block text-sm"><span className="mb-1 block font-medium text-slate-600">Frequency</span><select value={frequency} onChange={(e) => setFrequency(e.target.value)} className={cx(TASK_IN, "w-full")}>{REC_FREQ.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>
              <label className="block text-sm"><span className="mb-1 block font-medium text-slate-600">End date</span><input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={cx(TASK_IN, "w-full")} /></label>
            </div>
            {monthly && <label className="block text-sm"><span className="mb-1 block font-medium text-slate-600">Day of month (1–31)</span><input type="number" min={1} max={31} value={dayOfMonth} onChange={(e) => setDayOfMonth(e.target.value)} className={cx(TASK_IN, "w-32")} /></label>}
            {frequency === "custom" && <label className="block text-sm"><span className="mb-1 block font-medium text-slate-600">Every N days</span><input type="number" min={1} value={intervalDays} onChange={(e) => setIntervalDays(e.target.value)} className={cx(TASK_IN, "w-32")} /></label>}
            <p className="text-xs text-slate-500">Occurrences are auto-generated by the daily job (daily → same day; others → one day before due). The template itself isn't a to-do.</p>
          </div>
        )}
        <div><span className="mb-1 block text-sm font-medium text-slate-600">Attachment links (optional)</span><LinkAttachmentsEditor items={links} setItems={setLinks} /><p className="mt-1 text-xs text-slate-400">Links work end-to-end. File upload isn't wired yet — paste a URL/reference as a link.</p></div>
        {error ? <InlineError message={error} /> : null}
      </div>
    </Modal>
  );
}

// Detail + actions modal (loads getTask). Actions mirror backend authz.
function TaskDetailLive({ id, open, onClose, onChanged, myId, isManager }) {
  const { data, loading, error, reload } = useResource(() => tasksApi.getTask(id), [id]);
  const t = data && data.task;
  const [remarks, setRemarks] = useState("");
  const [compLinks, setCompLinks] = useState([]);
  const [acting, setActing] = useState(false);
  const [actErr, setActErr] = useState("");
  React.useEffect(() => { if (open) { setRemarks(""); setCompLinks([]); setActErr(""); setActing(false); } }, [open, id]);
  const run = async (fn, closeAfter) => {
    setActing(true); setActErr("");
    try { await fn(); onChanged(); if (closeAfter) { onClose(); } else { await reload(); } }
    catch (e) { setActErr(e instanceof ApiError ? e.message : "Action failed."); }
    finally { setActing(false); }
  };
  const isAssignee = t && t.assignee && t.assignee.id === myId;
  const isCreator = t && t.createdBy && t.createdBy.id === myId;
  const canDo = isAssignee || isManager;
  const canManage = isCreator || isManager;
  const active = t && (t.status === "open" || t.status === "in_progress" || t.status === "reopened");
  // A recurring TEMPLATE is a definition, not a to-do — it can be closed/reopened but never
  // started or completed. Hide Start + Complete (and the completion panel) for templates.
  const isTemplate = !!(t && t.kind === "recurring" && !t.parentTaskId);
  const completable = !!(t && active && canDo && !isTemplate);
  const footer = t ? (
    <>
      <Btn variant="soft" onClick={onClose}>Close</Btn>
      {canManage && t.status !== "closed" && <Btn variant="soft" disabled={acting} onClick={() => run(() => tasksApi.setTaskStatus(t.id, { status: "closed" }))}>Close task</Btn>}
      {canManage && (t.status === "completed" || t.status === "closed") && <Btn variant="soft" disabled={acting} onClick={() => run(() => tasksApi.setTaskStatus(t.id, { status: "reopened" }))}><Repeat size={14} /> Reopen</Btn>}
      {completable && t.status !== "in_progress" && <Btn variant="soft" disabled={acting} onClick={() => run(() => tasksApi.setTaskStatus(t.id, { status: "in_progress" }))}><Clock size={14} /> Start</Btn>}
      {completable && <Btn disabled={acting} onClick={() => run(() => tasksApi.setTaskStatus(t.id, { status: "completed", remarks: remarks.trim() || undefined, attachments: compLinks.length ? compLinks : undefined }), true)}><CheckCircle2 size={14} /> {acting ? "…" : "Complete"}</Btn>}
    </>
  ) : <Btn variant="soft" onClick={onClose}>Close</Btn>;
  return (
    <Modal open={open} onClose={onClose} title={t ? t.title : "Task"} wide footer={footer}>
      {loading && !t ? <LoadingBlock label="Loading task…" /> : error ? <InlineError message={error} onRetry={reload} /> : t ? (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">{taskStatusBadge(t)}{taskKindBadge(t)}<span className="text-xs text-slate-400">Assigned to <b className="text-slate-600">{t.assignee ? t.assignee.name : "—"}</b> · due <b className="text-slate-600">{fmtDate(t.dueDate)}</b> · by {t.createdBy ? t.createdBy.name : "—"}</span></div>
          {t.description && <div className="rounded-lg bg-slate-50 p-3 text-sm text-slate-600">{t.description}</div>}
          {t.kind === "recurring" && <div className="rounded-lg bg-violet-50/40 p-3 text-xs text-slate-600 ring-1 ring-violet-200">Recurring template · {REC_FREQ_LABEL[t.frequency] || t.frequency}{MONTHLY_FREQ.includes(t.frequency) ? ` · day ${t.dayOfMonth}` : ""}{t.frequency === "custom" ? ` · every ${t.intervalDays}d` : ""} · start {fmtDate(t.dueDate)} · end {fmtDate(t.endDate)}</div>}
          {t.parentTaskId && <div className="text-xs text-slate-400">Part of a recurring series (a generated occurrence).</div>}
          {t.attachments && t.attachments.assignment.length > 0 && <div><div className="mb-1 text-xs font-semibold text-slate-600">Assignment attachments</div><div className="flex flex-wrap gap-1.5">{t.attachments.assignment.map((a) => <a key={a.id} href={a.linkUrl || a.fileUrl || "#"} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-1 text-xs text-indigo-600 hover:underline"><Download size={11} />{a.label || a.linkUrl || a.fileUrl}</a>)}</div></div>}
          {(t.status === "completed" || t.completedBy) && <div className="rounded-lg bg-emerald-50/50 p-3 ring-1 ring-emerald-200"><div className="text-xs font-semibold text-emerald-800">Completed{t.completedBy ? ` by ${t.completedBy.name}` : ""}{t.completedAt ? ` · ${fmtDateTime(t.completedAt)}` : ""}</div>{t.completionRemarks && <div className="mt-1 text-sm text-slate-600">“{t.completionRemarks}”</div>}{t.attachments && t.attachments.completion.length > 0 && <div className="mt-1.5 flex flex-wrap gap-1.5">{t.attachments.completion.map((a) => <a key={a.id} href={a.linkUrl || a.fileUrl || "#"} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded bg-white px-2 py-1 text-xs text-indigo-600 hover:underline"><Download size={11} />{a.label || a.linkUrl}</a>)}</div>}</div>}
          {completable && <div className="space-y-2 rounded-lg p-3 ring-1 ring-slate-200"><div className="text-xs font-semibold text-slate-600">Complete with optional remarks + links (all optional)</div><textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={2} placeholder="Completion remarks…" className={cx(TASK_IN, "w-full")} /><LinkAttachmentsEditor items={compLinks} setItems={setCompLinks} /></div>}
          {actErr ? <InlineError message={actErr} /> : null}
          {!isAssignee && !isCreator && !isManager && <p className="text-xs text-slate-400">You can view this task; only its assignee or creator can act on it.</p>}
        </div>
      ) : null}
    </Modal>
  );
}

function TasksView({ auth }) {
  const myId = auth && auth.user ? auth.user.id : null;
  const isManager = !!(auth && ((auth.role && auth.role.permissions && (auth.role.permissions.includes("*") || auth.role.permissions.includes("monitor"))) || (auth.user && auth.user.isPlatformAdmin)));
  const [tab, setTab] = useState("mine");
  const [bucket, setBucket] = useState("pending"); // status filter: pending | all | completed (default = actionable)
  const [q, setQ] = useState("");                  // name search (task title / assignee)
  const [dueBy, setDueBy] = useState("");          // due-date filter: show tasks due ON OR BEFORE this date
  const [openCreate, setOpenCreate] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const usersRes = useResource(() => tasksApi.listAssignees(), []);
  const users = (usersRes.data && usersRes.data.users) || [];
  const query = tab === "mine" ? { assigneeId: myId, limit: 200 }
    : tab === "byme" ? { createdByUserId: myId, limit: 200 }
    : tab === "templates" ? { includeTemplates: true, kind: "recurring", limit: 200 }
    : { limit: 200 };
  const { data, loading, error, reload } = useResource(() => ((tab === "mine" || tab === "byme") && !myId ? Promise.resolve({ tasks: [], total: 0 }) : tasksApi.listTasks(query)), [tab, myId]);
  const list = (data && data.tasks) || [];
  // STATUS BUCKETS — client-side, over the ALREADY-AUTHORISED list (privacy/audience unchanged; the
  // backend still scopes the fetch). Pending = open + in_progress + reopened (actionable); Completed =
  // completed + closed; All = everything. Templates tab is not bucketed (a template isn't pending/completed).
  const inBucket = (t) => bucket === "all" ? true
    : bucket === "completed" ? (t.status === "completed" || t.status === "closed")
    : (t.status === "open" || t.status === "in_progress" || t.status === "reopened");
  const ql = q.trim().toLowerCase();
  const shown = tab === "templates" ? list : list.filter((t) =>
    inBucket(t)
    && (!ql || (t.title || "").toLowerCase().includes(ql) || (t.assignee && (t.assignee.name || "").toLowerCase().includes(ql)))
    && (!dueBy || (t.dueDate && String(t.dueDate).slice(0, 10) <= dueBy)));
  // TASK PRIVACY: My Tasks (assigned to me) + By me (created by me) for everyone; "All tasks"
  // only for managers (owner/System-Controller). Backend enforces the same scope regardless.
  const tabs = [["mine", "My Tasks"], ["byme", "By me"], ...(isManager ? [["all", "All tasks"]] : []), ["templates", "Recurring templates"]];
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-slate-500">Assign a task (one-time or recurring) · the assignee completes it (optional remark + link). Anyone can assign. Click a row for full detail.</p>
        <Btn onClick={() => setOpenCreate(true)}><Plus size={15} /> New task</Btn>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1 rounded-lg bg-slate-100 p-1">{tabs.map(([k, l]) => <button key={k} onClick={() => setTab(k)} className={cx("whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium", tab === k ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700")}>{l}</button>)}</div>
        {tab !== "templates" && (
          <div className="flex flex-wrap items-center gap-2 ml-auto">
            <div className="flex gap-1 rounded-lg bg-slate-100 p-1">{[["pending", "Pending"], ["all", "All"], ["completed", "Completed"]].map(([k, l]) => <button key={k} type="button" onClick={() => setBucket(k)} className={cx("whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium", bucket === k ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700")}>{l}</button>)}</div>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title / assignee…" className={cx(TASK_IN, "w-44")} />
            <label className="flex items-center gap-1 text-xs text-slate-400">Due by<input type="date" value={dueBy} onChange={(e) => setDueBy(e.target.value)} className={TASK_IN} />{dueBy && <button type="button" onClick={() => setDueBy("")} className="text-slate-400 hover:text-rose-500" title="Clear due-by"><X size={12} /></button>}</label>
          </div>
        )}
        <span className={cx("text-xs text-slate-400", tab === "templates" ? "ml-auto" : "")}>{data ? (tab === "templates" ? `${data.total} template${data.total === 1 ? "" : "s"}` : `${shown.length} task${shown.length === 1 ? "" : "s"}`) : ""}{loading && list.length > 0 ? " · updating…" : ""}</span>
      </div>
      {tab === "templates" && <p className="text-xs text-slate-400">Recurring definitions. Occurrences are generated by the daily job and appear as normal tasks in the other tabs.</p>}
      {error ? <InlineError message={error} onRetry={reload} /> : null}
      <Card className="overflow-x-auto">
        {loading && list.length === 0 ? <LoadingBlock label="Loading tasks…" /> : (
          <table className="w-full">
            <thead className="border-b border-slate-100"><tr><Th>Task</Th><Th>Assigned to</Th><Th>By</Th><Th>Kind</Th><Th>{tab === "templates" ? "Start" : "Due"}</Th><Th>Status</Th><Th /></tr></thead>
            <tbody className="divide-y divide-slate-50">
              {shown.length === 0 ? <tr><Td className="text-slate-400" colSpan={7}>{list.length ? "No tasks match these filters." : "No tasks here yet."}</Td></tr> : shown.map((t) => (
                <tr key={t.id} className="cursor-pointer hover:bg-slate-50/70" onClick={() => setDetailId(t.id)}>
                  <Td className="font-medium text-slate-700">{t.title}{t.attachmentCount > 0 && <span className="ml-1 inline-flex items-center gap-0.5 text-xs text-slate-400"><Download size={10} />{t.attachmentCount}</span>}{t.description && <div className="truncate text-xs font-normal text-slate-400" style={{ maxWidth: 280 }}>{t.description}</div>}</Td>
                  <Td className="text-slate-600">{t.assignee ? t.assignee.name : "—"}</Td>
                  <Td className="text-slate-500">{t.createdBy ? t.createdBy.name : "—"}</Td>
                  <Td>{taskKindBadge(t)}</Td>
                  <Td className="text-slate-500">{fmtDate(t.dueDate)}</Td>
                  <Td>{taskStatusBadge(t)}</Td>
                  <Td className="text-right"><ChevronRight size={15} className="text-slate-300" /></Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
      <TaskCreateLive open={openCreate} onClose={() => setOpenCreate(false)} onCreated={() => { setOpenCreate(false); reload(); }} users={users} />
      {detailId && <TaskDetailLive id={detailId} open onClose={() => setDetailId(null)} onChanged={reload} myId={myId} isManager={isManager} />}
    </div>
  );
}

/* ============================ People & Org (users, branches, branch-wise holidays) ============================ */
function NewUserModal({ st, open, onClose, dispatch }) {
  const roleKeys = Object.keys(ROLES);
  const [name, setName] = useState("");
  const [branchId, setBranchId] = useState(st.branches[0] ? st.branches[0].id : "BR1");
  const [roleId, setRoleId] = useState("stores");
  const [weekOff, setWeekOff] = useState(0);
  const [inTime, setInTime] = useState("10:00");
  const [outTime, setOutTime] = useState("19:00");
  const submit = () => { if (!name.trim()) return; dispatch({ type: "ADD_USER", name: name.trim(), branchId, dept: ROLES[roleId].dept, role: roleId, weekOff, inTime, outTime }); setName(""); onClose(); };
  return (
    <Modal open={open} onClose={onClose} title="Create user" footer={<><Btn variant="soft" onClick={onClose}>Cancel</Btn><Btn onClick={submit}>Create user</Btn></>}>
      <div className="space-y-3">
        <label className="block text-sm"><span className="mb-1 block font-medium text-slate-600">Name</span><input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" className="w-full rounded-md bg-white py-1.5 px-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400" /></label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-sm"><span className="mb-1 block font-medium text-slate-600">Branch</span><select value={branchId} onChange={(e) => setBranchId(e.target.value)} className="w-full rounded-md bg-white py-1.5 px-2.5 text-sm ring-1 ring-slate-200">{st.branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}</select></label>
          <label className="block text-sm"><span className="mb-1 block font-medium text-slate-600">Role (sets dept + permissions)</span><select value={roleId} onChange={(e) => setRoleId(e.target.value)} className="w-full rounded-md bg-white py-1.5 px-2.5 text-sm ring-1 ring-slate-200">{roleKeys.map((rk) => <option key={rk} value={rk}>{ROLES[rk].name}</option>)}</select></label>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <label className="block text-sm"><span className="mb-1 block font-medium text-slate-600">Week-off</span><select value={weekOff} onChange={(e) => setWeekOff(Number(e.target.value))} className="w-full rounded-md bg-white py-1.5 px-2.5 text-sm ring-1 ring-slate-200">{DOW.map((d, i) => <option key={i} value={i}>{d}</option>)}</select></label>
          <label className="block text-sm"><span className="mb-1 block font-medium text-slate-600">In time</span><input type="time" value={inTime} onChange={(e) => setInTime(e.target.value)} className="w-full rounded-md bg-white py-1.5 px-2.5 text-sm ring-1 ring-slate-200" /></label>
          <label className="block text-sm"><span className="mb-1 block font-medium text-slate-600">Out time</span><input type="time" value={outTime} onChange={(e) => setOutTime(e.target.value)} className="w-full rounded-md bg-white py-1.5 px-2.5 text-sm ring-1 ring-slate-200" /></label>
        </div>
        <p className="text-xs text-slate-400">Permissions come from the role ({ROLES[roleId].name}). Department auto: {ROLES[roleId].dept}. Week-off + shift are per-user, and branch-wise holidays are excluded from SLA delay.</p>
      </div>
    </Modal>
  );
}

/* ===================== People & Org / Settings — Tier 1 (LIVE backend) =====================
   These components read/write the REAL tenant-scoped API (usersApi/rolesApi/
   branchesApi/departmentsApi/companyApi) — NOT the mock reducer. Saves persist.
   Field mapping (mock → API): role key string → roleKey (resolved to role obj on
   read); branch id → branchCode; dept name → departmentName; active bool ↔ status
   enum via deactivate/activate. Guards (last-owner / self-deactivate / system-role
   / in-use / plan-status-slug whitelist) are surfaced inline from ApiError.message. */

// (Tier2Notice retired — every Tier-2 section is now backend-wired; no
// "not yet saved" banners remain anywhere in the app.)

// Create/edit a user against the live API. Create requires an admin-set temporary
// password; edit never sends a password (use Reset password instead).
// RETAIL PHASE A: store-wise access — assign a user to specific internal store parties.
// Self-contained + self-loading; rendered only for retail tenants in EDIT mode. Stores are
// a fixed, known list (parties), so a checkbox multi-select is correct (contrast the free-
// text customer field on the indent form). No stores selected = unrestricted (as today).
function UserStoreAssign({ userId }) {
  const storesRes = useResource(() => storesApi.list(), []);
  const assignedRes = useResource(() => usersApi.getStores(userId), [userId]);
  const allStores = (storesRes.data && storesRes.data.stores) || [];
  const [sel, setSel] = useState(null); // Set<code> | null until loaded
  const [busy, setBusy] = useState(false); const [msg, setMsg] = useState(""); const [err, setErr] = useState("");
  React.useEffect(() => { if (assignedRes.data) setSel(new Set((assignedRes.data.stores || []).map((s) => s.code))); }, [assignedRes.data]);
  const toggle = (code) => setSel((s) => { const n = new Set(s); n.has(code) ? n.delete(code) : n.add(code); return n; });
  const save = async () => {
    setBusy(true); setErr(""); setMsg("");
    try { await usersApi.setStores(userId, [...(sel || [])]); setMsg("Store access saved."); }
    catch (e) { setErr(e instanceof ApiError ? e.message : "Could not save store access."); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-3 rounded-lg bg-slate-50 p-3 ring-1 ring-slate-200">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Store access (retail)</div>
      <p className="mb-2 mt-0.5 text-xs text-slate-500">Restrict this user to specific stores for indent-raise + receive. No stores ticked = unrestricted (all stores), same as today.</p>
      {sel === null ? <div className="text-xs text-slate-400">Loading store access…</div>
        : allStores.length === 0 ? <div className="text-xs text-slate-400">No stores defined yet — add one on the Store Indents screen.</div>
        : <div className="grid gap-1.5 sm:grid-cols-2">
            {allStores.map((s) => (
              <label key={s.code} className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={sel.has(s.code)} onChange={() => toggle(s.code)} className="accent-indigo-600" />
                {s.name} <Mono className="text-xs text-slate-400">{s.code}</Mono>
              </label>
            ))}
          </div>}
      <div className="mt-2 flex items-center gap-2">
        <Btn variant="soft" disabled={busy || sel === null} onClick={save}>{busy ? "Saving…" : "Save store access"}</Btn>
        {msg && <span className="text-xs text-emerald-600">{msg}</span>}
        {err && <span className="text-xs text-rose-600">{err}</span>}
      </div>
    </div>
  );
}

function WiredUserModal({ open, onClose, onSaved, user, rolesList, branchesList, deptsList, usersList, retail }) {
  const edit = !!user;
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [roleKey, setRoleKey] = useState("");
  const [branchCode, setBranchCode] = useState("");
  const [departmentName, setDepartmentName] = useState("");
  const [reportsToId, setReportsToId] = useState("");
  const [weekOff, setWeekOff] = useState(0);
  const [inTime, setInTime] = useState("10:00");
  const [outTime, setOutTime] = useState("19:00");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  React.useEffect(() => {
    if (!open) return;
    setError(""); setBusy(false); setPassword("");
    setName(user ? user.name : "");
    setEmail(user ? (user.email || "") : "");
    // Resolve display objects back to the friendly keys the pickers use.
    setRoleKey(user?.role?.key || (rolesList[0] ? rolesList[0].key : ""));
    setBranchCode(user?.branch ? (branchesList.find((b) => b.id === user.branch.id)?.code || "") : "");
    setDepartmentName(user?.department?.name || "");
    setReportsToId(user?.reportsTo?.id || "");
    setWeekOff(user ? (user.weekOff ?? 0) : 0);
    setInTime(user ? (user.inTime || "10:00") : "10:00");
    setOutTime(user ? (user.outTime || "19:00") : "19:00");
  }, [open, user]);
  const save = async () => {
    if (busy) return;
    if (!name.trim() || !email.trim()) { setError("Name and email are required."); return; }
    if (!roleKey) { setError("Pick a role."); return; }
    if (!edit && password.length < 8) { setError("Temporary password must be at least 8 characters."); return; }
    setBusy(true); setError("");
    const common = {
      roleKey,
      branchCode: branchCode || undefined,
      departmentName: departmentName || undefined,
      reportsToId: reportsToId || undefined,
      weekOff: Number(weekOff),
      inTime, outTime,
    };
    try {
      if (edit) {
        await usersApi.update(user.id, { name: name.trim(), email: email.trim(), ...common });
      } else {
        await usersApi.create({ name: name.trim(), email: email.trim(), password, ...common });
      }
      onSaved();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not save the user.");
      setBusy(false);
    }
  };
  return (
    <Modal open={open} onClose={onClose} title={edit ? ("Edit user · " + user.name) : "Add user"}
      footer={<><Btn variant="soft" onClick={onClose}>Cancel</Btn><Btn onClick={save} disabled={busy}>{busy ? "Saving…" : edit ? "Save changes" : "Create user"}</Btn></>}>
      <div className="grid gap-3 sm:grid-cols-2">
        <SField label="Full name"><input value={name} onChange={(e) => setName(e.target.value)} className={SETTINGS_IN} placeholder="e.g. Amit Saha" /></SField>
        <SField label="Login email"><input value={email} onChange={(e) => setEmail(e.target.value)} className={SETTINGS_IN} placeholder="name@company.com" /></SField>
        {!edit && <SField label="Temporary password (user can change after first login)" full><input type="text" value={password} onChange={(e) => setPassword(e.target.value)} className={SETTINGS_IN} placeholder="min 8 characters" /></SField>}
        <SField label="Role"><select value={roleKey} onChange={(e) => setRoleKey(e.target.value)} className={SETTINGS_IN}>{rolesList.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}</select></SField>
        <SField label="Branch"><select value={branchCode} onChange={(e) => setBranchCode(e.target.value)} className={SETTINGS_IN}><option value="">— None —</option>{branchesList.map((b) => <option key={b.id} value={b.code}>{b.name} ({b.code})</option>)}</select></SField>
        <SField label="Department"><select value={departmentName} onChange={(e) => setDepartmentName(e.target.value)} className={SETTINGS_IN}><option value="">— None —</option>{deptsList.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}</select></SField>
        <SField label="Reports to (manager)"><select value={reportsToId} onChange={(e) => setReportsToId(e.target.value)} className={SETTINGS_IN}><option value="">— None (top level) —</option>{usersList.filter((u) => !user || u.id !== user.id).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</select></SField>
        <SField label="Weekly off"><select value={weekOff} onChange={(e) => setWeekOff(Number(e.target.value))} className={SETTINGS_IN}>{DOW_FULL.map((d, i) => <option key={i} value={i}>{d}</option>)}</select></SField>
        <div className="grid grid-cols-2 gap-2">
          <SField label="In time"><input type="time" value={inTime} onChange={(e) => setInTime(e.target.value)} className={SETTINGS_IN} /></SField>
          <SField label="Out time"><input type="time" value={outTime} onChange={(e) => setOutTime(e.target.value)} className={SETTINGS_IN} /></SField>
        </div>
      </div>
      {/* RETAIL PHASE A: store access — only for retail tenants + existing users. */}
      {edit && retail ? <UserStoreAssign userId={user.id} /> : null}
      {error ? <InlineError message={error} /> : null}
    </Modal>
  );
}

// Admin sets a new temporary password for another user (revokes their sessions).
function ResetPwModal({ open, onClose, onDone, user }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  React.useEffect(() => { if (open) { setPw(""); setBusy(false); setError(""); } }, [open]);
  const save = async () => {
    if (busy) return;
    if (pw.length < 8) { setError("Password must be at least 8 characters."); return; }
    setBusy(true); setError("");
    try { await usersApi.resetPassword(user.id, pw); onDone(); }
    catch (e) { setError(e instanceof ApiError ? e.message : "Could not reset the password."); setBusy(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title={user ? ("Reset password · " + user.name) : "Reset password"}
      footer={<><Btn variant="soft" onClick={onClose}>Cancel</Btn><Btn onClick={save} disabled={busy}>{busy ? "Saving…" : "Set new password"}</Btn></>}>
      <SField label="New temporary password (the user can change it after next login)" full><input type="text" value={pw} onChange={(e) => setPw(e.target.value)} className={SETTINGS_IN} placeholder="min 8 characters" /></SField>
      <p className="mt-2 text-xs text-slate-400">This immediately logs the user out of all sessions and forces re-login with the new password.</p>
      {error ? <InlineError message={error} /> : null}
    </Modal>
  );
}

// Shared live users table — used by BOTH People & Org (users tab) and Settings
// (users section). One component, one users.* client. `me` = the signed-in user.
function WiredUsersPanel({ admin, me, title, retail }) {
  const usersRes = useResource(() => usersApi.list(), []);
  const rolesRes = useResource(() => rolesApi.list(), []);
  const branchesRes = useResource(() => branchesApi.list(), []);
  const deptsRes = useResource(() => departmentsApi.list(), []);
  const [modal, setModal] = useState(null); // null | "new" | userRow
  const [resetFor, setResetFor] = useState(null);
  const [acting, setActing] = useState(null);
  const [actErr, setActErr] = useState("");
  const list = usersRes.data?.users || [];
  const rolesList = rolesRes.data?.roles || [];
  const branchesList = branchesRes.data?.branches || [];
  const deptsList = deptsRes.data?.departments || [];

  const toggle = async (u) => {
    setActing(u.id); setActErr("");
    try {
      if (u.status === "ACTIVE") await usersApi.deactivate(u.id);
      else await usersApi.activate(u.id);
      await usersRes.reload();
    } catch (e) {
      setActErr(`${u.name}: ${e instanceof ApiError ? e.message : "Action failed."}`);
    } finally { setActing(null); }
  };

  return (
    <Card className="p-0">
      <div className="flex items-center justify-between p-4">
        <h3 className="text-base font-semibold text-slate-800">{title || "Users"} <span className="text-sm font-normal text-slate-400">({usersRes.data ? usersRes.data.total : "…"})</span></h3>
        <div className="flex items-center gap-2">{usersRes.loading && list.length > 0 ? <Spinner size={14} /> : null}{admin && <Btn onClick={() => setModal("new")}><Plus size={14} /> Add user</Btn>}</div>
      </div>
      {actErr ? <div className="px-4"><InlineError message={actErr} /></div> : null}
      {usersRes.error ? <div className="px-4"><InlineError message={usersRes.error} onRetry={usersRes.reload} /></div> : null}
      {usersRes.loading && list.length === 0 ? <LoadingBlock label="Loading users…" /> : (
        <div className="overflow-x-auto"><table className="w-full">
          <thead className="border-y border-slate-100"><tr><Th>Name / email</Th><Th>Role</Th><Th>Branch</Th><Th>Department</Th><Th>Reports to</Th><Th>Status</Th><Th></Th></tr></thead>
          <tbody className="divide-y divide-slate-50">
            {list.map((u) => (
              <tr key={u.id} className="hover:bg-slate-50/50">
                <Td><div className="font-medium text-slate-700">{u.name}{me && u.id === me.id && <span className="ml-1.5 text-xs text-indigo-400">(you)</span>}</div><div className="text-xs text-slate-400">{u.email}</div></Td>
                <Td className="text-slate-600">{u.role ? u.role.name : "—"}</Td>
                <Td><Mono className="text-slate-500">{u.branch ? u.branch.name : "—"}</Mono></Td>
                <Td className="text-slate-600">{u.department ? u.department.name : "—"}</Td>
                <Td className="text-slate-500">{u.reportsTo ? u.reportsTo.name : "—"}</Td>
                <Td><Badge tone={u.status === "ACTIVE" ? "emerald" : "stone"}>{u.status === "ACTIVE" ? "Active" : (u.status === "INACTIVE" ? "Inactive" : u.status)}</Badge></Td>
                <Td><div className="flex justify-end gap-1.5">{admin && <>
                  <Btn variant="soft" onClick={() => setModal(u)}>Edit</Btn>
                  <Btn variant="soft" onClick={() => setResetFor(u)}>Reset pw</Btn>
                  <Btn variant="soft" disabled={acting === u.id} onClick={() => toggle(u)}>{u.status === "ACTIVE" ? "Deactivate" : "Activate"}</Btn>
                </>}</div></Td>
              </tr>
            ))}
            {!usersRes.loading && list.length === 0 && <tr><Td className="text-slate-400" colSpan={7}>No users.</Td></tr>}
          </tbody>
        </table></div>
      )}
      <WiredUserModal open={modal !== null} onClose={() => setModal(null)} onSaved={() => { setModal(null); usersRes.reload(); }}
        user={modal === "new" ? null : modal} rolesList={rolesList} branchesList={branchesList} deptsList={deptsList} usersList={list} retail={retail} />
      <ResetPwModal open={resetFor !== null} onClose={() => setResetFor(null)} onDone={() => setResetFor(null)} user={resetFor} />
    </Card>
  );
}

// Live branches. admin=true shows the add form + delete (Settings); admin=false
// is a read-only summary (People & Org branches tab).
function WiredBranchesPanel({ admin }) {
  const res = useResource(() => branchesApi.list(), []);
  const list = res.data?.branches || [];
  const [nm, setNm] = useState(""); const [code, setCode] = useState(""); const [bt, setBt] = useState("Branch");
  const [busy, setBusy] = useState(false); const [err, setErr] = useState("");
  const add = async () => {
    if (busy || !nm.trim() || !code.trim()) return;
    setBusy(true); setErr("");
    try { await branchesApi.create({ name: nm.trim(), code: code.trim(), type: bt }); setNm(""); setCode(""); await res.reload(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : "Could not add the branch."); }
    finally { setBusy(false); }
  };
  const remove = async (b) => {
    setErr("");
    try { await branchesApi.remove(b.id); await res.reload(); }
    catch (e) { setErr(`${b.name}: ${e instanceof ApiError ? e.message : "Delete failed."}`); }
  };
  return (
    <Card className="p-0">
      <div className="p-4"><h3 className="text-base font-semibold text-slate-800">Branches &amp; locations</h3></div>
      {err ? <div className="px-4"><InlineError message={err} /></div> : null}
      {res.error ? <div className="px-4"><InlineError message={res.error} onRetry={res.reload} /></div> : null}
      {res.loading && list.length === 0 ? <LoadingBlock label="Loading branches…" /> : (
        <div className="overflow-x-auto"><table className="w-full">
          <thead className="border-y border-slate-100"><tr><Th>Name</Th><Th>Code</Th><Th>Type</Th><Th className="text-right">Users</Th>{admin && <Th></Th>}</tr></thead>
          <tbody className="divide-y divide-slate-50">
            {list.map((b) => (
              <tr key={b.id} className="hover:bg-slate-50/50">
                <Td className="font-medium text-slate-700">{b.name}</Td>
                <Td><Mono className="text-slate-500">{b.code}</Mono></Td>
                <Td><Badge tone={b.type === "HO" ? "indigo" : "stone"}>{b.type || "Branch"}</Badge></Td>
                <Td className="text-right"><Mono className="text-slate-500">{b.userCount}</Mono></Td>
                {admin && <Td className="text-right">{b.userCount === 0 && <button onClick={() => remove(b)} className="text-slate-300 hover:text-rose-500"><Trash2 size={14} /></button>}</Td>}
              </tr>
            ))}
          </tbody>
        </table></div>
      )}
      {admin && <div className="flex flex-wrap items-end gap-2 border-t border-slate-100 p-4">
        <SField label="Branch name"><input value={nm} onChange={(e) => setNm(e.target.value)} placeholder="e.g. Siliguri Depot" className={SETTINGS_IN + " w-48"} /></SField>
        <SField label="Code"><input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="SIL" className={SETTINGS_IN + " w-24"} /></SField>
        <SField label="Type"><select value={bt} onChange={(e) => setBt(e.target.value)} className={SETTINGS_IN + " w-36"}><option>Branch</option><option value="HO">Head office</option><option>Warehouse</option><option>Depot</option></select></SField>
        <Btn onClick={add} disabled={busy}><Plus size={14} /> Add</Btn>
      </div>}
    </Card>
  );
}

// Live company profile (tenant self-service). plan/status/slug are read-only
// (platform-admin owned); the backend ignores them even if sent.
function WiredCompanySettings({ admin }) {
  const res = useResource(() => companyApi.get(), []);
  const statesRes = useResource(() => gstApi.stateCodes(), []);
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false); const [msg, setMsg] = useState(""); const [err, setErr] = useState("");
  React.useEffect(() => { if (res.data) setD(res.data); }, [res.data]);
  const set = (k, v) => setD((x) => ({ ...x, [k]: v }));
  const states = statesRes.data?.stateCodes || [];
  const FIELDS = [["name", "Trade name"], ["legalName", "Legal name"], ["gstin", "GSTIN"], ["pan", "PAN"], ["cin", "CIN"], ["currency", "Default currency"], ["phone", "Phone"], ["email", "Email"], ["website", "Website"], ["city", "City"], ["state", "State"], ["pincode", "PIN code"]];
  const save = async () => {
    if (busy || !d) return;
    setBusy(true); setErr(""); setMsg("");
    const body = {
      name: d.name, legalName: d.legalName, gstin: d.gstin, pan: d.pan, cin: d.cin,
      addressLine: d.addressLine, city: d.city, state: d.state, pincode: d.pincode,
      phone: d.phone, email: d.email, website: d.website, currency: d.currency,
      gstStateCode: d.gstStateCode || null,
    };
    try { const saved = await companyApi.update(body); setD(saved); setMsg("Saved."); }
    catch (e) { setErr(e instanceof ApiError ? e.message : "Could not save the company profile."); }
    finally { setBusy(false); }
  };
  if (res.loading && !d) return <Card className="p-5"><LoadingBlock label="Loading company…" /></Card>;
  if (res.error && !d) return <Card className="p-5"><InlineError message={res.error} onRetry={res.reload} /></Card>;
  if (!d) return null;
  return (
    <Card className="p-5">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-base font-semibold text-slate-800">Company profile</h3>
        <div className="flex items-center gap-3">{msg && <span className="text-xs text-emerald-600">{msg}</span>}{admin && <Btn onClick={save} disabled={busy}>{busy ? "Saving…" : "Save"}</Btn>}</div>
      </div>
      {err ? <InlineError message={err} /> : null}
      {!d.gstStateCode && <div className="mb-3 flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800 ring-1 ring-amber-200"><AlertTriangle size={15} className="mt-0.5 shrink-0" /><span>Set your <b>GST state</b> below to enable tax invoicing — invoice generation is blocked until it is set (it determines CGST/SGST vs IGST).</span></div>}
      <div className="grid gap-3 sm:grid-cols-2">
        <SField label="Registered address" full><input disabled={!admin} value={d.addressLine || ""} onChange={(e) => set("addressLine", e.target.value)} className={SETTINGS_IN} /></SField>
        {FIELDS.map(([k, label]) => <SField key={k} label={label}><input disabled={!admin} value={d[k] || ""} onChange={(e) => set(k, e.target.value)} className={SETTINGS_IN} /></SField>)}
        <SField label="GST state (for place-of-supply) — required for tax invoicing">
          <select disabled={!admin} value={d.gstStateCode || ""} onChange={(e) => set("gstStateCode", e.target.value)} className={SETTINGS_IN}>
            <option value="">— Select your GST state —</option>
            {states.map((s) => <option key={s.code} value={s.code}>{s.code} · {s.name}</option>)}
          </select>
        </SField>
        <SField label="URL slug (read-only)"><input disabled value={d.slug || ""} className={SETTINGS_IN} /></SField>
        <SField label="Plan (read-only)"><input disabled value={d.plan || ""} className={SETTINGS_IN} /></SField>
        <SField label="Status (read-only)"><input disabled value={d.status || ""} className={SETTINGS_IN} /></SField>
      </div>
      <p className="mt-4 text-xs text-slate-400">Plan / status / slug are managed by the platform administrator and cannot be changed here. GST state{d.gstStateName ? " — currently " + d.gstStateName + " (" + d.gstStateCode + ")" : ""}.</p>
    </Card>
  );
}

// Live roles + permissions. System roles: perms/nav editable (per backend) but
// no delete / key-change. Owner "*" is full-access and locked.
function WiredRolesSettings({ admin }) {
  const res = useResource(() => rolesApi.list(), []);
  const list = res.data?.roles || [];
  const [selId, setSelId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [nk, setNk] = useState(""); const [nn, setNn] = useState("");
  const [busy, setBusy] = useState(false); const [err, setErr] = useState("");
  const sel = list.find((r) => r.id === selId) || list[0];
  React.useEffect(() => { if (!selId && list.length) setSelId(list[0].id); }, [list, selId]);

  const patchRole = async (id, patch) => {
    setErr("");
    try { await rolesApi.update(id, patch); await res.reload(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : "Could not update the role."); }
  };
  const toggleNav = (r, m) => {
    const on = r.navModules.includes(m);
    patchRole(r.id, { navModules: on ? r.navModules.filter((x) => x !== m) : [...r.navModules, m] });
  };
  const togglePerm = (r, p) => {
    const on = r.permissions.includes(p);
    patchRole(r.id, { permissions: on ? r.permissions.filter((x) => x !== p) : [...r.permissions, p] });
  };
  const addRole = async () => {
    if (busy || !nk.trim() || !nn.trim()) return;
    setBusy(true); setErr("");
    try { const out = await rolesApi.create({ key: nk.trim(), name: nn.trim(), permissions: [], navModules: [] }); setNk(""); setNn(""); setAdding(false); await res.reload(); if (out?.id) setSelId(out.id); }
    catch (e) { setErr(e instanceof ApiError ? e.message : "Could not add the role."); }
    finally { setBusy(false); }
  };
  const remove = async (r) => {
    setErr("");
    try { await rolesApi.remove(r.id); setSelId(null); await res.reload(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : "Could not delete the role."); }
  };

  if (res.loading && list.length === 0) return <Card className="p-5"><LoadingBlock label="Loading roles…" /></Card>;
  const isFullAccess = sel && sel.permissions.includes("*");
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
      <Card className="h-min p-2 sm:w-52 sm:shrink-0">
        <div className="flex items-center justify-between px-2 py-1.5"><span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Roles</span>{admin && <button onClick={() => setAdding(!adding)} className="text-indigo-500 hover:text-indigo-600"><Plus size={15} /></button>}</div>
        {adding && <div className="mb-2 space-y-1.5 rounded-lg bg-slate-50 p-2"><input value={nn} onChange={(e) => setNn(e.target.value)} placeholder="Role name" className={SETTINGS_IN} /><input value={nk} onChange={(e) => setNk(e.target.value)} placeholder="key (e.g. qc)" className={SETTINGS_IN} /><Btn className="w-full" onClick={addRole} disabled={busy}>Add role</Btn></div>}
        <div className="space-y-0.5">{list.map((r) => <button key={r.id} onClick={() => setSelId(r.id)} className={cx("flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-sm transition", sel && sel.id === r.id ? "bg-indigo-50 font-medium text-indigo-700" : "text-slate-600 hover:bg-slate-50")}><span className="truncate">{r.name}{r.isSystem && <span className="ml-1 text-xs text-slate-300">·sys</span>}</span><span className="ml-2 text-xs text-slate-400">{r.userCount}</span></button>)}</div>
      </Card>
      <Card className="min-w-0 flex-1 p-4">
        {err ? <InlineError message={err} /> : null}
        {sel && <>
          <div className="mb-1 flex items-center gap-2">
            <h3 className="text-base font-semibold text-slate-800">{sel.name}</h3>
            <Badge tone="stone">{sel.userCount} users</Badge>
            {sel.isSystem && <Badge tone="indigo">system</Badge>}
            <div className="ml-auto">{admin && !sel.isSystem && <Btn variant="soft" onClick={() => remove(sel)}><Trash2 size={13} /> Delete</Btn>}</div>
          </div>
          <p className="mb-4 text-xs text-slate-400">key <Mono>{sel.key}</Mono>{sel.isSystem && " · system role (perms/menu editable; can’t be deleted or renamed)"}</p>
          {isFullAccess ? <div className="rounded-lg bg-emerald-50 p-3 text-sm text-emerald-700">Full access — this role can do every module + every action (permission <Mono>*</Mono>). Locked.</div> : <>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Module access (sidebar menu)</div>
            <div className="mb-5 grid gap-1.5 sm:grid-cols-2">{ALL_MODULES.map((m) => { const on = sel.navModules.includes(m); return <label key={m} className={cx("flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-1.5 text-sm ring-1 transition", on ? "bg-indigo-50 ring-indigo-200 text-slate-700" : "ring-slate-100 text-slate-400")}><input type="checkbox" disabled={!admin} checked={on} onChange={() => toggleNav(sel, m)} className="accent-indigo-600" />{MODULE_LABELS[m] || m}</label>; })}</div>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Capabilities (allowed actions)</div>
            <div className="grid gap-1.5 sm:grid-cols-2">{ALL_PERMS.map((perm) => { const on = sel.permissions.includes(perm); return <label key={perm} className={cx("flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-1.5 text-sm ring-1 transition", on ? "bg-emerald-50 ring-emerald-200 text-slate-700" : "ring-slate-100 text-slate-400")}><input type="checkbox" disabled={!admin} checked={on} onChange={() => togglePerm(sel, perm)} className="accent-emerald-600" />{PERM_LABELS[perm] || perm}</label>; })}</div>
          </>}
        </>}
      </Card>
    </div>
  );
}

// Live departments (head picker from live users; 409 on delete-with-users).
function WiredDeptSettings({ admin }) {
  const res = useResource(() => departmentsApi.list(), []);
  const usersRes = useResource(() => usersApi.list(), []);
  const list = res.data?.departments || [];
  const usersList = usersRes.data?.users || [];
  const [nm, setNm] = useState(""); const [head, setHead] = useState("");
  const [busy, setBusy] = useState(false); const [err, setErr] = useState("");
  const setHeadOf = async (d, headUserId) => {
    setErr("");
    try { await departmentsApi.update(d.id, { headUserId: headUserId || null }); await res.reload(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : "Could not update the department."); }
  };
  const add = async () => {
    if (busy || !nm.trim()) return;
    setBusy(true); setErr("");
    try { await departmentsApi.create({ name: nm.trim(), headUserId: head || null }); setNm(""); setHead(""); await res.reload(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : "Could not add the department."); }
    finally { setBusy(false); }
  };
  const remove = async (d) => {
    setErr("");
    try { await departmentsApi.remove(d.id); await res.reload(); }
    catch (e) { setErr(`${d.name}: ${e instanceof ApiError ? e.message : "Delete failed."}`); }
  };
  return (
    <Card className="p-0">
      <div className="p-4"><h3 className="text-base font-semibold text-slate-800">Departments</h3></div>
      {err ? <div className="px-4"><InlineError message={err} /></div> : null}
      {res.error ? <div className="px-4"><InlineError message={res.error} onRetry={res.reload} /></div> : null}
      {res.loading && list.length === 0 ? <LoadingBlock label="Loading departments…" /> : (
        <div className="overflow-x-auto"><table className="w-full">
          <thead className="border-y border-slate-100"><tr><Th>Department</Th><Th>Head</Th><Th className="text-right">Members</Th><Th></Th></tr></thead>
          <tbody className="divide-y divide-slate-50">
            {list.map((d) => (
              <tr key={d.id} className="hover:bg-slate-50/50">
                <Td className="font-medium text-slate-700">{d.name}</Td>
                <Td>{admin ? <select value={d.head ? d.head.id : ""} onChange={(e) => setHeadOf(d, e.target.value)} className={SETTINGS_IN + " max-w-xs"}><option value="">— Unassigned —</option>{usersList.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</select> : <span className="text-slate-600">{d.head ? d.head.name : "—"}</span>}</Td>
                <Td className="text-right"><Mono className="text-slate-500">{d.userCount}</Mono></Td>
                <Td className="text-right">{admin && d.userCount === 0 && <button onClick={() => remove(d)} className="text-slate-300 hover:text-rose-500"><Trash2 size={14} /></button>}</Td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}
      {admin && <div className="flex flex-wrap items-end gap-2 border-t border-slate-100 p-4"><SField label="New department"><input value={nm} onChange={(e) => setNm(e.target.value)} placeholder="e.g. Quality" className={SETTINGS_IN + " w-48"} /></SField><SField label="Head"><select value={head} onChange={(e) => setHead(e.target.value)} className={SETTINGS_IN + " w-44"}><option value="">— Unassigned —</option>{usersList.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</select></SField><Btn onClick={add} disabled={busy}><Plus size={14} /> Add</Btn></div>}
    </Card>
  );
}

// Org hierarchy — derived (display-only) from live users' reportsTo relationships.
function WiredHierarchySettings() {
  const res = useResource(() => usersApi.list(), []);
  const list = res.data?.users || [];
  const roots = list.filter((u) => !u.reportsTo);
  const kids = (id) => list.filter((u) => u.reportsTo && u.reportsTo.id === id);
  const Node = ({ u }) => {
    const ch = kids(u.id);
    const initials = u.name.split(" ").map((x) => x[0]).slice(0, 2).join("");
    return (
      <div>
        <div className="flex items-center gap-3 rounded-lg border border-slate-100 bg-white px-3 py-2 shadow-sm">
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-indigo-50 text-xs font-semibold text-indigo-600">{initials}</span>
          <div className="min-w-0"><div className="text-sm font-medium text-slate-700">{u.name}{u.status !== "ACTIVE" && <span className="ml-2 text-xs text-slate-400">(inactive)</span>}</div><div className="text-xs text-slate-400">{u.role ? u.role.name : "—"}{u.department ? " · " + u.department.name : ""}</div></div>
        </div>
        {ch.length > 0 && <div className="ml-5 mt-1.5 space-y-1.5 border-l border-dashed border-slate-200 pl-4">{ch.map((c) => <Node key={c.id} u={c} />)}</div>}
      </div>
    );
  };
  return (
    <Card className="p-5">
      <h3 className="mb-1 text-base font-semibold text-slate-800">Organisational hierarchy</h3>
      <p className="mb-4 text-xs text-slate-400">Reporting structure (reports-to), from live user data. Managers are set from Settings → Users.</p>
      {res.loading && list.length === 0 ? <LoadingBlock label="Loading…" /> : res.error ? <InlineError message={res.error} onRetry={res.reload} /> : <div className="space-y-2">{roots.map((u) => <Node key={u.id} u={u} />)}</div>}
    </Card>
  );
}

// ===== Holidays (LIVE) — company-wide + branch-scoped; feeds working-day/SLA calc =====
function HolidayModal({ open, onClose, onSaved, holiday, branchesList }) {
  const edit = !!holiday;
  const [name, setName] = useState("");
  const [date, setDate] = useState("");
  const [branchCode, setBranchCode] = useState(""); // "" = company-wide
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  React.useEffect(() => {
    if (!open) return;
    setError(""); setBusy(false);
    setName(holiday ? holiday.name : "");
    setDate(holiday ? holiday.date : "");
    setBranchCode(holiday?.branch ? (branchesList.find((b) => b.id === holiday.branch.id)?.code || "") : "");
  }, [open, holiday]);
  const save = async () => {
    if (busy) return;
    if (!name.trim() || !date) { setError("Name and date are required."); return; }
    setBusy(true); setError("");
    try {
      if (edit) await holidaysApi.update(holiday.id, { name: name.trim(), date, branchCode: branchCode || null });
      else await holidaysApi.create({ name: name.trim(), date, branchCode: branchCode || undefined });
      onSaved();
    } catch (e) { setError(e instanceof ApiError ? e.message : "Could not save the holiday."); setBusy(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title={edit ? ("Edit holiday · " + holiday.name) : "Add holiday"}
      footer={<><Btn variant="soft" onClick={onClose}>Cancel</Btn><Btn onClick={save} disabled={busy}>{busy ? "Saving…" : edit ? "Save changes" : "Add holiday"}</Btn></>}>
      <div className="grid gap-3 sm:grid-cols-2">
        <SField label="Occasion" full><input value={name} onChange={(e) => setName(e.target.value)} className={SETTINGS_IN} placeholder="e.g. Diwali" /></SField>
        <SField label="Date"><input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={SETTINGS_IN} /></SField>
        <SField label="Scope"><select value={branchCode} onChange={(e) => setBranchCode(e.target.value)} className={SETTINGS_IN}><option value="">Company-wide (all branches)</option>{branchesList.map((b) => <option key={b.id} value={b.code}>{b.name} ({b.code})</option>)}</select></SField>
      </div>
      {error ? <InlineError message={error} /> : null}
    </Modal>
  );
}

function WiredHolidaysPanel({ admin, thisYear }) {
  const startYear = thisYear || 2026;
  const [year, setYear] = useState(startYear);
  const res = useResource(() => holidaysApi.list({ year }), [year]);
  const branchesRes = useResource(() => branchesApi.list(), []);
  const list = res.data?.holidays || [];
  const branchesList = branchesRes.data?.branches || [];
  const [modal, setModal] = useState(null); // null | "new" | holidayRow
  const [err, setErr] = useState("");
  const remove = async (h) => {
    setErr("");
    try { await holidaysApi.remove(h.id); await res.reload(); }
    catch (e) { setErr(`${h.name}: ${e instanceof ApiError ? e.message : "Delete failed."}`); }
  };
  const years = [startYear - 1, startYear, startYear + 1, startYear + 2];
  return (
    <div className="space-y-3">
      <Card className="p-0">
        <div className="flex items-center justify-between p-4">
          <div><h3 className="text-base font-semibold text-slate-800">Holiday calendar</h3><p className="mt-0.5 text-xs text-slate-400">Holidays are excluded from working-day / delay (SLA) calculations, along with each user's week-off.</p></div>
          <div className="flex items-center gap-2">
            {res.loading && list.length > 0 ? <Spinner size={14} /> : null}
            <select value={year} onChange={(e) => setYear(Number(e.target.value))} className={SETTINGS_IN + " w-28"}>{years.map((y) => <option key={y} value={y}>{y}</option>)}</select>
            {admin && <Btn onClick={() => setModal("new")}><Plus size={14} /> Add holiday</Btn>}
          </div>
        </div>
        {err ? <div className="px-4"><InlineError message={err} /></div> : null}
        {res.error ? <div className="px-4"><InlineError message={res.error} onRetry={res.reload} /></div> : null}
        {res.loading && list.length === 0 ? <LoadingBlock label="Loading holidays…" /> : (
          <div className="overflow-x-auto"><table className="w-full">
            <thead className="border-y border-slate-100"><tr><Th>Date</Th><Th>Day</Th><Th>Occasion</Th><Th>Scope</Th>{admin && <Th></Th>}</tr></thead>
            <tbody className="divide-y divide-slate-50">
              {list.map((h) => (
                <tr key={h.id} className="hover:bg-slate-50/50">
                  <Td><Mono className="text-slate-700">{h.date}</Mono></Td>
                  <Td className="text-slate-500">{DOW[new Date(h.date + "T00:00:00").getDay()]}</Td>
                  <Td className="font-medium text-slate-700">{h.name}</Td>
                  <Td>{h.branch ? <Badge tone="stone">{h.branch.name}</Badge> : <Badge tone="indigo">Company-wide</Badge>}</Td>
                  {admin && <Td className="text-right"><div className="flex justify-end gap-1.5"><Btn variant="soft" onClick={() => setModal(h)}>Edit</Btn><button onClick={() => remove(h)} className="text-slate-300 hover:text-rose-500"><Trash2 size={14} /></button></div></Td>}
                </tr>
              ))}
              {!res.loading && list.length === 0 && <tr><Td className="text-slate-400" colSpan={admin ? 5 : 4}>No holidays for {year}.</Td></tr>}
            </tbody>
          </table></div>
        )}
      </Card>
      <HolidayModal open={modal !== null} onClose={() => setModal(null)} onSaved={() => { setModal(null); res.reload(); }} holiday={modal === "new" ? null : modal} branchesList={branchesList} />
    </div>
  );
}

// ===== Shifts (LIVE) — schedule templates; assign copies in/out/week-off onto users =====
const DOW_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
function ShiftModal({ open, onClose, onSaved, shift }) {
  const edit = !!shift;
  const [name, setName] = useState("");
  const [inTime, setInTime] = useState("09:00");
  const [outTime, setOutTime] = useState("18:00");
  const [weekOff, setWeekOff] = useState([]); // array of dow numbers
  const [breakMinutes, setBreakMinutes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  React.useEffect(() => {
    if (!open) return;
    setError(""); setBusy(false);
    setName(shift ? shift.name : "");
    setInTime(shift ? shift.inTime : "09:00");
    setOutTime(shift ? shift.outTime : "18:00");
    setWeekOff(shift ? [...(shift.weekOff || [])] : []);
    setBreakMinutes(shift && shift.breakMinutes != null ? String(shift.breakMinutes) : "");
  }, [open, shift]);
  const toggleDow = (i) => setWeekOff((w) => (w.includes(i) ? w.filter((x) => x !== i) : [...w, i].sort()));
  const save = async () => {
    if (busy) return;
    if (!name.trim()) { setError("Name is required."); return; }
    setBusy(true); setError("");
    const body = { name: name.trim(), inTime, outTime, weekOff, breakMinutes: breakMinutes.trim() === "" ? undefined : Number(breakMinutes) };
    try {
      if (edit) await shiftsApi.update(shift.id, body);
      else await shiftsApi.create(body);
      onSaved();
    } catch (e) { setError(e instanceof ApiError ? e.message : "Could not save the shift."); setBusy(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title={edit ? ("Edit shift · " + shift.name) : "Add shift"}
      footer={<><Btn variant="soft" onClick={onClose}>Cancel</Btn><Btn onClick={save} disabled={busy}>{busy ? "Saving…" : edit ? "Save changes" : "Create shift"}</Btn></>}>
      <div className="grid gap-3 sm:grid-cols-2">
        <SField label="Shift name" full><input value={name} onChange={(e) => setName(e.target.value)} className={SETTINGS_IN} placeholder="e.g. Day Shift" /></SField>
        <SField label="In time"><input type="time" value={inTime} onChange={(e) => setInTime(e.target.value)} className={SETTINGS_IN} /></SField>
        <SField label="Out time"><input type="time" value={outTime} onChange={(e) => setOutTime(e.target.value)} className={SETTINGS_IN} /></SField>
        <SField label="Break (minutes, optional)"><input type="number" value={breakMinutes} onChange={(e) => setBreakMinutes(e.target.value)} className={SETTINGS_IN} placeholder="e.g. 30" /></SField>
        <SField label="Weekly off days" full>
          <div className="flex flex-wrap gap-1.5">{DOW_FULL.map((d, i) => { const on = weekOff.includes(i); return <button type="button" key={i} onClick={() => toggleDow(i)} className={cx("rounded-md px-2.5 py-1 text-sm ring-1 transition", on ? "bg-indigo-50 ring-indigo-200 text-indigo-700" : "ring-slate-200 text-slate-500 hover:bg-slate-50")}>{DOW_SHORT[i]}</button>; })}</div>
        </SField>
      </div>
      <p className="mt-2 text-xs text-slate-400">Assigning this shift to users copies its in/out times + week-off onto them.</p>
      {error ? <InlineError message={error} /> : null}
    </Modal>
  );
}

function AssignShiftModal({ open, onClose, onDone, shift, usersList }) {
  const [sel, setSel] = useState([]); // userIds
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  React.useEffect(() => { if (open) { setSel([]); setBusy(false); setError(""); } }, [open, shift]);
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const run = async (unassign) => {
    if (busy || sel.length === 0) { if (sel.length === 0) setError("Pick at least one user."); return; }
    setBusy(true); setError("");
    try { await shiftsApi.assign(shift.id, sel, unassign); onDone(); }
    catch (e) { setError(e instanceof ApiError ? e.message : "Could not assign the shift."); setBusy(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title={shift ? ("Assign shift · " + shift.name) : "Assign shift"}
      footer={<><Btn variant="soft" onClick={onClose}>Cancel</Btn><Btn variant="soft" onClick={() => run(true)} disabled={busy}>Un-assign</Btn><Btn onClick={() => run(false)} disabled={busy}>{busy ? "Applying…" : "Assign (apply schedule)"}</Btn></>}>
      {shift && <p className="mb-2 text-xs text-slate-500">Applies <Mono>{shift.inTime}–{shift.outTime}</Mono>{shift.weekOff && shift.weekOff.length ? ", week-off " + shift.weekOff.map((i) => DOW_SHORT[i]).join("/") : ""} to the selected users.</p>}
      <div className="max-h-72 space-y-1 overflow-y-auto">
        {usersList.map((u) => <label key={u.id} className={cx("flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-1.5 text-sm ring-1 transition", sel.includes(u.id) ? "bg-indigo-50 ring-indigo-200 text-slate-700" : "ring-slate-100 text-slate-500")}><input type="checkbox" checked={sel.includes(u.id)} onChange={() => toggle(u.id)} className="accent-indigo-600" /><span className="font-medium text-slate-700">{u.name}</span><span className="text-xs text-slate-400">{u.email}</span></label>)}
        {usersList.length === 0 && <p className="px-2 py-2 text-sm text-slate-400">No users.</p>}
      </div>
      {error ? <InlineError message={error} /> : null}
    </Modal>
  );
}

function WiredShiftsPanel({ admin }) {
  const res = useResource(() => shiftsApi.list({ includeInactive: true }), []);
  const usersRes = useResource(() => usersApi.list(), []);
  const list = res.data?.shifts || [];
  const usersList = usersRes.data?.users || [];
  const [modal, setModal] = useState(null); // null | "new" | shiftRow
  const [assignFor, setAssignFor] = useState(null);
  const [acting, setActing] = useState(null);
  const [err, setErr] = useState("");
  const toggle = async (s) => {
    setActing(s.id); setErr("");
    try { if (s.isActive) await shiftsApi.deactivate(s.id); else await shiftsApi.activate(s.id); await res.reload(); }
    catch (e) { setErr(`${s.name}: ${e instanceof ApiError ? e.message : "Action failed."}`); }
    finally { setActing(null); }
  };
  return (
    <Card className="p-0">
      <div className="flex items-center justify-between p-4">
        <div><h3 className="text-base font-semibold text-slate-800">Shifts</h3><p className="mt-0.5 text-xs text-slate-400">Schedule templates — assigning a shift applies its in/out times + week-off to users.</p></div>
        <div className="flex items-center gap-2">{res.loading && list.length > 0 ? <Spinner size={14} /> : null}{admin && <Btn onClick={() => setModal("new")}><Plus size={14} /> Add shift</Btn>}</div>
      </div>
      {err ? <div className="px-4"><InlineError message={err} /></div> : null}
      {res.error ? <div className="px-4"><InlineError message={res.error} onRetry={res.reload} /></div> : null}
      {res.loading && list.length === 0 ? <LoadingBlock label="Loading shifts…" /> : (
        <div className="overflow-x-auto"><table className="w-full">
          <thead className="border-y border-slate-100"><tr><Th>Shift</Th><Th>In–Out</Th><Th>Week-off</Th><Th>Break</Th><Th className="text-right">Users</Th><Th>Status</Th>{admin && <Th></Th>}</tr></thead>
          <tbody className="divide-y divide-slate-50">
            {list.map((s) => (
              <tr key={s.id} className="hover:bg-slate-50/50">
                <Td className="font-medium text-slate-700">{s.name}</Td>
                <Td><Mono className="text-slate-600">{s.inTime}–{s.outTime}</Mono></Td>
                <Td className="text-slate-600">{s.weekOff && s.weekOff.length ? s.weekOff.map((i) => DOW_SHORT[i]).join(", ") : "—"}</Td>
                <Td className="text-slate-500">{s.breakMinutes != null ? s.breakMinutes + "m" : "—"}</Td>
                <Td className="text-right"><Mono className="text-slate-500">{s.userCount}</Mono></Td>
                <Td><Badge tone={s.isActive ? "emerald" : "stone"}>{s.isActive ? "Active" : "Inactive"}</Badge></Td>
                {admin && <Td><div className="flex justify-end gap-1.5">
                  <Btn variant="soft" onClick={() => setModal(s)}>Edit</Btn>
                  <Btn variant="soft" onClick={() => setAssignFor(s)}>Assign</Btn>
                  <Btn variant="soft" disabled={acting === s.id} onClick={() => toggle(s)}>{s.isActive ? "Deactivate" : "Activate"}</Btn>
                </div></Td>}
              </tr>
            ))}
            {!res.loading && list.length === 0 && <tr><Td className="text-slate-400" colSpan={admin ? 7 : 6}>No shifts yet.</Td></tr>}
          </tbody>
        </table></div>
      )}
      <ShiftModal open={modal !== null} onClose={() => setModal(null)} onSaved={() => { setModal(null); res.reload(); }} shift={modal === "new" ? null : modal} />
      <AssignShiftModal open={assignFor !== null} onClose={() => setAssignFor(null)} onDone={() => { setAssignFor(null); res.reload(); usersRes.reload(); }} shift={assignFor} usersList={usersList} />
    </Card>
  );
}

// ===== Number-series (LIVE) — configurable per-docType numbering + FY reset =====
const DOCTYPE_LABEL = { sales_order: "Sales Orders", production_order: "Production Orders", work_order: "Work Orders", purchase: "Purchases", delivery: "Deliveries (Challans)", invoice: "Invoices" };
const fyNow = () => { const d = new Date(); const y = d.getFullYear(); const s = d.getMonth() >= 3 ? y : y - 1; return `${s}-${String((s + 1) % 100).padStart(2, "0")}`; };
// Client-side mirror of the backend renderDocNumber (live preview while editing).
function renderSeriesPreview(prefix, format, padding, fyBased, counter) {
  const n = String(counter).padStart(Number(padding) || 0, "0");
  return String(format || "")
    .replace(/\{PREFIX\}/g, prefix || "")
    .replace(/\{FY\}/g, fyBased ? fyNow() : "")
    .replace(/\{NNNN\}/g, n)
    .replace(/\{N\}/g, n);
}

function SeriesModal({ open, onClose, onSaved, series }) {
  const [prefix, setPrefix] = useState("");
  const [format, setFormat] = useState("");
  const [padding, setPadding] = useState(0);
  const [fyBased, setFyBased] = useState(false);
  const [nextNumber, setNextNumber] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  React.useEffect(() => {
    if (!open || !series) return;
    setError(""); setBusy(false);
    setPrefix(series.prefix); setFormat(series.format); setPadding(series.padding);
    setFyBased(series.fyBased); setNextNumber(series.nextNumber);
  }, [open, series]);
  const save = async () => {
    if (busy) return;
    if (!prefix.trim() || !format.trim()) { setError("Prefix and format are required."); return; }
    setBusy(true); setError("");
    try {
      await numberSeriesApi.update(series.docType, { prefix: prefix.trim(), format: format.trim(), padding: Number(padding), fyBased, nextNumber: Number(nextNumber) });
      onSaved();
    } catch (e) { setError(e instanceof ApiError ? e.message : "Could not save the series."); setBusy(false); }
  };
  const preview = renderSeriesPreview(prefix, format, padding, fyBased, Number(nextNumber) || 0);
  return (
    <Modal open={open} onClose={onClose} title={series ? ("Configure · " + (DOCTYPE_LABEL[series.docType] || series.docType)) : "Configure series"}
      footer={<><Btn variant="soft" onClick={onClose}>Cancel</Btn><Btn onClick={save} disabled={busy}>{busy ? "Saving…" : "Save"}</Btn></>}>
      <div className="grid gap-3 sm:grid-cols-2">
        <SField label="Prefix"><input value={prefix} onChange={(e) => setPrefix(e.target.value)} className={SETTINGS_IN} placeholder="e.g. SO- or INV/" /></SField>
        <SField label="Padding (zero-pad width)"><input type="number" min="0" value={padding} onChange={(e) => setPadding(e.target.value)} className={SETTINGS_IN} /></SField>
        <SField label="Format" full><input value={format} onChange={(e) => setFormat(e.target.value)} className={SETTINGS_IN} placeholder="{PREFIX}{N}" /></SField>
        <SField label="Next number"><input type="number" min="1" value={nextNumber} onChange={(e) => setNextNumber(e.target.value)} className={SETTINGS_IN} /></SField>
        <SField label="Financial-year based"><label className="flex items-center gap-2 py-1.5 text-sm text-slate-600"><input type="checkbox" checked={fyBased} onChange={(e) => setFyBased(e.target.checked)} className="accent-indigo-600" /> Reset counter each FY (Apr–Mar)</label></SField>
      </div>
      <div className="mt-3 rounded-lg bg-slate-50 p-3 text-sm">
        <span className="text-slate-500">Preview of next number: </span><Mono className="font-semibold text-indigo-700">{preview || "—"}</Mono>
      </div>
      <p className="mt-2 text-xs text-slate-400">Tokens: <Mono>{"{PREFIX}"}</Mono> = prefix · <Mono>{"{FY}"}</Mono> = financial year (e.g. {fyNow()}) · <Mono>{"{N}"}</Mono> or <Mono>{"{NNNN}"}</Mono> = the counter (zero-padded). {fyBased ? "FY-based series must include {FY} in the format." : ""}</p>
      {error ? <InlineError message={error} /> : null}
    </Modal>
  );
}

function WiredNumberSeriesPanel({ admin }) {
  const res = useResource(() => numberSeriesApi.list(), []);
  const list = res.data?.series || [];
  const [modal, setModal] = useState(null); // null | seriesRow
  return (
    <Card className="p-0">
      <div className="flex items-center justify-between p-4">
        <div><h3 className="text-base font-semibold text-slate-800">Document number series</h3><p className="mt-0.5 text-xs text-slate-400">Per-document prefix, format &amp; counter. The <span className="font-medium">example</span> shows exactly what the next number will look like. Invoices default to GST-style FY-based numbering.</p></div>
        {res.loading && list.length > 0 ? <Spinner size={14} /> : null}
      </div>
      {res.error ? <div className="px-4"><InlineError message={res.error} onRetry={res.reload} /></div> : null}
      {res.loading && list.length === 0 ? <LoadingBlock label="Loading number series…" /> : (
        <div className="overflow-x-auto"><table className="w-full">
          <thead className="border-y border-slate-100"><tr><Th>Document</Th><Th>Prefix</Th><Th>Format</Th><Th className="text-right">Padding</Th><Th>FY-based</Th><Th>Next example</Th>{admin && <Th></Th>}</tr></thead>
          <tbody className="divide-y divide-slate-50">
            {list.map((s) => (
              <tr key={s.docType} className="hover:bg-slate-50/50">
                <Td className="font-medium text-slate-700">{DOCTYPE_LABEL[s.docType] || s.docType}</Td>
                <Td><Mono className="text-slate-600">{s.prefix}</Mono></Td>
                <Td><Mono className="text-slate-500">{s.format}</Mono></Td>
                <Td className="text-right"><Mono className="text-slate-500">{s.padding}</Mono></Td>
                <Td>{s.fyBased ? <Badge tone="indigo">FY {s.currentFy || fyNow()}</Badge> : <Badge tone="stone">No</Badge>}</Td>
                <Td><Mono className="font-semibold text-indigo-700">{s.example}</Mono></Td>
                {admin && <Td className="text-right"><Btn variant="soft" onClick={() => setModal(s)}>Configure</Btn></Td>}
              </tr>
            ))}
          </tbody>
        </table></div>
      )}
      <SeriesModal open={modal !== null} onClose={() => setModal(null)} onSaved={() => { setModal(null); res.reload(); }} series={modal} />
    </Card>
  );
}

// ===== Units & Tax (LIVE) — UOM master + HSN codes/rates (GST config) =====
const GST_SLABS = [0, 5, 12, 18, 28];

function UomModal({ open, onClose, onSaved, unit }) {
  const edit = !!unit;
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  React.useEffect(() => { if (!open) return; setError(""); setBusy(false); setCode(unit ? unit.code : ""); setName(unit ? unit.name : ""); }, [open, unit]);
  const save = async () => {
    if (busy) return;
    if (!code.trim() || !name.trim()) { setError("Code and name are required."); return; }
    setBusy(true); setError("");
    try { if (edit) await unitsApi.update(unit.id, { code: code.trim(), name: name.trim() }); else await unitsApi.create({ code: code.trim(), name: name.trim() }); onSaved(); }
    catch (e) { setError(e instanceof ApiError ? e.message : "Could not save the unit."); setBusy(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title={edit ? ("Edit unit · " + unit.code) : "Add unit"}
      footer={<><Btn variant="soft" onClick={onClose}>Cancel</Btn><Btn onClick={save} disabled={busy}>{busy ? "Saving…" : edit ? "Save" : "Add unit"}</Btn></>}>
      <div className="grid gap-3 sm:grid-cols-2">
        <SField label="Code"><input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} className={SETTINGS_IN} placeholder="e.g. KG" /></SField>
        <SField label="Name"><input value={name} onChange={(e) => setName(e.target.value)} className={SETTINGS_IN} placeholder="e.g. Kilogram" /></SField>
      </div>
      {error ? <InlineError message={error} /> : null}
    </Modal>
  );
}

function HsnModal({ open, onClose, onSaved, hsn }) {
  const edit = !!hsn;
  const [code, setCode] = useState("");
  const [description, setDescription] = useState("");
  const [rate, setRate] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  React.useEffect(() => { if (!open) return; setError(""); setBusy(false); setCode(hsn ? hsn.code : ""); setDescription(hsn ? (hsn.description || "") : ""); setRate(hsn ? String(hsn.defaultGstRate) : ""); }, [open, hsn]);
  const save = async () => {
    if (busy) return;
    if (!code.trim() || rate === "") { setError("HSN code and default GST rate are required."); return; }
    setBusy(true); setError("");
    const body = { code: code.trim(), description: description.trim() || undefined, defaultGstRate: Number(rate) };
    try { if (edit) await hsnApi.update(hsn.id, body); else await hsnApi.create(body); onSaved(); }
    catch (e) { setError(e instanceof ApiError ? e.message : "Could not save the HSN code."); setBusy(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title={edit ? ("Edit HSN · " + hsn.code) : "Add HSN code"}
      footer={<><Btn variant="soft" onClick={onClose}>Cancel</Btn><Btn onClick={save} disabled={busy}>{busy ? "Saving…" : edit ? "Save" : "Add HSN"}</Btn></>}>
      <div className="grid gap-3 sm:grid-cols-2">
        <SField label="HSN / SAC code"><input value={code} onChange={(e) => setCode(e.target.value)} className={SETTINGS_IN} placeholder="e.g. 1704" /></SField>
        <SField label="Default GST rate (%)"><input type="number" min="0" max="28" step="0.01" value={rate} onChange={(e) => setRate(e.target.value)} className={SETTINGS_IN} placeholder="e.g. 5" /></SField>
        <SField label="Description" full><input value={description} onChange={(e) => setDescription(e.target.value)} className={SETTINGS_IN} placeholder="e.g. Sugar confectionery" /></SField>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-1.5"><span className="text-xs text-slate-400">Common slabs:</span>{GST_SLABS.map((s) => <button type="button" key={s} onClick={() => setRate(String(s))} className={cx("rounded-md px-2 py-0.5 text-xs ring-1 transition", Number(rate) === s ? "bg-indigo-50 ring-indigo-200 text-indigo-700" : "ring-slate-200 text-slate-500 hover:bg-slate-50")}>{s}%</button>)}</div>
      <p className="mt-2 text-xs text-slate-400">Enter the HSN codes + GST rates your CA has determined for your products. Any rate 0–28% is allowed (0% = genuinely 0-rated goods).</p>
      {error ? <InlineError message={error} /> : null}
    </Modal>
  );
}

function WiredUnitsTaxPanel({ admin }) {
  const unitsRes = useResource(() => unitsApi.list({ includeInactive: true }), []);
  const hsnRes = useResource(() => hsnApi.list({ includeInactive: true }), []);
  const unitList = unitsRes.data?.units || [];
  const hsnList = hsnRes.data?.hsnCodes || [];
  const [uModal, setUModal] = useState(null);
  const [hModal, setHModal] = useState(null);
  const [err, setErr] = useState("");
  const toggleUnit = async (u) => { setErr(""); try { if (u.isActive) await unitsApi.deactivate(u.id); else await unitsApi.activate(u.id); await unitsRes.reload(); } catch (e) { setErr(e instanceof ApiError ? e.message : "Action failed."); } };
  const toggleHsn = async (h) => { setErr(""); try { if (h.isActive) await hsnApi.deactivate(h.id); else await hsnApi.activate(h.id); await hsnRes.reload(); } catch (e) { setErr(e instanceof ApiError ? e.message : "Action failed."); } };
  return (
    <div className="space-y-4">
      {err ? <InlineError message={err} /> : null}
      {/* --- UOM --- */}
      <Card className="p-0">
        <div className="flex items-center justify-between p-4"><div><h3 className="text-base font-semibold text-slate-800">Units of measure</h3><p className="mt-0.5 text-xs text-slate-400">Units used on items + invoices. Defaults are seeded; add your own.</p></div>{admin && <Btn onClick={() => setUModal("new")}><Plus size={14} /> Add unit</Btn>}</div>
        {unitsRes.loading && unitList.length === 0 ? <LoadingBlock label="Loading units…" /> : (
          <div className="overflow-x-auto"><table className="w-full">
            <thead className="border-y border-slate-100"><tr><Th>Code</Th><Th>Name</Th><Th>Status</Th>{admin && <Th></Th>}</tr></thead>
            <tbody className="divide-y divide-slate-50">
              {unitList.map((u) => (
                <tr key={u.id} className="hover:bg-slate-50/50">
                  <Td><Mono className="font-medium text-slate-700">{u.code}</Mono></Td>
                  <Td className="text-slate-600">{u.name}</Td>
                  <Td><Badge tone={u.isActive ? "emerald" : "stone"}>{u.isActive ? "Active" : "Inactive"}</Badge></Td>
                  {admin && <Td><div className="flex justify-end gap-1.5"><Btn variant="soft" onClick={() => setUModal(u)}>Edit</Btn><Btn variant="soft" onClick={() => toggleUnit(u)}>{u.isActive ? "Deactivate" : "Activate"}</Btn></div></Td>}
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </Card>
      {/* --- HSN codes + rates --- */}
      <Card className="p-0">
        <div className="flex items-center justify-between p-4"><div><h3 className="text-base font-semibold text-slate-800">HSN codes &amp; GST rates</h3><p className="mt-0.5 text-xs text-slate-400">Your CA-determined HSN/SAC codes + their default GST %. Items reference these for tax invoicing.</p></div>{admin && <Btn onClick={() => setHModal("new")}><Plus size={14} /> Add HSN</Btn>}</div>
        {hsnRes.loading && hsnList.length === 0 ? <LoadingBlock label="Loading HSN codes…" /> : (
          <div className="overflow-x-auto"><table className="w-full">
            <thead className="border-y border-slate-100"><tr><Th>HSN</Th><Th>Description</Th><Th className="text-right">Default GST %</Th><Th className="text-right">Items</Th><Th>Status</Th>{admin && <Th></Th>}</tr></thead>
            <tbody className="divide-y divide-slate-50">
              {hsnList.map((h) => (
                <tr key={h.id} className="hover:bg-slate-50/50">
                  <Td><Mono className="font-medium text-slate-700">{h.code}</Mono></Td>
                  <Td className="text-slate-600">{h.description || "—"}</Td>
                  <Td className="text-right"><Mono className="font-semibold text-indigo-700">{h.defaultGstRate}%</Mono></Td>
                  <Td className="text-right"><Mono className="text-slate-500">{h.itemCount}</Mono></Td>
                  <Td><Badge tone={h.isActive ? "emerald" : "stone"}>{h.isActive ? "Active" : "Inactive"}</Badge></Td>
                  {admin && <Td><div className="flex justify-end gap-1.5"><Btn variant="soft" onClick={() => setHModal(h)}>Edit</Btn><Btn variant="soft" onClick={() => toggleHsn(h)}>{h.isActive ? "Deactivate" : "Activate"}</Btn></div></Td>}
                </tr>
              ))}
              {!hsnRes.loading && hsnList.length === 0 && <tr><Td className="text-slate-400" colSpan={admin ? 6 : 5}>No HSN codes yet — add the codes + rates your CA determined for your products.</Td></tr>}
            </tbody>
          </table></div>
        )}
      </Card>
      <UomModal open={uModal !== null} onClose={() => setUModal(null)} onSaved={() => { setUModal(null); unitsRes.reload(); }} unit={uModal === "new" ? null : uModal} />
      <HsnModal open={hModal !== null} onClose={() => setHModal(null)} onSaved={() => { setHModal(null); hsnRes.reload(); }} hsn={hModal === "new" ? null : hModal} />
    </div>
  );
}

function PeopleView({ st, dispatch, role, auth }) {
  const [tab, setTab] = useState("users");
  const canEdit = can(role, "all");
  const tabs = [["users", "Users"], ["branches", "Branches"], ["holidays", "Holiday list"]];
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex w-min gap-1 rounded-lg bg-slate-100 p-1">{tabs.map(([k, l]) => <button key={k} onClick={() => setTab(k)} className={cx("whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium", tab === k ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700")}>{l}</button>)}</div>
      </div>

      {/* Users + Branches + Holidays now read/write the REAL backend (saves persist). */}
      {tab === "users" && <WiredUsersPanel admin={canEdit} me={auth?.user} title="Users" retail={Array.isArray(auth?.enabledModules) && auth.enabledModules.includes("warehouses")} />}
      {tab === "branches" && <WiredBranchesPanel admin={false} />}
      {tab === "holidays" && <WiredHolidaysPanel admin={canEdit} thisYear={new Date().getFullYear()} />}
    </div>
  );
}



/* ============================ Store Indents (Retail Phase 2) ============================ */
function IndentsView({ auth, role }) {
  const canWrite = !!(authCan(auth, role, "*") || authCan(auth, role, "issue") || authCan(auth, role, "createOrder")
    || (auth && auth.role && (auth.role.navModules || []).includes("indents")));
  const [open, setOpen] = useState(null); // indent code being viewed
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <ClipboardList size={18} className="text-amber-600" />
        <span className="text-lg font-semibold text-slate-800">Store Indents</span>
        <span className="text-xs text-slate-400">a store asks for items — the system routes each to its warehouse</span>
      </div>
      {canWrite && <NewIndentPanel onCreated={(code) => setOpen(code)} />}
      <IndentListPanel onOpen={setOpen} />
      <IndentDetailModal code={open} open={!!open} onClose={() => setOpen(null)} />
    </div>
  );
}

function NewIndentPanel({ onCreated }) {
  const storesRes = useResource(() => storesApi.list(), []);
  const stores = (storesRes.data && storesRes.data.stores) || [];
  // Item → warehouse mappings, so we can warn (before submit) that a picked item has no
  // warehouse mapped and would fail at split. Degrades to no-hint if unavailable.
  const mapRes = useResource(() => itemWarehouseDefaultsApi.list(), []);
  const mappedSet = useMemo(() => {
    if (!mapRes.data) return null; // not loaded / unavailable → show no hint
    const s = new Set();
    (mapRes.data.items || []).forEach((it) => { if (it.warehouse) s.add(it.itemCode); });
    return s;
  }, [mapRes.data]);
  const [store, setStore] = useState("");
  // A row now carries the full picked item object (from ItemPicker), not a typed code.
  const [rows, setRows] = useState([{ item: null, qty: "", unit: "" }]);
  // RETAIL PHASE B: order type. 'regular' (default — byte-identical to before) or
  // 'customer' (captures FREE-TEXT name + phone, typed fresh each time — NOT a picklist).
  const [orderType, setOrderType] = useState("regular");
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerAddress, setCustomerAddress] = useState("");
  // P2b: user-filled customer DELIVERY date & time (datetime-local). Sent as requiredBy for a
  // customer order (overrides the backend +4d default); empty ⇒ omitted ⇒ the default is kept.
  const [deliveryAt, setDeliveryAt] = useState("");
  // RETAIL P-life: "Deliver to" target. Auto-follows the address (non-empty ⇒ Customer) UNTIL the
  // user manually picks a side, after which their choice sticks. Only sent for customer orders.
  const [deliverTarget, setDeliverTarget] = useState("store");
  const [targetTouched, setTargetTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [newStore, setNewStore] = useState(false);
  const [sForm, setSForm] = useState({ code: "", name: "" });
  React.useEffect(() => { if (!store && stores.length) setStore(stores[0].code); }, [stores, store]);
  const setRow = (i, patch) => setRows((r) => r.map((x, j) => j === i ? { ...x, ...patch } : x));
  const IN = "rounded-md bg-white py-1.5 px-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400";
  const createStore = async () => {
    if (!sForm.code.trim() || !sForm.name.trim()) return;
    setBusy(true); setErr("");
    try { const r = await storesApi.create({ code: sForm.code.trim(), name: sForm.name.trim() }); setSForm({ code: "", name: "" }); setNewStore(false); await storesRes.reload(); setStore(r.code); }
    catch (e) { setErr(e instanceof ApiError ? e.message : "Could not create the store."); }
    finally { setBusy(false); }
  };
  // The delivery target the form will send for a customer order (auto-follow until touched).
  const effectiveTarget = targetTouched ? deliverTarget : (customerAddress.trim() ? "customer" : "store");
  // A 'Customer' drop-ship needs an address (mirrors the backend guard) — blocks submit.
  const needsAddress = orderType === "customer" && effectiveTarget === "customer" && !customerAddress.trim();
  // P2d: a customer order REQUIRES a delivery date & time (no +4d fallback) — mirrors needsAddress.
  const needsDeliveryAt = orderType === "customer" && !deliveryAt;
  const submit = async () => {
    // The picker supplies the exact code; unit is sent as entered (backend converts to base).
    const lines = rows.filter((r) => r.item && Number(r.qty) > 0).map((r) => ({ itemCode: r.item.code, qty: Number(r.qty), unit: r.unit || undefined }));
    if (!store || lines.length === 0 || busy) { setErr("Pick a store and add at least one item + qty."); return; }
    // RETAIL PHASE B: client validation mirrors the backend for a customer order.
    const isCustomer = orderType === "customer";
    const cName = customerName.trim(), cPhone = customerPhone.trim(), cAddr = customerAddress.trim();
    if (isCustomer && (!cName || !cPhone)) { setErr("A customer order needs a customer name and phone."); return; }
    if (isCustomer && !/^[0-9+\-\s]{6,20}$/.test(cPhone)) { setErr("Enter a valid customer phone (6–20 chars: digits, spaces, + or -)."); return; }
    // RETAIL P-life: a 'Customer' delivery needs an address (mirror the backend 400).
    if (isCustomer && effectiveTarget === "customer" && !cAddr) { setErr("A customer-delivery order needs a customer address (or set Deliver to → Store)."); return; }
    if (isCustomer && !deliveryAt) { setErr("A customer order needs a delivery date & time."); return; }
    setBusy(true); setErr("");
    // Regular: send only orderType (no target/address). Customer: name+phone (+ address if given) + target.
    const body = { storePartyCode: store, lines, orderType, ...(isCustomer ? { customerName: cName, customerPhone: cPhone, ...(cAddr ? { customerAddress: cAddr } : {}), deliveryTarget: effectiveTarget, ...(deliveryAt ? { requiredBy: new Date(deliveryAt).toISOString() } : {}) } : {}) };
    try { const res = await indentsApi.create(body); setRows([{ item: null, qty: "", unit: "" }]); setCustomerName(""); setCustomerPhone(""); setCustomerAddress(""); setDeliveryAt(""); setDeliverTarget("store"); setTargetTouched(false); setOrderType("regular"); onCreated && onCreated(res.code); }
    catch (e) { setErr(e instanceof ApiError ? e.message : "Could not raise the indent."); }
    finally { setBusy(false); }
  };
  return (
    <Card>
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-3">
        <Plus size={15} className="text-amber-600" /><span className="text-sm font-semibold text-slate-700">Raise an indent</span>
        <span className="text-xs text-slate-400">just item + quantity — no warehouse; the system splits it</span>
      </div>
      {err ? <div className="px-4 pt-2"><InlineError message={err} /></div> : null}
      <div className="space-y-3 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-slate-500">Store</span>
          <select value={store} onChange={(e) => setStore(e.target.value)} className={IN}>
            {stores.length === 0 && <option value="">No stores yet</option>}
            {stores.map((s) => <option key={s.code} value={s.code}>{s.name} ({s.code})</option>)}
          </select>
          <button onClick={() => setNewStore((v) => !v)} className="text-xs font-medium text-indigo-600 hover:underline">+ new store</button>
          {newStore && (
            <span className="inline-flex items-center gap-1.5">
              <input value={sForm.code} onChange={(e) => setSForm((f) => ({ ...f, code: e.target.value }))} placeholder="ST-1" className={cx(IN, "w-20")} />
              <input value={sForm.name} onChange={(e) => setSForm((f) => ({ ...f, name: e.target.value }))} placeholder="Store name" className={IN} />
              <Btn variant="soft" disabled={busy} onClick={createStore}>Add</Btn>
            </span>
          )}
        </div>
        {/* RETAIL PHASE B: order type. Regular (store restock, default) vs Customer (walk-in). */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-slate-500">Order type</span>
          <div className="inline-flex rounded-lg bg-slate-100 p-1">
            {[["regular", "Regular"], ["customer", "Customer"]].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setOrderType(k)} className={cx("rounded-md px-3 py-1 text-sm font-medium", orderType === k ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700")}>{l}</button>
            ))}
          </div>
          {orderType === "customer" && (
            <span className="inline-flex flex-wrap items-center gap-1.5">
              <input value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="Customer name" className={cx(IN, "w-44")} />
              <input value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} placeholder="Customer phone" className={cx(IN, "w-40")} />
              <input value={customerAddress} onChange={(e) => setCustomerAddress(e.target.value)} placeholder="Customer address (for drop-ship)" className={cx(IN, "w-60")} />
              <span className="inline-flex items-center gap-1"><span className="text-xs font-medium text-slate-500">Delivery*</span><input type="datetime-local" value={deliveryAt} onChange={(e) => setDeliveryAt(e.target.value)} title="Delivery date & time (required)" className={cx(IN, "w-52", needsDeliveryAt && "ring-amber-400")} /></span>
            </span>
          )}
          {/* RETAIL P-life: where the goods go — Store (restock; store receives) or Customer (drop-ship). */}
          {orderType === "customer" && (
            <span className="inline-flex items-center gap-1.5">
              <span className="text-xs font-medium text-slate-500">Deliver to</span>
              <div className="inline-flex rounded-lg bg-slate-100 p-1">
                {[["store", "Store"], ["customer", "Customer"]].map(([k, l]) => (
                  <button key={k} type="button" onClick={() => { setTargetTouched(true); setDeliverTarget(k); }} className={cx("rounded-md px-3 py-1 text-sm font-medium", effectiveTarget === k ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700")}>{l}</button>
                ))}
              </div>
            </span>
          )}
          {orderType === "customer" && (needsDeliveryAt
            ? <span className="text-xs font-medium text-amber-600">Set a delivery date & time (required for customer orders).</span>
            : needsAddress
            ? <span className="text-xs font-medium text-amber-600">Add a customer address, or switch Deliver to → Store.</span>
            : <span className="text-xs text-slate-400">billed by the store, not the ERP — no GST invoice</span>)}
        </div>
        <div className="space-y-1.5">
          {rows.map((r, i) => {
            const packs = (r.item && r.item.packs) || [];
            const baseUom = r.item && r.item.uom && r.item.uom.code;
            const hasPacks = packs.length > 0;
            // live "= 10 kg" preview when a non-base unit is chosen
            const base = hasPacks && r.unit && r.qty !== "" ? uomToBase(Number(r.qty), r.unit, baseUom, packs) : NaN;
            // warn (pre-submit) if the picked item has no warehouse mapping.
            const unmapped = r.item && mappedSet && !mappedSet.has(r.item.code);
            return (
              <div key={i} className="space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <ItemPicker value={r.item} onPick={(it) => setRow(i, { item: it, unit: "" })} onClear={() => setRow(i, { item: null, unit: "" })} />
                  <input type="number" value={r.qty} onChange={(e) => setRow(i, { qty: e.target.value })} placeholder="Qty" className={cx(IN, "w-24 mono text-right")} />
                  {hasPacks ? (
                    <select value={r.unit} onChange={(e) => setRow(i, { unit: e.target.value })} className={cx(IN, "w-24")} title="unit you're entering">
                      <option value="">{baseUom || "base"}</option>
                      {packs.map((p) => <option key={p.packName} value={p.packName}>{p.packName}</option>)}
                    </select>
                  ) : baseUom ? <span className="w-12 text-xs text-slate-400">{baseUom}</span> : null}
                  {Number.isFinite(base) && r.unit ? <span className="text-xs text-slate-500">= {uomFmt(base)} {baseUom}</span> : null}
                  {rows.length > 1 && <button onClick={() => setRows((x) => x.filter((_, j) => j !== i))} className="text-slate-300 hover:text-rose-500"><Trash2 size={14} /></button>}
                </div>
                {unmapped ? <div className="pl-1 text-xs text-amber-700">⚠ No warehouse mapped for <Mono>{r.item.code}</Mono> — set it in Warehouses → Item → Warehouse, or this line fails at submit.</div> : null}
              </div>
            );
          })}
          <button onClick={() => setRows((x) => [...x, { item: null, qty: "", unit: "" }])} className="text-xs font-medium text-indigo-600 hover:underline">+ add item</button>
        </div>
        <Btn disabled={busy || needsAddress || needsDeliveryAt} onClick={submit}>{busy ? "Raising…" : "Raise indent"}</Btn>
      </div>
    </Card>
  );
}

// PHASE 8B: indent tone for the derived rollup status.
const indentTone = (s) => s === "Received" ? "emerald" : s === "Awaiting receipt" ? "amber" : s === "Open" ? "stone" : "sky";
function IndentListPanel({ onOpen }) {
  const res = useResource(() => indentsApi.list(), []);
  const all = (res.data && res.data.indents) || [];
  const [filter, setFilter] = useState("outstanding"); // PHASE 8B: all | outstanding — default to Outstanding (P-IND) so received/complete drop out of the default view; "All" still shows them
  const outstandingCount = res.data ? (res.data.outstandingCount ?? all.filter((i) => i.pending > 0).length) : 0;
  const rows = filter === "outstanding" ? all.filter((i) => i.pending > 0) : all;
  return (
    <Card>
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-3">
        <span className="text-sm font-semibold text-slate-700">Indents</span>
        <Badge tone={outstandingCount > 0 ? "amber" : "stone"}>{outstandingCount} with pending</Badge>
        <div className="ml-auto flex gap-1 rounded-lg bg-slate-100 p-1">
          {[["all", "All"], ["outstanding", "Outstanding"]].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} className={cx("rounded-md px-2.5 py-1 text-xs font-medium", filter === k ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700")}>{l}</button>
          ))}
        </div>
      </div>
      {res.loading && all.length === 0 ? <LoadingBlock label="Loading indents…" /> : res.error ? <div className="p-4"><InlineError message={res.error} onRetry={res.reload} /></div> : rows.length === 0 ? (
        <p className="px-4 py-6 text-sm text-slate-400">{filter === "outstanding" ? "Nothing outstanding — every indent is fully received." : "No indents yet."}</p>
      ) : (
        <div className="overflow-x-auto"><table className="w-full">
          <thead className="border-b border-slate-50"><tr><Th>Indent</Th><Th>Store</Th><Th>Date</Th><Th>Status</Th><Th className="text-right">Pending</Th><Th /></tr></thead>
          <tbody className="divide-y divide-slate-50">
            {rows.map((r) => (
              <tr key={r.code} className="hover:bg-slate-50/50">
                <Td><Mono className="font-semibold text-slate-800">{r.code}</Mono></Td>
                <Td className="text-slate-700">{r.store.name}</Td>
                <Td className="text-xs text-slate-500">{fmtDate(r.indentDate)}</Td>
                <Td><Badge tone={indentTone(r.status)}>{r.status}</Badge></Td>
                <Td className="text-right"><Mono className={r.pending > 0 ? "text-amber-700 font-semibold" : "text-slate-300"}>{fmtQty(r.pending)}</Mono></Td>
                <Td className="text-right"><Btn variant="soft" onClick={() => onOpen(r.code)}>View</Btn></Td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}
    </Card>
  );
}

function IndentDetailModal({ code, open, onClose }) {
  const res = useResource(() => (code && open ? indentsApi.get(code) : Promise.resolve(null)), [code, open]);
  const d = res.data;
  return (
    <Modal open={open} onClose={onClose} wide title={"Indent " + (code || "")} footer={<Btn variant="soft" onClick={onClose}>Close</Btn>}>
      {res.loading && !d ? <LoadingBlock label="Loading…" /> : res.error ? <InlineError message={res.error} onRetry={res.reload} /> : d ? (
        <div className="space-y-3">
          <div className="text-sm text-slate-600">Store <b className="text-slate-800">{d.store.name}</b> <Mono className="text-xs text-slate-400">{d.store.code}</Mono> · raised {fmtDate(d.indentDate)}</div>
          <div className="text-xs text-slate-400">Each item was routed to its warehouse and became a line on that warehouse's own order. Progress below is the live status of those child order lines.</div>
          <div className="overflow-x-auto rounded-lg ring-1 ring-slate-200"><table className="w-full">
            <thead className="bg-slate-50/60"><tr><Th>Item</Th><Th className="text-right">Asked</Th><Th>Warehouse</Th><Th className="text-right">Dispatched</Th><Th className="text-right">Delivered</Th><Th className="text-right">Received</Th><Th className="text-right">Pending</Th><Th>Status</Th></tr></thead>
            <tbody className="divide-y divide-slate-50">
              {d.lines.map((l) => (
                <tr key={l.lineCode}>
                  <Td className="font-medium text-slate-700">{l.item.name} <Mono className="ml-1 text-xs text-slate-400">{l.item.code}</Mono>{l.isExtra ? <span className="ml-1"><Badge tone="amber">extra</Badge></span> : null}</Td>
                  <Td className="text-right"><Mono className="font-semibold text-slate-800">{packLabel(l.qtyOrdered, l.item.baseUom, l.item.packs)}</Mono></Td>
                  <Td>{l.warehouse ? <Badge tone="sky">{l.warehouse.code}</Badge> : <Badge tone="rose">Unmapped</Badge>}</Td>
                  <Td className="text-right"><Mono className={l.qtyDispatched > 0 ? "text-slate-600" : "text-slate-300"}>{fmtQty(l.qtyDispatched)}</Mono></Td>
                  <Td className="text-right"><Mono className={l.qtyDelivered > 0 ? "text-slate-600" : "text-slate-300"}>{fmtQty(l.qtyDelivered)}</Mono></Td>
                  <Td className="text-right"><Mono className={l.received > 0 ? "text-emerald-600 font-semibold" : "text-slate-300"}>{fmtQty(l.received)}</Mono></Td>
                  <Td className="text-right"><Mono className={l.pending > 0 ? "text-amber-700 font-semibold" : "text-slate-300"}>{fmtQty(l.pending)}</Mono></Td>
                  <Td><Badge tone={statusTone(l.status)}>{l.status}</Badge></Td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      ) : null}
    </Modal>
  );
}

/* ============================ Warehouses (Retail Phase 1) ============================ */
// PHASE 8A: per-company "maintain warehouse stock" toggle. OFF lets the warehouse punch
// a dispatch qty without tracking stock (no check/debit). Reads/writes companies.settings
// via the retail-gated /warehouses/settings endpoint (server is authoritative).
function RetailStockToggle({ canWrite }) {
  const res = useResource(() => warehousesApi.settings(), []);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const maintain = res.data ? res.data.maintainWarehouseStock : true;
  const toggle = async () => {
    setBusy(true); setErr("");
    try { await warehousesApi.setSettings({ maintainWarehouseStock: !maintain }); await res.reload(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : "Could not save the setting."); }
    finally { setBusy(false); }
  };
  if (res.loading && !res.data) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-xs">
      <span className="font-medium text-slate-600">Warehouse stock tracking:</span>
      <Badge tone={maintain ? "emerald" : "stone"}>{maintain ? "ON — stock checked & debited on send" : "OFF — send any qty, no stock check"}</Badge>
      {canWrite && <button disabled={busy} onClick={toggle} className="rounded-md px-2 py-0.5 font-medium text-indigo-600 ring-1 ring-indigo-200 hover:bg-indigo-50 disabled:opacity-50">{busy ? "Saving…" : maintain ? "Turn OFF" : "Turn ON"}</button>}
      <span className="text-slate-400">Stores that don't keep warehouse stock should turn this OFF.</span>
      {err ? <span className="text-rose-600">{err}</span> : null}
    </div>
  );
}

function WarehousesView({ auth, role }) {
  const [tab, setTab] = useState("list"); // list | mapping | pick
  const canWrite = !!(authCan(auth, role, "*") || authCan(auth, role, "issue")
    || (auth && auth.role && (auth.role.navModules || []).includes("warehouses")));
  // PHASE 8B: the cross-warehouse pick queue, fetched once — drives the "To Pick" tab
  // count AND the per-warehouse count on the list.
  const pendingRes = useResource(() => warehousesApi.pendingPicks(), []);
  const pending = pendingRes.data;
  const totalPick = pending ? pending.totalWaitingOrders : 0;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Boxes size={18} className="text-amber-600" />
        <span className="text-lg font-semibold text-slate-800">Warehouses</span>
        <div className="ml-auto flex gap-1 rounded-lg bg-slate-100 p-1">
          {[["list", "Warehouses & stock"], ["mapping", "Item → Warehouse"], ["pick", totalPick > 0 ? `To Pick (${totalPick})` : "To Pick"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} className={cx("rounded-md px-3 py-1.5 text-sm font-medium", tab === k ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700")}>{l}</button>
          ))}
        </div>
      </div>
      <RetailStockToggle canWrite={canWrite} />
      {tab === "list" ? <WarehouseListPanel canWrite={canWrite} pendingByCode={(pending && pending.countByCode) || {}} pendingSplitByCode={(pending && pending.warehouses) ? Object.fromEntries(pending.warehouses.map((w) => [w.code, { store: w.storePending, customer: w.customerPending }])) : {}} onPicked={pendingRes.reload} />
        : tab === "mapping" ? <ItemWarehousePanel canWrite={canWrite} />
        : <PickQueuePanel canWrite={canWrite} pending={pending} loading={pendingRes.loading} error={pendingRes.error} reload={pendingRes.reload} />}
    </div>
  );
}

// PHASE 8B: the warehouse pick queue — every warehouse with orders still needing a pick,
// each opening straight into the existing Phase-3 pick modal. Data from GET /warehouses/pending-picks.
function PickQueuePanel({ canWrite, pending, loading, error, reload }) {
  const [ord, setOrd] = useState(null); // warehouse code whose pick modal is open
  const whs = ((pending && pending.warehouses) || []).filter((w) => w.waitingOrders > 0);
  return (
    <Card>
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-3">
        <ListChecks size={15} className="text-amber-600" />
        <span className="text-sm font-semibold text-slate-700">Orders waiting to pick</span>
        {pending ? <Badge tone={pending.totalWaitingOrders > 0 ? "amber" : "stone"}>{pending.totalWaitingOrders} order(s) · {pending.totalWaitingLines} line(s)</Badge> : null}
        {/* RETAIL PHASE B: split the pending count by order type (store restock vs customer). */}
        {pending ? <Badge tone="sky">Store: {pending.totalStorePending ?? 0}</Badge> : null}
        {pending ? <Badge tone="violet">Customer: {pending.totalCustomerPending ?? 0}</Badge> : null}
        <button onClick={reload} className="ml-auto text-xs font-medium text-indigo-600 hover:underline">Refresh</button>
      </div>
      {loading && !pending ? <LoadingBlock label="Loading queue…" /> : error ? <div className="p-4"><InlineError message={error} onRetry={reload} /></div> : whs.length === 0 ? (
        <p className="px-4 py-6 text-sm text-slate-400">Nothing waiting to pick — every warehouse order is fully picked.</p>
      ) : (
        <div className="divide-y divide-slate-50">
          {whs.map((w) => (
            <div key={w.code} className="px-4 py-3">
              <div className="flex flex-wrap items-center gap-2">
                <Mono className="font-semibold text-slate-800">{w.code}</Mono>
                <span className="text-sm text-slate-700">{w.name}</span>
                <Badge tone="amber">{w.waitingOrders} order(s) · {w.waitingLines} line(s)</Badge>
                {(w.storePending != null || w.customerPending != null) ? <><Badge tone="sky">Store: {w.storePending ?? 0}</Badge><Badge tone="violet">Customer: {w.customerPending ?? 0}</Badge></> : null}
                <Btn variant="soft" className="ml-auto" onClick={() => setOrd(w.code)}>Pick →</Btn>
              </div>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {w.orders.map((o) => <span key={o.code} className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600"><Mono className="text-slate-700">{o.code}</Mono> · {o.store.name} · {o.waitingLines} line(s){o.orderType === "customer" ? <span className="ml-1 rounded bg-violet-100 px-1 font-medium text-violet-700">Customer</span> : null}</span>)}
              </div>
            </div>
          ))}
        </div>
      )}
      <WarehouseOrdersModal code={ord} canWrite={canWrite} open={!!ord} onClose={() => { setOrd(null); reload && reload(); }} />
    </Card>
  );
}

function WarehouseListPanel({ canWrite, pendingByCode = {}, pendingSplitByCode = {}, onPicked }) {
  const res = useResource(() => warehousesApi.list({ includeInactive: true }), []);
  const rows = (res.data && res.data.warehouses) || [];
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ code: "", name: "", addressLine: "" });
  const [sel, setSel] = useState(null); // warehouse code whose stock is open
  const [ord, setOrd] = useState(null); // warehouse code whose orders are open
  const [ordTab, setOrdTab] = useState("store"); // which tab the orders modal opens on
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const create = async () => {
    if (!form.code.trim() || !form.name.trim() || busy) return;
    setBusy(true); setErr("");
    try { await warehousesApi.create({ code: form.code.trim(), name: form.name.trim(), addressLine: form.addressLine.trim() || undefined }); setForm({ code: "", name: "", addressLine: "" }); setCreating(false); await res.reload(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : "Could not create the warehouse."); }
    finally { setBusy(false); }
  };
  const toggleActive = async (w) => { setBusy(true); setErr(""); try { await warehousesApi.update(w.code, { isActive: !w.isActive }); await res.reload(); } catch (e) { setErr(e instanceof ApiError ? e.message : "Update failed."); } finally { setBusy(false); } };
  const IN = "w-full rounded-md bg-white py-1.5 px-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400";
  return (
    <Card>
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-3">
        <span className="text-sm font-semibold text-slate-700">Your warehouses</span>
        <span className="text-xs text-slate-400">each holds its own stock (independent of store stock)</span>
        {canWrite && <Btn className="ml-auto" onClick={() => setCreating((v) => !v)}><Plus size={14} /> New warehouse</Btn>}
      </div>
      {err ? <div className="px-4 pt-2"><InlineError message={err} /></div> : null}
      {creating && (
        <div className="grid gap-2 border-b border-slate-100 bg-slate-50/50 px-4 py-3 sm:grid-cols-4">
          <input value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} placeholder="Code (e.g. WH-1)" className={IN} />
          <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Name" className={cx(IN, "sm:col-span-2")} />
          <input value={form.addressLine} onChange={(e) => setForm((f) => ({ ...f, addressLine: e.target.value }))} placeholder="Address (optional)" className={IN} />
          <div className="sm:col-span-4"><Btn disabled={busy} onClick={create}>{busy ? "Saving…" : "Create warehouse"}</Btn></div>
        </div>
      )}
      {res.loading && rows.length === 0 ? <LoadingBlock label="Loading warehouses…" /> : res.error ? <div className="p-4"><InlineError message={res.error} onRetry={res.reload} /></div> : rows.length === 0 ? (
        <p className="px-4 py-6 text-sm text-slate-400">No warehouses yet. Create one to start holding stock.</p>
      ) : (
        <div className="overflow-x-auto"><table className="w-full">
          <thead className="border-b border-slate-50"><tr><Th>Code</Th><Th>Name</Th><Th>Address</Th><Th>Status</Th><Th className="text-right">To pick</Th><Th className="text-right">Stock</Th></tr></thead>
          <tbody className="divide-y divide-slate-50">
            {rows.map((w) => (
              <tr key={w.code} className={w.isActive ? "" : "opacity-60"}>
                <Td><Mono className="font-semibold text-slate-800">{w.code}</Mono></Td>
                <Td className="text-slate-700">{w.name}</Td>
                <Td className="text-xs text-slate-500">{w.addressLine || "—"}</Td>
                <Td>{w.isActive ? <Badge tone="emerald">Active</Badge> : <Badge tone="stone">Inactive</Badge>}</Td>
                <Td className="text-right">{pendingByCode[w.code] > 0 ? (
                  <span className="inline-flex flex-col items-end gap-0.5">
                    <Badge tone="amber">{pendingByCode[w.code]}</Badge>
                    {pendingSplitByCode[w.code] && pendingSplitByCode[w.code].customer > 0 ? <span className="text-[10px] font-medium text-slate-400">Store {pendingSplitByCode[w.code].store} · Cust {pendingSplitByCode[w.code].customer}</span> : null}
                  </span>
                ) : <span className="text-xs text-slate-300">0</span>}</Td>
                <Td className="text-right"><div className="flex justify-end gap-1.5">
                  <button onClick={() => setSel(w.code)} className="rounded-md px-2 py-1 text-xs font-medium text-indigo-600 ring-1 ring-indigo-200 hover:bg-indigo-50">View stock</button>
                  <button onClick={() => { setOrdTab("customer"); setOrd(w.code); }} className="rounded-md px-2 py-1 text-xs font-medium text-slate-500 ring-1 ring-slate-200 hover:bg-slate-50">Customer orders ({(pendingSplitByCode[w.code] && pendingSplitByCode[w.code].customer) || 0})</button>
                  <button onClick={() => { setOrdTab("store"); setOrd(w.code); }} className="rounded-md px-2 py-1 text-xs font-medium text-slate-500 ring-1 ring-slate-200 hover:bg-slate-50">Store orders ({(pendingSplitByCode[w.code] && pendingSplitByCode[w.code].store) || 0})</button>
                  {canWrite && <button disabled={busy} onClick={() => toggleActive(w)} className="rounded-md px-2 py-1 text-xs font-medium text-slate-500 ring-1 ring-slate-200 hover:bg-slate-50">{w.isActive ? "Deactivate" : "Reactivate"}</button>}
                </div></Td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}
      <WarehouseStockModal code={sel} canWrite={canWrite} open={!!sel} onClose={() => setSel(null)} />
      <WarehouseOrdersModal code={ord} initialTab={ordTab} canWrite={canWrite} open={!!ord} onClose={() => { setOrd(null); onPicked && onPicked(); }} />
    </Card>
  );
}

function WarehouseOrdersModal({ code, canWrite, open, onClose, initialTab }) {
  const res = useResource(() => (code && open ? indentsApi.warehouseOrders(code) : Promise.resolve(null)), [code, open]);
  const d = res.data;
  // PHASE 8A: does this tenant track warehouse stock? default true; false ⇒ hide stock
  // columns/warnings, allow punching any qty (no stock check).
  const trackStock = !d || d.maintainStock !== false;
  const [send, setSend] = useState({}); // lineCode → qty string (per order), in the chosen unit
  const [sendUnit, setSendUnit] = useState({}); // PHASE 6: lineCode → unit ("" = base)
  const [extras, setExtras] = useState({}); // PHASE 8A: orderCode → [{itemCode, qty}]
  const [busy, setBusy] = useState(null); // order code being picked
  const [err, setErr] = useState("");
  // RETAIL: which orderType tab is showing (opened from the Customer/Store list buttons).
  const [tab, setTab] = useState(initialTab || "store");
  React.useEffect(() => { if (open) { setSend({}); setSendUnit({}); setExtras({}); setErr(""); setTab(initialTab || "store"); } }, [open, code, initialTab]);
  const remaining = (l) => Math.max(0, l.qtyOrdered - l.qtyAllocated);
  // "Send all available": when stock is tracked, cap at what the warehouse holds; when
  // it isn't tracked, default to the remaining needed qty.
  const pickAll = (o) => { setSend((x) => { const n = { ...x }; o.lines.forEach((l) => { const cap = trackStock ? Math.min(remaining(l), l.inWarehouse) : remaining(l); if (cap > 0) n[l.lineCode] = String(cap); }); return n; }); setSendUnit((u) => { const n = { ...u }; o.lines.forEach((l) => { n[l.lineCode] = ""; }); return n; }); };
  const exRows = (oc) => extras[oc] || [];
  const setEx = (oc, i, patch) => setExtras((s) => ({ ...s, [oc]: exRows(oc).map((r, j) => j === i ? { ...r, ...patch } : r) }));
  const addEx = (oc) => setExtras((s) => ({ ...s, [oc]: [...exRows(oc), { itemCode: "", qty: "" }] }));
  const rmEx = (oc, i) => setExtras((s) => ({ ...s, [oc]: exRows(oc).filter((_, j) => j !== i) }));
  const submitPick = async (o) => {
    // qty is sent as entered; the backend converts to base (unit omitted ⇒ base).
    const lines = o.lines.map((l) => ({ lineCode: l.lineCode, qty: Number(send[l.lineCode]) || 0, unit: sendUnit[l.lineCode] || undefined })).filter((l) => l.qty > 0);
    const ex = exRows(o.code).map((r) => ({ itemCode: (r.itemCode || "").trim(), qty: Number(r.qty) || 0 })).filter((r) => r.itemCode && r.qty > 0);
    if (lines.length === 0 && ex.length === 0) { setErr("Enter a quantity to send on at least one line, or add an extra item."); return; }
    setBusy(o.code); setErr("");
    try { await indentsApi.pick(code, o.code, { lines, extras: ex.length ? ex : undefined }); setSend({}); setSendUnit({}); setExtras((s) => ({ ...s, [o.code]: [] })); await res.reload(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : "Could not record the pick."); }
    finally { setBusy(null); }
  };
  const IN = "mono w-24 rounded-md bg-white py-1 px-2 text-right text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400";
  const IN2 = "rounded-md bg-white py-1 px-2 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400";
  return (
    <Modal open={open} onClose={onClose} wide title={"Orders for " + (d ? d.warehouse.name : code || "")} footer={<Btn variant="soft" onClick={onClose}>Close</Btn>}>
      {res.loading && !d ? <LoadingBlock label="Loading orders…" /> : res.error ? <InlineError message={res.error} onRetry={res.reload} /> : d ? (
        <div className="space-y-3">
          <div className="text-xs text-slate-400">This warehouse's own orders (from store indents). Record what you're <b className="text-slate-500">sending now</b> per item — the rest stays pending and can be sent later.</div>
          {!trackStock ? <div className="rounded-md bg-slate-50 px-3 py-1.5 text-[11px] text-slate-500">Warehouse stock isn't tracked for this company — send any quantity (no stock check), send more than asked, or add an extra item below.</div> : null}
          {err ? <InlineError message={err} /> : null}
          {d.orders.length === 0 ? <p className="py-4 text-sm text-slate-400">No orders for this warehouse yet.</p> : (() => {
            // RETAIL: tab by orderType (Store | Customer), then within a tab split ACTIVE
            // (complete=false — needs picking/sending/receiving) vs COMPLETE (complete=true,
            // read-only). Both flags come straight off each order row (backend 6e94d4d).
            const storeAll = d.orders.filter((o) => o.orderType !== "customer");
            const custAll = d.orders.filter((o) => o.orderType === "customer");
            // P-WH-1: per-order warehouse status from existing fields (no backend change).
            // remaining = any line still to SEND (qtyOrdered > qtyAllocated) — the warehouse's job.
            const orderRemaining = (o) => (o.lines || []).some((l) => (l.qtyOrdered - l.qtyAllocated) > 1e-6);
            // PENDING (still to send) → red; AWAITING (sent, not complete) → amber; DONE → green.
            const orderStatus = (o) => o.complete ? { tone: "emerald", label: "Done", rank: 2 }
              : orderRemaining(o) ? { tone: "rose", label: "Pending", rank: 0 }
              : { tone: "amber", label: "Awaiting receipt", rank: 1 };
            // P-WH-3: within a tab, PENDING (red) first (rank), then by DATE — STORE by orderDate
            // (oldest-waiting on top), CUSTOMER by requiredBy (soonest delivery on top); nulls last.
            const dateKeyOf = (o) => tab === "customer" ? o.requiredBy : o.orderDate;
            const cmpDateAsc = (x, y) => (x ? new Date(x).getTime() : Infinity) - (y ? new Date(y).getTime() : Infinity);
            const byStatus = (arr) => [...arr].sort((a, b) => orderStatus(a).rank - orderStatus(b).rank || cmpDateAsc(dateKeyOf(a), dateKeyOf(b)) || a.code.localeCompare(b.code));
            const shown = tab === "customer" ? custAll : storeAll;
            // Sort so PENDING (red) come first, then AWAITING (amber); DONE (green) render in the
            // separate Complete section below (always last).
            const activeOrders = byStatus(shown.filter((o) => !o.complete));
            const doneOrders = shown.filter((o) => o.complete);
            // isActive === false ⇒ view-only Complete card (no send controls).
            const orderCard = (o, isActive) => (
            <div key={o.code} className="rounded-lg ring-1 ring-slate-200">
              <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-3 py-2"><Mono className="font-semibold text-slate-800">{o.code}</Mono>{o.indentCode ? <Mono className="text-xs text-slate-400">· {o.indentCode}</Mono> : null}<span className="text-xs text-slate-500">{o.store.name}</span>
                {/* RETAIL PHASE B: customer orders show the (free-text) customer name/phone. */}
                {o.orderType === "customer" && o.customer ? <span className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-700">Customer: {o.customer.name}{o.customer.phone ? " · " + o.customer.phone : ""}</span> : null}
                {(() => { const s = orderStatus(o); return <Badge tone={s.tone}>{s.label}</Badge>; })()}
                <span className="text-xs text-slate-400">{o.orderType === "customer" ? "Delivery" : "Required"}: {fmtDateTime(o.requiredBy)}</span>
                <span className="text-xs text-slate-400">Ordered: {fmtDateTime(o.orderDate)}</span>
                {canWrite && isActive && <span className="ml-auto flex items-center gap-1.5"><button onClick={() => pickAll(o)} className="rounded-md px-2 py-0.5 text-xs font-medium text-indigo-600 ring-1 ring-indigo-200 hover:bg-indigo-50">{trackStock ? "Send all available" : "Send all needed"}</button><Btn variant="soft" disabled={busy === o.code} onClick={() => submitPick(o)}>{busy === o.code ? "Recording…" : "Confirm send"}</Btn></span>}
              </div>
              <div className="overflow-x-auto"><table className="w-full">
                <thead className="bg-slate-50/60"><tr><Th>Item</Th><Th className="text-right">Needed</Th><Th className="text-right">Already sent</Th>{trackStock && <Th className="text-right">In this warehouse</Th>}{canWrite && isActive && <Th className="text-right">Sending now</Th>}<Th className="text-right">Pending after</Th><Th>Status</Th></tr></thead>
                <tbody className="divide-y divide-slate-50">
                  {o.lines.map((l) => {
                    const rem = remaining(l);
                    const packs = (l.item && l.item.packs) || [];
                    const baseUom = l.item && l.item.baseUom;
                    const unit = sendUnit[l.lineCode] || "";
                    // entered qty is in the chosen unit → convert to BASE for the maths below.
                    const enteredBase = uomToBase(Number(send[l.lineCode]) || 0, unit, baseUom, packs);
                    const eb = Number.isFinite(enteredBase) ? enteredBase : 0;
                    const sending = Math.min(eb, rem);
                    const pendingAfter = Math.max(0, rem - sending);
                    // amber HINT (not a block): tracked ⇒ over what's available; untracked ⇒ over what's asked.
                    const over = eb > (trackStock ? Math.min(rem, l.inWarehouse) : rem) + 1e-6;
                    return (
                      <tr key={l.lineCode}>
                        <Td className="font-medium text-slate-700">{l.item.name} <Mono className="ml-1 text-xs text-slate-400">{l.item.code}</Mono></Td>
                        <Td className="text-right"><Mono className="font-semibold text-slate-800">{packLabel(l.qtyOrdered, baseUom, packs)}</Mono></Td>
                        <Td className="text-right"><Mono className={l.qtyAllocated > 0 ? "text-slate-600" : "text-slate-300"}>{fmtQty(l.qtyAllocated)}</Mono></Td>
                        {trackStock && <Td className="text-right"><Mono className={l.inWarehouse > 0 ? "text-emerald-600" : "text-rose-500"}>{packLabel(l.inWarehouse, baseUom, packs)}</Mono></Td>}
                        {canWrite && isActive && <Td className="text-right">{rem > 0 ? (
                          <span className="inline-flex items-center justify-end gap-1">
                            <input type="number" value={send[l.lineCode] ?? ""} onChange={(e) => setSend((x) => ({ ...x, [l.lineCode]: e.target.value }))} placeholder="0" className={cx(IN, over && "ring-amber-300")} />
                            {packs.length ? (
                              <select value={unit} onChange={(e) => setSendUnit((u) => ({ ...u, [l.lineCode]: e.target.value }))} className="rounded-md bg-white py-1 px-1 text-xs ring-1 ring-slate-200 focus:outline-none">
                                <option value="">{baseUom || "base"}</option>
                                {packs.map((p) => <option key={p.packName} value={p.packName}>{p.packName}</option>)}
                              </select>
                            ) : baseUom ? <span className="text-[10px] text-slate-400">{baseUom}</span> : null}
                          </span>
                        ) : <span className="text-xs text-emerald-600">✓ done</span>}
                        {unit && eb > 0 ? <div className="text-[10px] text-slate-500">= {uomFmt(eb)} {baseUom}</div> : null}</Td>}
                        <Td className="text-right"><Mono className={pendingAfter > 0 ? "text-amber-700" : "text-slate-300"}>{fmtQty(pendingAfter)}</Mono></Td>
                        <Td><Badge tone={statusTone(l.status)}>{l.status}</Badge></Td>
                      </tr>
                    );
                  })}
                </tbody>
              </table></div>
              {canWrite && isActive && (
                <div className="border-t border-slate-100 px-3 py-2">
                  <div className="mb-1 text-[11px] font-medium text-slate-500">Extra items (not on the indent) — sent with this order</div>
                  {exRows(o.code).map((r, i) => (
                    <div key={i} className="mb-1 flex items-center gap-2">
                      {/* Search + select an item (same picker as the Store Indent form) instead of typing a raw code. */}
                      <div className="flex min-w-[10rem] flex-1"><ItemPicker value={r.item || null} onPick={(it) => setEx(o.code, i, { item: it, itemCode: it.code })} onClear={() => setEx(o.code, i, { item: null, itemCode: "" })} /></div>
                      <input type="number" value={r.qty} onChange={(e) => setEx(o.code, i, { qty: e.target.value })} placeholder="Qty" className={cx(IN, "w-24")} />
                      <button onClick={() => rmEx(o.code, i)} className="text-slate-300 hover:text-rose-500"><Trash2 size={14} /></button>
                    </div>
                  ))}
                  <button onClick={() => addEx(o.code)} className="text-xs font-medium text-indigo-600 hover:underline">+ add extra item</button>
                </div>
              )}
            </div>
            );
            return (
              <>
                {/* orderType tab-strip with per-tab counts */}
                <div className="flex w-min gap-1 rounded-lg bg-slate-100 p-1">
                  {[["store", "Store", storeAll.length], ["customer", "Customer", custAll.length]].map(([k, l, n]) => (
                    <button key={k} type="button" onClick={() => setTab(k)} className={cx("flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium", tab === k ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700")}>{l}<Badge tone={tab === k ? "sky" : "stone"}>{n}</Badge></button>
                  ))}
                </div>
                {activeOrders.length === 0 && doneOrders.length === 0 ? <p className="py-4 text-sm text-slate-400">{tab === "customer" ? "No customer orders." : "No store orders."}</p> : null}
                {activeOrders.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 pt-1"><span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Active</span><Badge tone="amber">{activeOrders.length}</Badge></div>
                    {activeOrders.map((o) => orderCard(o, true))}
                  </div>
                )}
                {doneOrders.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 pt-1"><span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Complete</span><Badge tone="emerald">{doneOrders.length}</Badge></div>
                    {doneOrders.map((o) => orderCard(o, false))}
                  </div>
                )}
              </>
            );
          })()}
        </div>
      ) : null}
    </Modal>
  );
}

function WarehouseStockModal({ code, canWrite, open, onClose }) {
  const res = useResource(() => (code && open ? warehousesApi.stock(code) : Promise.resolve(null)), [code, open]);
  const d = res.data;
  // P3b: the add form now holds the full picked item (from ItemPicker); we submit item.code.
  const [form, setForm] = useState({ item: null, qty: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // P3b: per-row inline qty edit.
  const [editCode, setEditCode] = useState(null); // itemCode of the row being edited
  const [editQty, setEditQty] = useState("");
  const [rowBusy, setRowBusy] = useState(false);
  const [savedCode, setSavedCode] = useState(null); // brief "Saved ✓" on the last-updated row
  React.useEffect(() => { if (open) { setForm({ item: null, qty: "" }); setErr(""); setEditCode(null); setSavedCode(null); } }, [open, code]);
  const setStock = async () => {
    if (!form.item || form.qty === "" || busy) return;
    setBusy(true); setErr("");
    try { await warehousesApi.setStock(code, { itemCode: form.item.code, qty: Number(form.qty) }); setForm({ item: null, qty: "" }); await res.reload(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : "Could not set the quantity."); }
    finally { setBusy(false); }
  };
  const startEdit = (l) => { setEditCode(l.itemCode); setEditQty(String(l.qty)); setSavedCode(null); setErr(""); };
  const saveRow = async (l) => {
    if (editQty === "" || rowBusy) return;
    setRowBusy(true); setErr("");
    // Absolute SET for THIS row — same endpoint as the add form.
    try { await warehousesApi.setStock(code, { itemCode: l.itemCode, qty: Number(editQty) }); setEditCode(null); setSavedCode(l.itemCode); await res.reload(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : "Could not update the quantity."); }
    finally { setRowBusy(false); }
  };
  const IN = "rounded-md bg-white py-1.5 px-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400";
  return (
    <Modal open={open} onClose={onClose} title={"Warehouse stock — " + (d ? d.warehouse.name : code || "")}
      footer={<Btn variant="soft" onClick={onClose}>Close</Btn>}>
      {res.loading && !d ? <LoadingBlock label="Loading stock…" /> : res.error ? <InlineError message={res.error} onRetry={res.reload} /> : d ? (
        <div className="space-y-3">
          {err ? <InlineError message={err} /> : null}
          {canWrite && (
            <div className="flex flex-wrap items-end gap-2 rounded-lg bg-slate-50 p-2">
              <div className="min-w-[12rem] flex-1"><span className="mb-1 block text-xs font-medium text-slate-500">Item</span><ItemPicker value={form.item} onPick={(it) => setForm((f) => ({ ...f, item: it }))} onClear={() => setForm((f) => ({ ...f, item: null }))} /></div>
              <label className="text-xs"><span className="mb-1 block font-medium text-slate-500">Set qty</span><input type="number" value={form.qty} onChange={(e) => setForm((f) => ({ ...f, qty: e.target.value }))} placeholder="0" className={cx(IN, "w-28 mono text-right")} /></label>
              <Btn disabled={busy || !form.item || form.qty === ""} onClick={setStock}>{busy ? "Saving…" : "Set qty"}</Btn>
              <span className="text-xs text-slate-400">Opening qty — later phases move stock via the indent/receive flow.</span>
            </div>
          )}
          <div className="overflow-x-auto rounded-lg ring-1 ring-slate-200"><table className="w-full">
            <thead className="bg-slate-50/60"><tr><Th>Item</Th><Th>Type</Th><Th className="text-right">Qty in this warehouse</Th>{canWrite && <Th className="text-right">Update</Th>}</tr></thead>
            <tbody className="divide-y divide-slate-50">
              {(d.lines || []).length === 0 ? <tr><Td className="text-slate-400" colSpan={canWrite ? 4 : 3}>No stock recorded in this warehouse yet.</Td></tr> : d.lines.map((l) => (
                <tr key={l.itemCode}>
                  <Td className="font-medium text-slate-700">{l.itemName} <Mono className="ml-1 text-xs text-slate-400">{l.itemCode}</Mono></Td>
                  <Td className="text-xs"><Badge tone="stone">{l.itemType || "?"}</Badge></Td>
                  <Td className="text-right">{editCode === l.itemCode
                    ? <input type="number" autoFocus value={editQty} onChange={(e) => setEditQty(e.target.value)} className={cx(IN, "w-24 mono text-right")} />
                    : <Mono className="font-semibold text-slate-800">{fmtQty(l.qty)}</Mono>}</Td>
                  {canWrite && <Td className="text-right">{editCode === l.itemCode
                    ? <span className="inline-flex items-center justify-end gap-1.5"><Btn variant="soft" disabled={rowBusy || editQty === ""} onClick={() => saveRow(l)}>{rowBusy ? "Saving…" : "Save"}</Btn><button onClick={() => setEditCode(null)} className="text-xs font-medium text-slate-400 hover:text-slate-600">Cancel</button></span>
                    : <span className="inline-flex items-center justify-end gap-1.5">{savedCode === l.itemCode ? <span className="text-xs font-medium text-emerald-600">Saved ✓</span> : null}<button onClick={() => startEdit(l)} className="rounded-md px-2 py-1 text-xs font-medium text-indigo-600 ring-1 ring-indigo-200 hover:bg-indigo-50">Update</button></span>}</Td>}
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      ) : null}
    </Modal>
  );
}

function ItemWarehousePanel({ canWrite }) {
  const res = useResource(() => itemWarehouseDefaultsApi.list(), []);
  const whRes = useResource(() => warehousesApi.list(), []);
  const items = (res.data && res.data.items) || [];
  const warehouses = (whRes.data && whRes.data.warehouses) || [];
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState("");
  const [onlyUnmapped, setOnlyUnmapped] = useState(false);
  const setMap = async (itemCode, warehouseCode) => {
    setBusy(itemCode); setErr("");
    try { await itemWarehouseDefaultsApi.set({ itemCode, warehouseCode: warehouseCode || null }); await res.reload(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : "Could not save the mapping."); }
    finally { setBusy(null); }
  };
  const shown = onlyUnmapped ? items.filter((it) => !it.warehouse) : items;
  const unmappedCount = items.filter((it) => !it.warehouse).length;
  return (
    <Card>
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-3">
        <span className="text-sm font-semibold text-slate-700">Item → Warehouse</span>
        <span className="text-xs text-slate-400">each item ships from its mapped warehouse — an unmapped item can't be indented later</span>
        {unmappedCount > 0 && <Badge tone="rose">{unmappedCount} unmapped</Badge>}
        <label className="ml-auto flex items-center gap-1.5 text-xs text-slate-500"><input type="checkbox" checked={onlyUnmapped} onChange={(e) => setOnlyUnmapped(e.target.checked)} className="accent-indigo-600" /> Show unmapped only</label>
      </div>
      {err ? <div className="px-4 pt-2"><InlineError message={err} /></div> : null}
      {res.loading && items.length === 0 ? <LoadingBlock label="Loading items…" /> : res.error ? <div className="p-4"><InlineError message={res.error} onRetry={res.reload} /></div> : items.length === 0 ? (
        <p className="px-4 py-6 text-sm text-slate-400">No items yet — add items first (Items &amp; BOM).</p>
      ) : (
        <div className="overflow-x-auto"><table className="w-full">
          <thead className="border-b border-slate-50"><tr><Th>Item</Th><Th>Type</Th><Th>Warehouse</Th></tr></thead>
          <tbody className="divide-y divide-slate-50">
            {shown.map((it) => (
              <tr key={it.itemCode} className={it.warehouse ? "" : "bg-rose-50/30"}>
                <Td className="font-medium text-slate-700">{it.itemName} <Mono className="ml-1 text-xs text-slate-400">{it.itemCode}</Mono></Td>
                <Td className="text-xs"><Badge tone="stone">{it.itemType}</Badge></Td>
                <Td>{canWrite ? (
                  <select value={it.warehouse ? it.warehouse.code : ""} disabled={busy === it.itemCode} onChange={(e) => setMap(it.itemCode, e.target.value)} className="rounded-md bg-white py-1 px-2 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400">
                    <option value="">— unmapped —</option>
                    {warehouses.map((w) => <option key={w.code} value={w.code}>{w.name} ({w.code})</option>)}
                  </select>
                ) : it.warehouse ? <span className="text-sm text-slate-600">{it.warehouse.name}</span> : <span className="text-xs text-rose-600">Unmapped</span>}</Td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}
    </Card>
  );
}


/* ============================ Store Receive (Retail Phase 4) ============================ */
function ReceiveView({ auth, role }) {
  const canWrite = !!(authCan(auth, role, "*") || authCan(auth, role, "issue")
    || (auth && auth.role && (auth.role.navModules || []).includes("warehouses")));
  const res = useResource(() => indentsApi.receivable(), []);
  const rows = (res.data && res.data.deliveries) || [];
  const [open, setOpen] = useState(null); // challan code being received
  const [filter, setFilter] = useState("awaiting"); // PHASE 8B: default to awaiting
  // awaiting = a delivered challan not yet fully received; done = fully received.
  const awaitingCount = res.data ? (res.data.awaitingCount ?? rows.filter((r) => !r.received).length) : 0;
  const shown = filter === "awaiting" ? rows.filter((r) => !r.received) : rows;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Inbox size={18} className="text-amber-600" /><span className="text-lg font-semibold text-slate-800">Store Receive</span>
        <span className="text-xs text-slate-400">record what actually arrived against each challan — the difference stays pending</span>
      </div>
      <Card>
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-3">
          <span className="text-sm font-semibold text-slate-700">Challans</span>
          <Badge tone={awaitingCount > 0 ? "amber" : "stone"}>{awaitingCount} awaiting receipt</Badge>
          <div className="ml-auto flex gap-1 rounded-lg bg-slate-100 p-1">
            {[["awaiting", "Awaiting"], ["all", "All"]].map(([k, l]) => (
              <button key={k} onClick={() => setFilter(k)} className={cx("rounded-md px-2.5 py-1 text-xs font-medium", filter === k ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700")}>{l}</button>
            ))}
          </div>
        </div>
        {res.loading && rows.length === 0 ? <LoadingBlock label="Loading challans…" /> : res.error ? <div className="p-4"><InlineError message={res.error} onRetry={res.reload} /></div> : shown.length === 0 ? (
          <p className="px-4 py-6 text-sm text-slate-400">{filter === "awaiting" ? "Nothing awaiting receipt — all challans received." : "No challans yet."}</p>
        ) : (
          <div className="overflow-x-auto"><table className="w-full">
            <thead className="border-b border-slate-50"><tr><Th>Challan</Th><Th>Status</Th><Th className="text-right">Lines</Th><Th>Delivered</Th><Th className="text-right">Receive</Th></tr></thead>
            <tbody className="divide-y divide-slate-50">
              {shown.map((d) => (
                <tr key={d.code}>
                  <Td><Mono className="font-semibold text-slate-800">{d.code}</Mono></Td>
                  <Td>{d.status === "Delivered" ? <Badge tone="emerald">Delivered</Badge> : <Badge tone="sky">{d.status}</Badge>}</Td>
                  <Td className="text-right"><Mono className="text-slate-600">{d.lineCount}</Mono></Td>
                  <Td className="text-xs text-slate-500">{d.deliveredAt ? fmtDate(d.deliveredAt) : "—"}</Td>
                  <Td className="text-right">{d.received ? <Badge tone="stone">Received</Badge> : <button onClick={() => setOpen(d.code)} className="rounded-md px-2 py-1 text-xs font-medium text-indigo-600 ring-1 ring-indigo-200 hover:bg-indigo-50">Receive</button>}</Td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </Card>
      <ReceiveModal code={open} canWrite={canWrite} open={!!open} onClose={() => setOpen(null)} onSaved={res.reload} />
    </div>
  );
}

function ReceiveModal({ code, canWrite, open, onClose, onSaved }) {
  const res = useResource(() => (code && open ? indentsApi.receipt(code) : Promise.resolve(null)), [code, open]);
  const d = res.data;
  const [recv, setRecv] = useState({}); // deliveryLineId → qty string (in the chosen unit)
  const [recvUnit, setRecvUnit] = useState({}); // PHASE 6: deliveryLineId → unit ("" = base)
  const [why, setWhy] = useState({});  // deliveryLineId → reason
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // Prefill received = sent (in BASE, unit ""), so an unchanged receipt shows diff 0.
  React.useEffect(() => { if (open && d) { const r = {}; (d.lines || []).forEach((l) => { r[l.deliveryLineId] = String(l.received ? l.received.receivedQty : l.sentQty); }); setRecv(r); setRecvUnit({}); setWhy({}); setErr(""); } }, [open, code, d && d.delivery.code]);
  // diff is computed in BASE: convert the entered qty (in its unit) then subtract sent.
  const recvBase = (l) => { const packs = (l.item && l.item.packs) || []; const baseUom = l.item && l.item.baseUom; const rb = uomToBase(Number(recv[l.deliveryLineId]) || 0, recvUnit[l.deliveryLineId] || "", baseUom, packs); return Number.isFinite(rb) ? rb : 0; };
  const diff = (l) => recvBase(l) - l.sentQty;
  const submit = async () => {
    // qty is sent as entered; the backend converts to base (unit omitted ⇒ base).
    const lines = (d.lines || []).map((l) => ({ deliveryLineId: l.deliveryLineId, receivedQty: Number(recv[l.deliveryLineId]) || 0, unit: recvUnit[l.deliveryLineId] || undefined, reason: (why[l.deliveryLineId] || "").trim() || undefined }));
    // client-side mirror of the server rule: reason required on any difference
    const missing = (d.lines || []).filter((l) => Math.abs(diff(l)) > 1e-6 && !(why[l.deliveryLineId] || "").trim());
    if (missing.length) { setErr("Add a reason for every line where received differs from sent."); return; }
    setBusy(true); setErr("");
    try { await indentsApi.receive(code, { lines }); onSaved && onSaved(); onClose(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : "Could not record the receipt."); setBusy(false); }
  };
  const IN = "mono w-24 rounded-md bg-white py-1 px-2 text-right text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400";
  return (
    <Modal open={open} onClose={onClose} wide title={"Receive " + (d ? d.delivery.code : code || "")}
      footer={d && canWrite ? <><Btn variant="soft" onClick={onClose}>Cancel</Btn><Btn disabled={busy} onClick={submit}>{busy ? "Saving…" : "Confirm receipt"}</Btn></> : <Btn variant="soft" onClick={onClose}>Close</Btn>}>
      {res.loading && !d ? <LoadingBlock label="Loading challan…" /> : res.error ? <InlineError message={res.error} onRetry={res.reload} /> : d ? (
        <div className="space-y-3">
          <div className="text-xs text-slate-400">Enter what actually <b className="text-slate-500">arrived</b> per item. If it differs from what was sent, tell us <b className="text-slate-500">why</b> — the shortfall stays pending and can arrive on a later challan.</div>
          {err ? <InlineError message={err} /> : null}
          <div className="overflow-x-auto rounded-lg ring-1 ring-slate-200"><table className="w-full">
            <thead className="bg-slate-50/60"><tr><Th>Item</Th><Th className="text-right">Sent</Th>{canWrite && <Th className="text-right">Received</Th>}<Th className="text-right">Difference</Th><Th>Why?</Th></tr></thead>
            <tbody className="divide-y divide-slate-50">
              {d.lines.map((l) => {
                const df = diff(l);
                const needReason = Math.abs(df) > 1e-6;
                const packs = (l.item && l.item.packs) || [];
                const baseUom = l.item && l.item.baseUom;
                const unit = recvUnit[l.deliveryLineId] || "";
                const rb = recvBase(l);
                return (
                  <tr key={l.deliveryLineId}>
                    <Td className="font-medium text-slate-700">{l.item.name} <Mono className="ml-1 text-xs text-slate-400">{l.item.code}</Mono></Td>
                    <Td className="text-right"><Mono className="font-semibold text-slate-800">{packLabel(l.sentQty, baseUom, packs)}</Mono></Td>
                    {canWrite && <Td className="text-right">
                      <span className="inline-flex items-center justify-end gap-1">
                        <input type="number" value={recv[l.deliveryLineId] ?? ""} onChange={(e) => setRecv((x) => ({ ...x, [l.deliveryLineId]: e.target.value }))} className={IN} />
                        {packs.length ? (
                          <select value={unit} onChange={(e) => setRecvUnit((u) => ({ ...u, [l.deliveryLineId]: e.target.value }))} className="rounded-md bg-white py-1 px-1 text-xs ring-1 ring-slate-200 focus:outline-none">
                            <option value="">{baseUom || "base"}</option>
                            {packs.map((p) => <option key={p.packName} value={p.packName}>{p.packName}</option>)}
                          </select>
                        ) : baseUom ? <span className="text-[10px] text-slate-400">{baseUom}</span> : null}
                      </span>
                      {unit ? <div className="text-[10px] text-slate-500">= {uomFmt(rb)} {baseUom}</div> : null}
                    </Td>}
                    <Td className="text-right"><Mono className={df < -1e-6 ? "text-rose-600" : df > 1e-6 ? "text-amber-700" : "text-slate-300"}>{df > 0 ? "+" : ""}{fmtQty(df)}</Mono></Td>
                    <Td>{canWrite ? <input value={why[l.deliveryLineId] ?? ""} onChange={(e) => setWhy((x) => ({ ...x, [l.deliveryLineId]: e.target.value }))} placeholder={needReason ? "Reason required (damage, short…)" : "—"} className={cx("w-full rounded-md bg-white py-1 px-2 text-sm ring-1 focus:outline-none focus:ring-indigo-400", needReason && !(why[l.deliveryLineId] || "").trim() ? "ring-amber-300" : "ring-slate-200")} /> : (l.received && l.received.reason) || "—"}</Td>
                  </tr>
                );
              })}
            </tbody>
          </table></div>
        </div>
      ) : null}
    </Modal>
  );
}

/* ============================ Item Packs (Retail Phase 6) ============================ */
// Define an item's alternate handling units (tin / box / pcs) as multiples of its
// EXISTING base unit. "2 tin = 10 kg" ⇒ tin factor 5. Quantities are always STORED
// in the base unit; packs only convert at the retail entry edges + for display.
// Retail-gated (rides the "warehouses" module) so the shared Items screen is
// untouched. Data-gated: an item with no packs behaves exactly as before.
/* ============================ Retail Deliveries (P2c) — send to store / drop-ship to customer, bulk mark-delivered ============================ */
function StoreDispatchView({ auth, role }) {
  // Write actions (location flip + mark-delivered) require the "deliver" cap; monitor-only
  // users still see the state. Mirrors how other retail views gate writes by capability.
  const canDeliver = !!(authCan(auth, role, "*") || authCan(auth, role, "deliver"));
  const res = useResource(() => retailDeliveriesApi.list(), []);
  const all = (res.data && res.data.deliveries) || [];
  const [tab, setTab] = useState("pending"); // PRIMARY: pending | delivered  (default pending)
  const [seg, setSeg] = useState("all"); // SECONDARY filter within a tab: all | store | customer
  const [delivBy, setDelivBy] = useState(""); // optional "Delivered by" name for Mark Delivered
  const [sel, setSel] = useState(() => new Set());
  const [expanded, setExpanded] = useState(() => new Set()); // P-expand: challan codes whose line items are shown
  const [busy, setBusy] = useState(null); // delivery code being toggled, or "bulk"
  const [notice, setNotice] = useState("");
  const [err, setErr] = useState("");
  const toggleExpand = (code) => setExpanded((s) => { const n = new Set(s); n.has(code) ? n.delete(code) : n.add(code); return n; });
  const isDelivered = (d) => !!d.deliveredAt || d.status === "Delivered";
  // PRIMARY split is the delivery lifecycle (Pending vs Delivered). Store/Customer becomes a
  // SECONDARY filter — and the per-row Location pill always shows it too, so the store-vs-customer
  // distinction is never lost even in the "All" segment.
  const pendingList = all.filter((d) => !isDelivered(d));
  const deliveredList = all.filter((d) => isDelivered(d));
  const bySeg = (arr) => seg === "all" ? arr : seg === "customer" ? arr.filter((d) => d.deliveryTarget === "customer") : arr.filter((d) => d.deliveryTarget !== "customer");
  const shown = bySeg(tab === "delivered" ? deliveredList : pendingList);
  const showCustomer = seg !== "store"; // Customer column visible unless filtered to Store-only
  const toggleSel = (code) => setSel((s) => { const n = new Set(s); n.has(code) ? n.delete(code) : n.add(code); return n; });
  const selectedCodes = [...sel].filter((code) => shown.some((d) => d.code === code && !isDelivered(d)));

  const flip = async (d) => {
    if (!canDeliver || isDelivered(d) || busy) return;
    setBusy(d.code); setErr(""); setNotice("");
    try { await retailDeliveriesApi.setTarget(d.code, d.deliveryTarget === "customer" ? "store" : "customer"); await res.reload(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : "Could not change the location."); }
    finally { setBusy(null); }
  };
  const markDelivered = async () => {
    if (!canDeliver || selectedCodes.length === 0 || busy) return;
    setBusy("bulk"); setErr(""); setNotice("");
    try {
      const name = delivBy.trim();
      const r = await retailDeliveriesApi.bulkDeliver(selectedCodes, name || undefined);
      const ok = (r.results || []).filter((x) => x.ok).length;
      const fails = (r.results || []).filter((x) => !x.ok);
      setNotice(`${ok} marked delivered${name ? ` · by ${name}` : ""}.` + (fails.length ? " Skipped — " + fails.map((f) => `${f.code}: ${f.error}`).join("; ") : ""));
      setSel(new Set()); setDelivBy(""); await res.reload();
    } catch (e) { setErr(e instanceof ApiError ? e.message : "Could not mark delivered."); }
    finally { setBusy(null); }
  };

  // Checkbox + Mark Delivered live in the PENDING tab only; the Delivered tab is read-only history.
  const renderHead = () => (
    <thead className="border-b border-slate-50"><tr>
      {canDeliver && tab === "pending" && <Th className="w-8" />}
      <Th>Challan</Th><Th>Store</Th><Th className="text-right">Lines · Qty</Th><Th>Orders</Th>
      {showCustomer && <Th>Customer</Th>}
      <Th>Location</Th><Th>Status</Th>
    </tr></thead>
  );
  // colSpan for the expanded detail row: 6 base cols + checkbox col (pending & canDeliver) + customer col (seg ≠ store).
  const colCount = 6 + (canDeliver && tab === "pending" ? 1 : 0) + (showCustomer ? 1 : 0);
  const renderRow = (d) => {
    const delivered = isDelivered(d);
    const isOpen = expanded.has(d.code);
    const lines = d.lines || [];
    return (
      <React.Fragment key={d.code}>
      <tr className="cursor-pointer hover:bg-slate-50/50" onClick={() => toggleExpand(d.code)}>
        {canDeliver && tab === "pending" && <Td>{!delivered ? <input type="checkbox" checked={sel.has(d.code)} onChange={() => toggleSel(d.code)} onClick={(e) => e.stopPropagation()} className="accent-indigo-600" /> : null}</Td>}
        <Td><span className="inline-flex items-center gap-1.5"><ChevronRight size={13} className={cx("text-slate-400 transition-transform", isOpen && "rotate-90")} /><Mono className="font-semibold text-slate-800">{d.code}</Mono></span></Td>
        <Td className="text-slate-700">{d.parties && d.parties[0] ? d.parties[0].name : "—"}</Td>
        <Td className="text-right text-slate-500">{d.lineCount} · {fmtQty(d.totalQty)}</Td>
        <Td className="text-xs text-slate-500">{(d.orders || []).join(", ")}</Td>
        {showCustomer && <Td className="text-xs text-slate-600">{d.customer ? <span>{d.customer.name}{d.customer.phone ? " · " + d.customer.phone : ""}{d.customer.address ? <span className="block text-slate-400">{d.customer.address}</span> : null}</span> : "—"}</Td>}
        <Td>
          <span className="inline-flex items-center gap-2">
            <Badge tone={d.deliveryTarget === "customer" ? "violet" : d.deliveryTarget === "mixed" ? "amber" : "sky"}>{d.deliveryTarget === "customer" ? "Customer" : d.deliveryTarget === "mixed" ? "Mixed" : "Store"}</Badge>
            {canDeliver && !delivered && d.deliveryTarget !== "mixed" && <button disabled={busy === d.code} onClick={(e) => { e.stopPropagation(); flip(d); }} className="rounded-md px-2 py-0.5 text-xs font-medium text-indigo-600 ring-1 ring-indigo-200 hover:bg-indigo-50 disabled:opacity-40">{busy === d.code ? "…" : `→ ${d.deliveryTarget === "customer" ? "Store" : "Customer"}`}</button>}
          </span>
        </Td>
        <Td>{delivered ? <span className="inline-flex flex-col items-start gap-0.5"><Badge tone="emerald">Delivered{d.deliveredAt ? " · " + fmtDate(d.deliveredAt) : ""}</Badge>{d.deliveredByName ? <span className="text-[11px] text-slate-500">by {d.deliveredByName}</span> : null}</span> : <Badge tone="sky">{d.status}</Badge>}</Td>
      </tr>
      {isOpen && (
        <tr className="bg-slate-50/40">
          <Td colSpan={colCount} className="px-4 py-2 align-top">
            <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Items sent</div>
            {lines.length === 0 ? <span className="text-xs text-slate-400">No line items.</span> : (
              <div className="flex flex-col gap-0.5">
                {lines.map((ln, i) => (
                  <div key={i} className="flex items-center justify-between gap-4">
                    <span className="text-sm text-slate-700">{ln.item.name} <Mono className="ml-1 text-xs text-slate-400">{ln.item.code}</Mono></span>
                    <Mono className="text-sm text-slate-600">{fmtQty(ln.qty)}</Mono>
                  </div>
                ))}
              </div>
            )}
          </Td>
        </tr>
      )}
      </React.Fragment>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Truck size={18} className="text-amber-600" /><span className="text-lg font-semibold text-slate-800">Deliveries</span>
        <span className="text-xs text-slate-400">send picked challans to the store or drop-ship to a customer, then mark them delivered</span>
      </div>
      {notice ? <div className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700 ring-1 ring-emerald-200">{notice}</div> : null}
      {err ? <InlineError message={err} /> : null}
      <Card>
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-3">
          {/* PRIMARY tabs: Pending | Delivered (default Pending) */}
          <div className="flex gap-1 rounded-lg bg-slate-100 p-1">
            {[["pending", "Pending", pendingList.length], ["delivered", "Delivered", deliveredList.length]].map(([k, l, n]) => (
              <button key={k} onClick={() => { setTab(k); setSel(new Set()); }} className={cx("flex items-center gap-1.5 rounded-md px-3 py-1 text-xs font-medium", tab === k ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700")}>{l}<Badge tone={tab === k ? (k === "delivered" ? "emerald" : "sky") : "stone"}>{n}</Badge></button>
            ))}
          </div>
          {/* SECONDARY filter: All | Store | Customer (keeps the store-vs-customer distinction) */}
          <div className="flex gap-1 rounded-lg bg-slate-50 p-1 ring-1 ring-slate-100">
            {[["all", "All"], ["store", "Store"], ["customer", "Customer"]].map(([k, l]) => (
              <button key={k} onClick={() => setSeg(k)} className={cx("rounded-md px-2.5 py-1 text-xs font-medium", seg === k ? "bg-white text-slate-700 shadow-sm" : "text-slate-400 hover:text-slate-600")}>{l}</button>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-2">
            {canDeliver && tab === "pending" && (
              <>
                <input value={delivBy} onChange={(e) => setDelivBy(e.target.value)} placeholder="Delivered by (optional)" className="w-44 rounded-md bg-white py-1 px-2 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400" />
                <Btn variant="soft" disabled={selectedCodes.length === 0 || busy === "bulk"} onClick={markDelivered}>{busy === "bulk" ? "Marking…" : `Mark Delivered${selectedCodes.length ? ` (${selectedCodes.length})` : ""}`}</Btn>
              </>
            )}
            <button onClick={res.reload} className="text-xs font-medium text-indigo-600 hover:underline">Refresh</button>
          </div>
        </div>
        {res.loading && all.length === 0 ? <LoadingBlock label="Loading deliveries…" /> : res.error ? <div className="p-4"><InlineError message={res.error} onRetry={res.reload} /></div> : shown.length === 0 ? (
          <p className="px-4 py-6 text-sm text-slate-400">{tab === "delivered" ? "No delivered challans yet." : "No pending deliveries."}{seg !== "all" ? ` · ${seg} filter` : ""}</p>
        ) : (
          <div className="overflow-x-auto"><table className="w-full">
            {renderHead()}
            <tbody className="divide-y divide-slate-50">{shown.map(renderRow)}</tbody>
          </table></div>
        )}
      </Card>
    </div>
  );
}

function PacksView({ auth, role }) {
  const canWrite = !!(authCan(auth, role, "*") || (auth && auth.role && (auth.role.navModules || []).includes("bom")));
  const itemsRes = useResource(() => catalog.listItems({ includeInactive: false, limit: 200 }), []);
  const items = (itemsRes.data && itemsRes.data.items) || [];
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const filtered = items.filter((it) => !q || (it.code + " " + it.name).toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Layers size={18} className="text-indigo-600" />
        <h2 className="text-lg font-semibold text-slate-800">Item Packs</h2>
        <span className="text-xs text-slate-400">alternate units (tin / box / pcs) as multiples of each item's base unit — every quantity is still stored in the base unit</span>
      </div>
      <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
        <Card>
          <div className="border-b border-slate-100 px-3 py-2"><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search items…" className="w-full rounded-md bg-white py-1.5 px-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400" /></div>
          {itemsRes.loading && !items.length ? <LoadingBlock label="Loading items…" /> : itemsRes.error ? <div className="p-3"><InlineError message={itemsRes.error} onRetry={itemsRes.reload} /></div> : (
            <div className="max-h-[62vh] divide-y divide-slate-50 overflow-y-auto">
              {filtered.map((it) => (
                <button key={it.code} onClick={() => setSel(it.code)} className={cx("flex w-full items-center justify-between px-3 py-2 text-left hover:bg-slate-50", sel === it.code && "bg-indigo-50")}>
                  <span><span className="text-sm font-medium text-slate-700">{it.name}</span> <Mono className="ml-1 text-xs text-slate-400">{it.code}</Mono></span>
                  {it.packs && it.packs.length ? <Badge tone="sky">{it.packs.length} pack{it.packs.length > 1 ? "s" : ""}</Badge> : <span className="text-[10px] text-slate-300">{(it.uom && it.uom.code) || "—"}</span>}
                </button>
              ))}
              {filtered.length === 0 ? <p className="px-3 py-4 text-sm text-slate-400">No items.</p> : null}
            </div>
          )}
        </Card>
        {sel ? <PackEditor key={sel} itemCode={sel} canWrite={canWrite} onChanged={itemsRes.reload} /> : <Card><p className="p-8 text-center text-sm text-slate-400">Pick an item on the left to define its packs.</p></Card>}
      </div>
    </div>
  );
}

function PackEditor({ itemCode, canWrite, onChanged }) {
  const res = useResource(() => packsApi.get(itemCode), [itemCode]);
  const d = res.data;
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  React.useEffect(() => { if (d) { setRows((d.packs || []).map((p) => ({ packName: p.packName, factorToBase: String(p.factorToBase) }))); setErr(""); setOk(""); } }, [res.data]);
  const baseUom = (d && d.baseUom && d.baseUom.code) || null;
  const setRow = (i, patch) => setRows((r) => r.map((x, j) => j === i ? { ...x, ...patch } : x));
  const IN = "rounded-md bg-white py-1.5 px-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400";
  const save = async () => {
    const packs = rows.map((r) => ({ packName: (r.packName || "").trim(), factorToBase: Number(r.factorToBase) })).filter((p) => p.packName);
    setBusy(true); setErr(""); setOk("");
    try { await packsApi.put(itemCode, packs); await res.reload(); onChanged && onChanged(); setOk("Saved."); }
    catch (e) { setErr(e instanceof ApiError ? e.message : "Could not save packs."); }
    finally { setBusy(false); }
  };
  return (
    <Card>
      {res.loading && !d ? <LoadingBlock label="Loading packs…" /> : res.error ? <div className="p-3"><InlineError message={res.error} onRetry={res.reload} /></div> : d ? (
        <div className="space-y-3 p-4">
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="text-sm font-semibold text-slate-800">{d.item.name}</span>
            <Mono className="text-xs text-slate-400">{d.item.code}</Mono>
            <span className="ml-auto text-xs text-slate-500">base unit <Badge tone="stone">{baseUom || "—"}</Badge></span>
          </div>
          <div className="text-xs text-slate-400">A pack is a multiple of the base unit. e.g. if base is <b>{baseUom || "kg"}</b> and one tin holds 5 {baseUom || "kg"}, add a pack <b>tin</b> with factor <b>5</b>. Staff can then enter "2 tin" and the system stores 10 {baseUom || "kg"}.</div>
          {err ? <InlineError message={err} /> : null}
          {ok ? <div className="rounded-md bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-700">{ok}</div> : null}
          <div className="overflow-x-auto rounded-lg ring-1 ring-slate-200"><table className="w-full">
            <thead className="bg-slate-50/60"><tr><Th>Pack name</Th><Th className="text-right">Factor</Th><Th>1 pack equals</Th>{canWrite && <Th></Th>}</tr></thead>
            <tbody className="divide-y divide-slate-50">
              {rows.map((r, i) => {
                const f = Number(r.factorToBase);
                return (
                  <tr key={i}>
                    <Td>{canWrite ? <input value={r.packName} onChange={(e) => setRow(i, { packName: e.target.value })} placeholder="tin" className={cx(IN, "w-32")} /> : <span className="text-sm text-slate-700">{r.packName}</span>}</Td>
                    <Td className="text-right">{canWrite ? <input type="number" step="any" value={r.factorToBase} onChange={(e) => setRow(i, { factorToBase: e.target.value })} placeholder="5" className={cx(IN, "mono w-28 text-right")} /> : <Mono>{r.factorToBase}</Mono>}</Td>
                    <Td className="text-xs text-slate-500">{Number.isFinite(f) && f > 0 ? `${uomFmt(f)} ${baseUom || "base"}` : "—"}</Td>
                    {canWrite && <Td className="text-right"><button onClick={() => setRows((x) => x.filter((_, j) => j !== i))} className="text-slate-300 hover:text-rose-500"><Trash2 size={14} /></button></Td>}
                  </tr>
                );
              })}
              {rows.length === 0 ? <tr><Td colSpan={canWrite ? 4 : 3} className="py-3 text-center text-sm text-slate-400">No packs — this item is handled in {baseUom || "its base unit"} only.</Td></tr> : null}
            </tbody>
          </table></div>
          {canWrite && (
            <div className="flex items-center gap-2">
              <button onClick={() => setRows((x) => [...x, { packName: "", factorToBase: "" }])} className="text-xs font-medium text-indigo-600 hover:underline">+ add pack</button>
              <Btn variant="soft" disabled={busy} onClick={save} className="ml-auto">{busy ? "Saving…" : "Save packs"}</Btn>
            </div>
          )}
        </div>
      ) : null}
    </Card>
  );
}

/* ============================ User Guide ============================ */
// In-app end-user guide. Content lives in GUIDE_SECTIONS (data) so it stays
// maintainable and Pass 2 can append sections. Rendered with the app's own Card
// + slate/bg-white utilities so the data-theme="dark" CSS overrides apply
// automatically (no light-only hardcoded colors). Periwinkle #4F6FE8 accents.
const GH = ({ children }) => <h3 className="mt-7 mb-2 text-base font-semibold text-slate-800">{children}</h3>;
const GP = ({ children }) => <p className="mb-3 max-w-3xl text-[15px] leading-relaxed text-slate-600">{children}</p>;
const GSteps = ({ children }) => <ol className="mb-4 ml-5 max-w-3xl list-decimal space-y-1.5 text-[15px] leading-relaxed text-slate-600 marker:font-medium marker:text-slate-400">{children}</ol>;
const GList = ({ children }) => <ul className="mb-4 ml-5 max-w-3xl list-disc space-y-1.5 text-[15px] leading-relaxed text-slate-600 marker:text-slate-400">{children}</ul>;
const GB = ({ children }) => <span className="font-semibold text-slate-800">{children}</span>;
const GNote = ({ label = "Tip", children }) => (
  <div className="mb-4 max-w-3xl rounded-lg bg-indigo-50 px-4 py-3 text-[15px] leading-relaxed text-slate-700" style={{ borderLeft: "3px solid #4F6FE8" }}>
    <span className="font-semibold" style={{ color: "#4F6FE8" }}>{label}: </span>{children}
  </div>
);

const GUIDE_SECTIONS = [
  {
    key: "overview",
    title: "Welcome & How the App Works",
    body: (
      <>
        <GP>This is your manufacturing ERP — it runs your business from a customer's order all the way to a tax invoice, tracking production, stock, dispatch, and team performance along the way.</GP>
        <GH>The big picture — how work flows</GH>
        <GP>Work moves through the app in one connected chain:</GP>
        <GSteps>
          <li>A customer order is recorded in <GB>Sales orders</GB>.</li>
          <li>For each ordered item you make a <GB>Stock decision</GB> — either dispatch it from existing stock, or send it to production to be made.</li>
          <li>Items sent to production become <GB>Production Orders</GB>, which you bundle into <GB>Work Orders</GB> (grouped by the room they start in).</li>
          <li>On the shop floor you <GB>issue</GB> the raw materials (recording which batch went), <GB>start</GB> the work, <GB>move it through its rooms</GB>, and <GB>finish</GB> it. The finished batch is then checked by QC.</li>
          <li>Finished goods are dispatched via a <GB>Delivery Challan</GB> — one company per challan.</li>
          <li>Marking that challan delivered generates the <GB>GST Tax Invoice</GB> automatically.</li>
          <li>You record the customer's <GB>payment</GB> against the invoice.</li>
          <li>Throughout, <GB>Stock</GB> tracks what you have, <GB>Batches</GB> trace what was made, and the <GB>Performance</GB> report shows how fast your team is completing their jobs.</li>
        </GSteps>
        <GH>Finding your way around</GH>
        <GP>The sidebar on the left groups everything — <GB>Workspace</GB> (your dashboard and personal work), <GB>Sales</GB>, <GB>Production</GB>, <GB>Inventory</GB>, and <GB>Administration</GB>. Click any item to open it. The <GB>Dashboard</GB> is your home screen — a live overview you can customize.</GP>
        <GNote>You'll see a <GB>Stock decision</GB> choice on orders — this is the heart of the system: decide whether to ship from stock or make it fresh.</GNote>
      </>
    ),
  },
  {
    key: "roles",
    title: "Roles & Who Does What",
    body: (
      <>
        <GP>Every person who uses the system has a <GB>role</GB>, and their role controls what they can see and do. Roles are fully flexible — you set them up to match how <GB>your</GB> business works, in <GB>Settings → Roles &amp; permissions</GB>. So the division of work below is just a <GB>typical example</GB> — you can assign permissions to any role however suits your team.</GP>
        <GH>How roles work</GH>
        <GP>A role is a named set of permissions (which menus a person sees and which actions they can take). You create and adjust roles in <GB>Settings → Roles &amp; permissions</GB>, and assign a role to each person in <GB>People &amp; Org</GB> (or <GB>Settings → Users</GB>). Because you control this, two companies can run the same system with completely different role setups.</GP>
        <GH>A typical way to divide the work (example)</GH>
        <GP>Here's one common setup, following the flow of an order through the system. Yours may differ — adapt it to your team. (The names in brackets are the roles the system ships with by default.)</GP>
        <GList>
          <li><GB>Sales / Order-taker</GB> (<GB>Sales Exec</GB>) — records customer orders (Sales orders) and manages customers. Sales raises the order; deciding <i>how</i> each line is fulfilled is the Production Manager's call (below).</li>
          <li><GB>Production Manager</GB> (<GB>Production Manager</GB>) — makes the <GB>Stock decision</GB> on every ordered line: dispatch it from stock, send it to production, or split the line between the two. Also groups the work orders, assigns the jobs, and reviews the Performance report. This is the role that decides what gets made.</li>
          <li><GB>Production Planner</GB> (<GB>Production Planner</GB>) — ships with <GB>no permissions of its own</GB>: it's an empty starting point for you to shape. Give it whatever your planner should do (for example, the Production Manager's permissions above).</li>
          <li><GB>Production team / Shop floor</GB> (<GB>Production Op</GB>) — runs the making: works through the jobs assigned to them, moves each job through its rooms, and finishes it.</li>
          <li><GB>Store Manager</GB> (<GB>Store Manager</GB>) — looks after raw materials: records purchases, keeps Store Stock, and <GB>issues</GB> materials to the rooms (recording which batch was physically sent). The Store does <i>not</i> check quality and does <i>not</i> dispatch — those are the two roles below.</li>
          <li><GB>QC Department</GB> (<GB>QC Department</GB>) — checks quality at <i>both</i> ends: <GB>Purchase QC</GB> on raw materials arriving from a supplier (passing them is what puts them into stock), and <GB>FG QC</GB> on the finished batches your team produces.</li>
          <li><GB>Dispatch In-charge</GB> (<GB>Dispatch In-charge</GB>) — sends finished goods out: creates the delivery challan (one company per challan), assigns it, and marks it delivered.</li>
          <li><GB>Accounts</GB> (<GB>Accounts</GB>) — the money side: generates GST tax invoices from delivered challans, and <GB>records payments</GB> against them (including one payment from a group that settles all its sister companies' invoices). Works with Invoices, Customers and the customer statement.</li>
          <li><GB>Supervisor / Monitor</GB> (<GB>Supervisor (Monitor)</GB>) — watches, rather than does: the <GB>System Controller</GB> board (where every job is stuck), order tracking, and the Dashboard. A good fit for a manager who needs the whole picture without doing the steps.</li>
          <li><GB>Owner / Admin</GB> (<GB>Owner / Admin</GB>) — has full access: configures the company (Settings), manages people and roles, customizes the Dashboard, and can do anything the other roles do.</li>
        </GList>
        <GH>Following an order through the roles (example)</GH>
        <GP>To see how the roles fit together, here's a single order flowing through a typical team:</GP>
        <GSteps>
          <li><GB>Sales</GB> records the customer's order.</li>
          <li>The <GB>Production Manager</GB> decides, per item, whether to dispatch it from stock or send it to production (or split it).</li>
          <li>For items sent to production, work orders are created (grouped by the room they start in) and assigned to the Production team.</li>
          <li>The <GB>Store</GB> issues the raw materials to the room, recording which batch was physically sent.</li>
          <li>The <GB>Production team</GB> does the work through each room and finishes the job.</li>
          <li><GB>QC</GB> checks the finished batch — and separately checks raw materials when they arrive from a supplier.</li>
          <li><GB>Dispatch</GB> creates the delivery challan (one company per challan), assigns it, and marks it delivered. The tax invoice is generated automatically at that moment.</li>
          <li><GB>Accounts</GB> records the customer's payment against that invoice.</li>
          <li>Throughout, the <GB>Manager/Owner</GB> watches the Dashboard and Performance report to see how it's all moving.</li>
        </GSteps>
        <GNote label="Important">This is only an example. In this system, roles and permissions are completely up to you — assign any permission to any role in <GB>Settings → Roles &amp; permissions</GB> to match exactly how your business runs. The steps above simply show one sensible way to divide the work.</GNote>
      </>
    ),
  },
  {
    key: "dashboard",
    title: "Dashboard",
    body: (
      <>
        <GH>What it's for</GH>
        <GP>Your home screen — a live, at-a-glance overview of your whole operation. It shows KPI cards (key numbers like open orders, invoiced value, stock alerts, on-time rate) and charts (order status, revenue by month, on-time gauge, team leaderboard). Everything updates from real data.</GP>
        <GH>How to use it</GH>
        <GP>It loads automatically when you log in. Read the number cards for headline figures and the charts for trends.</GP>
        <GH>Customizing (Owner / Admin)</GH>
        <GSteps>
          <li>Click <GB>Customize</GB> (top right).</li>
          <li>A window opens with two lists — <GB>KPI cards</GB> and <GB>Charts</GB> — each with checkboxes.</li>
          <li>Tick the ones you want on your dashboard.</li>
          <li>Click <GB>Save</GB> — the dashboard rebuilds with your choices.</li>
        </GSteps>
        <GP>Every company sets its own dashboard. If you're not an admin, you'll see the dashboard your admin configured.</GP>
        <GNote label="What to expect">Numbers reflect the last 30 days (or the current financial year for tax). Some performance cards only appear for roles with reporting access.</GNote>
      </>
    ),
  },
  {
    key: "sales",
    title: "Sales Orders & The Stock Decision",
    body: (
      <>
        <GH>What it's for</GH>
        <GP>Where you record what customers have ordered and decide how to fulfil each item.</GP>
        <GH>Creating an order</GH>
        <GSteps>
          <li>Go to <GB>Sales orders</GB>.</li>
          <li>Click <GB>New order</GB>.</li>
          <li>Choose the customer, add each ordered item with <GB>+ Add line</GB> (item, quantity), and set the required-by date.</li>
          <li>Click <GB>Create order</GB>.</li>
        </GSteps>
        <GH>The Stock Decision (now in Production → WO Pending)</GH>
        <GP>After you create an order, its unfulfilled lines appear in <GB>Production → WO Pending</GB> under <GB>Sales-order demand</GB>. There you decide, per line:</GP>
        <GList>
          <li><GB>Stock</GB> — allocate the item from existing finished-goods stock (use this when you already have it made); it goes straight to the dispatch queue, no work order.</li>
          <li><GB>Production</GB> — make it: a work order is created and the item shows "In Production". Each room can have a <GB>default responsible person</GB> (set in <GB>Production Master</GB>), and if the item's starting room has one, that person is filled in for you — change it if someone else is doing the job. A responsible person is always required: if the room has no default, pick someone.</li>
          <li><GB>Both</GB> — split the line: dispatch part from stock and produce the rest.</li>
        </GList>
        <GNote label="What changed">The decision used to live on the Sales-order screen; it now lives in one place — <GB>Production → WO Pending</GB> — so the same person who plans production makes the call. The Sales-order screen is now just for creating and viewing orders.</GNote>
        <GP>Your customers themselves — the customer list and ledgers — are managed in the <GB>Customers</GB> screen.</GP>
      </>
    ),
  },
  {
    key: "production_flow",
    title: "Production: Orders, Work Orders & The Shop Floor",
    body: (
      <>
        <GH>What it's for</GH>
        <GP>Where items sent to production get made — from a production order, through work orders, to completion.</GP>
        <GH>Production Orders</GH>
        <GP>Each item sent to production (or a make-to-stock item) becomes a <GB>Production Order</GB>, visible in <GB>Production</GB> (Board / List views).</GP>
        <GH>Creating a Work Order</GH>
        <GSteps>
          <li>Go to <GB>Work Orders</GB>.</li>
          <li>Click <GB>Create work order</GB>.</li>
          <li>Pick the pending items to produce (same items merge automatically) — and/or add make-to-stock items.</li>
          <li>Assign each to an employee with the <GB>Assign to</GB> dropdown.</li>
          <li>Items are automatically grouped by the room they <GB>start</GB> in — the preview shows "This will create N work order(s)". Items that start in the same room go on one work order; items starting in different rooms are split into separate ones. (An item with no routing set can't be added — set its routing in <GB>Production Master</GB> first.)</li>
          <li>Click <GB>Create work order</GB> (or <GB>Create N work orders</GB>).</li>
        </GSteps>
        <GNote label="Why the room grouping">A work order represents items being made together through the same starting stage — so everything on one work order begins in the same room.</GNote>
        <GH>Running the work</GH>
        <GP>On a work order, for each item: <GB>issue</GB> the raw materials, <GB>Start</GB> the job, <GB>move it to the next room</GB> until it's through them all, then <GB>Finish production</GB>. While a job is running it tells you plainly where it is — <GB>Now at: Mixing Room (step 2 of 4)</GB> — and the button says where it's going next, e.g. <GB>Move to Packing Room</GB>. There are bulk actions too (<GB>Issue all remaining</GB>, <GB>Start all</GB>).</GP>
        <GNote>Materials can be issued <GB>before or after</GB> you press Start — whichever suits your floor. You don't have to start a job just to give it its materials.</GNote>
        <GH>Issuing materials — and recording the batch</GH>
        <GP>When the Store issues materials, it records <GB>which physical bags went</GB>, so the room can check what it received against what was sent. The button does both in one step: <GB>Issue &amp; record batch</GB>. Three simple rules:</GP>
        <GList>
          <li>If the material <GB>has quality-checked batches in stock</GB>, choosing the batch is <GB>required</GB>. The oldest batch is filled in for you (oldest stock first), so usually you just confirm — but change it if you physically picked a different bag.</li>
          <li>If the material has <GB>no batches at all</GB>, the batch is left blank and the issue goes through as normal.</li>
          <li>If the material's stock is <GB>waiting on QC</GB>, the issue is <GB>blocked</GB> — QC it first. This stops unchecked material reaching the floor.</li>
        </GList>
        <GH>Consolidated Materials (a helper)</GH>
        <GP>On a work order, click <GB>Consolidated materials</GB> — it shows the total raw materials needed for <GB>all</GB> items on that work order combined (shared ingredients added up into one line), against what's in stock, flagging anything <GB>Short</GB> or <GB>Low</GB>. Use it to gather everything for the whole work order in one go, instead of checking each item. It also shows <GB>who's responsible</GB> for each job on the work order.</GP>
        <GNote label="Two different things">On that screen, <GB>Send stock to &lt;room&gt;</GB> simply <i>moves</i> materials from the store into the room so they're to hand — it is <GB>not</GB> issuing, and it records no batch. <GB>Issue all remaining</GB> is what actually consumes the materials against the job and records which batch went. The <GB>Sent batches</GB> list at the bottom is the store's record of the bags they sent, with each one's QC date — the room can physically check every bag against it.</GNote>
        <GNote label="What to expect">Completing production creates the finished goods (which then go through QC in <GB>Batches</GB>) and records the completion time (feeding the <GB>Performance</GB> report).</GNote>
      </>
    ),
  },
  {
    key: "dispatch",
    title: "Dispatch & Deliveries",
    body: (
      <>
        <GH>What it's for</GH>
        <GP>Where you send finished goods out to customers, via a <GB>Delivery Challan</GB> (DC).</GP>
        <GH>Creating a dispatch</GH>
        <GSteps>
          <li>Go to <GB>Deliveries</GB>.</li>
          <li>Click <GB>New dispatch</GB>.</li>
          <li>In <GB>Create dispatch (delivery challan)</GB>, the ready goods are grouped by <GB>company</GB>. Pick one company's lines — <GB>Select all for this company</GB> ticks them all — and assign a store employee with <GB>Assign to</GB>. Lower a quantity to send only part of it.</li>
          <li>Click <GB>Create challan</GB>.</li>
        </GSteps>
        <GH>One challan = one company</GH>
        <GP>A challan carries <GB>one company's goods only</GB>. Once you tick a line, the other companies grey out — dispatch each company on its own challan. The reason is timing: a challan is delivered all at once, and delivering it bills that company. If two companies shared a challan, marking it delivered would invoice the second company while their goods were still on the lorry. Separate challans let each company be delivered, and billed, on its own schedule.</GP>
        <GH>Completing a delivery</GH>
        <GP>On the challan row, click <GB>Delivered</GB> once it's been sent — this records the delivery (and its timing, which feeds the <GB>Performance</GB> report for your store team).</GP>
        <GP><GB>The tax invoice is generated automatically at that moment</GB> — you don't have to do anything else. If it can't be (usually an item is missing a price or an HSN code, or your company's GST state isn't set), the delivery still stands and the <GB>Invoice</GB> button stays on the row, so you can fix the setup and generate it by hand.</GP>
        <GH>Other row actions</GH>
        <GList>
          <li><GB>PDF</GB> / <GB>Print</GB> the challan.</li>
          <li><GB>Cancel</GB> if needed.</li>
          <li><GB>Invoice</GB> to generate the tax invoice (see the <GB>Invoicing</GB> section).</li>
        </GList>
        <GNote label="What to expect">A delivered challan is what you generate a GST invoice from.</GNote>
      </>
    ),
  },
  {
    key: "system_controller",
    title: "System Controller (the manager's board)",
    body: (
      <>
        <GH>What it's for</GH>
        <GP>One screen that answers "where is everything stuck, and whose job is it?". It's for owners and supervisors — it shows the whole business, not one person's work.</GP>
        <GH>Pending work by stage</GH>
        <GP>Every job in the business, grouped by the step it's waiting at, with the role that owns that step and how many are late:</GP>
        <GList>
          <li><GB>Stock decision</GB> (Production Manager) — ordered lines nobody has decided on yet.</li>
          <li><GB>Material issue</GB> (Store Manager) — jobs waiting for their raw materials.</li>
          <li><GB>Production</GB> (Production team) — jobs being made.</li>
          <li><GB>FG QC</GB> (QC Department) — finished batches waiting to be checked.</li>
          <li><GB>Dispatch</GB> (Dispatch In-charge) — goods ready to go out.</li>
          <li><GB>Invoice</GB> (Accounts) — delivered challans not yet invoiced.</li>
          <li><GB>Purchase QC</GB> (QC Department) — raw materials from suppliers waiting to be checked.</li>
        </GList>
        <GH>Click a stage to see exactly which jobs</GH>
        <GP>Every stage opens up to show the specific jobs waiting there — not just a number. You'll see which orders are waiting on a stock decision, which jobs need materials (and who's responsible), which batches are waiting for QC (with the batch number, what it is, and who made it), which goods are ready to send, and which challans still need an invoice.</GP>
        <GNote label="Reading it">A job can appear at both <GB>Material issue</GB> and <GB>Production</GB> — that's two separate tasks (issue the materials, then make it), not a mistake. <GB>Overdue</GB> counts anything past its required-by date. Two stages show <GB>n/a</GB> instead of a number: an invoice and a finished batch have no due date to be late against.</GNote>
        <GH>Order tracking — item by item</GH>
        <GP>The <GB>Order tracking</GB> tab lists every order with a progress bar and the step it's at. Click any order to open it up and see <GB>each item's own status</GB> — Pending, In Production, Ready, In Transit, or Delivered — with the quantities ordered, allocated, dispatched and delivered. This matters because one badge can hide a lot: an order can read "In Progress" while one item is fully delivered and another hasn't even been made yet.</GP>
        <GH>Taking action on an order</GH>
        <GList>
          <li><GB>Hold</GB> — pause an order temporarily (and <GB>Unhold</GB> to resume).</li>
          <li><GB>Close</GB> — stop it permanently.</li>
          <li><GB>Delete</GB> — owner only.</li>
        </GList>
        <GP>The <GB>Purchases oversight</GB> panel at the bottom does the same for purchases: hold, close, and set the expected date (which is what makes a purchase count as overdue).</GP>
      </>
    ),
  },
  {
    key: "invoicing",
    title: "GST Tax Invoices",
    body: (
      <>
        <GH>What it's for</GH>
        <GP>Generating proper GST-compliant tax invoices for delivered goods.</GP>
        <GH>Before you can invoice — the setup (important)</GH>
        <GP>For an item to be invoiceable it needs, in <GB>Items &amp; BOM</GB>: a <GB>Unit of Measure</GB>, a <GB>Selling price</GB> (₹ per unit), and an <GB>HSN code</GB> (or a GST rate override). And your company's <GB>GST state</GB> must be set in <GB>Settings → Company profile</GB> (this decides the tax type). If any of these are missing, the system will stop you and tell you what to fix.</GP>
        <GH>Generating an invoice</GH>
        <GSteps>
          <li>Go to <GB>Deliveries</GB>.</li>
          <li>On a <GB>delivered</GB> challan, click <GB>Invoice</GB>. The system calculates the tax and creates the invoice.</li>
        </GSteps>
        <GH>Intra vs inter-state tax (automatic)</GH>
        <GP>The system compares your state to the customer's (from their GSTIN) and applies the right tax: same state → <GB>CGST + SGST</GB>; different state → <GB>IGST</GB>; unregistered customer → treated as <GB>B2C</GB>. You'll see a badge showing which.</GP>
        <GH>Viewing invoices</GH>
        <GP>Go to <GB>Invoices</GB> to see all generated tax invoices, expand any to see the line-by-line tax breakup, and <GB>PDF</GB> / <GB>Print</GB> the statutory invoice. Invoice numbers follow your financial-year series (set in <GB>Settings → Number series</GB>).</GP>
        <GNote label="What to expect">Once generated, an invoice is a permanent statutory record — its numbers are frozen even if you later change an item's price or rate.</GNote>
      </>
    ),
  },
  {
    key: "payments",
    title: "Payments & Money Received",
    body: (
      <>
        <GH>What it's for</GH>
        <GP>Recording the money customers actually pay you, and keeping each invoice's balance up to date. Payments live on the <GB>Invoices</GB> screen.</GP>
        <GH>Two ways to record a payment</GH>
        <GList>
          <li><GB>Against one invoice</GB> — on the invoice row, click <GB>Pay</GB>. The company is already known (it's that invoice's customer), so you just enter the amount. Anything extra stays on account rather than moving to another invoice.</li>
          <li><GB>A general payment</GB> — click <GB>Record payment</GB> at the top. Here you must first pick <GB>who's paying</GB>, because the money isn't tied to one invoice yet.</li>
        </GList>
        <GH>Picking who's paying</GH>
        <GP>On a general payment, search the company by name or code and pick it. Nothing is assumed — until you pick a company, the <GB>Record payment</GB> button stays disabled. The list shows your customers, with any sister companies shown underneath their parent group.</GP>
        <GH>Lump sum — oldest invoices first</GH>
        <GP>A general payment is spread over that company's open invoices <GB>oldest first</GB>, until the money runs out. Before you confirm, the screen shows exactly which invoices will be settled and what each one's balance will be afterwards — so there are no surprises.</GP>
        <GH>One payment for a whole group</GH>
        <GP>If a parent company pays on behalf of its sister companies, pick the <GB>group</GB> in the list (it's marked as a group, with the number of sisters). The payment then settles the open invoices of the <GB>whole group</GB>, oldest first, whichever sister they belong to — the preview shows you which sister each invoice belongs to. This saves recording the same cheque against each sister separately.</GP>
        <GH>Paying more than is owed</GH>
        <GP>If the amount is more than the open invoices, the extra is held as an <GB>advance</GB> ("on account") for that company. It's not lost — it's automatically used against their next invoice.</GP>
        <GH>Seeing where you stand</GH>
        <GList>
          <li>Each invoice shows <GB>Pending</GB>, <GB>Partial</GB> or <GB>Complete</GB>, and the tabs at the top let you see just the unpaid ones.</li>
          <li>The <GB>customer statement</GB> (in Reports) lists everything a customer was invoiced and everything they paid, with the running balance — this is the page to send a customer who asks "what do I owe?".</li>
          <li>If your bank sends you a list of payments, <GB>Import CSV</GB> reads them in one go. It shows you a preview of what it matched before anything is saved.</li>
        </GList>
        <GNote>The <GB>Accounts</GB> role covers invoices and payments. Money received is only ever recorded against a company you picked — the app never guesses who paid.</GNote>
      </>
    ),
  },
  {
    key: "lot_wise",
    title: "Lot-wise Production",
    mfg: true,
    body: (
      <>
        <GH>What it's for</GH>
        <GP>Recording a production run <GB>lot by lot</GB> as it comes off the floor, instead of one single figure at the end. This suits items made in fixed-size lots (trays, kettles, batches).</GP>
        <GH>How lots add up</GH>
        <GP>Each finished item has a <GB>lot size</GB> — how many units one lot yields. When you record the run you enter the <GB>number of lots</GB> produced, and the app works out the output as <GB>lots × lot size</GB>. Add as many lot entries as the run took.</GP>
        <GH>Recording it</GH>
        <GSteps>
          <li>Go to <GB>Production → In&nbsp;Progress</GB> and open the production order.</li>
          <li>In the <GB>Lot-wise production</GB> panel, enter the number of lots for each entry (add more lines as the run continues).</li>
          <li>The panel shows the running <GB>total output</GB> against the <GB>planned</GB> quantity.</li>
        </GSteps>
        <GNote label="What to expect">A production order can be <GB>completed only when the total output is within ±4%</GB> of the plan — a little over or under is fine (real production is never exact), but a big gap is flagged so you can check before closing. Finished lots then go to QC in <GB>Batches</GB> like any other output.</GNote>
      </>
    ),
  },
  {
    key: "stock",
    title: "Store Stock & Inventory",
    body: (
      <>
        <GH>What it's for</GH>
        <GP>Seeing what you have on hand — raw materials, packing, and finished goods.</GP>
        <GH>The views</GH>
        <GP>Go to <GB>Store Stock</GB>. Tabs let you see stock <GB>Store-wise</GB>, <GB>Room-wise</GB>, or <GB>Total</GB>. Columns show <GB>Available</GB>, <GB>Reserved</GB> (committed to orders), <GB>WIP</GB> (in production), <GB>In-transit</GB>, <GB>Buffer</GB> (your low-stock threshold), and <GB>Total</GB>.</GP>
        <GH>Starting off — your opening stock</GH>
        <GP>When you first start using the app you'll already have materials on the shelf. Use <GB>Opening stock</GB> to type in what you actually hold, item by item — it sets each item's balance and creates an "opening" batch, so your stock is traceable from day one. This saves inventing fake purchases or production runs just to get your starting numbers in.</GP>
        <GH>Moving stock</GH>
        <GList>
          <li><GB>Send to room</GB> — moves stock from the store to a production room.</li>
          <li><GB>Return to store</GB> — brings it back.</li>
        </GList>
        <GNote label="What to expect">Stock updates automatically as you produce (consuming materials), dispatch (reducing finished goods), and purchase (adding materials). Items below their buffer are flagged (and appear on the Dashboard's low-stock alerts).</GNote>
      </>
    ),
  },
  {
    key: "vendor_stock",
    title: "Vendor-wise Stock & Batch Issue",
    mfg: true,
    body: (
      <>
        <GH>What it's for</GH>
        <GP>Seeing raw-material stock <GB>broken down by vendor and batch</GB> instead of one lump figure — so you know exactly whose material (and which delivery) you hold, and can issue a specific batch to production.</GP>
        <GH>Reading the stock</GH>
        <GP>In <GB>Store Stock</GB>, a material's balance splits into the <GB>vendor batches</GB> that make it up — each batch shows its vendor, the quantity remaining, and its QC date. The same total, now traceable to where each part came from.</GP>
        <GH>Issuing material to production</GH>
        <GSteps>
          <li>On the work order, choose <GB>Issue &amp; record batch</GB> for the material.</li>
          <li>Pick the <GB>vendor / batch</GB> you're physically taking from, and enter the <GB>quantity</GB> from that batch.</li>
          <li>Need more than one batch? Add another line and split the quantity across batches.</li>
        </GSteps>
        <GNote label="Tip">The oldest batch is offered first (oldest stock used first), so usually you just confirm — but change it if you physically picked a different bag. Issuing reduces that exact batch, keeping your vendor-wise balances correct.</GNote>
      </>
    ),
  },
  {
    key: "items_bom",
    title: "Items & Bills of Material",
    body: (
      <>
        <GH>What it's for</GH>
        <GP>Your product catalogue — every item you make, buy, or sell — and the recipes (BOMs) for what you manufacture.</GP>
        <GH>Items</GH>
        <GP>Go to <GB>Items &amp; BOM → Items</GB> tab. Click <GB>New item</GB> to add one — set its code, name, type (<GB>Finished Good</GB>, <GB>Semi-Finished</GB>, <GB>Raw Material</GB>, <GB>Packing</GB>), Unit of Measure, and for sellable items the <GB>Selling price</GB>, <GB>HSN code</GB>, and any <GB>GST rate override</GB> (these make it invoiceable). Use <GB>Edit</GB> to change, and <GB>Deactivate</GB> / <GB>Activate</GB> to toggle.</GP>
        <GH>Bills of Material</GH>
        <GP>Switch to the <GB>Bill of materials</GB> tab. A BOM is the recipe for a manufactured item — which components (and how much of each) go into making it.</GP>
        <GSteps>
          <li>Click <GB>New BOM</GB>.</li>
          <li>Pick the item.</li>
          <li>Add each component with <GB>Add row</GB> (component + quantity).</li>
          <li>Click <GB>Create BOM</GB>.</li>
        </GSteps>
        <GP>BOMs can be multi-level — an item's recipe can include a semi-finished item that has its own recipe.</GP>
        <GNote label="What to expect">BOMs drive production (what materials to issue) and the <GB>Consolidated Materials</GB> view on work orders. Get an item's recipe right here and the rest of production follows.</GNote>
      </>
    ),
  },
  {
    key: "batches",
    title: "Batches & Traceability",
    body: (
      <>
        <GH>What it's for</GH>
        <GP>Tracking every batch you make or receive, and quality control on finished goods.</GP>
        <GH>The views</GH>
        <GP>Go to <GB>Batches</GB>. Tabs: <GB>All</GB>, <GB>RM/PM</GB> (materials), <GB>FG/SFG</GB> (what you've made), and <GB>FG QC pending</GB> (finished goods awaiting quality check).</GP>
        <GH>Actions</GH>
        <GList>
          <li><GB>Sticker</GB> — prints a batch label.</li>
          <li>On finished goods pending QC, <GB>QC pass</GB> approves the batch (making it available) or <GB>Reject</GB> fails it.</li>
        </GList>
        <GNote label="What to expect">Batches give you traceability — you can see which materials went into which finished product. Completing production creates a finished-goods batch that lands in QC pending until you pass it.</GNote>
      </>
    ),
  },
  {
    key: "purchases",
    title: "Purchases",
    mfg: true,
    body: (
      <>
        <GH>What it's for</GH>
        <GP>The full purchase journey for raw materials and packing — from raising a priced Purchase Order to the vendor, through receiving and quality-checking the goods, to matching the vendor's invoice and closing the PO in Accounts.</GP>

        <GH>1. Raise the Purchase Order (with prices)</GH>
        <GSteps>
          <li>Go to <GB>Purchases</GB> and click <GB>New purchase</GB>.</li>
          <li>Add each item and its quantity. The <GB>rate</GB> pre-fills from that item's <GB>last purchase price</GB> — leave it or type the agreed rate.</li>
          <li>The line <GB>amount</GB> and the <GB>PO total</GB> add up automatically as you type.</li>
          <li>Click <GB>Create purchase</GB> to raise the PO.</li>
        </GSteps>

        <GH>2. Send the PO to your vendor</GH>
        <GP>Open the purchase and click <GB>PDF</GB> to download a clean Purchase Order document — your company details, the items, rates and total. Share it with the vendor over WhatsApp, email, or print.</GP>

        <GH>3. Receive the goods (GRN)</GH>
        <GP>When the delivery arrives, record what <GB>actually came in</GB>. The received quantity <GB>may differ</GB> from what you ordered — enter the real received quantity per item.</GP>

        <GH>4. Quality check — pass some, fail some</GH>
        <GP>QC doesn't have to be all-or-nothing. For each item, split the received quantity:</GP>
        <GList>
          <li><GB>Passed</GB> — good stock. It's added to your Store Stock and gets a <GB>batch sticker</GB> (a label you can print) for traceability.</li>
          <li><GB>Failed</GB> — enter the failed quantity and a <GB>reject reason</GB>. Failed goods are <GB>not</GB> added to stock.</li>
        </GList>
        <GNote>Only the <GB>passed</GB> quantity becomes usable stock — this is how raw materials enter the system to be consumed in production.</GNote>

        <GH>5. Attach the vendor's invoice</GH>
        <GSteps>
          <li>Enter the <GB>invoice number</GB>, <GB>date</GB> and <GB>amount</GB> from the vendor's bill.</li>
          <li>Attach the bill — either <GB>Choose file</GB> (a PDF or photo already on your device) or <GB>Take photo</GB> to snap it with your phone's camera on the spot.</li>
        </GSteps>

        <GH>6. Verify the invoice (price check)</GH>
        <GP>The purchase head checks the <GB>invoice amount against the PO</GB>. The screen shows the <GB>price difference</GB> so over-charging stands out. Then either:</GP>
        <GList>
          <li><GB>Approve</GB> — the difference is acceptable; the PO moves on to Accounts.</li>
          <li><GB>Return for correction</GB> — send it back (e.g. wrong rate or amount) to be fixed and re-submitted.</li>
        </GList>

        <GH>7. Accounts closes the PO</GH>
        <GP>Accounts opens the approved PO and clicks <GB>Complete</GB>. The <GB>PO Sheet</GB> lists every purchase with its <GB>PO amount</GB>, <GB>invoice amount</GB>, the <GB>difference</GB>, and its <GB>status</GB> — the whole purchase on one line.</GP>
        <GNote label="What to expect">A PO flows Raised → Received (GRN) → QC → Invoice attached → Verified → Completed. Each step only opens once the one before it is done, so nothing is skipped.</GNote>
      </>
    ),
  },
  {
    key: "people",
    title: "People & Org",
    body: (
      <>
        <GH>What it's for</GH>
        <GP>Managing the people who use the system and your organisational structure.</GP>
        <GH>What's here</GH>
        <GP>Go to <GB>People &amp; Org</GB>. Tabs:</GP>
        <GList>
          <li><GB>Users</GB> — add / edit the people who log in, set their role and details.</li>
          <li><GB>Branches</GB> — your locations.</li>
          <li><GB>Holiday list</GB> — company and branch holidays (these feed working-day calculations for delivery timelines and performance).</li>
        </GList>
        <GH>Roles</GH>
        <GP>A user's role controls what they can see and do. Roles themselves are configured in <GB>Settings → Roles &amp; permissions</GB>.</GP>
        <GNote label="What to expect">Assigning the right role to each user determines their access. Holidays you set here affect due-date and performance-duration calculations across the app.</GNote>
      </>
    ),
  },
  {
    key: "performance",
    title: "Performance Reports",
    body: (
      <>
        <GH>What it's for</GH>
        <GP>Seeing how quickly your team completes their work — production and dispatch.</GP>
        <GH>How it works</GH>
        <GP>Employees are assigned to production orders (when a work order is created) and to delivery challans (when a dispatch is created). When the work is marked complete, the system automatically records how long it took.</GP>
        <GH>Using the report</GH>
        <GP>Go to <GB>Performance</GB>. Use the filter to view <GB>All</GB>, <GB>Production</GB>, or <GB>Dispatch</GB> work; pick a date window (<GB>Last 30 days</GB>, <GB>This month</GB>, or a custom range). The table shows each employee's completed jobs, average time (in working days — weekends and holidays excluded, so it's fair), and on-time rate (completed by the required-by date). Click <GB>Report →</GB> on any row for that person's detailed job list.</GP>
        <GNote label="What to expect"><GB>On-time</GB> is measured against the order's required-by date. Durations exclude weekends and holidays. Make-to-stock jobs (no customer deadline) count toward completed totals but not the on-time rate. This report is available to management roles.</GNote>
      </>
    ),
  },
  {
    key: "settings",
    title: "Settings & Configuration",
    body: (
      <>
        <GH>What it's for</GH>
        <GP>Configuring your company and how the system behaves (<GB>Owner / Admin</GB> only — others see it read-only).</GP>
        <GH>The sections</GH>
        <GList>
          <li><GB>Company profile</GB> — your company name, legal details, GSTIN, and GST state (the state is essential for tax invoicing).</li>
          <li><GB>Users / Roles &amp; permissions / Departments / Branches &amp; locations / Org hierarchy</GB> — your people and structure (Users and Branches are also on the People &amp; Org screen).</li>
          <li><GB>Number series</GB> — your document numbering (e.g. invoice numbers), including financial-year reset.</li>
          <li><GB>Units &amp; tax</GB> — your Units of Measure and your HSN codes with their GST rates (your accountant's HSN/rate determinations go here; items reference these).</li>
          <li><GB>Holidays</GB> — company and branch holidays (feed working-day and performance calculations).</li>
          <li><GB>Shifts</GB> — your working shifts.</li>
        </GList>
        <GNote label="What to expect">Set up <GB>Company profile</GB> (GST state), <GB>Units &amp; tax</GB> (HSN/rates), and <GB>Number series</GB> before generating tax invoices. Changes here apply across the whole system.</GNote>
      </>
    ),
  },
  {
    key: "coming_soon",
    title: "Coming Soon",
    body: (
      <>
        <GP>Almost everything in the menu is live and working. One area is still being built:</GP>
        <GList>
          <li><GB>Reports</GB> — extra summary reports beyond the Dashboard, the Performance report and the customer statement. (In development — the numbers on this screen are placeholders for now.)</li>
        </GList>
        <GH>Now live (this guide used to list these as unfinished)</GH>
        <GP>These are fully working — please use them:</GP>
        <GList>
          <li><GB>My Queue</GB> — your personal work inbox: the jobs assigned to you. See the sections above for the work it lists.</li>
          <li><GB>Tasks</GB> — create and assign day-to-day tasks to your team, and tick them off.</li>
          <li><GB>System Controller</GB> — the manager's board: pending work by stage, order tracking, and purchase oversight. It has its own section in this guide.</li>
        </GList>
        <GNote>The <GB>Dashboard</GB> gives you the headline numbers, <GB>System Controller</GB> shows where every job is stuck, and <GB>Performance</GB> shows how quickly your team finishes work.</GNote>
      </>
    ),
  },
];

function UserGuideView({ auth }) {
  const [gsec, setGsec] = useState("overview");
  // Manufacturing-only sections (mfg:true) are hidden for RETAIL tenants (enabledModules includes
  // "warehouses"). A manufacturing tenant — or a legacy tenant with null enabledModules — is NOT retail,
  // so it sees them. Raw enabledModules test (not tenantHas, which returns true-for-all when null).
  const isRetail = Array.isArray(auth?.enabledModules) && auth.enabledModules.includes("warehouses");
  const sections = GUIDE_SECTIONS.filter((s) => !(s.mfg && isRetail));
  const active = sections.find((s) => s.key === gsec) || sections[0];
  return (
    <div className="mx-auto max-w-6xl">
      <p className="mb-5 max-w-3xl text-sm leading-relaxed text-slate-500">
        Your guide to running the business{auth?.company?.name ? " in " + auth.company.name : ""} — from a customer order to a tax invoice. Pick a topic on the left.
      </p>
      <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
        {/* In-page section nav (TOC) — sticky on wide screens, stacks on top when narrow */}
        <nav className="lg:sticky lg:top-24 lg:w-60 lg:shrink-0" aria-label="Guide sections">
          <Card className="p-2">
            <div className="px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Contents</div>
            <div className="space-y-0.5">
              {sections.map((s) => (
                <button key={s.key} onClick={() => setGsec(s.key)} aria-current={gsec === s.key ? "page" : undefined} style={gsec === s.key ? { backgroundColor: "#4F6FE8" } : undefined} className={cx("flex w-full items-center rounded-lg px-3 py-2 text-left text-sm font-medium transition", gsec === s.key ? "text-white" : "text-slate-600 hover:bg-slate-100")}>
                  <span>{s.title}</span>
                </button>
              ))}
            </div>
          </Card>
        </nav>
        {/* Content */}
        <Card className="min-w-0 flex-1 p-6 sm:p-8">
          <h2 className="text-xl font-semibold" style={{ color: "#4F6FE8" }}>{active.title}</h2>
          <div className="mt-4 mb-5 h-px w-full bg-slate-100" />
          <div>{active.body}</div>
          <div className="mt-8 max-w-3xl">
            <GNote>That covers the whole app. Use the menu on the left of this guide to jump to any topic.</GNote>
          </div>
        </Card>
      </div>
    </div>
  );
}

// RETAIL OPERATIONS DASHBOARD — one screen composing the existing retail reads/actions
// (warehouseChildOrders + retail-deliveries + retail-receive) with inline per-line partial dispatch,
// deliver, and receive. RETAIL-ONLY (nav gated by tenantHas("warehouses")); reuses existing endpoints
// only — NO new business logic. Manufacturing never renders it. Actions are cap-gated (issue →
// dispatch/receive, deliver → deliver); the existing Warehouses/Deliveries/Receive pages are untouched.
// PUBLIC ORDER LINK card — owner/admin only, retail-only (rendered only inside RetailOpsView, which is
// itself gated to retail tenants). Reads the current token; Generate / Copy / Rotate via the existing
// company endpoints. Shows only the shareable link (no other secret).
function PublicOrderLinkCard() {
  const [token, setToken] = useState(undefined); // undefined = loading, null = none, string = exists
  const [busy, setBusy] = useState(false); const [copied, setCopied] = useState(false); const [err, setErr] = useState("");
  const link = token ? `${window.location.origin}/order/${token}` : "";
  React.useEffect(() => { let alive = true; companyApi.getPublicOrderToken().then((r) => { if (alive) setToken((r && r.token) || null); }).catch(() => { if (alive) setToken(null); }); return () => { alive = false; }; }, []);
  const gen = async (rotate) => {
    if (rotate && !window.confirm("Rotate the public order link? The current link will stop working immediately.")) return;
    setBusy(true); setErr("");
    try { const r = await companyApi.rotatePublicOrderToken(); setToken((r && r.token) || null); setCopied(false); }
    catch (e) { setErr(e instanceof ApiError ? e.message : "Could not generate the link."); }
    finally { setBusy(false); }
  };
  const copy = async () => { try { await navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch (e) {} };
  return (
    <Card className="p-4">
      <div className="flex items-center gap-2"><Link2 size={15} style={{ color: "#4F6FE8" }} /><span className="text-sm font-semibold text-slate-700">Public order link</span></div>
      <p className="mt-1 text-xs text-slate-400">Share this link with your stores — they can raise indents without logging in.</p>
      {err ? <p className="mt-2 text-xs text-rose-600">{err}</p> : null}
      {token === undefined ? <p className="mt-3 text-xs text-slate-400">Loading…</p>
        : token === null ? <Btn className="mt-3" disabled={busy} onClick={() => gen(false)}><Link2 size={13} /> {busy ? "Generating…" : "Generate link"}</Btn>
        : <div className="mt-3 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <input readOnly value={link} onFocus={(e) => e.target.select()} className="min-w-[220px] flex-1 rounded-md bg-slate-50 py-1.5 px-2.5 text-sm text-slate-600 ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400" />
              <Btn variant="soft" onClick={copy}>{copied ? <><CheckCircle2 size={13} /> Copied</> : "Copy link"}</Btn>
            </div>
            <button disabled={busy} onClick={() => gen(true)} className="text-xs font-medium text-slate-400 hover:text-rose-600 disabled:opacity-50">Rotate / regenerate link</button>
          </div>}
    </Card>
  );
}

function RetailOpsView({ auth, role }) {
  const pcan = (k) => authCan(auth, role, k);
  const canDispatch = pcan("issue");
  const canDeliver = pcan("deliver");
  const canReceive = pcan("issue");
  // Owner/admin only (matches the backend requireModule("settings") gate on the token endpoints).
  const isAdmin = pcan("*") || !!(auth && auth.role && Array.isArray(auth.role.navModules) && auth.role.navModules.includes("settings"));
  const [data, setData] = useState({ orders: [], deliveries: [], receivables: [] });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(""); const [notice, setNotice] = useState("");
  const [tab, setTab] = useState("pending");    // pending | complete
  const [seg, setSeg] = useState("all");         // all | store | customer
  const [storeFilter, setStoreFilter] = useState("");
  const [expanded, setExpanded] = useState(() => new Set());
  const [send, setSend] = useState({});          // `${order}:${line}` -> qty string
  const [busy, setBusy] = useState(null);
  const [delivSel, setDelivSel] = useState(() => new Set());
  const [delivBy, setDelivBy] = useState("");
  const [recvOpen, setRecvOpen] = useState(null);
  const [recvDetail, setRecvDetail] = useState(null);
  const [recvQty, setRecvQty] = useState({});
  const storesRes = useResource(() => storesApi.list(), []);
  const stores = (storesRes.data && storesRes.data.stores) || [];

  const load = React.useCallback(async () => {
    try {
      const wh = await warehousesApi.list();
      const codes = ((wh && wh.warehouses) || []).map((w) => w.code);
      const [ordSets, deliv, recv] = await Promise.all([
        Promise.all(codes.map((c) => indentsApi.warehouseOrders(c).then((r) => (r.orders || []).map((o) => ({ ...o, warehouseCode: c }))).catch(() => []))),
        retailDeliveriesApi.list().catch(() => ({ deliveries: [] })),
        indentsApi.receivable().catch(() => ({ deliveries: [] })),
      ]);
      setData({ orders: ordSets.flat(), deliveries: (deliv && deliv.deliveries) || [], receivables: (recv && recv.deliveries) || [] });
      setErr("");
    } catch (e) { setErr(e instanceof ApiError ? e.message : "Failed to load operations."); }
    finally { setLoading(false); }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  // pending-to-send per line = ordered − allocated (matches the warehouse pick modal's cap).
  const pendingOf = (l) => Math.max(0, (l.qtyOrdered || 0) - (l.qtyAllocated || 0));
  const toggle = (key) => setExpanded((s) => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n; });

  const orders = data.orders
    .filter((o) => (tab === "pending" ? !o.complete : o.complete))
    .filter((o) => seg === "all" ? true : seg === "customer" ? o.deliveryTarget === "customer" : o.deliveryTarget !== "customer")
    .filter((o) => !storeFilter || (o.store && o.store.code === storeFilter))
    .sort((a, b) => String(a.requiredBy || "").localeCompare(String(b.requiredBy || "")) || a.code.localeCompare(b.code));
  const inTransit = data.deliveries.filter((d) => !(d.deliveredAt || d.status === "Delivered"));
  const toReceive = data.receivables.filter((r) => !r.received);
  const cPending = data.orders.filter((o) => !o.complete).length;
  const cDone = data.orders.filter((o) => o.complete).length;
  const cCust = data.orders.filter((o) => o.orderType === "customer").length;

  const doDispatch = async (o) => {
    const lines = (o.lines || []).map((l) => ({ lineCode: l.lineCode, qty: Number(send[`${o.code}:${l.lineCode}`]) || 0 })).filter((l) => l.qty > 0);
    if (!lines.length) { setErr("Enter a quantity to send on at least one line."); return; }
    setBusy(o.code); setErr(""); setNotice("");
    try { await indentsApi.pick(o.warehouseCode, o.code, { lines }); setSend((s) => { const n = { ...s }; (o.lines || []).forEach((l) => delete n[`${o.code}:${l.lineCode}`]); return n; }); setNotice(`Dispatched from ${o.code} — In-Transit challan created; the rest stays pending.`); await load(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : "Dispatch failed."); }
    finally { setBusy(null); }
  };
  const doDeliver = async () => {
    const codes = [...delivSel]; if (!codes.length) return;
    setBusy("deliver"); setErr(""); setNotice("");
    try { await retailDeliveriesApi.bulkDeliver(codes, delivBy.trim() || undefined); setDelivSel(new Set()); setDelivBy(""); setNotice(`${codes.length} challan(s) marked delivered.`); await load(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : "Deliver failed."); }
    finally { setBusy(null); }
  };
  const openReceive = async (code) => {
    if (recvOpen === code) { setRecvOpen(null); setRecvDetail(null); return; }
    setRecvOpen(code); setRecvDetail(null); setErr("");
    try { const d = await indentsApi.receipt(code); setRecvDetail(d); const q = {}; (d.lines || []).forEach((l) => { q[l.deliveryLineId] = String(l.received ? l.received.receivedQty : l.sentQty); }); setRecvQty(q); }
    catch (e) { setErr(e instanceof ApiError ? e.message : "Could not load challan."); }
  };
  const doReceive = async () => {
    if (!recvDetail) return; const code = recvDetail.delivery.code;
    const lines = (recvDetail.lines || []).map((l) => ({ deliveryLineId: l.deliveryLineId, receivedQty: Number(recvQty[l.deliveryLineId]) || 0 }));
    setBusy(code); setErr(""); setNotice("");
    try { await indentsApi.receive(code, { lines }); setRecvOpen(null); setRecvDetail(null); setNotice(`Receipt confirmed for ${code}.`); await load(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : "Receive failed."); }
    finally { setBusy(null); }
  };

  const seg3 = (v, l) => <button onClick={() => setSeg(v)} className={cx("rounded-md px-2.5 py-1 text-xs font-medium transition", seg === v ? "bg-white text-slate-700 shadow-sm ring-1 ring-slate-200" : "text-slate-500 hover:text-slate-700")}>{l}</button>;
  const locPill = (t) => <Badge tone={t === "customer" ? "violet" : "sky"}>{t === "customer" ? "Customer" : "Store"}</Badge>;
  const Stat = ({ label, value, tone }) => <Card className="p-4"><div className="text-xs text-slate-400">{label}</div><div className="mt-1 text-2xl font-semibold" style={{ color: tone }}>{value}</div></Card>;

  return (
    <div className="space-y-4">
      {isAdmin ? <PublicOrderLinkCard /> : null}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Pending orders" value={cPending} tone="#4F6FE8" />
        <Stat label="In transit" value={inTransit.length} tone="#D97706" />
        <Stat label="Completed" value={cDone} tone="#16A34A" />
        <Stat label="Customer orders" value={cCust} tone="#7C3AED" />
      </div>
      {err ? <InlineError message={err} /> : null}
      {notice ? <div className="rounded-md bg-emerald-50 px-3 py-2 text-xs text-emerald-700 ring-1 ring-emerald-200">{notice}</div> : null}

      <Card>
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-3">
          <div className="flex gap-1 rounded-lg bg-slate-100 p-1">{["pending", "complete"].map((t) => <button key={t} onClick={() => setTab(t)} className={cx("rounded-md px-3 py-1 text-sm font-medium capitalize transition", tab === t ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700")}>{t}</button>)}</div>
          <div className="flex gap-1 rounded-lg bg-slate-100 p-1">{seg3("all", "All")}{seg3("store", "Store")}{seg3("customer", "Customer")}</div>
          <select value={storeFilter} onChange={(e) => setStoreFilter(e.target.value)} className="ml-auto rounded-md bg-white py-1.5 px-2.5 text-sm text-slate-600 ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400"><option value="">All stores</option>{stores.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}</select>
        </div>
        {loading ? <LoadingBlock label="Loading orders…" /> : orders.length === 0 ? <p className="px-4 py-8 text-center text-sm text-slate-400">No {tab} orders.</p> : (
          <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead className="border-b border-slate-100 text-left text-xs text-slate-500"><tr><Th>Order</Th><Th>Store / Customer</Th><Th>Location</Th><Th>Order date</Th><Th>Required by</Th><Th className="text-right">Lines</Th><Th /></tr></thead>
            <tbody className="divide-y divide-slate-50">
              {orders.map((o) => { const key = o.warehouseCode + o.code; const totPend = (o.lines || []).reduce((s, l) => s + pendingOf(l), 0); return (
                <React.Fragment key={key}>
                  <tr className="cursor-pointer hover:bg-slate-50/60" onClick={() => toggle(key)}>
                    <Td><Mono className="font-semibold text-slate-800">{o.code}</Mono>{o.indentCode && <div className="text-[11px] text-slate-400">{o.indentCode}</div>}</Td>
                    <Td className="text-slate-700">{o.orderType === "customer" && o.customer ? o.customer.name : (o.store && o.store.name) || "—"}</Td>
                    <Td>{locPill(o.deliveryTarget)}</Td>
                    <Td className="text-slate-500">{fmtDate(o.orderDate)}</Td>
                    <Td className="text-slate-500">{o.requiredBy ? fmtDate(o.requiredBy) : "—"}</Td>
                    <Td className="text-right text-slate-500">{(o.lines || []).length}{totPend > 1e-6 && <span className="ml-1 text-[11px] text-amber-600">({fmtQty(totPend)} pend)</span>}</Td>
                    <Td className="text-right"><ChevronRight size={15} className={cx("text-slate-300 transition", expanded.has(key) && "rotate-90")} /></Td>
                  </tr>
                  {expanded.has(key) && <tr><td colSpan={7} className="bg-slate-50/60 px-4 py-3">
                    <table className="w-full text-xs"><thead className="text-left text-slate-400"><tr><Th>Item</Th><Th className="text-right">Ordered</Th><Th className="text-right">Sent</Th><Th className="text-right">Delivered</Th><Th className="text-right">Pending</Th>{canDispatch && tab === "pending" && <Th className="text-right">Send now</Th>}</tr></thead>
                      <tbody>{(o.lines || []).map((l) => { const pend = pendingOf(l); return (
                        <tr key={l.lineCode} className="border-t border-slate-100">
                          <Td className="text-slate-700">{l.item.name} <Mono className="text-slate-400">{l.item.code}</Mono></Td>
                          <Td className="text-right"><Mono>{fmtQty(l.qtyOrdered)}</Mono></Td>
                          <Td className="text-right"><Mono className="text-slate-500">{fmtQty(l.qtyDispatched)}</Mono></Td>
                          <Td className="text-right"><Mono className="text-slate-500">{fmtQty(l.qtyDelivered)}</Mono></Td>
                          <Td className="text-right"><Mono className={pend > 1e-6 ? "text-amber-600" : "text-slate-300"}>{fmtQty(pend)}</Mono></Td>
                          {canDispatch && tab === "pending" && <Td className="text-right">{pend > 1e-6 ? <input type="number" min="0" max={pend} value={send[`${o.code}:${l.lineCode}`] ?? ""} onChange={(e) => setSend((s) => ({ ...s, [`${o.code}:${l.lineCode}`]: e.target.value }))} placeholder={fmtQty(pend)} className="w-20 rounded-md bg-white py-1 px-2 text-right text-xs ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400" /> : <span className="text-slate-300">—</span>}</Td>}
                        </tr>
                      ); })}</tbody></table>
                    {canDispatch && tab === "pending" && totPend > 1e-6 && <div className="mt-2 flex items-center gap-2">
                      <Btn variant="soft" disabled={busy === o.code} onClick={() => setSend((s) => { const n = { ...s }; (o.lines || []).forEach((l) => { const p = pendingOf(l); if (p > 1e-6) n[`${o.code}:${l.lineCode}`] = String(p); }); return n; })}>Send all pending</Btn>
                      <Btn disabled={busy === o.code} onClick={() => doDispatch(o)}><Truck size={13} /> {busy === o.code ? "Dispatching…" : "Dispatch"}</Btn>
                    </div>}
                  </td></tr>}
                </React.Fragment>
              ); })}
            </tbody></table></div>
        )}
      </Card>

      {canDeliver && inTransit.length > 0 && <Card>
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-3"><Truck size={14} className="text-amber-600" /><span className="text-sm font-semibold text-slate-700">In transit — mark delivered</span>
          <input value={delivBy} onChange={(e) => setDelivBy(e.target.value)} placeholder="Delivered by (optional)" className="ml-auto w-44 rounded-md bg-white py-1.5 px-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400" />
          <Btn disabled={busy === "deliver" || delivSel.size === 0} onClick={doDeliver}><CheckCircle2 size={14} /> Mark delivered ({delivSel.size})</Btn>
        </div>
        <div className="divide-y divide-slate-50">{inTransit.map((d) => <label key={d.code} className="flex cursor-pointer items-center gap-3 px-4 py-2 text-sm hover:bg-slate-50/60">
          <input type="checkbox" checked={delivSel.has(d.code)} onChange={() => setDelivSel((s) => { const n = new Set(s); n.has(d.code) ? n.delete(d.code) : n.add(d.code); return n; })} className="accent-indigo-600" />
          <Mono className="font-semibold text-slate-800">{d.code}</Mono>{locPill(d.deliveryTarget)}<span className="text-slate-500">{d.orderType === "customer" && d.customer ? d.customer.name : (d.store && d.store.name) || ""}</span><span className="ml-auto text-xs text-slate-400">{(d.lines || []).length} line(s)</span>
        </label>)}</div>
      </Card>}

      {canReceive && toReceive.length > 0 && <Card>
        <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-3"><Inbox size={14} className="text-indigo-600" /><span className="text-sm font-semibold text-slate-700">Delivered — confirm store receipt</span></div>
        <div className="divide-y divide-slate-50">{toReceive.map((r) => <div key={r.code}>
          <div className="flex cursor-pointer items-center gap-3 px-4 py-2 text-sm hover:bg-slate-50/60" onClick={() => openReceive(r.code)}>
            <Mono className="font-semibold text-slate-800">{r.code}</Mono><span className="text-slate-500">{(r.store && r.store.name) || ""}</span><ChevronRight size={14} className={cx("ml-auto text-slate-300 transition", recvOpen === r.code && "rotate-90")} />
          </div>
          {recvOpen === r.code && <div className="bg-slate-50/60 px-4 py-3">{!recvDetail ? <LoadingBlock label="Loading…" /> : (
            <>
              <table className="w-full text-xs"><thead className="text-left text-slate-400"><tr><Th>Item</Th><Th className="text-right">Sent</Th><Th className="text-right">Received</Th></tr></thead>
                <tbody>{(recvDetail.lines || []).map((l) => <tr key={l.deliveryLineId} className="border-t border-slate-100"><Td className="text-slate-700">{l.item.name} <Mono className="text-slate-400">{l.item.code}</Mono></Td><Td className="text-right"><Mono className="text-slate-500">{fmtQty(l.sentQty)}</Mono></Td><Td className="text-right"><input type="number" min="0" value={recvQty[l.deliveryLineId] ?? ""} onChange={(e) => setRecvQty((q) => ({ ...q, [l.deliveryLineId]: e.target.value }))} className="w-20 rounded-md bg-white py-1 px-2 text-right ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400" /></Td></tr>)}</tbody></table>
              <div className="mt-2"><Btn disabled={busy === recvDetail.delivery.code} onClick={doReceive}><CheckCircle2 size={13} /> {busy === recvDetail.delivery.code ? "Saving…" : "Confirm receipt"}</Btn></div>
            </>
          )}</div>}
        </div>)}</div>
      </Card>}
    </div>
  );
}

const NAV = [
  ["queue", "My Queue", Inbox],
  ["dashboard", "Dashboard", LayoutDashboard], ["monitor", "System Controller", Activity], ["tasks", "Tasks", ListChecks], ["people", "People & Org", Users],
  ["orders", "Sales orders", ClipboardList],
  ["production", "Production", Factory], ["workorders", "Work Orders", Layers], ["routing", "Production Master", ListOrdered],
  ["purchases", "Purchases", Inbox], ["inventory", "Store Stock", Boxes], ["batches", "Batches", CircleDot], ["bom", "Items & BOM", GitBranch],
  ["retailops", "Operations", LayoutDashboard], ["warehouses", "Warehouses", Boxes], ["indents", "Store Indents", ClipboardList], ["receive", "Store Receive", Inbox], ["packs", "Item Packs", Layers], ["storedispatch", "Deliveries", Truck],
    ["deliveries", "Deliveries", Truck], ["invoices", "Invoices", FileText],
  ["customers", "Customers", Users], ["reports", "Reports", BarChart3], ["performance", "Performance", TrendingUp], ["settings", "Settings", Settings],
  ["guide", "User Guide", BookOpen],
];

// Grouped sidebar sections (label → ordered nav keys). The sidebar renders these
// as collapsible groups; RBAC is applied by intersecting each group with the
// already role-filtered navItems, so a group with no permitted item is dropped.
// `monitor` ("System Controller") is a single screen → placed once, in Workspace.
const NAV_GROUPS = [
  ["Workspace", ["queue", "dashboard", "tasks", "monitor"]],
  ["Sales", ["orders", "customers", "deliveries", "invoices"]],
  ["Production", ["production", "workorders", "routing"]],
  ["Inventory", ["bom", "inventory", "batches", "purchases"]],
    ["Retail", ["retailops", "warehouses", "indents", "receive", "packs", "storedispatch"]],
  ["Administration", ["people", "reports", "settings"]],
];

/* ============================ Settings / Administration ============================ */
const SField = ({ label, children, full }) => <label className={cx("block", full && "sm:col-span-2")}><span className="text-xs font-medium text-slate-500">{label}</span><div className="mt-1">{children}</div></label>;
const MODULE_LABELS = { dashboard: "Dashboard", monitor: "System Controller", tasks: "Tasks", people: "People & Org", queue: "My Queue", orders: "Sales orders", production: "Production", workorders: "Work Orders", routing: "Production Master", purchases: "Purchases", inventory: "Inventory", batches: "Batches", bom: "Items & BOM", deliveries: "Deliveries", invoices: "Invoices", customers: "Customers", reports: "Reports", settings: "Settings", retailops: "Operations", warehouses: "Warehouses", indents: "Store Indents", receive: "Store Receive", packs: "Item Packs", storedispatch: "Deliveries" };
const ALL_MODULES = ["dashboard", "queue", "orders", "production", "workorders", "routing", "purchases", "inventory", "batches", "bom", "deliveries", "invoices", "customers", "monitor", "tasks", "people", "reports", "settings", "retailops", "warehouses", "indents", "receive", "packs", "storedispatch"];
const PERM_LABELS = { monitor: "Monitor / controller", createOrder: "Create sales order", fulfill: "Stock decision (dispatch / produce)", issue: "Issue materials", fgqc: "FG QC", buy: "Raise purchases", stockPO: "Make-to-stock WO", prodmgr: "Production-manager board", startProd: "Start production", advanceProd: "Advance production", dispatch: "Create dispatch", deliver: "Mark delivered", invoice: "Create invoice" };
const ALL_PERMS = ["createOrder", "fulfill", "issue", "fgqc", "buy", "stockPO", "prodmgr", "startProd", "advanceProd", "dispatch", "deliver", "invoice", "monitor"];
const DOW_FULL = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const SETTINGS_IN = "w-full rounded-md bg-white py-1.5 px-2.5 text-sm text-slate-700 ring-1 ring-slate-200 focus:outline-none focus:ring-indigo-400 disabled:bg-slate-50 disabled:text-slate-400";

function UserEditor({ st, dispatch, open, onClose, user }) {
  const edit = !!user;
  const roleKeys = Object.keys(st.roles);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [roleId, setRoleId] = useState("stores");
  const [branchId, setBranchId] = useState(st.branches[0] ? st.branches[0].id : "BR1");
  const [dept, setDept] = useState(st.departments[0] ? st.departments[0].name : "");
  const [reportsTo, setReportsTo] = useState("");
  const [weekOff, setWeekOff] = useState(0);
  const [inTime, setInTime] = useState("10:00");
  const [outTime, setOutTime] = useState("19:00");
  React.useEffect(() => {
    if (!open) return;
    setName(user ? user.name : ""); setEmail(user ? (user.email || "") : "");
    setRoleId(user ? user.role : "stores"); setBranchId(user ? user.branchId : (st.branches[0] ? st.branches[0].id : "BR1"));
    setDept(user ? (user.dept || "") : (st.departments[0] ? st.departments[0].name : ""));
    setReportsTo(user ? (user.reportsTo || "") : ""); setWeekOff(user ? user.weekOff : 0);
    setInTime(user ? user.inTime : "10:00"); setOutTime(user ? user.outTime : "19:00");
  }, [open, user]);
  const save = () => {
    if (!name.trim()) return;
    if (edit) dispatch({ type: "UPDATE_USER", id: user.id, patch: { name: name.trim(), email, role: roleId, branchId, dept, reportsTo: reportsTo || null, weekOff: Number(weekOff), inTime, outTime } });
    else dispatch({ type: "ADD_USER", name: name.trim(), email, role: roleId, branchId, dept, reportsTo: reportsTo || null, weekOff, inTime, outTime });
    onClose();
  };
  return (
    <Modal open={open} onClose={onClose} title={edit ? ("Edit user · " + user.name) : "Add user"} footer={<><Btn variant="ghost" onClick={onClose}>Cancel</Btn><Btn onClick={save}>{edit ? "Save changes" : "Create user"}</Btn></>}>
      <div className="grid gap-3 sm:grid-cols-2">
        <SField label="Full name"><input value={name} onChange={(e) => setName(e.target.value)} className={SETTINGS_IN} placeholder="e.g. Amit Saha" /></SField>
        <SField label="Login email"><input value={email} onChange={(e) => setEmail(e.target.value)} className={SETTINGS_IN} placeholder="name@company.com" /></SField>
        <SField label="Role"><select value={roleId} onChange={(e) => setRoleId(e.target.value)} className={SETTINGS_IN}>{roleKeys.map((k) => <option key={k} value={k}>{st.roles[k].name}</option>)}</select></SField>
        <SField label="Branch"><select value={branchId} onChange={(e) => setBranchId(e.target.value)} className={SETTINGS_IN}>{st.branches.map((b) => <option key={b.id} value={b.id}>{b.name} ({b.code})</option>)}</select></SField>
        <SField label="Department"><select value={dept} onChange={(e) => setDept(e.target.value)} className={SETTINGS_IN}>{st.departments.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}</select></SField>
        <SField label="Reports to (manager)"><select value={reportsTo} onChange={(e) => setReportsTo(e.target.value)} className={SETTINGS_IN}><option value="">— None (top level) —</option>{st.users.filter((u) => !user || u.id !== user.id).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</select></SField>
        <SField label="Weekly off"><select value={weekOff} onChange={(e) => setWeekOff(Number(e.target.value))} className={SETTINGS_IN}>{DOW_FULL.map((d, i) => <option key={i} value={i}>{d}</option>)}</select></SField>
        <div className="grid grid-cols-2 gap-2">
          <SField label="In time"><input type="time" value={inTime} onChange={(e) => setInTime(e.target.value)} className={SETTINGS_IN} /></SField>
          <SField label="Out time"><input type="time" value={outTime} onChange={(e) => setOutTime(e.target.value)} className={SETTINGS_IN} /></SField>
        </div>
      </div>
    </Modal>
  );
}

function CompanySettings({ st, dispatch, admin, auth }) {
  // Identity fields (name/legalName/GSTIN) come from the REAL tenant session
  // (/auth/me company.name) so each tenant sees their own company — never a
  // hardcoded one. Remaining fields stay on the local profile until a company
  // API lands. (The mock reducer is shared across tenants, so without this an
  // Acme tenant would otherwise see the seed "Jainys" profile.)
  const base = st.settings.company;
  const c = auth?.company
    ? { ...base, name: auth.company.name || base.name, legalName: auth.company.legalName || auth.company.name || base.legalName, gstin: auth.company.gstin || base.gstin }
    : base;
  const [d, setD] = useState(c);
  React.useEffect(() => setD(c), [base, auth?.company?.name]);
  const set = (k, v) => setD((x) => ({ ...x, [k]: v }));
  const FIELDS = [["name", "Trade name"], ["legalName", "Legal name"], ["gstin", "GSTIN"], ["pan", "PAN"], ["cin", "CIN"], ["currency", "Default currency"], ["phone", "Phone"], ["email", "Email"], ["website", "Website"], ["city", "City"], ["state", "State"], ["pincode", "PIN code"]];
  return (
    <Card className="p-5">
      <div className="mb-4 flex items-center justify-between"><h3 className="text-base font-semibold text-slate-800">Company profile</h3>{admin && <Btn onClick={() => dispatch({ type: "UPDATE_COMPANY", patch: d })}>Save</Btn>}</div>
      <div className="grid gap-3 sm:grid-cols-2">
        <SField label="Registered address" full><input disabled={!admin} value={d.address || ""} onChange={(e) => set("address", e.target.value)} className={SETTINGS_IN} /></SField>
        {FIELDS.map(([k, label]) => <SField key={k} label={label}><input disabled={!admin} value={d[k] || ""} onChange={(e) => set(k, e.target.value)} className={SETTINGS_IN} /></SField>)}
      </div>
      <p className="mt-4 text-xs text-slate-400">This profile is used in the challan / invoice / PDF document header (once wired to the backend).</p>
    </Card>
  );
}

function UsersSettings({ st, dispatch, admin }) {
  const [editing, setEditing] = useState(null);
  const uname = (id) => { const u = st.users.find((x) => x.id === id); return u ? u.name : "—"; };
  const br = (id) => { const b = st.branches.find((x) => x.id === id); return b ? b.code : id; };
  return (
    <Card className="p-0">
      <div className="flex items-center justify-between p-4"><h3 className="text-base font-semibold text-slate-800">Users <span className="text-sm font-normal text-slate-400">({st.users.length})</span></h3>{admin && <Btn onClick={() => setEditing("new")}><Plus size={14} /> Add user</Btn>}</div>
      <div className="overflow-x-auto"><table className="w-full">
        <thead className="border-y border-slate-100"><tr><Th>Name / email</Th><Th>Role</Th><Th>Branch</Th><Th>Department</Th><Th>Reports to</Th><Th>Status</Th><Th></Th></tr></thead>
        <tbody className="divide-y divide-slate-50">
          {st.users.map((u) => (
            <tr key={u.id} className="hover:bg-slate-50/50">
              <Td><div className="font-medium text-slate-700">{u.name}</div><div className="text-xs text-slate-400">{u.email}</div></Td>
              <Td className="text-slate-600">{st.roles[u.role] ? st.roles[u.role].name : u.role}</Td>
              <Td><Mono className="text-slate-500">{br(u.branchId)}</Mono></Td>
              <Td className="text-slate-600">{u.dept || "—"}</Td>
              <Td className="text-slate-500">{u.reportsTo ? uname(u.reportsTo) : "—"}</Td>
              <Td><Badge tone={u.active ? "emerald" : "stone"}>{u.active ? "Active" : "Inactive"}</Badge></Td>
              <Td><div className="flex justify-end gap-1.5">{admin && <><Btn variant="soft" onClick={() => setEditing(u)}>Edit</Btn><Btn variant="soft" onClick={() => dispatch({ type: "TOGGLE_USER_ACTIVE", id: u.id })}>{u.active ? "Deactivate" : "Activate"}</Btn></>}</div></Td>
            </tr>
          ))}
        </tbody>
      </table></div>
      <UserEditor st={st} dispatch={dispatch} open={editing !== null} onClose={() => setEditing(null)} user={editing === "new" ? null : editing} />
    </Card>
  );
}

function RolesSettings({ st, dispatch, admin }) {
  const roleKeys = Object.keys(st.roles);
  const [sel, setSel] = useState(roleKeys[0]);
  const [adding, setAdding] = useState(false);
  const [nk, setNk] = useState(""); const [nn, setNn] = useState("");
  const r = st.roles[sel] || st.roles[roleKeys[0]];
  const memberCount = (rk) => st.users.filter((u) => u.role === rk).length;
  const addRole = () => { if (nk.trim() && nn.trim()) { dispatch({ type: "ADD_ROLE", key: nk, name: nn }); setNk(""); setNn(""); setAdding(false); } };
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
      <Card className="h-min p-2 sm:w-48 sm:shrink-0">
        <div className="flex items-center justify-between px-2 py-1.5"><span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Roles</span>{admin && <button onClick={() => setAdding(!adding)} className="text-indigo-500 hover:text-indigo-600"><Plus size={15} /></button>}</div>
        {adding && <div className="mb-2 space-y-1.5 rounded-lg bg-slate-50 p-2"><input value={nn} onChange={(e) => setNn(e.target.value)} placeholder="Role name" className={SETTINGS_IN} /><input value={nk} onChange={(e) => setNk(e.target.value)} placeholder="key (e.g. qc)" className={SETTINGS_IN} /><Btn className="w-full" onClick={addRole}>Add role</Btn></div>}
        <div className="space-y-0.5">{roleKeys.map((k) => <button key={k} onClick={() => setSel(k)} className={cx("flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-sm transition", sel === k ? "bg-indigo-50 font-medium text-indigo-700" : "text-slate-600 hover:bg-slate-50")}><span className="truncate">{st.roles[k].name}</span><span className="ml-2 text-xs text-slate-400">{memberCount(k)}</span></button>)}</div>
      </Card>
      <Card className="min-w-0 flex-1 p-4">
        <div className="mb-1 flex items-center gap-2"><h3 className="text-base font-semibold text-slate-800">{r.name}</h3><Badge tone="stone">{memberCount(sel)} users</Badge></div>
        <p className="mb-4 text-xs text-slate-400">Dept: {r.dept} · key <Mono>{sel}</Mono></p>
        {r.can.all ? <div className="rounded-lg bg-emerald-50 p-3 text-sm text-emerald-700">Full access — Owner / Admin can do every module + every action. This role is locked.</div> : <>
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Module access (sidebar menu)</div>
          <div className="mb-5 grid gap-1.5 sm:grid-cols-2">{ALL_MODULES.map((m) => { const on = r.nav.includes(m); return <label key={m} className={cx("flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-1.5 text-sm ring-1 transition", on ? "bg-indigo-50 ring-indigo-200 text-slate-700" : "ring-slate-100 text-slate-400")}><input type="checkbox" disabled={!admin} checked={on} onChange={() => dispatch({ type: "TOGGLE_ROLE_NAV", role: sel, module: m })} className="accent-indigo-600" />{MODULE_LABELS[m] || m}</label>; })}</div>
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Capabilities (allowed actions)</div>
          <div className="grid gap-1.5 sm:grid-cols-2">{ALL_PERMS.map((perm) => { const on = !!r.can[perm]; return <label key={perm} className={cx("flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-1.5 text-sm ring-1 transition", on ? "bg-emerald-50 ring-emerald-200 text-slate-700" : "ring-slate-100 text-slate-400")}><input type="checkbox" disabled={!admin} checked={on} onChange={() => dispatch({ type: "TOGGLE_ROLE_PERM", role: sel, perm })} className="accent-emerald-600" />{PERM_LABELS[perm] || perm}</label>; })}</div>
        </>}
      </Card>
    </div>
  );
}

function DeptSettings({ st, dispatch, admin }) {
  const [nm, setNm] = useState(""); const [head, setHead] = useState("");
  const count = (dn) => st.users.filter((u) => u.dept === dn).length;
  return (
    <Card className="p-0">
      <div className="p-4"><h3 className="text-base font-semibold text-slate-800">Departments</h3></div>
      <div className="overflow-x-auto"><table className="w-full">
        <thead className="border-y border-slate-100"><tr><Th>Department</Th><Th>Head</Th><Th className="text-right">Members</Th><Th></Th></tr></thead>
        <tbody className="divide-y divide-slate-50">
          {st.departments.map((d) => (
            <tr key={d.id} className="hover:bg-slate-50/50">
              <Td className="font-medium text-slate-700">{d.name}</Td>
              <Td>{admin ? <select value={d.head || ""} onChange={(e) => dispatch({ type: "UPDATE_DEPARTMENT", id: d.id, patch: { head: e.target.value || null } })} className={SETTINGS_IN + " max-w-xs"}><option value="">— Unassigned —</option>{st.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</select> : <span className="text-slate-600">{d.head ? uName(st, d.head) : "—"}</span>}</Td>
              <Td className="text-right"><Mono className="text-slate-500">{count(d.name)}</Mono></Td>
              <Td className="text-right">{admin && count(d.name) === 0 && <button onClick={() => dispatch({ type: "REMOVE_DEPARTMENT", id: d.id })} className="text-slate-300 hover:text-rose-500"><Trash2 size={14} /></button>}</Td>
            </tr>
          ))}
        </tbody>
      </table></div>
      {admin && <div className="flex flex-wrap items-end gap-2 border-t border-slate-100 p-4"><SField label="New department"><input value={nm} onChange={(e) => setNm(e.target.value)} placeholder="e.g. Quality" className={SETTINGS_IN + " w-48"} /></SField><SField label="Head"><select value={head} onChange={(e) => setHead(e.target.value)} className={SETTINGS_IN + " w-44"}><option value="">— Unassigned —</option>{st.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</select></SField><Btn onClick={() => { if (nm.trim()) { dispatch({ type: "ADD_DEPARTMENT", name: nm, head: head || null }); setNm(""); setHead(""); } }}><Plus size={14} /> Add</Btn></div>}
    </Card>
  );
}

function BranchSettings({ st, dispatch, admin }) {
  const [nm, setNm] = useState(""); const [code, setCode] = useState(""); const [bt, setBt] = useState("Branch");
  const count = (id) => st.users.filter((u) => u.branchId === id).length;
  return (
    <Card className="p-0">
      <div className="p-4"><h3 className="text-base font-semibold text-slate-800">Branches & locations</h3></div>
      <div className="overflow-x-auto"><table className="w-full">
        <thead className="border-y border-slate-100"><tr><Th>Name</Th><Th>Code</Th><Th>Type</Th><Th className="text-right">Users</Th><Th></Th></tr></thead>
        <tbody className="divide-y divide-slate-50">
          {st.branches.map((b) => (
            <tr key={b.id} className="hover:bg-slate-50/50">
              <Td className="font-medium text-slate-700">{b.name}</Td>
              <Td><Mono className="text-slate-500">{b.code}</Mono></Td>
              <Td><Badge tone={b.type === "HO" ? "indigo" : "stone"}>{b.type || "Branch"}</Badge></Td>
              <Td className="text-right"><Mono className="text-slate-500">{count(b.id)}</Mono></Td>
              <Td className="text-right">{admin && count(b.id) === 0 && <button onClick={() => dispatch({ type: "REMOVE_BRANCH", id: b.id })} className="text-slate-300 hover:text-rose-500"><Trash2 size={14} /></button>}</Td>
            </tr>
          ))}
        </tbody>
      </table></div>
      {admin && <div className="flex flex-wrap items-end gap-2 border-t border-slate-100 p-4"><SField label="Branch name"><input value={nm} onChange={(e) => setNm(e.target.value)} placeholder="e.g. Siliguri Depot" className={SETTINGS_IN + " w-48"} /></SField><SField label="Code"><input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="SIL" className={SETTINGS_IN + " w-24"} /></SField><SField label="Type"><select value={bt} onChange={(e) => setBt(e.target.value)} className={SETTINGS_IN + " w-36"}><option>Branch</option><option value="HO">Head office</option><option>Warehouse</option><option>Depot</option></select></SField><Btn onClick={() => { if (nm.trim() && code.trim()) { dispatch({ type: "ADD_BRANCH", name: nm, code, btype: bt }); setNm(""); setCode(""); } }}><Plus size={14} /> Add</Btn></div>}
    </Card>
  );
}

function HierarchySettings({ st }) {
  const roots = st.users.filter((u) => !u.reportsTo);
  const kids = (id) => st.users.filter((u) => u.reportsTo === id);
  const Node = ({ u }) => {
    const ch = kids(u.id);
    const initials = u.name.split(" ").map((x) => x[0]).slice(0, 2).join("");
    return (
      <div>
        <div className="flex items-center gap-3 rounded-lg border border-slate-100 bg-white px-3 py-2 shadow-sm">
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-indigo-50 text-xs font-semibold text-indigo-600">{initials}</span>
          <div className="min-w-0"><div className="text-sm font-medium text-slate-700">{u.name}{!u.active && <span className="ml-2 text-xs text-slate-400">(inactive)</span>}</div><div className="text-xs text-slate-400">{st.roles[u.role] ? st.roles[u.role].name : u.role} · {u.dept}</div></div>
        </div>
        {ch.length > 0 && <div className="ml-5 mt-1.5 space-y-1.5 border-l border-dashed border-slate-200 pl-4">{ch.map((c) => <Node key={c.id} u={c} />)}</div>}
      </div>
    );
  };
  return (
    <Card className="p-5">
      <h3 className="mb-1 text-base font-semibold text-slate-800">Organisational hierarchy</h3>
      <p className="mb-4 text-xs text-slate-400">Reporting structure (reports-to). Managers are set from Settings → Users.</p>
      <div className="space-y-2">{roots.map((u) => <Node key={u.id} u={u} />)}</div>
    </Card>
  );
}

// NumberingSettings (mock) retired — Settings now renders the live
// WiredNumberSeriesPanel (Tier 2 part 2, backend-persisted with FY reset).

// UnitsTaxSettings (mock) + Tier2Notice retired — Settings now renders the live
// WiredUnitsTaxPanel (GST Phase 2). No "not yet saved" banners remain anywhere.

function PrefsSettings({ st, dispatch, admin }) {
  const pf = st.settings.preferences;
  const [d, setD] = useState(pf);
  React.useEffect(() => setD(pf), [pf]);
  const set = (k, v) => setD((x) => ({ ...x, [k]: v }));
  return (
    <Card className="p-5">
      <div className="mb-4 flex items-center justify-between"><h3 className="text-base font-semibold text-slate-800">Preferences</h3>{admin && <Btn onClick={() => dispatch({ type: "UPDATE_PREFERENCES", patch: d })}>Save</Btn>}</div>
      <div className="grid gap-3 sm:grid-cols-2">
        <SField label="Date format"><select disabled={!admin} value={d.dateFormat} onChange={(e) => set("dateFormat", e.target.value)} className={SETTINGS_IN}><option>DD MMM YYYY</option><option>DD/MM/YYYY</option><option>MM/DD/YYYY</option><option>YYYY-MM-DD</option></select></SField>
        <SField label="Currency symbol"><input disabled={!admin} value={d.currencySymbol} onChange={(e) => set("currencySymbol", e.target.value)} className={SETTINGS_IN} /></SField>
        <SField label="Default branch"><select disabled={!admin} value={d.defaultBranch} onChange={(e) => set("defaultBranch", e.target.value)} className={SETTINGS_IN}>{st.branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}</select></SField>
        <SField label="Fiscal year start"><select disabled={!admin} value={d.fyStart} onChange={(e) => set("fyStart", e.target.value)} className={SETTINGS_IN}>{["January", "April", "July", "October"].map((m) => <option key={m}>{m}</option>)}</select></SField>
        <SField label="Week starts on"><select disabled={!admin} value={d.weekStart} onChange={(e) => set("weekStart", e.target.value)} className={SETTINGS_IN}><option>Monday</option><option>Sunday</option></select></SField>
        <SField label="Theme"><select disabled={!admin} value={d.theme} onChange={(e) => { set("theme", e.target.value); dispatch({ type: "UPDATE_PREFERENCES", patch: { theme: e.target.value } }); }} className={SETTINGS_IN}><option>Light</option><option>System</option><option>Dark</option></select></SField>
        <SField label="Density"><select disabled={!admin} value={d.density} onChange={(e) => { set("density", e.target.value); dispatch({ type: "UPDATE_PREFERENCES", patch: { density: e.target.value } }); }} className={SETTINGS_IN}><option>Comfortable</option><option>Compact</option></select></SField>
      </div>
      <p className="mt-4 text-xs text-slate-400">Note: preferences (theme/density/format) are a <span className="font-medium">local browser preference</span> — they persist client-side (localStorage), not to the server. This is intentional and fine.</p>
    </Card>
  );
}

// HolidaysSettings + ShiftsSettings (mock) retired — Settings now renders the
// live WiredHolidaysPanel / WiredShiftsPanel (Tier 2 part 1, backend-persisted).

function SettingsView({ st, dispatch, role, auth }) {
  const SECTIONS = [["company", "Company profile", Building2], ["users", "Users", Users], ["roles", "Roles & permissions", ShieldCheck], ["departments", "Departments", GitBranch], ["branches", "Branches & locations", Network], ["hierarchy", "Org hierarchy", Network], ["numbering", "Number series", ListOrdered], ["unitstax", "Units & tax", Layers], ["holidays", "Holidays", Calendar], ["shifts", "Shifts", Clock], ["prefs", "Preferences", Settings]];
  const [sec, setSec] = useState("company");
  const admin = can(role, "all");
  return (
    <div className="flex flex-col gap-5 lg:flex-row lg:items-start">
      <Card className="h-min p-2 lg:w-56 lg:shrink-0">
        <div className="px-2 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">Settings</div>
        <div className="space-y-0.5">
          {SECTIONS.map(([k, label, Icon]) => (
            <button key={k} onClick={() => setSec(k)} className={cx("flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition", sec === k ? "text-white" : "text-slate-600 hover:bg-slate-50")} style={sec === k ? { backgroundColor: "#4F6FE8" } : undefined}>
              <Icon size={16} /> {label}
            </button>
          ))}
        </div>
      </Card>
      <div className="min-w-0 flex-1">
        {!admin && <Card className="mb-3 bg-amber-50 p-3 text-sm text-amber-700">Read-only — only Owner / Admin can change settings.</Card>}
        {/* Group A — wired to the real backend (saves persist). */}
        {sec === "company" && <WiredCompanySettings admin={admin} />}
        {sec === "users" && <WiredUsersPanel admin={admin} me={auth?.user} title="Users" retail={Array.isArray(auth?.enabledModules) && auth.enabledModules.includes("warehouses")} />}
        {sec === "roles" && <WiredRolesSettings admin={admin} />}
        {sec === "departments" && <WiredDeptSettings admin={admin} />}
        {sec === "branches" && <WiredBranchesPanel admin={admin} />}
        {sec === "hierarchy" && <WiredHierarchySettings />}
        {/* Tier 2 part 2 — now wired to the real backend (banner removed). */}
        {sec === "numbering" && <WiredNumberSeriesPanel admin={admin} />}
        {/* GST Phase 2 — now wired to the real backend (last amber banner removed). */}
        {sec === "unitstax" && <WiredUnitsTaxPanel admin={admin} />}
        {/* Tier 2 part 1 — now wired to the real backend (banners removed). */}
        {sec === "holidays" && <WiredHolidaysPanel admin={admin} thisYear={new Date().getFullYear()} />}
        {sec === "shifts" && <WiredShiftsPanel admin={admin} />}
        {sec === "prefs" && <PrefsSettings st={st} dispatch={dispatch} admin={admin} />}
      </div>
    </div>
  );
}

// P-PWA-2: install-prompt banner. ADDITIVE, behavior no-op. Renders NOTHING on desktop
// (Chrome fires beforeinstallprompt only on installable mobile; the iOS hint is gated on
// iOS Safari + phone width) and NOTHING when already installed (display-mode: standalone).
// It only listens for the event, reads matchMedia/UA, and writes a localStorage dismiss
// flag — no API/business/data path is touched. Lives in the bundle (CSP forbids inline).
function InstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = React.useState(null);
  const [platform, setPlatform] = React.useState(null); // 'android' | 'ios'
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => {
    // Already installed → never show.
    try { if (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true) return; } catch (e) {}
    // Previously dismissed → never nag.
    try { if (localStorage.getItem("pwa-install-dismissed") === "1") return; } catch (e) {}

    // Android / installable Chrome: capture the event but ONLY surface the banner at phone
    // width — desktop Chrome also fires beforeinstallprompt, and desktop must stay untouched.
    const onBIP = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      let phone = false; try { phone = window.matchMedia("(max-width: 767px)").matches; } catch (er) {}
      if (phone) { setPlatform("android"); setVisible(true); }
    };
    const onInstalled = () => { setVisible(false); setDeferredPrompt(null); };
    window.addEventListener("beforeinstallprompt", onBIP);
    window.addEventListener("appinstalled", onInstalled);

    // iOS Safari has no beforeinstallprompt → manual Add-to-Home hint, but ONLY on an iOS
    // device in Safari at phone width (desktop Safari and in-app iOS Chrome/FF excluded).
    try {
      const ua = window.navigator.userAgent || "";
      const isIOS = /iphone|ipad|ipod/i.test(ua);
      const isSafari = /safari/i.test(ua) && !/crios|fxios|edgios/i.test(ua);
      const phone = window.matchMedia("(max-width: 767px)").matches;
      if (isIOS && isSafari && phone) { setPlatform("ios"); setVisible(true); }
    } catch (e) {}

    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => { setVisible(false); try { localStorage.setItem("pwa-install-dismissed", "1"); } catch (e) {} };
  const install = async () => {
    if (!deferredPrompt) return;
    try { deferredPrompt.prompt(); await deferredPrompt.userChoice; } catch (e) {}
    setDeferredPrompt(null); setVisible(false);
  };

  if (!visible) return null;
  return (
    <div className="fixed inset-x-0 bottom-0 z-40 flex justify-center px-3 pb-3" aria-label="Install app">
      <div className="flex w-full max-w-md items-center gap-3 rounded-xl bg-white px-4 py-3 shadow-2xl ring-1 ring-slate-200">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-white" style={{ backgroundColor: "#4F6FE8" }}><Download size={18} /></span>
        <div className="min-w-0 flex-1 text-sm text-slate-700">
          {platform === "ios"
            ? <span>Install <b>Sweet ERP</b>: tap <b>Share</b> <ArrowUp size={13} className="inline align-text-bottom" />, then <b>Add to Home Screen</b>.</span>
            : <span>Install <b>Sweet ERP</b> for a faster, full-screen experience.</span>}
        </div>
        {platform === "android" && deferredPrompt
          ? <Btn variant="primary" onClick={install} className="shrink-0"><Download size={14} /> Install</Btn>
          : null}
        <button onClick={dismiss} aria-label="Dismiss" className="shrink-0 rounded-md p-1 text-slate-400 hover:bg-slate-100"><X size={18} /></button>
      </div>
    </div>
  );
}

// P3.1b — in-app notification bell. `data` = { unreadCount, items } from GET /notifications (polled
// by App). Retail-only emits ⇒ manufacturing/JAINYS get { 0, [] } → no badge, "No notifications".
function NotificationBell({ data, onReload }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = React.useRef(null);
  const unread = (data && data.unreadCount) || 0;
  const items = (data && data.items) || [];
  React.useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  const markOne = async (id) => { try { await notificationsApi.markRead([id]); onReload && onReload(); } catch (e) {} };
  const markAll = async () => { setBusy(true); try { await notificationsApi.markRead(); onReload && onReload(); } catch (e) {} finally { setBusy(false); } };
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((v) => !v)} title="Notifications" aria-label={`Notifications${unread > 0 ? ` (${unread} unread)` : ""}`} className="relative grid h-8 w-8 place-items-center rounded-full bg-white text-slate-500 ring-1 ring-slate-200 shadow-sm hover:text-slate-700">
        <Bell size={15} />
        {unread > 0 && <span className="absolute -right-0.5 -top-0.5 grid min-h-[16px] min-w-[16px] place-items-center rounded-full px-1 text-[10px] font-semibold leading-none text-white" style={{ backgroundColor: "#4F6FE8" }}>{unread > 9 ? "9+" : unread}</span>}
      </button>
      {open && (
        <div className="absolute right-0 z-40 mt-2 w-80 max-w-[90vw] overflow-hidden rounded-xl bg-white shadow-xl ring-1 ring-slate-200">
          <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2">
            <span className="text-sm font-semibold text-slate-700">Notifications</span>
            {unread > 0 && <button onClick={markAll} disabled={busy} className="text-xs font-medium hover:underline disabled:opacity-50" style={{ color: "#4F6FE8" }}>Mark all read</button>}
          </div>
          <div className="max-h-80 divide-y divide-slate-50 overflow-y-auto">
            {items.length === 0 ? <div className="px-3 py-8 text-center text-sm text-slate-400">No notifications</div> : items.map((n) => (
              <button key={n.id} onClick={() => { if (!n.read) markOne(n.id); }} className={cx("block w-full px-3 py-2.5 text-left transition hover:bg-slate-50", !n.read && "bg-indigo-50/40")}>
                <div className="flex items-start gap-2">
                  {!n.read ? <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: "#4F6FE8" }} /> : <span className="mt-1.5 h-1.5 w-1.5 shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <div className={cx("truncate text-sm", n.read ? "text-slate-600" : "font-medium text-slate-800")}>{n.title}</div>
                    <div className="truncate text-xs text-slate-500">{n.body}</div>
                    <div className="mt-0.5 text-[11px] text-slate-400">{relTime(n.createdAt)}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function App({ auth, onLogout } = {}) {
  const [st, dispatch] = useReducer(reducer, undefined, buildInitial);
  if (st.roles) ROLES = st.roles;
  const _prefs = st.settings.preferences;
  const [sysDark, setSysDark] = useState(() => { try { return window.matchMedia("(prefers-color-scheme: dark)").matches; } catch (e) { return false; } });
  React.useEffect(() => { let mq; try { mq = window.matchMedia("(prefers-color-scheme: dark)"); const h = () => setSysDark(mq.matches); mq.addEventListener("change", h); return () => mq.removeEventListener("change", h); } catch (e) {} }, []);
  const dark = _prefs.theme === "Dark" || (_prefs.theme === "System" && sysDark);
  THEME_DARK = dark;
  const density = (_prefs.density || "Comfortable").toLowerCase();
  React.useEffect(() => {
    try { document.documentElement.setAttribute("data-theme", dark ? "dark" : "light"); document.documentElement.setAttribute("data-density", density); document.documentElement.style.backgroundColor = dark ? "#0B0F1A" : "#F6F7FA"; } catch (e) {}
    try { localStorage.setItem("growthh-theme", _prefs.theme); localStorage.setItem("growthh-density", _prefs.density); } catch (e) {}
  }, [dark, density, _prefs.theme, _prefs.density]);
  const [meId, setMeId] = useState("U-1");
  // Fallback guards the delete-all-users edge: with no users, st.users[0] is
  // undefined → me.role/me.name would throw and unmount the app.
  const me = st.users.find((u) => u.id === meId) || st.users[0] || { id: meId, name: "—", role: "owner" };
  const role = me.role;
  // Real auth (from /auth/me) gates the sidebar nav via RBAC: owners carry
  // permissions ["*"] (all modules); a restricted role shows only its
  // role.navModules. NAV keys === backend navModules keys. When no auth is
  // injected (e.g. legacy standalone render) we fall back to the mock role nav.
  const authAllPerms = !!auth && Array.isArray(auth.role?.permissions) && auth.role.permissions.includes("*");
  // "performance" (management reports) piggybacks on "reports" visibility, so any
  // manager who can see Reports also sees Performance (no separate module to seed).
  const navVisible = (k, mods) => mods.includes(k) || (k === "performance" && mods.includes("reports"));
  // "guide" is excluded here — the User Guide is rendered as a dedicated,
  // un-gated standalone link pinned to the bottom of the sidebar (visible to
  // every logged-in user), so it must NOT appear inside the role-grouped nav
  // (owners with all-perms would otherwise see it duplicated in the "Other" group).
  // PER-TENANT MODULE ENTITLEMENT (from /auth/me). null ⇒ ALL modules (every existing
  // tenant → unchanged). Applied OUTSIDE the authAllPerms bypass so a restricted
  // tenant's OWNER ("*") never sees a module the tenant doesn't have. "guide" and
  // "performance" have no module key of their own — never entitlement-filtered
  // ("performance" rides "reports" in navVisible; "guide" is the un-gated help link).
  const tenantMods = auth && Array.isArray(auth.enabledModules) ? auth.enabledModules : null;
  // RETAIL nav keys are OPT-IN: each maps to the module it REQUIRES, and shows only when
  // enabled_modules EXPLICITLY includes that module. Existing keys stay null-implied, so
  // a null tenant (every current tenant) never surfaces any retail nav — byte-identical
  // to today. Both "warehouses" and "indents" ride the single "warehouses" module.
  const RETAIL_KEY_MODULE = { retailops: "warehouses", warehouses: "warehouses", indents: "warehouses", receive: "warehouses", packs: "warehouses", storedispatch: "warehouses" };
  const tenantHas = (k) => {
    const reqMod = RETAIL_KEY_MODULE[k];
    if (reqMod) return tenantMods !== null && tenantMods.includes(reqMod);
    return tenantMods === null || k === "guide" || k === "performance" || tenantMods.includes(k);
  };
  const navItems = (auth
    ? NAV.filter(([k]) => tenantHas(k) && (authAllPerms || navVisible(k, auth.role?.navModules || [])))
    : NAV.filter((n) => navVisible(n[0], ROLES[role].nav))
  ).filter(([k]) => k !== "guide");
  // Land on Dashboard by default, but only if the role can access it (RBAC);
  // otherwise fall back to the first nav item the role CAN see (so a restricted
  // role isn't dropped onto a screen it can't open). RETAIL: if the user can see the
  // Operations dashboard (retail tenant + role has "retailops"), land there instead —
  // navItems is already tenant+role filtered, so this NEVER fires for manufacturing or
  // a retail role without Operations (their default stays byte-identical).
  const [view, setView] = useState(() =>
    navItems.some((it) => it[0] === "retailops") ? "retailops"
      : navItems.some((it) => it[0] === "dashboard") ? "dashboard" : navItems[0] ? navItems[0][0] : ROLES["owner"].nav[0],
  );
  // PRODUCTION #2: header item-search → Items & BOM. `itemSeed` carries the picked
  // item's code to CatalogView, which lands the Items list filtered on it. Only shown
  // to users who can actually open the Items screen (their nav includes "bom", or
  // all-perms owner) — a role without bom (e.g. sales) simply doesn't see the search,
  // so we never route them to a screen absent from their sidebar.
  const [itemSeed, setItemSeed] = useState(null);
  const canOpenItems = authAllPerms || navItems.some((it) => it[0] === "bom");

  // RETAIL: Store Receive "awaiting receipt" count, surfaced as a nav pill. Fetched ONLY
  // for users whose nav includes "receive" (retail tenants with the warehouses module) —
  // a manufacturing tenant never has this nav key, so this NEVER fires for them and the
  // nav is byte-identical. Reuses the SAME source ReceiveView uses (indentsApi.receivable
  // → awaitingCount); no new endpoint. Re-fetches on view change so marking a delivery /
  // receiving a challan refreshes the pill.
  const hasReceiveNav = navItems.some((it) => it[0] === "receive");
  const [awaitingReceive, setAwaitingReceive] = useState(0);
  React.useEffect(() => {
    if (!hasReceiveNav) { setAwaitingReceive(0); return; }
    let alive = true;
    indentsApi.receivable()
      .then((r) => { if (alive) setAwaitingReceive((r && r.awaitingCount) || 0); })
      .catch(() => { if (alive) setAwaitingReceive(0); });
    return () => { alive = false; };
  }, [hasReceiveNav, view]);

  // P3.1b — notification bell: fetch on mount + every ~60s + on view change. Retail-only emits ⇒
  // manufacturing tenants always get { 0, [] } (inert bell). Only fetches for a real session (auth).
  const [notif, setNotif] = useState({ unreadCount: 0, items: [] });
  const reloadNotif = React.useCallback(() => {
    if (!auth) { setNotif({ unreadCount: 0, items: [] }); return; }
    notificationsApi.list()
      .then((r) => setNotif({ unreadCount: (r && r.unreadCount) || 0, items: ((r && r.items) || []).slice(0, 20) }))
      .catch(() => {});
  }, [auth]);
  React.useEffect(() => {
    if (!auth) { setNotif({ unreadCount: 0, items: [] }); return; }
    reloadNotif();
    const t = setInterval(reloadNotif, 60000);
    return () => clearInterval(t);
  }, [auth, view, reloadNotif]);

  // Grouped, collapsible sidebar state (persisted like the theme/density prefs).
  // navCollapsed = icon-only rail; navGroups = per-group collapsed flags (true =
  // collapsed; default expanded). RBAC is preserved by grouping navItems (already
  // role-filtered) — groups with zero permitted items are not rendered.
  const [navCollapsed, setNavCollapsed] = useState(() => { try { return localStorage.getItem("sweet-erp-nav-collapsed") === "1"; } catch (e) { return false; } });
  const [navGroups, setNavGroups] = useState(() => { try { return JSON.parse(localStorage.getItem("sweet-erp-nav-groups") || "{}") || {}; } catch (e) { return {}; } });
  // P-RESP-1: mobile off-canvas drawer (below md only). Desktop never opens it (the
  // hamburger is md:hidden) so drawerOpen stays false ≥md → the shell is byte-identical.
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Force the drawer closed at/above md (handles a resize/rotate from mobile→desktop),
  // which also releases the scroll lock below. On desktop this only ever keeps it false.
  React.useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const onChange = () => { if (mq.matches) setDrawerOpen(false); };
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  // Lock body scroll while the mobile drawer is open (drawerOpen is only ever true <md).
  React.useEffect(() => {
    if (!drawerOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [drawerOpen]);
  React.useEffect(() => { try { localStorage.setItem("sweet-erp-nav-collapsed", navCollapsed ? "1" : "0"); } catch (e) {} }, [navCollapsed]);
  React.useEffect(() => { try { localStorage.setItem("sweet-erp-nav-groups", JSON.stringify(navGroups)); } catch (e) {} }, [navGroups]);
  const toggleGroup = (name) => setNavGroups((g) => ({ ...g, [name]: !g[name] }));
  const allowedByKey = new Map(navItems.map((it) => [it[0], it]));
  const grouped = NAV_GROUPS
    .map(([name, keys]) => [name, keys.map((k) => allowedByKey.get(k)).filter(Boolean)]);
  const groupedKeys = new Set(NAV_GROUPS.flatMap(([, keys]) => keys));
  const ungrouped = navItems.filter((it) => !groupedKeys.has(it[0]));
  const visibleGroups = [...grouped, ...(ungrouped.length ? [["Other", ungrouped]] : [])]
    .filter(([, items]) => items.length > 0); // drop empty groups (RBAC)
  // A group is open when not explicitly collapsed OR it holds the active item.
  const isGroupOpen = (name, items) => items.some((it) => it[0] === view) || !navGroups[name];

  const party = (id) => st.parties.find((p) => p.id === id) || { name: id, gstin: "" };
  const titleEntry = NAV.find((n) => n[0] === view);
  const title = titleEntry ? titleEntry[1] : "";
  const branchOf = (id) => st.branches.find((b) => b.id === id);
  // W5: the mock "View as" switcher is retired; meId stays "U-1" so the few
  // still-internal mock screens (MyQueue, Dashboard, Monitor) keep a role prop.
  void setMeId;
  const [logoOk, setLogoOk] = useState(true);

  return (
    <div className="erp min-h-screen text-slate-800" data-theme={dark ? "dark" : "light"} data-density={density} style={{ backgroundColor: dark ? "#0B0F1A" : "#F6F7FA" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;700&family=IBM+Plex+Mono:wght@400;500&display=swap');
        .erp{font-family:'Poppins',-apple-system,'Segoe UI',Roboto,sans-serif} .mono{font-family:'IBM Plex Mono',ui-monospace,monospace}
        .erp ::-webkit-scrollbar{width:10px;height:10px}.erp ::-webkit-scrollbar-thumb{background:#cbd5e1;border-radius:6px}
        .erp button[data-primary]:hover{background-color:#4056C4}
        /* dark mode (scoped to .erp[data-theme=dark]) */
        .erp[data-theme="dark"]{color:rgba(255,255,255,0.92)}
        .erp[data-theme="dark"] .bg-white{background-color:#161B2E !important}
        .erp[data-theme="dark"] .bg-slate-50{background-color:#1A2034 !important}
        .erp[data-theme="dark"] .bg-slate-100{background-color:#232B42 !important}
        .erp[data-theme="dark"] .bg-slate-200{background-color:#2C3450 !important}
        .erp[data-theme="dark"] .bg-slate-300{background-color:#3A4658 !important}
        .erp[data-theme="dark"] .text-slate-800{color:rgba(255,255,255,0.92) !important}
        .erp[data-theme="dark"] .text-slate-700{color:rgba(255,255,255,0.85) !important}
        .erp[data-theme="dark"] .text-slate-600{color:rgba(255,255,255,0.72) !important}
        .erp[data-theme="dark"] .text-slate-500{color:rgba(255,255,255,0.60) !important}
        .erp[data-theme="dark"] .text-slate-400{color:rgba(255,255,255,0.45) !important}
        .erp[data-theme="dark"] .text-slate-300{color:rgba(255,255,255,0.34) !important}
        .erp[data-theme="dark"] .border-slate-50,.erp[data-theme="dark"] .border-slate-100,.erp[data-theme="dark"] .border-slate-200{border-color:rgba(255,255,255,0.08) !important}
        .erp[data-theme="dark"] .ring-slate-100,.erp[data-theme="dark"] .ring-slate-200,.erp[data-theme="dark"] [class~="ring-slate-200/80"],.erp[data-theme="dark"] [class~="ring-slate-500/20"]{--tw-ring-color:rgba(255,255,255,0.10) !important}
        .erp[data-theme="dark"] .divide-slate-50>:not([hidden])~:not([hidden]),.erp[data-theme="dark"] .divide-slate-100>:not([hidden])~:not([hidden]){border-color:rgba(255,255,255,0.07) !important}
        .erp[data-theme="dark"] [class~="hover:bg-slate-50"]:hover,.erp[data-theme="dark"] [class~="hover:bg-slate-50/50"]:hover,.erp[data-theme="dark"] [class~="hover:bg-slate-100"]:hover{background-color:rgba(255,255,255,0.05) !important}
        .erp[data-theme="dark"] ::placeholder{color:rgba(255,255,255,0.38) !important}
        .erp[data-theme="dark"] ::-webkit-scrollbar-thumb{background:rgba(255,255,255,0.16)}
        .erp[data-theme="dark"] .bg-emerald-50{background-color:rgba(45,164,78,0.16) !important}
        .erp[data-theme="dark"] .bg-rose-50{background-color:rgba(229,72,77,0.16) !important}
        .erp[data-theme="dark"] .bg-amber-50{background-color:rgba(242,182,0,0.13) !important}
        .erp[data-theme="dark"] .bg-sky-50{background-color:rgba(66,133,244,0.15) !important}
        .erp[data-theme="dark"] .bg-violet-50{background-color:rgba(124,58,237,0.18) !important}
        .erp[data-theme="dark"] .bg-indigo-50{background-color:rgba(79,111,232,0.18) !important}
        .erp[data-theme="dark"] .text-emerald-700,.erp[data-theme="dark"] .text-emerald-600{color:#34D399 !important}
        .erp[data-theme="dark"] .text-rose-700,.erp[data-theme="dark"] .text-rose-600{color:#FF6B6B !important}
        .erp[data-theme="dark"] .text-amber-700,.erp[data-theme="dark"] .text-amber-600,.erp[data-theme="dark"] .text-amber-800,.erp[data-theme="dark"] .text-amber-900{color:#FBBF24 !important}
        .erp[data-theme="dark"] .text-sky-700,.erp[data-theme="dark"] .text-sky-600{color:#7CB0FF !important}
        .erp[data-theme="dark"] .text-violet-700,.erp[data-theme="dark"] .text-violet-600{color:#C4B5FD !important}
        .erp[data-theme="dark"] .text-indigo-700,.erp[data-theme="dark"] .text-indigo-600{color:#8094FF !important}
        /* density */
        .erp[data-density="compact"] td,.erp[data-density="compact"] th{padding-top:.34rem !important;padding-bottom:.34rem !important}
        .erp[data-density="compact"] .p-5{padding:.9rem !important}
        .erp[data-density="compact"] .p-4{padding:.72rem !important}
        .erp[data-density="compact"] .py-4{padding-top:.5rem !important;padding-bottom:.5rem !important}`}</style>

      <div className="flex">
        {/* P-RESP-1: mobile drawer backdrop — only <md and only while open. Inert on
            desktop (md:hidden, and drawerOpen stays false ≥md). */}
        {drawerOpen && <div className="fixed inset-0 z-40 bg-slate-900/40 md:hidden" onClick={() => setDrawerOpen(false)} aria-hidden="true" />}
        {/* sidebar — grouped, collapsible, with an icon-only rail mode (W6) */}
        <aside className={cx("sticky top-0 flex h-screen shrink-0 flex-col overflow-hidden text-slate-300 transition-[width] duration-200 ease-in-out", navCollapsed ? "w-16" : "w-60", "max-md:fixed max-md:left-0 max-md:z-50 max-md:w-60 max-md:shadow-2xl max-md:transition-transform", drawerOpen ? "max-md:translate-x-0" : "max-md:-translate-x-full")} style={{ backgroundColor: "#1A1F3C" }}>
          <div className={cx("flex items-center gap-2.5 py-5", navCollapsed ? "justify-center px-2" : "px-5")}>
            <div className="rounded-lg bg-white px-2.5 py-1.5 shadow-sm shrink-0">{logoOk ? <img src="https://leads.growthh.com/growthh-logo-original.png" alt="growthh" onError={() => setLogoOk(false)} className="h-5 w-auto" style={{ display: "block" }} /> : <span className="text-sm font-bold" style={{ color: "#1A1F3C" }}>growthh</span>}</div>
            {!navCollapsed && <div className="min-w-0"><div className="truncate text-sm font-semibold text-white">Sweet Manufacturing ERP</div><div className="truncate text-xs text-slate-400">{auth?.company?.name || "by Growthh"}</div></div>}
          </div>

          <nav className={cx("flex-1 overflow-y-auto overflow-x-hidden py-2", navCollapsed ? "px-2 space-y-0.5" : "px-3 space-y-1")} aria-label="Primary">
            {visibleGroups.map(([name, items], gi) => {
              const open = isGroupOpen(name, items);
              const containsActive = items.some((it) => it[0] === view);
              if (navCollapsed) {
                // Rail mode: group headers become dividers; items are icon-only.
                return (
                  <div key={name}>
                    {gi > 0 && <div className="mx-2 my-1.5 border-t border-white/10" />}
                    {items.map(([k, label, Icon]) => (
                      <button key={k} onClick={() => { setView(k); setDrawerOpen(false); }} title={label + " · " + name + (k === "receive" && awaitingReceive > 0 ? ` · ${awaitingReceive} awaiting receipt` : "")} aria-label={label} aria-current={view === k ? "page" : undefined} style={view === k ? { backgroundColor: "#2E3566", color: "#8094FF" } : undefined} className={cx("relative mb-0.5 flex w-full items-center justify-center rounded-lg p-2.5 transition", view === k ? "" : "text-slate-400 hover:bg-white/5 hover:text-white")}>
                        <Icon size={18} />
                        {k === "receive" && awaitingReceive > 0 && <span className="absolute right-0.5 top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[9px] font-bold leading-none text-white" style={{ backgroundColor: "#4F6FE8" }}>{awaitingReceive}</span>}
                      </button>
                    ))}
                  </div>
                );
              }
              return (
                <div key={name}>
                  <button onClick={() => { if (!containsActive) toggleGroup(name); }} disabled={containsActive} aria-expanded={open} title={containsActive ? "Contains the current screen" : undefined} className={cx("flex w-full items-center justify-between rounded px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider transition", containsActive ? "cursor-default text-slate-400" : "text-slate-500 hover:text-slate-300")}>
                    <span>{name}</span>
                    <ChevronRight size={13} className={cx("transition-transform duration-200", open ? "rotate-90" : "")} />
                  </button>
                  <div className={cx("grid transition-all duration-200 ease-in-out", open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0")} aria-hidden={!open}>
                    <div className="overflow-hidden">
                      <div className={cx("space-y-0.5 pt-0.5", open ? "" : "pointer-events-none")}>
                        {items.map(([k, label, Icon]) => (
                          <button key={k} onClick={() => { setView(k); setDrawerOpen(false); }} tabIndex={open ? 0 : -1} aria-current={view === k ? "page" : undefined} style={view === k ? { backgroundColor: "#2E3566", color: "#8094FF" } : undefined} className={cx("flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition", view === k ? "" : "text-slate-400 hover:bg-white/5 hover:text-white")}>
                            <Icon size={17} className="shrink-0" /> <span className="truncate">{label}</span>
                            {k === "receive" && awaitingReceive > 0 && <span className="ml-auto shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-bold leading-none text-white" style={{ backgroundColor: "#4F6FE8" }} title={`${awaitingReceive} awaiting receipt`}>{awaitingReceive}</span>}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </nav>

          {/* User Guide — standalone bottom link, un-gated (visible to every logged-in
              user regardless of role). Sits below the last group, above Collapse. */}
          <div className={cx("border-t border-white/10", navCollapsed ? "px-2 py-1" : "px-3 py-2")}>
            {navCollapsed ? (
              <button onClick={() => { setView("guide"); setDrawerOpen(false); }} title="User Guide" aria-label="User Guide" aria-current={view === "guide" ? "page" : undefined} style={view === "guide" ? { backgroundColor: "#2E3566", color: "#8094FF" } : undefined} className={cx("flex w-full items-center justify-center rounded-lg p-2.5 transition", view === "guide" ? "" : "text-slate-400 hover:bg-white/5 hover:text-white")}>
                <BookOpen size={18} />
              </button>
            ) : (
              <button onClick={() => { setView("guide"); setDrawerOpen(false); }} aria-current={view === "guide" ? "page" : undefined} style={view === "guide" ? { backgroundColor: "#2E3566", color: "#8094FF" } : undefined} className={cx("flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition", view === "guide" ? "" : "text-slate-400 hover:bg-white/5 hover:text-white")}>
                <BookOpen size={17} className="shrink-0" /> <span className="truncate">User Guide</span>
              </button>
            )}
          </div>

          <button onClick={() => setNavCollapsed((v) => !v)} aria-label={navCollapsed ? "Expand sidebar" : "Collapse sidebar"} title={navCollapsed ? "Expand sidebar" : "Collapse sidebar"} className={cx("flex items-center border-t border-slate-800 py-3 text-slate-400 transition hover:text-white", navCollapsed ? "justify-center px-2" : "gap-2 px-5")}>
            <ChevronRight size={16} className={cx("shrink-0 transition-transform duration-200", navCollapsed ? "" : "rotate-180")} />
            {!navCollapsed && <span className="text-xs font-medium">Collapse</span>}
          </button>

          {!navCollapsed && <div className="border-t border-slate-800 px-5 py-3 text-xs text-slate-500">{auth ? <>{auth.user?.name} · {auth.role?.name}{auth.company?.name ? " · " + auth.company.name : ""}</> : <>{me.name} · {ROLES[role].name}{branchOf(me.branchId) ? " · " + branchOf(me.branchId).name : ""}</>}</div>}
        </aside>

        {/* main */}
        <main className="min-w-0 flex-1">
          <header className="sticky top-0 z-30 flex items-center justify-between border-b border-slate-200 px-4 md:px-7 py-4" style={{ backgroundColor: dark ? "#0E1322ee" : "#F6F7FAee", backdropFilter: "blur(6px)", boxShadow: dark ? "0 1px 14px rgba(0,0,0,0.3)" : "0 1px 14px rgba(30,34,56,0.06)" }}>
            <div className="flex min-w-0 items-center gap-2">
              <button onClick={() => setDrawerOpen(true)} aria-label="Open menu" className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-slate-600 hover:bg-slate-200/60 md:hidden"><Menu size={20} /></button>
              <div className="min-w-0"><h1 className="text-lg font-semibold text-slate-800 max-md:truncate">{title}</h1><p className="text-xs text-slate-400 max-md:truncate">{auth?.company?.name ? auth.company.name + " — operations console" : "Sweet Manufacturing ERP"}</p></div>
            </div>
            <div className="flex items-center gap-2.5">
            {/* PRODUCTION #2: header product-search with autocomplete. Reuses ItemPicker
                UNMODIFIED (value pinned null so it stays in search mode; on pick it clears
                itself). On select we seed the Items & BOM screen and switch to it. Hidden
                on narrow headers and for roles that can't open Items. */}
            {canOpenItems && (
              <div className="hidden w-64 lg:block">
                <ItemPicker value={null} onClear={() => {}} onPick={(it) => { setItemSeed({ q: it.code, n: (typeof performance !== "undefined" ? performance.now() : 0) }); setView("bom"); }} />
              </div>
            )}
            <NotificationBell data={notif} onReload={reloadNotif} />
            <button onClick={() => dispatch({ type: "UPDATE_PREFERENCES", patch: { theme: dark ? "Light" : "Dark" } })} title={dark ? "Light mode" : "Dark mode"} className="grid h-8 w-8 place-items-center rounded-full bg-white text-slate-500 ring-1 ring-slate-200 shadow-sm hover:text-slate-700">{dark ? <Sun size={15} /> : <Moon size={15} />}</button>
            {/* W5: mock "View as" switcher retired — the app runs entirely on the
                REAL /auth/me session. This is now a read-only identity chip. */}
            {auth && <span className="flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-xs text-slate-600 ring-1 ring-slate-200 shadow-sm max-md:hidden">
              <ShieldCheck size={14} style={{ color: "#4F6FE8" }} />
              <span className="text-slate-400">Signed in as</span>
              <span className="font-medium text-slate-700">{auth.user?.name} · {auth.role?.name}</span>
            </span>}
            {onLogout && <button onClick={() => onLogout()} title="Sign out" className="flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-xs font-medium text-slate-600 ring-1 ring-slate-200 shadow-sm hover:text-rose-600"><ArrowRight size={14} /> Sign out</button>}
            </div>
          </header>
          <div className="p-4 md:p-7">
            {view === "queue" && <MyQueue st={st} dispatch={dispatch} role={role} me={me} go={setView} party={party} auth={auth} />}
            {view === "dashboard" && <Dashboard auth={auth} dark={dark} />}
            {view === "monitor" && <MonitorView st={st} party={party} dispatch={dispatch} role={role} me={me} auth={auth} />}
            {view === "tasks" && <TasksView auth={auth} />}
            {view === "people" && <PeopleView st={st} dispatch={dispatch} role={role} auth={auth} />}
            {view === "orders" && <OrdersView st={st} dispatch={dispatch} party={party} role={role} auth={auth} />}
            {view === "production" && <ProductionView auth={auth} role={role} />}
            {view === "workorders" && <WorkOrdersView auth={auth} role={role} />}
            {view === "routing" && <RoutingView st={st} dispatch={dispatch} role={role} auth={auth} />}
            {view === "purchases" && <PurchasesView auth={auth} role={role} />}
            {view === "inventory" && <InventoryView st={st} dispatch={dispatch} role={role} auth={auth} />}
            {view === "batches" && <BatchesView auth={auth} />}
            {view === "bom" && <CatalogView seed={itemSeed} onConsumeSeed={() => setItemSeed(null)} />}
            {view === "deliveries" && <DeliveriesView auth={auth} role={role} />}
            {view === "invoices" && <InvoicesView auth={auth} role={role} />}
            {view === "customers" && <CustomersView />}
            {view === "retailops" && <RetailOpsView auth={auth} role={role} />}
            {view === "warehouses" && <WarehousesView auth={auth} role={role} />}
            {view === "indents" && <IndentsView auth={auth} role={role} />}
            {view === "receive" && <ReceiveView auth={auth} role={role} />}
            {view === "storedispatch" && <StoreDispatchView auth={auth} role={role} />}
            {view === "packs" && <PacksView auth={auth} role={role} />}
            {view === "reports" && <ReportsView />}
            {view === "performance" && <PerformanceView auth={auth} role={role} />}
            {view === "settings" && <SettingsView st={st} dispatch={dispatch} role={role} auth={auth} />}
            {view === "guide" && <UserGuideView dark={dark} auth={auth} />}
          </div>
        </main>
      </div>
      <InstallBanner />
    </div>
  );
}
